/* ===== app-utils.js ===== */
const { useState, useEffect, useCallback, useRef, useMemo } = React;
window.useState = useState; window.useEffect = useEffect; window.useCallback = useCallback; window.useRef = useRef; window.useMemo = useMemo;

// System-wide error reporting. Posts to /api/errors/log fire-and-forget;
// the server dedupes (10-min, per-hash+user) and rate-caps (20/hr/subscriber)
// before sending the system_error alert email. Safe to call from anywhere.
window.__reportError = function(info) {
  try {
    // Rate-limit (429) and auth (401) failures are operational noise, not bugs — and each
    // report BURNS the same per-IP request budget that's already exhausted during a 429
    // storm (2026-07-13 lockout: logged-out restored tabs + their /errors/log posts starved
    // the login endpoint). Skip them, and cap reporting at one post per 5s per tab — the
    // __reportingError flag below only stops CONCURRENT posts, not rapid sequential ones.
    var sc = info && info.status_code;
    if (sc === 429 || sc === 401) return;
    var now = Date.now();
    if (window.__lastErrorReportAt && (now - window.__lastErrorReportAt) < 5000) return;
    var body = {
      area: info && info.area || window.__currentPage || (location.hash || location.pathname || 'unknown'),
      source: info && info.source || 'client',
      message: info && info.message != null ? String(info.message) : '',
      stack: info && info.stack ? String(info.stack) : null,
      url: location.href,
      method: info && info.method || null,
      status_code: info && info.status_code || null,
    };
    if (window.__reportingError) return;
    window.__reportingError = true;
    window.__lastErrorReportAt = now;
    fetch('/api/errors/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).catch(function() { /* reporting is best-effort — a failed report must never become its own unhandled rejection */ })
      .finally(function() { window.__reportingError = false; });
  } catch (e) { /* swallow — never let error reporting break the UI */ }
};

window.addEventListener('error', function(ev) {
  if (ev && ev.target && ev.target !== window) return; // skip resource-load failures
  window.__reportError({
    source: 'unhandled',
    message: ev && ev.message ? ev.message : 'window.onerror',
    stack: ev && ev.error && ev.error.stack ? ev.error.stack : null,
  });
});

window.addEventListener('unhandledrejection', function(ev) {
  var reason = ev && ev.reason;
  var msg = reason && reason.message ? reason.message : String(reason || 'unhandledrejection');
  var stack = reason && reason.stack ? reason.stack : null;
  var info = { source: 'unhandled', message: msg, stack: stack };
  // If thrown by api() / apiUpload(), surface which call failed so error_log
  // captures the endpoint, method, and status — minified stacks alone don't.
  if (reason && reason.apiPath) {
    info.method = reason.apiMethod;
    info.status_code = reason.apiStatus;
    info.message = reason.apiMethod + ' ' + reason.apiPath + ' → ' + msg;
  }
  window.__reportError(info);
});

// Chart.js hover fix under the app-wide body zoom (index.html). Chrome reports
// MouseEvent offsetX/offsetY in visual (zoomed) px, but Chart.js hit-tests them against
// its layout-px canvas size, so tooltips/highlights land ~25% right/below the cursor and
// the far edge of every chart goes dead. Normalize offsets on canvas-targeted mouse
// events in the capture phase, before Chart.js's own canvas listeners read them:
// local = (client − rect) × (layoutSize / visualSize). Self-measuring (no APP_ZOOM
// dependency) and a no-op at zoom 1 or on browsers that already report layout px.
(function() {
  function fixCanvasOffsets(e) {
    var t = e.target;
    if (!t || t.tagName !== 'CANVAS') return;
    var rect = t.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var sx = t.offsetWidth / rect.width, sy = t.offsetHeight / rect.height;
    if (Math.abs(sx - 1) < 0.001 && Math.abs(sy - 1) < 0.001) return;
    var ox = (e.clientX - rect.left) * sx, oy = (e.clientY - rect.top) * sy;
    Object.defineProperty(e, 'offsetX', { get: function() { return ox; }, configurable: true });
    Object.defineProperty(e, 'offsetY', { get: function() { return oy; }, configurable: true });
  }
  ['mousemove', 'mouseout', 'click', 'mousedown', 'mouseup'].forEach(function(type) {
    document.addEventListener(type, fixCanvasOffsets, true);
  });
})();

// APP_ZOOM self-heal: the global is inlined in index.html, but a session running a
// STALE cached index.html (served after the body-zoom CSS landed but before the global
// did) gets the CURRENT bundle with APP_ZOOM undefined — every ÷APP_ZOOM popover
// compensation then no-ops and fixed menus land ~25% down/right of their button.
// Derive it from the computed body zoom so the bundle never depends on the HTML copy.
if (!window.APP_ZOOM) {
  var _bodyZoom = parseFloat(getComputedStyle(document.body).zoom);
  window.APP_ZOOM = (_bodyZoom && isFinite(_bodyZoom)) ? _bodyZoom : 1;
}

// Render chart canvases at visual resolution: with the body zoomed 1.25×, Chart.js's
// default backing store (layout px × devicePixelRatio) gets upscaled by the zoom and
// text goes soft. Hit-testing is unaffected (Chart.js divides canvas.width back out).
if (window.Chart) {
  Chart.defaults.devicePixelRatio = (window.devicePixelRatio || 1) * (window.APP_ZOOM || 1);
}

// React error boundary — catches render/lifecycle crashes in the wrapped subtree,
// reports the REAL error + React componentStack (immune to the cross-origin "Script error."
// mask), and shows a non-fatal fallback instead of white-screening the app. Wrapped
// per-page and keyed on the route, so navigating away clears a tripped boundary.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
    this.handleRetry = this.handleRetry.bind(this);
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) ? error.message : 'Unexpected error' };
  }
  componentDidCatch(error, info) {
    try {
      var cs = (info && info.componentStack) ? info.componentStack : '';
      var stk = (error && error.stack ? error.stack : String(error)) + (cs ? '\n--- React componentStack ---' + cs : '');
      if (window.__reportError) window.__reportError({
        source: 'unhandled',
        area: this.props.area || window.__currentPage || 'unknown',
        message: (error && error.message) ? error.message : String(error),
        stack: stk,
      });
    } catch (e) { /* the boundary's own reporting must never throw */ }
  }
  handleRetry() { this.setState({ hasError: false, message: null }); }
  render() {
    if (!this.state.hasError) return this.props.children;
    var btn = { padding: '8px 16px', borderRadius: 8, border: '1px solid #2A2E3A', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 };
    return React.createElement('div', { style: { padding: 24, maxWidth: 560, margin: '40px auto', background: '#171A23', border: '1px solid #2A2E3A', borderRadius: 12, color: '#E8EAF0' } },
      React.createElement('div', { style: { fontSize: 18, fontWeight: 700, marginBottom: 8 } }, 'Something went wrong on this page'),
      React.createElement('div', { style: { fontSize: 14, color: '#9AA0B5', marginBottom: 18, wordBreak: 'break-word' } }, this.state.message || 'An unexpected error occurred. The team has been notified.'),
      React.createElement('div', { style: { display: 'flex', gap: 10 } },
        React.createElement('button', { onClick: this.handleRetry, style: Object.assign({}, btn, { background: '#4F6EF7', borderColor: '#4F6EF7', color: '#fff' }) }, 'Try again'),
        React.createElement('button', { onClick: function() { location.reload(); }, style: Object.assign({}, btn, { background: 'transparent', color: '#E8EAF0' }) }, 'Reload page')
      )
    );
  }
}
window.ErrorBoundary = ErrorBoundary;

function LIcon({ name, size, color, spin, style: extraStyle }) {
  var pascal = name.split('-').map(function(s) { return s.charAt(0).toUpperCase() + s.slice(1); }).join('');
  var Icon = window.LucideReact && window.LucideReact[pascal];
  if (!Icon) return null;
  var s = spin ? { animation: 'spin 1s linear infinite', ...(extraStyle || {}) } : extraStyle;
  return React.createElement(Icon, { size: size || 16, color: color, style: s });
}
window.LIcon = LIcon;

// Custom hover tooltip that renders as real DOM (not the native `title`
// overlay). Visible in screen captures and streams; doesn't disappear on
// modifier-key press. Use this in place of `title=` whenever the user
// might need to screenshot or share the tooltip content.
function Tooltip({ text, children, style, wrapperStyle }) {
  var [visible, setVisible] = useState(false);
  var [pos, setPos] = useState({ x: 0, y: 0 });
  if (!text) return React.createElement('span', { style: style }, children);
  function show(e) {
    var rect = e.currentTarget.getBoundingClientRect();
    // gBCR returns visual px; fixed-position styles on the zoomed body are in zoomed px.
    var z = window.APP_ZOOM || 1;
    setPos({ x: (rect.left + rect.width / 2) / z, y: rect.top / z });
    setVisible(true);
  }
  function hide() { setVisible(false); }
  var spanStyle = Object.assign({ display: 'inline-block', position: 'relative' }, style || {});
  return React.createElement('span', { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide, style: spanStyle },
    children,
    visible && React.createElement('div', {
      role: 'tooltip',
      style: Object.assign({
        position: 'fixed',
        top: pos.y - 8, left: pos.x,
        transform: 'translate(-50%, -100%)',
        background: '#1F2937', color: '#F9FAFB',
        padding: '8px 12px', borderRadius: 6,
        border: '1px solid #374151',
        fontSize: 12, lineHeight: 1.5,
        whiteSpace: 'pre-line', textAlign: 'left',
        maxWidth: 360, minWidth: 60,
        zIndex: 10000, pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        fontWeight: 400,
      }, wrapperStyle || {})
    }, text)
  );
}
window.Tooltip = Tooltip;

const API_BASE = '/api';

var themes = {
  dark: {
    bg: '#0F1117', surface: '#1A1D27', surfaceHover: '#22252F',
    border: '#2A2D3A', borderFocus: '#4F6EF7', cardShadow: 'none',
    text: '#E8EAF0', textMuted: '#8B8FA3', textDim: '#5C6078',
    accent: '#4F6EF7', accentHover: '#3D5CE5', accentSoft: 'rgba(79,110,247,0.12)',
    success: '#34D399', successSoft: 'rgba(52,211,153,0.12)',
    warning: '#FBBF24', warningSoft: 'rgba(251,191,36,0.12)',
    danger: '#F87171', dangerSoft: 'rgba(248,113,113,0.12)',
    purple: '#A78BFA', purpleSoft: 'rgba(167,139,250,0.12)',
  },
  light: {
    bg: '#F6F7F9', surface: '#FFFFFF', surfaceHover: '#F3F4F6',
    border: '#E5E7EB', borderFocus: '#4F6EF7', cardShadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
    text: '#111827', textMuted: '#4B5563', textDim: '#6B7280',
    accent: '#4F6EF7', accentHover: '#3D5CE5', accentSoft: 'rgba(79,110,247,0.10)',
    success: '#059669', successSoft: 'rgba(5,150,105,0.10)',
    warning: '#D97706', warningSoft: 'rgba(217,119,6,0.10)',
    danger: '#DC2626', dangerSoft: 'rgba(220,38,38,0.10)',
    purple: '#7C3AED', purpleSoft: 'rgba(124,58,237,0.10)',
  }
};

// ─── SIDENAV: shared sidebar style kit ───
// Single source of truth for the left nav across ALL shells (main SPA, ads.html,
// ship.html). The bundles concatenate app-utils.js first with shared top-level
// scope, so every shell must reference these instead of hand-copied style
// objects — copies drifted apart until normalized 2026-08-11. The sidebar is
// deliberately light-themed in every shell (hardcoded colors, not C.*).
// Sidebar widths — defined ONCE here; anything that positions against the
// sidebar (full-screen product/vendor editor overlays) must use these, never a literal.
var SIDENAV_NAV_W = 216;   // pinned sidebar + hover drawer (216 so 15px 'FBA Reconciliation' fits on one line)
var SIDENAV_RAIL_W = 56;   // unpinned icon rail
var SIDENAV = {
  ICON: 16,        // icon size in nav rows
  CHEVRON: 14,     // chevron-down icon size
  NAV_W: SIDENAV_NAV_W,
  RAIL_W: SIDENAV_RAIL_W,
  nav: { width: SIDENAV_NAV_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 10px', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden', zIndex: 10 },
  rail: { width: SIDENAV_RAIL_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, overflow: 'hidden', zIndex: 10 },
  drawer: function(hover) { return { position: 'fixed', top: 0, left: 0, bottom: 0, width: SIDENAV_NAV_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1100, boxShadow: hover ? '4px 0 16px rgba(0,0,0,0.1)' : 'none', opacity: hover ? 1 : 0, transition: 'opacity 0.15s ease, box-shadow 0.15s ease', pointerEvents: hover ? 'auto' : 'none' }; },
  logoBox: { padding: '0 12px 12px', borderBottom: '1px solid #E5E7EB', marginBottom: 10, textAlign: 'center', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoImg: { height: 44, maxWidth: '100%', objectFit: 'contain', display: 'block' },
  railLogoBox: { padding: '0 0 12px', borderBottom: '1px solid #E5E7EB', marginBottom: 10, width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 44 },
  railLogoImg: { width: 44, height: 44, objectFit: 'contain', borderRadius: 6 },
  sectionLabel: { padding: '0 10px 8px', fontSize: 10, fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.6 },
  scroller: { flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 },
  railList: { flex: 1, display: 'flex', flexDirection: 'column', width: '100%' },
  item: function(active) { return { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, lineHeight: '21px', fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 3, background: active ? 'rgba(79,110,247,0.10)' : 'transparent', color: active ? '#4F6EF7' : '#374151', transition: 'background 0.2s', textDecoration: 'none' }; },
  // minHeight 31 = item's 21px line + 10px padding, so a collapsed rail row sits on the same 34px pitch as an expanded row (9-05)
  railItem: function(active) { return { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0', minHeight: 31, boxSizing: 'border-box', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%', marginBottom: 3, background: active ? 'rgba(79,110,247,0.10)' : 'transparent', color: active ? '#4F6EF7' : '#374151', textDecoration: 'none' }; },
  chevron: function(expanded) { return { fontSize: 10, color: '#9CA3AF', transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }; },
  childWrap: function(expanded) { return { overflow: 'hidden', transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), margin 0.3s cubic-bezier(0.4,0,0.2,1)', maxHeight: expanded ? 500 : 0, paddingLeft: 18, marginBottom: expanded ? 4 : 0 }; },
  child: function(active) { return { display: 'flex', alignItems: 'center', gap: 7, padding: '4px 9px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 2, background: active ? 'rgba(79,110,247,0.08)' : 'transparent', color: active ? '#4F6EF7' : '#4B5563', borderLeft: active ? '2px solid #4F6EF7' : '2px solid transparent', textDecoration: 'none' }; },
  bottomWrap: { borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 8 },
  railBottomWrap: { borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 8, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  bottomBtn: function(opts) { opts = opts || {}; return { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', background: 'transparent', color: opts.active ? '#4F6EF7' : '#374151', textDecoration: 'none', marginTop: opts.topGap ? 4 : 0 }; },
  railBottomBtn: function(opts) { opts = opts || {}; return { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%', background: 'transparent', color: opts.active ? '#4F6EF7' : '#374151', textDecoration: 'none', marginTop: opts.topGap ? 4 : 0 }; },
  divider: { borderTop: '1px solid #E5E7EB', margin: '10px 0' },
  // the "opens a new tab" arrow (arrow-up-right, 13px) at the right edge of a sidebar row that is a
  // real target=_blank link: Customer Service / Advertising / Shipping in the main app, AtharvaToolz
  // in the three sub-app shells (Anthony 2026-10-13)
  newTab: { color: '#9CA3AF', flexShrink: 0, marginLeft: 'auto' },
};
// Hover/transition rules for the kit's rows — one source for the main SPA, ads and shipping
// shells (ads had no hover at all before 9-05). Rows opt in with className side-nav-btn /
// side-nav-child / side-bottom-btn.
(function() {
  if (typeof document === 'undefined' || document.getElementById('sidenav-kit-styles')) return;
  var st = document.createElement('style');
  st.id = 'sidenav-kit-styles';
  st.textContent =
    '.side-nav-btn{transition:background 0.15s ease!important}' +
    '.side-nav-btn:hover{background:rgba(79,110,247,0.06)!important}' +
    '.side-nav-child{transition:background 0.15s ease!important}' +
    '.side-nav-child:hover{background:rgba(79,110,247,0.06)!important}' +
    '.side-bottom-btn{transition:background 0.15s ease!important}' +
    '.side-bottom-btn:hover{background:rgba(0,0,0,0.04)!important}';
  document.head.appendChild(st);
})();

// ─── Responsive: phones and tablets (UX review item 1, 2026-09-27) ───
// Below VIEWPORT_NARROW_MAX every shell drops the pinned sidebar / 56px icon rail for a 48px
// top bar + hamburger drawer (NarrowNav below), and pages stack their card grids. Widths are
// the CSS px the LAYOUT sees: window.innerWidth is visual px and the body is zoomed by
// APP_ZOOM (index.html), so layout width = innerWidth / zoom. Wide (>= 900) renders exactly
// as before — every narrow branch is behind isNarrow, and the rg-* classes below only act
// under html.vp-narrow.
var VIEWPORT_NARROW_MAX = 900;
var VIEWPORT_PHONE_MAX = 600;
function viewportFlags(innerWidth, zoom) {
  var z = (typeof zoom === 'number' && isFinite(zoom) && zoom > 0) ? zoom : 1;
  // no usable width (hidden iframe, test harness): assume a desktop so nothing changes
  var w = (typeof innerWidth === 'number' && isFinite(innerWidth) && innerWidth > 0) ? innerWidth / z : 1280;
  return { width: w, isNarrow: w < VIEWPORT_NARROW_MAX, isPhone: w < VIEWPORT_PHONE_MAX };
}
function _readViewport() {
  return viewportFlags(typeof window !== 'undefined' ? window.innerWidth : 0, typeof window !== 'undefined' ? (window.APP_ZOOM || 1) : 1);
}
// One module-level listener (resize + orientationchange, throttled to 150 ms) feeds every
// useViewport() and keeps html.vp-narrow / html.vp-phone in sync for the CSS helpers.
var _vpState = _readViewport();
var _vpSubs = [];
function _vpApply() {
  var n = _readViewport();
  var changed = n.isNarrow !== _vpState.isNarrow || n.isPhone !== _vpState.isPhone;
  _vpState = n;
  try {
    var cl = document.documentElement.classList;
    cl.toggle('vp-narrow', n.isNarrow);
    cl.toggle('vp-phone', n.isPhone);
  } catch (e) {}
  if (changed) _vpSubs.slice().forEach(function(fn) { try { fn(n); } catch (e) {} });
}
(function() {
  if (typeof window === 'undefined' || !window.addEventListener) return;
  var timer = null;
  var onChange = function() {
    if (timer) return;
    timer = setTimeout(function() { timer = null; _vpApply(); }, 150);
  };
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  _vpApply();
})();
// Re-renders only when a breakpoint flag flips (not on every pixel of a resize drag), so
// `width` is the layout width at the last flip — use the flags for layout decisions.
function useViewport() {
  var _s = useState(_vpState); var vp = _s[0], setVp = _s[1];
  useEffect(function() {
    var fn = function(n) { setVp(n); };
    _vpSubs.push(fn);
    if (_vpState.isNarrow !== vp.isNarrow || _vpState.isPhone !== vp.isPhone) setVp(_vpState);
    return function() { var i = _vpSubs.indexOf(fn); if (i !== -1) _vpSubs.splice(i, 1); };
  }, []);
  return vp;
}
window.viewportFlags = viewportFlags;
window.useViewport = useViewport;
// Non-hook read for code that positions against the sidebar (full-screen editors): on narrow
// widths there is no sidebar, so they start at left 0.
function isNarrowViewport() { return !!_vpState.isNarrow; }
window.isNarrowViewport = isNarrowViewport;

// Top-bar title from document.title ("AtharvaToolz | PO Detail" → "PO Detail",
// "Batch 12 · AtharvaToolz Shipping" → "Batch 12").
function shortAppTitle(t) {
  var s = String(t || '').replace(/^AtharvaToolz\s*\|\s*/, '').replace(/\s*·\s*AtharvaToolz\b.*$/, '');
  if (/^AtharvaToolz\b/.test(s)) s = s.replace(/^AtharvaToolz\s*/, '');
  return s || 'AtharvaToolz';
}
function useDocTitle() {
  var _t = useState(function() { return typeof document !== 'undefined' ? document.title : ''; });
  var title = _t[0], setTitle = _t[1];
  useEffect(function() {
    var sync = function() { setTitle(document.title); };
    sync();
    var el = document.querySelector('title');
    if (!el || typeof MutationObserver === 'undefined') return;
    var mo = new MutationObserver(sync);
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return function() { mo.disconnect(); };
  }, []);
  return title;
}

// Narrow-width chrome shared by every shell: a 48px top bar (hamburger "Open Menu", the page
// title, the shell's own top-area controls in `right`) and the full sidebar content as a
// slide-in drawer over a dimmed backdrop. Backdrop tap, Escape, a tapped link, or any change of
// `navKey` (= navigation) closes it. The pin preference is never touched here.
var NARROW_TOPBAR_H = 48;
function narrowDrawerStyle() {
  return { position: 'fixed', top: 0, left: 0, bottom: 0, width: 288, maxWidth: 'calc(86vw * var(--invzoom, 1))', background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '14px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1160, boxShadow: '6px 0 24px rgba(0,0,0,0.25)', boxSizing: 'border-box', animation: 'narrowDrawerIn 0.18s ease' };
}
function NarrowNav(props) {
  var _o = useState(false); var open = _o[0], setOpen = _o[1];
  var docTitle = useDocTitle();
  useEffect(function() { setOpen(false); }, [props.navKey]);
  useEffect(function() {
    if (!open) return;
    var onKey = function(e) { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [open]);
  var close = function() { setOpen(false); };
  var title = props.title || shortAppTitle(docTitle);
  return React.createElement(React.Fragment, null,
    React.createElement('header', { className: 'narrow-topbar', style: { height: NARROW_TOPBAR_H, minHeight: NARROW_TOPBAR_H, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px 0 4px', background: C.surface, borderBottom: '1px solid ' + C.border, flexShrink: 0, boxSizing: 'border-box', position: 'relative', zIndex: 20 } },
      React.createElement('button', { type: 'button', 'aria-label': 'Open Menu', 'aria-expanded': open, onClick: function() { setOpen(true); },
        style: { width: 44, height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: C.text, padding: 0, flexShrink: 0 } },
        React.createElement(LIcon, { name: 'menu', size: 22 })),
      React.createElement('div', { style: { flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, title),
      props.right || null),
    open && React.createElement('div', { className: 'narrow-backdrop', onClick: close, 'aria-hidden': true, style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1150 } }),
    open && React.createElement('nav', { className: 'narrow-drawer', role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu',
      // a tapped link navigates (or opens a new tab): close; group headers are buttons and stay open
      onClickCapture: function(e) { var a = e.target && e.target.closest ? e.target.closest('a[href]') : null; if (a) setOpen(false); },
      style: narrowDrawerStyle() },
      React.createElement('button', { type: 'button', 'aria-label': 'Close Menu', onClick: close,
        style: { position: 'absolute', top: 8, right: 8, width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#6B7280', zIndex: 2 } },
        React.createElement(LIcon, { name: 'x', size: 18 })),
      props.drawer));
}
window.NarrowNav = NarrowNav;
window.shortAppTitle = shortAppTitle;
// Style of a shell's <main> on narrow widths: full width, 12px sides, no horizontal page
// scroll (tables keep their own scroll containers).
function narrowMainStyle(extra) {
  return Object.assign({ flex: 1, minWidth: 0, minHeight: 0, width: '100%', padding: 12, boxSizing: 'border-box', overflowX: 'hidden', overflowY: 'auto' }, extra || {});
}
window.narrowMainStyle = narrowMainStyle;
(function() {
  if (typeof document === 'undefined' || document.getElementById('narrow-layout-styles')) return;
  var st = document.createElement('style');
  st.id = 'narrow-layout-styles';
  st.textContent =
    '@keyframes narrowDrawerIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}' +
    // touch targets: every drawer row at least 40px tall
    '.narrow-drawer .side-nav-btn,.narrow-drawer .side-nav-child,.narrow-drawer .side-bottom-btn,.narrow-drawer .snav{min-height:40px!important;box-sizing:border-box}' +
    // open groups: the 500px max-height cap (SIDENAV.childWrap) would clip taller 40px rows
    '.narrow-drawer div[style*="max-height: 500px"]{max-height:none!important}' +
    // page helpers — only under html.vp-narrow, so wide layouts are untouched
    '.vp-narrow .rg-stack{grid-template-columns:minmax(0,1fr)!important}' +
    '.vp-narrow .rg-2x2{grid-template-columns:repeat(2,minmax(0,1fr))!important}' +
    '.vp-narrow .rg-wrap{flex-wrap:wrap!important}' +
    '.vp-narrow .rg-scrollx{overflow-x:auto!important;overflow-y:hidden!important}' +
    // tabs keep their width and their underline (the -2px overlap would be clipped by the scroller)
    '.vp-narrow .rg-scrollx>*{flex-shrink:0!important;white-space:nowrap!important;margin-bottom:0!important}';
  document.head.appendChild(st);
})();

// C is a mutable reference that always points to current theme
var C = Object.assign({}, themes.dark);
var _currentTheme = 'dark';

function setThemeColors(name) {
  _currentTheme = name;
  // Expose the active theme on <html> so plain CSS can adapt (e.g. scrollbar colors: white in
  // dark mode, dark in light mode).
  try { document.documentElement.setAttribute('data-theme', name); } catch (e) {}
  var t = themes[name] || themes.dark;
  Object.keys(t).forEach(function(k) { C[k] = t[k]; });
  // Update statusColors in-place
  var sc = {
    draft: { bg: C.warningSoft, text: C.warning },
    confirmed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
    shipped: { bg: C.accentSoft, text: C.accent },
    'checked-in': { bg: C.purpleSoft, text: C.purple },
    received: { bg: C.successSoft, text: C.success },
    closed: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted },
    cancelled: { bg: C.dangerSoft, text: C.danger },
  };
  Object.keys(sc).forEach(function(k) { statusColors[k] = sc[k]; });
  _updateDtStyles();
}

// Inject interactive styles for DataTable hover/focus effects
var _dtStyleEl = document.createElement('style');
_dtStyleEl.id = 'dt-interactive-styles';
document.head.appendChild(_dtStyleEl);
function _updateDtStyles() {
  _dtStyleEl.textContent = [
    '.dt-input:focus { border-color: ' + C.accent + ' !important; box-shadow: 0 0 0 2px ' + C.accentSoft + ' !important; outline: none; }',
    '.dt-select:hover, .dt-btn:hover { border-color: ' + C.accent + ' !important; background: ' + C.surfaceHover + ' !important; }',
    '.dt-select:focus { border-color: ' + C.accent + ' !important; box-shadow: 0 0 0 2px ' + C.accentSoft + ' !important; outline: none; }',
    '.dt-row-btn:hover { background: ' + C.surfaceHover + ' !important; }',
    '.dt-th:hover { color: ' + C.accent + ' !important; }',
    '.dt-filter-btn:hover { background: ' + C.surfaceHover + ' !important; opacity: 1 !important; }',
    '.filter-pill:hover { background: ' + C.accentSoft + ' !important; border-color: ' + C.accent + ' !important; }',
  ].join('\n');
}
_updateDtStyles();

function getThemeName() { return _currentTheme; }

function getStatusColors() {
  return {
    draft: { bg: C.warningSoft, text: C.warning },
    submitted: { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
    approved: { bg: 'rgba(34,197,94,0.12)', text: '#22C55E' },
    confirmed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
    next: { bg: 'rgba(6,182,212,0.12)', text: '#06B6D4' },
    shipped: { bg: C.accentSoft, text: C.accent },
    'checked-in': { bg: C.purpleSoft, text: C.purple },
    receiving: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
    received: { bg: C.successSoft, text: C.success },
    closed: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted },
    cancelled: { bg: C.dangerSoft, text: C.danger },
  };
}

var statusColors = getStatusColors();

// Auth token management (cookie-based — kept for backward compat during transition)
var _authToken = null;
// Auth is cookie-based (httpOnly po_session, sent via credentials:'include'). The token is
// kept in-memory only — never in localStorage, where XSS could exfiltrate it. We also clear
// any token persisted by older builds.
function setAuthToken(token) { _authToken = token; try { localStorage.removeItem('po_token'); } catch (e) {} }
function getAuthToken() { return _authToken; }

async function api(path, options = {}) {
  var method = (options && options.method) || 'GET';
  var headers = { 'Content-Type': 'application/json', ...options.headers };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    setAuthToken(null); window.location.reload();
    // reload() is async — never resolve, so pending .then(setState) chains can't run
    // with undefined during the teardown window (crashed the PO list — error_log 295).
    return new Promise(function() {});
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || 'Request failed');
    err.apiPath = path; err.apiMethod = method; err.apiStatus = res.status;
    err.apiBody = errBody;
    throw err;
  }
  return res.json();
}

async function apiUpload(path, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body: form, credentials: 'include' });
  if (res.status === 401) {
    setAuthToken(null); window.location.reload();
    // reload() is async — never resolve, so pending .then(setState) chains can't run
    // with undefined during the teardown window (crashed the PO list — error_log 295).
    return new Promise(function() {});
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error(errBody.error || 'Upload failed');
    err.apiPath = path; err.apiMethod = 'POST'; err.apiStatus = res.status;
    throw err;
  }
  return res.json();
}

// Permission helpers
function hasPermission(user, area, level) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  var perms = user.permissions;
  if (!perms || !perms[area]) return false;
  return !!perms[area][level || 'read'];
}
// Legacy role helpers (now permission-aware)
function canEdit(roleOrUser) {
  if (typeof roleOrUser === 'string') return ['admin', 'manager'].includes(roleOrUser);
  return hasPermission(roleOrUser, 'purchase_orders', 'write');
}
function canManageUsers(roleOrUser) {
  if (typeof roleOrUser === 'string') return roleOrUser === 'admin';
  return hasPermission(roleOrUser, 'settings', 'write');
}
function canUpdatePOStatus(roleOrUser, newStatus) {
  if (typeof roleOrUser === 'string') {
    if (roleOrUser === 'admin' || roleOrUser === 'manager') return true;
    if (roleOrUser === 'warehouse' && ['checked-in', 'received'].includes(newStatus)) return true;
    return false;
  }
  return hasPermission(roleOrUser, 'purchase_orders', 'write');
}

function Badge({ status }) {
  var sc = getStatusColors();
  const c = sc[status] || sc.draft || { bg: C.warningSoft, text: C.warning };
  return React.createElement('span', {
    style: { background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }
  }, status);
}

function Btn({ children, variant = 'default', size = 'md', onClick, disabled, style, className }) {
  const base = { border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s', fontFamily: 'inherit', opacity: disabled ? 0.5 : 1 };
  const sizes = { sm: { padding: '6px 12px', fontSize: 12, borderRadius: 6 }, md: { padding: '8px 16px', fontSize: 13, borderRadius: 8 } };
  const variants = {
    default: { background: C.surface, color: C.text, border: '1px solid ' + C.border },
    primary: { background: C.accent, color: '#fff' },
    danger: { background: C.dangerSoft, color: C.danger },
    ghost: { background: 'transparent', color: C.textMuted },
    success: { background: C.successSoft, color: C.success },
  };
  var v = variant || 'default';
  var hoverHandlers = disabled ? {} : {
    onMouseOver: function(e) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; },
    onMouseOut: function(e) { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }
  };
  return React.createElement('button', { onClick, disabled, className, ...hoverHandlers, style: { ...base, ...sizes[size], ...variants[v], ...style } }, children);
}

// Per-SKU "max units per PO" (products.max_po_qty) → the largest whole-case
// multiple at or below it; a max under one case still allows one case. Mirrors
// server/services/po-cap.js — keep the two in sync. Returns null when unset.
function effectivePoCap(maxPoQty, caseQty) {
  var max = parseInt(maxPoQty) || 0;
  if (max <= 0) return null;
  var cq = parseInt(caseQty) || 1;
  if (cq <= 1) return max;
  var whole = Math.floor(max / cq) * cq;
  return whole > 0 ? whole : cq;
}

function Inp({ label, style: extraStyle, ...props }) {
  var baseStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' };
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    label && React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    React.createElement('input', {
      // Keep Chrome's password manager out of credential/settings fields — 'new-password'
      // is the only value it reliably honors on password inputs. Login uses its own inputs.
      autoComplete: props.type === 'password' ? 'new-password' : 'off',
      style: Object.assign({}, baseStyle, extraStyle || {}),
      onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
      onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
      ...props
    })
  );
}

function Sel({ label, options, value, onChange }) {
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    label && React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    React.createElement('select', {
      style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' },
      onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
      onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
      value: value || '', onChange
    },
      React.createElement('option', { value: '' }, '— Select —'),
      options.map(o => React.createElement('option', { key: o.value, value: o.value }, o.label))
    )
  );
}

// ─── SEARCHABLE SELECT ───
// Custom dropdown with a type-to-filter box. Native <select> popups misbehave under the
// app-wide body zoom (Chrome keeps snapping the popup's scroll back to the selected option
// on long lists), so browsable pickers use this instead — the list is our own scroll
// container, unaffected by zoom. Hover moves the highlight but never scrolls the list;
// only keyboard navigation scrolls, so wheel-scrolling is never fought over.
// Props: options [{value, label, sub?}], value, onChange(value), placeholder, disabled, style
function SearchSelect({ options, value, onChange, placeholder, disabled, style: extraStyle }) {
  var _o = React.useState(false); var open = _o[0], setOpen = _o[1];
  var _s = React.useState(''); var search = _s[0], setSearch = _s[1];
  var _h = React.useState(-1); var highlight = _h[0], setHighlight = _h[1];
  var rootRef = React.useRef(null);
  var listRef = React.useRef(null);
  var inputRef = React.useRef(null);

  var selected = null;
  for (var i = 0; i < options.length; i++) {
    if (String(options[i].value) === String(value)) { selected = options[i]; break; }
  }

  // Rank matches: labels starting with the query first, then any word starting with it,
  // then substring matches anywhere (label or sub) — so typing "v" jumps straight to the
  // V's instead of surfacing every name that merely contains a v.
  var q = search.trim().toLowerCase();
  var filtered;
  if (!q) {
    filtered = options;
  } else {
    var starts = [], wordStarts = [], contains = [];
    options.forEach(function(o) {
      var label = String(o.label).toLowerCase();
      if (label.indexOf(q) === 0) { starts.push(o); return; }
      var words = label.split(/[^a-z0-9]+/);
      for (var w = 0; w < words.length; w++) {
        if (words[w] && words[w].indexOf(q) === 0) { wordStarts.push(o); return; }
      }
      if (label.indexOf(q) !== -1 || (o.sub && String(o.sub).toLowerCase().indexOf(q) !== -1)) contains.push(o);
    });
    filtered = starts.concat(wordStarts, contains);
  }

  React.useEffect(function() {
    if (!open) return;
    var onDown = function(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return function() { document.removeEventListener('mousedown', onDown); };
  }, [open]);

  // On open: focus the filter box and bring the current selection into view
  React.useEffect(function() {
    if (!open) return;
    if (inputRef.current) inputRef.current.focus();
    var idx = -1;
    for (var j = 0; j < options.length; j++) {
      if (String(options[j].value) === String(value)) { idx = j; break; }
    }
    setHighlight(idx);
    if (idx >= 0 && listRef.current) {
      var el = listRef.current.querySelector('[data-idx="' + idx + '"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  var pick = function(opt) {
    setOpen(false); setSearch('');
    if (onChange) onChange(opt.value);
  };

  var moveHighlight = function(delta) {
    if (!filtered.length) return;
    var next = Math.max(0, Math.min(filtered.length - 1, highlight + delta));
    setHighlight(next);
    if (listRef.current) {
      var el = listRef.current.querySelector('[data-idx="' + next + '"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  };
  var onKeyDown = function(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && filtered[highlight]) pick(filtered[highlight]);
      else if (filtered.length === 1) pick(filtered[0]);
    }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setSearch(''); }
  };

  var triggerStyle = Object.assign({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
    background: C.bg, border: '1px solid ' + (open ? C.accent : C.border), color: C.text,
    padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', textAlign: 'left',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
    boxShadow: open ? '0 0 0 2px ' + C.accent + '33' : 'none', transition: 'border-color 0.15s, box-shadow 0.15s'
  }, extraStyle || {});

  return React.createElement('div', { ref: rootRef, style: { position: 'relative', minWidth: 0 } },
    React.createElement('button', {
      type: 'button', disabled: disabled,
      onClick: function() { if (!disabled) { setOpen(!open); setSearch(''); } },
      style: triggerStyle
    },
      React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? C.text : C.textDim } },
        selected ? selected.label : (placeholder || 'Select…')),
      React.createElement(LIcon, { name: 'chevron-down', size: 14, color: C.textMuted })
    ),
    open && React.createElement('div', {
      style: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 1200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', overflow: 'hidden' }
    },
      React.createElement('input', {
        ref: inputRef, value: search,
        onChange: function(e) { setSearch(e.target.value); setHighlight(0); },
        onKeyDown: onKeyDown,
        placeholder: 'Type to search…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }
      }),
      React.createElement('div', { ref: listRef, style: { maxHeight: 280, overflowY: 'auto' } },
        filtered.length === 0
          ? React.createElement('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, 'No matches')
          : filtered.map(function(o, idx) {
              var isSel = selected && String(o.value) === String(selected.value);
              return React.createElement('div', {
                key: o.value, 'data-idx': idx,
                onClick: function() { pick(o); },
                onMouseEnter: function() { setHighlight(idx); },
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: isSel ? C.accent : C.text, background: idx === highlight ? C.surfaceHover : 'transparent' }
              },
                React.createElement('div', { style: { minWidth: 0, flex: 1 } },
                  React.createElement('div', { style: { fontWeight: isSel ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.label),
                  o.sub ? React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.sub) : null
                ),
                isSel ? React.createElement(LIcon, { name: 'check', size: 14, color: C.accent }) : null
              );
            })
      )
    )
  );
}

function Card({ children, style, onClick }) {
  return React.createElement('div', {
    onClick,
    style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 20, boxShadow: C.cardShadow, ...style }
  }, children);
}

// ─── In-app dialogs (Anthony 2026-09-29: "for any confirmation toasts can we make them system
// specific in the center of the screen instead of the chrome default?"): promise-based replacements
// for window.confirm / prompt / alert, drawn like the app's own Modal and hosted once per shell by
// AppDialogHost. Enter = OK, Escape = cancel, the prompt's input autofocuses; several requests queue
// one after another. With no host mounted (a bare print window, a test) they fall back to the
// browser dialogs so nothing can hang.
//   appConfirm(message, { title, okLabel, cancelLabel, danger })  -> Promise<boolean>
//   appPrompt(message, defaultValue, { title, okLabel, placeholder }) -> Promise<string|null>
//   appAlert(message, { title })                                  -> Promise<void>
//   appChoice(message, ['Label', ...], { title, cancelLabel })     -> Promise<index | -1>
var _appDialogQueue = [];
var _appDialogSetter = null;
function _appDialogNext() { if (_appDialogSetter) _appDialogSetter(_appDialogQueue[0] || null); }
function _appDialog(kind, message, opts) {
  opts = opts || {};
  var text = String(message == null ? '' : message);
  if (!_appDialogSetter) {
    if (kind === 'confirm') return Promise.resolve(window.confirm(text));
    if (kind === 'prompt') return Promise.resolve(window.prompt(text, opts.defaultValue == null ? '' : String(opts.defaultValue)));
    window.alert(text);
    return Promise.resolve(kind === 'choice' ? -1 : undefined);
  }
  return new Promise(function(resolve) {
    var done = false;
    var item = { kind: kind, message: text, opts: opts, resolve: function(v) {
      if (done) return; done = true;
      var at = _appDialogQueue.indexOf(item); if (at !== -1) _appDialogQueue.splice(at, 1);
      resolve(v); _appDialogNext();
    } };
    _appDialogQueue.push(item);
    if (_appDialogQueue.length === 1) _appDialogNext();
  });
}
function appConfirm(message, opts) { return _appDialog('confirm', message, opts); }
function appPrompt(message, defaultValue, opts) { return _appDialog('prompt', message, Object.assign({ defaultValue: defaultValue }, opts || {})); }
function appAlert(message, opts) { return _appDialog('alert', message, opts); }
function appChoice(message, choices, opts) { return _appDialog('choice', message, Object.assign({ choices: choices || [] }, opts || {})); }
function AppDialogHost() {
  var ds = useState(null); var d = ds[0], setD = ds[1];
  var vs = useState(''); var value = vs[0], setValue = vs[1];
  var cardRef = useRef(null);
  var prevFocusRef = useRef(null); // what had the focus before the dialog: it gets it back afterwards
  useEffect(function() { _appDialogSetter = setD; _appDialogNext(); return function() { if (_appDialogSetter === setD) _appDialogSetter = null; }; }, []);
  useEffect(function() {
    if (!d) {
      var back = prevFocusRef.current; prevFocusRef.current = null;
      if (back && back.isConnected && typeof back.focus === 'function') { try { back.focus(); } catch (e) {} }
      return;
    }
    var act = document.activeElement;
    if (!prevFocusRef.current && act && act !== document.body && !(act.closest && act.closest('[data-app-dialog]'))) prevFocusRef.current = act;
    if (d.kind === 'prompt') setValue(d.opts.defaultValue == null ? '' : String(d.opts.defaultValue));
    var el = cardRef.current; if (!el) return;
    var btns = el.querySelectorAll('button');
    var f = el.querySelector('input') || (d.kind === 'choice' ? btns[1] : btns[btns.length - 1]) || el;
    setTimeout(function() { try { f.focus(); if (f.select) f.select(); } catch (e) {} }, 0);
  }, [d]);
  if (!d) return null;
  var o = d.opts;
  var cancel = function() { d.resolve(d.kind === 'confirm' ? false : d.kind === 'prompt' ? null : d.kind === 'choice' ? -1 : undefined); };
  var ok = function() { d.resolve(d.kind === 'confirm' ? true : d.kind === 'prompt' ? value : undefined); };
  var title = o.title || (d.kind === 'alert' ? 'Notice' : d.kind === 'prompt' ? 'Enter a value' : 'Please confirm');
  // an OK button that reads "Cancel order" beside a plain "Cancel" is a trap: the way out says "Go back"
  var cancelText = o.cancelLabel || (/^cancel/i.test(o.okLabel || '') ? 'Go back' : 'Cancel');
  var onKey = function(e) {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    else if (e.key === 'Enter' && d.kind !== 'choice' && !(e.target && e.target.tagName === 'BUTTON' && e.target !== cardRef.current.querySelectorAll('button')[cardRef.current.querySelectorAll('button').length - 1])) { e.preventDefault(); ok(); }
  };
  // A choice keeps every button on ONE line (the card grows to fit them) and can ask for a red
  // Cancel (`cancelDanger`) — Anthony 2026-09-29, the case-pack question.
  var oneLine = { whiteSpace: 'nowrap', flexShrink: 0 };
  var buttons = d.kind === 'choice'
    ? [React.createElement(Btn, { key: 'c', variant: o.cancelDanger ? 'danger' : 'default', onClick: cancel, style: oneLine }, cancelText)].concat((o.choices || []).map(function(label, i) {
        return React.createElement(Btn, { key: 'b' + i, variant: i === 0 ? 'primary' : 'default', onClick: function() { d.resolve(i); }, style: oneLine }, label);
      }))
    : [d.kind !== 'alert' && React.createElement(Btn, { key: 'c', variant: o.cancelDanger ? 'danger' : 'default', onClick: cancel }, cancelText),
       React.createElement(Btn, { key: 'ok', variant: o.danger ? 'danger' : 'primary', onClick: ok }, o.okLabel || 'OK')];
  return React.createElement('div', {
    // data-app-dialog: the pages' click-away / blur auto-saves treat anything inside as "not away"
    'data-app-dialog': '',
    style: { position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    onMouseDown: function(e) { if (e.target === e.currentTarget && d.kind !== 'alert') cancel(); }
  },
    // No focus ring on the dialog's buttons at all (Anthony 2026-09-29: "little circles around Round
    // Up", then "we don't need a ring around that button"). Enter and Escape still work, and the
    // default button is the filled one.
    React.createElement('style', null, '[data-app-dialog] button,[data-app-dialog] button:focus,[data-app-dialog] button:focus-visible{outline:none;box-shadow:none}'),
    React.createElement('div', { ref: cardRef, role: 'dialog', 'aria-modal': true, tabIndex: -1, onKeyDown: onKey, onMouseDown: function(e) { e.stopPropagation(); },
      style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: d.kind === 'choice' ? 'max-content' : 440, minWidth: d.kind === 'choice' ? 440 : undefined, maxWidth: 'calc(100vw - 40px)', padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.45)', outline: 'none' } },
      React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 } }, title),
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '50vh', overflowY: 'auto' } }, d.message),
      d.kind === 'prompt' && React.createElement('div', { style: { marginTop: 12 } },
        React.createElement(Inp, { value: value, placeholder: o.placeholder || '', onChange: function(e) { setValue(e.target.value); }, style: { width: '100%' } })),
      // no overflow on this row: a scrolling row clips the focus ring top and bottom and leaves only
      // its rounded ends showing (the "little circles"); a choice wraps only on a phone-width screen
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: d.kind === 'choice' && window.innerWidth >= 600 ? 'nowrap' : 'wrap' } }, buttons)
    )
  );
}

// Escape closes the top-most open modal (polish 2026-10-11; the Orders detail modal only had the X
// and the backdrop). The stack keeps a dialog opened on top of a modal (appConfirm inside Order
// Details) from closing both: only the last-opened modal's listener acts, a key a dialog or a
// picker already handled (defaultPrevented) is left alone, and a key pressed on a <select> is
// ignored because Escape there closes its dropdown.
var _modalStack = [];
function Modal({ open, onClose, title, width, maxHeight, children }) {
  var onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  useEffect(function() {
    if (!open) return;
    var me = {};
    _modalStack.push(me);
    var onKey = function(e) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.target && e.target.tagName === 'SELECT') return;
      if (_modalStack[_modalStack.length - 1] !== me) return;
      var close = onCloseRef.current;
      if (!close) return;
      // The apps mount through the legacy ReactDOM.render root, where state set from a native key
      // listener renders after EACH setter: a close that clears the selected order and then its
      // detail rendered in between with the order gone and the detail still there (crash on the
      // first live Escape). One render for the whole close, as a React event handler would get.
      var batched = typeof ReactDOM !== 'undefined' && ReactDOM && ReactDOM.unstable_batchedUpdates;
      if (batched) batched(function() { close(); }); else close();
    };
    document.addEventListener('keydown', onKey);
    return function() {
      document.removeEventListener('keydown', onKey);
      var i = _modalStack.indexOf(me);
      if (i >= 0) _modalStack.splice(i, 1);
    };
  }, [open]);
  if (!open) return null;
  return React.createElement('div', {
    style: { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    onMouseDown: function(e) { if (e.target === e.currentTarget) onClose(); }
  },
    React.createElement('div', {
      style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: width || 560, maxWidth: 'calc(calc(100vw * var(--invzoom, 1)) - 40px)', maxHeight: maxHeight || 'calc(85vh * var(--invzoom, 1))', overflowX: 'hidden', overflowY: 'auto', padding: 24, boxSizing: 'border-box' },
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
        React.createElement('h3', { style: { margin: 0, fontSize: 16, fontWeight: 600, color: C.text } }, title),
        React.createElement('button', { onClick: onClose,
          onMouseOver: function(e) { e.currentTarget.style.background = C.danger + '20'; e.currentTarget.style.color = C.danger; },
          onMouseOut: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; },
          style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 6, borderRadius: 6, transition: 'all 0.15s' } }, React.createElement(LIcon, { name: 'x', size: 18 }))
      ),
      children
    )
  );
}

// fill: the empty state takes the table's remaining height and centres in it (a fill-height table,
// Anthony 2026-09-23: "center the message")
function Empty({ title, sub, fill }) {
  return React.createElement('div', { style: Object.assign({ textAlign: 'center', padding: '60px 20px', color: C.textDim },
    fill ? { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' } : {}) },
    React.createElement('div', { style: { fontSize: 15, fontWeight: 500, color: C.textMuted, marginBottom: sub ? 4 : 0 } }, title),
    sub ? React.createElement('div', { style: { fontSize: 13 } }, sub) : null
  );
}

function Toast({ message, type, onClose }) {
  // Per-type timeout: errors and warnings carry data the user may need to read/copy,
  // so they stay longer than success toasts.
  var timeout = type === 'error' ? 12000 : type === 'warning' ? 7000 : 3500;
  useEffect(() => { const t = setTimeout(onClose, timeout); return () => clearTimeout(t); }, [onClose, timeout]);
  var toastRef = React.useRef(null);
  // Errors do NOT close on outside-click \u2014 Paula needs time to read/copy before clicking elsewhere.
  useEffect(() => {
    if (type === 'error') return;
    var handler = function(e) { if (toastRef.current && toastRef.current.contains(e.target)) return; onClose(); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onClose, type]);
  // Detect MSKU-like tokens in error/warning messages (2-5 caps, dash, alphanumeric with
  // optional segments). Match real seller SKUs like ALRE-852, MIAM-105699-3PK, VDIM-142.
  var skus = (type === 'error' || type === 'warning') && typeof message === 'string'
    ? Array.from(new Set(message.match(/\b[A-Z]{2,5}-[A-Z0-9]+(?:-[A-Z0-9]+){0,3}\b/g) || []))
    : [];
  var [copied, setCopied] = useState(false);
  var doCopy = function(e) {
    e.stopPropagation();
    if (!skus.length) return;
    var text = skus.join(', ');
    var done = function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(function() {});
    } else {
      var ta = document.createElement('textarea'); ta.value = text;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (e2) {}
      document.body.removeChild(ta);
    }
  };
  const bg = type === 'error' ? C.dangerSoft : type === 'warning' ? C.warningSoft : C.successSoft;
  const clr = type === 'error' ? C.danger : type === 'warning' ? C.warning : C.success;
  return React.createElement('div', { ref: toastRef,
    style: { position: 'fixed', top: 20, right: 20, zIndex: 2000, background: bg, color: clr, padding: '12px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid ' + clr + '33', boxShadow: '0 8px 30px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 600 }
  }, React.createElement('span', { style: { wordBreak: 'break-word' } }, message),
    skus.length > 0 && React.createElement('button', {
      onClick: doCopy, title: 'Copy SKUs: ' + skus.join(', '),
      style: { background: clr + '22', border: '1px solid ' + clr + '55', color: clr, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit' }
    }, copied ? 'Copied!' : 'Copy ' + skus.length + ' SKU' + (skus.length > 1 ? 's' : '')),
    React.createElement('button', { onClick: onClose,
      style: { background: 'none', border: 'none', color: clr, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, opacity: 0.7, flexShrink: 0 }
    }, '\u00D7')
  );
}

function TH({ children, style }) {
  return React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', ...style } }, children);
}

function TD({ children, style, onClick }) {
  return React.createElement('td', { style: { padding: '8px 12px', wordBreak: 'break-word', ...style }, onClick: onClick }, children);
}

// Column preferences — loaded from server, saved back on change. Saves send
// ONLY the keys that changed since load (diff vs baseline): the main app and
// the ship/ads apps each hold a copy, and full-object saves let a stale copy
// revert the other app's layouts.
var _colPrefs = {};
var _colPrefsBaseline = {};
var _colPrefsLoaded = false;
var _colPrefsSaveTimer = null;

function loadColumnPrefs() {
  if (_colPrefsLoaded) return Promise.resolve(_colPrefs);
  return fetch('/api/auth/preferences', { credentials: 'include' })
    .then(function(r) { return r.ok ? r.json() : {}; })
    .then(function(prefs) {
      _colPrefs = (prefs && prefs.columns) || {};
      try { _colPrefsBaseline = JSON.parse(JSON.stringify(_colPrefs)); } catch (e) { _colPrefsBaseline = {}; }
      _colPrefsLoaded = true;
      return _colPrefs;
    })
    .catch(function() { return {}; });
}

function saveColumnPrefs() {
  clearTimeout(_colPrefsSaveTimer);
  _colPrefsSaveTimer = setTimeout(function() {
    var dirty = {};
    Object.keys(_colPrefs).forEach(function(k) {
      if (JSON.stringify(_colPrefs[k]) !== JSON.stringify(_colPrefsBaseline[k])) dirty[k] = _colPrefs[k];
    });
    // Deleted keys (e.g. reset column order) go up as null — loaders treat
    // null as unset, and the server merge keeps explicit nulls
    Object.keys(_colPrefsBaseline).forEach(function(k) {
      if (!(k in _colPrefs)) dirty[k] = null;
    });
    if (!Object.keys(dirty).length) return;
    fetch('/api/auth/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ columns: dirty })
    }).then(function(r) {
      if (!r.ok) return;
      Object.keys(dirty).forEach(function(k) {
        if (dirty[k] === null) delete _colPrefsBaseline[k];
        else { try { _colPrefsBaseline[k] = JSON.parse(JSON.stringify(dirty[k])); } catch (e) {} }
      });
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, 500);
}

function resetColumnPrefsCache() {
  _colPrefs = {};
  _colPrefsLoaded = false;
}

// scrollMode (opt-in): fixed-height viewport with scroll-to-load-more instead of page
// buttons — pageSize becomes the batch loaded per scroll (client mode only).
// compact (opt-in): smaller cell + header type for dense side-panel tables.
// enableViews (opt-in, needs storageKey): "View: Default" dropdown in the toolbar — saves
// the current table state (column filters, sort, order, visibility, page size) as a named
// per-user view; the active view is remembered and re-applied on the next visit.
// ── Column filter kinds (2026-09-05) ─────────────────────────────────────────────
// Every column filters as one of: 'list' (checkbox list of the actual values), 'number'
// (comparisons only), 'date' (on / before / after) or 'text' (contains / equals / …).
// A column can pin its kind with `filterKind`; `filterOptions` (array or 'auto') implies
// 'list'. Otherwise the kind is inferred from the data. Server-paged tables only see one
// page, so they never infer 'list' — pass `filterOptions` explicitly for those.
var DT_IN_SEP = '\u001f'; // 'in' filter separator (values may contain commas); legacy = ','
function _dtSplitIn(v) { v = v || ''; return (v.indexOf(DT_IN_SEP) !== -1 ? v.split(DT_IN_SEP) : v.split(',')).map(function(x) { return x.trim(); }).filter(function(x) { return x !== ''; }); }
function _dtColValue(col, row) {
  try {
    if (col.filterValue) return col.filterValue(row);
    if (row[col.key] !== undefined) return row[col.key];
    if (col.sortValue) return col.sortValue(row);
    if (col.render) { var r = col.render(row); if (typeof r === 'string' || typeof r === 'number') return r; }
  } catch (e) {}
  return undefined;
}
function _dtIsNumLike(v) {
  if (typeof v === 'number') return !isNaN(v);
  if (typeof v !== 'string') return false;
  return /^[-+]?\$?\s?[\d,]*\.?\d+\s?%?$/.test(v.trim());
}
function _dtToNum(v) {
  if (typeof v === 'number') return v;
  if (v == null) return NaN;
  var s = String(v).replace(/[^0-9.\-]/g, '');
  return s === '' || s === '-' || s === '.' ? NaN : Number(s);
}
// Numeric column-filter comparison (gt/gte/lt/lte/eq). Both sides go through
// _dtToNum so "$1,234" / "12%" compare like the displayed numbers. A missing
// value (null/'') is NOT zero and never satisfies a numeric comparison.
function _dtNumCompare(mode, rawVal, search) {
  search = search == null ? '' : String(search).trim();
  if (search === '' || rawVal == null || rawVal === '') return false;
  var n = _dtToNum(rawVal), s = _dtToNum(search);
  if (isNaN(n) || isNaN(s)) return false;
  switch (mode) {
    case 'gt': return n > s;
    case 'gte': return n >= s;
    case 'lt': return n < s;
    case 'lte': return n <= s;
    case 'eq': return n === s;
    default: return false;
  }
}
window._dtNumCompare = _dtNumCompare;
function _dtIsDateLike(v) {
  if (v instanceof Date) return !isNaN(v);
  if (typeof v !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(v.trim()) && !isNaN(new Date(v).getTime());
}
// Calendar day as YYYYMMDD, matching the day the cell shows. A date-only value (YYYY-MM-DD, the
// pg DATE -> JSON round-trip at UTC midnight, or a naive date-time with no zone) uses its leading
// date, the way fmtDateOnly prints it; a zoned timestamp (TIMESTAMPTZ, "...T23:30:00.000Z") uses
// the viewer's local day, the way the date cells print it. Review 2026-09-28, medium #30: zoned
// timestamps were filtered on their UTC day, so after 8 PM ET "on today" missed rows the table
// showed under today.
function _dtToDay(v) {
  if (v == null || v === '') return null;
  var s = typeof v === 'string' ? v.trim() : v;
  var m = typeof s === 'string' && s.match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/);
  if (m) {
    var zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(m[4]);
    var utcMidnight = /^[T ]00:00:00(\.0+)?(Z|\+00:?00)$/i.test(m[4]);
    if (!zoned || utcMidnight) return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
  }
  var d = s instanceof Date ? s : new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}
function _dtModesForKind(kind) {
  if (kind === 'list') return ['in'];
  if (kind === 'number') return ['eq', 'gt', 'gte', 'lt', 'lte', 'blank', 'not_blank'];
  if (kind === 'date') return ['on', 'before', 'after', 'blank', 'not_blank'];
  return ['contains', 'equals', 'starts', 'ends', 'blank', 'not_blank'];
}
function _dtInferFilterKind(col, rows, serverMode) {
  if (col.filterKind) return col.filterKind;
  if (col.filterOptions) return 'list';
  var vals = [];
  for (var i = 0; i < rows.length && vals.length < 400; i++) {
    var v = _dtColValue(col, rows[i]);
    if (v != null && String(v).trim() !== '') vals.push(v);
  }
  if (!vals.length) return 'text';
  var nums = 0, dates = 0, seen = {}, distinct = 0;
  vals.forEach(function(v) {
    if (_dtIsNumLike(v)) nums++;
    else if (_dtIsDateLike(v)) dates++;
    var k = String(v); if (!seen[k]) { seen[k] = true; distinct++; }
  });
  if (nums / vals.length >= 0.9) return 'number';
  if (dates / vals.length >= 0.9) return 'date';
  if (!serverMode && distinct <= 200 && distinct <= Math.max(12, vals.length * 0.5)) return 'list';
  return 'text';
}
function _dtListOptions(col, rows) {
  if (Array.isArray(col.filterOptions)) return col.filterOptions.map(function(o) { return String(o); });
  var seen = {}, out = [];
  for (var i = 0; i < rows.length; i++) {
    var v = _dtColValue(col, rows[i]);
    if (v == null) continue;
    var s = String(v).trim(); if (s === '' || seen[s]) continue;
    seen[s] = true; out.push(s);
    if (out.length >= 500) break;
  }
  var allNum = out.length > 0 && out.every(_dtIsNumLike);
  out.sort(allNum ? function(a, b) { return _dtToNum(a) - _dtToNum(b); } : function(a, b) { return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }); });
  return out;
}

// ── Invisible reCAPTCHA v2 → one-shot token for Firebase text-message codes (2026-09-05) ──
// Google will not text a verification code without proof the request came from a browser.
// The site key comes from the server (Firebase project); the widget renders once into a
// fixed host div, is reset before every execute, and its badge is hidden (attribution text
// is shown next to the phone step instead, per Google's terms).
var _rcLoad = null, _rcWidget = null, _rcSiteKey = null, _rcPending = null;
function loadRecaptchaApi() {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (_rcLoad) return _rcLoad;
  _rcLoad = new Promise(function(resolve, reject) {
    var cb = '__rcOnload' + Date.now();
    var timer = setTimeout(function() { _rcLoad = null; reject(new Error('The verification widget did not load. Check your connection or use email.')); }, 12000);
    window[cb] = function() { clearTimeout(timer); resolve(); };
    if (!document.getElementById('rc-badge-style')) {
      var st = document.createElement('style'); st.id = 'rc-badge-style';
      st.textContent = '.grecaptcha-badge{visibility:hidden}';
      document.head.appendChild(st);
    }
    var sc = document.createElement('script');
    sc.src = 'https://www.google.com/recaptcha/api.js?onload=' + cb + '&render=explicit';
    sc.async = true; sc.defer = true;
    sc.onerror = function() { clearTimeout(timer); _rcLoad = null; reject(new Error('The verification widget could not be loaded.')); };
    document.head.appendChild(sc);
  });
  return _rcLoad;
}
function getRecaptchaToken(siteKey) {
  if (!siteKey) return Promise.reject(new Error('Text-message codes are not available right now.'));
  return loadRecaptchaApi().then(function() {
    return new Promise(function(resolve, reject) {
      var host = document.getElementById('app-recaptcha-host');
      if (!host) { host = document.createElement('div'); host.id = 'app-recaptcha-host'; document.body.appendChild(host); }
      if (_rcWidget !== null && _rcSiteKey !== siteKey) { host.innerHTML = ''; _rcWidget = null; }
      if (_rcPending) { try { _rcPending.reject(new Error('Superseded')); } catch (e) {} _rcPending = null; }
      var timer = setTimeout(function() { done('err', new Error('The verification challenge timed out. Try again or use email.')); }, 90000);
      function done(kind, arg) { clearTimeout(timer); var p = _rcPending; _rcPending = null; if (!p) return; if (kind === 'ok') p.resolve(arg); else p.reject(arg); }
      _rcPending = { resolve: resolve, reject: reject };
      if (_rcWidget === null) {
        var el = document.createElement('div'); host.appendChild(el);
        _rcSiteKey = siteKey;
        _rcWidget = window.grecaptcha.render(el, {
          sitekey: siteKey, size: 'invisible', badge: 'bottomleft',
          callback: function(token) { done('ok', token); },
          'expired-callback': function() { done('err', new Error('The verification challenge expired. Try again.')); },
          'error-callback': function() { done('err', new Error('The verification challenge could not run. Try again or use email.')); },
        });
      } else {
        try { window.grecaptcha.reset(_rcWidget); } catch (e) {}
      }
      try { window.grecaptcha.execute(_rcWidget); } catch (e) { done('err', e); }
    });
  });
}
window.getRecaptchaToken = getRecaptchaToken;
// Load the Google script ahead of the first execute (login page mount); errors surface later, on use.
window.warmRecaptcha = function() { try { loadRecaptchaApi().catch(function() {}); } catch (e) {} };
var RECAPTCHA_NOTICE = 'Protected by reCAPTCHA. Google\u2019s Privacy Policy and Terms of Service apply.';
window.RECAPTCHA_NOTICE = RECAPTCHA_NOTICE;

// ── System checkbox (2026-09-05) — the rounded-square look the table selection columns use,
// for menus/popovers that used to fall back to the browser's native checkbox. Purely visual:
// the parent handles the click.
function SysCheckbox(props) {
  var size = props.size || 16;
  var on = !!props.checked, mixed = !!props.indeterminate && !on;
  return React.createElement('span', { 'aria-checked': mixed ? 'mixed' : on, role: 'checkbox', style: Object.assign({
    width: size, height: size, borderRadius: 4, flex: '0 0 auto', boxSizing: 'border-box',
    border: '2px solid ' + (on || mixed ? C.accent : C.textDim), background: on ? C.accent : 'transparent',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s',
  }, props.style || {}) },
    on ? React.createElement(LIcon, { name: 'check', size: Math.max(10, size - 5), color: '#fff' }) : null,
    mixed ? React.createElement('span', { style: { width: size - 8, height: 2, background: C.accent, borderRadius: 1 } }) : null);
}
window.SysCheckbox = SysCheckbox;

// Selection columns (the checkbox column every list adds as _select / select / _sel) never sort:
// clicking beside the box used to sort the rows by ticked state (Anthony 2026-09-07).
function colNoSort(col) { return !!(col && (col.noSort || /^_?sel(ect)?$/.test(String(col.key || '')))); }

function DataTable({ columns, data, onRowClick, rowHref, rowStyle, onAction, emptyTitle, emptySub, storageKey, defaultSort, defaultSortDir, onSortedData, onPageData, bulkActions, extraToolbar, serverTotal, onServerFetch, serverSearch, initialPage, onPageChange, initialScroll, onScrollChange, scrollMode, scrollHeight, compact, enableViews, viewsPortalEl, noColumnFilters, fontSize, cellPadding, defaultPageSize }) {
  // columns: [{ key, label, render?, defaultVisible?, style?, sortKey?, locked?, stopClick?, noFilter? }]
  // defaultPageSize: rows per page until the user picks one (their pick is saved per storageKey and wins)

  var initVisible = {};
  columns.forEach(function(col) {
    initVisible[col.key] = col.defaultVisible !== false;
  });

  if (storageKey && _colPrefs[storageKey]) {
    var saved = _colPrefs[storageKey];
    columns.forEach(function(col) {
      if (saved[col.key] !== undefined) {
        initVisible[col.key] = saved[col.key];
      }
    });
  }

  // Locked columns are always visible
  columns.forEach(function(col) {
    if (col.locked) initVisible[col.key] = true;
  });

  var rowOptions = [25, 50, 100, 200, 400, 800];
  var initPageSize = (defaultPageSize && defaultPageSize > 0) ? defaultPageSize : 50;
  if (storageKey && _colPrefs[storageKey + '_pageSize']) {
    initPageSize = _colPrefs[storageKey + '_pageSize'];
  }

  const [visible, setVisible] = useState(initVisible);
  // Per-user column order (drag a header onto another to reorder; persists via /auth/preferences)
  const [colOrder, setColOrder] = useState((storageKey && _colPrefs[storageKey + '_order']) || null);
  const [dragCol, setDragCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  // Named per-user views (enableViews): [{ name, state }] under storageKey + '_views'
  const [userViews, setUserViews] = useState((storageKey && _colPrefs[storageKey + '_views']) || []);
  const [activeView, setActiveView] = useState((storageKey && _colPrefs[storageKey + '_activeView']) || null);
  // Starred view auto-applied every time this table mounts (per user)
  const [defaultView, setDefaultView] = useState((storageKey && _colPrefs[storageKey + '_defaultView']) || null);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const viewMenuRef = useRef(null);
  const [prefsReady, setPrefsReady] = useState(_colPrefsLoaded);
  const [showColMenu, setShowColMenu] = useState(false);
  // Last manually-clicked sort persists per user under storageKey + '_sort' (same prefs
  // store as visibility/order/pageSize); defaultSort only applies until the first click.
  var _savedSort = (storageKey && _colPrefs[storageKey + '_sort']) || null;
  const [sortCol, setSortCol] = useState((_savedSort && _savedSort.col) || defaultSort || null);
  const [sortDir, setSortDir] = useState((_savedSort && _savedSort.col && _savedSort.dir) || defaultSortDir || 'asc');
  const [currentPage, _setCurrentPage] = useState(initialPage || 0);
  var setCurrentPage = function(p) { _setCurrentPage(p); if (onPageChange) onPageChange(p); };
  const [pageSize, setPageSize] = useState(initPageSize);
  const menuRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  useEffect(function() {
    // Restore scroll position once data is available (not on initial empty mount)
    if (!scrollRestoredRef.current && scrollRef.current && initialScroll && data && data.length > 0) {
      scrollRef.current.scrollTop = initialScroll;
      scrollRestoredRef.current = true;
    }
  }, [data]);
  var _scrollTotalRef = useRef(0);
  var handleScroll = function() {
    if (onScrollChange && scrollRef.current) onScrollChange(scrollRef.current.scrollTop);
    if (scrollMode && !onServerFetch && scrollRef.current) {
      var el = scrollRef.current;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240
          && (currentPage + 1) * pageSize < _scrollTotalRef.current) {
        setCurrentPage(currentPage + 1); // reveal the next batch
      }
    }
  };

  // Column filters: { colKey: { mode: 'contains'|'equals'|'starts'|'blank'|'not_blank', value: '' } }
  const [colFilters, setColFilters] = useState({});
  const [filterMenuCol, setFilterMenuCol] = useState(null);
  const filterMenuRef = useRef(null);
  const filterInputRef = useRef(null);

  var activeFilterCount = Object.keys(colFilters).filter(function(k) {
    var f = colFilters[k];
    if (!f || !f.mode) return false;
    if (f.mode === 'blank' || f.mode === 'not_blank') return true;
    return f.value && f.value.trim() !== '';
  }).length;

  // Pending filter edits (not yet applied)
  const [pendingFilter, setPendingFilter] = useState(null); // { mode, value, matchCase } for current filterMenuCol

  var openFilterMenu = function(colKey) {
    var existing = colFilters[colKey] || {};
    var col = columns.find(function(c) { return c.key === colKey; });
    var kind = col ? _dtInferFilterKind(col, data, !!onServerFetch) : 'text';
    var modes = _dtModesForKind(kind);
    var mode = existing.mode && modes.indexOf(existing.mode) !== -1 ? existing.mode : modes[0];
    setPendingFilter({ kind: kind, mode: mode, value: existing.value || '', matchCase: existing.matchCase || false, q: '' });
    setFilterMenuCol(colKey);
    setShowColMenu(false);
    setShowBulkMenu(false);
  };
  var applyFilter = function(colKey) {
    if (!pendingFilter) return;
    var next = Object.assign({}, colFilters);
    if (!pendingFilter.mode || pendingFilter.mode === '') { delete next[colKey]; }
    else { next[colKey] = { mode: pendingFilter.mode, value: pendingFilter.value || '', matchCase: pendingFilter.matchCase || false }; }
    setColFilters(next);
    setCurrentPage(0);
    setFilterMenuCol(null);
    setPendingFilter(null);
  };
  var clearFilter = function(colKey) {
    var next = Object.assign({}, colFilters);
    delete next[colKey];
    setColFilters(next);
    setCurrentPage(0);
    setFilterMenuCol(null);
    setPendingFilter(null);
  };
  var clearAllFilters = function() { setColFilters({}); setCurrentPage(0); setPendingFilter(null); setFilterMenuCol(null); };

  // Rebuild visibility + page size from saved prefs for the CURRENT storageKey/columns.
  // Reads through a latest-ref because storageKey can change while this instance stays
  // mounted (the PO list swaps its key per status tab) — mount-time init alone would keep
  // serving the previous tab's state and cross-pollute saves under the new key.
  var _latestRef = useRef();
  _latestRef.current = { storageKey: storageKey, columns: columns, defaultSort: defaultSort, defaultSortDir: defaultSortDir };
  var syncFromPrefs = function() {
    var key = _latestRef.current.storageKey;
    var saved = (key && _colPrefs[key]) || {};
    var next = {};
    _latestRef.current.columns.forEach(function(col) {
      next[col.key] = saved[col.key] !== undefined ? saved[col.key] : (col.defaultVisible !== false);
      if (col.locked) next[col.key] = true;
    });
    setVisible(next);
    if (key && _colPrefs[key + '_pageSize']) setPageSize(_colPrefs[key + '_pageSize']);
    setColOrder((key && _colPrefs[key + '_order']) || null);
    // Restore the user's last clicked sort; a saved view applied below still wins
    var savedSort = key && _colPrefs[key + '_sort'];
    if (savedSort && savedSort.col) {
      setSortCol(savedSort.col);
      setSortDir(savedSort.dir || 'asc');
    } else {
      setSortCol(_latestRef.current.defaultSort || null);
      setSortDir(_latestRef.current.defaultSortDir || 'asc');
    }
    // Saved views: refresh the list, then apply the starred default view (wins), else
    // whichever view was active last visit
    var vlist = (key && _colPrefs[key + '_views']) || [];
    setUserViews(vlist);
    var dvName = key && _colPrefs[key + '_defaultView'];
    setDefaultView(dvName || null);
    var avName = key && _colPrefs[key + '_activeView'];
    var pick = (dvName && vlist.find(function(v) { return v.name === dvName; }))
      || (avName && vlist.find(function(v) { return v.name === avName; })) || null;
    setActiveView(pick ? pick.name : null);
    if (pick) applyViewState(pick.state);
  };

  // Load prefs from server on first render
  useEffect(function() {
    if (!_colPrefsLoaded) {
      loadColumnPrefs().then(function() {
        syncFromPrefs();
        setPrefsReady(true);
      });
    }
  }, []);

  // Re-sync when the storage key changes on a live instance (e.g. PO list tab switch)
  var _prevKeyRef = useRef(storageKey);
  useEffect(function() {
    if (_prevKeyRef.current === storageKey) return;
    _prevKeyRef.current = storageKey;
    syncFromPrefs();
  }, [storageKey]);

  // Reset to page 0 when data length changes substantially (client mode only)
  var _prevDataLen = useRef(data.length);
  var _hadDataRef = useRef(data.length > 0);
  useEffect(function() {
    if (!onServerFetch && data.length !== _prevDataLen.current) {
      // Skip reset on initial load (empty → populated) — respect caller's initialPage
      var isInitialLoad = !_hadDataRef.current && data.length > 0;
      if (data.length > 0) _hadDataRef.current = true;
      if (!isInitialLoad) {
        // Only reset page if the data set changed significantly (>10% or full reload)
        var diff = Math.abs(data.length - _prevDataLen.current);
        if (diff > Math.max(5, _prevDataLen.current * 0.1) || _prevDataLen.current === 0) {
          setCurrentPage(0);
        }
      }
      _prevDataLen.current = data.length;
    }
  }, [data.length]);

  // Server-side pagination: call onServerFetch when state changes
  var _serverFiltersKey = onServerFetch ? JSON.stringify(colFilters) : '';
  var _fetchIdRef = useRef(0);
  useEffect(function() {
    if (!onServerFetch) return;
    _fetchIdRef.current++;
    var myId = _fetchIdRef.current;
    var timer = setTimeout(function() {
      if (myId === _fetchIdRef.current) {
        onServerFetch({ page: currentPage, pageSize: pageSize, sortCol: sortCol, sortDir: sortDir, filters: colFilters, search: serverSearch || '' });
      }
    }, 10);
    return function() { clearTimeout(timer); };
  }, [currentPage, pageSize, sortCol, sortDir, _serverFiltersKey, serverSearch]);

  // Server-side: reset page when search changes
  var _prevServerSearch = useRef(serverSearch);
  useEffect(function() {
    if (onServerFetch && serverSearch !== _prevServerSearch.current) {
      _prevServerSearch.current = serverSearch;
      setCurrentPage(0);
    }
  }, [serverSearch]);
  // Server-side: a filter change that shrinks the result set below the current page used to strand
  // the table on an empty page with the pager hidden (review M6 #76; Orders channel/status/date
  // filters, Catalog filter). Snap back to page 0 — the client-mode clamp above is server-exempt.
  useEffect(function() {
    if (onServerFetch && currentPage > 0 && data.length === 0 && (serverTotal || 0) > 0) setCurrentPage(0);
  }, [data.length, serverTotal]);

  var [showBulkMenu, setShowBulkMenu] = useState(false);
  var bulkMenuRef = useRef(null);
  var bulkBtnRef = useRef(null);
  var [bulkMenuPos, setBulkMenuPos] = useState({ top: 0, right: 0 });

  var toggleBulkMenu = function() {
    if (!showBulkMenu && bulkBtnRef.current) {
      var rect = bulkBtnRef.current.getBoundingClientRect();
      var z = window.APP_ZOOM || 1; // gBCR/innerWidth are visual px; the fixed menu positions in zoomed px
      setBulkMenuPos({ top: (rect.bottom + 4) / z, right: (window.innerWidth - rect.right) / z });
    }
    setShowBulkMenu(!showBulkMenu);
    setShowColMenu(false);
  };

  useEffect(function() {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowColMenu(false);
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setShowBulkMenu(false);
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setFilterMenuCol(null);
    }
    if (showColMenu || showBulkMenu || filterMenuCol) document.addEventListener('mousedown', handleClick);
    return function() { document.removeEventListener('mousedown', handleClick); };
  }, [showColMenu, showBulkMenu, filterMenuCol]);

  // The actions menu is position:fixed with coordinates captured at open — re-anchor it
  // to the button on scroll/resize (capture phase catches inner scrollers too), otherwise
  // it detaches and floats as the page scrolls.
  useEffect(function() {
    if (!showBulkMenu) return;
    function reposition() {
      if (!bulkBtnRef.current) return;
      var rect = bulkBtnRef.current.getBoundingClientRect();
      var z = window.APP_ZOOM || 1;
      setBulkMenuPos({ top: (rect.bottom + 4) / z, right: (window.innerWidth - rect.right) / z });
    }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return function() { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [showBulkMenu]);

  // Auto-focus filter input when menu opens
  useEffect(function() {
    if (filterMenuCol && filterInputRef.current) filterInputRef.current.focus();
  }, [filterMenuCol]);

  var toggleCol = function(key) {
    var next = Object.assign({}, visible);
    next[key] = !next[key];
    var anyVisible = Object.keys(next).some(function(k) { return next[k]; });
    if (!anyVisible) return;
    setVisible(next);
    if (storageKey) { _colPrefs[storageKey] = next; saveColumnPrefs(); }
  };

  var changePageSize = function(newSize) {
    setPageSize(newSize);
    setCurrentPage(0);
    if (storageKey) { _colPrefs[storageKey + '_pageSize'] = newSize; saveColumnPrefs(); }
  };

  // Display order for a column list under the saved order. Columns the saved order
  // doesn't know (added to the app after the user last dragged) anchor right after the
  // nearest preceding known column from the definition order — NOT at the end of the
  // table, which would strand every newly shipped column on the far right.
  var orderCols = function(cols) {
    if (!colOrder || !colOrder.length) return cols;
    var idx = {};
    colOrder.forEach(function(k, i) { idx[k] = i; });
    var lastKnown = -1;
    var keyed = cols.map(function(c, i) {
      if (idx[c.key] !== undefined) { lastKnown = idx[c.key]; return [c, lastKnown, i]; }
      return [c, lastKnown + (i + 1) / (cols.length + 1), i];
    });
    var out = keyed.sort(function(a, b) { return a[1] - b[1] || a[2] - b[2]; })
      .map(function(x) { return x[0]; });
    // Sticky (left-pinned) columns only work at the front — keep them there regardless
    var stk = out.filter(function(c) { return c.sticky; });
    return stk.length ? stk.concat(out.filter(function(c) { return !c.sticky; })) : out;
  };

  var dropColumn = function(targetKey) {
    var dk = dragCol;
    setDragCol(null);
    setDragOverCol(null);
    if (!dk || dk === targetKey) return;
    // Build the full current display order (all non-subRow columns, hidden ones included,
    // so a hidden column keeps a sane slot when toggled back on)
    var order = orderCols(columns.filter(function(c) { return !c.subRow; }))
      .map(function(c) { return c.key; });
    var from = order.indexOf(dk), to = order.indexOf(targetKey);
    if (from < 0 || to < 0 || from === to) return;
    order.splice(from, 1);
    order.splice(to, 0, dk);
    setColOrder(order);
    if (storageKey) { _colPrefs[storageKey + '_order'] = order; saveColumnPrefs(); }
  };

  var resetColOrder = function() {
    setColOrder(null);
    if (storageKey) { delete _colPrefs[storageKey + '_order']; saveColumnPrefs(); }
  };

  // ── Saved views (enableViews) ──
  var currentViewState = function() {
    return { filters: colFilters, sortCol: sortCol, sortDir: sortDir, visible: visible, order: colOrder, pageSize: pageSize };
  };
  var applyViewState = function(st) {
    st = st || {};
    setColFilters(st.filters || {});
    setSortCol(st.sortCol !== undefined ? st.sortCol : (defaultSort || null));
    setSortDir(st.sortDir || defaultSortDir || 'asc');
    var nextVis = {};
    columns.forEach(function(col) {
      nextVis[col.key] = st.visible && st.visible[col.key] !== undefined ? st.visible[col.key] : (col.defaultVisible !== false);
      if (col.locked) nextVis[col.key] = true;
    });
    setVisible(nextVis);
    setColOrder(st.order || null);
    if (st.pageSize) setPageSize(st.pageSize);
    setCurrentPage(0);
  };
  var selectView = function(v) {
    setViewMenuOpen(false);
    if (!v) {
      // "Default" = factory state; also clear the base prefs so a reload matches what's shown
      setActiveView(null);
      applyViewState(null);
      if (storageKey) {
        delete _colPrefs[storageKey + '_activeView'];
        delete _colPrefs[storageKey];
        delete _colPrefs[storageKey + '_order'];
        delete _colPrefs[storageKey + '_sort'];
        saveColumnPrefs();
      }
      return;
    }
    setActiveView(v.name);
    applyViewState(v.state);
    if (storageKey) { _colPrefs[storageKey + '_activeView'] = v.name; saveColumnPrefs(); }
  };
  var saveCurrentView = function() {
    var name = (viewName || '').trim();
    if (!name || !storageKey) return;
    var next = userViews.filter(function(v) { return v.name !== name; });
    next.push({ name: name, state: currentViewState() });
    setUserViews(next);
    setActiveView(name);
    setViewName('');
    setViewMenuOpen(false);
    _colPrefs[storageKey + '_views'] = next;
    _colPrefs[storageKey + '_activeView'] = name;
    saveColumnPrefs();
  };
  var deleteUserView = function(v) {
    if (!window.confirm('Delete the view "' + v.name + '"?')) return;
    var next = userViews.filter(function(x) { return x.name !== v.name; });
    setUserViews(next);
    if (activeView === v.name) setActiveView(null);
    if (defaultView === v.name) setDefaultView(null);
    if (storageKey) {
      _colPrefs[storageKey + '_views'] = next;
      if (activeView === v.name) delete _colPrefs[storageKey + '_activeView'];
      if (defaultView === v.name) delete _colPrefs[storageKey + '_defaultView'];
      saveColumnPrefs();
    }
  };
  var toggleDefaultView = function(v) {
    var next = defaultView === v.name ? null : v.name;
    setDefaultView(next);
    if (storageKey) {
      if (next) _colPrefs[storageKey + '_defaultView'] = next;
      else delete _colPrefs[storageKey + '_defaultView'];
      saveColumnPrefs();
    }
  };
  useEffect(function() {
    if (!viewMenuOpen) return;
    var onDoc = function(e) { if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) setViewMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [viewMenuOpen]);

  // Apply the starred default view (or the remembered active one) on mount. syncFromPrefs
  // only runs on the first-ever prefs load or a storageKey change — a remount with prefs
  // already cached would otherwise show the view's name without its filters/sort.
  useEffect(function() {
    if (!enableViews || !storageKey || !_colPrefsLoaded) return;
    var vlist = _colPrefs[storageKey + '_views'] || [];
    var dvName = _colPrefs[storageKey + '_defaultView'];
    var avName = _colPrefs[storageKey + '_activeView'];
    var pick = (dvName && vlist.find(function(v) { return v.name === dvName; }))
      || (avName && vlist.find(function(v) { return v.name === avName; })) || null;
    if (pick) { setActiveView(pick.name); applyViewState(pick.state); }
  }, []);

  var handleSort = function(col) {
    var nextDir = 'asc';
    if (sortCol === col.key) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(nextDir);
    } else {
      setSortCol(col.key);
      setSortDir('asc');
    }
    // Remember the last clicked sort per user — restored on the next visit
    if (storageKey) { _colPrefs[storageKey + '_sort'] = { col: col.key, dir: nextDir }; saveColumnPrefs(); }
  };
  // A column that stops being sortable cannot stay the sort key (the Orders list sorts by anything
  // only inside a search; outside one the server sorts by date whatever is asked): the arrow would
  // sit on a column the server ignores. Back to the default sort (polish 2026-10-11). The saved
  // per-user sort is left alone so it applies again once the column is sortable.
  var _noSortKeys = columns.filter(colNoSort).map(function(c) { return c.key; }).join('|');
  useEffect(function() {
    if (!sortCol) return;
    var cur = columns.find(function(c) { return c.key === sortCol; });
    if (!cur || !colNoSort(cur)) return;
    setSortCol(defaultSort || null);
    setSortDir(defaultSortDir || 'asc');
  }, [_noSortKeys, sortCol]);

  var visibleCols = columns.filter(function(col) { return visible[col.key]; });

  // Separate normal columns from subRow columns
  var normalCols = orderCols(visibleCols.filter(function(col) { return !col.subRow; }));
  var subRowCols = visibleCols.filter(function(col) { return col.subRow; });

  // Dynamic font size based on normal column count (subRow cols don't affect width)
  var colCount = normalCols.length;
  var tableFontSize = fontSize || (compact ? 12 : (colCount <= 6 ? 14 : colCount <= 10 ? 14 : 13));
  var cellPad = cellPadding || '6px 8px';

  // Apply column filters
  var filteredData = data;
  var filterKeys = Object.keys(colFilters);
  if (filterKeys.length > 0) {
    var _filterColByKey = {};
    columns.forEach(function(c) { _filterColByKey[c.key] = c; });
    filteredData = data.filter(function(row) {
      return filterKeys.every(function(colKey) {
        var f = colFilters[colKey];
        if (!f || !f.mode) return true;
        // filterValue → raw field → sortValue → render() when it returns plain text, so
        // computed columns (margin, vendor name, ROI…) filter on what the user sees.
        var cd = _filterColByKey[colKey];
        var rawVal = cd ? _dtColValue(cd, row) : row[colKey];
        var caseSensitive = f.matchCase;
        var val = rawVal == null ? '' : String(rawVal);
        var search = (f.value || '').trim();
        if (!caseSensitive) { val = val.toLowerCase(); search = search.toLowerCase(); }
        switch (f.mode) {
          case 'contains': return search === '' || val.includes(search);
          case 'equals': return search === '' || val === search;
          case 'starts': return search === '' || val.startsWith(search);
          case 'ends': return search === '' || val.endsWith(search);
          case 'blank': return rawVal == null || String(rawVal).trim() === '';
          case 'not_blank': return rawVal != null && String(rawVal).trim() !== '';
          // Numeric modes: a missing value (null/'') is NOT zero — it can never satisfy
          // a numeric comparison. Number(null) === 0 would silently include such rows.
          case 'gt': case 'lt': case 'gte': case 'lte': case 'eq':
            return _dtNumCompare(f.mode, rawVal, search);
          case 'on': case 'before': case 'after': {
            var _d = _dtToDay(rawVal), _q = _dtToDay(search);
            if (_d == null || _q == null) return false;
            return f.mode === 'on' ? _d === _q : f.mode === 'before' ? _d < _q : _d > _q;
          }
          case 'in': {
            if (!search) return true;
            var inVals = _dtSplitIn(search).map(function(v) { return v.toLowerCase(); });
            return inVals.includes(String(rawVal == null ? '' : rawVal).trim().toLowerCase());
          }
          default: return true;
        }
      });
    });
  }

  // Sort the data
  var sortedData = filteredData;
  if (sortCol) {
    var col = columns.find(function(c) { return c.key === sortCol; });
    sortedData = [].concat(filteredData).sort(function(a, b) {
      var aVal, bVal;
      if (col && typeof col.sortValue === 'function') {
        aVal = col.sortValue(a); bVal = col.sortValue(b);
      } else {
        var sk = (col && col.sortKey) || sortCol;
        aVal = a[sk]; bVal = b[sk];
      }
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      var aNum = Number(aVal), bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum) && String(aVal) !== '' && String(bVal) !== '') {
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      var aStr = String(aVal).toLowerCase(), bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDir === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Paginate
  var totalRows = sortedData.length;
  var totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  // Only reset page if we have data and it truly overflowed (avoid resetting during loading)
  if (!onServerFetch && totalRows > 0 && currentPage >= totalPages) setCurrentPage(Math.max(0, totalPages - 1));
  var pageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  var showingFrom = totalRows === 0 ? 0 : currentPage * pageSize + 1;
  var showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  if (scrollMode && !onServerFetch) {
    // Accumulate batches from the top: currentPage = batches loaded − 1
    pageData = sortedData.slice(0, (currentPage + 1) * pageSize);
    showingFrom = totalRows === 0 ? 0 : 1;
    showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  }
  _scrollTotalRef.current = totalRows;

  // Server-side override: data is already filtered/sorted/paginated by server
  if (onServerFetch) {
    filteredData = data; sortedData = data; pageData = data;
    totalRows = serverTotal || 0;
    totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    showingFrom = totalRows === 0 ? 0 : currentPage * pageSize + 1;
    showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  }

  // Expose sorted data to parent for shift-select.
  // No deps array: fires every render so the parent's ref stays in sync with the current sortedData
  // (data prop reference can change without length/sortCol/sortDir changing).
  useEffect(function() {
    if (onSortedData) onSortedData(sortedData);
    if (onPageData) onPageData(pageData);
  });

  var _isEmpty = data.length === 0;

  if (filteredData.length === 0 && activeFilterCount > 0) {
    return React.createElement(Card, { style: { padding: 0, overflow: 'auto' } },
      React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px' } },
        React.createElement('div', { style: { fontSize: 14, color: C.textMuted, marginBottom: 8 } }, 'No rows match the active filters (' + activeFilterCount + ' filter' + (activeFilterCount > 1 ? 's' : '') + ')'),
        React.createElement('button', { onClick: clearAllFilters, style: { background: C.accent, border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 } }, React.createElement(LIcon, { name: 'x', size: 12 }), ' Clear All Filters')
      )
    );
  }

  return React.createElement(Card, { style: Object.assign({ padding: 0, overflow: 'visible', position: 'relative' },
    (scrollHeight === 'fill') ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {}) },
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid ' + C.border + '44', position: 'relative', flexWrap: 'wrap', gap: 8 } },
      // Rows per page
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, scrollMode ? 'Load' : 'Show'),
        rowOptions.map(function(n) {
          return React.createElement('button', {
            key: n, onClick: function() { changePageSize(n); },
            className: pageSize !== n ? 'dt-row-btn' : '',
            style: {
              padding: '2px 8px', fontSize: 11, borderRadius: 4, border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: pageSize === n ? 600 : 400,
              background: pageSize === n ? C.accent : 'transparent',
              color: pageSize === n ? '#fff' : C.textMuted,
              transition: 'background 0.15s'
            }
          }, String(n));
        }),
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, scrollMode ? 'rows per scroll' : 'rows')
      ),
      // Active filters indicator
      activeFilterCount > 0 && React.createElement('button', {
        onClick: clearAllFilters,
        style: { background: C.warningSoft, border: '1px solid ' + C.warning + '44', color: C.warning, padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }
      }, React.createElement(LIcon, { name: 'zap', size: 12 }), ' ' + activeFilterCount + ' filter' + (activeFilterCount > 1 ? 's' : '') + ' active — Clear'),
      // Bulk Actions + Columns toggle
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        // Saved views dropdown (opt-in) — portals to viewsPortalEl when the page wants it
        // rendered outside the table toolbar (state stays in this component either way)
        (function() {
          if (!enableViews || !storageKey) return null;
          var ui = React.createElement('div', { key: '__views', ref: viewMenuRef, style: { position: 'relative', display: 'inline-block' } },
          React.createElement('button', {
            onClick: function() { setViewMenuOpen(!viewMenuOpen); },
            className: 'dt-btn',
            title: 'Saved views — save the current filters, sort, column order and visibility under a name',
            style: { background: 'none', border: '1px solid ' + (activeView ? C.accent + '77' : C.border), color: activeView ? C.accent : C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, fontWeight: activeView ? 600 : 400, maxWidth: 220 }
          },
            React.createElement(LIcon, { name: 'bookmark', size: 12 }),
            React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, 'View: ' + (activeView || 'Default')),
            React.createElement(LIcon, { name: 'chevron-down', size: 11 })),
          viewMenuOpen && React.createElement('div', {
            style: Object.assign({ position: 'absolute', top: '100%', zIndex: 120, marginTop: 4, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 6, minWidth: 240, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }, viewsPortalEl ? { left: 0 } : { right: 0 })
          },
            [{ v: null, label: 'Default' }].concat(userViews.map(function(v) { return { v: v, label: v.name }; })).map(function(it) {
              var isActive = it.v === null ? !activeView : activeView === it.v.name;
              var isStarred = it.v !== null && defaultView === it.v.name;
              return React.createElement('div', {
                key: it.v === null ? '__default' : it.v.name,
                onClick: function() { selectView(it.v); },
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 12, color: isActive ? C.accent : C.text, fontWeight: isActive ? 600 : 400 },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              },
                React.createElement('span', { style: { display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                  isActive && React.createElement(LIcon, { name: 'check', size: 12, color: C.accent }), it.label),
                it.v !== null && React.createElement('span', { style: { display: 'inline-flex', gap: 2, flexShrink: 0 } },
                  React.createElement('span', {
                    title: isStarred ? 'Default view for every list — click to unset' : 'Make this my default view for every list',
                    onClick: function(e) { e.stopPropagation(); toggleDefaultView(it.v); },
                    style: { display: 'inline-flex', padding: 2, opacity: isStarred ? 1 : 0.45 }
                  }, React.createElement(LIcon, { name: 'star', size: 12, color: isStarred ? '#F59E0B' : C.textMuted })),
                  React.createElement('span', {
                    title: 'Delete view',
                    onClick: function(e) { e.stopPropagation(); deleteUserView(it.v); },
                    style: { display: 'inline-flex', padding: 2, opacity: 0.55 }
                  }, React.createElement(LIcon, { name: 'x', size: 12, color: C.textMuted }))));
            }),
            React.createElement('div', { style: { borderTop: '1px solid ' + C.border + '66', marginTop: 6, paddingTop: 8, display: 'flex', gap: 6, alignItems: 'center' } },
              React.createElement('input', {
                value: viewName,
                onChange: function(e) { setViewName(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') saveCurrentView(); },
                placeholder: 'Save current as…',
                style: { flex: 1, minWidth: 0, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 5, fontSize: 12, fontFamily: 'inherit' }
              }),
              React.createElement('button', {
                onClick: saveCurrentView,
                disabled: !viewName.trim(),
                style: { background: viewName.trim() ? C.accent : C.border, border: 'none', color: '#fff', padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: viewName.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }
              }, 'Save'))));
          return viewsPortalEl && window.ReactDOM && ReactDOM.createPortal
            ? ReactDOM.createPortal(ui, viewsPortalEl) : ui;
        })(),
        extraToolbar,
        // Bulk Actions dropdown
        bulkActions && (bulkActions.count > 0 || bulkActions.alwaysShow) && React.createElement('div', { style: { position: 'relative' } },
          React.createElement('button', {
            ref: bulkBtnRef,
            onClick: toggleBulkMenu,
            // Same hover as a primary Btn — this was the only toolbar button with no hover feedback
            onMouseOver: function(e) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; },
            onMouseOut: function(e) { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; },
            style: { background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, transition: 'all 0.15s' }
          }, React.createElement(LIcon, { name: 'zap', size: 12 }),
            // Fixed-width count slot so the button (and everything left of it)
            // doesn't jitter when the selection count appears/changes
            React.createElement('span', { style: { display: 'inline-block', minWidth: 52, textAlign: 'left' } }, ' Actions' + (bulkActions.count > 0 ? ' (' + bulkActions.count + ')' : ''))),
          showBulkMenu && React.createElement('div', {
            ref: bulkMenuRef,
            style: { position: 'fixed', top: bulkMenuPos.top, right: bulkMenuPos.right, zIndex: 10000, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 0', minWidth: 220, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - ' + bulkMenuPos.top + 'px - 10px)', overflowY: 'auto' }
          },
            (bulkActions.items || []).map(function(item, idx) {
              if (item.divider) return React.createElement('div', { key: 'div-' + idx, style: { height: 1, background: C.border + '44', margin: '4px 0' } });
              if (item.section) return React.createElement('div', { key: 'sec-' + idx, style: { padding: '6px 14px 4px', fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.05em' } }, item.section);
              if (item.header) return React.createElement('div', { key: 'hdr-' + idx, style: { padding: '6px 14px', fontSize: 11, color: C.textMuted, fontWeight: 600, borderBottom: '1px solid ' + C.border + '44', marginBottom: 4 } }, item.header);
              return React.createElement('button', {
                key: item.label + '-' + idx,
                disabled: !!item.disabled,
                title: item.title || undefined,
                onClick: function() { if (item.disabled) return; setShowBulkMenu(false); item.onClick(); },
                style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 14px', cursor: item.disabled ? 'not-allowed' : 'pointer', fontSize: 12, color: item.disabled ? C.textDim : (item.danger ? C.danger : C.text), opacity: item.disabled ? 0.5 : 1, fontFamily: 'inherit', borderRadius: 0 },
                onMouseEnter: function(e) { if (!item.disabled) e.currentTarget.style.background = C.surfaceHover; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; }
              }, item.icon ? React.createElement(LIcon, { name: item.icon, size: 14, color: item.disabled ? C.textDim : (item.danger ? C.danger : C.textMuted) }) : null, item.label);
            }),
            bulkActions.count > 0 && bulkActions.onClear && React.createElement('div', { style: { height: 1, background: C.border + '44', margin: '4px 0' } }),
            bulkActions.count > 0 && bulkActions.onClear && React.createElement('button', {
              onClick: function() { setShowBulkMenu(false); bulkActions.onClear(); },
              style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: C.textMuted, fontFamily: 'inherit', borderRadius: 0 },
              onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; },
              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; }
            }, React.createElement(LIcon, { name: 'x', size: 14, color: C.textMuted }), 'Clear selection')
          )
        ),
        // Columns toggle
        React.createElement('div', { style: { position: 'relative' } },
        React.createElement('button', {
          onClick: function() { setShowColMenu(!showColMenu); },
          className: 'dt-btn',
          style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: 'border-color 0.15s, background 0.15s' }
        }, React.createElement(LIcon, { name: 'settings', size: 12 }), ' Columns'),
        showColMenu && React.createElement('div', {
          ref: menuRef,
          style: { position: 'absolute', top: '100%', right: 0, zIndex: 100, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 8, minWidth: 180, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 240px)', overflowY: 'auto' }
        },
          columns.filter(function(col) { return !col.locked; })
            .slice().sort(function(a, b) { return String(a.label || a.key).localeCompare(String(b.label || b.key)); })
            .map(function(col) {
            return React.createElement('div', {
              key: col.key,
              onClick: function() { toggleCol(col.key); },
              style: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', borderRadius: 4, fontSize: 12, color: visible[col.key] ? C.text : C.textDim, userSelect: 'none' }
            },
              React.createElement(SysCheckbox, { checked: !!visible[col.key], size: 16 }),
              col.label
            );
          }),
          colOrder && colOrder.length > 0 && React.createElement('button', {
            onClick: function() { resetColOrder(); setShowColMenu(false); },
            style: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', marginTop: 6, background: 'none', border: 'none', borderRadius: 0, padding: '8px 8px 4px', borderTop: '1px solid ' + C.border + '66', cursor: 'pointer', fontSize: 12, color: C.textMuted, fontFamily: 'inherit' }
          }, React.createElement(LIcon, { name: 'undo-2', size: 13 }), 'Reset column order')
        )
      )
      ) // close bulk actions + columns wrapper
    ),
    // Table — scrollHeight 'fill' makes the scroll area flex-fill its parent
    // (the DataTable root becomes a flex column; page owns the height).
    // Works with pagination (default) or scrollMode batches.
    React.createElement('div', { ref: scrollRef, onScroll: handleScroll, style: scrollHeight === 'fill'
      ? { overflow: 'auto', flex: _isEmpty ? '0 0 auto' : 1, minHeight: filterMenuCol ? 340 : 0 } // empty: the height goes to the centred empty state
      : scrollMode
      ? { overflow: 'auto', height: scrollHeight || 600, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 210px)', minHeight: filterMenuCol ? 340 : 0, transition: 'min-height 0.15s' }
      : { overflow: 'auto', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 210px)', minHeight: filterMenuCol ? 340 : 0, transition: 'min-height 0.15s' } },
    React.createElement('table', { style: { width: '100%', fontSize: tableFontSize, tableLayout: 'auto' } },
      React.createElement('thead', null,
        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
          normalCols.map(function(col) {
            var isSorted = sortCol === col.key;
            var arrow = isSorted ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
            var hasFilter = colFilters[col.key] && colFilters[col.key].mode;
            var isFilterOpen = filterMenuCol === col.key;
            var isFilterable = !noColumnFilters && !col.locked && !col.noFilter && col.key.charAt(0) !== '_';
            var colWidth = col.width || col.colWidth;
            return React.createElement('th', {
              key: col.key,
              className: 'dt-th',
              onClick: colNoSort(col) ? undefined : function() { handleSort(col); },
              style: {
                padding: cellPad, textAlign: 'left', fontWeight: 600, fontSize: compact ? 10 : (colCount <= 8 ? 11 : 10),
                color: isSorted ? C.accent : C.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                cursor: colNoSort(col) ? 'default' : 'pointer', userSelect: 'none', transition: 'color 0.15s', position: 'sticky', top: 0, zIndex: col.sticky ? 21 : 20, background: C.bg,
                left: col.sticky ? 0 : undefined,
                width: colWidth,
                boxShadow: dragOverCol === col.key && dragCol !== col.key ? 'inset 3px 0 0 ' + C.accent : undefined,
                opacity: dragCol === col.key ? 0.35 : 1
              },
              // Drag a header onto another to reorder columns (saved per user via storageKey)
              draggable: !col.sticky,
              onDragStart: function(e) {
                setDragCol(col.key);
                try { e.dataTransfer.setData('text/plain', col.key); } catch (err) {}
                e.dataTransfer.effectAllowed = 'move';
              },
              onDragOver: function(e) {
                if (!dragCol || dragCol === col.key) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverCol !== col.key) setDragOverCol(col.key);
              },
              onDragLeave: function() { if (dragOverCol === col.key) setDragOverCol(null); },
              onDrop: function(e) { e.preventDefault(); dropColumn(col.key); },
              onDragEnd: function() { setDragCol(null); setDragOverCol(null); }
            },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                col.headerRender
                  ? React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 4 } }, col.headerRender(), colNoSort(col) ? null : arrow)
                  : React.createElement('span', null, col.label + (colNoSort(col) ? '' : arrow)),
                // Filter button — its own 22px hit target so a near miss never sorts the column
                isFilterable && React.createElement('span', {
                  className: 'dt-filter-btn',
                  onClick: function(e) { e.stopPropagation(); if (isFilterOpen) { setFilterMenuCol(null); setPendingFilter(null); } else { openFilterMenu(col.key); } },
                  onMouseDown: function(e) { e.stopPropagation(); },
                  style: { cursor: 'pointer', opacity: (hasFilter || isFilterOpen) ? 1 : 0.55, marginLeft: 6, width: 22, height: 22, borderRadius: 6, flex: '0 0 auto',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: (hasFilter || isFilterOpen) ? C.accentSoft : 'transparent' }
                }, React.createElement(LIcon, { name: 'filter', size: 13, color: hasFilter ? C.accent : C.textMuted }))
              ),
              // Filter dropdown — kind-aware (list / number / date / text)
              isFilterOpen && pendingFilter && (function() {
                var kind = pendingFilter.kind || 'text';
                var setP = function(patch) { setPendingFilter(Object.assign({}, pendingFilter, patch)); };
                var valueOff = pendingFilter.mode === 'blank' || pendingFilter.mode === 'not_blank';
                var pillStyle = function(active) {
                  return { padding: '3px 9px', fontSize: 11, borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: active ? 600 : 400,
                    background: active ? C.accent : C.bg, color: active ? '#fff' : C.textMuted, transition: 'all 0.12s' };
                };
                var pillRow = function(modes, mb) {
                  return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: mb || 6 } },
                    modes.map(function(m) {
                      return React.createElement('button', { key: m.val, onClick: function() { setP({ mode: m.val }); }, style: pillStyle(pendingFilter.mode === m.val) }, m.lbl);
                    }));
                };
                var actionBtns = function() {
                  return [
                    React.createElement('button', { key: 'clr', onClick: function() { clearFilter(col.key); },
                      style: { background: 'none', border: 'none', color: C.textMuted, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' } }, 'Clear'),
                    React.createElement('button', { key: 'ok', onClick: function() { applyFilter(col.key); },
                      style: { background: C.accent, border: 'none', color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 } }, 'Apply')
                  ];
                };
                var body;
                if (kind === 'list') {
                  var options = _dtListOptions(col, data);
                  var q = (pendingFilter.q || '').trim().toLowerCase();
                  var shown = q ? options.filter(function(o) { return o.toLowerCase().indexOf(q) !== -1; }) : options;
                  var selectedVals = _dtSplitIn(pendingFilter.value);
                  var isSel = function(o) { return selectedVals.indexOf(o) !== -1; };
                  body = React.createElement(React.Fragment, null,
                    options.length > 8 && React.createElement('input', {
                      ref: filterInputRef, type: 'text', placeholder: 'Search ' + options.length + ' values…', value: pendingFilter.q || '',
                      onChange: function(e) { setP({ q: e.target.value }); },
                      onKeyDown: function(e) { if (e.key === 'Enter') applyFilter(col.key); if (e.key === 'Escape') { setFilterMenuCol(null); setPendingFilter(null); } },
                      className: 'dt-input',
                      style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', outline: 'none' }
                    }),
                    React.createElement('div', { style: { display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' } },
                      React.createElement('button', {
                        onClick: function() { var merged = selectedVals.slice(); shown.forEach(function(o) { if (merged.indexOf(o) === -1) merged.push(o); }); setP({ mode: 'in', value: merged.join(DT_IN_SEP) }); },
                        style: { background: 'none', border: 'none', color: C.accent, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: 0 }
                      }, q ? 'Select shown' : 'All'),
                      React.createElement('button', {
                        onClick: function() { setP({ mode: 'in', value: '' }); },
                        style: { background: 'none', border: 'none', color: C.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }
                      }, 'Clear'),
                      selectedVals.length > 0 && React.createElement('span', { style: { marginLeft: 'auto', fontSize: 11, color: C.textMuted } }, selectedVals.length + ' selected')
                    ),
                    React.createElement('div', { style: { maxHeight: 220, overflowY: 'auto', marginBottom: 10 } },
                      shown.length === 0 && React.createElement('div', { style: { fontSize: 12, color: C.textDim, padding: '6px 0' } }, options.length === 0 ? 'No values in this column' : 'No matches'),
                      shown.map(function(opt) {
                        var checked = isSel(opt);
                        return React.createElement('div', {
                          key: opt, role: 'option', 'aria-selected': checked,
                          onClick: function() {
                            var next = checked ? selectedVals.filter(function(v) { return v !== opt; }) : selectedVals.concat([opt]);
                            setP({ mode: 'in', value: next.join(DT_IN_SEP) });
                          },
                          style: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer', fontSize: 12, color: C.text, userSelect: 'none', borderRadius: 4 }
                        },
                          React.createElement(SysCheckbox, { checked: checked, size: 16 }),
                          React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, opt)
                        );
                      })
                    ),
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } }, actionBtns())
                  );
                } else {
                  var modes = kind === 'number'
                    ? [{ val: 'eq', lbl: '=' }, { val: 'gt', lbl: '>' }, { val: 'gte', lbl: '≥' }, { val: 'lt', lbl: '<' }, { val: 'lte', lbl: '≤' }]
                    : kind === 'date'
                      ? [{ val: 'on', lbl: 'On' }, { val: 'before', lbl: 'Before' }, { val: 'after', lbl: 'After' }]
                      : [{ val: 'contains', lbl: 'Contains' }, { val: 'equals', lbl: 'Equals' }, { val: 'starts', lbl: 'Starts with' }, { val: 'ends', lbl: 'Ends with' }];
                  body = React.createElement(React.Fragment, null,
                    React.createElement('input', {
                      ref: filterInputRef,
                      type: kind === 'date' ? 'date' : 'text',
                      inputMode: kind === 'number' ? 'decimal' : undefined,
                      placeholder: kind === 'number' ? 'Enter a number' : kind === 'date' ? '' : 'Type to filter...',
                      value: pendingFilter.value || '',
                      onChange: function(e) { setP({ value: e.target.value }); },
                      onKeyDown: function(e) { if (e.key === 'Enter') applyFilter(col.key); if (e.key === 'Escape') { setFilterMenuCol(null); setPendingFilter(null); } },
                      disabled: valueOff,
                      className: 'dt-input',
                      style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: valueOff ? C.textDim : C.text, padding: '7px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', outline: 'none', colorScheme: kind === 'date' ? (getThemeName() === 'dark' ? 'dark' : 'light') : undefined }
                    }),
                    pillRow(modes),
                    pillRow([{ val: 'blank', lbl: 'Is blank' }, { val: 'not_blank', lbl: 'Not blank' }], 10),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                      kind === 'text' && !valueOff
                        ? React.createElement('div', { onClick: function() { setP({ matchCase: !pendingFilter.matchCase }); }, style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textMuted, cursor: 'pointer', userSelect: 'none', flex: 1 } },
                            React.createElement(SysCheckbox, { checked: !!pendingFilter.matchCase, size: 14 }),
                            'Aa')
                        : React.createElement('div', { style: { flex: 1 } }),
                      actionBtns()
                    )
                  );
                }
                return React.createElement('div', {
                  ref: filterMenuRef,
                  onClick: function(e) { e.stopPropagation(); },
                  style: { position: 'absolute', top: '100%', left: 0, zIndex: 200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '12px 14px', minWidth: 250, maxWidth: 340, boxShadow: '0 8px 30px rgba(0,0,0,0.35)', textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 240px)', overflowY: 'auto', cursor: 'default' }
                },
                  React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 } },
                    'Filter ' + col.label, React.createElement('span', { style: { fontWeight: 400, color: C.textDim, marginLeft: 6 } }, kind === 'list' ? 'values' : kind)),
                  body
                );
              })()
            );
          }),
          onAction && React.createElement('th', { style: { width: 40, padding: cellPad } })
        )
      ),
      React.createElement('tbody', null,
        pageData.map(function(row, ri) {
          var totalNormalCols = normalCols.length + (onAction ? 1 : 0);
          var _rowHref = rowHref ? rowHref(row) : null;
          var _rs = rowStyle ? (rowStyle(row) || null) : null;
          var _rbg = (_rs && _rs.background) || 'transparent';
          var mainRow = React.createElement('tr', {
            key: row.id || ri,
            // Suppress Chrome's auto-scroll on middle-click so onAuxClick can open a new tab.
            onMouseDown: (_rowHref || onRowClick) ? function(e) { if (_rowHref && e.button === 1) e.preventDefault(); if (onRowClick && e.shiftKey) e.preventDefault(); } : undefined,
            onClick: onRowClick ? function(e) { if (_rowHref && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(_rowHref, '_blank'); return; } onRowClick(row, e); } : undefined,
            onAuxClick: onRowClick && _rowHref ? function(e) { if (e.button === 1) { e.preventDefault(); window.open(_rowHref, '_blank'); } } : undefined,
            style: Object.assign({ borderBottom: subRowCols.length > 0 ? 'none' : '1px solid ' + C.border, cursor: onRowClick ? 'pointer' : 'default', userSelect: 'none' }, _rs || {}),
            onMouseEnter: function(e) { var next = e.currentTarget.nextElementSibling; e.currentTarget.style.background = C.surfaceHover; if (next && next.dataset.subrow) next.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { var next = e.currentTarget.nextElementSibling; e.currentTarget.style.background = _rbg; if (next && next.dataset.subrow) next.style.background = _rbg; }
          },
            normalCols.map(function(col) {
              var content = col.render ? col.render(row) : (row[col.key] != null ? String(row[col.key]) : '');
              if (col.copyable) {
                var copyVal = typeof col.copyValue === 'function' ? col.copyValue(row) : row[col.key];
                if (copyVal != null && String(copyVal).trim() !== '') {
                  content = React.createElement(CopyCell, { value: copyVal }, content);
                }
              }
              var baseStyle = Object.assign({ padding: cellPad, whiteSpace: col.wrap ? 'normal' : 'nowrap' }, col.style || { color: C.textMuted }, col.sticky ? { position: 'sticky', left: 0, zIndex: 10, background: C.bg } : {});
              if (col.width || col.colWidth) { baseStyle.width = col.width || col.colWidth; baseStyle.minWidth = col.width || col.colWidth; baseStyle.maxWidth = col.width || col.colWidth; }
              var tdProps = { key: col.key, style: baseStyle };
              if (col.stopClick) tdProps.onClick = function(e) { e.stopPropagation(); };
              return React.createElement(TD, tdProps, content);
            }),
            onAction && React.createElement(TD, { style: { padding: cellPad } },
              React.createElement('button', {
                onClick: function(e) { e.stopPropagation(); onAction('delete', row); },
                style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer' }
              }, React.createElement(LIcon, { name: 'trash-2', size: 14 }))
            )
          );
          if (subRowCols.length === 0) return mainRow;
          var subRow = React.createElement('tr', {
            key: (row.id || ri) + '_sub', 'data-subrow': 'true',
            onMouseDown: (_rowHref || onRowClick) ? function(e) { if (_rowHref && e.button === 1) e.preventDefault(); if (onRowClick && e.shiftKey) e.preventDefault(); } : undefined,
            onClick: onRowClick ? function(e) { if (_rowHref && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(_rowHref, '_blank'); return; } onRowClick(row, e); } : undefined,
            onAuxClick: onRowClick && _rowHref ? function(e) { if (e.button === 1) { e.preventDefault(); window.open(_rowHref, '_blank'); } } : undefined,
            style: { borderBottom: '1px solid ' + C.border, cursor: onRowClick ? 'pointer' : 'default', userSelect: 'none' },
            onMouseEnter: function(e) { var prev = e.currentTarget.previousElementSibling; e.currentTarget.style.background = C.surfaceHover; if (prev) prev.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { var prev = e.currentTarget.previousElementSibling; e.currentTarget.style.background = 'transparent'; if (prev) prev.style.background = 'transparent'; }
          },
            React.createElement('td', { colSpan: totalNormalCols, style: { padding: '0 ' + (cellPad.split(' ')[1] || '12px') + ' 6px', fontSize: tableFontSize - 1 } },
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '2px 16px' } },
                subRowCols.map(function(col) {
                  var content = col.render ? col.render(row) : (row[col.key] != null ? String(row[col.key]) : '');
                  if (!content && content !== 0) return null;
                  return React.createElement('span', { key: col.key, style: Object.assign({ color: C.textMuted }, col.style) },
                    React.createElement('span', { style: { fontSize: tableFontSize - 2, color: C.textDim, marginRight: 4 } }, col.label + ':'),
                    content
                  );
                })
              )
            )
          );
          return React.createElement(React.Fragment, { key: row.id || ri }, mainRow, subRow);
        })
      )
    ),
    ), // close table wrapper div
    // Empty state
    _isEmpty && React.createElement(Empty, { title: emptyTitle || 'No data', sub: emptySub || '', fill: scrollHeight === 'fill' }),
    // Pagination footer (scroll mode: progress line only — batches load on scroll)
    !_isEmpty && scrollMode && React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 12, color: C.textMuted }
    },
      React.createElement('span', null, 'Showing ' + showingTo.toLocaleString() + ' of ' + totalRows.toLocaleString() + (activeFilterCount > 0 ? ' (filtered from ' + data.length.toLocaleString() + ')' : '')),
      showingTo < totalRows && React.createElement('span', { style: { color: C.textDim } }, 'scroll to load ' + Math.min(pageSize, totalRows - showingTo) + ' more')
    ),
    !_isEmpty && !scrollMode && totalRows > pageSize && React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 13, color: C.textMuted }
    },
      React.createElement('span', null, 'Showing ' + showingFrom + '–' + showingTo + ' of ' + totalRows.toLocaleString() + (activeFilterCount > 0 ? ' (filtered from ' + data.length.toLocaleString() + ')' : '')),
      React.createElement('div', { style: { display: 'flex', gap: 4 } },
        React.createElement('button', {
          onClick: function() { setCurrentPage(0); }, disabled: currentPage === 0,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage === 0 ? C.textDim : C.textMuted, cursor: currentPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, '« First'),
        React.createElement('button', {
          onClick: function() { setCurrentPage(Math.max(0, currentPage - 1)); }, disabled: currentPage === 0,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage === 0 ? C.textDim : C.textMuted, cursor: currentPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, '‹ Prev'),
        React.createElement('span', { style: { padding: '5px 10px', fontSize: 12, color: C.text, fontWeight: 500 } }, 'Page ' + (currentPage + 1) + ' of ' + totalPages),
        React.createElement('button', {
          onClick: function() { setCurrentPage(Math.min(totalPages - 1, currentPage + 1)); }, disabled: currentPage >= totalPages - 1,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage >= totalPages - 1 ? C.textDim : C.textMuted, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, 'Next ›'),
        React.createElement('button', {
          onClick: function() { setCurrentPage(totalPages - 1); }, disabled: currentPage >= totalPages - 1,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage >= totalPages - 1 ? C.textDim : C.textMuted, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, 'Last »')
      )
    )
  );
}

// CSV download helpers
function downloadCSV(filename, csvContent) {
  var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// One CSV cell rule for every export in the app (review 2026-09-28, medium #31): quoted when it
// holds a quote, comma or line break, and defused when it would start a spreadsheet formula
// (= + - @ tab CR) — a marketplace title or note like "=HYPERLINK(...)" ran as a formula when the
// file was opened in Excel. Plain numbers ("-3.5") stay numbers. Same rule as server/services/csv.js
// and the shipping app's shipCsvCell.
function escapeCSV(val) {
  if (val == null) return '';
  var s = String(val);
  if (/^[=+\-@\t\r]/.test(s) && !/^[-+]?\d+(\.\d+)?$/.test(s)) s = "'" + s;
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

function arrayToCSV(headers, rows) {
  var lines = [headers.map(escapeCSV).join(',')];
  rows.forEach(function(row) {
    lines.push(headers.map(function(h) { return escapeCSV(row[h]); }).join(','));
  });
  return lines.join('\n');
}

function downloadTemplate(filename, headers) {
  downloadCSV(filename, headers.join(',') + '\n');
}

// ─── MARKETPLACE CHANNEL ICONS (SVG logos) ───
function channelIcon(channel, size) {
  var s = size || 20;
  var svgProps = { width: s, height: s, viewBox: '0 0 24 24', style: { display: 'block', flexShrink: 0 } };

  if (channel === 'walmart') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#0071DC' }),
      React.createElement('path', { d: 'M12 4.5 L12 9.5 M12 14.5 L12 19.5 M5.2 8 L9.5 10.5 M14.5 13.5 L18.8 16 M18.8 8 L14.5 10.5 M9.5 13.5 L5.2 16', stroke: '#FFC220', strokeWidth: 2.2, strokeLinecap: 'round', fill: 'none' })
    );
  }
  if (channel === 'amazon') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#232F3E' }),
      React.createElement('path', { d: 'M7 13.5 Q12 17 17 13.5', stroke: '#FF9900', strokeWidth: 2, strokeLinecap: 'round', fill: 'none' }),
      React.createElement('path', { d: 'M15.5 12 L17 13.5 L15.5 15', stroke: '#FF9900', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' }),
      React.createElement('text', { x: 12, y: 11.5, textAnchor: 'middle', fill: '#FFFFFF', fontSize: 7, fontWeight: 700, fontFamily: 'Arial' }, 'a')
    );
  }
  if (channel === 'ebay') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#FFFFFF' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 11.5, fill: 'none', stroke: '#E0E0E0', strokeWidth: 0.5 }),
      React.createElement('text', { x: 3.5, y: 15.5, fill: '#E53238', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'e'),
      React.createElement('text', { x: 8, y: 15.5, fill: '#0064D2', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'b'),
      React.createElement('text', { x: 12.5, y: 15.5, fill: '#F5AF02', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'a'),
      React.createElement('text', { x: 17.2, y: 15.5, fill: '#86B817', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'y')
    );
  }
  if (channel === 'mercadolibre') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#FFE600' }),
      React.createElement('ellipse', { cx: 12, cy: 12, rx: 9, ry: 6.5, fill: 'none', stroke: '#2D3277', strokeWidth: 1.4 }),
      React.createElement('path', { d: 'M6.5 12.5 Q9 9.5 11 11.5 Q12 12.5 13 11.5 Q15 9.5 17.5 12.5', stroke: '#2D3277', strokeWidth: 1.3, strokeLinecap: 'round', fill: 'none' })
    );
  }
  if (channel === 'shopify') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#96BF48' }),
      React.createElement('path', { d: 'M14.5 5.5 L14.2 6.5 C14.2 6.5 13.5 6.3 12.7 6.4 C11.5 6.5 11.4 7.2 11.4 7.5 C11.5 8.5 14.6 8.8 14.8 11.3 C14.9 13.2 13.7 14.5 11.8 14.6 C9.6 14.7 8.4 13.4 8.4 13.4 L8.9 11.5 C8.9 11.5 10.1 12.5 11.1 12.4 C11.7 12.4 11.9 11.9 11.9 11.6 C11.8 10.3 9.3 10.4 9.1 8.1 C8.9 6.2 10.3 5.2 12.1 5 C12.9 4.9 13.3 5.1 13.3 5.1 L14.5 5.5 Z M10.5 18 L15 17 L15 7', stroke: '#FFFFFF', strokeWidth: 0.3, fill: '#FFFFFF', fillOpacity: 0.9 })
    );
  }
  // Fallback icons for non-marketplace channels
  var fallbacks = { email: 'mail', phone: 'phone', chat: 'message-square', other: 'pin' };
  return React.createElement(LIcon, { name: fallbacks[channel] || 'pin', size: s * 0.8 });
}

// ─── FILL BAR (color-coded progress) ───
function FillBar({ pct }) {
  var p = Math.min(Math.max(Number(pct) || 0, 0), 100);
  var color = p >= 90 ? C.danger : p >= 70 ? C.warning : p >= 40 ? C.accent : C.success;
  return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
    React.createElement('div', { style: { flex: 1, height: 6, background: C.bg, borderRadius: 3, minWidth: 40, overflow: 'hidden' } },
      React.createElement('div', { style: { width: p + '%', height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' } })
    ),
    React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: color, minWidth: 32, textAlign: 'right' } }, p.toFixed(0) + '%')
  );
}

// ─── BIN BADGE (type/status colored badge) ───
function BinBadge({ type, value }) {
  var colors = {};
  if (type === 'bin_type') {
    colors = { shelf: { bg: C.accentSoft, text: C.accent }, rack: { bg: C.purpleSoft, text: C.purple }, floor: { bg: C.warningSoft, text: C.warning }, pick: { bg: C.successSoft, text: C.success }, overstock: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted }, staging: { bg: 'rgba(6,182,212,0.12)', text: '#06B6D4' }, dock: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' } };
  } else {
    colors = { active: { bg: C.successSoft, text: C.success }, full: { bg: C.dangerSoft, text: C.danger }, unsellable: { bg: C.accentSoft, text: C.accent }, damaged: { bg: C.warningSoft, text: C.warning }, inactive: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted } };
  }
  var c = colors[value] || { bg: C.warningSoft, text: C.warning };
  return React.createElement('span', {
    style: { background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }
  }, value);
}

// ─── CODE 128B BARCODE SVG GENERATOR ───
var _c128Patterns = [
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11100101100','11100100110',
  '11101100100','11100110100','11100110010','11011011000','11011000110',
  '11000110110','10100011000','10001011000','10001000110','10110001000',
  '10001101000','10001100010','11010001000','11000101000','11000100010',
  '10110111000','10110001110','10001101110','10111011000','10111000110',
  '10001110110','11101110110','11010001110','11000101110','11011101000',
  '11011100010','11011101110','11101011000','11101000110','11100010110',
  '11101101000','11101100010','11100011010','11101111010','11001000010',
  '11110001010','10100110000','10100001100','10010110000','10010000110',
  '10000101100','10000100110','10110010000','10110000100','10011010000',
  '10011000010','10000110100','10000110010','11000010010','11001010000',
  '11110111010','11000010100','10001111010','10100111100','10010111100',
  '10010011110','10111100100','10011110100','10011110010','11110100100',
  '11110010100','11110010010','11011011110','11011110110','11110110110',
  '10101111000','10100011110','10001011110','10111101000','10111100010',
  '10001111100','11110101000','11110100010','10111011110','10111101110',
  '11101011110','11110101110','11010000100','11010010000','11010011100','11000111010',
  '1100011101011'
];

function generateCode128SVG(text, barHeight, moduleWidth) {
  barHeight = barHeight || 60;
  moduleWidth = moduleWidth || 2;
  var startCode = 104; // Code B
  var codes = [startCode];
  var checksum = startCode;
  for (var i = 0; i < text.length; i++) {
    var val = text.charCodeAt(i) - 32;
    if (val < 0 || val > 94) val = 0;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106); // stop
  var bits = '';
  for (var j = 0; j < codes.length; j++) bits += _c128Patterns[codes[j]];
  var totalWidth = bits.length * moduleWidth;
  var bars = '';
  for (var k = 0; k < bits.length; k++) {
    if (bits[k] === '1') {
      bars += '<rect x="' + (k * moduleWidth) + '" y="0" width="' + moduleWidth + '" height="' + barHeight + '"/>';
    }
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalWidth + ' ' + barHeight + '" width="100%">' + bars + '</svg>';
}

// Print-optimized: renders in mm units so barcode prints at exact physical size with no CSS scaling
function generateCode128SVGmm(text, targetWidthMm, barHeightMm) {
  barHeightMm = barHeightMm || 10;
  var startCode = 104;
  var codes = [startCode];
  var checksum = startCode;
  for (var i = 0; i < text.length; i++) {
    var val = text.charCodeAt(i) - 32;
    if (val < 0 || val > 94) val = 0;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106);
  var bits = '';
  for (var j = 0; j < codes.length; j++) bits += _c128Patterns[codes[j]];
  var modW = targetWidthMm / bits.length;
  var bars = '';
  for (var k = 0; k < bits.length; k++) {
    if (bits[k] === '1') {
      bars += '<rect x="' + (k * modW).toFixed(4) + '" y="0" width="' + modW.toFixed(4) + '" height="' + barHeightMm + '"/>';
    }
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" width="' + targetWidthMm + 'mm" height="' + barHeightMm + 'mm" viewBox="0 0 ' + targetWidthMm + ' ' + barHeightMm + '">' + bars + '</svg>';
}

// Generate barcode as data URL image using bwip-js (reliable Code 128)
function generateBarcodeImg(text, widthMm, heightMm) {
  if (!text || !window.bwipjs) return '';
  try {
    var canvas = document.createElement('canvas');
    bwipjs.toCanvas(canvas, {
      bcid: 'code128',
      text: text,
      scale: 3,
      height: Math.max(6, Math.round(heightMm * 0.8)),
      includetext: false,
      textxalign: 'center',
    });
    var dataUrl = canvas.toDataURL('image/png');
    return '<img src="' + dataUrl + '" style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;image-rendering:pixelated;" />';
  } catch (e) {
    console.error('Barcode generation failed:', e);
    return '';
  }
}

// HTML-escape for values interpolated into print popups (document.write) and any other markup
// string (review M6 #71). Vendor titles, SKUs, barcodes, PO numbers and bin codes all come from
// vendor confirmations, CSV imports and channel syncs — a bare '&' broke label layout and
// markup would run with the user's session in the same-origin popup.
function escHtml(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Calendar-date formatter for DATE columns (review M6 #75). pg returns a DATE as a JS Date at
// server-local midnight, serialised "YYYY-MM-DDT00:00:00.000Z" (prod Node runs in UTC), so
// `new Date(v).toLocaleDateString()` in an Eastern browser printed the PREVIOUS day. Read the
// civil date off the string and build a local Date from its parts; never parse it as an instant.
// Returns '' for empty input so exports stay blank; callers add their own '—'.
function fmtDateOnly(v, opts, locale) {
  if (v == null || v === '') return '';
  var m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  var d = m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(locale || undefined, opts || undefined);
}

// ─── Product label rendering shared by the label printers (Mercado Libre labels, 2026-09-29) ───
// A label = { code, title, sku, condition, expiry }; `tpl` is a label_templates row, null falls
// back to the 2.25 x 1.25 in roll the older printers assumed. The markup mirrors the FBA / FBM
// printers (barcode, code in bold, title, bottom row) so a MELI label on the same stock looks
// like an FNSKU label. `opts.skuPrefix` prints "SKU: " before the SKU, as Mercado Libre's own
// label file does.
function labelTemplateGeometry(tpl) {
  return {
    widthMm: tpl ? parseFloat(tpl.width_mm) : 57.15,
    heightMm: tpl ? parseFloat(tpl.height_mm) : 31.75,
    fontSize: tpl ? (tpl.font_size || 12) : 12,
    fields: tpl ? (tpl.fields || ['fnsku', 'sku', 'title']) : ['fnsku', 'sku', 'title'],
    showBarcode: tpl ? tpl.show_barcode !== false : true,
    isSheet: tpl ? tpl.label_type === 'sheet' : false,
    cols: tpl ? (tpl.columns || 1) : 1,
    rowsPerSheet: tpl ? (tpl.rows_per_sheet || 1) : 1,
    pageW: tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9,
    pageH: tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4,
  };
}

function productLabelBottomLine(label, fields, opts) {
  var parts = [];
  if (fields.indexOf('condition') >= 0 && label.condition) parts.push(label.condition);
  if (fields.indexOf('sku') >= 0 && label.sku) parts.push((opts && opts.skuPrefix ? 'SKU: ' : '') + label.sku);
  if (fields.indexOf('expiry') >= 0 && label.expiry) parts.push('Exp: ' + label.expiry);
  return parts.join(' | ');
}

// The title line of every product label: up to two lines, the way Mercado Libre's own label wraps
// a long title (Anthony 2026-09-29: "some of the titles are cut off", then "fix those too" for
// the FBA, FBM and WFS printers). A 2 x 1 label has the room: barcode, code, two title lines and
// the SKU line add up to about 18 of its 21 mm.
function productLabelTitleCss(pt, lines) {
  if (lines === 1) return 'font-size:' + pt + 'pt;color:#333;text-align:center;line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;';
  return 'font-size:' + pt + 'pt;color:#333;text-align:center;line-height:1.15;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;max-width:100%;word-break:break-word;';
}

// How many title lines the label has room for between the code line and the bottom row, and at
// what size: two when two fit (the type comes down half a point at a time, to 6 pt, to make them
// fit), else one at the template's size. A 2 x 1 label at 12 pt has room for two lines at 6 pt;
// the FBA 2 x 1 at 16 pt does not, its title stays on one line unless the template's type is
// made smaller. Sizes in mm; a text line is 1.15 of its type size.
function productLabelTitleFit(heightMm, fontSize, barcodeHeightMm) {
  var pad = 2, gap = 0.3;
  var mm = function(pt) { return pt * 0.3528; };
  var codePt = Math.max(5, fontSize - 4), bottomPt = Math.max(4, fontSize - 6);
  var titlePt = Math.max(4, fontSize - 6);
  var used = pad * 2 + (barcodeHeightMm || 0) + gap + mm(codePt) * 1.2 + gap + mm(bottomPt) * 1.2 + gap;
  var room = heightMm - used;
  for (var pt = titlePt; pt >= 6; pt -= 0.5) {
    if (mm(pt) * 1.15 * 2 <= room) return { lines: 2, pt: pt };
  }
  return { lines: 1, pt: titlePt };
}

function productLabelHtml(label, tpl, opts) {
  var g = labelTemplateGeometry(tpl);
  var code = label.code || '';
  var small = Math.max(4, g.fontSize - 6);
  var fit = productLabelTitleFit(g.heightMm, g.fontSize, g.showBarcode ? Math.max(4, g.heightMm * 0.28) : 0);
  var lbl = '<div style="width:' + g.widthMm + 'mm;height:' + g.heightMm + 'mm;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;">';
  if (g.showBarcode && code) {
    lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + generateBarcodeImg(code, g.widthMm - 4, Math.max(4, g.heightMm * 0.28)) + '</div>';
  }
  if (code) lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, g.fontSize - 4) + 'pt;font-weight:bold;letter-spacing:0.5px;margin-bottom:0.3mm;text-align:center;">' + escHtml(code) + '</div>';
  if (label.title && g.fields.indexOf('title') >= 0) {
    lbl += '<div style="' + productLabelTitleCss(fit.pt, fit.lines) + '">' + escHtml(label.title) + '</div>';
  }
  var bottom = productLabelBottomLine(label, g.fields, opts);
  if (bottom) lbl += '<div style="font-size:' + small + 'pt;color:#333;text-align:center;margin-top:0.3mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">' + escHtml(bottom) + '</div>';
  return lbl + '</div>';
}

// Lays the labels out: pages of cols x rows for a sheet template, a plain flow for a roll.
function productLabelPagesHtml(labelHtmls, tpl) {
  var g = labelTemplateGeometry(tpl);
  if (!g.isSheet) return '<div style="display:flex;flex-wrap:wrap;gap:0;justify-content:center;">' + labelHtmls.join('') + '</div>';
  var mH = (g.pageW - (g.cols * g.widthMm)) / (g.cols + 1);
  var mV = (g.pageH - (g.rowsPerSheet * g.heightMm)) / (g.rowsPerSheet + 1);
  var perPage = g.cols * g.rowsPerSheet;
  var pages = [];
  for (var p = 0; p < labelHtmls.length; p += perPage) {
    pages.push('<div style="width:' + g.pageW + 'mm;height:' + g.pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;display:flex;flex-wrap:wrap;align-content:flex-start;gap:0;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + g.cols + ',' + g.widthMm + 'mm);grid-template-rows:repeat(' + g.rowsPerSheet + ',' + g.heightMm + 'mm);gap:0;width:100%;">' + labelHtmls.slice(p, p + perPage).join('') + '</div></div>');
  }
  return pages.join('');
}

// Opens the print window and drives printing from the opener: the page CSP blocks an inline
// script in the popup and window.onload is unreliable for document.write popups. Returns false
// when the browser blocked the popup.
function openLabelPrintWindow(bodyHtml, title, tpl) {
  var g = labelTemplateGeometry(tpl);
  var pw = window.open('', '_blank', 'width=600,height=800');
  if (!pw) return false;
  pw.document.write('<!DOCTYPE html><html><head><title>' + escHtml(title || 'Labels') + '</title><style>@media print{body{margin:0;}@page{size:' + (g.isSheet ? g.pageW + 'mm ' + g.pageH + 'mm' : g.widthMm + 'mm ' + g.heightMm + 'mm') + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="font-family:Arial,sans-serif;margin:0;padding:0;">' + bodyHtml + '</body></html>');
  pw.document.close();
  setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
  return true;
}

// Splits a title into at most two lines that fit `maxMm` at `pt`, shrinking the type half a
// point at a time (never under 4 pt) before cutting. `doc` is a jsPDF document with the font
// already set; its splitTextToSize measures the real glyph widths.
function productLabelTitleLines(doc, title, maxMm, pt, maxLines) {
  var cap = maxLines === 1 ? 1 : 2;
  var lines = [];
  for (;;) {
    doc.setFontSize(pt);
    lines = doc.splitTextToSize(String(title), maxMm);
    if (lines.length <= cap || pt <= 4) break;
    pt = Math.max(4, pt - 0.5);
  }
  if (lines.length > cap) {
    lines = lines.slice(0, cap);
    lines[cap - 1] = lines[cap - 1].slice(0, Math.max(0, lines[cap - 1].length - 2)) + '…';
  }
  return { lines: lines, pt: pt };
}

// The same labels as one PDF (jsPDF + bwip-js), for a vendor who applies the labels: one page
// per label on a roll, cols x rows per page on a sheet. Returns the jsPDF document, or null when
// a library is missing.
function productLabelsPdf(labels, tpl, opts) {
  var jspdfNs = window.jspdf || window.jsPDF;
  var JsPDFCtor = jspdfNs && (jspdfNs.jsPDF || jspdfNs);
  if (!JsPDFCtor || !window.bwipjs) return null;
  var g = labelTemplateGeometry(tpl);
  var pad = 2;
  var barcodeHeightMm = Math.max(4, g.heightMm * 0.28);
  var barcodeWidthMm = g.widthMm - pad * 2;
  var ptToMm = function(pt) { return pt * 0.3528; };
  var clip = function(str, maxMm, sizePt) {
    if (!str) return '';
    var maxChars = Math.floor(maxMm / (ptToMm(sizePt) * 0.5));
    return str.length > maxChars ? str.slice(0, Math.max(0, maxChars - 1)) + '…' : str;
  };
  var renderAt = function(doc, label, x, y) {
    var cursorY = y + pad;
    var code = label.code || '';
    if (g.showBarcode && code) {
      var canvas = document.createElement('canvas');
      window.bwipjs.toCanvas(canvas, { bcid: 'code128', text: code, scale: 3, height: barcodeHeightMm, includetext: false, paddingleft: 4, paddingright: 4 });
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', x + pad, cursorY, barcodeWidthMm, barcodeHeightMm);
      cursorY += barcodeHeightMm + 0.5;
    }
    if (code) {
      var codePt = Math.max(5, g.fontSize - 4);
      doc.setFont('courier', 'bold'); doc.setFontSize(codePt);
      cursorY += ptToMm(codePt) * 0.75;
      doc.text(code, x + g.widthMm / 2, cursorY, { align: 'center' });
      cursorY += 0.4;
    }
    if (label.title && g.fields.indexOf('title') >= 0) {
      // Up to two lines, measured with the real font (Anthony 2026-09-29: "some of the titles are
      // cut off"): the type shrinks half a point at a time before anything is cut, and only a
      // title that still needs a third line at 4 pt loses its tail.
      var fit = productLabelTitleFit(g.heightMm, g.fontSize, g.showBarcode ? barcodeHeightMm : 0);
      var titlePt = fit.pt;
      doc.setFont('helvetica', 'normal');
      var titleLines = productLabelTitleLines(doc, label.title, g.widthMm - pad * 2, titlePt, fit.lines);
      titlePt = titleLines.pt;
      doc.setFontSize(titlePt);
      titleLines.lines.forEach(function(line, idx) {
        cursorY += ptToMm(titlePt) * (idx === 0 ? 0.75 : 1.15);
        doc.text(line, x + g.widthMm / 2, cursorY, { align: 'center' });
      });
    }
    var bottom = productLabelBottomLine(label, g.fields, opts);
    if (bottom) {
      var botPt = Math.max(4, g.fontSize - 6);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(botPt);
      doc.text(clip(bottom, g.widthMm - pad * 2, botPt), x + g.widthMm / 2, y + g.heightMm - pad, { align: 'center' });
    }
  };
  var doc;
  if (g.isSheet) {
    var perPage = g.cols * g.rowsPerSheet;
    var mH = (g.pageW - g.cols * g.widthMm) / (g.cols + 1);
    var mV = (g.pageH - g.rowsPerSheet * g.heightMm) / (g.rowsPerSheet + 1);
    var pageOrient = g.pageW >= g.pageH ? 'landscape' : 'portrait';
    doc = new JsPDFCtor({ orientation: pageOrient, unit: 'mm', format: [g.pageW, g.pageH] });
    labels.forEach(function(label, i) {
      var slot = i % perPage;
      if (i > 0 && slot === 0) doc.addPage([g.pageW, g.pageH], pageOrient);
      renderAt(doc, label, mH + (slot % g.cols) * (g.widthMm + mH), mV + Math.floor(slot / g.cols) * (g.heightMm + mV));
    });
  } else {
    var rollOrient = g.widthMm >= g.heightMm ? 'landscape' : 'portrait';
    doc = new JsPDFCtor({ orientation: rollOrient, unit: 'mm', format: [g.widthMm, g.heightMm] });
    labels.forEach(function(label, i) {
      if (i > 0) doc.addPage([g.widthMm, g.heightMm], rollOrient);
      renderAt(doc, label, 0, 0);
    });
  }
  return doc;
}
window.labelTemplateGeometry = labelTemplateGeometry; window.productLabelHtml = productLabelHtml; window.productLabelPagesHtml = productLabelPagesHtml;
window.productLabelTitleCss = productLabelTitleCss; window.productLabelTitleLines = productLabelTitleLines; window.productLabelTitleFit = productLabelTitleFit;
window.openLabelPrintWindow = openLabelPrintWindow; window.productLabelsPdf = productLabelsPdf;

function printBinLabel(binCode) {
  var barcodeSVG = generateCode128SVG(binCode, 80, 3);
  var html = '<!DOCTYPE html><html><head><title>Bin Label</title><style>' +
    '@page { size: 4in 4in; margin: 0; }' +
    'body { margin: 0; padding: 0; width: 4in; height: 4in; display: flex; flex-direction: column; align-items: center; justify-content: center; font-family: Arial, Helvetica, sans-serif; }' +
    '.bin-code { font-size: 48pt; font-weight: 800; letter-spacing: 2px; margin-bottom: 16px; text-align: center; }' +
    '.barcode { margin-bottom: 12px; }' +
    '.barcode svg { max-width: 3.4in; height: auto; }' +
    '.sub-text { font-size: 14pt; color: #333; letter-spacing: 1px; }' +
    '</style></head><body>' +
    '<div class="bin-code">' + escHtml(binCode) + '</div>' +
    '<div class="barcode">' + barcodeSVG + '</div>' +
    '<div class="sub-text">' + escHtml(binCode) + '</div>' +
    '</body></html>';
  var win = window.open('', '_blank', 'width=420,height=420');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  // Drive printing from the opener via a timer — win.onload set after document.write does not
  // reliably fire, leaving the label stuck in preview. The barcode is inline SVG so 300ms is ample.
  setTimeout(function() { try { win.focus(); win.print(); } catch (e) { /* popup closed */ } }, 300);
}

// ─── PDF417 barcode generation (via bwip-js) ───
function renderPDF417Canvas(data, opts) {
  // Throws if the data exceeds the symbol's capacity (bwip-js auto-grows rows
  // for a given column count but hits PDF417's 90-row ceiling).
  var canvas = document.createElement('canvas');
  var bwipOpts = {
    bcid: 'pdf417',
    text: data,
    scale: opts.scale || 2,
    columns: opts.columns || 4,
    rowmult: opts.rowmult || 2,
    eclevel: opts.eclevel || 6,
    padding: 0,
  };
  if (opts.rows) bwipOpts.rows = opts.rows;
  bwipjs.toCanvas(canvas, bwipOpts);
  // If rotate option, rotate 90° CW by drawing to a new canvas with swapped dimensions
  if (opts.rotate) {
    var rotCanvas = document.createElement('canvas');
    rotCanvas.width = canvas.height;
    rotCanvas.height = canvas.width;
    var ctx = rotCanvas.getContext('2d');
    ctx.translate(rotCanvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(canvas, 0, 0);
    canvas = rotCanvas;
  }
  return canvas;
}

function pdf417CanvasImg(canvas, imgStyle) {
  return '<img src="' + canvas.toDataURL('image/png') + '" style="' + (imgStyle || 'width:100%;height:auto;image-rendering:pixelated;') + '" />';
}

function generatePDF417SVG(data, opts) {
  opts = opts || {};
  try {
    return pdf417CanvasImg(renderPDF417Canvas(data, opts), opts.imgStyle);
  } catch (e) {
    console.error('PDF417 generation failed:', e);
    return '<div style="color:red;font-size:10px;">Barcode error</div>';
  }
}

// FBA case-label content barcode. The payload grows with each distinct FNSKU in
// the box, so a single fixed column count overflows on large mixed boxes (6
// columns tops out near 25 SKUs). Step up through wider symbols until the data
// fits AND the printed height stays within the label's vertical budget (aspect
// ≤ 0.63 of the 3.5in print width ≈ 2.2in tall). PDF417 itself caps out near
// 1,800 chars (~75 SKUs/box) — past that no symbol size can encode the box.
var CASE_PDF417_PRESETS = [
  { columns: 6, rows: 24, rowmult: 3, eclevel: 4 }, // typical boxes — same symbol as always
  { columns: 8, rowmult: 2, eclevel: 5 },
  { columns: 10, rowmult: 2, eclevel: 5 },
  { columns: 12, rowmult: 2, eclevel: 5 },
  { columns: 14, rowmult: 2, eclevel: 5 },
  { columns: 16, rowmult: 2, eclevel: 5 },
];
function generateCasePDF417SVG(data, imgStyle) {
  var best = null;
  for (var i = 0; i < CASE_PDF417_PRESETS.length; i++) {
    var p = CASE_PDF417_PRESETS[i];
    var canvas;
    try {
      canvas = renderPDF417Canvas(data, { scale: 4, columns: p.columns, rows: p.rows, rowmult: p.rowmult, eclevel: p.eclevel });
    } catch (e) { continue; }
    var aspect = canvas.height / canvas.width;
    if (aspect <= 0.63) return pdf417CanvasImg(canvas, imgStyle);
    if (!best || aspect < best.aspect) best = { canvas: canvas, aspect: aspect };
  }
  // Encodable but over the height budget at every size — a tall barcode beats none.
  if (best) return pdf417CanvasImg(best.canvas, imgStyle);
  console.error('PDF417 case barcode: data exceeds symbology capacity (' + data.length + ' chars)');
  return '<div style="color:red;font-size:10px;">Barcode error</div>';
}

// Build Amazon FBA 2D barcode data string
// Format: AMZN,PO:{shipmentId},FNSKU:{fnsku},QTY:{qty}[,EXP:{YYMMDD}]
// Multiple items repeat the FNSKU/QTY/EXP block
function buildAmazonCaseBarcodeData(shipmentId, items) {
  var parts = ['AMZN', 'PO:' + (shipmentId || '').toUpperCase()];
  (items || []).forEach(function(item) {
    parts.push('FNSKU:' + (item.fnsku || '').toUpperCase());
    parts.push('QTY:' + (item.qty || 1));
    if (item.expiry) parts.push('EXP:' + item.expiry); // YYMMDD
  });
  return parts.join(',');
}

// ─── SEARCH INPUT (with inline clear button) ───
function SearchInput(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var mergedStyle = Object.assign({ width: '100%', boxSizing: 'border-box' }, props.style || {}, { paddingRight: value ? 32 : (props.style && props.style.paddingRight) || 14 });
  var wrapStyle = { position: 'relative', width: mergedStyle.width, flex: props.flex };
  var inputProps = Object.assign({}, props, { style: mergedStyle, flex: undefined });
  delete inputProps.flex;
  return React.createElement('div', { style: wrapStyle },
    React.createElement('input', inputProps),
    value ? React.createElement('button', {
      type: 'button', 'aria-label': 'Clear search',
      onClick: function() { if (onChange) onChange({ target: { value: '' } }); },
      style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 4 },
      onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; e.currentTarget.style.color = C.text; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; }
    }, React.createElement(LIcon, { name: 'x', size: 14 })) : null
  );
}

// ─── BIN FORMAT HELPER ───
// Normalize bin location: "A100" → "A-100", "A1" → "A-01", "A100/B5" → "A-100/B-05"
function formatSingleBin(val) {
  // LOC* bins (e.g. LOC800) are staging/location codes that carry no dash — leave them as typed.
  // Only the single-letter zone bins get the dash + zero-pad treatment (B80 -> B-80).
  var m = val.match(/^([A-Z]+)-?(\d+)$/);
  if (m && m[1] !== 'LOC') {
    var num = m[2].length < 2 ? ('0' + m[2]) : m[2];
    return m[1] + '-' + num;
  }
  return val;
}
function formatBin(raw) {
  if (!raw) return '';
  var val = raw.trim().toUpperCase();
  if (!val) return '';
  return val.split('/').map(function(part) { return formatSingleBin(part.trim()); }).join('/');
}
window.formatBin = formatBin;
// ─── Country list (ISO 3166-1 alpha-2) ───
// One source for every country dropdown in the app. app-utils.js is the first file
// in all four bundles, so app-pages / app-main / app-cs can all reach these.
// app-shipping.js keeps its own SHIP_ISO_COUNTRIES (shipping bundle, carrier-scoped).
var ISO_COUNTRIES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
var _isoRegionNames = null;
// Names come from Intl.DisplayNames rather than a 250-row label table; a code the
// browser cannot name falls back to the code itself.
function countryName(code) {
  if (!code) return '';
  try {
    if (!_isoRegionNames) _isoRegionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return _isoRegionNames.of(String(code).toUpperCase()) || code;
  } catch (e) { return code; }
}
// Sorted once by display name; every dropdown reuses the same array.
function countryOptions() {
  if (!window._isoCountriesSorted) {
    window._isoCountriesSorted = ISO_COUNTRIES.slice().sort(function(a, b) { return countryName(a).localeCompare(countryName(b)); });
  }
  return window._isoCountriesSorted;
}
// Options for a country SearchSelect (type to filter, or scroll). The ISO code rides in
// the label so typing "US" still surfaces United States — SearchSelect ranks a word start
// ("(US)") above a bare substring, which a name-only label would not give us. A legacy or
// free-text value that is not a known code keeps its own entry, so opening an old record
// does not silently rewrite it.
function countryOptionList(current) {
  var opts = countryOptions().map(function(c) { return { value: c, label: countryName(c) + ' (' + c + ')' }; });
  if (current && ISO_COUNTRIES.indexOf(String(current).toUpperCase()) === -1) {
    opts.unshift({ value: current, label: current + ' (saved)' });
  }
  return opts;
}
window.ISO_COUNTRIES = ISO_COUNTRIES; window.countryName = countryName;
window.countryOptions = countryOptions; window.countryOptionList = countryOptionList;
// ─── Font size for the rich-text toolbars ───
// Shared by the PO "Email Vendor" modal (app-main.js) and the PO e-mail template editor
// (app-pages.js), so a size set in either place behaves identically.
//
// execCommand('fontSize') only speaks the legacy 1-7 scale and emits <font size=N>, which
// e-mail clients render inconsistently; turning on styleWithCSS yields CSS keywords
// ('xx-large'), not px. So size 7 is applied purely as a sentinel and those exact <font>
// nodes are then rewritten into spans carrying a real px size. server/services/sanitize.js
// allowlists font-size, so the span survives the outbound sanitiser and reaches the vendor.
var FONT_SIZES = [
  { px: 12, label: 'Small' },
  { px: 14, label: 'Normal' },
  { px: 16, label: 'Medium' },
  { px: 18, label: 'Large' },
  { px: 24, label: 'Huge' },
];

function applyFontSize(rootEl, px) {
  if (!rootEl || !px) return;
  document.execCommand('fontSize', false, '7');
  // Only the nodes this call just created carry size="7" — anything the user genuinely
  // wanted at the legacy maximum would have come in through a pasted template, and those
  // are rewritten here too rather than left as <font>, which is the better outcome anyway.
  var marked = rootEl.querySelectorAll('font[size="7"]');
  for (var i = 0; i < marked.length; i++) {
    var f = marked[i];
    var span = document.createElement('span');
    span.style.fontSize = px + 'px';
    while (f.firstChild) span.appendChild(f.firstChild);
    if (f.parentNode) f.parentNode.replaceChild(span, f);
  }
}

// The <select> has to be clickable, so unlike the toolbar buttons it cannot preventDefault
// on mousedown — focusing it blurs the editor and drops the selection. Stash the range on
// the way in and put it back before applying.
function FontSizeSelect({ targetRef, size }) {
  var rangeRef = React.useRef(null);
  var small = size === 'sm';
  return React.createElement('select', {
    value: '',
    title: 'Font size',
    onMouseDown: function() {
      var sel = window.getSelection();
      rangeRef.current = (sel && sel.rangeCount) ? sel.getRangeAt(0).cloneRange() : null;
    },
    onChange: function(e) {
      var px = parseInt(e.target.value, 10);
      e.target.value = '';
      if (!px) return;
      var el = targetRef && targetRef.current;
      if (!el) return;
      el.focus();
      var r = rangeRef.current;
      if (r) {
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      }
      applyFontSize(el, px);
    },
    style: {
      padding: small ? '2px 4px' : '3px 6px', fontSize: small ? 11 : 12, borderRadius: 4,
      border: 'none', background: C.bg, color: C.text, cursor: 'pointer',
      fontFamily: 'inherit', outline: 'none',
      // Without an explicit colour-scheme the browser paints both the closed control and
      // its native option popup light, so the picker read as a white block on a dark
      // toolbar. A transparent background made the closed control worse still.
      colorScheme: getThemeName() === 'light' ? 'light' : 'dark'
    }
  },
    React.createElement('option', { value: '' }, 'Size'),
    FONT_SIZES.map(function(f) { return React.createElement('option', { key: f.px, value: f.px }, f.label + ' (' + f.px + ')'); })
  );
}
window.FONT_SIZES = FONT_SIZES; window.applyFontSize = applyFontSize; window.FontSizeSelect = FontSizeSelect;

// ─── COPY CELL COMPONENT ───
function CopyCell({ value, children }) {
  var [copied, setCopied] = React.useState(false);
  return React.createElement('div', {
    style: { display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' },
    className: 'copy-cell'
  },
    children || value,
    React.createElement('span', {
      className: 'copy-icon',
      onClick: function(e) { e.stopPropagation(); navigator.clipboard.writeText(String(value || '')); setCopied(true); setTimeout(function() { setCopied(false); }, 1500); },
      style: { cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', display: 'inline-flex', alignItems: 'center' }
    }, React.createElement(LIcon, { name: copied ? 'check' : 'clipboard', size: 12, color: copied ? C.success : C.textMuted }))
  );
}

// ─── IMAGE LIGHTBOX ───
// Reusable full-screen image viewer. Pass `images` (string[]), `index` (current),
// `onIndexChange` (setter for index), `onClose`. Returns null when not active.
function ImageLightbox(props) {
  var images = props.images || [];
  var index = props.index;
  var onIndexChange = props.onIndexChange || function() {};
  var onClose = props.onClose || function() {};
  if (!images.length || index == null || index < 0 || index >= images.length) return null;
  var prev = function(e) { e && e.stopPropagation && e.stopPropagation(); onIndexChange((index - 1 + images.length) % images.length); };
  var next = function(e) { e && e.stopPropagation && e.stopPropagation(); onIndexChange((index + 1) % images.length); };
  return React.createElement('div', {
    onClick: onClose,
    style: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }
  },
    React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); onClose(); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '×'),
    images.length > 1 && React.createElement('div', {
      style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10002 }
    }, (index + 1) + ' / ' + images.length),
    images.length > 1 && React.createElement('button', {
      onClick: prev,
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '‹'),
    images.length > 1 && React.createElement('button', {
      onClick: next,
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '›'),
    React.createElement('img', {
      src: images[index],
      onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' }
    }),
    images.length > 1 && React.createElement('div', {
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 }
    },
      images.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { onIndexChange(i); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === index ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );
}
window.ImageLightbox = ImageLightbox;

// ─── PHOTO UPLOAD PANEL (QR-code phone-camera flow) ───
// Generates a token, shows QR + URL for the warehouse user to scan with their phone,
// polls for uploaded photos every 2s, displays thumbnails. Pass token back to parent
// via onTokenChange so the parent can include it when submitting the receive.
// Props:
//   onTokenChange(token|null) — fires when token becomes available or is reset
//   onPhotosChange(photos)    — optional, fires whenever photos array updates
//   resetSignal               — change this prop value to force a reset (e.g. after submit)
function PhotoUploadPanel(props) {
  var _o = React.useState(false); var open = _o[0], setOpen = _o[1];
  var _t = React.useState(null); var token = _t[0], setToken = _t[1];
  var _p = React.useState([]); var photos = _p[0], setPhotos = _p[1];
  var _l = React.useState(false); var loading = _l[0], setLoading = _l[1];
  var lastSignal = React.useRef(props.resetSignal);

  // Reset on signal change
  React.useEffect(function() {
    if (props.resetSignal !== lastSignal.current) {
      lastSignal.current = props.resetSignal;
      setOpen(false); setToken(null); setPhotos([]);
      if (props.onTokenChange) props.onTokenChange(null);
      if (props.onPhotosChange) props.onPhotosChange([]);
    }
  }, [props.resetSignal]);

  // Generate token when opening
  function handleOpen() {
    if (open) return;
    setOpen(true);
    setLoading(true);
    api('/receiving/photo-token', { method: 'POST', body: JSON.stringify({}) }).then(function(r) {
      setToken(r.token);
      if (props.onTokenChange) props.onTokenChange(r.token);
    }).catch(function() {})
      .finally(function() { setLoading(false); });
  }

  // Poll every 2s
  React.useEffect(function() {
    if (!token || !open) return;
    var alive = true;
    var fetchOnce = function() {
      api('/receiving/photo-status/' + token).then(function(r) {
        if (!alive) return;
        var next = r.photos || [];
        setPhotos(next);
        if (props.onPhotosChange) props.onPhotosChange(next);
      }).catch(function() {});
    };
    fetchOnce();
    var int = setInterval(fetchOnce, 2000);
    return function() { alive = false; clearInterval(int); };
  }, [token, open]);

  function deletePhoto(id) {
    api('/receiving/photo/' + id, { method: 'DELETE' }).then(function() {
      var next = photos.filter(function(p) { return p.id !== id; });
      setPhotos(next);
      if (props.onPhotosChange) props.onPhotosChange(next);
    }).catch(function() {});
  }

  // Render QR as a fixed-size <img> (data URL). A raw createSvgTag <svg> kept its intrinsic
  // pixel size — which grows with the URL length — and overflowed its 140px box, drawing on top
  // of the instructions and uploaded thumbnails to its right. An <img> is clamped to the box.
  var qrSrc = '';
  var phoneUrl = '';
  if (token && open) {
    phoneUrl = window.location.origin + '/u/' + token;
    try {
      var qr = window.qrcode(0, 'M');
      qr.addData(phoneUrl);
      qr.make();
      qrSrc = qr.createDataURL(6, 2);
    } catch (e) { qrSrc = ''; }
  }

  if (!open) {
    // Caller marks photos required (e.g. items with no catalog match must have a photo). Reflect
    // that in the label + amber styling instead of always saying "(optional)".
    var req = !!props.required;
    var edge = req ? '#F59E0B' : C.border, hoverEdge = req ? '#F59E0B' : C.accent;
    return React.createElement('button', {
      onClick: handleOpen,
      onMouseEnter: function(e) { e.currentTarget.style.background = hoverEdge + '22'; e.currentTarget.style.borderColor = hoverEdge; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = edge; },
      style: { background: 'transparent', border: '1px dashed ' + edge, color: req ? '#B45309' : C.textMuted, padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: req ? 600 : 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease' }
    }, React.createElement(LIcon, { name: 'camera', size: 14 }), req ? 'Add photos (required)' : 'Add photos (optional)');
  }

  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginBottom: 8 } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12 } },
      // QR + caption (left)
      React.createElement('div', { style: { flexShrink: 0, textAlign: 'center' } },
        loading
          ? React.createElement('div', { style: { width: 140, height: 140, background: C.bg, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim, fontSize: 11 } }, 'Generating…')
          : (qrSrc
              ? React.createElement('img', { src: qrSrc, alt: 'Scan to upload photos', style: { width: 140, height: 140, boxSizing: 'border-box', background: '#fff', padding: 4, borderRadius: 6, display: 'block' } })
              : React.createElement('div', { style: { width: 140, height: 140, background: '#fff', borderRadius: 6 } })),
        React.createElement('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 4 } }, 'Scan with phone camera')
      ),
      // Right: instructions, link, photos
      React.createElement('div', { style: { flex: 1, minWidth: 0 } },
        React.createElement('div', { style: { fontSize: 12, color: C.text, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement(LIcon, { name: 'camera', size: 14, color: C.accent }),
          React.createElement('span', { style: { fontWeight: 600 } }, 'Take photos with your phone'),
          React.createElement('button', { onClick: function() { setOpen(false); setToken(null); setPhotos([]); if (props.onTokenChange) props.onTokenChange(null); if (props.onPhotosChange) props.onPhotosChange([]); },
            style: { marginLeft: 'auto', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' } },
            React.createElement(LIcon, { name: 'x', size: 14 }))
        ),
        React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } },
          'Scan the QR with your phone camera, then take one or more photos. They appear here as they upload.'),
        phoneUrl && React.createElement('div', { style: { fontSize: 10, color: C.textDim, fontFamily: 'inherit', marginBottom: 8, wordBreak: 'break-all' } }, phoneUrl),
        photos.length > 0 && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))', gap: 4 } },
          photos.map(function(p) {
            return React.createElement('div', { key: p.id, style: { position: 'relative', aspectRatio: '1', background: C.bg, borderRadius: 4, overflow: 'hidden', border: '1px solid ' + C.border } },
              React.createElement('img', { src: p.url, style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } }),
              React.createElement('button', { onClick: function() { deletePhoto(p.id); },
                title: 'Remove',
                style: { position: 'absolute', top: 1, right: 1, background: 'rgba(220,38,38,0.85)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 2, padding: 0, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1 } }, '×')
            );
          })
        ),
        photos.length === 0 && React.createElement('div', { style: { fontSize: 11, color: C.textDim, fontStyle: 'italic' } }, 'Waiting for photos…')
      )
    )
  );
}
window.PhotoUploadPanel = PhotoUploadPanel;

// ─── UNEXPECTED ITEM MODAL ───
// Used by both FBA and WFS "Receive Unexpected Item" flows.
// Props:
//   open, onClose, onSuccess(result), lookupUrl, submitUrl
function UnexpectedItemModal(props) {
  var open = props.open, onClose = props.onClose, onSuccess = props.onSuccess;
  var lookupUrl = props.lookupUrl, submitUrl = props.submitUrl;
  // Optional: navigate to a sibling plan (FBA only). When provided, the split-shipment banner
  // renders the plan name as a link. Other callers (WFS/receiving) omit it → plain text.
  var onOpenPlan = props.onOpenPlan;

  var _st = React.useState('scan'); var step = _st[0], setStep = _st[1]; // 'scan' | 'receive' | 'manual'
  var _b = React.useState(''); var barcode = _b[0], setBarcode = _b[1];
  var _l = React.useState(null); var lookup = _l[0], setLookup = _l[1];
  var _sr = React.useState(false); var searching = _sr[0], setSearching = _sr[1];
  var _q = React.useState(''); var qty = _q[0], setQty = _q[1];
  var _bn = React.useState(''); var bin = _bn[0], setBin = _bn[1];
  var _n = React.useState(''); var notes = _n[0], setNotes = _n[1];
  var _t = React.useState(''); var manualTitle = _t[0], setManualTitle = _t[1];
  var _vs = React.useState(''); var manualVsku = _vs[0], setManualVsku = _vs[1];
  var _p = React.useState(''); var selectedPo = _p[0], setSelectedPo = _p[1];
  var _s = React.useState(false); var submitting = _s[0], setSubmitting = _s[1];
  var _e = React.useState(null); var error = _e[0], setError = _e[1];
  var _pp = React.useState(false); var needsPoPick = _pp[0], setNeedsPoPick = _pp[1];
  var _fu = React.useState(false); var forceUnexpected = _fu[0], setForceUnexpected = _fu[1];
  var _li = React.useState(-1); var lightboxIdx = _li[0], setLightboxIdx = _li[1];
  var _pt = React.useState(null); var photoToken = _pt[0], setPhotoToken = _pt[1];
  var _prs = React.useState(0); var photoResetSignal = _prs[0], setPhotoResetSignal = _prs[1];
  var _pc = React.useState(0); var photoCount = _pc[0], setPhotoCount = _pc[1];
  var lookupTimer = React.useRef(null);
  var lookupSeq = React.useRef(0);
  var binEdited = React.useRef(false);
  var titleEdited = React.useRef(false);
  var vskuEdited = React.useRef(false);
  var scanInputRef = React.useRef(null);

  React.useEffect(function() {
    if (!open) {
      setStep('scan'); setBarcode(''); setLookup(null); setSearching(false);
      setQty(''); setBin(''); setNotes('');
      setManualTitle(''); setManualVsku(''); setSelectedPo(''); setError(null);
      setNeedsPoPick(false); setForceUnexpected(false); setLightboxIdx(-1);
      setPhotoToken(null); setPhotoCount(0); setPhotoResetSignal(function(s) { return s + 1; });
      binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
    } else {
      // Auto-focus the scan input when modal opens
      setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
    }
  }, [open]);

  // Pick FBM bin first (MIA), then RETURNS, then any. Mirrors the receiving modal's logic.
  var pickBinForProduct = function(prod) {
    if (!prod) return '';
    if (Array.isArray(prod.bin_quantities) && prod.bin_quantities.length > 0) {
      var fbmBin = prod.bin_quantities.find(function(b) { return b.warehouse === 'FBM' && b.bin && b.bin !== 'DEFAULT'; });
      if (fbmBin) return fbmBin.bin;
      var anyBin = prod.bin_quantities.find(function(b) { return b.bin && b.bin !== 'DEFAULT'; });
      if (anyBin) return anyBin.bin;
    }
    if (prod.current_bins) {
      var first = String(prod.current_bins).split(',')[0].trim();
      if (first && first !== 'DEFAULT') return first;
    }
    return '';
  };

  // Apply a catalog-result product as the active match: pre-fill bin/title/vsku and advance to receive step.
  var pickCatalogMatch = function(prod) {
    var fakeLookup = Object.assign({}, lookup || {}, { product: prod });
    setLookup(fakeLookup);
    if (!binEdited.current) {
      var b = pickBinForProduct(prod);
      if (b) setBin(b);
    }
    if (!titleEdited.current) setManualTitle(prod.vendor_title || prod.title || prod.sku || '');
    if (!vskuEdited.current && prod.vendor_sku) setManualVsku(prod.vendor_sku);
    if (prod.vendor_id && lookup && Array.isArray(lookup.plan_pos)) {
      var matched = lookup.plan_pos.find(function(p) { return p.vendor_id === prod.vendor_id; });
      if (matched) setSelectedPo(String(matched.id));
    }
    setStep('receive');
  };

  React.useEffect(function() {
    if (!open || step !== 'scan') return;
    if (!barcode || !barcode.trim()) { setLookup(null); setSearching(false); return; }
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    setSearching(true);
    var seq = ++lookupSeq.current; // review M6 #79: only the newest lookup may fill the form or auto-advance
    lookupTimer.current = setTimeout(function() {
      api(lookupUrl + '?q=' + encodeURIComponent(barcode.trim())).then(function(r) {
        if (seq !== lookupSeq.current) return;
        setLookup(r);
        // Existing exact-match auto-fill behavior
        if (r && r.product) {
          if (r.fbm_bin && r.fbm_bin.bin_code && !binEdited.current) setBin(r.fbm_bin.bin_code);
          else if (!binEdited.current) {
            var b = pickBinForProduct(r.product);
            if (b) setBin(b);
          }
          if (r.matched_po) setSelectedPo(String(r.matched_po.id));
          if (!titleEdited.current) setManualTitle(r.product.vendor_title || r.product.title || r.product.sku || '');
          if (!vskuEdited.current && r.product.vendor_sku) setManualVsku(r.product.vendor_sku);
        } else if (r && Array.isArray(r.catalog_results) && r.catalog_results.length === 1) {
          // Single fuzzy match — auto-pick it
          pickCatalogMatch(r.catalog_results[0]);
        }
      }).catch(function() { if (seq === lookupSeq.current) setLookup(null); })
      .finally(function() { if (seq === lookupSeq.current) setSearching(false); });
    }, 250);
    // Cleanup: a timer still pending when the barcode changes, the step advances or the modal
    // closes must never fire (it used to auto-pick a stale single match and jump to Receive).
    return function() { clearTimeout(lookupTimer.current); lookupSeq.current++; setSearching(false); };
  }, [barcode, open, step]);

  if (!open) return null;

  var product = lookup && lookup.product;
  var fbmBin = lookup && lookup.fbm_bin;
  var matchedPo = lookup && lookup.matched_po;
  // POs on this shipment, sorted alphabetically by vendor name (then PO#) for the picker dropdowns.
  var planPos = ((lookup && lookup.plan_pos) || []).slice().sort(function(a, b) {
    var an = (a.vendor_name || '').toLowerCase(), bn = (b.vendor_name || '').toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return String(a.po_number || '').localeCompare(String(b.po_number || ''), undefined, { numeric: true });
  });
  var showPoPicker = needsPoPick || (!!product && !matchedPo && planPos.length > 1);
  var showManualFields = !product && barcode.trim().length > 0;
  // Sibling-split guard (FBA only — other lookups never return this field). If the scanned item
  // is still NEEDED on a sibling plan from the same split group, it isn't unexpected: it belongs
  // to that plan. Block the receive and point the packer there.
  var siblingMemberships = (lookup && lookup.sibling_memberships) || [];
  var siblingBlocking = siblingMemberships.filter(function(s) { return s && s.remaining > 0; });
  var siblingBlocked = siblingBlocking.length > 0;

  var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };

  var submit = async function() {
    if (siblingBlocked) {
      var sb = siblingBlocking[0];
      setError('This item belongs to split plan "' + (sb.plan_name || ('Plan ' + sb.plan_id)) + '" (' + sb.remaining + ' still to pack) — pack it there, it is not unexpected.');
      return;
    }
    var qtyN = parseInt(qty) || 0;
    var bc = barcode.trim();
    // Empty barcode is allowed — server uses a placeholder SKU. Require either a barcode or a title.
    if (!bc && !manualTitle.trim()) { setError('Enter a barcode or a vendor title'); return; }
    if (qtyN <= 0) { setError('Quantity must be greater than 0'); return; }
    if (!bin.trim()) { setError('Bin location is required'); return; }
    // If no barcode + no catalog match, confirm the item really has no printed barcode
    if (!bc && !product) {
      if (!(await appConfirm('No barcode entered. Confirm this item has no printed barcode (UPC/EAN) on the package?'))) return;
    }
    // Photos are mandatory for manually-entered unexpected items (not catalog-matched)
    if (!product && photoCount === 0) {
      setError('At least one photo is required for unexpected items not in our catalog. Use "Add photos" to scan the QR.');
      return;
    }

    setSubmitting(true); setError(null);
    var body = {
      barcode: barcode.trim(), qty: qtyN, bin_location: bin.trim(),
      // Send the resolved catalog product so the server matches the exact item the user
      // confirmed on screen (incl. fuzzy/keyword picks) instead of re-deriving from the
      // barcode — prevents catalog-matched extras filing as orphan UNEXPECTED-<keyword> lines.
      product_id: product ? product.id : null,
      notes: notes.trim() || null,
      vendor_title: manualTitle.trim() || null,
      vendor_sku: manualVsku.trim() || null,
      po_id: selectedPo ? parseInt(selectedPo) : null,
      force_unexpected: forceUnexpected,
      photo_token: photoToken || null,
    };
    api(submitUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(resp) { onSuccess(resp); onClose(); })
      .catch(function(e) {
        var msg = e && e.message ? e.message : 'Failed to receive';
        if (msg.indexOf('Select which PO') !== -1) setNeedsPoPick(true);
        setError(msg);
      })
      .finally(function() { setSubmitting(false); });
  };

  var label = function(txt) { return React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, txt); };
  var inp = function(value, onChange, placeholder, extraStyle) {
    return React.createElement('input', { value: value || '', onChange: function(e) { onChange(e.target.value); }, placeholder: placeholder,
      style: Object.assign({ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }, extraStyle || {}) });
  };

  var productImages = (product && product.images && product.images.length) ? product.images : (product && product.image_1_url ? [product.image_1_url] : []);
  var productCard = product && React.createElement('div', { style: { display: 'flex', gap: 10, padding: 10, background: C.surface, borderRadius: 6, marginBottom: 12, border: '1px solid ' + C.border } },
    productImages.length > 0
      ? React.createElement('img', {
          src: productImages[0],
          onClick: function() { setLightboxIdx(0); },
          title: productImages.length > 1 ? 'Click to view ' + productImages.length + ' images' : 'Click to enlarge',
          style: { width: 56, height: 56, borderRadius: 6, objectFit: 'cover', flexShrink: 0, cursor: 'pointer', transition: 'transform 0.1s' }
        })
      : React.createElement('div', { style: { width: 56, height: 56, borderRadius: 6, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, React.createElement(LIcon, { name: 'package', size: 24, color: C.textDim })),
    React.createElement('div', { style: { flex: 1, minWidth: 0 } },
      React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 } }, product.vendor_title || product.title || product.sku),
      React.createElement('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
        product.sku && React.createElement('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + product.sku),
        product.vendor_sku && React.createElement('span', { style: ps(C.accent + '14', C.accent) }, 'VSKU: ' + product.vendor_sku),
        product.barcode && React.createElement('span', { style: ps('#06B6D4' + '18', '#06B6D4') }, 'UPC: ' + product.barcode),
        product.fnsku && React.createElement('span', { style: ps('#8B5CF6' + '18', '#8B5CF6') }, 'FNSKU: ' + product.fnsku),
        product.warehouse_code && React.createElement('span', { style: ps(product.warehouse_code === 'FBM' ? '#10B981' + '18' : C.textDim + '18', product.warehouse_code === 'FBM' ? '#10B981' : C.textMuted) }, product.warehouse_code)
      ),
      fbmBin && React.createElement('div', { style: { fontSize: 11, color: C.success, fontWeight: 600, marginTop: 2 } },
        React.createElement(LIcon, { name: 'map-pin', size: 11, color: C.success }), ' Bin: ', fbmBin.bin_code, ' (' + fbmBin.quantity + ' on hand)'
      ),
      matchedPo && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } },
        'PO: ', React.createElement('span', { style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit' } }, matchedPo.po_number), ' — ', matchedPo.vendor_name
      )
    )
  );

  var qtyBinNotes = [
    // Qty + Bin
    React.createElement('div', { key: 'qbin', style: { display: 'flex', gap: 8, marginBottom: 12 } },
      React.createElement('div', { style: { flex: 1 } },
        label('Qty *'),
        inp(qty, setQty, '0')
      ),
      React.createElement('div', { style: { flex: 2 } },
        label('Bin Location *'),
        React.createElement('input', {
          value: bin || '',
          onChange: function(e) { binEdited.current = true; setBin((e.target.value || '').toUpperCase()); },
          onBlur: function() { setBin(function(v) { return formatBin(v) || v; }); },
          placeholder: 'A-100',
          style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
        })
      )
    ),
    // Notes
    React.createElement('div', { key: 'notes', style: { marginBottom: 12 } },
      label('Notes (optional)'),
      inp(notes, setNotes, 'Appears in the PO receiving history')
    ),
    // Photo upload via QR
    React.createElement('div', { key: 'photo', style: { marginBottom: 14 } },
      React.createElement(window.PhotoUploadPanel, { required: !product, onTokenChange: setPhotoToken, onPhotosChange: function(p) { setPhotoCount(p.length); }, resetSignal: photoResetSignal })
    ),
  ];

  var poPicker = showPoPicker && React.createElement('div', { style: { marginBottom: 12 } },
    label('Purchase Order *'),
    React.createElement('select', { value: selectedPo || '', onChange: function(e) { setSelectedPo(e.target.value); },
      style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } },
      React.createElement('option', { value: '' }, 'Select a PO…'),
      planPos.map(function(p) {
        return React.createElement('option', { key: p.id, value: String(p.id) }, p.po_number + ' — ' + (p.vendor_name || 'Unknown vendor'));
      })
    )
  );

  var errorBox = error && React.createElement('div', { style: { padding: 10, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#F87171' } }, error);

  var siblingBanner = siblingBlocked && React.createElement('div', { style: { padding: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 6, marginBottom: 12, fontSize: 12.5 } },
    React.createElement('div', { style: { fontWeight: 700, color: '#F59E0B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 14, color: '#F59E0B' }),
      'Not an unexpected item — it belongs to a split shipment'),
    siblingBlocking.map(function(s, i) {
      var planLabel = s.plan_name || ('Plan ' + s.plan_id);
      var planEl = onOpenPlan
        ? React.createElement('a', {
            href: '#',
            onClick: function(e) { e.preventDefault(); onOpenPlan(s.plan_id); },
            title: 'Open ' + planLabel,
            style: { fontWeight: 700, color: C.accent, textDecoration: 'underline', cursor: 'pointer' }
          }, planLabel)
        : React.createElement('span', { style: { fontWeight: 700 } }, planLabel);
      return React.createElement('div', { key: i, style: { color: C.text, marginTop: 2 } },
        'Pack it in ', planEl,
        ' — shipment #', String(s.shipment_id), ': ',
        React.createElement('span', { style: { fontWeight: 700, color: '#F59E0B' } }, s.remaining + ' of ' + s.quantity + ' still to pack'), '.');
    }),
    React.createElement('div', { style: { color: C.textMuted, marginTop: 6 } },
      'Open that plan and scan this item into a box. Only use "Not a match? Receive manually" if you genuinely have an extra unit beyond that plan.')
  );

  // ─── STEP: Scan ───
  var scanStep = step === 'scan' && React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: 6 } },
      label('Scan Barcode or FNSKU'),
      React.createElement('div', { style: { position: 'relative' } },
        React.createElement('input', {
          ref: scanInputRef,
          value: barcode,
          onChange: function(e) { setBarcode(e.target.value); },
          placeholder: 'Scan Barcode/FNSKU or type to search catalog..',
          style: { width: '100%', padding: '10px 36px 10px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
        }),
        barcode && React.createElement('button', {
          onClick: function() {
            setBarcode(''); setLookup(null); setSearching(false);
            setManualTitle(''); setManualVsku(''); setBin(''); setSelectedPo('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            if (scanInputRef.current) scanInputRef.current.focus();
          },
          title: 'Clear',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.border; e.currentTarget.style.color = C.text; },
          onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; },
          style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, width: 24, height: 24, borderRadius: 4, color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s, color 0.15s' }
        }, React.createElement(LIcon, { name: 'x', size: 14 }))
      )
    ),
    React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginBottom: 14 } }, 'We\'ll search the catalog, preferring FBM items so the current bin shows up.'),

    // Searching…
    searching && React.createElement('div', { style: { padding: '20px 0', textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'Searching catalog…'),

    // Reusable "Not a match? Receive manually." button — danger pill styling, clears all fields
    (function() { return null; })(),

    // Match found (single product — exact or auto-picked single fuzzy)
    !searching && product && React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          title: 'Not a match — fill the unexpected item form manually',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'Not a match? Receive manually.')
      ),
      siblingBanner,
      productCard,
      siblingBlocked
        ? null
        : React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } },
            React.createElement(Btn, { variant: 'primary', onClick: function() { setStep('receive'); }, style: { minWidth: 220, justifyContent: 'center' } },
              React.createElement(LIcon, { name: 'arrow-right', size: 14 }), ' Continue with this item'
            )
          )
    ),

    // Multi-match pick list (no exact, but multiple fuzzy results)
    !searching && !product && lookup && Array.isArray(lookup.catalog_results) && lookup.catalog_results.length > 1 && React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          title: 'None of these match — fill the unexpected item form manually',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'No matches? Receive manually.')
      ),
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } },
        lookup.catalog_capped
          ? 'Showing the first ' + lookup.catalog_results.length + ' of ' + lookup.catalog_total + ' catalog matches for "' + barcode.trim() + '". Narrow your search to see the rest.'
          : lookup.catalog_results.length + ' catalog matches for "' + barcode.trim() + '" — pick one to receive'),
      React.createElement('div', { style: { maxHeight: 360, overflow: 'auto', borderRadius: 8, border: '1px solid ' + C.border } },
        lookup.catalog_results.map(function(item, idx) {
          var imgUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
          var isFbm = (item.wh_code && /^FBM/i.test(item.wh_code)) || (item.sku && /-FBM(?:-|$)/i.test(item.sku));
          return React.createElement('div', { key: item.id,
            onClick: function() { pickCatalogMatch(item); },
            style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
              borderBottom: idx < lookup.catalog_results.length - 1 ? '1px solid ' + C.border + '44' : 'none',
              background: 'transparent', transition: 'background 0.1s' },
            onMouseEnter: function(e) { e.currentTarget.style.background = C.surface; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
          },
            React.createElement(ProductThumb, { src: imgUrl, size: 40, iconSize: 16 }),
            React.createElement('div', { style: { flex: 1, minWidth: 0 } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, item.sku),
                isFbm && React.createElement('span', { style: { background: C.success + '22', color: C.success, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, 'FBM'),
                item.wh_code && !isFbm && React.createElement('span', { style: { background: C.textDim + '22', color: C.textMuted, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, item.wh_code)
              ),
              React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 } }, item.vendor_title || item.title || ''),
              item.barcode && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontFamily: 'inherit', marginTop: 2 } }, 'UPC: ', React.createElement('span', { style: { color: C.textDim } }, item.barcode))
            ),
            React.createElement(LIcon, { name: 'chevron-right', size: 16, color: C.textDim })
          );
        })
      )
    ),

    // No match at all
    !searching && !product && barcode.trim().length > 0 && (!lookup || !Array.isArray(lookup.catalog_results) || lookup.catalog_results.length === 0) && React.createElement('div', null,
      React.createElement('div', { style: { padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#B45309' } },
        React.createElement(LIcon, { name: 'alert-triangle', size: 13, color: '#B45309' }), ' No catalog match for ',
        React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600 } }, barcode.trim()),
        '. Enter the item details manually.'
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'No matches? Receive manually.')
      )
    )
  );

  var backToScanBtn = React.createElement(Btn, { onClick: function() { setStep('scan'); setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 50); } },
    React.createElement(LIcon, { name: 'arrow-left', size: 14 }), ' Back'
  );

  // ─── STEP: Receive (catalog match) ───
  var receiveStep = step === 'receive' && React.createElement('div', null,
    productCard,
    siblingBanner,
    poPicker,
    qtyBinNotes,
    errorBox,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
      backToScanBtn,
      React.createElement(Btn, { variant: 'primary', onClick: submit, disabled: submitting || !(parseInt(qty) > 0) || !bin.trim() || siblingBlocked },
        submitting ? 'Receiving…' : React.createElement('span', null, React.createElement(LIcon, { name: 'check', size: 14 }), ' Receive')
      )
    )
  );

  // ─── STEP: Manual (no catalog match) ───
  var manualStep = step === 'manual' && React.createElement('div', null,
    React.createElement('div', { style: { padding: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#B45309' } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 12, color: '#B45309' }), ' No catalog match — details below will be stored on the PO line.'
    ),
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 } },
      React.createElement('div', null,
        label('Barcode'),
        inp(barcode, setBarcode, 'Scan or enter barcode. If the item has no barcode leave BLANK.')
      ),
      React.createElement('div', null,
        label('Vendor SKU'),
        inp(manualVsku, function(v) { vskuEdited.current = true; setManualVsku(v); }, 'Vendor item #…')
      )
    ),
    React.createElement('div', { style: { marginBottom: 12 } },
      label('Item Name'),
      inp(manualTitle, function(v) { titleEdited.current = true; setManualTitle(v); }, 'Item name / description')
    ),
    poPicker || (planPos.length > 0 && React.createElement('div', { style: { marginBottom: 12 } },
      label('Purchase Order *'),
      React.createElement('select', { value: selectedPo || '', onChange: function(e) { setSelectedPo(e.target.value); },
        style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } },
        React.createElement('option', { value: '' }, 'Select a PO…'),
        planPos.map(function(p) {
          return React.createElement('option', { key: p.id, value: String(p.id) }, p.po_number + ' — ' + (p.vendor_name || 'Unknown vendor'));
        })
      )
    )),
    qtyBinNotes,
    errorBox,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
      backToScanBtn,
      React.createElement(Btn, { variant: 'primary', onClick: submit, disabled: submitting || !barcode.trim() || !(parseInt(qty) > 0) || !bin.trim() || !selectedPo },
        submitting ? 'Receiving…' : React.createElement('span', null, React.createElement(LIcon, { name: 'check', size: 14 }), ' Receive')
      )
    )
  );

  var lightboxOverlay = lightboxIdx >= 0 && productImages.length > 0 && React.createElement('div', {
    onClick: function() { setLightboxIdx(-1); },
    style: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }
  },
    React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx(-1); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '×'),
    productImages.length > 1 && React.createElement('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10002 } },
      (lightboxIdx + 1) + ' / ' + productImages.length),
    productImages.length > 1 && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + productImages.length) % productImages.length); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '‹'),
    productImages.length > 1 && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % productImages.length); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '›'),
    React.createElement('img', {
      src: productImages[lightboxIdx],
      onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' }
    }),
    productImages.length > 1 && React.createElement('div', {
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 }
    },
      productImages.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { setLightboxIdx(i); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightboxIdx ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightboxIdx ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );

  var title = step === 'scan' ? 'Scan barcode or FNSKU' : (step === 'manual' ? 'Enter item manually' : 'Receive unexpected item');
  return React.createElement(React.Fragment, null,
    React.createElement(Modal, { open: open, onClose: onClose, title: title, width: 640 },
      React.createElement('div', null, scanStep, receiveStep, manualStep)
    ),
    lightboxOverlay
  );
}

window.UnexpectedItemModal = UnexpectedItemModal;

// ─── @mention autocomplete for note inputs ───
// Cache the mentionable roster once per session; lookups happen as the user types.
var _mentionableUsers = null;
var _mentionableLoading = null;
function loadMentionableUsers() {
  if (_mentionableUsers) return Promise.resolve(_mentionableUsers);
  if (_mentionableLoading) return _mentionableLoading;
  _mentionableLoading = api('/auth/users/mentionable')
    .then(function(rows) { _mentionableUsers = rows || []; return _mentionableUsers; })
    .catch(function(e) { _mentionableLoading = null; console.error('[mentions] load failed', e); return []; });
  return _mentionableLoading;
}
window.invalidateMentionableUsers = function() { _mentionableUsers = null; _mentionableLoading = null; };

// MentionInput — drop-in textarea with @-autocomplete.
// Props:
//   value, onChange(value), placeholder, rows, style, disabled,
//   onKeyDown (forwarded after the popup has had its chance to consume the key)
function MentionInput(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var inputRef = useRef(null);
  var [users, setUsers] = useState([]);
  var [popup, setPopup] = useState(null); // { query, start, end, anchor: {top, left}, items, index }
  useEffect(function() { loadMentionableUsers().then(setUsers); }, []);

  // Detect the @token the cursor is currently in (if any) and rebuild the popup.
  function rebuildPopup(text, caret) {
    if (caret == null) { setPopup(null); return; }
    // Walk left from caret to find an @, breaking on whitespace.
    var i = caret;
    while (i > 0) {
      var ch = text.charAt(i - 1);
      if (ch === '@') {
        // Must be at string start or preceded by whitespace.
        if (i - 2 < 0 || /\s/.test(text.charAt(i - 2))) {
          var query = text.slice(i, caret);
          // Don't fire on whitespace-only or trailing space.
          if (/\s/.test(query)) { setPopup(null); return; }
          var q = query.toLowerCase();
          var filtered = users.filter(function(u) {
            var dn = (u.display_name || '').toLowerCase();
            var un = (u.username || '').toLowerCase();
            return dn.indexOf(q) !== -1 || un.indexOf(q) !== -1;
          }).slice(0, 8);
          if (!filtered.length) { setPopup(null); return; }
          setPopup({ query: query, start: i - 1, end: caret, items: filtered, index: 0 });
          return;
        }
        setPopup(null);
        return;
      }
      if (/\s/.test(ch)) { setPopup(null); return; }
      i--;
    }
    setPopup(null);
  }

  function handleChange(e) {
    var v = e.target.value;
    if (onChange) onChange(v);
    setTimeout(function() {
      var el = inputRef.current; if (!el) return;
      rebuildPopup(v, el.selectionStart);
    }, 0);
  }

  function pickUser(u) {
    if (!popup || !inputRef.current) return;
    var before = value.slice(0, popup.start);
    var after = value.slice(popup.end);
    // Insert the display name (spaces collapsed to dots) so the chip reads as a
    // human name rather than an email-style username.
    var token = (u.display_name || u.username || '').replace(/\s+/g, '.');
    var inserted = '@' + token;
    var newVal = before + inserted + ' ' + after;
    if (onChange) onChange(newVal);
    setPopup(null);
    setTimeout(function() {
      var el = inputRef.current; if (!el) return;
      var pos = before.length + inserted.length + 1;
      el.focus();
      try { el.setSelectionRange(pos, pos); } catch (e) {}
    }, 0);
  }

  function handleKeyDown(e) {
    if (popup) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setPopup(Object.assign({}, popup, { index: (popup.index + 1) % popup.items.length })); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setPopup(Object.assign({}, popup, { index: (popup.index - 1 + popup.items.length) % popup.items.length })); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickUser(popup.items[popup.index]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setPopup(null); return; }
    }
    if (props.onKeyDown) props.onKeyDown(e);
  }

  return React.createElement('div', { style: { position: 'relative' } },
    React.createElement('textarea', Object.assign({}, {
      ref: inputRef,
      value: value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onSelect: function() { var el = inputRef.current; if (el) rebuildPopup(value, el.selectionStart); },
      onPaste: props.onPaste,
      placeholder: props.placeholder,
      rows: props.rows || 2,
      disabled: props.disabled,
      style: Object.assign({ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }, props.style || {}),
    })),
    popup && React.createElement('div', {
      style: { position: 'absolute', left: 0, bottom: '100%', marginBottom: 4, zIndex: 50, background: C.surface, border: '1px solid ' + C.border, borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.4)', minWidth: 200, maxWidth: 320, padding: 4 }
    },
      popup.items.map(function(u, idx) {
        var active = idx === popup.index;
        return React.createElement('div', {
          key: u.id,
          onMouseDown: function(e) { e.preventDefault(); pickUser(u); },
          onMouseEnter: function() { setPopup(Object.assign({}, popup, { index: idx })); },
          style: { padding: '6px 10px', borderRadius: 4, cursor: 'pointer', background: active ? C.accent + '22' : 'transparent', fontSize: 13, color: C.text, fontWeight: 600 }
        }, u.display_name || u.username);
      })
    )
  );
}
window.MentionInput = MentionInput;

// Renders note body text with @mentions visually highlighted as inline chips
// and URLs as clickable new-tab links. Render-time only — notes stay plain text.
function renderNoteBody(text) {
  var parts = [];
  var re = /(@[a-zA-Z0-9_.-]+)|(https?:\/\/[^\s<>"']+)|(\bwww\.[^\s<>"']+)/g;
  var last = 0; var match;
  var i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(React.createElement('span', { key: 'm' + (i++), style: { background: 'rgba(251,191,36,0.22)', color: '#FBBF24', padding: '1px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(251,191,36,0.45)' } }, match[0]));
    } else {
      // Trailing sentence punctuation belongs to the prose, not the URL;
      // a trailing ")" stays only when the URL itself contains a "(".
      var url = match[0];
      var trail = '';
      while (/[.,;:!?"']$/.test(url) || (url.slice(-1) === ')' && url.indexOf('(') === -1)) {
        trail = url.slice(-1) + trail;
        url = url.slice(0, -1);
      }
      // color:inherit — own-note bubbles are white-on-accent, a default blue link would vanish
      parts.push(React.createElement('a', {
        key: 'u' + (i++),
        href: match[2] ? url : 'https://' + url,
        target: '_blank', rel: 'noopener noreferrer',
        style: { color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2, fontWeight: 600, wordBreak: 'break-all' }
      }, url));
      if (trail) parts.push(trail);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
window.renderNoteBody = renderNoteBody;

// Renders a note's attachments inside its bubble: image thumbnails (click opens
// full size in a new tab) and download chips for documents. Notes fetched from
// GET /purchase-orders/:poId/notes carry an attachments array + po_id.
function renderNoteAttachments(note) {
  var atts = note.attachments || [];
  if (!atts.length) return null;
  function fmtSize(b) {
    if (!b) return '';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }
  return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: note.message ? 6 : 0 } },
    atts.map(function(a) {
      var url = API_BASE + '/purchase-orders/' + note.po_id + '/notes/attachment/' + a.id;
      if (/^image\//i.test(a.mime_type || '')) {
        return React.createElement('a', { key: a.id, href: url, target: '_blank', rel: 'noopener noreferrer', style: { display: 'block' } },
          React.createElement('img', { src: url, alt: a.name, title: a.name,
            style: { maxWidth: 180, maxHeight: 140, borderRadius: 8, display: 'block', border: '1px solid rgba(0,0,0,0.15)' } }));
      }
      return React.createElement('a', { key: a.id, href: url, target: '_blank', rel: 'noopener noreferrer', title: a.name,
        style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8,
          background: 'rgba(0,0,0,0.12)', color: 'inherit', textDecoration: 'none', fontSize: 12, fontWeight: 600, maxWidth: 240 } },
        React.createElement(LIcon, { name: 'paperclip', size: 12 }),
        React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, a.name),
        a.file_size ? React.createElement('span', { style: { opacity: 0.7, fontWeight: 400, flexShrink: 0 } }, fmtSize(a.file_size)) : null);
    })
  );
}
window.renderNoteAttachments = renderNoteAttachments;

// Deep link to a bill in the Bill.com web app. app.bill.com redirects to the
// org's regional host (app02.us.…) and lands on Bill Details — verified live 2026-08-13.
function bdcBillUrl(bdcId) { return 'https://app.bill.com/neo/bills/' + encodeURIComponent(bdcId); }
window.bdcBillUrl = bdcBillUrl;

// Hover wrapper for Bill.com-linked elements (native title= can't hold links).
// Wraps `children` and shows the shared card on hover: muted tip line, then either
// one row per bill in `bills` ([{bdc_id, invoice_number, amount, payment_status}] —
// each its own Bill.com link) or the single detail/bdcBillId pair. Used by the
// PO Billed badge and the accounting cash-cycle BILL.COM pill — one look everywhere.
function BdcBillHover({ tip, detail, bdcBillId, bills, children }) {
  var _o = useState(false), open = _o[0], setOpen = _o[1];
  var _p = useState(null), pos = _p[0], setPos = _p[1];
  var closeTimer = useRef(null);
  var show = function(e) {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    var r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
    setOpen(true);
  };
  // Grace delay so the pointer can travel from the pill into the card.
  var hide = function() { closeTimer.current = setTimeout(function() { setOpen(false); }, 250); };
  var cancelHide = function() { if (closeTimer.current) clearTimeout(closeTimer.current); };
  // Portaled to <body>: cash-cycle "complete" rows fade themselves with opacity 0.6,
  // which would fade any in-row descendant — including this fixed-position card.
  return React.createElement('span', { onMouseEnter: show, onMouseLeave: hide, style: { display: 'inline-flex' } },
    children,
    open && pos && ReactDOM.createPortal(React.createElement('div', {
      onMouseEnter: cancelHide, onMouseLeave: hide,
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'fixed', left: pos.x, top: pos.y, transform: 'translateX(-50%)', zIndex: 10500,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '9px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', minWidth: 190, maxWidth: 300, cursor: 'default',
        fontSize: 12, fontWeight: 400, color: C.text, textTransform: 'none', letterSpacing: 'normal', textAlign: 'left', lineHeight: 1.45, textDecoration: 'none' }
    },
      React.createElement('div', { style: { color: C.textMuted, fontSize: 11 } }, tip),
      (bills && bills.length > 0)
        ? bills.map(function(bill) {
            var label = 'Invoice ' + (bill.invoice_number || '—')
              + (bill.amount != null ? ' · $' + Number(bill.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
              + (bill.payment_status === 'paid' ? ' · paid' : '');
            return React.createElement('a', {
              key: bill.bdc_id,
              href: bdcBillUrl(bill.bdc_id), target: '_blank', rel: 'noopener noreferrer',
              onClick: function(e) { e.stopPropagation(); },
              onMouseDown: function(e) { e.stopPropagation(); },
              style: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, color: C.accent, fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }
            }, label, React.createElement(LIcon, { name: 'external-link', size: 11 }));
          })
        : [
            detail && React.createElement('div', { key: 'd', style: { marginTop: 5, fontWeight: 600 } }, detail),
            bdcBillId && React.createElement('a', {
              key: 'l',
              href: bdcBillUrl(bdcBillId), target: '_blank', rel: 'noopener noreferrer',
              onClick: function(e) { e.stopPropagation(); },
              onMouseDown: function(e) { e.stopPropagation(); },
              style: { display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6, color: C.accent, fontWeight: 600, fontSize: 12, textDecoration: 'none' }
            }, 'Open in Bill.com', React.createElement(LIcon, { name: 'external-link', size: 12 }))
          ]
    ), document.body)
  );
}
window.BdcBillHover = BdcBillHover;

// Billed/Unbilled pill for POs. big=true renders the PO-detail-header size.
// bills = full linked_bills array (several bills can invoice one PO); falls back
// to the single bdcBillId/invoiceNumber/amount fields when the array is absent.
function BilledBadge({ billed, isTransfer, source, bdcBillId, invoiceNumber, amount, bills, big }) {
  var b = billed || isTransfer;
  var n = bills ? bills.length : 0;
  var tip = isTransfer ? 'Internal transfer — no Bill.com bill needed'
    : !b ? 'No Bill.com bill linked yet'
    : n > 1 ? n + ' Bill.com bills are linked to this PO' + (source ? ' (' + source + ')' : '')
    : 'A Bill.com bill is linked to this PO' + (source ? ' (' + source + ')' : '');
  var detail = bdcBillId
    ? 'Invoice ' + (invoiceNumber || '—') + (amount != null ? ' · $' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
    : null;
  var pillStyle = big
    ? { padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }
    : { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600 };
  return React.createElement(BdcBillHover, { tip: tip, detail: detail, bdcBillId: bdcBillId, bills: bills },
    React.createElement('span', { style: Object.assign({ background: (b ? C.success : C.textMuted) + '22', color: b ? C.success : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }, pillStyle) }, b ? 'Billed' : 'Unbilled')
  );
}
window.BilledBadge = BilledBadge;

// Global paste sanitizer — strips leading/trailing whitespace from pastes into any search input.
// Catches stray spaces from copy/paste (e.g. UPC "844828086538 " from another system).
(function() {
  function isSearchInput(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    var t = (el.type || '').toLowerCase();
    if (t && t !== 'text' && t !== 'search') return false;
    var ph = (el.placeholder || '').toLowerCase();
    return ph.indexOf('search') !== -1;
  }
  document.addEventListener('paste', function(e) {
    var el = e.target;
    if (!isSearchInput(el)) return;
    var cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    var pasted = cd.getData('text');
    if (pasted == null) return;
    var trimmed = pasted.trim();
    if (trimmed === pasted) return; // nothing to fix
    e.preventDefault();
    var start = el.selectionStart || 0;
    var end = el.selectionEnd || 0;
    var newVal = el.value.substring(0, start) + trimmed + el.value.substring(end);
    // Use the native value setter so React's controlled-input change detection fires
    var nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeSetter.call(el, newVal);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    var caret = start + trimmed.length;
    try { el.setSelectionRange(caret, caret); } catch(_) {}
  }, true);
})();

// Session heartbeat — ping server when user is active to prevent timeout
(function() {
  var HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
  var _lastUserActivity = Date.now();
  var _heartbeatTimer = null;

  function onUserActivity() { _lastUserActivity = Date.now(); }

  ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'].forEach(function(evt) {
    document.addEventListener(evt, onUserActivity, { passive: true });
  });

  function sendHeartbeat() {
    // Only ping if user was active in the last interval
    if (Date.now() - _lastUserActivity < HEARTBEAT_INTERVAL) {
      fetch('/api/auth/heartbeat', { credentials: 'include' }).catch(function(e) { console.error('[silent-catch]', e); });
    }
  }

  _heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
})();

// ─── Number inputs: no mousewheel spin ───
// Chrome increments/decrements a FOCUSED <input type=number> when the wheel fires over it —
// scrolling the page past a focused qty/cost field silently changes its value. While a number
// input is focused, swallow wheel events on that element (attached per-focused-input so normal
// page scrolling stays on the passive fast path). Typing and arrow keys are unaffected.
(function() {
  function preventSpin(e) { e.preventDefault(); }
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (el.tagName === 'INPUT' && el.type === 'number') el.addEventListener('wheel', preventSpin, { passive: false });
  });
  document.addEventListener('focusout', function(e) {
    var el = e.target;
    if (el.tagName === 'INPUT' && el.type === 'number') el.removeEventListener('wheel', preventSpin);
  });
})();

// ─── Global Undo (Ctrl+Z) for React controlled inputs ───
(function() {
  var undoStacks = new WeakMap();
  var nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
  var textareaSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;

  // Track previous values on focus and before each change
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    if (!undoStacks.has(el)) undoStacks.set(el, []);
  }, true);

  document.addEventListener('input', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    if (!undoStacks.has(el)) undoStacks.set(el, []);
    var stack = undoStacks.get(el);
    // Store the value before this input event (use a short delay to capture after React re-render)
    if (!el._undoPrev && el._undoPrev !== '') el._undoPrev = '';
    if (stack.length === 0 || stack[stack.length - 1] !== el._undoPrev) {
      stack.push(el._undoPrev);
      if (stack.length > 50) stack.shift();
    }
    el._undoPrev = el.value;
  }, true);

  // Capture the value before input changes
  document.addEventListener('keydown', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    el._undoPrev = el.value;

    // Handle Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      var stack = undoStacks.get(el);
      if (!stack || stack.length === 0) return;
      e.preventDefault();
      var prev = stack.pop();
      var setter = el.tagName === 'TEXTAREA' ? textareaSetter : nativeSetter;
      setter.call(el, prev);
      el._undoPrev = prev;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, true);
})();

// Expose all shared symbols to window for cross-file access
window.C = C; window.themes = themes; window.statusColors = statusColors;
window.setThemeColors = setThemeColors; window.getThemeName = getThemeName; window.getStatusColors = getStatusColors;
window.api = api; window.apiUpload = apiUpload;

// thumbUrl(originUrl, width?) — proxy external product images through our local
// resize+cache route. Returns originUrl unchanged for empty/relative/local URLs
// or hosts we don't proxy (the route's allowlist is the source of truth, but the
// client matches it here so we don't waste a round-trip for non-allowlisted hosts).
// width should be one of [72, 144, 256, 512]; omit for the full-size original.
var THUMB_PROXY_HOSTS = { 'images.zentail.com': 1, 'm.media-amazon.com': 1, 'cloudinary.images-iherb.com': 1 };
function thumbUrl(originUrl, width) {
  if (!originUrl || typeof originUrl !== 'string') return originUrl;
  if (originUrl.indexOf('/api/img') === 0) return originUrl;
  var host;
  try { host = new URL(originUrl).hostname; } catch (e) { return originUrl; }
  if (!THUMB_PROXY_HOSTS[host]) return originUrl;
  var qs = 'u=' + encodeURIComponent(originUrl);
  if (width) qs += '&w=' + width;
  return '/api/img?' + qs;
}
window.thumbUrl = thumbUrl;

// ProductThumb — a product image that falls back to the package icon when the bytes never
// arrive (0-byte object at the origin, dead URL, 404 from /api/img — error_log 761,
// 2026-10-06). Draws the same square the inline img/placeholder pairs drew: `size` is the
// side, `radius` the corner, `iconSize` the placeholder icon; `onClick` makes it a pointer.
function ProductThumb(props) {
  var st = React.useState(false), failed = st[0], setFailed = st[1];
  var src = props.src || null;
  React.useEffect(function() { setFailed(false); }, [src]);
  var size = props.size || 40, radius = props.radius || 6;
  var box = Object.assign({ width: size, height: size, borderRadius: radius, flexShrink: 0 }, props.style || {});
  if (!src || failed) {
    return React.createElement('div', { style: Object.assign({}, box, { background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }) },
      React.createElement(LIcon, { name: 'package', size: props.iconSize || Math.round(size * 0.4), color: C.textDim }));
  }
  return React.createElement('img', { src: src, alt: props.alt || '', loading: 'lazy', decoding: 'async', title: props.title, onClick: props.onClick,
    onError: function() { setFailed(true); },
    style: Object.assign({}, box, { objectFit: 'cover', border: '1px solid ' + C.border, cursor: props.onClick ? 'pointer' : undefined }) });
}
window.ProductThumb = ProductThumb;
window.setAuthToken = setAuthToken; window.getAuthToken = getAuthToken;
window.hasPermission = hasPermission; window.canEdit = canEdit; window.canManageUsers = canManageUsers; window.canUpdatePOStatus = canUpdatePOStatus;
// ─── State / province dropdown shared by every address form (Anthony 2026-09-19) ───
// US (50 states, DC, the territories and the military codes), Canada and Australia get a
// select of "code — name" and store the code; any other country keeps a text field. A value
// already saved that is not in the list (e.g. "Florida" spelled out) stays visible as
// "(saved)" until someone picks the code. onChange receives an event-shaped { target: { value } }
// so the forms' existing handlers work unchanged.
var STATE_LISTS = {
  US: [['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
    ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
    ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
    ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
    ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
    ['PR', 'Puerto Rico'], ['VI', 'U.S. Virgin Islands'], ['GU', 'Guam'], ['AS', 'American Samoa'], ['MP', 'Northern Mariana Islands'],
    ['AA', 'Armed Forces Americas'], ['AE', 'Armed Forces Europe'], ['AP', 'Armed Forces Pacific']],
  CA: [['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'], ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'],
    ['NT', 'Northwest Territories'], ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon']],
  AU: [['ACT', 'Australian Capital Territory'], ['NSW', 'New South Wales'], ['NT', 'Northern Territory'], ['QLD', 'Queensland'], ['SA', 'South Australia'],
    ['TAS', 'Tasmania'], ['VIC', 'Victoria'], ['WA', 'Western Australia']],
};
var STATE_COUNTRY_ALIASES = { USA: 'US', 'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', CAN: 'CA', CANADA: 'CA', AUS: 'AU', AUSTRALIA: 'AU' };
function stateListFor(country) {
  var c = String(country == null || country === '' ? 'US' : country).trim().toUpperCase();
  return STATE_LISTS[STATE_COUNTRY_ALIASES[c] || c] || null;
}
function stateCodesFor(country) { var l = stateListFor(country); return l ? l.map(function(x) { return x[0]; }) : null; }
function StateSelect({ label, value, onChange, country, style: extraStyle, placeholder, compact, disabled, title }) {
  var list = stateListFor(country);
  if (!list) return React.createElement(Inp, { label: label, value: value || '', onChange: onChange, style: extraStyle, placeholder: placeholder || 'State / province', disabled: disabled, title: title });
  var cur = String(value || '').trim().toUpperCase();
  var known = list.some(function(x) { return x[0] === cur; });
  var baseStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: compact ? '4px 8px' : '8px 12px', borderRadius: compact ? 4 : 6, fontSize: compact ? 11 : 13,
    fontFamily: 'inherit', outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box', cursor: disabled ? 'default' : 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' };
  var select = React.createElement('select', {
    value: cur, disabled: disabled, title: title,
    onChange: function(e) { if (onChange) onChange({ target: { value: e.target.value } }); },
    onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
    onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
    style: Object.assign({}, baseStyle, extraStyle || {})
  },
    React.createElement('option', { value: '' }, placeholder || '— State —'),
    (cur && !known) ? React.createElement('option', { value: cur }, cur + ' (saved)') : null,
    list.map(function(x) { return React.createElement('option', { key: x[0], value: x[0] }, x[0] + ' — ' + x[1]); }));
  if (!label) return select;
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    select);
}
window.STATE_LISTS = STATE_LISTS; window.stateListFor = stateListFor; window.stateCodesFor = stateCodesFor; window.StateSelect = StateSelect;
window.Badge = Badge; window.Btn = Btn; window.Inp = Inp; window.Sel = Sel; window.SearchSelect = SearchSelect;
window.Card = Card; window.Modal = Modal; window.Empty = Empty; window.Toast = Toast;
window.AppDialogHost = AppDialogHost; window.appConfirm = appConfirm; window.appPrompt = appPrompt; window.appAlert = appAlert; window.appChoice = appChoice;
window.TH = TH; window.TD = TD; window.DataTable = DataTable; window.CopyCell = CopyCell; window.SearchInput = SearchInput;
window.loadColumnPrefs = loadColumnPrefs; window.saveColumnPrefs = saveColumnPrefs; window.resetColumnPrefsCache = resetColumnPrefsCache;
window.downloadCSV = downloadCSV; window.escapeCSV = escapeCSV; window.arrayToCSV = arrayToCSV; window.downloadTemplate = downloadTemplate;
window.channelIcon = channelIcon;
window.FillBar = FillBar; window.BinBadge = BinBadge;

// Gold "TOP" chip for products tagged by the nightly top-revenue job — pass any row that
// carries the product's top_revenue_* columns (catalog rows, PO line items).
function TopRevChip(row) {
  if (!row || !row.is_top_revenue) return null;
  var amt = row.top_revenue_amount != null ? '$' + Math.round(Number(row.top_revenue_amount)).toLocaleString() : '';
  var tip = 'Top revenue' + (row.top_revenue_rank ? ' #' + row.top_revenue_rank : '') +
            (amt ? ' · ' + amt + ' / ' + (row.top_revenue_window_days || 90) + 'd' : '');
  return React.createElement('span', { title: tip, style: { fontSize: 9, fontWeight: 700, padding: '1px 5px',
    borderRadius: 6, background: '#EAB30818', color: '#EAB308', marginLeft: 6, verticalAlign: 'middle',
    letterSpacing: '0.03em', whiteSpace: 'nowrap' } }, 'TOP');
}
window.TopRevChip = TopRevChip;

// Amazon Transparency program logo — shown in every Transparency pill/chip app-wide.
var TRANSPARENCY_LOGO_SRC = '/img/transparency-logo.png';
// Purple "T-CODE" chip for products enrolled in Amazon Transparency — every unit needs a
// serialized Transparency label (from the brand's code pool) in addition to the FNSKU.
function TransparencyChip(row) {
  if (!row || !row.requires_transparency) return null;
  return React.createElement('span', {
    title: 'Amazon Transparency — every unit needs a serialized Transparency label in addition to the FNSKU label',
    style: { fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: '#8B5CF618', color: '#8B5CF6',
      marginLeft: 6, verticalAlign: 'middle', letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 } },
    React.createElement('img', { src: TRANSPARENCY_LOGO_SRC, alt: '', style: { width: 10, height: 10, borderRadius: 2, display: 'block' } }),
    'T-CODE');
}
window.TransparencyChip = TransparencyChip;
// Standard row/list pill with the Transparency logo — use anywhere an item/PO needs the
// Transparency marker (FBA pack lists, create-shipment PO list, ...).
function TransparencyPill(props) {
  return React.createElement('span', {
    title: 'Amazon Transparency — every unit needs a serialized Transparency label in addition to the FNSKU label',
    style: Object.assign({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: '#A78BFA' + '1A', color: '#A78BFA', letterSpacing: '0.03em', whiteSpace: 'nowrap' }, (props && props.style) || {}) },
    React.createElement('img', { src: TRANSPARENCY_LOGO_SRC, alt: '', style: { width: 13, height: 13, borderRadius: 2, display: 'block' } }),
    'TRANSPARENCY');
}
window.TransparencyPill = TransparencyPill;

// Carrier display names + carrier tracking-page URLs — shared by the shipping module
// and the Orders detail modal (moved from app-shipping.js so every bundle can render
// clickable tracking numbers).
var SHIP_CARRIER_DISPLAY = { dhl_global_mail: 'DHL', dhl_ecommerce_us: 'DHL', stamps_com: 'USPS', usps: 'USPS', globalpost: 'GlobalPost', gp_plus: 'GlobalPost', fedex: 'FedEx', ups: 'UPS', amazon_shipping: 'Amazon Shipping', other: 'Other' };
function shipCarrierLabel(c) {
  if (!c) return c;
  return SHIP_CARRIER_DISPLAY[String(c).toLowerCase()] || c;
}
function shipTrackingUrl(carrier, tracking) {
  var c = String(carrier || '').toLowerCase();
  var t = encodeURIComponent(tracking || '');
  if (c.indexOf('dhl') !== -1) return 'https://webtrack.dhlecs.com/?trackingNumber=' + t;
  if (c.indexOf('usps') !== -1 || c.indexOf('stamps') !== -1) return 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' + t;
  if (c.indexOf('globalpost') !== -1 || c === 'gp_plus') return 'https://t.17track.net/en#nums=' + t;
  if (c.indexOf('ups') === 0) return 'https://www.ups.com/track?tracknum=' + t;
  if (c.indexOf('fedex') !== -1) return 'https://www.fedex.com/fedextrack/?trknbr=' + t;
  return 'https://t.17track.net/en#nums=' + t;
}
// Service codes → display names: globalmail_parcel_direct_ddp →
// "Globalmail Parcel Direct DDP". Strings that already look like display
// text (no underscores, mixed case) pass through untouched.
var SHIP_SERVICE_ACRONYMS = { ddp: 'DDP', ddu: 'DDU', usps: 'USPS', dhl: 'DHL' };
function shipServiceLabel(code) {
  if (!code) return code;
  var s = String(code);
  if (s.indexOf('_') === -1 && s !== s.toLowerCase()) return s;
  return s.split('_').map(function(w) {
    var lw = w.toLowerCase();
    if (SHIP_SERVICE_ACRONYMS[lw]) return SHIP_SERVICE_ACRONYMS[lw];
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
}
window.shipServiceLabel = shipServiceLabel;
window.shipCarrierLabel = shipCarrierLabel; window.shipTrackingUrl = shipTrackingUrl;
window.generateCode128SVG = generateCode128SVG; window.generateCode128SVGmm = generateCode128SVGmm; window.generateBarcodeImg = generateBarcodeImg; window.printBinLabel = printBinLabel;
window.generatePDF417SVG = generatePDF417SVG; window.generateCasePDF417SVG = generateCasePDF417SVG; window.buildAmazonCaseBarcodeData = buildAmazonCaseBarcodeData;

// ─── ReportParamSelect — shared multi-select dropdown (moved here from
// app-reports.js so the shipping/ads bundles can use it too). Two modes:
// preloaded list (default; client-side filtered) and search mode (`search`
// prop, high-cardinality fields) querying fetchReportFieldValues (defined in
// app-reports.js — only bundles that pass search:true need it). Selected
// values stay pinned at the top so they can always be deselected.
// The menu's reset row names the plural of the filter's label — "All batches", "All countries" —
// where a bare "s" used to be appended ("All batchs" on the Shipments Batch filter, Anthony 2026-10-13).
function pluralLower(word) {
  var w = String(word || '').trim().toLowerCase();
  if (/(ss|us|is|x|z|ch|sh)$/.test(w)) return w + 'es';
  // Already plural: the rule and auto-batch editors pass 'Tags', 'Countries', 'Carriers' ("All tagses", Anthony 2026-10-24)
  if (/s$/.test(w)) return w;
  if (/[^aeiou]y$/.test(w)) return w.slice(0, -1) + 'ies';
  return w + 's';
}
function ReportParamSelect({ label, values, loading, selected, onChange, search: searchable, source, fieldKey, onCreate, createLabel, valueColors, onManage, manageLabel }) {
  var h = React.createElement;
  var [open, setOpen] = useState(false);
  var [search, setSearch] = useState('');
  var [results, setResults] = useState(null);
  var [searching, setSearching] = useState(false);
  var [menuPos, setMenuPos] = useState(null);
  var searchSeq = useRef(0);
  var ref = useRef(null);
  var menuRef = useRef(null);

  // The menu portals to <body> (position: fixed) so it floats above modals and
  // never clips inside their scroll containers. Capture-phase listener: modal
  // content stopPropagation()s bubbling mousedowns, which used to keep the
  // menu open on click-away.
  var measure = function() {
    if (!ref.current) return;
    var r = ref.current.getBoundingClientRect();
    var z = window.APP_ZOOM || 1; // gBCR is visual px; fixed positions in zoomed px
    setMenuPos({ top: (r.bottom + 4) / z, left: r.left / z, minWidth: Math.max(260, r.width / z) });
  };
  useEffect(function() {
    if (!open) return;
    var onDown = function(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return function() {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  // Search mode: debounced live lookup once 2+ characters are typed
  useEffect(function() {
    if (!searchable || !open) return;
    var term = search.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    var seq = ++searchSeq.current;
    setSearching(true);
    var t = setTimeout(function() {
      fetchReportFieldValues(source, fieldKey, term).then(function(vals) {
        if (seq !== searchSeq.current) return;
        setResults(vals); setSearching(false);
      }).catch(function() {
        if (seq !== searchSeq.current) return;
        setResults([]); setSearching(false);
      });
    }, 300);
    return function() { clearTimeout(t); };
  }, [search, open, searchable, source, fieldKey]);

  var sel = selected || [];
  var summary = sel.length === 0 ? 'All'
    : sel.length === 1 ? sel[0]
    : sel.length + ' selected';
  var active = sel.length > 0;

  var list;
  if (searchable) {
    var found = results || [];
    list = sel.concat(found.filter(function(v) { return sel.indexOf(v) === -1; }));
  } else {
    list = (values || []).filter(function(v) {
      return !search || String(v).toLowerCase().indexOf(search.toLowerCase()) !== -1;
    });
  }

  var toggle = function(v) {
    if (sel.indexOf(v) !== -1) onChange(sel.filter(function(x) { return x !== v; }));
    else onChange(sel.concat([v]));
  };

  return h('div', { ref: ref, style: { position: 'relative', display: 'inline-block' } },
    h('button', {
      className: 'fbtn',
      onClick: function() { if (!open) measure(); setOpen(!open); setSearch(''); },
      style: {
        background: active ? C.accentSoft : C.bg,
        border: '1px solid ' + (active ? C.accent : C.border),
        color: active ? C.accent : C.text,
        padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
      }
    },
      h('span', { style: { color: active ? C.accent : C.textMuted, fontWeight: 600 } }, label + ':'),
      summary,
      h(LIcon, { name: 'chevron-down', size: 12 })
    ),
    open && menuPos && ReactDOM.createPortal(h('div', {
      ref: menuRef,
      className: 'rps-menu', // on <body>, outside every enclosing menu: their click-away handlers look for this (shipping "Other" menu, 9-22)
      style: {
        position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 5000,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)', minWidth: menuPos.minWidth, maxHeight: 'min(320px, calc(calc(100vh * var(--invzoom, 1)) - ' + Math.round(menuPos.top + 20) + 'px))', overflowY: 'auto',
      }
    },
      h('input', {
        autoFocus: true,
        value: search,
        onChange: function(e) { setSearch(e.target.value); },
        placeholder: searchable ? 'Type 2+ characters to search…' : 'Search…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }
      }),
      h('div', {
        onClick: function() { onChange([]); setOpen(false); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: sel.length === 0 ? C.accent : C.text, fontWeight: sel.length === 0 ? 600 : 400, borderBottom: '1px solid ' + C.border }
      }, 'All ' + pluralLower(label)),
      onCreate && h('div', {
        onClick: function() { setOpen(false); onCreate(); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.accent, fontWeight: 600, borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 }
      }, h(LIcon, { name: 'plus', size: 13 }), createLabel || 'Create new'),
      onManage && h('div', {
        onClick: function() { setOpen(false); onManage(); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.accent, fontWeight: 600, borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 }
      }, h(LIcon, { name: 'settings', size: 13 }), manageLabel || 'Manage'),
      (searchable ? searching : loading)
        ? h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, searchable ? 'Searching…' : 'Loading values…')
        : list.length === 0
          ? h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } },
              searchable
                ? (search.trim().length < 2 ? 'Type at least 2 characters to search your data' : 'No matches')
                : (values && values.length ? 'No matches' : 'No values in your data yet'))
          : list.map(function(v) {
              var isSel = sel.indexOf(v) !== -1;
              return h('div', {
                key: v,
                onClick: function() { toggle(v); },
                onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
                onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
                style: { padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }
              },
                h('div', {
                  style: {
                    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                    border: '1px solid ' + (isSel ? C.accent : C.border),
                    background: isSel ? C.accent : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }
                }, isSel && h(LIcon, { name: 'check', size: 11, color: '#fff' })),
                valueColors && valueColors[v] && h('span', { style: { width: 10, height: 10, borderRadius: 3, background: valueColors[v], flexShrink: 0 } }),
                h('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, v)
              );
            })
    ), document.body)
  );
}
window.ReportParamSelect = ReportParamSelect;

// ─── Sidebar pin state: server-side preference ───
// The pin (collapsed) state is stored in users.preferences.sidebar_collapsed so
// it follows the user across devices AND across the three app origins (main /
// ads / ship have separate localStorage). localStorage stays as the fast path
// for first paint; the server value wins once it arrives after login.
function syncSidebarPref(apply) {
  fetch('/api/auth/preferences', { credentials: 'include' })
    .then(function(r) { return r.ok ? r.json() : null; })
    .then(function(p) {
      if (p && typeof p.sidebar_collapsed === 'boolean') {
        try { localStorage.setItem('sidebar_collapsed', String(p.sidebar_collapsed)); } catch (e) {}
        apply(p.sidebar_collapsed);
      }
    })
    .catch(function() {});
}
function saveSidebarPref(collapsed) {
  try { localStorage.setItem('sidebar_collapsed', String(collapsed)); } catch (e) {}
  fetch('/api/auth/preferences', {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sidebar_collapsed: collapsed })
  }).catch(function() {});
}
window.syncSidebarPref = syncSidebarPref;
window.saveSidebarPref = saveSidebarPref;

// ─── HOURLY CAT PARADE (easter egg) ───
// Once per hour: a pixel cat gallops in along the bottom of the page, stops at
// center, faces the user and waves ("Hola" bubble) — then a rat streaks past
// ("!" above the cat), the cat bolts after it, a dog chases the cat, and a
// grandmother with a raised broom chases all three. Shared across tabs via
// localStorage 'cat_last_run'; only visible tabs fire. Every element is
// pointer-events: none — it can never intercept clicks. window.catRun() = demo.
// The hourly clock now plays the INVOICE STORM scene (further down; before it
// the FORKLIFT FUMBLE), or the TRICK-OR-TREAT PARADE during Oct 24-31; the cat
// parade and the forklift stay available as manual demos and the rig
// (catSpawn/catGo/catTwoFrames/catbub) is shared.
// Sprites are ASCII pixel grids: '.' transparent, 'f' fur (cat color variant),
// other chars index the actor's palette. One <rect> per horizontal run.
var CAT_PAL = { F: '#00000033', w: '#F1F5F9', p: '#F472B6', e: '#1F2937' };
var RAT_PAL = { f: '#78716C', p: '#F9A8D4', e: '#1F2937', w: '#D6D3D1' };
var DOG_PAL = { f: '#C9974C', d: '#8B5E2F', w: '#F1F5F9', e: '#1F2937', r: '#DC2626', p: '#F472B6' };
var CAT_RUN_A = [
".....................ff....ff...",
".....................fpf...fpf..",
"....................fffffffffff.",
".FF.................fffffffffff.",
".ff.................ffffffeefff.",
".ff.................ffffffeewwww",
"..ff................ffffffffwwwp",
"...ff.ffffffffffffffffffffwwww..",
".....ffffFFffFFffFFfffffff......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffffffffffffffwwww.......",
".....fffffffffffffffwww.........",
"......ffffffffffffffff..........",
"......ff...ff...ff..ff..........",
".....ff....ff...ff...ff.........",
"....ff.....ff...ff....ff........",
"....ff.....ff...ff....ff........",
"....ww.....ww...ww....ww........",
"................................"
];
var CAT_RUN_B = [
".....................ff....ff...",
".....................fpf...fpf..",
"....................fffffffffff.",
"....................fffffffffff.",
"....................ffffffeefff.",
".FF.................ffffffeewwww",
".ff.................ffffffffwwwp",
"..ff..ffffffffffffffffffffwwww..",
".....ffffFFffFFffFFfffffff......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffffffffffffffwwww.......",
".....fffffffffffffffwww.........",
"......ffffffffffffffff..........",
"........ff..ff....ff..ff........",
"........ff..ff....ff..ff........",
"........ww..ww....ww..ww........",
"................................",
"................................",
"................................"
];
var CAT_WAVE_A = [
"..ff..........ff........",
".fppf........fppf.......",
".ffffffffffffffff.......",
".ffffffffffffffff.ww....",
".fffeefffffeeffff.ww....",
".fffeefffffeeffff.ff....",
".ffffffppffffffff.ff....",
".ffffwwwwwwwfffff.ff....",
".ffffwwweewwfffff.ff....",
"..ffffffffffffffffff....",
"..ffffffffffffffff......",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
".ffffwwwwwwwffff........",
"....ff....ff............",
"....ww....ww............",
"....ww....ww............",
"........................"
];
var CAT_WAVE_B = [
"..ff..........ff........",
".fppf........fppf.......",
".ffffffffffffffff.......",
".ffffffffffffffff.......",
".fffeefffffeeffff.......",
".fffeefffffeeffff.......",
".ffffffppffffffff.ww....",
".ffffwwwwwwwfffff.ww....",
".ffffwwweewwfffff.ff....",
"..ffffffffffffffffff....",
"..ffffffffffffffff......",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
".ffffwwwwwwwffff........",
"....ff....ff............",
"....ww....ww............",
"....ww....ww............",
"........................"
];
var RAT_A = [
"..................ff......",
"ppp...............fpf.....",
"...pp....ffffffff.fff.....",
".....ppffffffffffffffffff.",
"......fffffffffffffffefff.",
"......fffffffffffffffffffp",
"......ffwwwwwwwffffffffff.",
"......ffwwwwwwwffffffff...",
".......fffffffffffff......",
".........ff......ff.......",
"........pp......pp........",
".........................."
];
var RAT_B = [
"..................ff......",
"..................fpf.....",
"ppppp....ffffffff.fff.....",
".....ppffffffffffffffffff.",
"......fffffffffffffffefff.",
"......fffffffffffffffffffp",
"......ffwwwwwwwffffffffff.",
"......ffwwwwwwwffffffff...",
".......fffffffffffff......",
"...........ff..ff.........",
"...........pp..pp.........",
".........................."
];
var DOG_A = [
"...........................ffffffff.....",
"..........................ddffffffff....",
"...dd.....................ddfffffefff...",
"...ff.....................ddfffffefff...",
"...ff.....................ddffffffffffee",
"....ff....................ddffffwwwwwww.",
"....ff....................dffffffwwww...",
"......ffffffffffffffffffffffffffff.pp...",
"......fffffffffffffffffffffrrrrrrr.pp...",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwww..........",
".......ffffffffffffffffffwwww...........",
".......fffffffffffffffffffff............",
"........ffffffffffffffffff..............",
"........ff...ff......ff...ff............",
".......ff....ff......ff....ff...........",
"......ff.....ff......ff.....ff..........",
"......ww.....ww......ww.....ww..........",
"........................................",
"........................................"
];
var DOG_B = [
"...........................ffffffff.....",
"..........................ddffffffff....",
"..........................ddfffffefff...",
"...dd.....................ddfffffefff...",
"...ff.....................ddffffffffffee",
"...ff.....................ddffffwwwwwww.",
"....ff....................dffffffwwww...",
"......ffffffffffffffffffffffffffff.pp...",
"......fffffffffffffffffffffrrrrrrr.pp...",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwww..........",
".......ffffffffffffffffffwwww...........",
".......fffffffffffffffffffff............",
"........ffffffffffffffffff..............",
".........ff...ff....ff...ff.............",
".........ff...ff....ff...ff.............",
".........ww...ww....ww...ww.............",
"........................................",
"........................................",
"........................................"
];
var LADY_PAL = { s: '#D9A574', g: '#E5E7EB', d: '#0D9488', D: '#0A5A54', w: '#F1F5F9', b: '#92400E', y: '#EAB308', e: '#1F2937', p: '#F9A8D4' };
var LADY_BASE = [
"..................................yyyyyyyy..",
"......................gggggggggg..yyyyyyyy..",
"..................ggggggggggggggg.yyyyyyyy..",
"..................ggggggggggggggg.yyyyyyyy..",
"..................ggggssssssssssg.yyyyyyyy..",
"......................ssssssssssg....bb.....",
"......................sssssseess.....bb.....",
"......................sssssseess.....bb.....",
"......................ssssssssss.....bb.....",
"......................ssppssssss.....bb.....",
"......................sssssseees.....bb.....",
"......................ssssssssss....sss.....",
"..........................sss.......sss.....",
"....................ddddddddddddddddd.......",
"..................ddddddddddddddddddd.......",
"..................dddddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
".................ssddddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
"...................ddddwwwwwwwwdddd.........",
"...................ddddwwwwwwwwdddd.........",
"...................ddddwwDDDDwwdddd.........",
"...................ddddwwDDDDwwdddd.........",
"..................dddddwwwwwwwwddddd........",
"..................dddddwwwwwwwwddddd........",
"..................dddDdddddddddDdddd........",
"..................dddddddddddddddddd........",
"........................sss..sss............",
"........................sss..sss............"
];
var LADY_A = LADY_BASE.concat([
".....................eeee.....eeee..........",
".....................eeee.....eeee.........."
]);
// Frame B: broom bobs down 1px, free hand swings up, legs pass mid-stride.
var LADY_B = LADY_A.slice();
LADY_B[0] = "............................................";
LADY_B[5] = "......................ssssssssssg.yyyyyyyy..";
LADY_B[15] = "................ssdddddwwwwwwwwddd..........";
LADY_B[17] = "....................dddwwwwwwwwddd..........";
LADY_B[28] = ".........................sss.sss............";
LADY_B[29] = ".........................sss.sss............";
LADY_B[30] = ".......................eeee..eeee...........";
LADY_B[31] = ".......................eeee..eeee...........";
function catAsciiRects(rows, pal, fur, dx) {
  var out = '';
  rows.forEach(function(row, y) {
    var x = 0;
    while (x < row.length) {
      var c = row[x];
      if (c === '.') { x++; continue; }
      var x0 = x;
      while (x < row.length && row[x] === c) x++;
      var fill;
      if (c === 'f') fill = fur;
      else if (c === '1' && !pal['1']) fill = catShadeHex(fur, 0.68);
      else if (c === '3' && !pal['3']) fill = catShadeHex(fur, 1.30);
      else fill = pal[c] || fur;
      if (c === 'F') out += '<rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + fur + '"/><rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + pal.F + '"/>';
      else out += '<rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + fill + '"/>';
    }
  });
  return out;
}
// ── Detail pipeline: 2x upscale + dark outline + auto-shading + accent
// patches (eye glints, whiskers, collar tag, broom texture). Runs once,
// lazily, on the base sprites above; only the processed frames render.
function catShadeHex(hex, k) {
  var n = parseInt(hex.slice(1), 16);
  var r = Math.min(255, Math.round(((n >> 16) & 255) * k));
  var g = Math.min(255, Math.round(((n >> 8) & 255) * k));
  var b = Math.min(255, Math.round((n & 255) * k));
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
function catDetail(rows, opts) {
  opts = opts || {};
  var W = rows[0].length;
  var g = [];
  var blank = function() { var a = []; for (var i = 0; i < W * 2 + 2; i++) a.push('.'); return a; };
  g.push(blank());
  rows.forEach(function(r) {
    var d = ['.'];
    for (var i = 0; i < r.length; i++) { d.push(r[i], r[i]); }
    d.push('.');
    g.push(d, d.slice());
  });
  g.push(blank());
  (opts.patches || []).forEach(function(p) {
    var bx = 2 * p[0] + 1, by = 2 * p[1] + 1, q = p[2], ch = p[3];
    var cells = q === 'full' ? [[bx, by], [bx + 1, by], [bx, by + 1], [bx + 1, by + 1]]
      : q === 'tl' ? [[bx, by]] : q === 'tr' ? [[bx + 1, by]] : q === 'bl' ? [[bx, by + 1]] : [[bx + 1, by + 1]];
    cells.forEach(function(c) { if (g[c[1]] && g[c[1]][c[0]] !== undefined) g[c[1]][c[0]] = ch; });
  });
  var y, x;
  for (y = 0; y < g.length; y++) for (x = 0; x < g[0].length; x++) {
    if (g[y][x] !== '.') continue;
    var nb = [g[y - 1] && g[y - 1][x], g[y + 1] && g[y + 1][x], g[y][x - 1], g[y][x + 1]];
    if (nb.some(function(c) { return c && c !== '.' && c !== 'O'; })) g[y][x] = 'O';
  }
  var out = g.map(function(r) { return r.slice(); });
  for (y = 1; y < g.length - 1; y++) for (x = 0; x < g[0].length; x++) {
    var c = g[y][x];
    if (opts.dark && opts.dark[c] && g[y + 1][x] === 'O') out[y][x] = opts.dark[c];
    else if (opts.light && opts.light[c] && g[y - 1][x] === 'O') out[y][x] = opts.light[c];
  }
  return out.map(function(r) { return r.join(''); });
}
CAT_PAL.O = '#141A23'; CAT_PAL['2'] = '#C7CDD6';
RAT_PAL.O = '#141A23'; RAT_PAL['1'] = '#57524E'; RAT_PAL['3'] = '#948E88';
DOG_PAL.O = '#141A23'; DOG_PAL['1'] = '#A0763B'; DOG_PAL['3'] = '#DDB06A'; DOG_PAL['2'] = '#C9CFD8'; DOG_PAL.y = '#EAB308';
LADY_PAL.O = '#141A23'; LADY_PAL['1'] = '#0A5A54'; LADY_PAL['3'] = '#16B3A5'; LADY_PAL['4'] = '#B9884F'; LADY_PAL['5'] = '#C3C9D1'; LADY_PAL['6'] = '#F5F7FA'; LADY_PAL['2'] = '#C9CFD8'; LADY_PAL['7'] = '#B45309';
var _catSprites = null;
function catSprites() {
  if (_catSprites) return _catSprites;
  var runOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[27,4,'tl','w'],[30,5,'br','e'],[30,6,'tr','e']] };
  var waveOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[5,4,'tl','w'],[12,4,'tl','w'],[5,7,'bl','e'],[11,7,'br','e'],[4,18,'br','O'],[10,18,'br','O']] };
  var ratOpts = { dark: { f: '1' }, light: { f: '3' }, patches: [[21,4,'tl','w']] };
  var dogOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[33,2,'tl','w'],[38,4,'tl','w'],[30,9,'full','y']] };
  var ladyPatches = [[24,1,'full','5'],[27,2,'full','5'],[21,3,'full','5']];
  [35,37,39,41].forEach(function(cx) { for (var cy = 0; cy < 5; cy++) ladyPatches.push([cx,cy,'full','7']); });
  var ladyOpts = { dark: { d: '1', s: '4', g: '5', w: '2', y: '7' }, light: { d: '3', g: '6' }, patches: ladyPatches };
  _catSprites = {
    runA: catDetail(CAT_RUN_A, runOpts), runB: catDetail(CAT_RUN_B, runOpts),
    waveA: catDetail(CAT_WAVE_A, waveOpts), waveB: catDetail(CAT_WAVE_B, waveOpts),
    ratA: catDetail(RAT_A, ratOpts), ratB: catDetail(RAT_B, ratOpts),
    dogA: catDetail(DOG_A, dogOpts), dogB: catDetail(DOG_B, dogOpts),
    ladyA: catDetail(LADY_A, ladyOpts), ladyB: catDetail(LADY_B, ladyOpts)
  };
  return _catSprites;
}
function catSvgWrap(inner, vwb, vhb, w, h, cls) {
  return '<svg class="' + cls + '" viewBox="0 0 ' + vwb + ' ' + vhb + '" width="' + w + '" height="' + h + '" shape-rendering="crispEdges">' + inner + '</svg>';
}
function catTwoFrames(fn0, fn1, extra) {
  return fn0('catfr' + (extra || '')) + fn1('catfr f2' + (extra || ''));
}
function catHtml(kind, fur, fast) {
  var S = catSprites();
  var mk = function(rows, dx) { return function(cls) { return catSvgWrap(catAsciiRects(rows, CAT_PAL, fur, dx), 66, 42, 99, 63, cls); }; };
  if (kind === 'w') return catTwoFrames(mk(S.waveA, 8), mk(S.waveB, 8), ' wv');
  return catTwoFrames(mk(S.runA), mk(S.runB), fast ? ' fast' : '');
}
function catRatHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, RAT_PAL, RAT_PAL.f), 54, 26, 81, 39, cls); }; };
  return catTwoFrames(mk(S.ratA), mk(S.ratB), ' fast');
}
function catDogHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, DOG_PAL, DOG_PAL.f), 82, 46, 123, 69, cls); }; };
  return catTwoFrames(mk(S.dogA), mk(S.dogB), ' fast');
}
function catLadyHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, LADY_PAL, LADY_PAL.d), 90, 66, 180, 132, cls); }; };
  return catTwoFrames(mk(S.ladyA), mk(S.ladyB), ' wv');
}
function catSpawn(html, w, h, x0, flip) {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:4px;left:0;width:' + w + 'px;height:' + h + 'px;z-index:99999;pointer-events:none;will-change:transform;';
  el.style.transform = 'translateX(' + x0 + 'px)' + flip;
  el.innerHTML = html;
  document.body.appendChild(el);
  el.getBoundingClientRect();
  return el;
}
function catGo(el, x, ms, flip) {
  el.style.transition = 'transform ' + ms + 'ms linear';
  el.style.transform = 'translateX(' + x + 'px)' + flip;
}
function catEnsureStyle() {
  if (document.getElementById('cat-style')) return;
  var st = document.createElement('style');
  st.id = 'cat-style';
  st.textContent =
    '@keyframes catblink { 0%, 49.9% { opacity: 1; } 50%, 100% { opacity: 0; } }' +
    '.catfr { position: absolute; inset: 0; animation: catblink 0.32s steps(1) infinite; }' +
    '.catfr.f2 { animation-delay: -0.16s; }' +
    '.catfr.wv { animation-duration: 0.5s; }' +
    '.catfr.f2.wv { animation-delay: -0.25s; }' +
    '.catfr.fast { animation-duration: 0.2s; }' +
    '.catfr.f2.fast { animation-delay: -0.1s; }' +
    '.catbub { position: fixed; z-index: 99999; pointer-events: none; background: #fff; color: #111; border: 2px solid #111; border-radius: 7px; font: 600 14px "DM Sans", system-ui, sans-serif; padding: 4px 10px; }' +
    '.catbub:after { content: ""; position: absolute; left: 14px; bottom: -7px; width: 9px; height: 9px; background: #fff; border-right: 2px solid #111; border-bottom: 2px solid #111; transform: rotate(45deg); }';
  document.head.appendChild(st);
}
function catRun() {
  try {
    catEnsureStyle();
    var fur = ['#64748B', '#D97706', '#9CA3AF', '#8B5A2B'][Math.floor(Math.random() * 4)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5;
    var flip = lr ? '' : ' scaleX(-1)';
    var enter = function(w) { return lr ? -w - 20 : vw + 20; };
    var exit = function(w) { return lr ? vw + 20 : -w - 20; };
    var mid = Math.round(vw / 2 - 50);
    var d1 = 2600, hold = 2600;
    var cat = catSpawn(catHtml('r', fur), 99, 63, enter(99), flip);
    catGo(cat, mid, d1, flip);
    var actors = [cat], bub = null, exc = null;
    setTimeout(function() {
      try {
        cat.innerHTML = catHtml('w', fur);
        bub = document.createElement('div');
        bub.className = 'catbub';
        bub.textContent = 'Hola';
        bub.style.left = (mid + (lr ? 55 : -8)) + 'px';
        bub.style.bottom = '75px';
        document.body.appendChild(bub);
      } catch (e) {}
    }, d1 + 60);
    setTimeout(function() {
      try {
        var rat = catSpawn(catRatHtml(), 81, 39, enter(81), flip);
        actors.push(rat);
        catGo(rat, exit(81), 2600, flip);
      } catch (e) {}
    }, d1 + hold - 600);
    setTimeout(function() {
      try {
        if (bub) { bub.remove(); bub = null; }
        exc = document.createElement('div');
        exc.textContent = '!';
        exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (mid + (lr ? 55 : 30)) + 'px;bottom:75px;font:800 30px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
        document.body.appendChild(exc);
      } catch (e) {}
    }, d1 + hold + 900);
    setTimeout(function() {
      try {
        if (exc) { exc.remove(); exc = null; }
        cat.innerHTML = catHtml('r', fur, true);
        catGo(cat, exit(99), 1900, flip);
      } catch (e) {}
    }, d1 + hold + 1800);
    setTimeout(function() {
      try {
        var dog = catSpawn(catDogHtml(), 123, 69, enter(123), flip);
        actors.push(dog);
        catGo(dog, exit(123), 2600, flip);
      } catch (e) {}
    }, d1 + hold + 2300);
    setTimeout(function() {
      try {
        var lady = catSpawn(catLadyHtml(), 180, 132, enter(180), flip);
        actors.push(lady);
        catGo(lady, exit(180), 4200, flip);
      } catch (e) {}
    }, d1 + hold + 2950);
    setTimeout(function() {
      try {
        actors.forEach(function(a) { try { a.remove(); } catch (e) {} });
        if (bub) bub.remove();
        if (exc) exc.remove();
      } catch (e) {}
    }, d1 + hold + 2950 + 4200 + 700);
  } catch (e) {}
}
function startCatClock() {
  var check = function() {
    try {
      if (document.hidden) return;
      var last = parseInt(localStorage.getItem('cat_last_run') || '0', 10);
      var now = Date.now();
      if (now - last < 3600 * 1000) return;
      // Claim the hour BEFORE the async auth probe (multi-tab dedupe), then
      // release it if this tab turns out to be logged out (login screen) —
      // the parade is for authenticated users only.
      localStorage.setItem('cat_last_run', String(now));
      fetch('/api/auth/me', { credentials: 'include' })
        .then(function(r) {
          if (r.ok) hourlyScene()();
          else localStorage.setItem('cat_last_run', String(last));
        })
        .catch(function() { try { localStorage.setItem('cat_last_run', String(last)); } catch (e) {} });
    } catch (e) {}
  };
  setInterval(check, 60000 + Math.floor(Math.random() * 5000));
  setTimeout(check, 15000 + Math.floor(Math.random() * 30000));
}
window.catRun = catRun;
try { if (typeof document !== 'undefined' && typeof localStorage !== 'undefined') startCatClock(); } catch (e) {}

// ─── FORKLIFT FUMBLE (the hourly cartoon until 2026-10-05; now a manual demo) ───
// A yellow forklift hauls a wobbling pallet of boxes in, stops at center, the
// top box slides off ("!"), the driver panics and reverses back out the way
// it came ("beep! beep!") — a warehouse worker sprints in chasing it and
// scoops the dropped box mid-run without breaking stride, then a portly
// supervisor waving paperwork storms after them both. Reuses the cat rig
// (catSpawn/catGo/catTwoFrames, catbub styles). Redrawn 2026-09-04 (Anthony:
// "same art style as the agents office") as smooth vector art — rounded flat
// shapes, The Office's palette, soft ground shadows — instead of the pixel
// sprite grids. Geometry stays in the old sprite's unit space (lift 126×70
// at 1.5 px/unit, worker 58×48, supervisor 62×50, box 24×14) so
// forkliftRun's drop/scoop math is unchanged: the front box stack sits at
// x 94-116 and box i occupies y [40-12i, 52-12i). Hard-hat color is random
// per run. window.forkliftRun() = demo.
var FORK_PAL = { h: '#F1F5F9' }; // driver's hard hat, randomised per run
var FORK_SCALE = 1.25; // Anthony 2026-09-04: "increase their size a little bit" — every px size and offset reads this
function fkPx(n) { return Math.round(n * FORK_SCALE); }
var FK = {
  body: '#EAB308', bodyDk: '#CA8A04', cage: '#2A333F', seat: '#39445A', mast: '#56617A', steel: '#8A94A2',
  skin: '#E8B98A', hair: '#4A3B2F', tire: '#0B0F16', hub: '#8A94A2', beacon: '#FBBF24',
  wood: '#B08968', woodDk: '#8F6B4E', tape: '#F1F5F9', vest: '#F97316', stripe: '#F1F5F9',
  pantsA: '#2A4879', pantsB: '#31548F', shirt: '#4C5A70', hat: '#FBBF24',
  suit: '#2E3950', shirtW: '#E5E7EB', tie: '#B91C1C', paper: '#F8FAFC', ink: '#94A3B8',
  shadow: 'rgba(0,0,0,0.28)', eye: '#1F2937',
};
function forkSvgWrap(inner, vwb, vhb, w, h, cls) {
  return '<svg class="' + cls + '" viewBox="0 0 ' + vwb + ' ' + vhb + '" width="' + w + '" height="' + h + '">' + inner + '</svg>';
}
function fkRect(x, y, w, h, r, fill, extra) {
  return '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + r + '" fill="' + fill + '"' + (extra || '') + '/>';
}
function fkCircle(cx, cy, r, fill, extra) { return '<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + fill + '"' + (extra || '') + '/>'; }
function fkEllipse(cx, cy, rx, ry, fill) { return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="' + rx + '" ry="' + ry + '" fill="' + fill + '"/>'; }
function fkG(inner, transform) { return '<g' + (transform ? ' transform="' + transform + '"' : '') + '>' + inner + '</g>'; }
// Cardboard box: wood-toned carton, darker top edge, a tape stripe, a label.
function fkBox(x, y, w, h) {
  return fkRect(x, y, w, h, 2, FK.wood) + fkRect(x, y, w, 2.4, 1.2, FK.woodDk) +
    fkRect(x + w / 2 - 1.3, y, 2.6, h, 0.9, FK.tape) + fkRect(x + 2.5, y + h - 4.5, 6, 2.6, 0.8, FK.tape, ' opacity="0.7"');
}
// Head with short hair and a hard hat (color per actor), facing right.
function fkHead(cx, cy, hat) {
  return fkCircle(cx, cy, 6.5, FK.skin) +
    '<path d="M ' + (cx - 6.5) + ',' + (cy - 1) + ' Q ' + (cx - 6) + ',' + (cy - 8) + ' ' + cx + ',' + (cy - 7.5) +
    ' Q ' + (cx + 6) + ',' + (cy - 8) + ' ' + (cx + 6.5) + ',' + (cy - 1) + ' L ' + (cx + 4.5) + ',' + (cy - 1) +
    ' Q ' + (cx + 3) + ',' + (cy - 5) + ' ' + cx + ',' + (cy - 5) + ' Q ' + (cx - 3) + ',' + (cy - 5) + ' ' + (cx - 4.5) + ',' + (cy - 1) + ' Z" fill="' + FK.hair + '"/>' +
    fkCircle(cx + 3, cy - 0.5, 1, FK.eye) +
    fkRect(cx - 7.5, cy - 12, 15, 6.5, 3.2, hat) + fkRect(cx - 9, cy - 7, 18.5, 2.2, 1.1, hat);
}
// The lift itself. mode 'run' (top front box leans on frame B) | 'wob'
// (stacks teeter). hts = [rearHeight, frontHeight] in boxes (0-4 each).
var FORK_STACK_X = [70, 94];
function forkliftFrame(mode, hts, which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(62, 68.5, 50, 3, FK.shadow));
  // rear roll-cage post (behind the driver), roof, beacon
  k.push(fkRect(13, 3, 3, 40, 1.5, FK.cage));
  k.push(fkRect(11, 1.5, 44, 4, 2, FK.cage));
  k.push(fkRect(29.5, 0, 6, 2.6, 1.3, FK.beacon));
  // seat
  k.push(fkRect(19, 28, 14, 13, 3.5, FK.seat));
  // driver (bobs one unit on frame B)
  var dy = B ? -1 : 0;
  var driver =
    fkRect(33, 37, 13, 5, 2.5, FK.pantsA) + fkRect(42, 37, 5, 9, 2.2, FK.pantsB) +
    fkRect(22, 19, 14, 20, 6, FK.shirt) + fkRect(24, 20, 10, 15, 4.5, FK.vest) + fkRect(24, 27, 10, 2, 1, FK.stripe) +
    fkRect(33, 24, 13, 4.2, 2.1, FK.shirt, ' transform="rotate(12 33 26)"') + fkCircle(46, 27.5, 2.4, FK.skin) +
    fkHead(29, 13, FORK_PAL.h);
  k.push(fkG(driver, dy ? 'translate(0,' + dy + ')' : ''));
  // steering column + wheel
  k.push(fkRect(46, 27, 3, 12, 1.5, FK.cage));
  k.push('<circle cx="47.5" cy="27" r="4" fill="none" stroke="' + FK.cage + '" stroke-width="2"/>');
  // body: main deck, counterweight block, lower skirt, front bumper
  k.push(fkRect(8, 40, 58, 20, 6, FK.body));
  k.push(fkRect(8, 40, 12, 20, 6, FK.bodyDk));
  k.push(fkRect(8, 52, 58, 8, 4, FK.bodyDk, ' opacity="0.55"'));
  k.push(fkRect(60, 44, 8, 12, 3, FK.cage));
  // front roll-cage post (in front of the driver)
  k.push(fkRect(49, 3, 3, 38, 1.5, FK.cage));
  // wheels: tires, hubs, spokes (spokes turn 45° between frames)
  [[24, 61], [56, 61]].forEach(function(w) {
    k.push(fkCircle(w[0], w[1], 9, FK.tire));
    k.push(fkCircle(w[0], w[1], 3.6, FK.hub));
    var rot = B ? 45 : 0;
    k.push('<g transform="rotate(' + rot + ' ' + w[0] + ' ' + w[1] + ')">' +
      fkRect(w[0] - 6.5, w[1] - 0.9, 13, 1.8, 0.9, FK.hub) + fkRect(w[0] - 0.9, w[1] - 6.5, 1.8, 13, 0.9, FK.hub) + '</g>');
  });
  // mast rails + crossbars, carriage, fork tines
  k.push(fkRect(62, 4, 3, 58, 1.5, FK.mast) + fkRect(68, 4, 3, 58, 1.5, FK.mast));
  [12, 30, 46].forEach(function(y) { k.push(fkRect(62, y, 9, 2, 1, FK.steel)); });
  k.push(fkRect(60, 50, 12, 9, 2.5, FK.cage));
  k.push(fkRect(64, 57, 58, 3, 1.5, FK.steel));
  // pallet: deck on the forks, three blocks beneath
  k.push(fkRect(68, 52, 52, 4.5, 1.2, FK.wood));
  [70, 92, 114].forEach(function(x) { k.push(fkRect(x, 56.5, 6, 3.5, 1, FK.woodDk)); });
  // box stacks (bottom box on the deck; box i at y 40-12i)
  hts.forEach(function(hgt, s) {
    for (var i = 0; i < hgt; i++) {
      var off = 0;
      if (mode === 'wob') off = !B ? Math.min(i, 2) * 2 : (i === 0 ? 0 : -2);
      else if (B && i === hgt - 1 && s === 1) off = 2;
      k.push(fkBox(FORK_STACK_X[s] + off, 40 - 12 * i, 22, 12));
    }
  });
  return k.join('');
}
function forkliftHtml(mode, hts, fast) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkliftFrame(mode, hts, which), 126, 70, fkPx(189), fkPx(105), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), fast ? ' fast' : '');
}
// Warehouse worker (58×48), hi-vis vest and hard hat, sprinting right;
// carry frames hold the rescued box out front.
function forkWorkerFrame(carry, which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(30, 47, 15, 2.5, FK.shadow));
  // legs: a running stride that swaps each frame
  k.push(fkRect(24, 33, 6, 14, 3, FK.pantsA, ' transform="rotate(' + (B ? 22 : -22) + ' 27 34)"'));
  k.push(fkRect(30, 33, 6, 14, 3, FK.pantsB, ' transform="rotate(' + (B ? -22 : 22) + ' 33 34)"'));
  // torso + vest + stripe
  k.push(fkRect(21, 14, 17, 21, 7, FK.shirt));
  k.push(fkRect(23, 15, 13, 16, 5, FK.vest) + fkRect(23, 22, 13, 2.2, 1.1, FK.stripe));
  if (carry) {
    k.push(fkRect(33, 19, 11, 4.4, 2.2, FK.shirt) + fkRect(33, 25, 11, 4.4, 2.2, FK.shirt));
    k.push(fkBox(36, 15, 21, 12));
    k.push(fkCircle(44, 21.2, 2.3, FK.skin) + fkCircle(44, 27.2, 2.3, FK.skin));
  } else {
    k.push(fkRect(34, 17, 12, 4.4, 2.2, FK.shirt, ' transform="rotate(' + (B ? 30 : -28) + ' 35 19)"'));
    k.push(fkRect(13, 17, 12, 4.4, 2.2, FK.shirt, ' transform="rotate(' + (B ? -30 : 28) + ' 24 19)"'));
  }
  k.push(fkHead(29.5, 9.5, FK.hat));
  return k.join('');
}
function forkWorkerHtml(carry) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkWorkerFrame(carry, which), 58, 48, fkPx(87), fkPx(72), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// Portly supervisor (62×50): dark suit, white shirt, red tie, waving a sheet
// of paper overhead — a slower two-frame stomp.
function forkSupFrame(which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(31, 49, 17, 2.6, FK.shadow));
  k.push(fkRect(24, 35, 7, 13, 3.5, FK.suit, ' transform="rotate(' + (B ? 14 : -14) + ' 27 36)"'));
  k.push(fkRect(32, 35, 7, 13, 3.5, FK.suit, ' transform="rotate(' + (B ? -14 : 14) + ' 35 36)"'));
  k.push(fkRect(17, 14, 28, 25, 12, FK.suit)); // the belly
  k.push(fkRect(27, 15, 9, 17, 3.5, FK.shirtW));
  k.push(fkRect(30, 16, 3, 12, 1.2, FK.tie));
  // waving arm + paper (higher on frame A)
  var ang = B ? 40 : 15;
  k.push(fkRect(41, 6, 4.4, 15, 2.2, FK.suit, ' transform="rotate(' + ang + ' 43 20)"'));
  var px = B ? 50 : 44, py = B ? 8 : 1.5;
  k.push(fkRect(px, py, 9, 11, 1.2, FK.paper) + fkRect(px + 1.5, py + 2.5, 6, 1.1, 0.5, FK.ink) +
    fkRect(px + 1.5, py + 5, 6, 1.1, 0.5, FK.ink) + fkRect(px + 1.5, py + 7.5, 4, 1.1, 0.5, FK.ink));
  // other arm pumping
  k.push(fkRect(12, 20, 11, 4.4, 2.2, FK.suit, ' transform="rotate(' + (B ? -20 : 20) + ' 20 22)"'));
  // head: bald on top, hair at the sides, glasses
  k.push(fkCircle(31, 9, 6.8, FK.skin));
  k.push(fkRect(24.5, 7, 3.2, 5, 1.6, FK.hair) + fkRect(34.3, 7, 3.2, 5, 1.6, FK.hair));
  k.push('<circle cx="34" cy="9" r="2.2" fill="none" stroke="' + FK.eye + '" stroke-width="0.9"/>' + fkCircle(34.6, 9, 0.9, FK.eye));
  return k.join('');
}
function forkSupHtml() {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkSupFrame(which), 62, 50, fkPx(93), fkPx(75), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
function forkBoxHtml() {
  return forkSvgWrap(fkBox(1, 1, 22, 12), 24, 14, fkPx(36), fkPx(21), '');
}
function forkliftRun() {
  try {
    catEnsureStyle();
    // Rear stack is always 4 boxes; front stack 3 or 4. The FRONT stack's top
    // box is the one that falls.
    var hf = Math.random() < 0.5 ? 4 : 3;
    FORK_PAL.h = ['#F1F5F9', '#FBBF24', '#3B82F6'][Math.floor(Math.random() * 3)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5;
    var flip = lr ? '' : ' scaleX(-1)';
    var chaseFlip = lr ? ' scaleX(-1)' : '';
    // Actor boxes in page px (unit sizes × FORK_SCALE)
    var LW = fkPx(189), LH = fkPx(105), WW = fkPx(87), WH = fkPx(72), SW = fkPx(93), SH = fkPx(75), BW = fkPx(36), BH = fkPx(21);
    // The forklift enters from one side and later REVERSES back out the same
    // side; the chasers enter from the opposite side and exit where it fled.
    var enter = function(w) { return lr ? -w - 20 : vw + 20; };
    var chaseEnter = function(w) { return lr ? vw + 20 : -w - 20; };
    var mid = Math.round(vw / 2 - fkPx(95));
    // Pacing (Anthony 2026-09-04, "slow them down"): travels ~40% longer than
    // the pixel version, and the beep sounds BEFORE the lift starts reversing.
    var d1 = 3600, dRev = 2700, dWorker = 3600, dSup = 5800;
    var tWob = d1 + 60, tDrop = d1 + 900, tBeep = d1 + 1700, tRev = d1 + 2300, tBeepOff = d1 + 3300, tWorker = d1 + 2600, tSup = d1 + 3400;
    var lift = catSpawn(forkliftHtml('run', [4, hf]), LW, LH, enter(LW), flip);
    catGo(lift, mid, d1, flip);
    var actors = [lift], bub = null, exc = null, boxEl = null;
    setTimeout(function() {
      try { lift.innerHTML = forkliftHtml('wob', [4, hf]); } catch (e) {}
    }, tWob);
    setTimeout(function() {
      try {
        exc = document.createElement('div');
        exc.textContent = '!';
        exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (mid + fkPx(lr ? 52 : 121)) + 'px;bottom:' + fkPx(112) + 'px;font:800 ' + fkPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
        document.body.appendChild(exc);
        var spawnBottom = fkPx(hf === 4 ? 81 : 63);
        var fall = spawnBottom - fkPx(6);
        boxEl = document.createElement('div');
        boxEl.style.cssText = 'position:fixed;bottom:' + spawnBottom + 'px;left:' + (mid + fkPx(lr ? 142 : 11)) + 'px;width:' + BW + 'px;height:' + BH + 'px;z-index:99999;pointer-events:none;will-change:transform;';
        boxEl.innerHTML = forkBoxHtml();
        document.body.appendChild(boxEl);
        boxEl.getBoundingClientRect();
        boxEl.style.transition = 'transform 340ms cubic-bezier(0.5,0,1,1)';
        boxEl.style.transform = 'translateY(' + fall + 'px)';
        setTimeout(function() { try { if (boxEl) { boxEl.style.transition = 'transform 110ms ease-out'; boxEl.style.transform = 'translateY(' + (fall - fkPx(8)) + 'px)'; } } catch (e) {} }, 360);
        setTimeout(function() { try { if (boxEl) { boxEl.style.transition = 'transform 110ms ease-in'; boxEl.style.transform = 'translateY(' + fall + 'px)'; } } catch (e) {} }, 480);
        lift.innerHTML = forkliftHtml('wob', [4, hf - 1]);
      } catch (e) {}
    }, tDrop);
    // "beep! beep!" — the driver notices, THEN backs out
    setTimeout(function() {
      try {
        if (exc) { exc.remove(); exc = null; }
        bub = document.createElement('div');
        bub.className = 'catbub';
        bub.textContent = 'beep! beep!';
        bub.style.left = (mid + fkPx(lr ? 44 : 113)) + 'px';
        bub.style.bottom = fkPx(116) + 'px';
        document.body.appendChild(bub);
      } catch (e) {}
    }, tBeep);
    setTimeout(function() {
      try {
        lift.innerHTML = forkliftHtml('run', [4, hf - 1], true);
        catGo(lift, enter(LW), dRev, flip);
      } catch (e) {}
    }, tRev);
    setTimeout(function() { try { if (bub) { bub.remove(); bub = null; } } catch (e) {} }, tBeepOff);
    setTimeout(function() {
      try {
        var worker = catSpawn(forkWorkerHtml(false), WW, WH, chaseEnter(WW), chaseFlip);
        actors.push(worker);
        catGo(worker, enter(WW), dWorker, chaseFlip);
        // Scoop the dropped box the moment the worker's leading edge reaches
        // it: swap to the carrying frames and remove the ground box.
        var bx = mid + fkPx(lr ? 142 : 11);
        var wx0 = chaseEnter(WW), wx1 = enter(WW);
        var grabX = lr ? bx + fkPx(30) : bx - fkPx(81);
        var frac = (grabX - wx0) / (wx1 - wx0);
        var scoopMs = Math.max(200, Math.min(dWorker - 200, Math.round(dWorker * frac)));
        setTimeout(function() {
          try {
            if (boxEl) { boxEl.remove(); boxEl = null; }
            worker.innerHTML = forkWorkerHtml(true);
          } catch (e) {}
        }, scoopMs);
      } catch (e) {}
    }, tWorker);
    setTimeout(function() {
      try {
        var sup = catSpawn(forkSupHtml(), SW, SH, chaseEnter(SW), chaseFlip);
        actors.push(sup);
        catGo(sup, enter(SW), dSup, chaseFlip);
      } catch (e) {}
    }, tSup);
    setTimeout(function() {
      try {
        actors.forEach(function(a) { try { a.remove(); } catch (e) {} });
        if (bub) bub.remove();
        if (exc) exc.remove();
        if (boxEl) boxEl.remove();
      } catch (e) {}
    }, tSup + dSup + 700);
  } catch (e) {}
}
window.forkliftRun = forkliftRun;

// ─── INVOICE STORM (the hourly cartoon) ───
// A gust of wind blows a flurry of invoices along the bottom of the page.
// Danny, the invoice-filing agent from The Office (his desk look: yellow
// shirt, cropped hair, beard), sprints in after them with an open folder
// ("my invoices!") and snaps them up one at a time. The last sheet slaps onto
// his face (no bubble: Anthony 2026-10-05 dropped the "mmf!") and he staggers
// off blind. One invoice fluttered to the
// floor behind him: the Filing floor's gray cat creeps up, wiggles, pounces
// and trots off with it. Took over the hourly clock 2026-10-05 (Anthony's
// pick); forkliftRun() and catRun() stay as manual demos. Same rig (catbub
// bubbles, catTwoFrames) and the forklift's vector helpers. Positions are
// planned along the wind: s runs from the upwind edge, and X(s, w) mirrors it
// when the wind blows right-to-left. window.stormRun() = demo.
var STORM_SCALE = 1.25;
function stPx(n) { return Math.round(n * STORM_SCALE); }
var ST = {
  shirt: '#EAB308', shirtDk: '#CA8A04', skin: '#9C6644', hair: '#1F1A17', // Danny as officeLook() draws 'invoice-filing'
  cat: '#8A94A2', // officePetFor('Filing')
  manila: '#E2B04A', manilaDk: '#C9952F', paper: '#FFFFFF', edge: '#64748B', line: '#CBD5E1', ink: '#475569', gust: '#94A3B8',
  accents: ['#14B8A6', '#3B82F6', '#F43F5E', '#A855F7', '#F59E0B', '#10B981', '#6366F1'],
};
function stCrescent(cx, cy, ro, ri, a0, a1) { // officeCrescent: filled band between two radii (degrees, 270 = top)
  var P = function(rad, a) { var t = a * Math.PI / 180; return (cx + Math.cos(t) * rad).toFixed(2) + ',' + (cy + Math.sin(t) * rad).toFixed(2); };
  var large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return 'M ' + P(ro, a0) + ' A ' + ro + ',' + ro + ' 0 ' + large + ' 1 ' + P(ro, a1) + ' L ' + P(ri, a1) + ' A ' + ri + ',' + ri + ' 0 ' + large + ' 0 ' + P(ri, a0) + ' Z';
}
// Danny's head at the Office walker scale (r 11.5): eye, beard, cropped cap
function stDannyHead(cx, cy) {
  var r = 11.5;
  return fkCircle(cx, cy, r, ST.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye) +
    '<path d="M ' + (cx - 0.3 * r) + ',' + (cy + 0.55 * r) + ' A ' + r + ',' + r + ' 0 0 0 ' + (cx + 0.9 * r) + ',' + (cy + 0.35 * r) +
    '" stroke="' + ST.hair + '" stroke-width="' + (0.3 * r) + '" fill="none" stroke-linecap="round"/>' +
    '<path d="' + stCrescent(cx, cy, 1.03 * r, 0.82 * r, 188, 352) + '" fill="' + ST.hair + '"/>';
}
// One invoice, 18×23: vendor color block, line items, bold total
function stInvoice(accent) {
  return fkRect(0, 0, 18, 23, 1.5, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.9"') +
    fkRect(2.2, 2.2, 7, 2.6, 0.7, accent) + fkRect(12, 2.6, 3.8, 1.4, 0.6, ST.line) +
    fkRect(2.2, 7.5, 13.6, 1.3, 0.6, ST.line) + fkRect(2.2, 10.4, 10.5, 1.3, 0.6, ST.line) +
    fkRect(2.2, 13.3, 13.6, 1.3, 0.6, ST.line) + fkRect(2.2, 16.2, 8, 1.3, 0.6, ST.line) +
    fkRect(10.2, 19.2, 5.6, 1.9, 0.7, ST.ink);
}
// Open folder held out front like a scoop, tipped forward: tall back panel
// with its tab, shorter front panel, k caught sheets standing up between.
function stFolderOpen(k) {
  var s = [fkRect(21, -68, 18, 25, 1.8, ST.manila) + fkRect(22, -71, 7, 4.5, 1.4, ST.manila)];
  for (var i = 0; i < k; i++) s.push(fkRect(23 + (i % 3) * 2.2, -67 + (i % 2) * 1.6 - Math.floor(i / 2) * 1.4, 13, 9, 0.9, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.7" transform="rotate(' + (-8 + i * 4) + ' 29 -62)"'));
  s.push('<path d="M 20.5,-58 L 39.5,-60 L 39.5,-43.8 Q 39.5,-42 37.7,-42 L 22.3,-42 Q 20.5,-42 20.5,-43.8 Z" fill="' + ST.manilaDk + '"/>');
  return fkG(s.join(''), 'rotate(16 26 -42)');
}
// Closed folder gripped at (hx, hy), k sheets peeking out of the top
function stFolderClosed(k, hx, hy, rot) {
  var s = [];
  for (var i = 0; i < k; i++) s.push(fkRect(2 + i * 1.6, -21 - (i % 2) * 1.2, 9, 6, 0.8, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.6"'));
  s.push(fkRect(0, -17, 15, 18, 1.5, ST.manila) + fkRect(9, -19, 5, 3, 1, ST.manila));
  return fkG(s.join(''), 'translate(' + (hx - 3) + ',' + (hy + 8) + ') rotate(' + rot + ' 3 -8)');
}
// Danny (84×100, feet at 30,97) sprinting, leaning in, open folder out front
function stDannyRun(k, which) {
  var B = which === 'B';
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow),
    fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? 30 : -30) + ' 0 -26)"'),
    fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? -30 : 30) + ' 0 -26)"')];
  var u = [fkRect(-2, -54, 28, 7.5, 3.75, ST.shirtDk, ' transform="rotate(6 -1 -50)"'), // far arm
    fkRect(-13, -58, 26, 36, 10, ST.shirt),
    fkRect(-1, -52, 28, 8, 4, ST.shirt, ' transform="rotate(12 0 -48)"'), // near arm
    stDannyHead(0, -68), stFolderOpen(k),
    fkCircle(25.5, -47, 3.6, ST.skin) + fkCircle(26.5, -42.5, 3.6, ST.skin)];
  p.push(fkG(u.join(''), 'translate(0,' + (B ? -1.5 : 0) + ') rotate(9 0 -28)'));
  return fkG(p.join(''), 'translate(30,97)');
}
// Danny blinded: the last invoice plastered over his face, staggering, one
// arm flailing overhead, the closed folder dangling from the other
function stDannySlap(k, which) {
  var B = which === 'B';
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow),
    fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? -10 : 14) + ' 0 -26)"'),
    fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? 16 : -8) + ' 0 -26)"')];
  var arm = function(ang, fill, sy) { return fkRect(-2, sy - 3.75, 27, 7.5, 3.75, fill, ' transform="rotate(' + ang + ' -1 ' + sy + ')"'); };
  var hand = function(ang, sy) { var t = ang * Math.PI / 180; return [-1 + Math.cos(t) * 26, sy + Math.sin(t) * 26]; };
  var farA = B ? -150 : -125, nearA = B ? 25 : 10;
  var fh = hand(farA, -51), nh = hand(nearA, -49);
  var u = [arm(farA, ST.shirtDk, -51), fkCircle(fh[0], fh[1], 3.6, ST.skin),
    fkRect(-13, -58, 26, 36, 10, ST.shirt), stDannyHead(0, -68),
    fkG(stInvoice(ST.accents[6]), 'translate(-1,-79) rotate(' + (B ? 6 : -6) + ' 9 11.5)'),
    arm(nearA, ST.shirt, -49), stFolderClosed(k, nh[0], nh[1], B ? 18 : -10), fkCircle(nh[0], nh[1], 3.6, ST.skin)];
  p.push(fkG(u.join(''), 'translate(0,' + (B ? -1 : 0) + ') rotate(-7 0 -28)'));
  return fkG(p.join(''), 'translate(30,97)');
}
// The Filing floor's gray cat (officeCatFig's shape, 58×44, feet at 26,41):
// walk, carry (the rescued invoice in its mouth), crouch (butt wiggle), leap
function stCatEars(dx, dy) {
  return '<path d="M ' + (6.5 + dx) + ',' + (-27 + dy) + ' L ' + (8 + dx) + ',' + (-33 + dy) + ' L ' + (11.5 + dx) + ',' + (-27.5 + dy) + ' Z" fill="' + ST.cat + '"/>' +
    '<path d="M ' + (12.5 + dx) + ',' + (-28 + dy) + ' L ' + (15.5 + dx) + ',' + (-33.5 + dy) + ' L ' + (17 + dx) + ',' + (-27 + dy) + ' Z" fill="' + ST.cat + '"/>';
}
function stCatFrame(pose, which) {
  var B = which === 'B', c = ST.cat, p = [];
  var tail = function(d) { return '<path d="' + d + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>'; };
  if (pose === 'leap') {
    p.push(tail('M -14,-17 Q -21,-19 -25,-14'));
    p.push(fkRect(-19, -14, 11, 4.2, 2.1, c, ' transform="rotate(18 -9 -12)"'));
    p.push(fkRect(8, -17, 12, 4.2, 2.1, c, ' transform="rotate(22 9 -15)"'));
    p.push('<ellipse cx="-1" cy="-18" rx="15" ry="6.5" fill="' + c + '" transform="rotate(-8 -1 -18)"/>');
    p.push(fkCircle(13, -25, 7, c) + stCatEars(1, -3) + fkCircle(15.5, -25.5, 1.3, FK.eye));
  } else if (pose === 'crouch') {
    p.push(fkEllipse(0, 1.2, 15, 2.2, FK.shadow));
    p.push(tail(B ? 'M -13,-6 Q -24,-9 -21,-19' : 'M -13,-6 Q -21,-13 -15,-21'));
    p.push(fkG(fkRect(-12, -7, 5, 7, 2.5, c) + fkEllipse(-1, -9, 13.5, 6.5, c), 'rotate(' + (B ? -7 : -3) + ' 9 -5)'));
    p.push(fkRect(6, -4.5, 7, 4.5, 2.2, c));
    p.push(fkCircle(13, -13, 7, c) + stCatEars(1, 9) + fkCircle(15.5, -13.5, 1.4, FK.eye));
  } else {
    p.push(fkEllipse(0, 1.2, 15, 2.2, FK.shadow));
    p.push(tail(B ? 'M -13,-9 Q -22,-10 -22,-22' : 'M -13,-9 Q -21,-12 -19,-25'));
    p.push(fkRect(-10, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? 22 : -22) + ' -7.75 -8)"'));
    p.push(fkRect(4, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? -22 : 22) + ' 6.25 -8)"'));
    p.push(fkEllipse(-1, -14, 13, 7.5, c));
    p.push(fkCircle(12, -22, 7, c) + stCatEars(0, 0) + fkCircle(14.5, -22.5, 1.1, FK.eye));
    if (pose === 'carry') p.push(fkG(stInvoice(ST.accents[1]), 'translate(16,-25) rotate(24) scale(0.5)'));
  }
  return fkG(p.join(''), 'translate(26,41)');
}
function stDannyHtml(pose, k) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(pose === 'slap' ? stDannySlap(k, which) : stDannyRun(k, which), 84, 100, stPx(84), stPx(100), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'slap' ? ' wv' : ' fast');
}
function stCatHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(stCatFrame(pose, which), 58, 44, stPx(58), stPx(44), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' ? ' wv' : ' fast');
}
function stPaperSvg(accent) { return forkSvgWrap(fkG(stInvoice(accent), 'translate(1,1)'), 20, 25, stPx(28), stPx(35), ''); }
function stGustSvg() {
  return forkSvgWrap('<path d="M 4,24 C 40,16 78,30 114,19 C 128,14 138,6 130,3.5 C 122,1.5 120,11 127,13" stroke="' + ST.gust + '" stroke-width="2.6" fill="none" stroke-linecap="round"/>' +
    '<path d="M 26,32 C 52,27 80,34 104,28" stroke="' + ST.gust + '" stroke-width="2" fill="none" stroke-linecap="round" opacity="0.7"/>', 150, 36, stPx(150), stPx(36), 'stgust');
}
function stEnsureStyle() {
  catEnsureStyle();
  if (document.getElementById('storm-style')) return;
  var st = document.createElement('style');
  st.id = 'storm-style';
  st.textContent =
    '@keyframes stFlut { 0%, 100% { transform: translateY(0) rotate(-14deg) scaleX(1); } 25% { transform: translateY(-10px) rotate(5deg) scaleX(0.5); } 50% { transform: translateY(-3px) rotate(18deg) scaleX(0.95); } 75% { transform: translateY(-12px) rotate(2deg) scaleX(0.45); } }' +
    '.stfl { position: absolute; inset: 0; animation: stFlut 1.4s ease-in-out infinite; }' +
    '.stflat { position: absolute; inset: 0; transform-origin: 50% 100%; transform: rotate(-3deg) scaleY(0.42); }' +
    '@keyframes stGust { 0% { opacity: 0; } 15%, 75% { opacity: 0.9; } 100% { opacity: 0; } }' +
    '.stgust { animation: stGust 1.4s linear forwards; }';
  document.head.appendChild(st);
}
// Actors here move on both axes (papers rise and fall, the cat pounces), so
// they use translate(x, y) instead of catSpawn/catGo's translateX.
function stSpawn(html, w, h, bottom, x, y, flip) {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:' + bottom + 'px;left:0;width:' + w + 'px;height:' + h + 'px;z-index:99999;pointer-events:none;will-change:transform;';
  el.style.transform = 'translate(' + x + 'px,' + y + 'px)' + (flip || '');
  el.innerHTML = html;
  document.body.appendChild(el);
  el.getBoundingClientRect();
  return el;
}
function stMove(el, x, y, ms, ease, flip) {
  el.style.transition = 'transform ' + ms + 'ms ' + (ease || 'linear');
  el.style.transform = 'translate(' + x + 'px,' + y + 'px)' + (flip || '');
}
function stBubble(text, x, bottom) {
  var b = document.createElement('div');
  b.className = 'catbub';
  b.textContent = text;
  b.style.left = '0px';
  b.style.bottom = bottom + 'px';
  b.style.transform = 'translateX(' + x + 'px)';
  document.body.appendChild(b);
  b.getBoundingClientRect();
  return b;
}
function stormRun() {
  try {
    stEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the wind blows
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var DW = stPx(84), DH = stPx(100), CW = stPx(58), CH = stPx(44), PW = stPx(28), PH = stPx(35), GW = stPx(150), GH = stPx(36);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var accents = ST.accents.slice().sort(function() { return Math.random() - 0.5; });

    // The gust: three wind streaks race across ahead of the paper
    [[0, 70], [180, 128], [380, 34]].forEach(function(g) {
      at(g[0], function() {
        var el = keep(stSpawn(stGustSvg(), GW, GH, g[1], X(-GW, GW), 0, flip));
        stMove(el, X(vw + 20, GW), 0, 1400, 'linear', flip);
      });
    });

    // Seven invoices ride it in to a hover spot, then drift on slowly,
    // fluttering. [spot as a share of the width, bottom px, role]: catches
    // hover at folder height, the stray sinks to the floor, the face-slapper
    // hovers at head height and goes last.
    var tIn = 1400, drift = 0.025; // px per ms once settled
    var sheets = [[0.20, 40, 'catch'], [0.31, 150, 'stray'], [0.41, 44, 'catch'], [0.51, 38, 'catch'], [0.61, 46, 'catch'], [0.71, 40, 'catch'], [0.84, 64, 'face']]
      .map(function(d, i) { var t0 = 150 + 140 * i; return { i: i, spot: Math.round(vw * d[0]), bottom: d[1], role: d[2], t0: t0, tS: t0 + tIn, el: null }; });
    var stray = sheets[1], face = sheets[6];
    sheets.forEach(function(p) {
      at(p.t0, function() {
        var fl = '<div class="stfl" style="animation-duration:' + (1.15 + (p.i % 3) * 0.22).toFixed(2) + 's;animation-delay:-' + (p.i * 0.37).toFixed(2) + 's">' + stPaperSvg(accents[p.i]) + '</div>';
        p.el = keep(stSpawn(fl, PW, PH, p.bottom, X(-PW - 10, PW), -40 - (p.i % 3) * 18, ''));
        stMove(p.el, X(p.spot, PW), 0, tIn, 'cubic-bezier(0.2,0.7,0.3,1)');
      });
      if (p.role === 'stray') {
        at(p.tS, function() { stMove(p.el, X(p.spot + 70, PW), p.bottom - 4, 1700, 'cubic-bezier(0.45,0,0.75,1)'); });
        at(p.tS + 1700, function() { var f = p.el.firstChild; f.style.animation = 'none'; f.className = 'stflat'; });
      } else {
        at(p.tS, function() { stMove(p.el, X(p.spot + drift * 16000, PW), 0, 16000, 'linear'); });
      }
    });

    // Danny sprints in once they've all settled. A sheet is caught when the
    // folder's mouth (fOff px from his back edge) reaches its center; the
    // face-slapper when his face (faceOff) does. Both move linearly, so each
    // meeting time is solved up front.
    var tD = 2700, sD0 = -DW - 20, v = (vw + DW + 20) / 4400;
    var fOff = stPx(72), faceOff = stPx(46);
    var meetAt = function(p, off) { return Math.round((p.spot + PW / 2 - off - sD0 + v * tD - drift * p.tS) / (v - drift)); };
    var tSlap = meetAt(face, faceOff);
    var sSlap = sD0 + v * (tSlap - tD), sStop = sSlap + stPx(10);
    var headX = function(s) { return lr ? X(s, DW) + stPx(36) : X(s, DW) + DW - stPx(36); };
    var bubBottom = 4 + stPx(90);
    var danny = null, caught = 0;
    at(tD, function() {
      danny = keep(stSpawn(stDannyHtml('run', 0), DW, DH, 4, X(sD0, DW), 0, flip));
      stMove(danny, X(sSlap, DW), 0, tSlap - tD, 'linear', flip);
    });
    // "my invoices!" rides along over his head for the first stretch
    at(tD + 500, function() {
      var b = keep(stBubble('my invoices!', headX(sD0 + v * 500) - 16, bubBottom));
      b.style.transition = 'transform 1600ms linear';
      b.style.transform = 'translateX(' + (headX(sD0 + v * 2100) - 16) + 'px)';
      at(1600, function() { drop(b); });
    });
    sheets.forEach(function(p) {
      if (p.role !== 'catch') return;
      at(meetAt(p, fOff), function() {
        drop(p.el);
        caught++;
        if (danny) danny.innerHTML = stDannyHtml('run', caught);
      });
    });
    at(tSlap, function() {
      drop(face.el);
      danny.innerHTML = stDannyHtml('slap', caught);
      stMove(danny, X(sStop, DW), 0, 260, 'ease-out', flip);
    });
    // ...then staggers off blind at a slower pace
    at(tSlap + 1500, function() { stMove(danny, X(vw + 20, DW), 0, Math.round((vw + 20 - sStop) / (v * 0.6)), 'linear', flip); });

    // The cat creeps in behind him toward the stray on the floor, crouches,
    // wiggles, pounces (mouth lands on the sheet) and trots back out with it.
    var sLand = stray.spot + 70 + PW / 2 - stPx(40), sCrouch = sLand - stPx(60), sC0 = -CW - 20;
    var tCat = tSlap + 300, dCatIn = Math.max(1000, Math.min(4000, Math.round((sCrouch - sC0) / 0.26)));
    var tPounce = tCat + dCatIn + 1100, tLand = tPounce + 480, dCatOut = Math.round((sLand - sC0) / 0.3);
    var cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(stCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, flip));
      stMove(cat, X(sCrouch, CW), 0, dCatIn, 'linear', flip);
    });
    at(tCat + dCatIn, function() { cat.innerHTML = stCatHtml('crouch'); });
    at(tPounce, function() {
      cat.innerHTML = stCatHtml('leap');
      stMove(cat, X((sCrouch + sLand) / 2, CW), -stPx(28), 240, 'ease-out', flip);
    });
    at(tPounce + 240, function() { stMove(cat, X(sLand, CW), 0, 240, 'ease-in', flip); });
    at(tLand, function() { drop(stray.el); cat.innerHTML = stCatHtml('carry'); });
    at(tLand + 450, function() {
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sLand, CW) + 'px,0px)' + back;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', back);
    });
    at(Math.max(tLand + 450 + dCatOut, tSlap + 1500 + Math.round((vw + 20 - sStop) / (v * 0.6))) + 600, function() { els.forEach(drop); });
  } catch (e) {}
}
window.stormRun = stormRun;

// ─── TRICK-OR-TREAT PARADE (the hourly cartoon Oct 24-31, every year) ───
// Bats flutter past, then the Office agents cross the bottom of the page in
// costume, each with their desk look: Emma (Customer Support) as a witch
// with a broom, Danny as a mummy holding out a pumpkin pail, Sage as a
// vampire whose cape swishes, the Filing floor's gray cat in a pumpkin suit,
// and last a bedsheet ghost that stops at center, turns to the screen
// ("boo!") and fades away. Anthony 2026-10-05: plays on every hourly run in
// the week before Halloween (the viewer's local date); the Invoice Storm
// plays the rest of the year. Same rig and helpers as the Invoice Storm.
// window.paradeRun() = demo.
var HW = {
  emma: { hair: '#B8BCC4', skin: '#E8B98A', shirt: '#14B8A6', headset: true }, // officeLook('reamaze-cs'): ponytail, stud
  sage: { hair: '#8C3B22', skin: '#6B4530', shirt: '#84CC16', glasses: true }, // officeLook('shopify-faq'): bob, round glasses, stud
  witch: '#2E1A47', band: '#7C3AED', stocking: '#7C3AED', broom: '#8B5A2B', straw: '#D4A24C', strawDk: '#A97C2F',
  cape: '#1C1A26', lining: '#B91C1C', pants: '#1F2937', pantsB: '#2B3444', rim: '#8B93A7', gold: '#F2CF6B',
  wrap: '#EDE6D6', wrapDk: '#D9D0BC', wrapLn: '#B9AE96', pumpkin: '#F97316', pumpkinDk: '#C2410C', stem: '#4D7C0F', leaf: '#65A30D',
  ghost: '#F8FAFC', ghostLn: '#94A3B8', bat: '#2E1065', batLn: '#A78BFA', batEye: '#FBBF24', ink: '#1F2937',
};
// OFC_HAIR_SHORT_WALKER: the short cap over a walker-scale head at (0,-68), r 11.5
var HW_CAP_SHORT = 'M 9.5,-74.8 Q 5.4,-81.2 -0.7,-81.5 Q -9.5,-81.2 -13.2,-74.8 Q -14.9,-70.7 -14.9,-65.3 Q -15,-61.2 -12.9,-58.9 Q -10.5,-60.6 -10.5,-63.9 Q -10.8,-70.7 -6.1,-74.4 Q -0.7,-77.8 4.7,-76.8 Q 8.1,-76.1 9.5,-74.8 Z';
function hwRim(c) { return ' stroke="' + (c || HW.rim) + '" stroke-width="0.9"'; }
// officeHeadGroup at walker scale for the ponytail and bob styles (stud earrings, optional glasses/headset)
function hwHead(lk, style) {
  var r = 11.5, cx = 0, cy = -68, k = [];
  if (style === 'ponytail') k.push('<ellipse cx="' + (cx - 1.02 * r) + '" cy="' + (cy + 0.35 * r) + '" rx="' + (0.3 * r) + '" ry="' + (0.9 * r) + '" fill="' + lk.hair + '" transform="rotate(10 ' + (cx - 1.02 * r) + ' ' + (cy + 0.35 * r) + ')"/>');
  if (style === 'bob') k.push(fkRect(cx - 1.1 * r, cy - 0.62 * r, 1.45 * r, 1.58 * r, 0.42 * r, lk.hair));
  k.push(fkCircle(cx, cy, r, lk.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye));
  k.push('<path d="' + HW_CAP_SHORT + '" fill="' + lk.hair + '"/>');
  if (lk.glasses) {
    var gx = cx + 0.52 * r, gy = cy - 0.05 * r, gr = 0.3 * r;
    k.push('<circle cx="' + gx + '" cy="' + gy + '" r="' + gr + '" fill="rgba(255,255,255,0.10)" stroke="#2B3444" stroke-width="' + (0.09 * r) + '"/>' +
      '<path d="M ' + (gx - gr) + ',' + gy + ' L ' + (cx - 0.55 * r) + ',' + (cy - 0.1 * r) + '" stroke="#2B3444" stroke-width="' + (0.08 * r) + '" fill="none"/>');
  }
  k.push(fkCircle(cx - 0.48 * r, cy + 0.58 * r, 0.09 * r, HW.gold));
  if (lk.headset) {
    var hs = '#222B3A';
    k.push('<path d="M ' + (cx - r) + ',' + (cy - 0.35 * r) + ' A ' + (1.03 * r) + ',' + (1.03 * r) + ' 0 0 1 ' + (cx + r) + ',' + (cy - 0.35 * r) + '" stroke="' + hs + '" stroke-width="' + (0.2 * r) + '" fill="none" stroke-linecap="round"/>' +
      fkCircle(cx - 0.88 * r, cy + 0.06 * r, 0.29 * r, hs) +
      '<path d="M ' + (cx - 0.88 * r) + ',' + (cy + 0.35 * r) + ' Q ' + (cx - 0.7 * r) + ',' + (cy + 0.82 * r) + ' ' + (cx - 0.12 * r) + ',' + (cy + 0.88 * r) + '" stroke="' + hs + '" stroke-width="' + (0.15 * r) + '" fill="none" stroke-linecap="round"/>' +
      fkCircle(cx - 0.06 * r, cy + 0.88 * r, 0.13 * r, hs));
  }
  return k.join('');
}
// Walker legs (Office stroll proportions), swinging on two frames; extra = trim drawn on each leg
function hwLegs(B, fillA, fillB, swing, trimA, trimB) {
  return fkG(fkRect(-9.5, -28, 8, 28, 4, fillA) + (trimA || ''), 'rotate(' + (B ? swing : -swing) + ' -5.5 -26)') +
    fkG(fkRect(1.5, -28, 8, 28, 4, fillB) + (trimB || ''), 'rotate(' + (B ? -swing : swing) + ' 5.5 -26)');
}
// Jack-o'-lantern candy pail hanging from a hand at (x, y)
function hwPail(x, y) {
  return '<path d="M ' + (x - 6) + ',' + (y + 6) + ' Q ' + x + ',' + (y - 3) + ' ' + (x + 6) + ',' + (y + 6) + '" stroke="' + HW.ink + '" stroke-width="1.1" fill="none"/>' +
    '<ellipse cx="' + x + '" cy="' + (y + 11) + '" rx="8" ry="6.8" fill="' + HW.pumpkin + '"/>' +
    '<ellipse cx="' + x + '" cy="' + (y + 11) + '" rx="3.4" ry="6.8" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="0.9"/>' +
    '<path d="M ' + (x - 4.4) + ',' + (y + 10) + ' L ' + (x - 2.4) + ',' + (y + 7.4) + ' L ' + (x - 0.8) + ',' + (y + 10) + ' Z M ' + (x + 0.8) + ',' + (y + 10) + ' L ' + (x + 2.4) + ',' + (y + 7.4) + ' L ' + (x + 4.4) + ',' + (y + 10) + ' Z" fill="' + HW.ink + '"/>' +
    '<path d="M ' + (x - 4.5) + ',' + (y + 12.8) + ' Q ' + x + ',' + (y + 17.5) + ' ' + (x + 4.5) + ',' + (y + 12.8) + ' Z" fill="' + HW.ink + '"/>';
}
// Emma as a witch (90×112, feet at 40,106): pointed hat, cape, striped stockings, broom
function hwEmmaFrame(which) {
  var B = which === 'B', lk = HW.emma, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var stripes = function(x) { return [-22, -15, -8].map(function(y) { return fkRect(x, y, 8, 2.6, 0.6, HW.stocking); }).join(''); };
  k.push(hwLegs(B, HW.witch, HW.witch, 16, stripes(-9.5), stripes(1.5)));
  var u = [];
  u.push('<path d="M -8,-58 Q ' + (B ? -26 : -22) + ',-34 ' + (B ? -24 : -20) + ',-12 L -4,-14 Q -6,-36 2,-57 Z" fill="' + HW.witch + '"' + hwRim() + '/>'); // cape
  u.push('<path d="M -13,-36 L 13,-36 L 17,-20 L -17,-20 Z" fill="' + HW.witch + '"' + hwRim() + '/>'); // skirt
  u.push(fkRect(-13, -58, 26, 26, 10, lk.shirt));
  u.push(hwHead(lk, 'ponytail'));
  u.push('<ellipse cx="1" cy="-77.5" rx="17" ry="3.2" fill="' + HW.witch + '"' + hwRim() + '/>' +
    '<path d="M -9,-78 L 10,-78 L 3,-94 Q 0,-101 -9,-99 Q -3,-96 -2,-92 Z" fill="' + HW.witch + '"' + hwRim() + '/>' +
    fkRect(-8, -82.5, 17, 3.6, 1, HW.band) + fkRect(-0.8, -82.5, 3.6, 3.6, 0.6, HW.gold));
  // broom carried across the body: straw trailing low behind her
  u.push('<line x1="33" y1="-52" x2="-27" y2="-12" stroke="' + HW.broom + '" stroke-width="2.6" stroke-linecap="round"/>' +
    '<path d="M -23,-15 L -39,-9 Q -40,-3 -35,0 L -21,-11 Z" fill="' + HW.straw + '"/>' +
    '<path d="M -26,-12 L -37,-5 M -24,-11 L -33,-1" stroke="' + HW.strawDk + '" stroke-width="0.9"/>');
  u.push(fkRect(2, -53, 20, 7.5, 3.75, lk.shirt, ' transform="rotate(18 4 -49)"') + fkCircle(21, -43.5, 4.2, lk.skin));
  k.push(fkG(u.join(''), B ? 'translate(0,-1.2)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// Danny as a mummy: bandaged head to toe, a slit for his eyes, arms out, stiff shuffle, pumpkin pail
function hwMummyFrame(which) {
  var B = which === 'B', W = HW.wrap, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var ln = function(x1, y1, x2, y2) { return '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" stroke="' + HW.wrapLn + '" stroke-width="1.1" stroke-linecap="round"/>'; };
  var legWraps = function(x) { return [-22, -14, -6].map(function(y) { return ln(x + 1, y + 1.5, x + 7, y - 1.5); }).join(''); };
  k.push(hwLegs(B, W, W, 8, legWraps(-9.5), legWraps(1.5)));
  var u = [];
  u.push('<path d="M -12,-46 Q -22,' + (B ? -40 : -48) + ' -31,' + (B ? -35 : -43) + '" stroke="' + W + '" stroke-width="3.2" fill="none" stroke-linecap="round"/>'); // loose end
  u.push(fkRect(-1, -55, 28, 7.5, 3.75, HW.wrapDk, ' transform="rotate(' + (B ? -6 : -2) + ' 0 -51)"')); // far arm
  u.push(fkRect(-13, -58, 26, 36, 10, W) + ln(-9, -50, 9, -54) + ln(-11, -43, 11, -47) + ln(-11, -36, 11, -40) + ln(-9, -29, 9, -33));
  u.push(fkCircle(0, -68, 11.5, W) + ln(-8, -74, 8, -77) + ln(-10, -59.5, 9, -62.5) + ln(-11, -65, -2, -66.5));
  u.push(fkRect(-1, -71, 12, 4.4, 2.2, ST.skin) + fkCircle(6, -68.8, 1.6, FK.eye)); // eye slit
  u.push(fkG(fkRect(0, -52, 28, 8, 4, W) + ln(9, -51, 8, -45) + ln(17, -51, 16, -45), 'rotate(' + (B ? 0 : -4) + ' 1 -48)')); // near arm
  u.push(hwPail(27, B ? -48 : -50));
  k.push(fkG(u.join(''), B ? 'translate(0,-1)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// Sage as a vampire: high collar, black cape with a red lining that swishes, fangs, pumpkin pail
function hwSageFrame(which) {
  var B = which === 'B', lk = HW.sage, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var u = [];
  u.push('<path d="M -6,-60 Q ' + (B ? -34 : -26) + ',-34 ' + (B ? -34 : -27) + ',-4 L -8,-8 Q -12,-34 0,-58 Z" fill="' + HW.cape + '"' + hwRim() + '/>' +
    '<path d="M -7,-57 Q ' + (B ? -28 : -21) + ',-34 ' + (B ? -28 : -22) + ',-7 L -10,-9 Q -13,-34 -3,-56 Z" fill="' + HW.lining + '"/>');
  u.push('<path d="M -12,-56 L -23,-84 L -15,-79 L -8,-86 L -3,-61 Z" fill="' + HW.cape + '"' + hwRim() + '/>' +
    '<path d="M -11,-57.5 L -20,-80 L -14.5,-76.5 L -8.5,-81.5 L -5,-62 Z" fill="' + HW.lining + '"/>'); // stand-up collar
  k.push(fkG(u.join(''), B ? 'translate(0,-1)' : ''));
  k.push(hwLegs(B, HW.pants, HW.pantsB, 16));
  var v = [];
  v.push(fkRect(-13, -58, 26, 36, 10, lk.shirt));
  v.push(hwHead(lk, 'bob'));
  v.push('<path d="M 8.6,-62.4 L 10.4,-62.6 L 9.6,-59.6 Z" fill="#FFFFFF"/>'); // fang
  v.push(fkRect(2, -53, 20, 7.5, 3.75, lk.shirt, ' transform="rotate(40 4 -49)"') + hwPail(18, -38) + fkCircle(18, -37.5, 4.2, lk.skin));
  k.push(fkG(v.join(''), B ? 'translate(0,-1)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// The Filing floor's gray cat in a pumpkin suit (58×46, feet at 26,43), stem cap between its ears
function hwCatFrame(which) {
  var B = which === 'B', c = ST.cat, k = [fkEllipse(0, 1.2, 15, 2.2, FK.shadow)];
  k.push('<path d="' + (B ? 'M -14,-12 Q -23,-14 -21,-26' : 'M -14,-12 Q -22,-16 -18,-28') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>');
  k.push(fkRect(-10, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? 22 : -22) + ' -7.75 -8)"'));
  k.push(fkRect(4, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? -22 : 22) + ' 6.25 -8)"'));
  k.push('<ellipse cx="-1" cy="-16" rx="15" ry="11.5" fill="' + HW.pumpkin + '"/>' +
    '<ellipse cx="-1" cy="-16" rx="8.5" ry="11.5" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="1.2"/>' +
    '<ellipse cx="-1" cy="-16" rx="2.6" ry="11.5" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="1.2"/>');
  k.push(fkCircle(12, -26, 7, c) + stCatEars(0, -4) + fkCircle(14.5, -26.5, 1.1, FK.eye));
  k.push(fkRect(11, -38.5, 2.4, 6, 1, HW.stem) + '<path d="M 13.2,-36.5 Q 18,-40 19.5,-35.5 Q 15.5,-34.5 13.2,-35.2 Z" fill="' + HW.leaf + '"/>');
  return fkG(k.join(''), 'translate(26,43)');
}
// Bedsheet ghost (50×54, ground at 25,52), floating; 'side' drifts right, 'front' faces the screen
function hwGhostFrame(view, which) {
  var B = which === 'B', k = [fkEllipse(0, 1, 13, 2.2, 'rgba(0,0,0,0.18)')];
  var d = view === 'front'
    ? 'M 15,-10 C 18,-30 12,-47 0,-47 C -12,-47 -18,-30 -15,-10 Q -11,' + (B ? -4 : -6) + ' -7.5,-9 Q -4,' + (B ? -6 : -4) + ' 0,-9 Q 4,' + (B ? -4 : -6) + ' 7.5,-9 Q 11,' + (B ? -6 : -4) + ' 15,-10 Z'
    : 'M 14,-10 C 17,-28 13,-46 -1,-47 C -14,-47 -17,-32 -16,-18 Q -18,-10 -22,-6 Q -17,' + (B ? -2 : -4) + ' -12,-8 Q -8,' + (B ? -4 : -2) + ' -4,-8 Q 0,' + (B ? -2 : -4) + ' 4,-8 Q 8,' + (B ? -4 : -2) + ' 10,-8 Q 12,-6 14,-10 Z';
  k.push('<path d="' + d + '" fill="' + HW.ghost + '" stroke="' + HW.ghostLn + '" stroke-width="1.1"/>');
  if (view === 'front') k.push('<ellipse cx="-5" cy="-33" rx="2.4" ry="3.4" fill="' + HW.ink + '"/><ellipse cx="5" cy="-33" rx="2.4" ry="3.4" fill="' + HW.ink + '"/><ellipse cx="0" cy="-24" rx="2.6" ry="3.4" fill="' + HW.ink + '"/>');
  else k.push('<ellipse cx="6" cy="-34" rx="2" ry="3" fill="' + HW.ink + '"/><ellipse cx="11" cy="-34.5" rx="1.5" ry="2.8" fill="' + HW.ink + '"/>');
  return fkG(k.join(''), 'translate(25,52)');
}
// Bat (34×18, front view): wings up on frame A, down on frame B
function hwBatFrame(which) {
  var wings = which === 'B'
    ? 'M 14,8 Q 8,10 3,16 Q 6,12 9,15 Q 10,11 14,11 Z M 20,8 Q 26,10 31,16 Q 28,12 25,15 Q 24,11 20,11 Z'
    : 'M 14,8 L 4,1 Q 6,6 1,6 Q 5,9 3,12 Q 9,9 14,10 Z M 20,8 L 30,1 Q 28,6 33,6 Q 29,9 31,12 Q 25,9 20,10 Z';
  return '<path d="' + wings + '" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    '<path d="M 14.2,6.5 L 14.6,3.2 L 16.2,5.8 Z M 19.8,6.5 L 19.4,3.2 L 17.8,5.8 Z" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    '<ellipse cx="17" cy="9" rx="4" ry="3.4" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    fkCircle(15.7, 8.3, 0.75, HW.batEye) + fkCircle(18.3, 8.3, 0.75, HW.batEye);
}
function hwHtml(frameFn, vw, vh, speed) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(frameFn(which), vw, vh, stPx(vw), stPx(vh), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), speed);
}
function hwEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('halloween-style')) return;
  var st = document.createElement('style');
  st.id = 'halloween-style';
  st.textContent =
    '@keyframes hwBob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }' +
    '.hwbob { position: absolute; inset: 0; animation: hwBob 1.6s ease-in-out infinite; }' +
    '@keyframes hwFlap { 0%, 100% { transform: translateY(0); } 30% { transform: translateY(-16px); } 65% { transform: translateY(6px); } }' +
    '.hwflap { position: absolute; inset: 0; animation: hwFlap 1.3s ease-in-out infinite; }';
  document.head.appendChild(st);
}
// Halloween week = Oct 24-31 by the viewer's own clock, every year
function hwIsHalloweenWeek(d) {
  d = d || new Date();
  return d.getMonth() === 9 && d.getDate() >= 24;
}
function paradeRun() {
  try {
    hwEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the parade walks
    var flip = lr ? '' : ' scaleX(-1)';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var end = 900 + 4200; // the last bat is gone by then

    // Bats first: [start ms, bottom px, crossing ms, flap period s]
    var BW = stPx(34), BH = stPx(18);
    [[0, 160, 3400, 1.2], [250, 205, 3900, 1.5], [600, 125, 3600, 1.1], [900, 235, 4200, 1.4]].forEach(function(b, i) {
      at(b[0], function() {
        var html = '<div class="hwflap" style="animation-duration:' + b[3] + 's;animation-delay:-' + (i * 0.4).toFixed(1) + 's">' + hwHtml(hwBatFrame, 34, 18, ' fast') + '</div>';
        var el = keep(stSpawn(html, BW, BH, b[1], X(-BW - 20, BW), 0, ''));
        stMove(el, X(vw + 20, BW), 0, b[2], 'linear');
      });
    });

    // The costumed agents walk across at one pace, about 1.3 s apart
    var PW = stPx(90), PH = stPx(112), CW = stPx(58), CH = stPx(46);
    var v = (vw + PW + 40) / 9000; // px per ms
    var walkers = [[700, hwEmmaFrame, PW, PH, 90, 112, ' wv'], [2000, hwMummyFrame, PW, PH, 90, 112, ' wv'],
      [3300, hwSageFrame, PW, PH, 90, 112, ' wv'], [4500, hwCatFrame, CW, CH, 58, 46, '']];
    walkers.forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(hwHtml(a[1], a[4], a[5], a[6]), a[2], a[3], 4, X(-a[2] - 20, a[2]), 0, flip));
        var ms = Math.round((vw + a[2] + 40) / v);
        stMove(el, X(vw + 20, a[2]), 0, ms, 'linear', flip);
      });
      end = Math.max(end, a[0] + Math.round((vw + a[2] + 40) / v));
    });

    // The ghost drifts in last, stops at center, turns to the screen, "boo!", fades away
    var GW = stPx(50), GH = stPx(54);
    // (same pace as the walkers so it keeps its distance, easing only over the last stretch)
    var sG = Math.round(vw / 2 - GW / 2), sEase = sG - stPx(40), tG = 5800, dG = Math.round((sEase + GW + 20) / v), tStop = tG + dG + 600;
    var ghost = null, ghostFrames = function(view) { return '<div class="hwbob">' + hwHtml(function(w) { return hwGhostFrame(view, w); }, 50, 54, ' wv') + '</div>'; };
    at(tG, function() {
      ghost = keep(stSpawn(ghostFrames('side'), GW, GH, 4, X(-GW - 20, GW), 0, flip));
      stMove(ghost, X(sEase, GW), 0, dG, 'linear', flip);
    });
    at(tG + dG, function() { stMove(ghost, X(sG, GW), 0, 600, 'ease-out', flip); });
    at(tStop + 250, function() { ghost.innerHTML = ghostFrames('front'); });
    at(tStop + 450, function() {
      var b = keep(stBubble('boo!', X(sG, GW) + Math.round(GW / 2) - 16, 4 + GH + 8));
      at(1300, function() { drop(b); });
    });
    at(tStop + 1750, function() { ghost.style.transition = 'opacity 700ms ease-in'; ghost.style.opacity = '0'; });
    end = Math.max(end, tStop + 2450);
    at(end + 600, function() { els.forEach(drop); });
  } catch (e) {}
}
window.paradeRun = paradeRun;

// ─── HOURLY SEASON CALENDAR (Anthony 2026-11-01) ───
// Which cartoon the hourly clock plays, by the viewer's own date, every
// year: the Trick-or-Treat Parade in Halloween week (Oct 24-31); the Leaf
// Pile from Nov 1 up to the week before Thanksgiving; the Thanksgiving
// Potluck over the seven days ending on Thanksgiving (the fourth Thursday of
// November); the Black Friday Stampede from Black Friday through Cyber
// Monday; the Holiday Sleigh Dec 18-31; Danny's Invoice Storm the rest of
// the year. A season whose scene isn't drawn yet plays the Storm.
function thanksgivingDay(year) { return 22 + ((11 - new Date(year, 10, 1).getDay()) % 7); } // the 4th Thursday of November
function hourlySceneName(d) {
  d = d || new Date();
  var m = d.getMonth(), day = d.getDate();
  if (hwIsHalloweenWeek(d)) return 'parade';
  if (m === 10) {
    var tg = thanksgivingDay(d.getFullYear());
    if (day < tg - 6) return 'leaf';
    if (day <= tg) return 'potluck';
    if (day <= tg + 4) return 'stampede';
    return 'storm';
  }
  if (m === 11 && day >= 18) return 'sleigh';
  return 'storm';
}
function hourlyScene(d) {
  var scenes = { parade: paradeRun, leaf: leafRun, potluck: potluckRun, stampede: stampedeRun, sleigh: sleighRun, storm: stormRun };
  return scenes[hourlySceneName(d)] || stormRun;
}
window.hourlySceneName = hourlySceneName;

// ─── LEAF PILE (the hourly cartoon from Nov 1 to the week before Thanksgiving) ───
// A leaf-blower gust tumbles maple leaves along the bottom of the page and
// they settle on the floor. Danny (the Office's invoice-filing agent, in his
// desk look) walks in behind a rake and sweeps them into a pile that grows
// ahead of him, parks it, steps back and leans on the rake ("phew!"). The
// Filing floor's gray cat creeps in from the other side, crouches, and leaps
// into the pile: leaves burst everywhere and the cat sits in the wreck with a
// leaf on its head. "!" — Danny drops the rake, throws up his hands and
// stomps off the way he came; the cat trots off with a leaf in its mouth.
// Anthony 2026-11-01 (picked over Fall Back Monday / Diwali Lights / Runaway
// Parcel). Same rig and helpers as the Invoice Storm. window.leafRun() = demo.
var LF = {
  leaves: ['#DC2626', '#EA580C', '#F59E0B', '#D97706', '#B45309', '#C2410C'],
  edge: '#7C2D12', vein: 'rgba(255,255,255,0.35)', handle: '#8B5A2B', tine: '#9CA3AF', mound: '#B45309',
};
var LF_LEAN = { rake: 10, lean: 0, drop: -6, walk: 4 }; // how far Danny's upper body tips forward in each pose
// One maple leaf, 20×22, stem down
function lfLeaf(fill) {
  return '<path d="M 10,0.5 L 12.3,5.6 L 18.5,4 L 16.6,9.2 L 19.5,13.6 L 14.6,13.4 L 13,17.6 L 10,15.8 L 7,17.6 L 5.4,13.4 L 0.5,13.6 L 3.4,9.2 L 1.5,4 L 7.7,5.6 Z" fill="' + fill + '" stroke="' + LF.edge + '" stroke-width="0.5" stroke-linejoin="round"/>' +
    '<path d="M 10,15.8 L 10,1.5 M 10,11.5 L 17.5,4.8 M 10,11.5 L 2.5,4.8 M 10,13 L 18.6,13.2 M 10,13 L 1.4,13.2" stroke="' + LF.vein + '" stroke-width="0.6" fill="none"/>' +
    '<path d="M 10,15.8 L 10.4,22" stroke="' + LF.edge + '" stroke-width="1" stroke-linecap="round"/>';
}
function lfLeafSvg(i) { return forkSvgWrap(lfLeaf(LF.leaves[i % LF.leaves.length]), 20, 22, stPx(24), stPx(26), ''); }
function lfRx(k) { return 7 + 3 * k; } // half-width of a pile of k leaves
// k leaves heaped at (cx, base): a mound with leaves tumbled over it
function lfMound(k, cx, base) {
  if (k <= 0) return '';
  var rx = lfRx(k), ry = 4 + 1.7 * k, s = [];
  s.push('<path d="M ' + (cx - rx) + ',' + base + ' Q ' + cx + ',' + (base - 2 * ry) + ' ' + (cx + rx) + ',' + base + ' Z" fill="' + LF.mound + '"/>');
  for (var i = 0; i < k; i++) {
    var fx = (((i * 37) % 11) - 5) / 5.5, top = ry * (1 - fx * fx); // the mound's height under this leaf
    var y = base - 1 - top * (0.25 + 0.65 * ((i * 53) % 7) / 7);
    s.push(fkG(lfLeaf(LF.leaves[i % LF.leaves.length]), 'translate(' + (cx + fx * rx * 0.85).toFixed(1) + ',' + y.toFixed(1) + ') rotate(' + ((i * 97) % 360) + ') scale(0.9) translate(-10,-11)'));
  }
  return s.join('');
}
function lfPileSvg(k) { return forkSvgWrap(lfMound(k, 55, 45), 110, 48, stPx(110), stPx(48), ''); }
// The pile after the cat lands in it: leaves lying flat all around
function lfScatterSvg() {
  var s = [];
  for (var i = 0; i < 7; i++) {
    var x = 55 + (((i * 43) % 13) - 6) * 6.2;
    s.push(fkG(lfLeaf(LF.leaves[(i + 1) % LF.leaves.length]), 'translate(' + x.toFixed(1) + ',44.5) scale(1,0.45) rotate(' + ((i * 71) % 360) + ') scale(0.9) translate(-10,-11)'));
  }
  return forkSvgWrap(s.join(''), 110, 48, stPx(110), stPx(48), '');
}
// A rake: handle from (hx, hy) down to its head on the floor at (ex, 0), tines fanned out
function lfRake(hx, hy, ex) {
  var t = [];
  for (var i = 0; i < 7; i++) t.push('M ' + (ex - 1.5) + ',-6 Q ' + (ex - 9.5 + 3.2 * i) + ',-3.5 ' + (ex - 10.5 + 3.5 * i) + ',0.4');
  return '<line x1="' + hx + '" y1="' + hy + '" x2="' + (ex - 1.5) + '" y2="-6" stroke="' + LF.handle + '" stroke-width="2.4" stroke-linecap="round"/>' +
    '<path d="' + t.join(' ') + '" stroke="' + LF.tine + '" stroke-width="1.1" fill="none" stroke-linecap="round"/>' +
    '<line x1="' + (ex - 10) + '" y1="-3.2" x2="' + (ex + 10) + '" y2="-3.2" stroke="' + LF.tine + '" stroke-width="1.4" stroke-linecap="round"/>';
}
function lfRakeSvg() { return forkSvgWrap(fkG(lfRake(14, -62, 14), 'translate(0,69)'), 70, 70, stPx(70), stPx(70), ''); }
// An arm from the shoulder (sx, sy) to the hand (hx, hy): a rounded bar and the hand
function lfArm(sx, sy, hx, hy, fill, skin) {
  var dx = hx - sx, dy = hy - sy, L = Math.sqrt(dx * dx + dy * dy), a = Math.atan2(dy, dx) * 180 / Math.PI;
  return fkRect(sx - 1, sy - 3.75, L + 1, 7.5, 3.75, fill, ' transform="rotate(' + a.toFixed(1) + ' ' + sx + ' ' + sy + ')"') + fkCircle(hx, hy, 3.6, skin || ST.skin);
}
// Where the shoulder (-1,-50) lands once the upper body tips `lean` degrees about the hips (0,-28)
function lfShoulder(lean) {
  var t = lean * Math.PI / 180;
  return [-Math.cos(t) + 22 * Math.sin(t), -28 - Math.sin(t) - 22 * Math.cos(t)];
}
// Danny (160×100, feet at 30,97) in the Storm's desk look: 'rake' walks behind the rake with k
// leaves piled ahead of it, 'lean' rests on the upright rake wiping his brow, 'drop' throws both
// hands up, 'walk' stomps off empty-handed
function lfDannyFrame(pose, k, which) {
  var B = which === 'B', lean = LF_LEAN[pose] || 0, S = lfShoulder(lean);
  var swing = pose === 'rake' ? 20 : pose === 'walk' ? 22 : 4;
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow)];
  if (pose === 'drop') p.push(fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(-12 0 -26)"') + fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(12 0 -26)"'));
  else p.push(fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? swing : -swing) + ' 0 -26)"') + fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? -swing : swing) + ' 0 -26)"'));
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, ST.shirt) + stDannyHead(0, -68), 'rotate(' + lean + ' 0 -28)');
  var H1, H2, held = ''; // near hand, far hand, whatever is held between the arms
  if (pose === 'rake') { H1 = [20, -44]; H2 = [28, -33]; held = lfRake(20, -44, B ? 53 : 52); }
  else if (pose === 'lean') { H1 = [12, B ? -79 : -78]; H2 = [16, -58]; held = lfRake(16, -64, 16); }
  else if (pose === 'drop') { H1 = [-16, B ? -79 : -80]; H2 = [14, B ? -81 : -82]; }
  else { H1 = B ? [-7, -32] : [9, -30]; H2 = B ? [9, -30] : [-7, -32]; }
  var u = lfArm(S[0], S[1], H2[0], H2[1], ST.shirtDk) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], ST.shirt);
  p.push(fkG(u, 'translate(0,' + (B && pose !== 'drop' ? -1.5 : 0) + ')'));
  if (pose === 'rake') p.push(lfMound(k, 54 + lfRx(k), 0));
  return fkG(p.join(''), 'translate(30,97)');
}
function lfDannyHtml(pose, k) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(lfDannyFrame(pose, k, which), 160, 100, stPx(160), stPx(100), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'lean' || pose === 'drop' ? ' wv' : '');
}
// The gray cat: the Storm's walk / crouch / leap, plus 'sit' (facing the screen, a leaf on its
// head, 58×60) and 'carry' (a leaf in its mouth)
function lfCatFrame(pose, which) {
  var B = which === 'B', c = ST.cat;
  if (pose === 'sit') {
    var p = [fkEllipse(0, 1.2, 14, 2.2, FK.shadow),
      '<path d="' + (B ? 'M 6,-4 Q 18,-4 20,-14' : 'M 6,-4 Q 19,-6 18,-16') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>',
      '<ellipse cx="-2" cy="-13" rx="11" ry="12.5" fill="' + c + '"/>',
      fkRect(-9, -12, 4.5, 12, 2.2, c) + fkRect(1, -12, 4.5, 12, 2.2, c),
      fkCircle(-1, -29, 7.5, c) + stCatEars(-13, -7) + fkCircle(-3.6, -30, 1.2, FK.eye) + fkCircle(1.6, -30, 1.2, FK.eye),
      fkG(lfLeaf(LF.leaves[0]), 'translate(-4,-37) rotate(' + (B ? -24 : -18) + ') scale(0.7) translate(-10,-22)')];
    return fkG(p.join(''), 'translate(26,57)');
  }
  var base = stCatFrame(pose === 'carry' ? 'walk' : pose, which);
  if (pose === 'carry') base += fkG(lfLeaf(LF.leaves[2]), 'translate(26,41) translate(17,-21) rotate(62) scale(0.6) translate(-10,-11)');
  return base;
}
function lfCatHtml(pose) {
  var vh = pose === 'sit' ? 60 : 44;
  var mk = function(which) { return function(cls) { return forkSvgWrap(lfCatFrame(pose, which), 58, vh, stPx(58), stPx(vh), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' || pose === 'sit' ? ' wv' : ' fast');
}
// A leaf that has landed: stop tumbling and lie flat
function lfFlat(el, i) {
  var f = el && el.firstChild;
  if (!f) return;
  f.style.animation = 'none';
  f.className = 'lfflat';
  f.style.transform = 'rotate(' + ((i * 53) % 50 - 25) + 'deg) scaleY(0.55)';
}
function lfEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('leaf-style')) return;
  var st = document.createElement('style');
  st.id = 'leaf-style';
  st.textContent =
    '.lfflat { position: absolute; inset: 0; transform-origin: 50% 100%; }' +
    '.lfrake { position: absolute; inset: 0; transform-origin: 20% 100%; transition: transform 380ms cubic-bezier(0.55,0,1,0.6); }';
  document.head.appendChild(st);
}
function leafRun() {
  try {
    lfEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the leaves blow
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var DW = stPx(160), DH = stPx(100), CW = stPx(58), CH = stPx(44), LW = stPx(24), LH = stPx(26), PW = stPx(110), PH = stPx(48), GW = stPx(150), GH = stPx(36), RW = stPx(70), RH = stPx(70);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var floor = 6; // landed leaves lie on the figures' floor line

    // The gust: three wind streaks low along the floor
    [[0, 40], [180, 96], [380, 18]].forEach(function(g) {
      at(g[0], function() {
        var el = keep(stSpawn(stGustSvg(), GW, GH, g[1], X(-GW, GW), 0, flip));
        stMove(el, X(vw + 20, GW), 0, 1400, 'linear', flip);
      });
    });

    // Nine leaves tumble in and settle on the floor between 12% and 64% of the width
    var leaves = [0.12, 0.19, 0.27, 0.33, 0.40, 0.46, 0.52, 0.58, 0.64].map(function(f, i) {
      var t0 = 150 + 120 * i, tIn = 1500 + (i % 3) * 150;
      return { i: i, spot: Math.round(vw * f), t0: t0, tS: t0 + tIn, tIn: tIn, el: null };
    });
    leaves.forEach(function(p) {
      at(p.t0, function() {
        var fl = '<div class="stfl" style="animation-duration:' + (1.05 + (p.i % 3) * 0.2).toFixed(2) + 's;animation-delay:-' + (p.i * 0.31).toFixed(2) + 's">' + lfLeafSvg(p.i) + '</div>';
        p.el = keep(stSpawn(fl, LW, LH, floor, X(-LW - 10, LW), -50 - (p.i % 4) * 22, ''));
        stMove(p.el, X(p.spot, LW), 0, p.tIn, 'cubic-bezier(0.2,0.7,0.3,1)');
      });
      at(p.tS, function() { lfFlat(p.el, p.i); });
    });

    // Danny walks in behind the rake once they're down. A leaf is swept the moment the rake head
    // (rOff px from his back edge) reaches its center; the pile ahead of the rake grows by one.
    var tD = 3000, sD0 = -DW - 20, v = (vw + DW + 20) / 6500; // px per ms: a working walk, not the Storm's sprint
    var rOff = stPx(82), K = leaves.length;
    var sStop = Math.round(vw * 0.74) - rOff, tStop = tD + Math.round((sStop - sD0) / v);
    var sPileC = sStop + stPx(84 + lfRx(K)), sLean = sStop - stPx(26); // the pile's center once parked; where he steps back to
    var headX = function(s) { return lr ? X(s, DW) + stPx(30) : X(s, DW) + DW - stPx(30); };
    var danny = null, k = 0;
    at(tD, function() {
      danny = keep(stSpawn(lfDannyHtml('rake', 0), DW, DH, 4, X(sD0, DW), 0, flip));
      stMove(danny, X(sStop, DW), 0, tStop - tD, 'linear', flip);
    });
    leaves.forEach(function(p) {
      at(tD + Math.round((p.spot + LW / 2 - rOff - sD0) / v), function() {
        drop(p.el);
        k++;
        if (danny) danny.innerHTML = lfDannyHtml('rake', k);
      });
    });
    // He parks the pile, steps back and leans on the rake
    var pile = null;
    at(tStop, function() {
      pile = keep(stSpawn(lfPileSvg(k), PW, PH, 4, X(sPileC - PW / 2, PW), 0, ''));
      danny.innerHTML = lfDannyHtml('lean', 0);
      stMove(danny, X(sLean, DW), 0, 600, 'ease-out', flip);
    });
    at(tStop + 800, function() {
      var b = keep(stBubble('phew!', headX(sLean) - 12, 4 + stPx(96)));
      at(1500, function() { drop(b); });
    });

    // The cat creeps in from the far side, crouches beside the pile, and leaps into it
    var sC0 = vw + 20, sCrouch = sPileC + stPx(lfRx(K) + 12), sLand = sPileC - CW / 2;
    var tCat = tStop + 900, dCatIn = Math.max(1000, Math.min(4500, Math.round((sC0 - sCrouch) / 0.26)));
    var tCrouch = tCat + dCatIn, tPounce = tCrouch + 1100, tLand = tPounce + 520;
    var cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(lfCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, back));
      stMove(cat, X(sCrouch, CW), 0, dCatIn, 'linear', back);
    });
    at(tCrouch, function() { cat.innerHTML = lfCatHtml('crouch'); });
    at(tPounce, function() {
      cat.innerHTML = lfCatHtml('leap');
      stMove(cat, X((sCrouch + sLand) / 2, CW), -stPx(34), 270, 'ease-out', back);
    });
    at(tPounce + 270, function() { stMove(cat, X(sLand, CW), 0, 250, 'ease-in', back); });
    // Splash: the pile flattens, eight leaves fly out and flutter down, the cat sits up in the wreck
    at(tLand, function() {
      pile.innerHTML = lfScatterSvg();
      cat.style.height = stPx(60) + 'px';
      cat.innerHTML = lfCatHtml('sit');
      for (var i = 0; i < 8; i++) (function(i) {
        var dx = (i % 2 ? 1 : -1) * stPx(36 + ((i * 41) % 5) * 26), up = stPx(34 + ((i * 29) % 4) * 18);
        var fl = '<div class="stfl" style="animation-duration:' + (0.9 + (i % 3) * 0.15).toFixed(2) + 's;animation-delay:-' + (i * 0.23).toFixed(2) + 's">' + lfLeafSvg(i + 3) + '</div>';
        var el = keep(stSpawn(fl, LW, LH, floor, X(sPileC - LW / 2, LW), -stPx(8), ''));
        stMove(el, X(sPileC - LW / 2 + dx, LW), -up, 420, 'cubic-bezier(0.2,0.6,0.4,1)');
        at(420, function() { stMove(el, X(sPileC - LW / 2 + dx * 1.3, LW), 0, 800 + i * 70, 'cubic-bezier(0.5,0,0.8,0.6)'); });
        at(1220 + i * 70, function() { lfFlat(el, i + 5); });
      })(i);
    });
    // "!" — Danny drops the rake (it stands for a beat, then keels over) and throws up his hands
    var exc = null;
    at(tLand + 120, function() {
      exc = keep(document.createElement('div'));
      exc.textContent = '!';
      exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (headX(sLean) - 6) + 'px;bottom:' + (4 + stPx(98)) + 'px;font:800 ' + stPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
      document.body.appendChild(exc);
    });
    at(tLand + 1000, function() { drop(exc); });
    at(tLand + 450, function() {
      danny.innerHTML = lfDannyHtml('drop', 0);
      var rake = keep(stSpawn('<div class="lfrake">' + lfRakeSvg() + '</div>', RW, RH, 4, X(sLean + stPx(32), RW), 0, flip));
      at(80, function() { rake.firstChild.style.transform = 'rotate(80deg)'; });
    });
    // He turns and stomps off the way he came (the element shifts so his feet stay put when it
    // mirrors); the cat picks a leaf and trots off its own way
    var tWalk = tLand + 1600, sTurn = sLean - stPx(100), dWalk = Math.round((sTurn - sD0) / (v * 0.85));
    at(tWalk, function() {
      danny.innerHTML = lfDannyHtml('walk', 0);
      danny.style.transition = 'none';
      danny.style.transform = 'translate(' + X(sTurn, DW) + 'px,0px)' + back;
      danny.getBoundingClientRect();
      stMove(danny, X(sD0, DW), 0, dWalk, 'linear', back);
    });
    var tCatOut = tLand + 2000, dCatOut = Math.round((sC0 - sLand) / 0.3);
    at(tCatOut, function() {
      cat.style.height = CH + 'px';
      cat.innerHTML = lfCatHtml('carry');
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sLand, CW) + 'px,0px)' + flip;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', flip);
    });
    var tEnd = Math.max(tWalk + dWalk, tCatOut + dCatOut) + 300;
    at(tEnd - 700, function() { els.forEach(function(el) { if (el === danny || el === cat) return; el.style.transition = 'opacity 600ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.leafRun = leafRun;

// ─── THANKSGIVING POTLUCK (the hourly cartoon over the seven days ending on Thanksgiving) ───
// Emma, Sage and Danny stroll across the bottom of the page toward the break room, each
// carrying their dish: Emma a pumpkin pie, Sage a casserole, Danny the big silver platter
// with a dome over the turkey. Danny stops mid-screen when the dome starts to jiggle; it
// pops off and lands upside-down behind him, and the roast turkey — drumsticks for legs —
// hops off the platter ("gobble!") and sprints back the way they came. "!" — Danny drops
// the platter (the garnish drumstick bounces off it), turns and chases the bird; Emma and
// Sage walk on, none the wiser. The Filing floor's gray cat creeps in, sniffs the
// drumstick on the floor and trots off with it. Anthony 2026-11-01. Same rig and helpers
// as the Storm, the parade and the Leaf Pile. window.potluckRun() = demo.
var TG = {
  turkey: '#B45309', turkeyHi: '#D97706', bone: '#F5F5F4', silver: '#CBD5E1', silverDk: '#94A3B8', silverHi: '#E5E7EB', dome: '#D1D5DB',
  crust: '#D9A066', pumpkin: '#C2410C', cream: '#FFFBEB', dish: '#F8FAFC', dishLn: '#CBD5E1', lid: '#E5E7EB',
};
var TG_WHO = {
  emma: { skin: HW.emma.skin, shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A', head: function() { return hwHead(HW.emma, 'ponytail'); } },
  sage: { skin: HW.sage.skin, shirt: '#84CC16', shirtDk: '#65A30D', pantsA: HW.pants, pantsB: HW.pantsB, head: function() { return hwHead(HW.sage, 'bob'); } },
  danny: { skin: ST.skin, shirt: ST.shirt, shirtDk: ST.shirtDk, pantsA: FK.pantsA, pantsB: FK.pantsB, head: function() { return stDannyHead(0, -68); } },
};
// A cloche: half-disc of radius 14 standing on (cx, by), knob on top
function tgDome(cx, by) {
  return '<path d="M ' + (cx - 14) + ',' + by + ' A 14,14 0 0 1 ' + (cx + 14) + ',' + by + ' Z" fill="' + TG.dome + '" stroke="' + TG.silverDk + '" stroke-width="0.9"/>' +
    '<path d="M ' + (cx - 9) + ',' + (by - 6) + ' A 11,11 0 0 1 ' + (cx - 2) + ',' + (by - 12.5) + '" stroke="' + TG.silverHi + '" stroke-width="1.6" fill="none" stroke-linecap="round"/>' +
    fkRect(cx - 1, by - 17, 2, 3.5, 0.8, TG.silverDk) + fkCircle(cx, by - 17.5, 2.4, TG.silverDk);
}
function tgDomeSvg() { return forkSvgWrap(tgDome(16, 21), 32, 22, stPx(32), stPx(22), ''); }
function tgPlatter(cx, cy) {
  return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="17" ry="3.8" fill="' + TG.silver + '" stroke="' + TG.silverDk + '" stroke-width="0.8"/><ellipse cx="' + cx + '" cy="' + (cy - 0.4) + '" rx="13" ry="2.2" fill="' + TG.silverHi + '"/>';
}
function tgPlatterSvg() { return forkSvgWrap(tgPlatter(20, 5), 40, 10, stPx(40), stPx(10), ''); }
// A drumstick (16×12): meaty end at the left, bone and knuckles to the right
function tgDrumstick() {
  return '<ellipse cx="6" cy="6.5" rx="5.2" ry="3.8" fill="' + TG.turkey + '" transform="rotate(-20 6 6.5)"/><line x1="9.5" y1="5" x2="13.5" y2="2.8" stroke="' + TG.bone + '" stroke-width="1.9" stroke-linecap="round"/>' +
    fkCircle(14, 2.3, 1.6, TG.bone) + fkCircle(13.2, 4.4, 1.3, TG.bone);
}
function tgDrumSvg() { return forkSvgWrap(tgDrumstick(), 16, 12, stPx(16), stPx(12), ''); }
// What each of them carries, held out front at chest height (figure coords, feet at 0,0)
function tgDish(who, withDome) {
  if (who === 'emma') return '<ellipse cx="26" cy="-44" rx="13" ry="4.2" fill="' + TG.crust + '"/><ellipse cx="26" cy="-45" rx="10.8" ry="2.8" fill="' + TG.pumpkin + '"/>' + fkCircle(26, -47.2, 2.2, TG.cream);
  if (who === 'sage') return '<ellipse cx="11.5" cy="-45" rx="2.4" ry="1.7" fill="' + TG.dish + '"/><ellipse cx="40.5" cy="-45" rx="2.4" ry="1.7" fill="' + TG.dish + '"/>' +
    fkRect(13, -49, 26, 8, 2.5, TG.dish, ' stroke="' + TG.dishLn + '" stroke-width="0.8"') + fkRect(14.5, -52, 23, 3.6, 1.6, TG.lid) + fkCircle(26, -53.5, 1.7, TG.silverDk);
  return tgPlatter(26, -44) + (withDome ? tgDome(26, -45) : '');
}
// A walker (90×112, feet at 40,106): 'carry' strolls with the dish, 'stand' holds it still,
// 'drop' throws both hands up, 'chase' sprints with both arms reaching ahead
function tgFigure(who, pose, which, withDome) {
  var B = which === 'B', lk = TG_WHO[who], k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  if (pose === 'drop') k.push(fkRect(-9.5, -28, 8, 28, 4, lk.pantsA, ' transform="rotate(-8 -5.5 -26)"') + fkRect(1.5, -28, 8, 28, 4, lk.pantsB, ' transform="rotate(8 5.5 -26)"'));
  else k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'carry' ? 16 : pose === 'chase' ? 30 : 3));
  var lean = pose === 'chase' ? 12 : pose === 'drop' ? -6 : 0, S = lfShoulder(lean);
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, lk.shirt) + lk.head(), 'rotate(' + lean + ' 0 -28)');
  var H1, H2, held = '';
  if (pose === 'carry' || pose === 'stand') { H1 = [21, -42]; H2 = [19, -46]; held = tgDish(who, withDome); }
  else if (pose === 'drop') { H1 = [-16, B ? -79 : -80]; H2 = [14, B ? -81 : -82]; }
  else { H1 = [25, B ? -52 : -50]; H2 = [27, B ? -56 : -58]; }
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  k.push(fkG(u, 'translate(0,' + (B && (pose === 'carry' || pose === 'chase') ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function tgHtml(who, pose, withDome) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(tgFigure(who, pose, which, withDome), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'chase' ? ' fast' : ' wv');
}
// The roast turkey (40×28, floor at 27): 'sit' on the platter with its drumsticks tucked up
// behind, 'run' with the drumsticks scissoring underneath
function tgTurkeyFrame(pose, which) {
  var B = which === 'B', k = [];
  var leg = function(x, y, ang) {
    return fkG('<ellipse cx="0" cy="0" rx="5" ry="3.6" fill="' + TG.turkey + '"/><line x1="3" y1="1" x2="9" y2="7" stroke="' + TG.bone + '" stroke-width="2" stroke-linecap="round"/>' + fkCircle(9.5, 7.5, 1.7, TG.bone) + fkCircle(8, 9.2, 1.4, TG.bone),
      'translate(' + x + ',' + y + ') rotate(' + ang + ')');
  };
  if (pose === 'sit') {
    k.push(leg(9, 13, -150) + leg(10, 17, -168));
    k.push('<ellipse cx="20" cy="18" rx="13" ry="8.5" fill="' + TG.turkey + '"/><ellipse cx="16" cy="14" rx="6.5" ry="3" fill="' + TG.turkeyHi + '"/>');
  } else {
    k.push(fkEllipse(20, 27, 12, 1.8, FK.shadow));
    k.push(leg(16, 19, B ? 40 : 95) + leg(23, 19, B ? 95 : 40));
    k.push(fkG('<ellipse cx="20" cy="14" rx="13" ry="8" fill="' + TG.turkey + '"/><ellipse cx="16" cy="10" rx="6.5" ry="3" fill="' + TG.turkeyHi + '"/>', 'rotate(' + (B ? 5 : 9) + ' 20 14)'));
  }
  return k.join('');
}
function tgTurkeyHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(tgTurkeyFrame(pose, which), 40, 28, stPx(34), stPx(24), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'sit' ? ' wv' : ' fast');
}
function tgCatHtml(pose) {
  var mk = function(which) { return function(cls) {
    var g = stCatFrame(pose === 'carry' ? 'walk' : pose, which);
    if (pose === 'carry') g += fkG(tgDrumstick(), 'translate(26,41) translate(13,-25) rotate(25) scale(0.9)');
    return forkSvgWrap(g, 58, 44, stPx(58), stPx(44), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' ? ' wv' : ' fast');
}
function tgEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('potluck-style')) return;
  var st = document.createElement('style');
  st.id = 'potluck-style';
  st.textContent =
    '@keyframes tgBob { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-5px) rotate(-9deg); } }' +
    '.tgbob { position: absolute; inset: 0; animation: tgBob 0.45s ease-in-out infinite; }' +
    '.tgspin { position: absolute; inset: 0; transform: rotate(170deg); transition: transform 800ms ease-out; }' +
    '.tgtilt { position: absolute; inset: 0; transition: transform 380ms ease-in; }';
  document.head.appendChild(st);
}
function potluckRun() {
  try {
    tgEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the break room is
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), CW = stPx(58), CH = stPx(44), TW = stPx(34), TH = stPx(24), DMW = stPx(32), DMH = stPx(22), PLW = stPx(40), PLH = stPx(10), DRW = stPx(16), DRH = stPx(12);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var floorY = function(bottom) { return bottom - (4 + stPx(6)); }; // translateY that puts an element's bottom edge on the figures' floor line
    var v = (vw + W + 40) / 9000; // the Office stroll, as in the parade
    var tCross = Math.round((vw + W + 40) / v);

    // Emma and Sage stroll straight across with their dishes
    [[0, 'emma'], [1300, 'sage']].forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(tgHtml(a[1], 'carry'), W, H, 4, X(-W - 20, W), 0, flip));
        stMove(el, X(vw + 20, W), 0, tCross, 'linear', flip);
      });
    });

    // Danny brings up the rear with the turkey under its dome and stops mid-screen
    var tD = 2600, sD0 = -W - 20, sStop = Math.round(vw / 2 - W / 2), tStop = tD + Math.round((sStop - sD0) / v);
    var headX = function(s) { return lr ? X(s, W) + stPx(40) : X(s, W) + W - stPx(40); };
    var danny = null;
    at(tD, function() {
      danny = keep(stSpawn(tgHtml('danny', 'carry', true), W, H, 4, X(sD0, W), 0, flip));
      stMove(danny, X(sStop, W), 0, tStop - tD, 'linear', flip);
    });
    at(tStop, function() { danny.innerHTML = tgHtml('danny', 'stand', true); });
    // The dome starts to jiggle: it leaves his frame for its own element, bobbing
    var sDome = sStop + stPx(50), domeBottom = 4 + stPx(50), dome = null;
    at(tStop + 500, function() {
      danny.innerHTML = tgHtml('danny', 'stand', false);
      dome = keep(stSpawn('<div class="tgbob">' + tgDomeSvg() + '</div>', DMW, DMH, domeBottom, X(sDome, DMW), 0, flip));
    });
    // ...then pops off, flips over in the air and lands upside-down behind him
    var tPop = tStop + 1500, sDomeLand = sStop - stPx(34);
    at(tPop, function() {
      dome.firstChild.className = 'tgspin';
      stMove(dome, X((sDome + sDomeLand) / 2, DMW), -stPx(46), 380, 'cubic-bezier(0.2,0.6,0.4,1)', flip);
    });
    at(tPop + 380, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom), 420, 'cubic-bezier(0.5,0,0.9,0.6)', flip); });
    at(tPop + 800, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom) - stPx(6), 120, 'ease-out', flip); });
    at(tPop + 920, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom), 120, 'ease-in', flip); });

    // The turkey sits up on the platter facing the way they came, hops down, "gobble!", and sprints off
    var sTurk = sStop + stPx(66) - TW / 2, turkBottom = 4 + stPx(50), sTurkDown = sStop - stPx(44), turkey = null;
    at(tPop, function() { turkey = keep(stSpawn(tgTurkeyHtml('sit'), TW, TH, turkBottom, X(sTurk, TW), 0, back)); });
    var tHop = tPop + 350;
    at(tHop, function() {
      turkey.innerHTML = tgTurkeyHtml('run');
      stMove(turkey, X((sTurk + sTurkDown) / 2, TW), -stPx(14), 200, 'ease-out', back);
    });
    at(tHop + 200, function() { stMove(turkey, X(sTurkDown, TW), floorY(turkBottom), 260, 'ease-in', back); });
    at(tHop + 460, function() {
      var b = keep(stBubble('gobble!', X(sTurkDown, TW) + Math.round(TW / 2) - 22, 4 + stPx(40)));
      at(1100, function() { drop(b); });
    });
    var tRun = tHop + 800, dTurk = Math.round((sTurkDown + TW + 20) / 0.55);
    at(tRun, function() { stMove(turkey, X(-TW - 20, TW), floorY(turkBottom), dTurk, 'linear', back); });

    // "!" — Danny drops the platter (the garnish drumstick bounces off it), then turns and gives chase
    var exc = null;
    at(tHop + 150, function() {
      exc = keep(document.createElement('div'));
      exc.textContent = '!';
      exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (headX(sStop) - 6) + 'px;bottom:' + (4 + stPx(114)) + 'px;font:800 ' + stPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
      document.body.appendChild(exc);
    });
    at(tHop + 1050, function() { drop(exc); });
    var sPlat = sStop + stPx(46), platBottom = 4 + stPx(45), sDrum = sStop + stPx(66) + stPx(30) - DRW / 2, drumBottom = 4 + stPx(50), drum = null;
    at(tHop + 500, function() {
      danny.innerHTML = tgHtml('danny', 'drop');
      var plat = keep(stSpawn('<div class="tgtilt">' + tgPlatterSvg() + '</div>', PLW, PLH, platBottom, X(sPlat, PLW), 0, flip));
      drum = keep(stSpawn(tgDrumSvg(), DRW, DRH, drumBottom, X(sStop + stPx(66) - DRW / 2, DRW), 0, flip));
      at(30, function() {
        plat.firstChild.style.transform = 'rotate(-18deg)';
        stMove(plat, X(sPlat + stPx(6), PLW), floorY(platBottom), 380, 'cubic-bezier(0.5,0,1,1)', flip);
        stMove(drum, X(sDrum, DRW), -stPx(22), 220, 'ease-out', flip);
      });
      at(250, function() { stMove(drum, X(sDrum, DRW), floorY(drumBottom), 300, 'ease-in', flip); });
      at(410, function() { plat.firstChild.style.transition = 'transform 160ms ease-out'; plat.firstChild.style.transform = 'rotate(3deg)'; });
    });
    var tChase = tHop + 1200, sTurn = sStop - stPx(10), dChase = Math.round((sTurn + W + 20) / 0.42);
    at(tChase, function() {
      danny.innerHTML = tgHtml('danny', 'chase');
      danny.style.transition = 'none';
      danny.style.transform = 'translate(' + X(sTurn, W) + 'px,0px)' + back;
      danny.getBoundingClientRect();
      stMove(danny, X(-W - 20, W), 0, dChase, 'linear', back);
    });

    // The cat creeps in from the far side, sniffs the drumstick, and trots off with it
    var sC0 = vw + 20, sSniff = sDrum + DRW + stPx(4), tCat = tHop + 1900, dCatIn = Math.max(900, Math.min(4500, Math.round((sC0 - sSniff) / 0.26)));
    var tSniff = tCat + dCatIn, tTake = tSniff + 700, dCatOut = Math.round((sC0 - sSniff) / 0.3), cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(tgCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, back));
      stMove(cat, X(sSniff, CW), 0, dCatIn, 'linear', back);
    });
    at(tSniff, function() { cat.innerHTML = tgCatHtml('crouch'); });
    at(tTake, function() {
      drop(drum);
      cat.innerHTML = tgCatHtml('carry');
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sSniff, CW) + 'px,0px)' + flip;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', flip);
    });
    var tEnd = Math.max(1300 + tCross, tChase + dChase, tRun + dTurk, tTake + dCatOut) + 300;
    at(tEnd - 700, function() { els.forEach(function(el) { if (el === danny || el === cat || el === turkey) return; el.style.transition = 'opacity 600ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.potluckRun = potluckRun;

// ─── BLACK FRIDAY STAMPEDE (the hourly cartoon from Black Friday through Cyber Monday) ───
// Emma walks in and plants herself by the edge, headset on: "ding!" "ding!" "ding!" — the
// orders are pouring in. A tidal wave of parcels and two runaway shopping carts races
// across the bottom of the page. Tracy (Logistics, her desk look) stands mid-screen facing
// the flood with a sheet of labels and slaps one onto every box as it rushes past. The
// forklift crawls through behind the wave with both stacks piled seven high and teetering
// ("beep! beep!"); Emma tags along behind it; last of all the Filing floor's gray cat
// rides a box out, and Tracy calls it a day. Anthony 2026-11-01. Same rig as the Storm;
// the boxes and the lift are the Forklift Fumble's. window.stampedeRun() = demo.
var BF = {
  tracy: { hair: '#B8BCC4', skin: '#E8B98A', shirt: '#0EA5E9', shirtDk: '#0284C7', pantsA: '#1F2937', pantsB: '#2B3444' }, // officeLook('tracking-claims'): curly, hoop earrings
  emma: { shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A' },
  label: '#FFFFFF', labelLn: '#94A3B8', bar: '#1F2937', cart: '#9CA3AF', cartDk: '#6B7280', tire: '#0B0F16', hoop: '#F2CF6B',
};
function bfBarcode(x, y, w, h) { var s = ''; for (var i = 0; i < 6; i++) s += fkRect(x + i * (w / 6), y, i % 3 === 0 ? 1.1 : 0.6, h, 0, BF.bar); return s; }
function bfLabel() { return fkRect(0, 0, 10, 6, 0.8, BF.label, ' stroke="' + BF.labelLn + '" stroke-width="0.5"') + bfBarcode(1.2, 1.3, 7.5, 3.2); }
// A parcel from the forklift's stacks, with Tracy's label slapped on once she's had it
function bfBoxHtml(labeled) {
  return forkSvgWrap(fkBox(1, 1, 22, 12) + (labeled ? fkG(bfLabel(), 'translate(5.5,3.4) scale(0.95)') : ''), 24, 14, fkPx(36), fkPx(21), '');
}
// Tracy's head at the walker scale: a halo of curls, the fringe cap, a hoop earring
function bfTracyHead() {
  var lk = BF.tracy, r = 11.5, cx = 0, cy = -68, k = [];
  [[-10, -8], [-6, -11.5], [0, -12.5], [6, -11.5], [10, -8], [-12.5, -2], [12.5, -2], [-12, 4.5], [12, 4.5]].forEach(function(p) { k.push(fkCircle(cx + p[0], cy + p[1], 4.4, lk.hair)); });
  k.push(fkCircle(cx, cy, r, lk.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye));
  k.push('<path d="' + HW_CAP_SHORT + '" fill="' + lk.hair + '"/>');
  k.push('<circle cx="' + (cx - 0.6 * r) + '" cy="' + (cy + 0.95 * r) + '" r="1.7" fill="none" stroke="' + BF.hoop + '" stroke-width="0.9"/>');
  return k.join('');
}
// Tracy (90×112, feet at 40,106): 'walk' in, 'stand' facing the flood with her label sheet,
// 'slap' with the near hand down on a passing box
function bfTracyFrame(pose, which) {
  var B = which === 'B', lk = BF.tracy, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'walk' ? 16 : pose === 'slap' ? 10 : 2));
  var lean = pose === 'slap' ? 16 : 0, S = lfShoulder(lean);
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, lk.shirt) + bfTracyHead(), 'rotate(' + lean + ' 0 -28)');
  var sheet = fkG(fkRect(0, 0, 13, 17, 1.2, BF.label, ' stroke="' + BF.labelLn + '" stroke-width="0.7"') + bfBarcode(2, 3, 9, 4) + bfBarcode(2, 10, 9, 4), 'translate(13,-55) rotate(-12)');
  var H1 = pose === 'slap' ? [30, B ? -25 : -27] : pose === 'walk' ? (B ? [-7, -32] : [9, -30]) : [18, B ? -34 : -35];
  var H2 = pose === 'walk' ? (B ? [9, -30] : [-7, -32]) : [15, -47];
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + (pose === 'walk' ? '' : sheet) + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  if (pose === 'slap') u += fkG(bfLabel(), 'translate(' + (H1[0] + 1) + ',' + (H1[1] - 2) + ') rotate(22)');
  k.push(fkG(u, 'translate(0,' + (B && pose === 'walk' ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function bfTracyHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfTracyFrame(pose, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'walk' ? '' : ' wv');
}
// Emma (desk look, headset): 'walk', or 'stand' with a hand to her earpiece as the orders ding in
function bfEmmaFrame(pose, which) {
  var B = which === 'B', lk = BF.emma, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'walk' ? 16 : 2));
  var S = lfShoulder(0);
  var torso = fkRect(-13, -58, 26, 36, 10, lk.shirt) + hwHead(HW.emma, 'ponytail');
  var H1 = pose === 'walk' ? (B ? [-7, -32] : [9, -30]) : [-8, B ? -65 : -64], H2 = pose === 'walk' ? (B ? [9, -30] : [-7, -32]) : [-6, -31];
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, HW.emma.skin) + torso + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, HW.emma.skin);
  k.push(fkG(u, 'translate(0,' + (B && pose === 'walk' ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function bfEmmaHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfEmmaFrame(pose, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'walk' ? '' : ' wv');
}
// A runaway shopping cart (60×44, floor at 43) heaped with parcels, handle at the back
function bfCartFrame(which) {
  var B = which === 'B', k = [fkEllipse(31, 43, 26, 2.2, FK.shadow)];
  k.push(fkBox(17, 4, 16, 9) + fkBox(32, 6, 14, 8) + fkBox(24, -3, 13, 8));
  k.push('<path d="M 12,12 L 52,12 L 48,32 L 16,32 Z" fill="' + BF.cart + '" opacity="0.45" stroke="' + BF.cartDk + '" stroke-width="1.4" stroke-linejoin="round"/>');
  [18, 24, 29].forEach(function(y) { k.push('<line x1="' + (13.5 + (y - 12) * 0.2).toFixed(1) + '" y1="' + y + '" x2="' + (50.5 - (y - 12) * 0.2).toFixed(1) + '" y2="' + y + '" stroke="' + BF.cartDk + '" stroke-width="0.8"/>'); });
  [22, 32, 42].forEach(function(x) { k.push('<line x1="' + x + '" y1="12" x2="' + (x - (x - 32) * 0.1).toFixed(1) + '" y2="32" stroke="' + BF.cartDk + '" stroke-width="0.8"/>'); });
  k.push('<path d="M 12,12 L 4,2" stroke="' + BF.cartDk + '" stroke-width="2.2" stroke-linecap="round"/>' + fkRect(0, 0.5, 9, 3, 1.5, BF.cartDk));
  k.push('<path d="M 16,32 L 16,38 M 48,32 L 48,38" stroke="' + BF.cartDk + '" stroke-width="1.6"/>');
  [[16, 39.5], [48, 39.5]].forEach(function(w) { k.push(fkCircle(w[0], w[1], 3.6, BF.tire) + fkG(fkRect(w[0] - 3, w[1] - 0.5, 6, 1, 0.5, BF.cart), 'rotate(' + (B ? 45 : 0) + ' ' + w[0] + ' ' + w[1] + ')') + fkCircle(w[0], w[1], 1.1, BF.cart)); });
  return k.join('');
}
function bfCartHtml() {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfCartFrame(which), 60, 48, fkPx(90), fkPx(72), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// The Forklift Fumble's lift with both stacks seven boxes high, teetering ('wob' frames),
// in a viewBox tall enough to hold them
function bfForkliftHtml() {
  var extra = 38;
  var mk = function(which) { return function(cls) { return forkSvgWrap(fkG(forkliftFrame('wob', [7, 7], which), 'translate(0,' + extra + ')'), 126, 70 + extra, fkPx(189), fkPx(Math.round(105 * (70 + extra) / 70)), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), '');
}
// The gray cat sitting on a parcel, facing the screen (58×68, box on the floor)
function bfCatRideHtml() {
  var mk = function(which) { return function(cls) {
    var B = which === 'B', c = ST.cat, p = [
      '<path d="' + (B ? 'M 6,-4 Q 18,-4 20,-14' : 'M 6,-4 Q 19,-6 18,-16') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>',
      '<ellipse cx="-2" cy="-13" rx="11" ry="12.5" fill="' + c + '"/>',
      fkRect(-9, -12, 4.5, 12, 2.2, c) + fkRect(1, -12, 4.5, 12, 2.2, c),
      fkCircle(-1, -29, 7.5, c) + stCatEars(-13, -7) + fkCircle(-3.6, -30, 1.2, FK.eye) + fkCircle(1.6, -30, 1.2, FK.eye)];
    return forkSvgWrap(fkBox(5, 46, 36, 20) + fkG(p.join(''), 'translate(23,46)'), 58, 68, stPx(58), stPx(68), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
function bfEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('stampede-style')) return;
  var st = document.createElement('style');
  st.id = 'stampede-style';
  st.textContent =
    '@keyframes bfHop { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-9px) rotate(-4deg); } }' +
    '.bfhop { position: absolute; inset: 0; animation: bfHop 0.42s ease-in-out infinite; }';
  document.head.appendChild(st);
}
function stampedeRun() {
  try {
    bfEnsureStyle();
    FORK_PAL.h = ['#F1F5F9', '#FBBF24', '#3B82F6'][Math.floor(Math.random() * 3)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the flood runs
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), BW = fkPx(36), BH = fkPx(21), KW = fkPx(90), KH = fkPx(72), LW = fkPx(189), LH = fkPx(162), RW = stPx(58), RH = stPx(68);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var hop = function(html, period, delay) { return '<div class="bfhop" style="animation-duration:' + period.toFixed(2) + 's;animation-delay:-' + delay.toFixed(2) + 's">' + html + '</div>'; };

    // Emma walks in to the edge and takes the calls: "ding!" x3
    var v = (vw + W + 40) / 9000, sE = Math.round(vw * 0.06), tE = Math.round((sE + W + 20) / v), emma = null;
    var headXE = function(s) { return lr ? X(s, W) + stPx(40) : X(s, W) + W - stPx(40); };
    at(0, function() {
      emma = keep(stSpawn(bfEmmaHtml('walk'), W, H, 4, X(-W - 20, W), 0, flip));
      stMove(emma, X(sE, W), 0, tE, 'linear', flip);
    });
    at(tE, function() { emma.innerHTML = bfEmmaHtml('stand'); });
    [0, 1, 2].forEach(function(i) {
      at(tE + 400 + 480 * i, function() {
        var b = keep(stBubble('ding!', headXE(sE) - 24 + i * 30, 4 + stPx(114) + i * 12));
        at(1500, function() { drop(b); });
      });
    });

    // Tracy hurries in from the far side and stands mid-screen facing the flood, label sheet ready
    var sT = Math.round(vw / 2) - W + stPx(70), sHand = sT + W - stPx(70), tT = Math.round((vw + 20 - sT) / 0.4), tracy = null;
    at(0, function() {
      tracy = keep(stSpawn(bfTracyHtml('walk'), W, H, 4, X(vw + 20, W), 0, back));
      stMove(tracy, X(sT, W), 0, tT, 'linear', back);
    });
    at(tT, function() { tracy.innerHTML = bfTracyHtml('stand'); });
    var slapTimer = 0;
    var slap = function() {
      if (!tracy) return;
      tracy.innerHTML = bfTracyHtml('slap');
      slapTimer++;
      var mine = slapTimer;
      at(190, function() { if (slapTimer === mine) tracy.innerHTML = bfTracyHtml('stand'); });
    };

    // The wave: fourteen parcels and two carts race through; each box gets its label as it passes her hand
    var sBox0 = -BW - 20;
    for (var i = 0; i < 14; i++) (function(i) {
      var t0 = 1800 + 110 * i + ((i * 53) % 4) * 40, sp = 0.5 + ((i * 7) % 5) * 0.05, cross = Math.round((vw + BW + 40) / sp);
      var box = null;
      at(t0, function() {
        box = keep(stSpawn(hop(bfBoxHtml(false), 0.36 + (i % 3) * 0.05, i * 0.13), BW, BH, 4, X(sBox0, BW), 0, flip));
        stMove(box, X(vw + 20, BW), 0, cross, 'linear', flip);
      });
      at(t0 + Math.round((sHand - BW / 2 - sBox0) / sp), function() {
        if (box) box.firstChild.innerHTML = bfBoxHtml(true);
        slap();
      });
    })(i);
    [3000, 4300].forEach(function(t0, i) {
      at(t0, function() {
        var cart = keep(stSpawn(hop(bfCartHtml(), 0.5, i * 0.2), KW, KH, 4, X(-KW - 20, KW), 0, flip));
        stMove(cart, X(vw + 20, KW), 0, Math.round((vw + KW + 40) / 0.62), 'linear', flip);
      });
    });

    // The forklift crawls through behind the wave, stacks seven high and teetering, "beep! beep!"
    var tL = 7800, vL = 0.28, dL = Math.round((vw + LW + 40) / vL), lift = null;
    var sLift = function(t) { return -LW - 20 + vL * (t - tL); };
    at(tL, function() {
      lift = keep(stSpawn(bfForkliftHtml(), LW, LH, 4, X(-LW - 20, LW), 0, flip));
      stMove(lift, X(vw + 20, LW), 0, dL, 'linear', flip);
    });
    at(tL + 2000, function() {
      var cabX = function(s) { return lr ? X(s, LW) + fkPx(50) : X(s, LW) + LW - fkPx(50); };
      var b = keep(stBubble('beep! beep!', cabX(sLift(tL + 2000)) - 20, 4 + LH + 6));
      b.style.transition = 'transform 1400ms linear';
      b.style.transform = 'translateX(' + (cabX(sLift(tL + 3400)) - 20) + 'px)';
      at(1400, function() { drop(b); });
    });
    // Emma tags along behind the lift once it has gone by
    var tEmmaOff = tL + Math.round((sE + W + 20 + LW) / vL) + 400, dEmmaOff = Math.round((vw + 20 - sE) / 0.3);
    at(tEmmaOff, function() { emma.innerHTML = bfEmmaHtml('walk'); stMove(emma, X(vw + 20, W), 0, dEmmaOff, 'linear', flip); });

    // Last of all the cat rides a parcel out; Tracy turns and calls it a day
    var tCat = tL + dL - 1700, dCat = Math.round((vw + RW + 40) / 0.45);
    at(tCat, function() {
      var ride = keep(stSpawn(hop(bfCatRideHtml(), 0.5, 0), RW, RH, 4, X(-RW - 20, RW), 0, ''));
      stMove(ride, X(vw + 20, RW), 0, dCat, 'linear');
    });
    var tTracyOff = tCat + Math.round((sT + RW + 20 + W) / 0.45) + 500, sTurn = sT + W - stPx(90), dTracyOff = Math.round((vw + 20 - sTurn) / 0.3);
    at(tTracyOff, function() {
      tracy.innerHTML = bfTracyHtml('walk');
      tracy.style.transition = 'none';
      tracy.style.transform = 'translate(' + X(sTurn, W) + 'px,0px)' + flip;
      tracy.getBoundingClientRect();
      stMove(tracy, X(vw + 20, W), 0, dTracyOff, 'linear', flip);
    });
    var tEnd = Math.max(tEmmaOff + dEmmaOff, tCat + dCat, tTracyOff + dTracyOff, tL + dL) + 400;
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.stampedeRun = stampedeRun;

// ─── HOLIDAY SLEIGH (the hourly cartoon Dec 18-31) ───
// Snow drifts down over the bottom of the page. The Office agents cross in holiday trim,
// each with their desk look: Emma in an elf hat, Danny with a candy cane over his
// shoulder, Sage hugging a wrapped gift. Then the Filing floor's gray cat, in antlers
// and a red nose, trots through pulling a red sleigh — Santa at the reins, parcels
// heaped in the back — which eases to a stop at center. Santa waves: "ho ho ho!", and
// sleigh, cat and all fade away like the Halloween ghost. A Christmas tree waits in the
// corner they're all headed for (Anthony 2026-11-01, "in the corner of where they're all
// headed"). Same rig and helpers as the Storm and the parade. window.sleighRun() = demo.
var SL = {
  red: '#B91C1C', redDk: '#991B1B', gold: '#F2CF6B', white: '#F8FAFC', green: '#16A34A', greenDk: '#15803D', belt: '#1F2937',
  santaSkin: '#F3D2B3', antler: '#8B5A2B', nose: '#DC2626', wrap: '#DC2626', ribbon: '#F2CF6B', cane: '#F8FAFC', caneStripe: '#DC2626', snow: '#F8FAFC',
};
var SL_WHO = {
  emma: { skin: HW.emma.skin, shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A', head: function() { return hwHead(HW.emma, 'ponytail'); } },
  sage: { skin: HW.sage.skin, shirt: '#84CC16', shirtDk: '#65A30D', pantsA: HW.pants, pantsB: HW.pantsB, head: function() { return hwHead(HW.sage, 'bob'); } },
  danny: { skin: ST.skin, shirt: ST.shirt, shirtDk: ST.shirtDk, pantsA: FK.pantsA, pantsB: FK.pantsB, head: function() { return stDannyHead(0, -68); } },
};
// Elf hat over a walker head at (0,-68): white brim, green cone, the tip flopping forward with a pom
function slElfHat() {
  return '<path d="M -11.5,-77 L 11.5,-77 L 4,-95 Q 1,-103 9,-104 Z" fill="' + SL.green + '"/>' +
    '<path d="M -4,-80 L 4,-95" stroke="' + SL.greenDk + '" stroke-width="1" fill="none"/>' +
    '<ellipse cx="0" cy="-77" rx="12.5" ry="3" fill="' + SL.white + '"/>' + fkCircle(10, -104.5, 3, SL.white);
}
// Candy cane held in a hand at (hx, hy), rising over the shoulder, hook curling forward
function slCandyCane(hx, hy) {
  var d = 'M ' + hx + ',' + (hy + 6) + ' L ' + (hx + 2) + ',' + (hy - 30) + ' Q ' + (hx + 3) + ',' + (hy - 40) + ' ' + (hx + 11) + ',' + (hy - 39) + ' Q ' + (hx + 16) + ',' + (hy - 38) + ' ' + (hx + 16) + ',' + (hy - 32);
  return '<path d="' + d + '" stroke="' + SL.cane + '" stroke-width="4.6" fill="none" stroke-linecap="round"/>' +
    '<path d="' + d + '" stroke="' + SL.caneStripe + '" stroke-width="4.6" fill="none" stroke-linecap="butt" stroke-dasharray="3.2 3.8"/>';
}
// A wrapped gift, 16×14, held out front with its bow on top
function slGift(x, y) {
  return fkRect(x, y, 16, 14, 1.5, SL.wrap) + fkRect(x + 6.5, y, 3, 14, 0.5, SL.ribbon) + fkRect(x, y + 5.5, 16, 3, 0.5, SL.ribbon) +
    '<path d="M ' + (x + 8) + ',' + (y - 1) + ' Q ' + (x + 2) + ',' + (y - 7) + ' ' + (x + 4) + ',' + (y - 1) + ' Z M ' + (x + 8) + ',' + (y - 1) + ' Q ' + (x + 14) + ',' + (y - 7) + ' ' + (x + 12) + ',' + (y - 1) + ' Z" fill="' + SL.ribbon + '"/>';
}
// The three walkers (90×112, feet at 40,106) in their holiday trim
function slFigure(who, which) {
  var B = which === 'B', lk = SL_WHO[who], k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, 16));
  var S = lfShoulder(0), H1, H2, held = '', hat = '';
  if (who === 'emma') { H1 = B ? [-7, -32] : [9, -30]; H2 = B ? [9, -30] : [-7, -32]; hat = slElfHat(); }
  else if (who === 'danny') { H1 = [12, -52]; H2 = B ? [-8, -31] : [6, -30]; }
  else { H1 = [21, -42]; H2 = [19, -46]; held = slGift(18, -53); }
  var torso = fkRect(-13, -58, 26, 36, 10, lk.shirt) + lk.head() + hat;
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  if (who === 'danny') u += slCandyCane(12, -52) + fkCircle(12, -52, 3.6, lk.skin);
  k.push(fkG(u, 'translate(0,' + (B ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function slHtml(who) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(slFigure(who, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
// The gray cat in antlers with a red nose, trotting (the Storm's walk frames)
function slCatHtml() {
  var mk = function(which) { return function(cls) {
    var g = stCatFrame('walk', which) +
      fkG('<path d="M 9,-28 L 7,-37 M 7.5,-33 L 3.5,-36 M 7.2,-34.5 L 10,-39 M 15,-28 L 17.5,-37 M 17,-33 L 21,-36 M 17.3,-34.5 L 14.5,-39" stroke="' + SL.antler + '" stroke-width="1.5" fill="none" stroke-linecap="round"/>' + fkCircle(18.6, -20.8, 1.7, SL.nose), 'translate(26,41)');
    return forkSvgWrap(g, 58, 44, stPx(58), stPx(44), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// The sleigh (170×98, floor at 97): red body on a gold runner, parcels in the back, Santa at the
// reins ('ride') or waving ('wave'); the harness runs ahead to the cat
function slSleighFrame(pose, which) {
  var B = which === 'B', k = [];
  var arm = function(ang) {
    var t = ang * Math.PI / 180;
    return fkRect(50, 8, 16, 5, 2.5, SL.red, ' transform="rotate(' + ang + ' 50 10.5)"') + fkCircle(50 + Math.cos(t) * 16, 10.5 + Math.sin(t) * 16, 2.6, SL.santaSkin);
  };
  k.push(fkEllipse(64, 82, 56, 2.6, FK.shadow));
  k.push('<path d="M 118,50 L 166,42" stroke="' + SL.antler + '" stroke-width="1.6" stroke-linecap="round"/>'); // harness to the cat
  k.push('<path d="M 10,62 Q 6,62 7,56 M 10,64 L 100,64 Q 118,64 121,50" stroke="' + SL.gold + '" stroke-width="2.6" fill="none" stroke-linecap="round"/>');
  k.push('<path d="M 30,54 L 30,63 M 95,54 L 95,63" stroke="' + SL.gold + '" stroke-width="2.2"/>');
  k.push(fkBox(72, 6, 18, 11) + fkBox(90, 2, 16, 10) + fkBox(80, -4, 14, 9)); // parcels heaped behind the seat
  if (pose === 'ride') k.push('<path d="M 66,10 Q 100,20 124,46" stroke="' + SL.antler + '" stroke-width="1.3" fill="none"/>'); // reins
  k.push(fkRect(34, 6, 22, 26, 8, SL.red) + fkRect(34, 22, 22, 4, 1, SL.belt) + fkRect(43, 21.5, 5, 5, 1, SL.gold)); // Santa's coat and belt
  k.push(fkCircle(45, -2, 8, SL.santaSkin) + '<ellipse cx="45" cy="5.5" rx="7.8" ry="6" fill="' + SL.white + '"/>' + fkCircle(48, -3, 1, FK.eye) + fkCircle(51, -0.5, 1.6, '#F28B82'));
  k.push('<path d="M 37,-6.5 Q 45,-20 58,-12 L 54,-6 Z" fill="' + SL.red + '"/>' + fkRect(36, -8, 19, 3.6, 1.6, SL.white) + fkCircle(58.5, -12.5, 2.8, SL.white)); // hat
  k.push(arm(pose === 'wave' ? (B ? -118 : -95) : 8));
  k.push('<path d="M 18,54 L 14,30 Q 12,14 34,16 L 96,16 Q 118,16 116,36 L 110,54 Z" fill="' + SL.red + '" stroke="' + SL.gold + '" stroke-width="1.8" stroke-linejoin="round"/>' +
    '<path d="M 24,50 L 21,32 Q 20,22 34,22 L 94,22 Q 108,22 106,34 L 103,50 Z" fill="' + SL.redDk + '" opacity="0.5"/>'); // the body hides Santa's legs
  return fkG(k.join(''), 'translate(0,28)');
}
function slSleighHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(slSleighFrame(pose, which), 170, 98, stPx(170), stPx(98), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
// The Christmas tree waiting in the corner they're all headed for (80×110, floor at 108):
// three tiers with garlands and baubles, a star on top, two gifts underneath
function slTreeSvg() {
  var k = [fkEllipse(40, 108, 34, 2.6, FK.shadow)];
  k.push(fkRect(35, 90, 10, 18, 2, SL.antler));
  [[4, 96, 52], [10, 70, 32], [16, 48, 12]].forEach(function(t) {
    k.push('<path d="M ' + t[0] + ',' + t[1] + ' L ' + (80 - t[0]) + ',' + t[1] + ' L 40,' + t[2] + ' Z" fill="' + SL.green + '" stroke="' + SL.greenDk + '" stroke-width="1.2" stroke-linejoin="round"/>');
  });
  k.push('<path d="M 14,88 Q 40,80 66,90 M 20,64 Q 40,57 60,66 M 26,42 Q 40,37 54,43" stroke="' + SL.gold + '" stroke-width="1.4" fill="none" opacity="0.9"/>');
  [[22, 90, SL.red], [40, 84, '#3B82F6'], [58, 91, SL.gold], [30, 66, SL.gold], [50, 62, SL.red], [40, 72, SL.white], [34, 44, '#3B82F6'], [46, 40, SL.red], [40, 54, SL.gold]].forEach(function(b) { k.push(fkCircle(b[0], b[1], 2.7, b[2])); });
  k.push('<path d="M 40,2 L 42.4,8.2 L 49,8.6 L 43.8,12.8 L 45.6,19.2 L 40,15.6 L 34.4,19.2 L 36.2,12.8 L 31,8.6 L 37.6,8.2 Z" fill="' + SL.gold + '"/>');
  k.push(slGift(2, 94) + fkG(slGift(0, 0), 'translate(62,96) scale(0.85)'));
  return forkSvgWrap(k.join(''), 80, 110, stPx(80), stPx(110), '');
}
function slFlakeSvg(r) { return forkSvgWrap(fkCircle(5, 5, r, SL.snow, ' opacity="' + (0.5 + r * 0.12).toFixed(2) + '"'), 10, 10, 12, 12, ''); }
function sleighRun() {
  try {
    stEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the sleigh runs
    var flip = lr ? '' : ' scaleX(-1)';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), CW = stPx(58), CH = stPx(44), SW = stPx(170), SH = stPx(98);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };

    // The Christmas tree waits in the corner they're all headed for; it fades in first
    var TRW = stPx(80), TRH = stPx(110);
    at(0, function() {
      var tree = keep(stSpawn(slTreeSvg(), TRW, TRH, 4, X(vw - TRW - 12, TRW), 0, ''));
      tree.style.opacity = '0';
      tree.getBoundingClientRect();
      tree.style.transition = 'opacity 700ms ease-out';
      tree.style.opacity = '1';
    });

    // Snow: two dozen flakes drift down the bottom strip, each falling three times
    var fall = 240;
    for (var i = 0; i < 24; i++) (function(i) {
      var x = Math.round(vw * ((i * 61) % 97) / 97), r = 1.6 + ((i * 7) % 4) * 0.7, dur = 4200 + ((i * 13) % 5) * 300, sway = ((i % 2) ? 1 : -1) * (14 + (i % 4) * 8);
      var el = null;
      [0, 1, 2].forEach(function(round) {
        at(i * 170 + round * (dur + 150), function() {
          if (!el) el = keep(stSpawn(slFlakeSvg(r), 12, 12, 0, x, -fall, ''));
          el.style.transition = 'none';
          el.style.transform = 'translate(' + x + 'px,' + (-fall) + 'px)';
          el.getBoundingClientRect();
          stMove(el, x + sway, 0, dur, 'linear');
        });
      });
    })(i);

    // The agents cross in their holiday trim, about 1.3 s apart
    var v = (vw + W + 40) / 9000, tCross = Math.round((vw + W + 40) / v);
    [[600, 'emma'], [1900, 'danny'], [3200, 'sage']].forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(slHtml(a[1]), W, H, 4, X(-W - 20, W), 0, flip));
        stMove(el, X(vw + 20, W), 0, tCross, 'linear', flip);
      });
    });

    // The cat pulls the sleigh in and they ease to a stop at center
    var vS = 0.26, tS = 6500, sStop = Math.round(vw / 2 - stPx(65)), sEase = sStop - stPx(40), catOff = stPx(152);
    var dS = Math.round((sEase + SW + 20) / vS), tStop = tS + dS + 600, sleigh = null, cat = null;
    at(tS, function() {
      sleigh = keep(stSpawn(slSleighHtml('ride'), SW, SH, 4, X(-SW - 20, SW), 0, flip));
      cat = keep(stSpawn(slCatHtml(), CW, CH, 4, X(-SW - 20 + catOff, CW), 0, flip));
      stMove(sleigh, X(sEase, SW), 0, dS, 'linear', flip);
      stMove(cat, X(sEase + catOff, CW), 0, dS, 'linear', flip);
    });
    at(tS + dS, function() {
      stMove(sleigh, X(sStop, SW), 0, 600, 'ease-out', flip);
      stMove(cat, X(sStop + catOff, CW), 0, 600, 'ease-out', flip);
    });
    at(tStop + 200, function() { cat.innerHTML = slCatHtml().replace(/catfr f2 fast/g, 'catfr f2 wv').replace(/catfr fast/g, 'catfr wv'); });
    // Santa waves: "ho ho ho!" — then sleigh, cat and all fade away
    at(tStop + 300, function() { sleigh.innerHTML = slSleighHtml('wave'); });
    var santaX = function(s) { return lr ? X(s, SW) + stPx(46) : X(s, SW) + SW - stPx(46); };
    at(tStop + 500, function() {
      var b = keep(stBubble('ho ho ho!', santaX(sStop) - 20, 4 + SH + 6));
      at(1500, function() { drop(b); });
    });
    at(tStop + 2100, function() {
      [sleigh, cat].forEach(function(el) { el.style.transition = 'opacity 800ms ease-in'; el.style.opacity = '0'; });
    });
    var tEnd = Math.max(3200 + tCross, tStop + 3000) + 300;
    at(tEnd - 800, function() { els.forEach(function(el) { if (el === sleigh || el === cat) return; el.style.transition = 'opacity 700ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.sleighRun = sleighRun;

// ─── Marketplace order page (Anthony 2026-09-23, to-do 15): the seller-side page for a channel order
//     id — Amazon by its 3-7-7 id (Seller Central of the channel's country), eBay by its 2-5-5 id,
//     Walmart by channel name. Null when we know no page (Faire, Shopify relays, SHEIN …). ───
function marketplaceOrderUrl(channel, channelOrderId) {
  var id = String(channelOrderId || '').trim();
  var ch = String(channel || '').toLowerCase();
  if (!id) return null;
  var enc = encodeURIComponent(id);
  if (/^\d{3}-\d{7}-\d{7}$/.test(id)) {
    var host = /canada|\bca\b/.test(ch) ? 'sellercentral.amazon.ca' : /mexico|\bmx\b/.test(ch) ? 'sellercentral.amazon.com.mx' : /australia|\bau\b/.test(ch) ? 'sellercentral.amazon.com.au' : /brazil|\bbr\b/.test(ch) ? 'sellercentral.amazon.com.br' : 'sellercentral.amazon.com';
    return 'https://' + host + '/orders-v3/order/' + enc;
  }
  if (/^\d{2}-\d{5}-\d{5}$/.test(id) || /ebay/.test(ch)) return 'https://www.ebay.com/sh/ord/details?orderid=' + enc;
  if (/walmart/.test(ch)) return 'https://seller.walmart.com/order-management/details?purchaseOrderId=' + enc;
  return null;
}
// The id as a new-tab link when we know its page, else plain text (app-utils has no global h)
function marketplaceOrderLink(channel, channelOrderId, style) {
  var url = marketplaceOrderUrl(channel, channelOrderId);
  var text = channelOrderId == null || channelOrderId === '' ? '—' : String(channelOrderId);
  if (!url) return React.createElement('span', { style: style || null }, text);
  return React.createElement('a', { href: url, target: '_blank', rel: 'noopener', title: 'Open this order on the marketplace',
    onClick: function(e) { e.stopPropagation(); },
    style: Object.assign({ color: C.accent, textDecoration: 'none' }, style || {}) }, text);
}
window.marketplaceOrderUrl = marketplaceOrderUrl;
window.marketplaceOrderLink = marketplaceOrderLink;

;
/* ===== app-i18n.js ===== */
// ─── UI TRANSLATOR (window.I18N) ───
// Live DOM translation for users whose profile language isn't English.
//
// REACT-SAFETY INVARIANT — the reason this exists instead of Chrome's page
// translate: we ONLY ever write text via node.nodeValue and attributes via
// setAttribute. We never insert, remove, wrap, or re-parent nodes. Chrome's
// translator wraps text nodes in <font> elements, and the next React commit
// that touches them throws "removeChild ... not a child of this node"
// (error_log 334/335). Keep it that way: any change to this file that
// mutates DOM *structure* re-introduces that crash.
//
// Mechanics: digit-bearing tokens (counts, SKUs, dates, prices) are swapped
// for ⟦N⟧ placeholders before dictionary lookup, so volatile values never
// reach the server and can never be mangled by translation. Lookups hit an
// in-memory Map seeded from GET /api/i18n/bundle; misses batch-POST to
// /api/i18n/translate (debounced), then the document is re-walked.
// When React re-renders a translated label, the new English text appears for
// one frame and the MutationObserver re-translates it.

(function() {
  var state = {
    lang: null,
    dict: new Map(),      // placeholderized source -> translation (null = gave up this session)
    pending: new Set(),   // keys awaiting server translation
    attempts: new Map(),  // key -> failed batch count
    observer: null,
    timer: null,
    inflight: false,
  };
  // Guards against reacting to our own writes: MutationObserver records arrive
  // async, so suppression flags don't work — instead remember what we wrote
  // per node/attribute and skip mutations that match it.
  var lastWritten = new WeakMap();     // Text node -> last value we wrote
  var lastWrittenAttr = new WeakMap(); // Element -> { attrName: last value we wrote }
  // What each node said BEFORE we translated it — lets a switch back to
  // English restore the page live instead of asking for a refresh.
  var originals = new WeakMap();       // Text node -> original value
  var originalAttrs = new WeakMap();   // Element -> { attrName: original value }

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, IFRAME: 1, CANVAS: 1 };
  var ATTRS = ['placeholder', 'title', 'aria-label'];

  function skipElement(el) {
    for (var e = el; e && e.nodeType === 1; e = e.parentElement) {
      if (SKIP_TAGS[e.tagName]) return true;
      if (e.isContentEditable) return true;
      if (e.hasAttribute && (e.hasAttribute('data-no-i18n') || e.getAttribute('translate') === 'no')) return true;
      if (e.classList && e.classList.contains('notranslate')) return true;
    }
    return false;
  }

  // Swap every whitespace-delimited run containing a digit for ⟦N⟧ — "Box 12"
  // → "Box ⟦0⟧", "MFK-ROUGE-540" → "⟦0⟧", "8.5x11 in" → "⟦0⟧ in". Keeps the
  // dictionary low-cardinality and guarantees codes survive verbatim.
  function keyFor(text) {
    var tokens = [];
    var key = text.replace(/\S*\d\S*/g, function(m) { tokens.push(m); return '⟦' + (tokens.length - 1) + '⟧'; });
    return { key: key, tokens: tokens };
  }
  function restore(translated, tokens) {
    return translated.replace(/⟦(\d+)⟧/g, function(m, i) {
      return tokens[+i] !== undefined ? tokens[+i] : m;
    });
  }
  function eligible(key) {
    if (!key || key.length < 2 || key.length > 250) return false;
    if (!/[A-Za-z]{2,}/.test(key)) return false;         // pure numbers/symbols/single letters
    if (/^https?:\/\/|@.*\./.test(key)) return false;    // URLs, email-ish
    return true;
  }

  function translateText(raw, node, write) {
    var m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
    var lead = m[1], core = m[2], trail = m[3];
    if (!core) return;
    var pk = keyFor(core);
    if (!eligible(pk.key)) return;
    var tr = state.dict.get(pk.key);
    if (tr === undefined) {
      state.pending.add(pk.key);
      scheduleFlush();
    } else if (tr !== null) {
      var out = lead + restore(tr, pk.tokens) + trail;
      if (out !== raw) write(out);
    }
  }

  function applyToTextNode(node) {
    if (!state.lang || !node.parentElement || skipElement(node.parentElement)) return;
    var raw = node.nodeValue;
    if (raw == null || lastWritten.get(node) === raw) return;
    translateText(raw, node, function(out) {
      originals.set(node, raw);
      lastWritten.set(node, out);
      node.nodeValue = out; // nodeValue write only — never restructure (see header)
    });
  }

  function applyToAttr(el, attr) {
    if (!state.lang || skipElement(el)) return;
    var raw = el.getAttribute && el.getAttribute(attr);
    if (!raw) return;
    var mine = lastWrittenAttr.get(el);
    if (mine && mine[attr] === raw) return;
    translateText(raw, el, function(out) {
      var oa = originalAttrs.get(el);
      if (!oa) { oa = {}; originalAttrs.set(el, oa); }
      oa[attr] = raw;
      if (!mine) { mine = {}; lastWrittenAttr.set(el, mine); }
      mine[attr] = out;
      el.setAttribute(attr, out);
    });
  }

  function walk(root) {
    if (!root) return;
    if (root.nodeType === 3) { applyToTextNode(root); return; }
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
    var n = root.nodeType === 1 ? root : walker.nextNode();
    if (root.nodeType === 1) {
      for (var a = 0; a < ATTRS.length; a++) if (root.hasAttribute(ATTRS[a])) applyToAttr(root, ATTRS[a]);
      n = walker.nextNode();
    }
    while (n) {
      if (n.nodeType === 3) applyToTextNode(n);
      else for (var i = 0; i < ATTRS.length; i++) if (n.hasAttribute(ATTRS[i])) applyToAttr(n, ATTRS[i]);
      n = walker.nextNode();
    }
  }

  function scheduleFlush() {
    if (state.timer) return;
    state.timer = setTimeout(function() { state.timer = null; flush(); }, 500);
  }

  // One POST of up to 200 pending keys. Resolves after the response is merged
  // and re-applied (never rejects — failures feed the retry/give-up counters).
  function flushBatch() {
    if (!state.lang || !state.pending.size) return Promise.resolve();
    var batch = Array.from(state.pending).slice(0, 200);
    batch.forEach(function(k) { state.pending.delete(k); });
    return fetch('/api/i18n/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lang: state.lang, strings: batch }),
    }).then(function(r) {
      if (!r.ok) throw new Error('translate ' + r.status);
      return r.json();
    }).then(function(data) {
      var entries = (data && data.entries) || {};
      batch.forEach(function(k) {
        if (entries[k] !== undefined) { state.dict.set(k, entries[k]); state.attempts.delete(k); }
      });
      walk(document.body);
    }).catch(function() {
      // Transient failure: retry each key once more, then give up for the
      // session (dict=null) so a broken endpoint can't hot-loop requests.
      batch.forEach(function(k) {
        var n = (state.attempts.get(k) || 0) + 1;
        state.attempts.set(k, n);
        if (n >= 2) state.dict.set(k, null);
        else state.pending.add(k);
      });
    });
  }

  // Background path: one batch at a time, re-scheduled while misses remain.
  function flush() {
    if (!state.lang || state.inflight) { if (state.pending.size) scheduleFlush(); return; }
    if (!state.pending.size) return;
    state.inflight = true;
    flushBatch().finally(function() {
      state.inflight = false;
      if (state.pending.size) scheduleFlush();
    });
  }

  // Blocking path: translate every pending key NOW, batch after batch, until
  // the queue is dry or the deadline passes (leftovers finish in background).
  function drain(deadline) {
    if (!state.lang || !state.pending.size || Date.now() > deadline) return Promise.resolve();
    if (state.inflight) { // background flush racing us — wait a beat, retry
      return new Promise(function(res) { setTimeout(res, 150); }).then(function() { return drain(deadline); });
    }
    state.inflight = true;
    return flushBatch().finally(function() { state.inflight = false; })
      .then(function() { return drain(deadline); });
  }

  // Full-screen progress overlay for interactive language switches. Appended
  // directly to document.body — OUTSIDE React's root, so inserting/removing it
  // never touches React-managed nodes (same pattern as download anchors).
  var overlayEl = null;
  function showOverlay() {
    if (overlayEl) return;
    overlayEl = document.createElement('div');
    overlayEl.setAttribute('data-no-i18n', 'true');
    overlayEl.style.cssText = 'position:fixed;inset:0;z-index:100000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(10,12,18,0.78);backdrop-filter:blur(2px);color:#E8EAF0;font-family:inherit;';
    var spinner = document.createElement('div');
    spinner.style.cssText = 'width:34px;height:34px;border-radius:50%;border:3px solid rgba(255,255,255,0.25);border-top-color:#4F6EF7;animation:i18nspin 0.8s linear infinite;';
    var label = document.createElement('div');
    label.style.cssText = 'font-size:14px;font-weight:600;';
    label.textContent = 'Traduciendo la interfaz…';
    var sub = document.createElement('div');
    sub.style.cssText = 'font-size:12px;opacity:0.7;';
    sub.textContent = 'Translating the interface…';
    var style = document.createElement('style');
    style.textContent = '@keyframes i18nspin{to{transform:rotate(360deg)}}';
    overlayEl.appendChild(style);
    overlayEl.appendChild(spinner);
    overlayEl.appendChild(label);
    overlayEl.appendChild(sub);
    document.body.appendChild(overlayEl);
  }
  function hideOverlay() {
    if (overlayEl && overlayEl.parentNode) overlayEl.parentNode.removeChild(overlayEl);
    overlayEl = null;
  }

  function start(lang, block) {
    state.lang = lang;
    if (block) showOverlay();
    fetch('/api/i18n/bundle?lang=' + encodeURIComponent(lang), { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : { entries: [] }; })
      .then(function(data) {
        ((data && data.entries) || []).forEach(function(pair) { state.dict.set(pair[0], pair[1]); });
      })
      .catch(function() { /* dictionary warms via /translate misses instead */ })
      .finally(function() {
        if (state.lang !== lang) { hideOverlay(); return; } // switched off while bundle loaded
        walk(document.body);
        if (block) {
          // Hold the overlay until every visible string is translated (or the
          // deadline passes — leftovers then finish via the background path).
          drain(Date.now() + 20000).finally(hideOverlay);
        }
        if (!state.observer) {
          state.observer = new MutationObserver(function(muts) {
            for (var i = 0; i < muts.length; i++) {
              var mu = muts[i];
              if (mu.type === 'characterData') applyToTextNode(mu.target);
              else if (mu.type === 'attributes') applyToAttr(mu.target, mu.attributeName);
              else if (mu.type === 'childList') {
                for (var j = 0; j < mu.addedNodes.length; j++) walk(mu.addedNodes[j]);
              }
            }
          });
          state.observer.observe(document.body, {
            subtree: true, childList: true, characterData: true,
            attributes: true, attributeFilter: ATTRS,
          });
        }
      });
  }

  // Put every node we translated back to its original text — same
  // nodeValue/setAttribute-only writes as translation itself.
  function restoreAll() {
    if (!document.body) return;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeType === 3) {
        var orig = originals.get(n);
        if (orig !== undefined) { n.nodeValue = orig; originals.delete(n); lastWritten.delete(n); }
      } else {
        var oa = originalAttrs.get(n);
        if (oa) {
          for (var a in oa) n.setAttribute(a, oa[a]);
          originalAttrs.delete(n); lastWrittenAttr.delete(n);
        }
      }
    }
  }

  function stop() {
    state.lang = null;
    hideOverlay();
    // Disconnect BEFORE restoring so the observer never sees our own reverts
    if (state.observer) { state.observer.disconnect(); state.observer = null; }
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    state.pending.clear();
    restoreAll();
  }

  // Idempotent entry point — call with the user's preference ('es', 'en',
  // null/undefined). English or empty turns the translator off. Pass
  // opts.block=true (interactive switches) to hold a progress overlay until
  // the visible page is fully translated; boot-time calls stay non-blocking.
  function setLanguage(lang, opts) {
    if (!lang || lang === 'en') { stop(); return; }
    if (lang === state.lang) return;
    stop();
    start(lang, !!(opts && opts.block));
  }

  // Self-contained boot for shells that don't already fetch preferences:
  // reads the logged-in user's saved language and applies it.
  function boot() {
    fetch('/api/auth/preferences', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : {}; })
      .then(function(prefs) { setLanguage(prefs && prefs.language); })
      .catch(function() { /* stay in English */ });
  }

  window.I18N = { setLanguage: setLanguage, boot: boot };
})();

;
/* ===== app-chat.js ===== */
// AI Chat module — full page (ChatPage) + floating quick-ask widget (ChatWidget).
// Admin-only (area 'chat'). Talks to /api/chat/* (routes/chat.js); assistant
// replies stream over SSE. Exports: window.ChatPage, window.ChatWidget.
(function () {
  var h = React.createElement;

  // ─── Markdown-lite renderer (bold, inline code, fences, tables, lists, headers) ───
  function mdInline(text, keyBase) {
    var parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map(function (p, i) {
      if (/^\*\*[^*]+\*\*$/.test(p)) return h('strong', { key: keyBase + ':' + i }, p.slice(2, -2));
      if (/^`[^`]+`$/.test(p)) return h('code', { key: keyBase + ':' + i, style: { background: C.surfaceHover, border: '1px solid ' + C.border, borderRadius: 4, padding: '0 4px', fontSize: '0.92em' } }, p.slice(1, -1));
      return p;
    });
  }

  function mdRender(text) {
    var lines = String(text || '').split('\n');
    var out = [], i = 0, key = 0;
    function tableAt(idx) { // returns rows count if a md table starts at idx
      if (!/^\s*\|.*\|\s*$/.test(lines[idx] || '')) return 0;
      if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[idx + 1] || '')) return 0;
      var n = 2;
      while (/^\s*\|.*\|\s*$/.test(lines[idx + n] || '')) n++;
      return n;
    }
    while (i < lines.length) {
      var line = lines[i];
      if (/^```/.test(line)) {
        var code = []; i++;
        while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
        i++;
        out.push(h('pre', { key: key++, style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 10, fontSize: 11.5, overflowX: 'auto', margin: '6px 0' } }, code.join('\n')));
        continue;
      }
      var tn = tableAt(i);
      if (tn) {
        var cells = function (l) { return l.replace(/^\s*\||\|\s*$/g, '').split('|').map(function (c) { return c.trim(); }); };
        var head = cells(lines[i]);
        var rows = [];
        for (var r = 2; r < tn; r++) rows.push(cells(lines[i + r]));
        out.push(h('div', { key: key++, style: { overflowX: 'auto', margin: '6px 0' } },
          h('table', { style: { borderCollapse: 'collapse', fontSize: 12, minWidth: 240 } },
            h('thead', null, h('tr', null, head.map(function (c, ci) {
              return h('th', { key: ci, style: { textAlign: 'left', padding: '4px 10px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 600, whiteSpace: 'nowrap' } }, mdInline(c, 'th' + ci));
            }))),
            h('tbody', null, rows.map(function (row, ri) {
              return h('tr', { key: ri }, row.map(function (c, ci) {
                return h('td', { key: ci, style: { padding: '3px 10px', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, mdInline(c, 'td' + ri + ':' + ci));
              }));
            }))
          )));
        i += tn;
        continue;
      }
      var mHead = /^(#{1,4})\s+(.*)$/.exec(line);
      if (mHead) {
        out.push(h('div', { key: key++, style: { fontWeight: 700, fontSize: mHead[1].length <= 2 ? 14 : 12.5, margin: '10px 0 4px' } }, mdInline(mHead[2], 'h' + key)));
        i++; continue;
      }
      if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
        var items = [];
        while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, ''));
          i++;
        }
        out.push(h('ul', { key: key++, style: { margin: '4px 0', paddingLeft: 20 } }, items.map(function (it, ii) {
          return h('li', { key: ii, style: { margin: '2px 0' } }, mdInline(it, 'li' + ii));
        })));
        continue;
      }
      if (line.trim() === '') { out.push(h('div', { key: key++, style: { height: 6 } })); i++; continue; }
      out.push(h('div', { key: key++, style: { margin: '2px 0' } }, mdInline(line, 'p' + key)));
      i++;
    }
    return out;
  }

  // ─── Shared bits ───────────────────────────────────────────────────────────
  function toolChip(t, i) {
    return h('span', { key: i, style: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.accentSoft, border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 12, padding: '2px 9px', fontSize: 10.5, marginRight: 6, marginBottom: 4 } },
      h(LIcon, { name: t.running ? 'loader-2' : 'wrench', size: 11, spin: !!t.running, color: C.accent }),
      t.label || t.name,
      t.summary ? h('span', { style: { color: C.textDim } }, '· ' + t.summary) : null);
  }

  function extractStored(m) {
    // Map a stored chat_messages row to renderable {kind, text, tools}
    var blocks = Array.isArray(m.content) ? m.content : [];
    if (m.role === 'user') {
      if (blocks.some(function (b) { return b.type === 'tool_result'; })) return null; // internal
      var text = blocks.filter(function (b) { return b.type === 'text' && !/^\[context\]/.test(b.text || ''); })
        .map(function (b) { return b.text; }).join('\n');
      return text ? { kind: 'user', text: text } : null;
    }
    var tools = blocks.filter(function (b) { return b.type === 'tool_use'; })
      .map(function (b) { return { name: b.name, label: window._chatToolLabel(b) }; });
    var atext = blocks.filter(function (b) { return b.type === 'text'; }).map(function (b) { return b.text; }).join('');
    if (!tools.length && !atext) return null;
    return { kind: 'assistant', text: atext, tools: tools, usage: m.usage, cost: m.cost };
  }

  window._chatToolLabel = function (b) {
    var inp = b.input || {};
    switch (b.name) {
      case 'run_report': return 'Report: ' + (inp.report_id || '');
      case 'days_of_stock': return 'Days of stock';
      case 'inventory_valuation': return 'Inventory valuation';
      case 'product_lookup': return 'Lookup: ' + (inp.query || '');
      case 'list_tables': return 'List tables';
      case 'describe_table': return 'Describe: ' + (inp.table || '');
      case 'sql_query': return inp.purpose ? 'SQL: ' + inp.purpose : 'SQL query';
      default: return b.name;
    }
  };

  // ─── ChatThread — message list + composer + streaming, for one conversation ─
  function ChatThread(props) {
    var convId = props.conversationId;
    var compact = props.compact;
    var _m = useState(null); var conv = _m[0], setConv = _m[1];
    var _s = useState(null); var live = _s[0], setLive = _s[1]; // {text, tools, status}
    var _i = useState(''); var input = _i[0], setInput = _i[1];
    var _b = useState(false); var busy = _b[0], setBusy = _b[1];
    var _e = useState(null); var err = _e[0], setErr = _e[1];
    var scrollRef = useRef(null);
    var abortRef = useRef(null);
    var liveRef = useRef(null);
    var loadSeqRef = useRef(0); // review M7 #105: switching conversations mid-load must not paint the old one

    function load(id) {
      var target = id || convId;
      if (!target) { setConv(null); return; }
      var seq = ++loadSeqRef.current;
      api('/chat/conversations/' + target)
        .then(function (c) { if (seq === loadSeqRef.current) setConv(c); })
        .catch(function (e) { if (seq === loadSeqRef.current) setErr(e.message); });
    }
    useEffect(function () { load(); }, [convId]);
    useEffect(function () {
      var el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });

    function updateLive(fn) {
      liveRef.current = fn(liveRef.current || { text: '', tools: [], status: null });
      setLive(Object.assign({}, liveRef.current));
    }

    async function sendMessage() {
      var text = input.trim();
      if (!text || busy) return;
      var cid = convId;
      if (!cid) {
        // Conversations are created on first send — never empty shells.
        if (!props.ensureConversation) return;
        try { cid = await props.ensureConversation(); } catch (e) { setErr(e.message); return; }
        if (!cid) return;
      }
      setInput(''); setErr(null); setBusy(true);
      liveRef.current = { text: '', tools: [], status: null, userText: text };
      setLive(Object.assign({}, liveRef.current));
      var ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        var resp = await fetch(API_BASE + '/chat/conversations/' + cid + '/message', {
          method: 'POST', credentials: 'include', signal: ctrl.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text, model: props.model }),
        });
        if (!resp.ok) {
          var eb = await resp.json().catch(function () { return {}; });
          throw new Error(eb.error || 'Request failed (' + resp.status + ')');
        }
        var reader = resp.body.getReader();
        var dec = new TextDecoder();
        var buf = '';
        while (true) {
          var chunk = await reader.read();
          if (chunk.done) break;
          buf += dec.decode(chunk.value, { stream: true });
          var idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            var frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
            frame.split('\n').forEach(function (line) {
              if (!line.startsWith('data: ')) return;
              var ev; try { ev = JSON.parse(line.slice(6)); } catch (_) { return; }
              if (ev.type === 'text') updateLive(function (l) { l.text += ev.delta; l.status = null; return l; });
              else if (ev.type === 'status') updateLive(function (l) { l.status = ev.status; return l; });
              else if (ev.type === 'tool') updateLive(function (l) { l.tools.push({ name: ev.name, label: ev.label, running: true }); l.status = null; return l; });
              else if (ev.type === 'tool_done') updateLive(function (l) {
                for (var k = l.tools.length - 1; k >= 0; k--) if (l.tools[k].name === ev.name && l.tools[k].running) { l.tools[k].running = false; l.tools[k].summary = ev.summary; break; }
                return l;
              });
              else if (ev.type === 'usage') updateLive(function (l) { l.usage = ev; return l; });
              else if (ev.type === 'error') { setErr(ev.message); }
            });
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message);
      } finally {
        abortRef.current = null;
        setBusy(false);
        liveRef.current = null;
        setLive(null);
        load(cid);
        if (props.onActivity) props.onActivity();
      }
    }

    function stop() { if (abortRef.current) abortRef.current.abort(); }

    var msgs = ((conv && conv.messages) || []).map(extractStored).filter(Boolean);
    var fs = 14; // 9-05: was 12 (widget) / 13 (page) — too small to read comfortably

    function bubble(m, i) {
      if (m.kind === 'user') {
        return h('div', { key: i, style: { display: 'flex', justifyContent: 'flex-end', margin: '8px 0' } },
          h('div', { style: { background: C.accentSoft, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 2px 12px', padding: '9px 13px', maxWidth: '82%', fontSize: fs, whiteSpace: 'pre-wrap' } }, m.text));
      }
      return h('div', { key: i, style: { margin: '8px 0' } },
        m.tools.length ? h('div', { style: { marginBottom: 4 } }, m.tools.map(toolChip)) : null,
        m.text ? h('div', { style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 12px 2px', padding: '10px 13px', maxWidth: '95%', fontSize: fs, lineHeight: 1.55 } }, mdRender(m.text)) : null);
    }

    return h('div', { style: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 } },
      h('div', { ref: scrollRef, style: { flex: 1, overflowY: 'auto', padding: compact ? '10px 12px' : '4px 4px 12px', minHeight: 0 } },
        convId && !conv ? h('div', { style: { color: C.textDim, fontSize: 14, textAlign: 'center', marginTop: 40 } }, 'Loading…') :
        msgs.length === 0 && !live ? h('div', { style: { color: C.textDim, fontSize: 14, textAlign: 'center', marginTop: 40 } },
          'Ask about sales, inventory, purchasing, shipments…', h('br'), h('span', { style: { fontSize: 12.5 } }, 'e.g. "top 10 overstocked SKUs by tied-up value"')) : null,
        msgs.map(bubble),
        live ? [
          live.userText ? bubble({ kind: 'user', text: live.userText }, 'lu') : null,
          h('div', { key: 'la', style: { margin: '8px 0' } },
            live.tools.length ? h('div', { style: { marginBottom: 4 } }, live.tools.map(toolChip)) : null,
            (live.text || live.status) ? h('div', { style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 12px 2px', padding: '10px 13px', maxWidth: '95%', fontSize: fs, lineHeight: 1.55 } },
              live.text ? mdRender(live.text) : null,
              !live.text && live.status === 'thinking' ? h('span', { style: { color: C.textDim, fontStyle: 'italic', fontSize: 13 } }, 'Thinking…') : null,
              busy ? h('span', { style: { display: 'inline-block', width: 7, height: 13, background: C.accent, marginLeft: 3, verticalAlign: 'text-bottom', opacity: 0.8 } }) : null) : null,
            live.usage ? h('div', { style: { color: C.textDim, fontSize: 11.5, marginTop: 4 } },
              '$' + Number(live.usage.cost).toFixed(3) + ' · ' + Math.round((live.usage.input_tokens + live.usage.cache_read_input_tokens + live.usage.cache_creation_input_tokens) / 1000) + 'k in / ' + Math.round(live.usage.output_tokens / 1000 * 10) / 10 + 'k out' + (live.usage.tool_calls ? ' · ' + live.usage.tool_calls + ' tools' : '')) : null)
        ] : null,
        err ? h('div', { style: { color: C.danger, fontSize: 13, margin: '8px 0' } }, err) : null),
      h('div', { style: { borderTop: '1px solid ' + C.border, padding: compact ? '8px 10px' : '10px 4px 0', display: 'flex', gap: 8, alignItems: 'flex-end' } },
        h('textarea', {
          value: input, rows: compact ? 3 : 2,
          placeholder: busy ? 'Answering…' : 'Ask a question… (Enter to send)',
          onChange: function (ev) { setInput(ev.target.value); },
          onKeyDown: function (ev) { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); sendMessage(); } },
          style: { flex: 1, resize: 'none', background: C.surface, color: C.text, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 12px', fontSize: fs, fontFamily: 'inherit', outline: 'none' },
        }),
        busy
          ? h('button', { onClick: stop, style: { background: C.dangerSoft, color: C.danger, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit' } }, 'Stop')
          : (function () {
              var canSend = !!input.trim() && (!!convId || !!props.ensureConversation);
              return h('button', { onClick: sendMessage, disabled: !canSend, style: { background: canSend ? C.accent : C.surfaceHover, color: canSend ? '#fff' : C.textDim, border: 'none', borderRadius: 10, padding: '10px 14px', cursor: canSend ? 'pointer' : 'default', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 } },
                h(LIcon, { name: 'send', size: 16, color: canSend ? '#fff' : C.textDim }), compact ? null : 'Send');
            })()));
  }

  // ─── Model picker (Opus / Haiku) ───────────────────────────────────────────
  function ModelPicker(props) {
    return h('select', {
      value: props.value,
      onChange: function (ev) { props.onChange(ev.target.value); },
      style: { background: C.surface, color: C.textMuted, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12.5, padding: '5px 8px', outline: 'none', fontFamily: 'inherit' },
    },
      h('option', { value: 'fable' }, 'Fable 5.1 (most capable)'),
      h('option', { value: 'opus' }, 'Opus 5 (smart)'),
      h('option', { value: 'haiku' }, 'Haiku (fast/cheap)'));
  }
  function modelLabel(m) { return m === 'haiku' ? 'Haiku' : m === 'fable' ? 'Fable 5.1' : 'Opus'; }

  // ─── Full page ─────────────────────────────────────────────────────────────
  function ChatPage(props) {
    var _c = useState([]); var convs = _c[0], setConvs = _c[1];
    var _sel = useState(null); var selId = _sel[0], setSelId = _sel[1];
    var _mdl = useState('opus'); var model = _mdl[0], setModel = _mdl[1];

    function loadConvs(selectFirst) {
      api('/chat/conversations').then(function (rows) {
        setConvs(rows);
        if (selectFirst && rows.length && !selId) setSelId(rows[0].id);
      }).catch(function () {});
    }
    useEffect(function () { loadConvs(true); }, []);
    useEffect(function () {
      var cur = convs.find(function (c) { return c.id === selId; });
      if (cur) setModel(cur.model || 'opus');
    }, [selId]);

    // Conversations are created on first send (never empty shells) — "+ New"
    // just clears the selection so the composer targets a fresh chat.
    function newChat() { setSelId(null); }
    async function ensureConversation() {
      var c = await api('/chat/conversations', { method: 'POST', body: JSON.stringify({ model: model }) });
      setConvs(function (prev) { return [c].concat(prev); });
      setSelId(c.id);
      return c.id;
    }
    async function delChat(id, ev) {
      ev.stopPropagation();
      if (!window.confirm('Delete this conversation?')) return;
      await api('/chat/conversations/' + id, { method: 'DELETE' });
      if (selId === id) setSelId(null);
      loadConvs(false);
    }
    function changeModel(m) {
      setModel(m);
      if (selId) api('/chat/conversations/' + selId, { method: 'PATCH', body: JSON.stringify({ model: m }) }).catch(function () {});
    }

    var totalCost = convs.reduce(function (s, c) { return s + (parseFloat(c.total_cost) || 0); }, 0);

    return h('div', { style: { display: 'flex', gap: 16, height: 'calc(100vh - 110px)', minHeight: 480 } },
      // Conversation list
      h('div', { style: { width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden' } },
        h('div', { style: { padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
          h('div', { style: { fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 } }, h(LIcon, { name: 'sparkles', size: 15, color: C.accent }), 'Ask AI'),
          h('button', { onClick: newChat, style: { background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '4px 9px', fontSize: 11.5, cursor: 'pointer' } }, '+ New')),
        h('div', { style: { flex: 1, overflowY: 'auto' } },
          convs.map(function (c) {
            var sel = c.id === selId;
            return h('div', {
              key: c.id, onClick: function () { setSelId(c.id); },
              style: { padding: '8px 12px', cursor: 'pointer', background: sel ? C.accentSoft : 'transparent', borderLeft: '3px solid ' + (sel ? C.accent : 'transparent'), position: 'relative' },
            },
              h('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 18 } }, c.title || 'New chat'),
              h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } },
                modelLabel(c.model) + ' · ' + c.message_count + ' msgs' + (parseFloat(c.total_cost) ? ' · $' + parseFloat(c.total_cost).toFixed(2) : '')),
              h('span', { onClick: function (ev) { delChat(c.id, ev); }, style: { position: 'absolute', right: 8, top: 9, opacity: 0.55, cursor: 'pointer' } }, h(LIcon, { name: 'x', size: 12, color: C.textMuted })));
          })),
        totalCost > 0 ? h('div', { style: { padding: '7px 12px', borderTop: '1px solid ' + C.border, fontSize: 10.5, color: C.textDim } }, 'Total spend: $' + totalCost.toFixed(2)) : null),
      // Thread
      h('div', { style: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: '12px 16px' } },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 8, borderBottom: '1px solid ' + C.border } },
          h('div', { style: { fontSize: 12.5, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            (convs.find(function (c) { return c.id === selId; }) || {}).title || 'Ask questions over live app data'),
          h(ModelPicker, { value: model, onChange: changeModel })),
        h(ChatThread, { conversationId: selId, model: model, ensureConversation: ensureConversation, onActivity: function () { loadConvs(false); } })));
  }

  // ─── Floating widget ───────────────────────────────────────────────────────
  function ChatWidget(props) {
    var _o = useState(false); var open = _o[0], setOpen = _o[1];
    var _c = useState(null); var convId = _c[0], setConvId = _c[1];
    var _mdl = useState('opus'); var model = _mdl[0], setModel = _mdl[1];
    var _v = useState('thread'); var view = _v[0], setView = _v[1]; // 'thread' | 'list'
    var _h = useState([]); var hist = _h[0], setHist = _h[1];
    var restoredRef = useRef(false);

    function toggle() {
      var next = !open;
      setOpen(next);
      if (next && !restoredRef.current) {
        restoredRef.current = true;
        var saved = parseInt(localStorage.getItem('chat_widget_conv') || '', 10);
        if (saved) api('/chat/conversations/' + saved)
          .then(function (c) { setConvId(c.id); setModel(c.model || 'opus'); })
          .catch(function () { localStorage.removeItem('chat_widget_conv'); });
      }
    }
    async function ensureConversation() {
      var nc = await api('/chat/conversations', { method: 'POST', body: JSON.stringify({ model: model }) });
      localStorage.setItem('chat_widget_conv', String(nc.id));
      setConvId(nc.id);
      return nc.id;
    }
    function fresh() { setConvId(null); localStorage.removeItem('chat_widget_conv'); setView('thread'); }
    function openHistory() {
      setView('list');
      api('/chat/conversations').then(setHist).catch(function () { setHist([]); });
    }
    function pick(c) {
      setConvId(c.id); setModel(c.model || 'opus');
      localStorage.setItem('chat_widget_conv', String(c.id));
      setView('thread');
    }
    function changeModel(m) {
      setModel(m);
      if (convId) api('/chat/conversations/' + convId, { method: 'PATCH', body: JSON.stringify({ model: m }) }).catch(function () {});
    }

    var header = view === 'list'
      ? h('div', { style: { padding: '11px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
          h('span', { onClick: function () { setView('thread'); }, title: 'Back', style: { cursor: 'pointer', display: 'flex' } }, h(LIcon, { name: 'chevron-left', size: 18, color: C.textMuted })),
          h('div', { style: { fontWeight: 700, fontSize: 14.5, flex: 1 } }, 'Conversations'),
          h('span', { onClick: fresh, title: 'New conversation', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'plus', size: 18, color: C.textMuted })),
          h('span', { onClick: toggle, style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'x', size: 18, color: C.textMuted })))
      : h('div', { style: { padding: '11px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
          h('span', { onClick: openHistory, title: 'All conversations', style: { cursor: 'pointer', display: 'flex' } }, h(LIcon, { name: 'history', size: 18, color: C.textMuted })),
          h(LIcon, { name: 'sparkles', size: 18, color: C.accent }),
          h('div', { style: { fontWeight: 700, fontSize: 14.5, flex: 1 } }, 'Ask AI'),
          h(ModelPicker, { value: model, onChange: changeModel }),
          h('span', { onClick: fresh, title: 'New conversation', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'plus', size: 18, color: C.textMuted })),
          props.onOpenFull ? h('span', { onClick: function () { setOpen(false); props.onOpenFull(); }, title: 'Open full page', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'maximize-2', size: 14, color: C.textMuted })) : null,
          h('span', { onClick: toggle, style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'x', size: 18, color: C.textMuted })));

    var listBody = h('div', { style: { flex: 1, overflowY: 'auto', minHeight: 0 } },
      hist.length === 0 ? h('div', { style: { color: C.textDim, fontSize: 13.5, textAlign: 'center', marginTop: 30 } }, 'No conversations yet') : null,
      hist.map(function (c) {
        return h('div', { key: c.id, onClick: function () { pick(c); }, style: { padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid ' + C.border, background: c.id === convId ? C.accentSoft : 'transparent' } },
          h('div', { style: { fontSize: 13.5, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, c.title || 'New chat'),
          h('div', { style: { fontSize: 11.5, color: C.textDim, marginTop: 3 } },
            modelLabel(c.model) + ' · ' + c.message_count + ' msgs · ' + new Date(c.updated_at).toLocaleDateString() + (parseFloat(c.total_cost) ? ' · $' + parseFloat(c.total_cost).toFixed(2) : '')));
      }));

    return h(React.Fragment, null,
      open ? h('div', { style: { position: 'fixed', bottom: 92, right: 20, width: 470, height: 'min(720px, 82vh)', background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.45)', zIndex: 940, display: 'flex', flexDirection: 'column', overflow: 'hidden' } },
        header,
        view === 'list' ? listBody :
        h('div', { style: { flex: 1, minHeight: 0 } },
          h(ChatThread, { conversationId: convId, model: model, compact: true, ensureConversation: ensureConversation }))) : null,
      h('button', {
        onClick: toggle, title: 'Ask AI',
        style: { position: 'fixed', bottom: 20, right: 20, width: 54, height: 54, borderRadius: 27, background: C.accent, border: 'none', cursor: 'pointer', zIndex: 940, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(79,110,247,0.45)' } },
        h(LIcon, { name: open ? 'chevron-down' : 'sparkles', size: 24, color: '#fff' })));
  }

  window.ChatPage = ChatPage;
  window.ChatWidget = ChatWidget;
})();

;
/* ===== app-ship-maps.js ===== */
// GENERATED FILE — do not hand-edit. Rebuilt by scratchpad maps/gen.js
// (sources: us-atlas@3, world-atlas@2, click-that-hood geojson — all
// Natural Earth derived, public domain). SVG path data for the Insights
// Regions choropleths; each map: { w, h, regions: { CODE: { n, d } } }.
var SHIP_MAP_DATA = {"us":{"w":975,"h":610,"regions":{"AL":{"n":"Alabama","d":"M648,492L648,492L648,492ZM642,384L642,384L666,382L689,380L702,427L710,440L707,443L706,450L709,457L708,465L711,469L694,471L661,474L660,477L665,481L665,485L666,486L664,490L658,492L659,490L656,488L655,482L653,482L652,490L646,489L642,455L644,386Z"},"AK":{"n":"Alaska","d":"M104,552L104,552L104,552ZM99,570L103,565L105,569ZM95,582L95,582L95,582ZM93,575L99,571L103,571L105,573L99,578L95,580ZM93,583L93,583L93,583ZM89,588L89,588L89,588ZM72,594L72,594L72,594ZM71,592L71,592L71,592ZM68,593L68,593L68,593ZM66,590L66,590L66,590ZM68,564L68,564L68,564ZM61,591L61,591L61,591ZM58,592L58,592L58,592ZM66,524L66,524L66,524ZM56,596L56,596L56,596ZM45,597L45,597L45,597ZM43,595L43,595L43,595ZM41,596L41,596L41,596ZM44,547L50,547L51,551L48,552ZM31,601L34,600L38,596L40,598L37,601ZM51,505L59,501L66,500L65,504L71,505L74,504L71,500L73,498L67,496L66,492L59,484L62,481L67,481L70,479L72,475L75,471L78,471L82,468L87,468L89,465L94,466L95,469L101,469L101,470L104,472L109,471L114,473L123,474L126,472L133,474L147,547L151,547L155,545L155,547L162,552L164,555L167,552L167,548L169,546L176,552L181,554L193,565L200,567L203,573L201,578L198,575L193,574L192,571L189,570L190,568L188,565L183,563L182,560L178,557L176,557L171,551L175,558L173,557L168,559L160,554L151,551L141,550L135,552L126,547L124,545L119,547L121,553L114,554L109,560L105,560L107,557L105,556L107,552L107,549L112,544L109,544L106,546L102,553L100,557L96,560L96,562L100,564L96,567L96,569L90,572L86,576L86,578L82,579L77,586L71,586L67,589L63,589L61,591L56,592L53,594L47,595L49,592L57,590L62,585L66,585L75,579L81,573L82,566L84,563L80,565L78,563L76,566L71,561L65,564L64,558L66,557L64,551L58,553L53,547L54,545L51,538L54,534L61,526L64,528L67,525L71,525L73,523L72,517L73,514L68,517L59,516L55,514L54,509L56,509ZM25,603L29,599L31,600ZM28,573L28,573L28,573ZM20,602L20,602L20,602ZM27,568L27,568L27,568ZM16,603L16,603L16,603ZM34,518L39,518L45,522L41,524L39,521ZM7,603L7,603L7,603ZM23,540L23,540L23,540ZM0,602L0,602L0,602ZM-6,601L-1,601L-2,603ZM-10,599L-10,599L-10,599ZM-11,601L-11,601L-11,601ZM-15,601L-13,599L-11,601ZM-18,600L-18,600L-18,600ZM-20,597L-20,597L-20,597ZM-30,593L-30,593L-30,593ZM-35,593L-35,593L-35,593ZM-41,589L-41,589L-41,589ZM-56,577L-56,577L-56,577ZM-58,571L-58,571L-58,571ZM197,577L197,577L197,577ZM196,577L196,577L196,577ZM195,575L195,575L195,575ZM188,570L188,570L188,570ZM187,569L187,569L187,569ZM186,577L186,577L186,577ZM185,573L187,570L194,576L196,580L190,578ZM183,574L183,574L183,574ZM180,567L182,565L185,564L189,567L184,569L183,572ZM175,556L178,557L181,563L180,566L179,563ZM174,565L177,563L181,573L176,568ZM173,566L173,566L173,566ZM169,561L172,558L175,559L177,563L174,562L174,565ZM121,555L121,555L121,555ZM120,551L120,551L120,551Z"},"AZ":{"n":"Arizona","d":"M145,407L149,407L151,404L150,401L147,400L148,392L152,389L154,380L157,377L163,374L160,370L158,363L156,359L157,356L159,353L158,345L160,331L167,331L170,335L173,331L176,313L203,318L239,324L265,328L247,456L208,450L172,429L142,412Z"},"CO":{"n":"Colorado","d":"M277,237L301,240L333,244L361,247L395,249L394,272L390,341L372,339L349,338L315,334L265,328L268,302L268,299Z"},"FL":{"n":"Florida","d":"M815,594L815,594L815,594ZM813,596L813,596L813,596ZM807,599L807,599L807,599ZM794,604L795,602L800,598L805,600L796,604ZM790,605L790,605L790,605ZM786,605L786,605L786,605ZM777,558L777,558L777,558ZM770,606L770,606L770,606ZM720,495L720,495L720,495ZM711,469L714,475L766,471L767,476L770,476L769,466L770,465L775,466L780,466L783,476L787,486L795,500L806,514L805,517L807,522L812,529L820,544L822,549L823,554L824,575L822,574L821,580L822,583L822,587L819,589L820,586L807,590L805,588L805,585L801,578L793,574L792,575L785,563L780,559L781,555L780,552L780,556L778,557L769,546L766,543L771,533L765,530L764,532L767,534L765,537L762,533L762,527L764,517L761,512L762,510L759,505L755,505L750,500L746,498L745,495L743,494L740,491L732,487L727,487L726,492L723,492L716,497L712,498L714,501L710,499L707,500L708,497L706,494L696,488L689,486L681,486L664,490L666,486L665,485L665,481L660,477L661,474L694,471Z"},"GA":{"n":"Georgia","d":"M689,380L713,377L735,374L732,379L732,382L738,385L741,385L744,391L748,396L755,400L756,402L762,406L766,412L771,414L774,423L777,424L781,430L781,433L786,435L787,436L783,442L781,451L781,454L778,460L780,461L780,466L775,466L770,465L769,466L770,476L767,476L766,471L714,475L711,469L708,465L709,457L706,450L707,443L710,440L702,427Z"},"IN":{"n":"Indiana","d":"M640,230L642,231L647,231L652,228L686,224L686,225L688,247L693,284L691,286L694,292L688,295L683,295L684,298L681,301L680,304L677,305L676,312L674,313L669,309L666,312L666,314L662,315L661,313L657,316L655,319L651,316L647,315L639,320L638,317L642,309L647,298L646,293L643,288L645,284Z"},"KS":{"n":"Kansas","d":"M394,272L422,273L465,275L510,275L512,277L516,278L514,286L519,293L522,295L523,344L489,344L459,344L433,343L390,341Z"},"ME":{"n":"Maine","d":"M940,115L940,115L940,115ZM938,116L938,116L938,116ZM936,112L936,112L936,112ZM932,116L932,116L932,116ZM934,125L934,125L934,125ZM931,119L933,117L934,119ZM930,116L930,116L930,116ZM910,150L905,145L895,114L891,102L895,103L894,99L897,99L895,96L900,86L898,83L898,76L900,74L899,67L906,48L909,48L910,51L912,52L917,49L923,46L930,50L937,74L939,80L945,80L946,87L949,90L952,88L957,95L955,99L953,99L950,106L947,104L944,110L941,110L941,113L939,114L937,110L935,111L937,115L936,118L935,115L932,114L931,110L930,114L929,120L930,123L921,129L920,132L918,131L915,134L915,137L913,138L913,142L911,143Z"},"MA":{"n":"Massachusetts","d":"M928,187L928,187L928,187ZM918,188L921,184L925,187ZM871,168L884,165L903,161L906,156L909,155L911,159L914,158L909,166L917,171L922,178L925,177L929,175L928,171L931,177L925,180L920,183L918,179L915,185L913,186L911,183L910,182L908,181L905,176L899,178L899,178L871,184L871,183Z"},"MN":{"n":"Minnesota","d":"M479,71L511,71L511,62L516,64L518,73L520,77L531,79L532,81L536,80L540,78L548,80L551,82L553,87L555,84L558,84L559,86L563,87L566,91L571,90L577,86L578,89L588,88L592,91L596,91L590,94L582,98L573,106L570,109L559,119L560,121L556,122L556,136L551,139L548,144L548,147L551,150L550,154L550,162L549,166L554,170L557,170L559,173L564,175L565,178L570,182L572,182L576,187L576,193L549,194L516,195L491,195L491,154L487,152L484,147L489,142L489,140L489,131L486,123L485,102L481,89L481,81L481,78Z"},"NJ":{"n":"New Jersey","d":"M849,246L850,244L851,241L855,239L855,237L861,231L854,226L850,221L852,216L850,214L855,204L870,209L869,216L869,217L867,221L871,222L872,225L873,237L870,247L867,251L865,257L863,259L863,254L859,254L852,250L849,246Z"},"NC":{"n":"North Carolina","d":"M864,329L864,329L864,329ZM863,350L864,348L871,343ZM756,334L780,331L812,326L840,320L859,316L866,328L864,328L860,319L856,317L862,325L859,323L857,326L851,330L847,330L847,333L856,330L859,330L860,336L860,330L863,329L866,335L860,344L852,345L843,343L854,347L850,356L855,353L859,354L856,360L862,350L863,350L856,361L851,361L844,365L838,370L834,377L833,384L829,383L823,386L798,368L778,371L777,369L772,365L749,367L746,368L735,374L713,377L713,371L717,369L717,366L721,363L726,362L733,356L735,356L738,350L740,348L741,351L747,345L751,346L752,341L755,338Z"},"ND":{"n":"North Dakota","d":"M377,66L404,68L445,70L479,71L481,78L481,81L481,89L485,102L486,123L489,131L489,140L460,139L443,139L412,137L371,134Z"},"OK":{"n":"Oklahoma","d":"M372,339L390,341L433,343L459,344L489,344L523,344L523,355L527,381L526,421L520,418L517,416L512,413L511,415L506,414L501,416L499,415L493,418L492,420L485,415L484,417L481,414L476,419L475,414L472,417L467,413L463,416L462,413L454,409L452,411L450,409L447,409L443,407L439,407L438,404L435,401L435,403L429,403L426,399L424,398L426,354L396,352L372,351Z"},"PA":{"n":"Pennsylvania","d":"M756,209L768,200L769,206L800,201L841,192L843,195L847,197L850,202L855,204L850,214L852,216L850,221L854,226L861,231L855,237L855,239L851,241L847,242L845,245L816,251L782,257L765,260L761,239Z"},"SD":{"n":"South Dakota","d":"M369,156L371,134L412,137L443,139L460,139L489,140L489,142L484,147L487,152L491,154L491,195L488,195L488,200L491,204L488,212L491,218L488,218L487,215L476,210L467,210L466,212L457,207L457,206L434,205L404,204L365,201Z"},"TX":{"n":"Texas","d":"M372,351L396,352L426,354L424,398L426,399L429,403L435,403L435,401L438,404L439,407L443,407L447,409L450,409L452,411L454,409L462,413L463,416L467,413L472,417L475,414L476,419L481,414L484,417L485,415L492,420L493,418L499,415L501,416L506,414L511,415L512,413L517,416L520,418L526,421L529,423L535,423L535,435L535,458L540,463L540,467L542,469L545,476L546,484L542,490L543,502L539,507L541,510L535,511L522,517L527,513L522,514L524,509L518,511L517,514L520,515L520,519L523,519L517,523L510,530L492,539L480,548L475,555L470,566L470,574L473,587L470,576L470,569L472,560L480,546L489,541L489,540L484,542L482,540L482,544L477,547L474,553L469,552L473,556L469,564L467,563L465,566L469,566L468,576L469,583L472,588L471,592L474,593L474,595L467,597L464,593L461,593L452,592L447,589L443,588L440,585L434,584L431,574L428,570L427,564L428,559L425,556L420,552L419,548L412,541L410,534L406,526L405,522L398,513L394,511L390,506L378,505L373,503L372,505L366,505L364,510L361,517L357,522L353,521L348,517L337,511L330,504L328,498L328,492L325,487L325,483L323,480L316,475L312,470L309,468L306,463L302,460L299,454L296,452L294,449L295,447L339,451L363,453L365,430L367,408L371,351Z"},"WY":{"n":"Wyoming","d":"M369,156L365,201L361,247L333,244L301,240L277,237L244,232L247,210L256,154L258,142L263,143L298,148L339,153Z"},"CT":{"n":"Connecticut","d":"M871,184L899,178L899,178L902,191L902,193L893,197L885,198L882,203L874,208L872,206L876,203L874,201Z"},"MO":{"n":"Missouri","d":"M502,262L521,262L545,261L571,260L576,265L575,268L576,273L578,280L584,286L590,290L591,297L595,296L601,298L600,303L598,309L598,313L606,321L607,320L614,326L615,332L614,333L619,340L621,340L620,349L617,349L617,351L616,351L615,352L615,354L612,358L614,360L612,363L600,364L605,357L604,352L586,353L550,355L523,355L523,344L522,295L519,293L514,286L516,278L512,277L510,275L507,270L505,268Z"},"WV":{"n":"West Virginia","d":"M733,295L738,293L740,290L739,285L741,280L744,283L746,281L745,278L746,273L750,269L754,269L758,263L760,256L759,252L761,247L759,241L761,239L765,260L782,257L784,269L790,261L792,262L794,257L800,258L805,253L811,256L814,260L813,265L803,259L803,266L797,277L794,275L790,286L784,283L784,287L780,294L780,297L776,305L777,309L773,310L767,315L761,318L758,316L755,320L750,319L747,313L741,311L734,302Z"},"IL":{"n":"Illinois","d":"M576,265L577,259L581,258L584,252L584,248L581,245L582,241L588,239L593,236L596,231L596,224L592,222L587,215L618,214L634,212L634,217L637,222L640,230L645,284L643,288L646,293L647,298L642,309L638,317L639,320L637,323L639,327L632,330L634,335L632,337L624,334L621,338L621,340L619,340L614,333L615,332L614,326L607,320L606,321L598,313L598,309L600,303L601,298L595,296L591,297L590,290L584,286L578,280L576,273L575,268Z"},"NM":{"n":"New Mexico","d":"M265,328L315,334L349,338L372,339L372,351L371,351L367,408L365,430L363,453L339,451L295,447L294,449L296,452L264,448L263,458L247,456Z"},"AR":{"n":"Arkansas","d":"M523,355L550,355L586,353L604,352L605,357L600,364L612,363L613,367L608,369L610,372L607,374L605,378L607,383L603,387L604,389L598,393L599,400L595,402L593,409L590,411L592,415L589,415L590,418L588,420L591,430L589,433L569,434L535,435L535,423L529,423L526,421L527,381Z"},"CA":{"n":"California","d":"M77,374L81,377L79,378ZM75,384L78,390L76,389ZM65,358L65,358L65,358ZM58,374L58,374L58,374ZM57,355L63,359L57,357ZM50,355L54,355L55,358L52,358ZM47,353L47,353L47,353ZM33,160L84,175L100,179L83,246L157,356L156,359L158,363L160,370L163,374L157,377L154,380L152,389L148,392L147,400L150,401L151,404L149,407L145,407L99,402L98,398L99,388L97,382L92,376L86,370L82,369L83,367L82,362L76,362L70,357L69,354L65,349L59,348L55,345L49,344L46,340L49,335L48,333L50,328L46,325L47,321L43,315L42,313L37,300L35,296L36,289L40,286L38,281L35,281L31,274L33,271L32,267L33,261L35,261L35,266L38,270L39,265L36,262L36,257L39,256L38,253L33,260L29,255L26,253L28,250L27,243L24,240L20,229L22,227L21,219L24,216L24,209L22,202L18,197L19,193L27,184L28,179L31,173L32,166L30,164Z"},"DE":{"n":"Delaware","d":"M849,246L849,246L849,246ZM845,245L847,242L851,241L850,244L849,249L853,252L855,258L860,263L861,263L864,270L853,273Z"},"DC":{"n":"District of Columbia","d":"M826,267L827,265L830,267L828,270Z"},"HI":{"n":"Hawaii","d":"M305,584L310,578L309,575L310,573L315,576L322,578L326,582L326,584L332,589L329,593L322,595L317,598L314,603L309,600L309,594ZM292,558L294,555L296,559L300,557L306,561L305,564L299,566L297,561L293,560ZM292,567L292,567L292,567ZM284,558L287,558L289,560L286,562ZM279,554L280,551L291,553L288,555ZM258,543L261,543L265,540L267,546L271,549L262,549ZM226,533L227,530L231,528L235,529L236,534L233,536L230,536ZM216,537L220,533L217,538Z"},"IA":{"n":"Iowa","d":"M491,195L516,195L549,194L576,193L577,196L579,199L577,202L580,211L586,212L587,215L592,222L596,224L596,231L593,236L588,239L582,241L581,245L584,248L584,252L581,258L577,259L576,265L571,260L545,261L521,262L502,262L500,259L501,255L499,245L499,242L496,240L497,234L494,230L491,218L488,212L491,204L488,200L488,195Z"},"KY":{"n":"Kentucky","d":"M621,340L621,338L624,334L632,337L634,335L632,330L639,327L637,323L639,320L647,315L651,316L655,319L657,316L661,313L662,315L666,314L666,312L669,309L674,313L676,312L677,305L680,304L681,301L684,298L683,295L688,295L694,292L691,286L693,284L699,283L701,285L704,290L710,290L714,293L716,291L721,292L727,288L728,292L733,295L734,302L741,311L747,313L741,320L735,325L733,330L730,332L729,334L720,339L720,339L704,341L692,341L681,343L672,343L645,346L641,345L641,350L617,351L617,349L620,349ZM616,351L615,352L616,351Z"},"MD":{"n":"Maryland","d":"M850,285L849,285L850,285ZM847,281L847,281L847,281ZM782,257L816,251L845,245L853,273L864,270L863,280L855,284L852,285L851,282L848,276L848,279L846,279L841,274L842,271L846,271L840,268L842,266L841,264L839,266L839,259L842,252L839,254L837,254L836,259L838,263L836,269L838,274L841,277L840,279L844,284L838,281L835,282L830,278L827,280L825,278L827,274L828,270L830,267L827,265L826,267L818,264L819,262L814,260L811,256L805,253L800,258L794,257L792,262L790,261L784,269Z"},"MI":{"n":"Michigan","d":"M677,131L682,132L680,133ZM663,133L663,133L663,133ZM662,139L663,135L665,138ZM661,136L661,136L661,136ZM659,142L659,142L659,142ZM657,150L657,150L657,150ZM655,153L655,153L655,153ZM646,140L646,140L646,140ZM652,228L655,224L659,216L660,205L659,198L652,184L654,180L652,175L655,168L655,160L657,159L657,155L661,154L664,148L665,151L664,156L666,158L668,153L667,146L670,143L674,142L670,138L675,133L681,135L685,135L687,138L697,140L701,147L699,149L702,154L702,162L699,166L699,169L695,171L694,177L699,180L705,171L710,168L713,169L715,172L719,186L722,191L721,200L719,202L719,199L716,199L715,207L712,210L713,213L708,222L686,225L686,224ZM601,92L610,86L614,84L610,89L603,93ZM585,123L591,120L594,117L601,116L613,106L619,101L624,100L622,102L615,112L616,117L619,112L624,112L628,114L633,121L644,121L652,115L662,114L665,112L670,111L670,118L676,118L679,116L683,115L684,121L682,123L686,124L690,126L690,124L693,124L694,128L685,127L681,129L676,127L674,131L671,128L663,127L661,130L657,132L653,131L650,135L645,139L648,134L644,135L643,138L638,140L632,153L630,151L631,148L627,147L628,140L622,137L623,135L612,134L607,131L590,128L588,124Z"},"MS":{"n":"Mississippi","d":"M644,493L644,493L644,493ZM639,493L639,493L639,493ZM635,494L635,494L635,494ZM632,494L632,494L632,494ZM589,433L591,430L588,420L590,418L589,415L592,415L590,411L593,409L595,402L599,400L598,393L604,389L603,387L642,384L644,386L642,455L646,489L644,491L636,490L627,494L625,495L622,493L621,489L618,485L619,477L583,479L584,478L582,473L585,473L584,467L586,466L587,461L593,453L590,451L595,448L591,445L593,442L590,441L591,436Z"},"MT":{"n":"Montana","d":"M198,39L233,46L265,52L303,57L340,62L377,66L371,134L369,156L339,153L298,148L263,143L258,142L256,154L253,148L250,148L250,151L243,150L239,150L237,148L235,151L228,149L227,152L225,149L224,142L219,139L220,134L219,132L215,118L208,123L205,120L206,115L209,114L208,109L213,96L209,96L205,90L205,88L201,80L195,74L197,69L193,62Z"},"NH":{"n":"New Hampshire","d":"M886,111L887,105L891,102L895,114L905,145L910,150L909,155L906,156L903,161L884,165L882,163L882,158L881,146L883,133L882,128L885,126L888,122L886,117Z"},"NY":{"n":"New York","d":"M899,196L899,196L899,196ZM897,198L897,198L897,198ZM869,217L867,221L869,217ZM816,154L816,154L816,154ZM814,154L814,154L814,154ZM768,200L776,192L780,186L777,182L775,176L784,172L796,170L800,172L802,171L809,169L813,167L817,162L819,161L819,158L817,154L819,152L815,150L815,146L820,141L822,138L829,128L832,124L858,118L859,127L862,134L861,140L864,145L864,149L867,150L871,168L871,183L871,184L874,201L876,203L872,206L874,208L874,211L877,208L882,208L883,207L891,204L895,200L900,202L896,206L886,213L872,219L869,216L870,209L855,204L850,202L847,197L843,195L841,192L800,201L769,206Z"},"OH":{"n":"Ohio","d":"M721,223L721,223L721,223ZM719,222L719,222L719,222ZM686,225L708,222L718,225L719,224L726,227L733,223L738,223L742,218L748,213L756,209L761,239L759,241L761,247L759,252L760,256L758,263L754,269L750,269L746,273L745,278L746,281L744,283L741,280L739,285L740,290L738,293L733,295L728,292L727,288L721,292L716,291L714,293L710,290L704,290L701,285L699,283L693,284L688,247Z"},"OR":{"n":"Oregon","d":"M71,70L74,74L77,74L80,80L79,87L86,91L94,90L98,91L103,93L103,95L112,94L118,96L123,95L136,96L139,95L171,102L172,107L175,109L176,112L167,124L164,129L162,130L157,137L158,140L162,143L158,150L149,191L122,185L100,179L84,175L33,160L31,157L32,149L34,146L33,140L43,126L45,121L53,103L54,99L57,92L61,81L62,75L64,74L64,69L65,71Z"},"TN":{"n":"Tennessee","d":"M612,363L614,360L612,358L615,354L615,352L616,351L617,351L641,350L641,345L645,346L672,343L681,343L692,341L704,341L720,339L720,339L756,334L755,338L752,341L751,346L747,345L741,351L740,348L738,350L735,356L733,356L726,362L721,363L717,366L717,369L713,371L713,377L689,380L666,382L642,384L642,384L603,387L607,383L605,378L607,374L610,372L608,369L613,367Z"},"UT":{"n":"Utah","d":"M198,201L200,202L247,210L244,232L277,237L268,299L268,302L265,328L239,324L203,318L176,313Z"},"VA":{"n":"Virginia","d":"M855,284L863,280L861,284L858,288L859,292L857,301L855,304L853,301L853,292ZM850,285L849,285L850,285ZM747,313L750,319L755,320L758,316L761,318L767,315L773,310L777,309L776,305L780,297L780,294L784,287L784,283L790,286L794,275L797,277L803,266L803,259L813,265L814,260L819,262L818,264L826,267L828,270L827,274L825,279L826,281L830,279L832,283L838,283L840,285L846,287L846,293L842,295L846,295L848,300L845,299L846,303L849,305L847,309L840,305L846,310L849,308L855,308L859,316L840,320L812,326L780,331L756,334L720,339L729,334L730,332L733,330L735,325L741,320Z"},"WA":{"n":"Washington","d":"M93,51L93,51L93,51ZM99,25L99,25L99,25ZM99,20L99,20L99,20ZM98,23L98,23L98,23ZM95,31L99,28L99,31L97,33ZM96,18L96,18L96,18ZM94,20L94,20L94,20ZM95,13L95,13L95,13ZM91,21L96,20L98,22L96,27L92,24ZM91,19L91,19L91,19ZM184,36L171,92L171,98L171,102L139,95L136,96L123,95L118,96L112,94L103,95L103,93L98,91L94,90L86,91L79,87L80,80L77,74L74,74L71,70L66,69L64,65L66,60L70,59L66,57L67,55L71,54L67,51L67,43L68,34L66,30L66,23L70,20L77,27L81,28L90,33L95,33L95,38L92,43L97,39L95,46L92,52L96,52L96,47L98,42L102,38L101,31L98,25L101,27L102,21L99,19L100,14L115,19L140,25Z"},"WI":{"n":"Wisconsin","d":"M642,146L642,146L642,146ZM636,150L636,150L636,150ZM584,113L584,113L584,113ZM583,116L583,116L583,116ZM581,115L581,115L581,115ZM580,117L580,117L580,117ZM580,114L580,114L580,114ZM579,113L579,113L579,113ZM579,118L579,118L579,118ZM576,114L576,114L576,114ZM560,121L568,120L576,115L580,116L577,123L580,120L585,123L588,124L590,128L607,131L612,134L623,135L622,137L628,140L627,147L631,148L630,151L632,153L632,156L629,157L626,166L628,166L633,159L636,157L638,151L642,148L641,152L639,155L635,165L635,174L633,176L632,181L633,185L631,195L632,201L634,206L634,212L618,214L587,215L586,212L580,211L577,202L579,199L577,196L576,193L576,187L572,182L570,182L565,178L564,175L559,173L557,170L554,170L549,166L550,162L550,154L551,150L548,147L548,144L551,139L556,136L556,122Z"},"NE":{"n":"Nebraska","d":"M365,201L404,204L434,205L457,206L457,207L466,212L467,210L476,210L487,215L488,218L491,218L494,230L497,234L496,240L499,242L499,245L501,255L500,259L502,262L505,268L507,270L510,275L465,275L422,273L394,272L395,249L361,247Z"},"SC":{"n":"South Carolina","d":"M735,374L746,368L749,367L772,365L777,369L778,371L798,368L823,386L820,388L816,392L813,399L814,404L811,408L808,408L807,411L803,415L801,419L792,424L793,427L790,429L786,435L781,433L781,430L777,424L774,423L771,414L766,412L762,406L756,402L755,400L748,396L744,391L741,385L738,385L732,382L732,379Z"},"ID":{"n":"Idaho","d":"M171,102L171,98L171,92L184,36L198,39L193,62L197,69L195,74L201,80L205,88L205,90L209,96L213,96L208,109L209,114L206,115L205,120L208,123L215,118L219,132L220,134L219,139L224,142L225,149L227,152L228,149L235,151L237,148L239,150L243,150L250,151L250,148L253,148L256,154L247,210L200,202L198,201L160,193L149,191L158,150L162,143L158,140L157,137L162,130L164,129L167,124L176,112L175,109L172,107Z"},"NV":{"n":"Nevada","d":"M100,179L122,185L149,191L160,193L198,201L176,313L173,331L170,335L167,331L160,331L158,345L159,353L157,356L83,246Z"},"VT":{"n":"Vermont","d":"M858,118L874,114L886,111L886,117L888,122L885,126L882,128L883,133L881,146L882,158L882,163L884,165L871,168L867,150L864,149L864,145L861,140L862,134L859,127Z"},"LA":{"n":"Louisiana","d":"M638,497L638,497L638,497ZM629,498L631,495L632,498ZM624,510L624,510L624,510ZM610,521L610,521L610,521ZM606,521L606,521L606,521ZM602,522L602,522L602,522ZM577,511L580,511L583,512L580,514ZM535,435L569,434L589,433L591,436L590,441L593,442L591,445L595,448L590,451L593,453L587,461L586,466L584,467L585,473L582,473L584,478L583,479L619,477L618,485L621,489L622,493L625,495L619,499L619,501L624,502L626,498L628,501L627,507L623,508L623,510L626,513L632,514L635,518L634,522L632,520L629,524L631,519L624,516L620,515L613,521L608,515L601,523L598,520L590,517L592,516L590,513L588,514L584,508L579,509L579,506L574,508L575,511L571,513L565,512L554,508L548,508L541,510L539,507L543,502L542,490L546,484L545,476L542,469L540,467L540,463L535,458Z"},"RI":{"n":"Rhode Island","d":"M911,183L913,186L909,188ZM908,188L908,188L908,188ZM907,196L907,196L907,196ZM902,193L902,191L899,178L905,176L908,181L910,182L907,181L908,188L908,191Z"}}},"ca":{"w":600,"h":540,"regions":{"QC":{"n":"Quebec","d":"M370,341L370,346L370,343L369,343L368,348L367,348L367,345L366,345L365,349L364,348L366,343L367,344L367,339L370,340ZM505,374L510,373L514,373L517,373L520,373L523,374L522,375L519,377L512,379L507,379L504,377L499,378L498,377L500,375ZM538,331L537,330L535,333L534,333L533,336L530,338L530,340L529,343L530,345L528,348L528,351L527,353L527,355L523,358L519,361L516,364L515,363L506,367L505,368L502,370L500,370L498,371L496,372L493,374L490,375L487,377L485,378L485,379L483,380L482,380L481,383L480,385L479,387L480,391L480,394L475,397L475,399L473,401L473,402L470,405L470,412L469,415L469,420L468,422L468,424L466,426L466,430L463,435L465,434L468,431L469,426L470,425L472,415L474,409L476,406L481,399L484,396L487,393L489,391L494,388L499,386L502,386L506,387L507,388L503,388L508,390L508,392L505,395L506,397L503,401L498,401L497,401L497,403L493,404L491,407L488,410L485,409L482,411L482,412L480,413L482,417L481,419L478,422L477,421L476,421L473,433L474,436L474,437L474,441L475,443L473,449L473,453L472,452L471,453L469,453L469,457L448,465L442,467L444,464L443,463L442,459L438,460L433,463L431,466L428,465L426,466L423,465L421,462L419,464L417,463L416,462L412,460L403,461L400,459L398,456L394,453L394,451L392,449L392,448L382,407L382,406L380,401L381,398L382,399L384,399L386,402L386,399L386,397L383,396L385,395L384,394L386,391L385,388L385,386L382,384L380,381L380,378L378,375L379,374L376,370L377,369L374,367L372,365L371,364L370,362L378,356L381,352L384,347L384,346L386,340L386,337L383,328L379,322L376,318L372,316L370,316L365,313L366,313L365,310L366,310L367,307L368,303L367,300L369,298L368,297L368,295L366,295L367,294L365,291L365,290L363,289L364,287L362,287L361,288L362,284L362,282L361,279L361,278L360,276L359,276L357,273L356,270L357,269L359,265L362,265L370,266L371,265L374,265L377,264L380,260L381,261L383,261L384,263L387,262L388,264L390,264L390,266L394,266L395,267L393,268L394,269L395,268L396,271L400,270L403,271L405,269L406,271L407,270L408,268L410,270L409,272L409,274L411,277L412,279L412,280L408,281L410,282L412,281L413,281L414,285L416,288L417,287L418,287L417,289L419,292L418,294L417,292L416,291L417,293L415,295L417,295L418,296L419,292L423,290L425,290L427,292L428,293L429,298L428,293L430,292L431,295L430,292L433,296L433,294L435,290L437,289L436,287L437,284L441,286L439,284L439,281L441,279L440,280L438,278L440,277L438,275L438,274L436,272L438,270L437,265L437,264L439,263L437,265L440,266L438,267L440,268L440,269L442,273L443,273L444,272L444,274L447,277L444,278L445,279L448,278L450,279L451,277L453,277L451,280L452,281L453,281L452,283L452,287L453,287L456,289L456,289L459,289L459,291L460,293L462,300L461,301L464,303L463,304L464,306L466,306L466,308L469,306L469,309L473,311L474,311L475,312L474,316L476,318L476,322L474,323L471,323L468,325L467,326L464,327L463,328L461,326L458,326L454,325L454,327L457,330L456,331L453,330L450,330L453,333L454,335L451,335L453,337L454,339L453,341L457,344L457,346L460,345L461,347L462,347L463,349L463,352L465,354L467,354L467,353L466,350L467,349L469,350L469,352L471,355L471,357L473,358L472,356L475,358L478,357L478,355L481,354L483,355L484,356L486,356L486,357L488,359L489,356L488,355L487,351L488,351L487,348L485,347L483,343L485,342L484,340L485,339L487,340L487,342L490,343L487,345L486,347L488,349L491,349L491,351L533,326L534,325Z"},"NL":{"n":"Newfoundland and Labrador","d":"M439,263L440,264L441,267L443,266L444,269L448,270L447,272L450,271L450,273L451,271L452,273L449,276L452,274L452,273L454,273L453,275L456,275L457,277L456,280L458,279L460,277L462,280L459,282L462,280L464,281L463,282L466,282L467,283L469,284L468,287L470,286L471,289L473,289L474,288L475,292L474,293L476,294L471,295L476,295L477,295L475,297L477,297L477,298L476,299L477,299L481,297L482,299L481,300L484,299L484,300L487,299L489,301L488,299L490,299L489,300L493,302L492,302L492,308L494,303L494,304L496,301L497,303L499,302L499,305L500,300L501,300L504,303L508,301L511,300L513,300L514,299L515,300L515,303L512,305L512,306L509,310L506,314L510,309L511,307L511,310L509,311L507,315L505,317L503,319L505,321L503,321L499,321L499,322L502,321L506,321L505,324L507,323L507,320L508,319L509,316L511,315L510,314L510,312L514,307L511,307L512,306L517,304L521,305L522,307L521,310L522,309L523,311L523,309L523,307L526,304L526,304L525,306L528,305L533,306L535,307L534,309L536,311L536,312L537,313L537,315L539,314L541,315L540,316L542,316L542,320L541,323L539,327L539,330L538,331L534,325L533,326L491,351L491,349L488,349L486,347L487,345L490,343L487,342L487,340L485,339L484,340L485,342L483,343L485,347L487,348L488,351L487,351L488,355L489,356L488,359L486,357L486,356L484,356L483,355L481,354L478,355L478,357L475,358L472,356L473,358L471,357L471,355L469,352L469,350L467,349L466,350L467,353L467,354L465,354L463,352L463,349L462,347L461,347L460,345L457,346L457,344L453,341L454,339L453,337L451,335L454,335L453,333L450,330L453,330L456,331L457,330L454,327L454,325L458,326L461,326L463,328L464,327L467,326L468,325L471,323L474,323L476,322L476,318L474,316L475,312L474,311L473,311L469,309L469,306L466,308L466,306L464,306L463,304L464,303L461,301L462,300L460,293L459,291L459,289L456,289L456,289L453,287L452,287L452,283L453,281L452,281L451,280L453,277L451,277L450,279L448,278L445,279L444,278L447,277L444,274L444,272L443,273L442,273L440,269L440,268L438,267L440,266L437,265ZM550,331L550,343L553,349L553,339L555,340L555,342L556,340L558,340L559,338L559,339L557,343L559,343L559,345L562,345L562,343L564,343L564,341L566,344L569,342L568,338L572,338L572,336L578,336L578,338L577,340L579,344L581,343L580,345L581,345L583,343L583,342L585,342L584,339L586,340L586,343L585,344L584,348L582,348L583,350L586,348L587,353L590,353L589,350L588,347L590,344L591,349L594,352L594,350L593,347L597,349L597,352L600,357L600,360L597,360L597,363L596,362L595,360L594,359L593,357L592,359L592,363L590,362L589,358L589,356L588,356L585,353L584,354L585,360L582,361L582,364L583,366L583,368L581,369L580,371L578,371L578,370L580,367L580,365L579,362L581,361L581,358L579,361L576,360L578,363L576,364L574,366L575,364L573,364L572,362L571,366L568,368L568,369L566,370L564,371L563,371L561,373L558,374L551,380L549,380L549,379L547,378L548,373L549,367L547,367L544,371L544,370L545,366L545,368L547,367L546,362L547,359L549,358L548,357L546,358L545,357L545,354L544,346L542,340L541,339L543,337L542,335L542,332L542,330L544,325L545,323L546,325L548,322L549,325L545,327L547,328L549,327Z"},"BC":{"n":"British Columbia","d":"M1,308L1,306L4,302L7,304L6,305L9,306L8,309L7,309L3,309L4,310L7,310L8,309L9,307L10,308L13,308L11,309L7,312L6,315L5,316L1,315L0,313L1,311L2,312L1,309ZM20,314L20,315L19,316L17,314ZM20,326L19,326L19,320L19,316L21,317L22,324ZM17,318L19,322L18,325L17,325L16,321L16,318ZM22,335L21,335L23,332L20,334L20,332L21,329L22,329L23,327L24,327L25,330L24,333ZM3,330L1,330L1,325L0,320L0,317L2,318L0,316L0,314L3,316L6,316L6,320L3,320L2,322L2,324L3,326L2,327L4,327L2,328ZM19,331L20,334L19,336L18,332ZM26,344L24,346L22,346L25,343L29,343L29,344ZM23,344L20,347L19,345L21,344ZM15,371L15,369L16,368L15,367L14,368L13,366L11,365L13,364L12,362L13,361L16,362L17,362L14,360L16,362L12,361L11,359L12,356L15,356L18,360L19,363L22,366L24,367L26,370L30,372L29,378L30,381L29,381L29,384L33,388L34,392L34,393L35,396L34,399L35,398L34,401L34,401L32,402L30,400L23,391L23,390L26,389L23,388L23,387L21,388L20,385L21,383L19,382L20,380L18,380L17,376L19,376L18,375L18,373ZM18,372L18,373L17,376L16,375L16,372ZM16,222L35,237L41,241L87,268L93,271L114,281L85,351L86,354L85,353L85,356L87,357L87,360L89,361L90,360L91,362L90,365L91,365L92,368L91,372L93,371L94,373L93,374L94,376L96,377L97,381L97,383L100,383L99,385L101,388L101,389L102,392L103,394L104,396L103,396L105,399L105,401L107,401L108,404L107,411L105,414L107,419L108,420L108,422L93,417L85,415L71,409L56,403L42,397L41,395L39,395L40,391L41,388L39,388L38,390L36,387L35,385L38,383L35,383L33,382L32,379L35,376L35,375L32,374L33,372L35,371L36,370L35,369L35,371L32,372L31,371L28,370L28,369L27,369L25,367L25,366L28,365L28,364L25,363L26,362L24,362L23,360L21,361L19,357L19,353L23,355L19,352L22,352L23,351L25,351L29,353L26,351L23,350L22,351L20,350L21,348L22,346L24,346L26,345L29,344L30,342L32,341L33,340L30,341L30,342L24,343L24,339L22,340L26,336L24,334L25,330L24,328L25,324L27,328L27,324L31,321L27,322L24,323L22,325L22,320L21,316L22,314L21,312L22,311L21,310L23,308L24,312L24,307L25,308L26,306L32,303L32,302L28,305L28,304L30,302L32,297L34,296L35,293L33,292L33,291L32,288L31,287L30,283L28,282L29,280L28,278L30,277L28,275L30,274L31,262L31,259L32,257L32,253L31,250L31,247L30,244L30,242L32,240L31,237L25,235L21,239L20,238L15,238L17,234L16,225L16,223Z"},"NU":{"n":"Nunavut","d":"M225,77L227,80L226,82L224,81ZM214,124L216,115L219,117L220,114L217,111L217,111L220,109L218,107L219,104L222,101L223,100L224,102L223,103L223,106L224,108L222,110L224,110L223,112L226,113L228,111L230,113L230,117L227,124L223,125L222,124L220,124L218,122L215,124ZM207,152L207,152L207,151L208,150L208,149L208,149L209,146L211,149L213,152L212,155L213,160L212,162L214,164L216,161L215,158L215,156L215,146L217,143L218,144L220,146L221,144L222,148L224,148L225,152L225,160L225,163L226,167L226,170L224,172L226,175L226,177L229,181L229,179L230,181L234,184L235,184L235,186L238,186L238,190L237,192L236,192L235,189L234,192L233,189L231,190L230,192L229,193L227,190L226,191L228,192L228,196L231,193L232,193L232,195L233,197L233,199L230,200L229,201L226,201L221,199L220,200L218,198L219,197L217,195L213,194L214,192L213,190L211,192L211,194L209,195L206,196L204,195L201,197L198,198L190,197L189,197L182,196L183,196L182,194L181,191L183,189L183,187L180,185L177,184L174,183L172,181L172,179L171,177L173,178L171,174L172,173L189,179L188,180L190,180L190,179L200,182ZM264,310L234,309L220,308L226,257L189,243L184,234L175,231L149,190L156,173L158,176L162,179L165,183L167,186L172,188L171,186L174,187L178,192L178,196L178,198L174,196L174,197L172,197L172,199L170,199L171,201L173,202L175,203L183,206L183,206L187,207L190,207L194,205L196,209L198,210L198,214L200,211L200,214L200,213L202,216L202,219L199,218L200,220L202,225L202,219L203,222L204,219L205,218L204,217L203,214L204,214L203,211L203,209L205,208L205,207L209,207L211,204L215,204L215,201L211,202L210,205L209,204L208,205L205,203L206,206L203,205L202,205L202,203L201,203L204,200L208,199L211,199L214,197L215,198L216,201L216,204L219,207L219,210L224,210L226,212L227,213L229,215L230,214L233,215L236,214L242,215L244,216L245,215L247,215L245,213L246,212L248,216L250,218L253,216L250,213L248,215L246,211L247,211L246,208L247,208L248,209L249,209L249,206L252,207L253,209L253,210L256,209L254,212L256,213L256,211L258,210L257,215L257,217L256,219L257,220L257,223L259,221L260,221L258,223L260,223L261,226L257,226L255,224L257,227L258,228L258,230L259,229L257,226L261,227L261,222L261,219L259,217L260,212L264,212L266,210L266,208L269,205L268,206L269,203L267,201L266,204L264,204L265,201L265,197L267,197L267,199L269,196L269,195L267,196L265,196L264,193L263,194L260,191L258,191L258,189L256,188L256,185L257,182L258,181L256,179L256,175L258,172L260,173L260,171L259,169L261,168L261,165L264,164L264,167L267,167L267,169L269,171L271,175L270,179L272,180L274,181L275,184L276,184L277,187L275,187L274,186L272,188L275,188L271,192L273,192L276,195L277,192L279,193L279,194L281,194L282,195L279,197L281,199L282,202L282,207L283,208L284,209L285,206L285,205L286,202L285,200L286,197L288,196L289,198L292,200L293,202L295,207L294,209L292,208L292,211L294,216L297,219L298,222L300,220L301,218L302,219L302,213L304,210L304,205L303,203L304,201L307,201L305,198L307,197L304,195L303,194L302,189L306,187L310,188L314,187L315,190L312,190L317,191L318,192L321,192L321,194L319,197L322,196L323,199L320,202L317,202L317,202L319,204L319,205L321,206L321,208L325,211L326,213L326,219L324,220L324,223L322,223L322,225L320,225L319,228L317,226L315,226L315,223L314,224L313,223L312,222L309,222L313,224L314,226L316,227L317,230L316,230L313,228L313,229L314,231L309,230L308,227L306,228L304,228L302,229L303,231L307,232L307,234L305,236L305,238L302,242L301,243L298,244L295,241L293,240L289,237L285,238L280,237L280,238L282,239L282,238L287,239L290,241L292,244L297,245L299,244L303,244L303,246L302,250L301,251L301,253L300,253L299,257L296,260L293,260L294,261L289,259L290,261L287,261L289,263L288,265L284,266L282,265L276,264L271,261L269,262L271,264L271,262L278,265L276,267L280,265L281,266L286,269L286,272L286,274L284,274L282,276L278,275L276,276L277,277L279,278L279,279L276,278L276,280L275,280L276,284L275,282L274,283L274,285L272,287L270,286L272,289L270,290L268,293L269,294L267,294L268,295L267,299L266,300L265,304L264,304L265,306ZM417,188L417,189L414,189L417,190L418,192L417,193L415,192L413,193L415,194L414,195L417,195L416,197L418,199L416,201L417,201L417,202L414,203L415,204L414,206L416,207L417,209L418,212L415,211L415,209L414,211L411,209L412,212L411,210L408,210L404,207L404,205L405,202L405,202L403,204L401,206L400,204L399,204L397,203L395,203L394,201L392,202L394,204L393,205L391,204L390,204L391,206L395,207L396,209L392,211L391,209L389,208L388,208L390,209L393,212L393,211L394,215L395,213L396,212L399,214L400,217L400,216L402,217L406,218L407,220L407,217L409,218L410,221L413,220L413,223L415,224L418,224L417,226L419,227L422,231L421,232L418,229L419,232L422,234L422,236L424,236L421,237L424,239L423,241L421,240L421,239L419,239L419,238L416,239L413,239L407,237L405,235L404,235L406,237L402,237L400,236L398,237L399,238L404,240L405,241L409,241L412,243L413,244L414,243L417,245L417,246L421,247L421,248L423,250L420,251L419,251L414,251L407,252L404,252L400,250L401,248L398,249L396,250L395,250L391,250L387,249L386,247L384,246L386,246L387,244L386,244L384,242L384,244L382,243L381,241L381,243L379,242L379,241L377,241L377,240L373,238L371,235L371,240L369,237L367,237L365,236L364,236L367,238L367,240L364,240L364,241L360,239L362,241L362,242L359,243L360,243L357,245L355,245L354,246L350,245L349,242L349,240L348,239L348,237L351,235L350,234L352,232L354,231L358,232L361,235L360,235L361,237L362,235L360,232L357,231L361,231L361,229L364,229L365,226L369,226L366,223L364,222L362,220L362,218L363,216L365,211L365,207L367,205L367,203L366,203L363,198L361,197L359,194L356,193L355,189L353,189L354,191L353,192L349,190L350,188L348,189L346,188L346,190L343,193L342,193L342,191L341,189L344,189L345,187L345,186L343,184L341,185L339,183L337,183L338,182L337,181L334,181L333,177L332,176L330,177L330,176L328,176L326,174L325,176L327,176L329,178L330,182L326,183L325,182L317,181L319,182L322,185L321,186L315,182L312,181L317,184L316,186L312,184L309,185L305,185L301,185L303,186L301,186L298,185L295,181L294,182L295,183L291,183L288,182L284,177L286,175L288,175L289,176L293,175L293,175L291,173L290,172L289,173L283,172L282,172L281,169L282,167L281,165L281,163L283,162L281,161L281,160L282,154L284,147L286,143L288,141L291,140L296,139L297,140L295,143L294,147L292,152L293,154L295,157L294,159L295,162L296,165L299,168L301,168L302,170L300,170L299,172L302,171L302,174L303,174L302,168L303,168L302,165L300,166L298,163L296,162L298,161L298,159L300,159L303,161L302,159L300,158L300,156L299,157L297,156L296,154L296,150L297,150L302,152L298,150L296,149L296,148L302,148L297,147L297,146L304,139L308,138L310,140L311,144L313,145L314,147L316,149L315,151L316,154L314,156L317,157L318,153L319,154L320,152L322,154L321,157L324,158L322,157L322,153L327,153L322,151L322,149L325,147L328,146L328,147L334,147L336,150L335,151L333,153L332,156L334,152L336,151L339,151L340,154L338,156L339,156L339,159L340,156L341,154L343,154L342,157L344,156L344,157L347,159L347,153L351,154L354,156L354,159L352,159L351,162L352,163L355,161L356,157L358,159L358,161L357,164L358,169L357,163L360,158L361,160L360,162L362,160L362,159L364,159L368,160L367,163L365,165L364,168L366,165L368,164L368,168L369,167L370,164L369,163L370,162L371,162L375,164L376,167L373,168L372,170L371,170L369,173L371,171L374,171L377,170L380,170L381,172L377,174L374,173L375,174L374,175L378,176L377,177L378,179L375,179L374,180L378,180L377,181L378,182L383,181L384,182L382,182L385,182L386,185L386,183L388,183L390,184L393,182L394,181L395,181L395,184L397,183L400,184L402,187L400,190L405,187L405,185L407,186L406,187L407,190L409,188L410,192L409,188L411,188L411,186L412,185L414,186ZM305,17L307,16L307,18L305,22L307,21L309,17L310,16L315,18L318,19L319,20L319,24L317,29L315,29L316,31L313,33L315,33L312,39L316,33L318,31L319,33L317,36L317,39L315,46L316,48L314,50L315,51L311,55L314,53L315,55L315,57L313,60L312,60L311,58L308,58L307,59L310,59L312,62L308,65L306,65L307,66L302,68L304,68L310,67L311,69L308,69L307,68L302,70L305,69L307,70L303,71L303,75L304,72L306,71L308,71L308,72L311,72L312,73L312,76L310,76L312,78L311,80L308,81L312,81L312,83L311,83L309,85L306,85L307,88L308,89L306,93L303,94L301,94L298,92L298,93L300,94L298,95L299,97L300,95L303,95L305,94L307,96L306,98L309,96L310,97L311,100L310,103L309,102L308,106L307,106L305,108L303,108L304,106L302,104L301,105L300,105L300,106L298,107L295,107L293,105L294,107L292,107L293,109L291,109L288,109L287,109L284,108L284,110L282,109L281,104L280,109L278,107L278,104L281,101L285,98L282,97L282,95L280,93L281,91L283,90L285,91L286,92L287,95L291,96L291,95L294,95L293,94L294,93L295,89L294,86L293,92L292,94L290,94L288,92L288,91L287,89L288,87L288,81L288,84L287,87L285,88L285,86L284,87L282,87L281,84L283,79L286,77L290,77L294,79L291,77L291,76L295,76L294,74L293,76L288,76L287,75L287,74L290,75L288,73L287,70L285,68L282,66L281,64L281,62L281,59L286,59L291,64L292,65L295,66L297,64L294,66L293,65L292,63L288,58L293,55L293,54L298,53L293,53L296,50L300,48L297,48L295,50L294,48L296,43L295,44L294,47L294,50L291,53L287,55L287,54L289,53L289,51L286,54L285,53L286,56L283,57L280,56L281,51L286,48L287,47L281,50L280,51L278,56L276,55L274,52L275,51L279,51L282,46L281,47L279,49L273,51L272,51L272,47L275,44L271,47L270,46L273,43L270,43L269,45L268,44L269,42L272,40L273,41L273,38L275,37L279,38L280,37L277,36L277,35L279,34L278,32L279,31L281,33L283,33L289,36L284,32L284,30L284,28L287,29L284,27L286,26L286,24L287,23L288,24L290,23L293,25L290,22L292,21L295,20L296,22L299,23L296,21L297,18L301,16L302,18ZM257,103L261,102L265,104L265,103L268,106L268,107L270,107L273,106L275,108L276,109L273,109L279,111L279,112L274,113L277,114L274,114L275,116L277,114L280,117L280,119L281,121L281,118L283,120L286,118L288,120L292,120L290,119L293,117L294,117L296,114L299,115L301,113L304,113L304,115L307,114L310,117L311,118L310,121L308,122L310,122L311,121L312,123L310,125L310,127L307,128L306,130L302,129L301,126L300,130L294,131L293,129L293,131L287,133L284,133L283,128L282,129L281,132L279,133L275,130L275,132L273,131L272,130L272,126L270,125L270,123L272,120L271,117L269,115L269,113L267,110L266,112L263,112L261,112L262,111L260,110L258,106L257,106L259,105L257,104ZM307,252L308,247L307,242L307,238L309,235L309,236L311,236L312,238L311,239L312,241L313,243L315,239L317,241L317,242L319,243L321,243L323,245L326,246L329,247L331,248L331,251L333,253L331,255L334,253L336,254L336,252L341,255L337,260L331,258L329,259L329,256L328,256L325,256L326,254L323,255L322,256L323,259L321,261L320,261L319,265L316,268L314,268L313,267L313,262L309,263L307,263L305,264L304,262L305,260L309,257L308,256ZM264,50L265,51L266,49L264,49L265,47L268,49L268,51L271,56L271,57L275,58L275,61L277,63L276,60L276,58L278,59L278,61L277,62L279,63L280,65L280,70L282,68L283,70L284,68L286,72L283,75L281,81L279,77L279,79L280,82L279,83L278,82L279,84L278,87L277,87L275,82L275,84L277,87L275,85L274,88L270,87L267,84L271,83L268,82L267,83L264,78L266,75L269,75L273,74L269,75L270,73L268,72L264,74L265,73L262,74L260,72L261,71L263,70L260,69L259,66L258,63L263,64L264,62L263,62L261,63L259,61L259,60L261,60L260,58L260,57L263,57L263,54L261,54L262,51ZM241,147L239,145L241,144L242,146L243,144L241,142L242,141L246,140L248,143L252,142L252,141L255,141L256,143L254,147L252,149L250,152L250,153L252,151L254,152L255,156L257,156L257,159L256,161L257,163L256,167L256,166L253,169L250,168L251,170L249,172L248,173L246,172L245,169L244,165L243,165L241,161L240,161L239,159L238,160L237,159L237,157L235,155L234,153L235,150L237,150L238,151L241,155L242,155L244,152L244,150L243,150L243,147L242,148ZM260,149L260,143L261,143L263,144L261,141L261,140L263,139L267,138L270,138L271,140L274,139L279,141L275,149L274,154L272,156L269,155L265,155L266,156L268,159L266,162L266,165L264,164L262,165L262,159L260,156L260,152ZM249,115L248,115L247,113L248,113L246,111L248,111L246,110L245,108L247,106L249,107L250,109L251,107L252,107L255,109L254,111L255,113L255,117L253,118L255,119L255,121L254,120L253,122L254,126L253,127L248,127L245,127L244,122L245,121L248,120L247,119L250,118L242,119L239,120L238,118L242,115L243,117L242,112L243,111L241,111L242,108L244,109L244,111L246,114L247,116ZM242,200L243,199L245,199L248,196L247,194L248,194L248,191L250,190L252,191L254,195L257,197L257,200L258,198L259,203L261,203L260,205L259,204L257,207L256,208L253,206L251,206L250,205L245,203L244,203ZM240,81L241,80L240,78L240,76L238,78L239,75L237,75L238,71L241,71L244,73L244,76L246,76L248,77L248,80L249,79L251,82L250,86L252,89L251,92L249,92L247,91L247,87L244,85L243,86L243,84L238,85L237,82L238,81L241,82L241,81ZM311,137L312,137L316,138L320,136L324,137L327,141L328,141L330,143L328,145L324,145L322,145L319,148L316,147L315,143L312,143L311,139ZM351,198L351,200L352,201L353,204L353,207L351,209L347,210L345,210L343,206L343,200L345,197L348,196L350,196ZM259,129L257,128L258,125L260,121L263,120L264,120L266,123L267,129L267,131L263,132L261,130ZM332,267L334,267L334,270L332,273L329,278L328,277L326,279L326,277L324,275L326,272L326,270L327,269L328,270L330,268ZM255,80L256,80L261,83L263,86L261,87L263,89L262,91L257,92L257,91L255,89L257,89L256,87L255,87L254,83L255,83L254,80ZM226,150L225,150L223,146L222,144L220,142L222,140L227,140L229,143L229,146ZM350,278L349,281L346,279L345,275L346,271L348,270L350,272ZM364,382L367,385L368,388L364,387L359,386L358,386L359,384L362,382ZM317,233L318,234L318,235L321,236L321,237L317,236L314,232ZM267,94L266,97L263,96L260,97L259,94L261,92ZM358,197L359,197L359,199L355,201L353,199L353,198ZM234,91L235,94L235,95L236,97L235,99L233,96L233,91ZM352,256L355,256L357,259L355,260L351,258ZM332,187L334,188L333,189L332,193L330,194L330,192L331,191L331,188ZM326,184L327,183L328,184L327,186L325,186L326,188L324,187L322,185L324,184ZM276,95L277,98L276,99L273,98L273,95L273,94ZM253,138L252,141L249,141L247,141L250,139ZM234,120L235,121L235,124L233,125L231,123L233,120ZM301,209L302,212L301,214L300,213L299,210L299,207ZM394,251L397,252L395,254L394,251ZM236,105L239,108L237,109L235,109L234,105ZM312,234L314,238L313,239L311,236L311,234ZM431,250L431,253L427,252L428,250ZM254,69L252,66L251,66L251,64L252,62L254,63L254,67ZM240,110L239,112L237,113L234,112L235,110L239,110ZM360,254L360,255L359,255L356,254L356,252L357,252L360,253ZM349,191L351,193L351,195L348,194L348,191ZM333,183L334,183L332,186L330,187L331,184ZM277,104L278,106L277,108L276,108L275,105ZM338,185L340,186L338,189L337,186ZM241,115L239,117L237,116L238,115L241,114ZM419,270L420,271L419,274L418,273L418,270ZM245,90L246,93L244,93L242,92L243,90ZM260,194L261,196L259,197L258,195L259,193ZM352,159L354,159L354,160L352,163L351,161ZM240,113L240,114L236,115L235,114L239,113ZM233,202L232,205L230,203L232,201ZM239,200L240,201L239,204L238,203L238,200ZM427,239L427,241L425,242L425,240Z"},"NT":{"n":"Northwest Territories","d":"M225,77L224,81L222,81L222,80L221,81L219,79L216,80L217,78L220,77L221,78L224,76ZM223,86L221,87L222,88L221,91L216,92L214,89L215,84L221,84L224,84ZM214,124L212,124L212,125L208,127L205,127L202,126L200,123L200,121L206,120L209,121L212,118L208,118L208,118L205,118L203,118L204,115L203,114L201,117L199,117L199,115L197,117L196,115L196,114L195,114L192,112L195,110L198,111L202,109L198,110L195,108L197,106L203,107L203,107L198,105L198,104L201,102L204,104L202,102L202,101L203,100L207,100L207,101L207,104L210,104L211,106L211,107L212,109L210,110L213,111L212,115L215,115L216,115ZM207,152L200,182L190,179L190,180L188,180L189,179L172,173L171,174L173,178L171,177L170,176L171,173L173,172L178,172L183,173L191,176L195,177L194,175L193,173L191,172L190,170L187,169L184,169L178,168L177,167L173,166L172,162L171,160L174,159L178,159L181,158L184,157L177,157L174,156L174,154L176,154L176,153L174,153L174,152L172,152L173,148L176,146L178,146L177,144L177,142L181,141L183,139L195,137L195,138L196,141L194,144L191,147L196,146L196,145L198,143L201,145L204,148L203,150L200,152L201,153L203,152L205,150ZM208,149L207,147L206,145L209,146ZM220,308L206,306L189,303L172,300L114,281L93,271L93,267L93,265L92,264L93,261L94,261L94,259L93,258L92,259L89,259L87,256L86,257L81,254L84,248L83,245L82,245L81,242L81,235L79,235L77,233L79,230L80,230L79,228L79,224L81,223L80,222L80,219L83,218L83,215L84,213L82,210L83,204L82,203L80,203L81,202L81,199L79,197L82,195L83,192L86,189L86,186L88,185L88,184L86,184L86,183L84,183L83,180L85,179L85,178L87,176L87,173L88,173L88,171L89,171L80,164L81,162L83,161L85,158L85,157L93,145L95,147L95,150L96,151L96,147L98,148L99,150L99,152L100,153L100,149L104,148L107,148L109,148L111,147L113,148L113,147L114,148L118,147L119,148L120,147L123,147L124,149L126,148L126,150L126,151L120,151L117,152L113,150L112,151L109,151L105,153L104,153L107,156L106,154L107,153L112,153L114,151L115,153L117,152L116,155L120,152L126,153L125,155L129,154L130,152L131,152L134,153L132,151L132,149L134,148L135,153L134,159L134,163L136,166L136,165L139,164L139,163L140,162L142,160L145,159L142,164L144,165L140,168L142,168L143,169L146,169L147,166L149,165L154,169L156,173L149,190L175,231L184,234L189,243L226,257ZM180,123L182,121L183,122L182,124L184,123L187,123L189,124L190,127L192,135L190,136L188,136L176,140L174,143L172,144L170,143L170,144L168,146L167,149L166,151L162,151L161,150L159,151L156,152L155,151L154,149L155,144L152,138L150,137L152,134L155,132L156,132L157,128L160,129L159,126L163,123L164,123L167,120L166,119L167,114L167,113L172,114L175,115L177,115L180,120L182,121ZM204,92L205,94L203,93L203,97L202,98L200,97L199,100L198,100L196,98L199,94L196,94L196,96L194,96L194,98L192,101L192,97L191,97L191,101L189,102L187,102L188,100L187,97L185,100L184,98L182,98L181,97L183,96L182,95L184,93L187,94L189,91L192,91L194,89L196,87L197,86L201,87L202,88L204,88L203,86L205,86L207,90L204,90ZM96,146L98,143L100,143L100,145L101,143L103,144L107,143L106,145L107,146L104,147L100,149L100,153L99,152L100,148L96,147ZM196,102L196,102L192,107L191,108L189,106L192,103ZM212,86L210,82L213,82L214,85ZM208,98L206,97L207,96L210,96L209,98Z"},"NB":{"n":"New Brunswick","d":"M478,422L481,419L482,417L480,413L482,412L482,411L485,409L488,410L491,407L495,404L495,405L500,404L502,406L504,403L508,401L509,402L508,404L509,408L508,411L509,411L512,409L512,412L515,413L516,416L519,418L521,417L525,416L524,418L523,421L522,423L521,421L521,426L517,430L515,434L512,435L510,438L509,438L507,440L506,439L505,441L503,441L501,440L499,436L496,437L495,433L489,421L484,419L480,423Z"},"NS":{"n":"Nova Scotia","d":"M549,401L549,405L548,407L549,407L548,408L551,408L551,406L552,403L551,405L549,405L551,400L550,403L550,399L554,399L555,400L557,401L555,403L555,405L553,409L551,409L548,412L546,411L543,407L544,401L543,396L544,392L546,392L545,393L547,393L548,396ZM524,418L527,419L529,417L532,417L536,415L539,416L541,411L543,413L544,413L546,411L549,412L548,414L552,413L551,415L547,419L546,421L543,424L542,426L540,429L539,428L538,431L536,432L537,433L536,434L534,435L533,432L532,434L533,436L531,435L531,436L531,438L533,439L532,440L531,446L530,448L527,450L527,453L526,453L521,451L522,453L520,452L518,449L517,446L518,442L520,438L518,440L523,431L526,427L526,429L530,425L533,423L527,425L525,427L523,427L521,430L521,428L523,424L523,421Z"},"SK":{"n":"Saskatchewan","d":"M220,308L214,358L214,367L214,367L213,376L214,380L213,389L213,389L212,397L213,397L212,406L212,406L211,419L211,427L211,427L210,441L210,443L197,442L188,440L171,438L154,434L140,431L172,300L189,303L206,306Z"},"AB":{"n":"Alberta","d":"M108,422L108,420L107,419L105,414L107,411L108,404L107,401L105,401L105,399L103,396L104,396L103,394L102,392L101,389L101,388L99,385L100,383L97,383L97,381L96,377L94,376L93,374L94,373L93,371L91,372L92,368L91,365L90,365L91,362L90,360L89,361L87,360L87,357L85,356L85,353L86,354L85,351L114,281L172,300L140,431L123,427Z"},"PE":{"n":"Prince Edward Island","d":"M522,414L521,414L520,411L518,413L517,412L517,409L518,406L519,408L519,410L522,411L522,413L524,412L524,410L528,410L533,408L532,408L536,405L537,405L536,407L534,409L535,409L535,412L534,413L532,413L532,413L530,412L529,414L526,414Z"},"YT":{"n":"Yukon","d":"M93,271L87,268L41,241L35,237L16,222L15,220L18,218L15,216L13,216L11,213L10,213L9,211L84,126L85,128L87,129L89,132L88,136L90,136L89,138L92,145L93,145L85,157L85,158L83,161L81,162L80,164L89,171L88,171L88,173L87,173L87,176L85,178L85,179L83,180L84,183L86,183L86,184L88,184L88,185L86,186L86,189L83,192L82,195L79,197L81,199L81,202L80,203L82,203L83,204L82,210L84,213L83,215L83,218L80,219L80,222L81,223L79,224L79,228L80,230L79,230L77,233L79,235L81,235L81,242L82,245L83,245L84,248L81,254L86,257L87,256L89,259L92,259L93,258L94,259L94,261L93,261L92,264L93,265L93,267Z"},"MB":{"n":"Manitoba","d":"M220,308L234,309L264,310L263,315L264,318L264,322L266,326L267,325L266,332L267,330L267,325L274,325L274,328L276,334L277,336L279,341L279,344L278,347L279,346L281,346L289,343L292,343L296,345L301,347L301,350L272,387L261,398L261,441L261,446L251,445L238,445L220,444L210,443L210,441L211,427L211,427L211,419L212,406L212,406L213,397L212,397L213,389L213,389L214,380L213,376L214,367L214,367L214,358Z"},"ON":{"n":"Ontario","d":"M380,401L382,406L380,404L380,402ZM382,407L392,448L392,449L394,451L394,453L398,456L400,459L403,461L412,460L416,462L417,463L419,464L421,462L423,465L426,466L428,465L431,466L433,463L438,460L442,459L443,463L444,464L442,467L437,471L433,478L429,481L428,482L426,485L425,483L424,485L427,484L426,486L427,486L425,488L423,487L422,487L419,487L408,493L406,496L404,497L403,501L404,501L407,501L410,500L411,503L412,504L411,505L407,505L401,508L399,511L402,511L398,512L394,511L391,512L387,516L387,518L385,519L382,521L382,524L380,523L378,524L376,523L376,520L381,519L381,516L379,516L380,510L382,509L385,504L385,502L383,495L384,491L385,490L386,488L384,483L384,483L380,480L384,480L385,483L388,483L387,485L389,485L389,487L391,485L392,486L397,487L397,484L396,483L398,482L398,481L395,479L395,476L393,476L391,474L390,474L387,470L378,471L378,470L367,471L357,471L356,468L353,470L352,469L353,467L352,465L350,464L350,459L347,456L347,452L341,453L339,453L336,450L334,446L333,444L330,443L329,444L326,445L320,443L317,443L319,445L318,447L316,450L316,449L317,447L317,445L316,445L314,451L313,452L314,448L311,450L310,455L308,456L305,456L303,455L298,456L297,454L294,456L291,457L290,455L288,454L287,453L285,453L284,455L283,452L279,450L276,450L275,451L272,452L272,450L266,449L265,447L264,441L261,441L261,398L272,387L301,350L301,347L303,347L307,350L310,351L313,355L315,357L325,358L327,360L329,361L332,362L335,362L339,361L347,361L349,361L351,362L352,364L352,370L352,372L354,374L355,377L355,379L357,383L356,384L356,387L358,388L360,390L363,392L363,394L362,395L364,395L365,396L368,396L372,399L374,404L378,404ZM368,474L370,475L373,472L375,474L376,472L378,473L378,475L380,473L379,476L378,477L379,475L376,477L370,475L366,475Z"}}},"au":{"w":600,"h":540,"regions":{"NT":{"n":"Northern Territory","d":"M343,52L343,52L345,51L345,50L345,48L345,47L345,46L346,46L346,45L347,46L348,45L347,44L348,44L349,43L349,43L349,44L349,44L349,46L351,46L351,45L352,45L351,47L350,47L350,48L350,49L350,49L350,50L349,49L349,51L350,51L350,51L351,51L352,51L353,53L352,53L350,52L349,52L349,53L348,52L346,52L345,51ZM367,86L367,231L303,232L239,231L239,61L240,61L242,63L241,64L242,65L241,66L242,65L242,66L242,65L243,65L243,64L242,63L242,62L243,62L243,61L244,62L246,62L247,65L247,66L248,65L248,63L248,61L250,61L249,60L248,59L248,58L249,58L250,57L250,56L248,57L246,56L246,56L245,56L245,55L244,54L244,53L245,53L245,52L246,51L246,50L246,49L247,50L248,49L249,48L250,47L249,46L250,45L250,43L251,42L251,41L251,41L252,40L252,41L253,41L255,40L255,40L257,39L257,38L256,36L255,36L255,35L255,33L256,32L257,33L258,32L258,31L258,29L260,28L261,29L260,29L261,29L263,30L262,29L262,29L261,28L262,28L261,28L262,26L262,25L262,25L262,25L264,25L264,27L264,26L264,27L265,27L266,28L267,28L266,27L266,27L267,26L265,26L265,25L266,24L268,24L268,24L268,22L267,22L268,21L270,22L271,22L271,20L272,20L272,20L272,22L273,23L274,23L278,23L280,22L281,23L283,24L284,22L285,22L286,23L286,22L287,22L288,23L288,23L288,22L288,21L290,20L290,21L292,21L291,20L291,18L291,17L291,16L290,16L291,15L291,14L292,14L292,13L292,13L290,14L290,13L290,13L290,12L289,11L289,11L288,11L287,11L286,11L285,11L283,12L282,12L282,11L282,11L281,10L282,10L281,9L280,9L279,9L279,8L280,9L279,7L280,8L280,7L281,8L282,8L281,6L282,6L282,7L283,7L283,9L284,9L284,11L285,10L284,10L285,9L285,9L285,8L284,6L284,6L286,8L286,7L287,6L287,7L287,9L288,8L289,8L289,9L290,9L291,10L291,12L292,12L294,10L294,11L295,10L294,9L295,9L296,10L296,11L297,13L298,15L299,15L301,15L301,16L302,16L303,16L304,16L304,17L304,16L305,17L305,16L306,16L307,16L307,15L308,15L309,15L309,16L308,16L308,17L309,18L311,17L311,18L311,18L312,18L313,18L312,19L313,19L313,21L314,20L314,19L316,20L317,20L319,20L320,19L320,19L321,18L321,19L322,20L322,20L322,21L324,21L325,23L327,23L328,24L327,22L328,22L329,23L329,23L330,22L330,22L329,23L328,22L329,21L329,21L331,20L331,21L332,20L333,21L334,20L336,19L335,20L334,21L334,22L335,23L335,24L336,22L337,21L337,23L338,21L338,21L339,20L339,20L338,22L338,22L338,23L339,22L340,23L338,24L339,25L339,25L339,26L340,25L340,26L341,25L342,26L343,25L343,24L344,23L344,22L342,22L341,21L342,21L343,20L344,20L345,18L346,18L345,19L346,19L346,19L346,20L347,20L347,22L348,22L348,23L349,23L350,23L349,22L350,21L350,22L351,22L352,23L352,24L353,24L352,25L350,27L350,26L349,26L349,27L349,27L349,28L348,29L349,29L347,30L347,31L347,30L346,31L346,30L346,31L347,32L347,32L348,33L348,34L348,34L348,33L347,34L347,33L346,34L347,35L346,36L346,36L345,37L345,37L344,38L344,37L344,37L344,36L344,35L344,34L343,35L343,36L342,36L342,37L341,37L341,36L340,36L340,37L340,37L339,38L339,37L338,37L338,38L338,38L337,39L338,40L338,40L338,40L337,40L337,40L337,42L336,42L337,43L338,44L337,44L338,45L338,45L340,44L339,45L339,46L338,48L338,48L338,49L337,51L336,52L335,53L334,54L333,55L333,56L332,57L332,58L331,58L331,59L328,58L328,59L326,59L326,60L326,60L327,59L327,60L328,59L329,59L329,59L330,59L330,61L331,63L333,64L334,64L334,66L332,67L334,66L334,66L335,65L337,66L339,67L341,69L342,69L343,71L343,72L345,72L347,74L348,73L349,73L348,75L349,76L349,77L350,76L352,77L352,76L352,76L357,78L358,79L359,80L362,81L364,81L364,82L365,84ZM260,17L258,16L255,17L254,16L253,16L254,15L254,14L255,15L256,14L257,15L257,14L256,14L256,13L256,12L255,11L256,11L256,10L257,9L258,9L259,11L259,12L259,12L260,13L260,14L261,15L262,16L262,17ZM270,8L270,9L271,10L270,9L271,8L271,7L272,8L272,8L272,8L273,9L273,8L274,9L274,10L275,10L275,11L274,12L274,13L274,13L273,13L272,14L272,15L271,15L270,16L269,17L268,17L267,18L266,17L263,16L263,16L261,14L260,13L260,13L259,12L259,9L258,8L258,7L259,7L261,9L261,8L262,9L261,10L262,10L263,9L263,10L263,11L264,11L265,12L263,10L265,10L266,9L268,10L268,9Z"},"ACT":{"n":"Australian Capital Territory","d":"M521,389L522,387L521,386L522,386L526,383L527,383L527,384L530,386L530,386L529,386L528,386L527,386L526,388L526,391L525,391L526,393L526,394L526,395L525,396L525,396L524,396L523,395L523,393L522,394L521,393L521,391Z"},"TAS":{"n":"Tasmania","d":"M500,538L499,537L499,536L499,536L498,537L498,537L498,536L498,537L499,534L499,535L500,532L500,533L501,533L501,531L500,531L501,529L502,530L502,532L501,532L500,533L501,534ZM507,532L506,531L506,530L506,529L505,529L505,528L506,527L505,526L506,527L506,528L507,528L508,529L508,528L507,527L507,525L509,525L510,525L510,527L509,528L510,530L510,531L508,531ZM512,476L511,475L510,472L509,471L508,471L509,470L509,469L507,468L508,467L508,466L509,465L513,469L514,470L514,472L515,474L514,475L513,474ZM452,473L451,473L451,470L451,469L451,467L451,465L452,464L452,463L455,464L454,466L455,467L455,468L455,469L455,470ZM515,480L515,478L513,479L513,479L511,479L510,478L511,477L513,477L513,476L514,477L515,476L516,477L517,477L517,479L516,480ZM480,531L480,532L480,533L480,533L480,534L479,533L479,531L477,529L476,527L474,527L473,523L473,523L472,521L471,520L472,520L471,517L471,517L470,514L470,512L471,514L475,517L475,515L474,514L472,513L472,512L470,512L471,512L471,509L470,507L468,505L467,503L466,503L466,502L465,498L464,497L464,496L463,494L462,490L462,489L462,488L463,487L463,486L463,485L463,483L463,482L464,483L465,484L466,485L467,484L469,486L469,485L471,485L471,484L472,485L471,485L472,486L474,487L475,486L475,488L478,488L478,489L479,490L481,490L484,492L483,492L488,492L489,491L490,493L491,491L492,491L493,493L494,492L495,495L496,496L497,495L495,495L496,494L495,493L495,493L494,492L493,492L493,490L494,489L495,490L496,489L499,489L499,488L501,488L502,489L503,488L504,486L505,486L506,487L508,487L509,487L509,485L509,484L511,485L512,486L513,486L515,489L514,490L514,490L514,491L514,494L515,494L514,498L515,501L514,502L514,505L515,506L514,509L515,510L514,512L515,512L514,514L514,513L514,512L514,511L513,510L513,509L512,508L512,510L513,510L511,510L511,511L510,513L510,514L510,515L510,516L510,517L510,518L508,519L509,520L509,522L508,523L508,524L507,526L505,526L504,525L504,523L503,525L503,526L503,528L503,528L502,527L503,526L502,526L502,525L501,525L500,527L501,529L500,528L499,529L500,529L499,530L500,532L499,533L498,533L496,531L496,529L495,530L495,531L497,533L496,535L496,536L495,537L495,538L494,538L495,539L494,540L493,539L492,540L491,539L489,538L488,538L486,538L486,538L485,537L484,538L484,537L482,539L482,537L481,535L480,535L482,534L485,535L485,534L484,533L483,534L481,534L481,533L481,532Z"},"SA":{"n":"South Australia","d":"M360,399L358,398L356,397L355,398L353,398L352,399L351,398L349,399L348,397L347,397L346,396L347,395L347,393L349,393L350,393L354,392L358,390L361,391L362,390L361,391L362,392L361,393L363,393L364,393L364,394L365,394L366,393L368,393L369,395L368,396L366,396L364,395L362,397L362,398ZM410,435L408,434L405,435L404,434L402,433L402,432L400,428L398,427L398,426L398,425L397,424L396,424L394,421L392,419L393,418L392,416L391,415L393,414L394,413L394,411L393,407L392,403L389,399L387,396L383,392L380,390L381,391L385,393L387,395L389,399L392,403L391,400L391,400L390,398L388,396L387,396L387,395L385,393L381,390L381,390L377,389L376,390L376,391L375,392L374,391L371,392L370,392L369,391L370,389L372,389L372,387L374,386L374,384L374,382L375,381L374,378L375,376L375,377L375,376L374,375L373,374L373,373L372,372L371,371L371,368L369,368L370,367L369,365L368,365L367,367L367,369L366,371L366,372L365,377L365,378L365,379L364,381L364,382L362,383L361,382L359,382L358,383L356,383L356,384L353,384L352,385L351,386L351,385L352,384L353,382L352,381L353,380L353,379L354,379L357,379L358,380L360,379L360,373L360,372L360,371L359,371L360,370L360,367L359,365L360,365L361,364L361,363L362,362L362,361L363,359L364,358L365,358L366,356L366,356L367,355L366,353L366,352L365,351L365,349L367,348L368,347L368,346L367,346L366,342L366,342L365,341L365,340L365,339L365,339L364,338L364,341L365,341L364,344L364,345L364,346L362,345L362,347L361,348L359,348L359,350L359,351L359,352L358,354L357,356L356,357L355,358L354,358L353,358L352,357L351,359L351,359L349,360L348,361L347,361L347,362L345,363L344,364L343,366L342,368L340,369L341,371L340,372L340,371L338,372L337,374L337,376L336,377L337,377L338,376L339,377L338,378L339,380L337,379L337,378L335,378L334,379L334,379L333,378L331,376L330,375L329,375L329,374L328,373L326,373L327,372L328,370L328,372L329,372L329,373L330,374L332,374L331,373L331,372L330,373L330,372L330,371L330,370L330,368L329,366L328,366L329,365L328,363L327,361L325,359L322,357L323,355L322,354L322,352L320,350L321,350L320,349L319,348L318,349L319,349L317,349L316,348L316,349L315,348L315,347L314,348L315,349L315,349L314,349L314,347L313,347L313,346L313,345L311,344L312,343L311,341L313,341L313,342L314,341L314,339L313,337L312,337L311,337L310,338L309,337L309,338L308,338L308,336L309,336L310,336L308,333L307,333L307,332L306,332L306,331L304,330L304,332L303,331L302,332L302,332L301,332L300,333L298,332L297,331L295,330L294,329L292,328L291,328L289,328L288,329L287,329L285,329L284,329L283,327L278,324L275,322L270,320L268,321L265,322L255,322L247,323L239,324L239,231L325,233L410,231L410,363L410,364L410,363Z"},"VIC":{"n":"Victoria","d":"M410,435L410,363L410,364L411,364L411,364L413,364L413,364L413,364L414,365L415,365L417,366L418,366L418,367L418,366L419,366L419,365L420,365L421,365L421,365L423,365L424,365L425,365L426,365L426,366L427,366L428,366L428,367L428,368L429,369L430,369L430,369L430,369L430,371L430,372L431,373L431,374L432,375L432,375L432,376L432,376L434,376L433,376L434,375L434,375L434,374L435,374L435,373L436,372L437,374L438,374L438,375L439,374L440,375L441,375L442,376L443,376L443,376L444,377L444,378L443,380L444,382L444,384L445,384L447,384L447,385L447,386L448,387L449,387L450,387L451,389L453,389L453,390L454,390L455,391L455,392L456,392L457,394L458,394L459,396L460,397L460,398L463,399L464,400L465,399L467,399L466,398L467,395L469,395L469,395L471,395L472,396L474,394L476,395L476,396L478,397L479,398L480,397L481,398L482,398L484,398L484,399L485,398L486,398L486,399L487,399L488,397L489,398L490,397L491,399L493,399L494,399L495,400L496,399L496,400L497,398L498,398L500,399L502,397L503,397L504,398L506,397L508,398L509,399L510,399L510,399L510,401L511,405L512,406L512,408L513,408L513,409L512,412L513,412L538,425L537,425L536,426L536,424L535,425L535,425L534,428L532,429L531,429L525,430L524,429L524,430L520,430L514,430L512,431L510,432L510,431L508,432L507,431L506,431L505,431L506,432L506,432L504,434L502,434L502,435L504,435L505,433L506,433L505,434L505,434L497,442L495,444L493,445L493,446L490,446L489,446L487,447L487,446L485,446L484,447L485,448L486,450L488,449L488,448L488,448L489,450L488,451L488,452L488,453L488,454L487,454L486,453L486,452L485,451L484,449L482,448L482,450L480,450L480,449L479,447L478,445L477,446L475,445L474,444L473,443L473,443L473,441L475,441L475,440L474,438L471,437L470,440L471,441L469,441L468,442L468,442L466,443L462,439L464,440L465,440L467,439L468,438L469,436L469,434L468,433L467,431L466,431L465,431L464,432L462,434L460,435L460,435L459,435L458,436L461,437L462,436L463,436L462,438L462,439L461,439L459,439L458,440L457,441L454,442L451,446L449,447L448,448L447,449L446,449L444,447L442,447L440,446L439,445L437,445L436,444L432,441L431,441L430,440L428,440L428,441L427,441L424,439L421,438L420,438L419,439L419,441L418,441L418,441L417,441L417,440L416,441L416,439L414,437L412,436Z"},"WA":{"n":"Western Australia","d":"M14,234L13,232L11,230L11,229L10,228L10,227L9,225L9,224L10,223L11,224L11,225L12,229L13,229L12,230L14,232L14,233ZM239,61L239,324L236,325L234,327L228,329L225,330L221,331L218,333L215,333L210,334L207,334L206,334L202,334L199,333L197,334L197,334L195,334L193,335L190,338L188,339L186,339L183,341L182,341L180,343L178,344L173,345L171,346L169,348L168,353L167,356L166,356L166,357L164,358L164,360L162,361L161,362L160,361L159,361L157,363L156,363L155,361L154,361L153,361L152,361L151,361L150,361L148,361L148,362L146,362L144,362L143,362L143,363L141,363L140,361L139,360L138,360L138,361L136,361L135,361L134,361L134,361L132,360L130,360L128,360L127,361L126,360L125,361L123,361L122,361L119,361L118,362L117,363L114,362L112,362L111,362L110,363L108,363L107,364L106,365L104,367L103,368L103,369L104,370L104,370L103,370L102,369L102,370L102,371L102,371L101,371L100,371L100,372L98,372L97,371L95,371L95,372L93,372L93,373L93,374L91,375L89,375L88,376L88,377L88,379L87,378L85,379L85,380L83,380L82,381L81,380L80,381L81,382L82,382L81,383L80,382L79,381L77,381L77,381L77,383L76,382L75,382L73,381L75,380L73,380L72,381L70,381L70,382L69,381L67,381L67,381L64,381L62,381L61,380L60,381L59,380L57,378L55,378L53,378L53,376L51,374L49,371L47,370L44,368L42,368L41,370L41,369L40,367L40,366L39,365L39,363L39,361L39,360L39,358L40,357L39,355L40,356L41,357L42,357L44,357L45,356L47,354L48,351L49,351L49,348L48,340L48,339L49,338L49,338L48,339L49,342L49,342L49,339L49,340L50,340L50,339L49,339L50,336L50,334L49,333L50,333L50,332L50,330L50,329L51,329L51,328L50,330L50,329L50,327L49,323L47,319L45,317L45,314L43,310L42,309L40,304L40,300L39,300L39,298L38,296L39,294L38,290L39,287L38,285L37,281L34,277L33,274L32,271L31,269L29,267L28,266L27,265L27,263L26,261L27,258L26,255L25,252L22,247L19,243L18,241L15,237L14,236L13,233L14,234L14,235L15,232L15,231L15,234L15,235L16,236L16,237L16,237L15,236L16,233L16,234L17,234L18,236L18,238L17,239L18,239L18,237L18,235L18,236L19,239L18,240L19,241L19,241L19,240L19,239L20,242L21,240L22,240L23,239L23,236L22,235L20,234L19,233L19,232L18,230L18,230L17,227L16,227L17,225L18,223L18,225L19,225L19,227L21,228L21,229L21,229L21,231L20,232L21,234L22,234L23,232L23,230L24,231L23,231L24,234L24,236L24,236L25,237L26,238L28,237L28,236L28,235L27,234L28,233L28,231L29,231L28,230L29,229L27,228L27,227L26,225L25,224L24,222L23,220L22,218L21,217L20,215L20,214L20,213L19,211L17,208L16,206L16,204L16,203L17,202L17,200L17,199L17,198L18,197L18,196L19,196L19,194L21,192L22,191L22,189L22,186L22,185L22,183L21,180L20,180L20,179L20,177L21,175L21,175L22,173L24,168L25,167L26,166L27,165L28,166L27,166L26,171L27,173L27,173L27,173L27,174L27,175L27,176L27,177L28,176L28,176L29,175L29,176L31,176L31,174L31,173L31,172L32,172L31,171L32,171L32,169L34,168L34,167L34,166L35,165L36,165L38,164L39,163L40,164L41,163L41,163L43,162L44,162L46,161L46,161L48,158L49,158L50,157L51,157L52,155L52,154L55,153L56,153L56,151L58,151L59,150L60,150L61,149L62,149L63,148L65,147L65,146L66,146L65,147L65,148L65,149L67,149L68,148L68,148L69,147L70,148L70,147L71,149L73,149L76,149L77,148L78,148L80,147L80,146L81,147L81,145L82,145L85,143L86,144L87,143L87,143L88,143L89,143L89,143L90,143L90,143L91,142L92,143L94,142L94,141L96,140L96,139L97,138L98,137L99,138L99,137L102,138L103,138L104,139L107,138L107,137L109,137L110,137L114,137L120,134L125,132L128,129L130,128L132,124L133,121L134,120L134,119L134,118L134,119L135,118L136,118L136,116L136,115L137,114L138,115L139,114L140,113L141,112L142,111L143,110L144,110L144,108L144,107L143,107L142,108L142,107L142,107L142,103L141,101L141,98L141,97L142,96L143,95L143,95L143,95L146,92L146,91L146,92L147,92L147,90L147,89L148,90L149,89L150,89L150,88L150,88L150,87L152,85L152,84L153,84L154,83L154,85L153,86L153,86L153,87L154,87L154,88L155,88L155,89L156,90L156,91L155,92L157,92L157,93L157,95L158,95L159,96L159,98L160,98L161,100L162,102L162,99L161,99L162,98L162,96L163,97L162,96L162,95L162,93L163,93L163,94L163,93L166,96L166,96L165,95L165,93L166,93L165,92L167,91L165,91L164,90L162,88L160,88L161,87L161,87L162,87L162,86L161,86L161,87L160,86L160,85L161,85L162,86L162,85L163,85L160,84L162,84L160,84L162,84L162,84L163,84L162,83L163,83L162,82L162,83L161,82L162,82L161,81L162,80L163,80L165,81L165,82L164,82L165,83L166,83L166,84L166,82L167,84L168,84L167,82L167,82L169,82L170,83L171,84L172,84L172,85L173,85L174,86L174,85L173,84L172,84L173,83L174,84L176,84L178,84L179,84L177,83L176,83L173,83L173,82L173,80L174,81L174,79L175,81L176,80L176,80L176,78L177,76L177,76L178,75L178,75L179,73L178,72L178,75L178,75L178,74L177,75L176,76L175,78L175,77L174,77L174,75L173,76L173,74L174,72L174,71L175,70L176,71L177,70L177,70L177,69L178,68L177,68L177,67L180,68L182,69L182,70L182,71L183,71L183,70L186,72L183,70L183,68L182,68L181,68L180,68L181,66L182,67L183,66L182,65L182,66L181,66L180,67L180,66L180,65L181,65L182,65L183,64L182,63L183,63L183,63L184,64L184,66L184,65L186,65L186,65L187,66L187,65L188,66L189,65L187,65L188,64L187,64L189,63L187,64L187,63L186,63L185,62L186,62L187,61L186,61L185,61L185,60L184,59L185,59L185,58L186,58L186,57L188,56L188,57L189,56L189,56L190,56L190,57L191,56L190,56L191,54L190,54L190,52L191,52L192,53L191,53L191,54L192,56L191,58L192,54L192,55L193,55L194,56L194,57L195,58L195,58L195,57L196,56L196,56L197,56L196,54L197,53L197,52L198,52L198,51L198,49L197,50L196,49L196,48L196,49L197,48L196,47L197,47L198,48L199,48L199,49L198,49L199,49L199,51L200,51L199,52L200,51L201,51L201,50L201,49L202,49L202,48L203,48L204,47L204,48L203,48L203,49L203,50L203,50L203,51L204,50L204,52L205,52L205,51L205,51L205,49L206,50L206,50L207,48L206,49L207,48L208,48L208,47L209,48L208,47L208,47L207,45L208,45L208,45L209,45L208,45L209,45L210,44L210,45L212,46L211,47L212,48L213,47L213,48L214,47L214,47L215,48L215,47L217,48L217,49L218,49L219,50L220,51L220,51L221,53L222,54L222,54L222,55L223,55L224,56L224,57L225,57L227,58L227,59L228,59L228,59L227,59L228,61L226,61L227,61L226,62L226,64L226,65L226,66L226,67L226,68L226,69L226,70L225,71L224,71L222,72L223,72L225,71L226,70L227,68L226,67L226,66L227,66L229,68L229,70L230,70L229,69L229,68L228,66L228,65L227,64L228,63L229,63L230,64L230,64L230,63L231,64L231,63L230,61L231,62L231,61L230,60L233,60L235,60L237,61L238,61Z"},"QLD":{"n":"Queensland","d":"M584,221L583,226L583,228L582,228L581,227L581,225L581,224L581,223L581,222L582,221L582,219L581,219L582,218L584,217L585,215L585,214L584,212L583,212L584,211L585,211L585,213L587,215ZM556,196L555,194L554,194L553,192L553,191L554,191L555,192L556,192L556,193L557,194L557,196L557,196ZM386,89L385,89L384,88L384,87L385,86L385,85L386,85L388,85L390,84L392,85L392,86L391,86L390,86L390,85L389,86L389,86L388,86L388,88L387,87L387,88ZM486,115L485,115L485,114L483,111L483,111L485,112L485,111L486,111L485,112L486,113L487,114ZM568,275L568,275L568,276L568,277L566,278L565,278L565,279L564,279L563,277L562,278L561,279L560,280L560,280L559,282L557,282L557,281L557,278L554,277L554,277L553,275L552,275L549,273L548,274L547,274L545,274L544,273L544,272L543,272L542,272L541,272L540,273L539,272L538,273L535,273L534,273L533,272L531,273L530,274L529,274L528,275L526,277L525,279L524,279L467,280L410,279L410,231L367,231L367,86L370,88L372,89L373,89L374,90L376,89L379,91L382,91L383,93L384,93L384,94L384,95L385,97L386,98L388,98L388,99L391,101L395,102L396,103L398,104L402,103L404,102L405,101L406,100L408,100L409,98L409,96L409,95L410,93L410,92L411,91L411,90L412,89L413,88L414,87L414,86L415,85L414,84L415,83L415,81L416,79L416,77L416,76L416,73L417,71L418,68L418,66L419,65L420,63L419,61L418,57L418,55L419,52L419,51L419,49L417,48L417,47L417,46L418,42L419,39L420,39L420,40L421,39L420,39L420,37L419,36L419,34L419,34L419,32L420,32L421,31L422,29L423,29L423,30L423,31L424,32L423,30L423,30L423,29L422,28L423,28L424,28L424,27L423,28L422,28L421,27L420,27L421,26L420,26L420,27L420,27L419,28L419,27L420,22L422,23L421,22L421,22L422,20L423,18L423,20L423,21L424,20L425,20L424,19L424,19L424,16L426,15L425,15L425,14L425,12L426,10L426,8L427,6L426,4L427,4L427,3L429,3L430,3L431,0L432,0L433,1L432,2L432,2L432,4L432,3L432,3L433,3L433,4L434,4L434,4L435,4L436,6L436,9L436,9L437,10L436,11L437,14L437,17L438,18L439,18L440,17L441,18L442,19L440,21L440,24L441,24L443,25L443,26L443,27L445,28L445,29L444,30L444,32L445,31L447,31L446,33L446,34L446,36L446,36L446,38L447,40L447,41L447,43L446,45L447,46L448,48L449,49L449,52L450,54L451,55L453,55L455,54L456,53L456,52L457,53L458,53L459,52L460,51L460,51L462,52L461,54L462,54L462,55L463,56L464,56L464,57L466,57L467,58L467,59L467,59L468,60L470,61L471,61L470,61L471,62L472,62L472,63L471,64L471,65L471,67L472,67L472,68L471,69L471,70L471,70L471,71L472,72L472,73L472,74L472,75L473,76L472,77L473,77L474,79L474,80L474,82L474,82L473,83L473,84L473,85L474,85L474,86L476,88L477,89L478,91L478,92L479,91L480,91L480,91L481,91L480,92L480,94L481,95L482,98L483,99L482,100L484,103L483,103L483,105L483,106L483,107L482,108L482,110L482,111L483,113L484,113L484,114L485,115L486,116L486,117L486,118L485,121L488,124L491,126L493,126L493,126L494,127L495,127L496,126L497,127L496,127L498,129L499,129L499,129L500,129L502,129L502,128L503,130L504,131L504,132L503,133L502,132L504,134L505,135L507,136L506,134L507,133L508,133L508,134L508,136L510,137L511,136L511,137L513,137L514,138L513,139L515,140L516,141L517,140L516,139L518,139L518,141L519,141L519,141L520,142L521,142L521,141L521,142L522,143L522,144L523,146L523,146L522,145L521,145L520,145L519,147L520,148L520,149L521,149L521,150L522,150L522,151L523,151L524,152L525,153L526,153L527,153L527,154L527,155L527,156L527,157L527,158L528,157L529,157L529,159L528,161L529,160L530,161L531,162L531,162L531,163L531,164L530,165L530,166L531,166L531,168L532,169L532,170L533,173L532,173L533,173L534,174L534,174L534,176L536,175L536,174L537,176L538,177L539,179L539,177L537,174L537,173L538,171L539,170L540,171L540,172L541,173L541,174L541,174L543,175L546,177L547,177L548,179L547,177L548,177L548,176L546,175L546,174L547,173L548,174L548,175L548,174L549,175L549,175L549,177L548,176L549,177L549,178L550,177L550,178L551,179L550,182L550,183L549,183L550,183L549,186L550,187L550,187L550,188L550,189L550,190L551,191L551,192L549,192L548,192L549,192L548,193L549,193L550,193L550,193L551,193L552,195L552,194L553,193L553,193L554,194L555,195L555,197L557,197L557,199L558,198L559,201L560,200L561,201L561,200L562,200L563,201L563,200L562,199L563,200L563,201L564,200L564,201L565,202L566,202L566,203L567,206L568,208L570,210L573,211L574,212L574,214L574,215L575,217L576,218L576,218L577,219L578,219L579,219L579,220L580,220L580,222L579,223L579,223L581,222L580,223L580,225L580,226L580,226L582,228L582,229L582,230L582,231L583,229L583,230L584,230L583,234L582,236L583,237L582,238L583,238L583,237L583,237L583,239L583,241L584,242L584,244L583,245L582,247L583,247L584,248L582,249L582,250L583,250L583,251L582,252L583,252L584,253L584,255L585,254L586,257L586,258L587,258L587,261L587,261L587,263L588,262L588,265L589,266L589,266L588,266L587,267L585,267L585,267L584,267L584,268L583,269L582,269L581,269L580,268L578,269L576,267L575,269L575,268L575,267L574,267L573,269L572,269L570,270L569,270L568,271L567,272L567,271L567,274Z"},"NSW":{"n":"New South Wales","d":"M495,400L494,399L493,399L491,399L490,397L489,398L488,397L487,399L486,399L486,398L485,398L484,399L484,398L482,398L481,398L480,397L479,398L478,397L476,396L476,395L474,394L472,396L471,395L469,395L469,395L467,395L466,398L467,399L465,399L464,400L463,399L460,398L460,397L459,396L458,394L457,394L456,392L455,392L455,391L454,390L453,390L453,389L451,389L450,387L449,387L448,387L447,386L447,385L447,384L445,384L444,384L444,382L443,380L444,378L444,377L443,376L443,376L442,376L441,375L440,375L439,374L438,375L438,374L437,374L436,372L435,373L435,374L434,374L434,375L434,375L433,376L434,376L432,376L432,375L432,375L431,374L431,373L430,372L430,371L430,369L430,369L430,369L429,369L428,368L428,367L428,366L427,366L426,366L426,365L425,365L424,365L423,365L421,365L421,365L420,365L419,365L419,366L418,366L418,367L418,366L417,366L415,365L414,365L413,364L413,364L413,364L411,364L411,364L410,363L410,279L467,280L524,279L525,279L526,277L528,275L529,274L530,274L531,273L533,272L534,273L535,273L538,273L539,272L540,273L541,272L542,272L543,272L544,272L544,273L545,274L547,274L548,274L549,273L552,275L553,275L554,277L554,277L557,278L557,281L557,282L559,282L560,280L560,280L561,279L562,278L563,277L564,279L565,279L565,278L566,278L568,277L568,276L568,275L568,275L567,274L567,271L567,272L568,271L569,270L570,270L572,269L573,269L574,267L575,267L575,268L575,269L576,267L578,269L580,268L581,269L582,269L583,269L584,268L584,267L585,267L585,267L587,267L588,266L589,266L589,267L590,267L589,270L589,272L590,273L591,273L590,276L590,277L588,280L587,282L586,284L587,285L585,285L587,286L587,287L586,289L586,290L586,291L585,294L584,296L584,298L584,300L584,301L583,301L582,304L582,306L581,307L581,309L582,310L583,310L583,311L582,312L582,313L581,316L581,318L580,319L581,320L579,322L579,323L578,326L578,326L576,328L575,330L575,332L575,333L575,334L575,335L575,336L575,336L572,338L571,339L570,340L570,341L568,341L567,340L567,341L568,341L570,341L570,342L569,342L568,342L565,343L564,344L564,345L563,345L563,346L562,347L562,345L561,346L562,347L561,347L561,348L561,349L562,347L562,347L562,349L561,350L561,351L560,351L561,350L560,350L559,352L560,352L559,353L559,355L557,356L555,354L555,354L556,356L557,356L557,357L557,357L558,356L558,357L557,359L556,360L557,361L556,363L555,362L555,363L556,363L556,364L555,364L555,364L554,364L555,365L555,365L553,367L552,369L552,370L552,372L551,371L550,373L551,372L551,373L550,377L549,377L550,380L551,381L551,381L550,382L550,380L548,381L549,382L548,382L547,384L546,384L545,386L546,387L544,388L544,390L543,391L543,393L541,393L542,393L542,395L541,395L541,399L540,398L540,399L541,399L540,400L541,403L540,404L540,406L540,408L539,411L538,412L538,416L537,418L538,417L539,418L539,420L538,420L538,421L538,421L538,422L538,425L513,412L512,412L513,409L513,408L512,408L512,406L511,405L510,401L510,399L510,399L509,399L508,398L506,397L504,398L503,397L502,397L500,399L498,398L497,398L496,400L496,399ZM521,386L522,387L521,389L521,391L521,391L521,393L522,394L523,393L523,395L524,396L525,396L525,396L526,395L526,394L526,393L525,391L526,391L526,388L527,386L528,386L529,386L530,386L530,386L527,384L527,383L526,383L522,386Z"}}},"eu":{"w":600,"h":540,"regions":{"RU":{"n":"Russia","d":"M443,-98L442,-103L449,-104L463,-105L473,-101L473,-96L467,-91L461,-89L452,-91L449,-89ZM471,-109L479,-113L484,-111L484,-96L470,-105L469,-108ZM510,-202L509,-209L512,-221L521,-222L524,-211L528,-206L522,-199L513,-198ZM511,-231L512,-239L517,-238L518,-233L516,-226L512,-225ZM542,-212L536,-214L534,-218L537,-224L540,-225L542,-220L542,-213ZM379,-21L382,-25L385,-26L387,-24L386,-27L387,-30L391,-29L391,-27L390,-24L389,-22L389,-20L388,-17L385,-19L384,-23ZM388,290L377,292L369,292L370,287L378,282L384,283L387,284L387,287ZM443,39L443,26L439,22L441,13L443,1L449,-7L449,-14L452,-19L457,-17L458,-13L455,-2L452,9L452,24L455,36L458,48L464,55L475,59L474,61L463,67L460,63L452,64L448,59L450,55L446,50L446,38ZM748,-284L758,-289L768,-292L778,-297L794,-310L793,-299L810,-299L818,-309L826,-311L821,-304L830,-301L822,-297L812,-295L801,-291L792,-291L778,-287L769,-281L755,-277L748,-282L744,-278L742,-280ZM118,-417L117,-417L117,-418L118,-417ZM499,-418L505,-419L512,-415L519,-423L522,-424L525,-421L525,-416L528,-409L526,-405L528,-400L533,-398L532,-393L528,-391L534,-389L536,-394L542,-396L542,-396L549,-395L553,-390L554,-398L559,-406L559,-410L562,-413L565,-415L570,-406L586,-401L590,-399L601,-397L611,-396L616,-397L615,-386L630,-380L628,-376L635,-374L637,-367L644,-369L657,-369L661,-372L659,-379L673,-386L676,-382L685,-384L688,-389L698,-382L710,-387L717,-380L728,-383L741,-379L736,-374L723,-364L703,-350L688,-346L680,-347L676,-345L669,-352L652,-354L638,-357L625,-360L610,-355L614,-350L624,-353L641,-347L631,-337L641,-327L663,-323L667,-330L675,-320L680,-312L672,-310L674,-301L682,-297L685,-280L694,-264L719,-251L749,-233L748,-243L754,-248L755,-254L749,-257L748,-264L757,-276L766,-278L778,-276L791,-279L808,-280L826,-273L834,-272L849,-264L863,-257L870,-253L878,-243L879,-238L875,-233L884,-223L888,-222L888,-222L886,-221L880,-220L880,-224L867,-224L858,-220L853,-226L854,-236L842,-239L830,-240L826,-242L815,-244L821,-233L826,-227L827,-216L817,-212L811,-203L808,-191L798,-188L791,-185L787,-182L776,-177L772,-171L769,-162L770,-153L772,-145L777,-140L779,-143L785,-143L789,-139L800,-133L805,-132L811,-122L806,-114L806,-106L803,-103L801,-99L807,-87L808,-78L809,-71L806,-63L805,-56L799,-53L792,-48L789,-41L788,-32L789,-26L782,-17L774,-18L769,-12L766,-6L759,0L767,10L774,9L779,18L773,27L770,33L763,35L760,42L755,46L755,58L758,74L756,81L755,84L748,84L745,93L739,93L735,96L728,96L724,98L722,108L711,112L714,118L683,115L671,117L670,113L668,132L665,137L661,133L653,136L651,143L641,137L632,144L631,151L623,165L623,169L608,188L608,193L616,195L614,201L616,203L615,211L628,209L630,214L623,220L623,224L613,225L604,232L601,240L592,241L576,244L567,250L561,267L565,274L555,272L556,285L559,286L560,295L569,299L573,296L581,300L583,305L587,304L588,312L582,317L581,331L593,336L596,343L610,350L608,356L608,359L605,360L596,357L594,358L588,358L584,355L576,357L573,361L571,360L558,360L547,363L540,364L540,366L527,361L517,361L509,357L513,354L515,343L510,341L518,332L517,330L512,334L510,330L512,326L519,322L518,318L514,313L515,307L513,304L502,305L499,307L493,304L489,307L479,307L478,305L475,301L469,302L468,299L468,297L462,292L456,296L454,296L453,299L451,299L447,293L445,290L446,289L451,287L452,284L450,282L445,282L445,280L442,279L436,273L436,270L434,265L428,264L425,266L424,264L417,263L413,257L412,252L408,251L410,247L406,237L408,230L407,229L411,221L404,218L412,201L414,194L415,188L405,183L405,175L399,169L399,159L391,148L392,139L384,133L383,125L386,123L391,117L394,112L402,117L413,115L433,121L438,124L441,131L439,139L434,144L412,143L409,146L419,151L421,156L425,166L432,167L437,169L435,163L431,160L432,155L445,157L448,153L442,147L447,132L451,131L456,133L455,125L449,121L447,114L441,109L453,107L459,111L454,115L457,120L462,122L467,116L464,110L469,101L476,84L479,83L479,91L484,89L484,84L490,79L492,72L500,74L499,65L491,63L491,59L502,54L508,53L528,51L525,45L518,44L516,42L512,45L509,40L501,36L499,33L496,21L490,12L490,9L498,4L503,7L508,16L513,17L520,21L531,32L539,32L544,39L550,55L555,53L553,49L554,43L550,39L548,32L542,30L537,24L534,27L528,24L521,14L511,12L509,2L502,-2L503,-4L509,-1L517,7L521,5L513,1L513,-6L517,-13L526,-14L516,-17L506,-24L507,-30L511,-36L514,-43L507,-44L502,-51L503,-54L500,-62L502,-70L500,-72L502,-79L505,-81L501,-89L503,-94L498,-96L494,-100L491,-108L496,-111L498,-107L501,-112L507,-112L505,-115L506,-123L513,-128L518,-130L524,-129L527,-125L532,-116L535,-113L538,-117L541,-121L539,-124L547,-125L544,-127L543,-132L546,-142L551,-143L553,-157L544,-157L546,-164L546,-170L552,-176L559,-178L564,-176L573,-181L577,-188L564,-190L568,-196L564,-202L566,-210L563,-212L563,-218L553,-213L547,-216L544,-248L550,-254L554,-266L545,-279L543,-286L546,-291L553,-295L554,-301L549,-304L545,-310L543,-317L537,-323L541,-334L535,-336L532,-329L526,-327L521,-339L515,-345L511,-357L511,-364L509,-377L508,-393L513,-398L512,-402L508,-395L500,-404L499,-417L499,-418ZM490,-343L490,-343L487,-345L486,-351L487,-352L492,-352L496,-349L499,-346L497,-343ZM483,358L484,355L490,356L492,355L494,357L495,357L495,358L500,359L507,356L507,360L499,364L492,374L487,373L487,368L478,367L478,365L484,359Z"},"NO":{"n":"Norway","d":"M320,8L321,4L324,3L327,7L336,15L331,21L330,31L328,33L328,43L325,44L319,37L321,33L317,29L311,19L309,9L315,4L317,9ZM391,117L386,123L383,125L382,117L376,114L371,119L371,128L369,133L364,132L359,133L353,128L351,131L349,132L349,139L341,139L341,145L337,145L335,154L331,166L325,182L327,186L326,190L321,190L318,201L319,215L322,221L321,234L317,241L314,248L310,241L299,253L292,256L284,250L283,238L283,213L288,206L301,197L311,186L319,170L329,148L335,139L344,124L352,118L358,117L362,107L369,106L375,102L389,107L385,111ZM347,-1L345,7L339,9L332,8L331,5L327,5L324,0L330,-5L334,-2L336,-6L341,-4ZM346,28L342,34L337,32L338,28L336,24L340,21L342,26Z"},"FR":{"n":"France","d":"M-492,533L-508,535L-519,539L-528,538L-533,533L-531,529L-533,526L-539,525L-540,518L-533,517L-527,513L-520,511L-517,501L-510,494L-495,492L-494,497L-489,508L-489,527ZM282,349L285,352L295,354L291,361L290,369L288,371L285,370L285,372L279,378L279,383L282,382L285,386L284,390L286,394L283,397L285,405L289,407L288,412L281,418L265,414L253,416L252,423L242,423L234,417L231,419L217,412L214,407L219,401L224,380L218,368L214,361L204,355L205,347L214,347L226,351L225,339L231,345L248,339L252,330L258,328L258,332L261,333L264,337L269,343L272,342L278,348L280,349ZM298,424L304,420L305,429L302,437L298,435L297,428Z"},"SE":{"n":"Sweden","d":"M314,248L317,241L321,234L322,221L319,215L318,201L321,190L326,190L327,186L325,182L331,166L335,154L337,145L341,145L341,139L349,139L349,132L351,131L358,136L365,142L369,159L371,163L364,168L361,176L362,183L356,192L348,203L346,219L350,226L356,231L352,244L347,247L346,266L344,276L337,276L334,284L327,285L324,275L319,263Z"},"BY":{"n":"Belarus","d":"M417,263L424,264L425,266L428,264L434,265L436,270L436,273L442,279L445,280L445,282L450,282L452,284L451,287L446,289L445,290L447,293L451,299L446,301L444,304L445,309L443,309L437,311L434,309L433,312L430,311L425,312L417,311L410,312L405,313L402,317L399,318L398,313L395,309L398,306L397,301L395,298L394,293L400,292L406,287L406,280L410,276L408,271L412,269Z"},"UA":{"n":"Ukraine","d":"M451,299L453,299L454,296L456,296L462,292L468,297L468,299L469,302L475,301L478,305L479,307L489,307L493,304L499,307L502,305L513,304L515,307L514,313L518,318L519,322L512,326L510,330L512,334L507,337L503,342L497,346L492,352L495,357L494,357L492,355L490,356L484,355L483,358L481,358L469,359L468,355L461,358L460,365L457,374L453,373L450,376L446,375L448,373L448,369L449,365L448,364L450,362L451,364L455,363L457,361L455,361L455,359L452,357L450,353L447,352L446,348L442,346L439,347L433,345L429,347L427,349L424,350L423,352L418,355L416,357L412,355L408,356L403,355L400,358L399,355L395,353L396,349L397,346L399,346L396,341L401,331L404,330L404,326L399,318L402,317L405,313L410,312L417,311L425,312L430,311L433,312L434,309L437,311L443,309L445,309L444,304L446,301Z"},"PL":{"n":"Poland","d":"M394,293L395,298L397,301L398,306L395,309L398,313L399,318L404,326L404,330L401,331L396,341L399,346L397,346L390,343L385,345L382,344L378,347L374,344L371,346L371,345L367,340L362,340L360,337L356,336L355,339L351,337L351,334L346,334L342,330L339,324L340,320L338,314L335,311L337,308L335,302L339,299L348,293L356,289L362,290L363,292L369,292L377,292L388,290L392,290Z"},"AT":{"n":"Austria","d":"M359,361L359,366L355,366L356,368L354,376L353,378L347,378L343,381L337,381L326,378L324,375L317,377L316,379L312,377L308,377L305,375L306,372L305,370L308,370L311,373L312,370L319,370L324,368L328,368L330,371L331,369L329,362L332,360L334,355L340,358L343,354L346,353L351,355L355,355L358,356L358,358Z"},"HU":{"n":"Hungary","d":"M395,353L399,355L400,358L396,361L394,369L391,377L385,380L381,380L375,384L373,386L366,384L360,380L358,379L356,376L354,376L356,368L355,366L359,366L359,361L363,364L366,365L372,363L372,360L375,360L378,358L379,358L382,356L383,354L386,353L394,355Z"},"MD":{"n":"Moldova","d":"M427,349L429,347L433,345L439,347L442,346L446,348L447,352L450,353L452,357L455,359L455,361L457,361L455,363L451,364L450,362L448,364L449,365L448,369L448,373L446,375L444,370L443,366L442,361L436,356L433,352L430,349Z"},"RO":{"n":"Romania","d":"M446,375L450,376L453,373L457,374L458,377L455,380L452,380L454,393L449,393L442,391L434,395L431,399L419,401L413,400L410,401L407,397L405,396L407,394L405,393L403,396L398,393L396,389L391,387L390,384L385,380L391,377L394,369L396,361L400,358L403,355L408,356L412,355L416,357L418,355L423,352L424,350L427,349L430,349L433,352L436,356L442,361L443,366L444,370Z"},"LT":{"n":"Lithuania","d":"M408,271L410,276L406,280L406,287L400,292L394,293L392,290L388,290L387,287L387,284L384,283L378,282L375,273L381,269L391,268L397,265L398,267L401,267Z"},"LV":{"n":"Latvia","d":"M408,251L412,252L413,257L417,263L412,269L408,271L401,267L398,267L397,265L391,268L381,269L375,273L374,265L376,258L381,253L387,260L391,259L391,251L395,248L398,249L404,252Z"},"EE":{"n":"Estonia","d":"M407,229L408,230L406,237L410,247L408,251L404,252L398,249L395,248L391,251L390,245L388,246L384,243L382,237L389,233L395,230L401,230Z"},"DE":{"n":"Germany","d":"M335,302L337,308L335,311L338,314L340,320L339,324L342,330L340,332L338,331L336,333L331,335L329,338L324,340L325,344L326,348L330,351L334,355L332,360L329,362L331,369L330,371L328,368L324,368L319,370L312,370L311,373L308,370L305,370L298,367L296,369L290,369L291,361L295,354L285,352L282,349L282,344L281,341L282,334L282,323L286,323L288,319L290,309L289,305L290,303L295,303L296,305L301,300L299,296L299,290L304,291L308,290L308,294L314,296L314,300L321,298L324,295L332,299Z"},"BG":{"n":"Bulgaria","d":"M407,397L410,401L413,400L419,401L431,399L434,395L442,391L449,393L454,393L451,398L450,407L454,412L447,412L439,418L441,423L434,426L427,423L421,427L415,428L413,421L409,418L410,417L408,416L409,412L412,408L407,404L406,400Z"},"GR":{"n":"Greece","d":"M457,488L457,492L444,496L444,494L432,494L433,489L439,492L446,489L452,489L453,491ZM415,428L421,427L427,423L434,426L441,423L439,418L444,420L443,427L441,429L436,429L432,429L423,434L430,439L426,442L421,443L416,438L415,441L418,447L423,451L420,454L426,458L430,461L432,467L423,465L427,470L422,473L427,482L421,483L413,479L408,471L405,464L401,459L396,454L395,451L398,445L398,441L400,439L400,436L405,435L408,432L413,431L414,429Z"},"AL":{"n":"Albania","d":"M400,436L400,439L398,441L398,445L395,451L393,450L393,448L388,445L386,440L386,432L387,429L385,427L384,424L387,418L388,420L390,418L392,421L394,422L395,426L394,430L396,434Z"},"HR":{"n":"Croatia","d":"M358,379L360,380L366,384L373,386L375,384L378,388L380,391L378,395L374,393L369,394L362,393L359,393L357,396L354,393L353,398L357,403L359,406L363,410L366,412L370,416L377,419L377,421L369,418L364,415L356,412L349,405L350,404L347,399L346,396L341,394L339,399L337,396L337,392L337,392L342,392L343,390L346,391L349,391L349,388L351,387L352,383Z"},"CH":{"n":"Switzerland","d":"M305,370L306,372L305,375L308,377L312,377L311,382L308,383L302,382L301,386L297,387L296,385L291,389L288,389L285,386L282,382L279,383L279,378L285,372L285,370L288,371L290,369L296,369L298,367Z"},"LU":{"n":"Luxembourg","d":"M281,341L282,344L282,349L280,349L278,348L279,342Z"},"BE":{"n":"Belgium","d":"M282,334L281,341L279,342L278,348L272,342L269,343L264,337L261,333L258,332L258,328L263,327L268,328L275,326L279,331Z"},"NL":{"n":"Netherlands","d":"M289,305L290,309L288,319L286,323L282,323L282,334L279,331L275,326L268,328L263,327L267,324L274,309L283,305Z"},"PT":{"n":"Portugal","d":"M154,411L158,409L162,409L162,414L167,416L169,415L173,416L174,422L170,424L168,432L166,434L164,439L160,439L162,446L158,453L160,456L158,459L154,462L154,466L150,468L146,465L141,465L144,457L145,450L142,448L141,444L144,437L148,434L150,430L153,424L154,419L154,415Z"},"ES":{"n":"Spain","d":"M154,466L154,462L158,459L160,456L158,453L162,446L160,439L164,439L166,434L168,432L170,424L174,422L173,416L169,415L167,416L162,414L162,409L158,409L154,411L157,404L155,398L168,394L177,398L187,400L195,404L202,405L214,407L217,412L231,419L234,417L242,423L252,423L252,429L243,436L232,437L231,440L225,446L220,454L223,461L217,465L214,472L207,473L200,481L188,479L180,477L174,480L169,483L165,481L163,477L162,470Z"},"IE":{"n":"Ireland","d":"M206,290L205,298L198,306L185,309L177,305L185,296L185,284L195,278L201,275L201,281L198,286L202,287Z"},"IT":{"n":"Italy","d":"M312,377L316,379L317,377L324,375L326,378L337,381L336,386L338,391L332,389L326,393L327,399L326,402L329,407L336,413L341,421L350,430L357,429L359,431L357,434L365,437L371,440L379,445L380,447L379,451L374,446L366,445L363,453L370,456L369,462L366,463L362,473L358,474L358,471L360,465L361,462L357,456L354,450L351,449L348,445L342,443L339,439L332,438L326,434L318,427L313,420L310,410L306,408L300,405L296,406L291,411L288,412L289,407L285,405L283,397L286,394L284,390L285,386L288,389L291,389L296,385L297,387L301,386L302,382L308,383L311,382ZM350,472L357,471L354,480L356,483L354,489L347,485L343,484L330,479L331,473L341,474ZM298,443L302,439L307,447L306,462L302,461L298,465L295,462L295,448L293,442Z"},"DK":{"n":"Denmark","d":"M308,290L304,291L299,290L297,284L297,273L298,270L300,267L305,266L307,263L312,260L312,265L310,269L311,272L314,274L312,278L311,277L306,284ZM323,277L325,283L321,291L315,285L314,281Z"},"GB":{"n":"United Kingdom","d":"M206,290L202,287L198,286L201,281L201,275L206,276L211,284ZM225,299L225,299L227,293L224,286L224,286L217,282L216,279L219,275L218,271L213,276L215,265L213,259L218,249L224,241L228,243L235,243L227,254L233,253L239,255L236,263L229,272L235,273L236,275L239,288L243,290L245,303L247,308L254,311L253,318L249,320L251,326L244,331L235,330L223,331L220,328L215,332L209,330L203,333L200,330L212,321L219,320L218,320L209,316L208,311L215,309L213,303L215,296Z"},"IS":{"n":"Iceland","d":"M201,146L198,152L200,161L192,167L176,169L171,169L166,165L155,156L162,154L155,144L163,146L164,143L157,137L163,132L170,133L173,143L182,140L186,145L195,142Z"},"SI":{"n":"Slovenia","d":"M337,381L343,381L347,378L353,378L354,376L356,376L358,379L352,383L351,387L349,388L349,391L346,391L343,390L342,392L337,392L338,391L336,386Z"},"FI":{"n":"Finland","d":"M383,125L384,133L392,139L391,148L399,159L399,169L405,175L405,183L415,188L414,194L412,201L404,218L395,221L387,227L378,231L374,225L369,223L368,212L364,202L365,196L369,188L377,174L380,171L378,167L371,163L369,159L365,142L358,136L351,131L353,128L359,133L364,132L369,133L371,128L371,119L376,114L382,117Z"},"SK":{"n":"Slovakia","d":"M397,346L396,349L395,353L394,355L386,353L383,354L382,356L379,358L378,358L375,360L372,360L372,363L366,365L363,364L359,361L358,358L358,356L359,354L362,354L365,352L365,351L366,351L366,348L368,347L368,345L371,345L371,346L374,344L378,347L382,344L385,345L390,343Z"},"CZ":{"n":"Czechia","d":"M342,330L346,334L351,334L351,337L355,339L356,336L360,337L362,340L367,340L371,345L368,345L368,347L366,348L366,351L365,351L365,352L362,354L359,354L358,356L355,355L351,355L346,353L343,354L340,358L334,355L330,351L326,348L325,344L324,340L329,338L331,335L336,333L338,331L340,332Z"},"CY":{"n":"Cyprus","d":"M515,474L517,474L519,472L521,472L521,472L522,473L522,473L524,473L525,471L526,471L527,472L519,479L514,479L511,476Z"},"BA":{"n":"Bosnia and Herzegovina","d":"M377,419L370,416L366,412L363,410L359,406L357,403L353,398L354,393L357,396L359,393L362,393L369,394L374,393L378,395L381,395L379,400L384,403L383,409L381,409L380,410L378,413Z"},"MK":{"n":"Macedonia","d":"M409,418L413,421L415,428L414,429L413,431L408,432L405,435L400,436L396,434L394,430L395,426L396,426L396,424L401,421L402,420L405,419Z"},"RS":{"n":"Serbia","d":"M375,384L381,380L385,380L390,384L391,387L396,389L398,393L403,396L405,393L407,394L405,396L407,397L406,400L407,404L412,408L409,412L408,416L410,417L409,418L405,419L402,420L402,420L403,418L403,415L402,416L400,414L399,414L397,412L396,412L394,410L393,411L393,415L391,416L391,415L388,413L385,412L384,411L381,409L383,409L384,403L379,400L381,395L378,395L380,391L378,388Z"},"ME":{"n":"Montenegro","d":"M390,418L388,420L387,418L384,424L385,427L383,426L380,423L377,421L377,419L378,413L380,410L381,409L384,411L385,412L388,413L391,415L391,416Z"},"XK":{"n":"Kosovo","d":"M395,426L394,422L392,421L390,418L391,416L393,415L393,411L394,410L396,412L397,412L399,414L400,414L402,416L403,415L403,418L402,420L402,420L401,421L396,424L396,426Z"}}}};
window.SHIP_MAP_DATA = SHIP_MAP_DATA;

;
/* ===== app-ship-filter.js ===== */
// ─── Queue filter matcher (2026-09-23): ONE implementation of the Ready to Ship filters for the
//     browser page and the server — a saved filter can now select the orders of an auto-batch
//     rule (Anthony: "add filters to the auto batch conditions"), and the sweep must read a
//     filter exactly the way the page does. Browser: bundled before app-shipping-core.js (the
//     queue page calls shipFilterMatches). Node: require()d by routes/shipping/31-auto-batch.js.
//     Pure: everything environmental comes through `env` — now (Date), today / tomorrow (civil
//     dates 'YYYY-MM-DD'), stateKey(state, country), carrierLabel(code), rateTotals ({id: total}).
//     The page's free-text search and its "selected rows stay visible" pin are NOT part of a
//     saved filter and stay on the page.
var SHIP_FILTER_EMPTY_OTHER = {
  wMin: '', wMax: '', vMin: '', vMax: '', qMin: '', qMax: '', lineMode: '',
  sku: '', location: '', tagged: '', noted: '', hs: '', shipBy: '', svcSource: '', carrier: '',
  countries: [], states: [], recipient: '', itemName: '', orderNum: '',
  requestedSvc: '', ageMin: '', ageMax: '', batchMode: '', rateStatus: '',
  business: '', // Business address True ('yes') / False ('no') — the queue row's residential answer (Anthony 2026-10-30)
  // Text-field operators (Anthony 9-17): '' = Contains (what every saved filter set means), 'exact', 'starts'
  recipientOp: '', orderNumOp: '', skuOp: '', itemNameOp: '', locationOp: '',
};
var SHIP_FILTER_DOMESTIC = ['US', 'PR', 'VI', 'GU', 'AS', 'MP', ''];

function shipFilterTextMatch(hay, needle, op) {
  var h2 = String(hay || '').toLowerCase(), n = String(needle || '').toLowerCase();
  if (op === 'exact') return h2 === n;
  if (op === 'starts') return h2.indexOf(n) === 0;
  return h2.indexOf(n) !== -1;
}

// civil-date arithmetic on 'YYYY-MM-DD' strings — timezone-agnostic, so the server can pass an
// Eastern "today" and get the same answer the page gets in the office
function shipFilterAddDays(ymd, days) {
  var p = ymd.split('-').map(Number);
  var d = new Date(Date.UTC(p[0], p[1] - 1, p[2] + days));
  return d.toISOString().slice(0, 10);
}
function shipFilterDateRange(filters, env) {
  var p = (filters && filters.datePreset) || '';
  var today = (env && env.today) || new Date().toLocaleDateString('en-CA');
  if (!p || p === 'custom') return { from: (filters && filters.dateFrom) || null, to: (filters && filters.dateTo) || null };
  if (p === 'today') return { from: today, to: today };
  if (p === 'yesterday') { var ys = shipFilterAddDays(today, -1); return { from: ys, to: ys }; }
  if (p === '7d') return { from: shipFilterAddDays(today, -6), to: today };
  if (p === '30d') return { from: shipFilterAddDays(today, -29), to: today };
  if (p === 'month') return { from: today.slice(0, 8) + '01', to: today };
  if (p === 'lastmonth') {
    var firstThis = today.slice(0, 8) + '01';
    var lastPrev = shipFilterAddDays(firstThis, -1);
    return { from: lastPrev.slice(0, 8) + '01', to: lastPrev };
  }
  return { from: null, to: null };
}

// The per-filter-set work (the merged "Other" menu, the order-date range) is computed ONCE per
// filter object and civil day, not once per row (2026-09-26: the queue filters ~900 rows on every
// render). Filter objects are replaced, never mutated, on change, so the object identity is the key.
var _shipFilterPrepCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
function shipFilterPrepare(filters, todayStr) {
  var hit = _shipFilterPrepCache && _shipFilterPrepCache.get(filters);
  if (hit && hit.today === todayStr) return hit;
  var prep = {
    today: todayStr,
    fo: Object.assign({}, SHIP_FILTER_EMPTY_OTHER, filters.other || {}),
    dr: shipFilterDateRange(filters, { today: todayStr }),
  };
  if (_shipFilterPrepCache) _shipFilterPrepCache.set(filters, prep);
  return prep;
}

// The civil day an order was placed ('YYYY-MM-DD'), for the date filters, the Order Date column
// and the CSV. order_date is a TIMESTAMPTZ, so its ISO text carries the UTC day and an order placed
// 8 PM-midnight Eastern sat under the next day (review 2026-09-28, medium #33). A date-only value
// is used as is; a zoned instant is read in `timeZone` when given (the auto-batch sweep passes the
// rule's zone) or in the viewer's local zone (the page).
function shipOrderDay(v, timeZone) {
  if (!v) return '';
  var s = String(v);
  var m = s.match(/^(\d{4}-\d{2}-\d{2})(.*)$/);
  if (m && !/(Z|[+-]\d{2}:?\d{2})$/i.test(m[2])) return m[1];
  var d = v instanceof Date ? v : new Date(s);
  if (isNaN(d.getTime())) return s.slice(0, 10);
  return timeZone ? d.toLocaleDateString('en-CA', { timeZone: timeZone }) : d.toLocaleDateString('en-CA');
}

// Does queue row `o` pass saved-filter set `filters`? Same order and semantics as the Ready to
// Ship page (channels, destination, assignees, tags, hold, order date, then the "Other" menu).
function shipFilterMatches(o, filters, env) {
  env = env || {};
  filters = filters || {};
  var now = env.now || new Date();
  var todayStr = env.today || now.toLocaleDateString('en-CA');
  var tomorrowStr = env.tomorrow || shipFilterAddDays(todayStr, 1);
  var stateKey = env.stateKey || function(s) { return String(s || '').trim().toUpperCase(); };
  var carrierLabel = env.carrierLabel || function(c) { return c; };
  var rateTotals = env.rateTotals || {};
  var countryKey = function(c) { return String(c || 'US').trim().toUpperCase(); };
  var isDomestic = SHIP_FILTER_DOMESTIC.indexOf(countryKey(o.country)) !== -1;
  var prep = shipFilterPrepare(filters, todayStr);
  var fo = prep.fo;
  var channels = filters.channels || [], assignees = filters.assignees || [], tags = filters.tags || [];

  if (channels.length && channels.indexOf(o.channel || '?') === -1) return false;
  if (filters.dest === 'dom' && !isDomestic) return false;
  if (filters.dest === 'intl' && isDomestic) return false;
  if (assignees.length && assignees.indexOf(o.assignee || 'Unassigned') === -1) return false;
  if (tags.length && !tags.some(function(t) { return (o.tags || []).indexOf(t) !== -1; })) return false;
  if (filters.holdOnly && !o.hold) return false;
  var d = shipOrderDay(o.order_date, env.timeZone);
  var dr = prep.dr;
  if (dr.from && d < dr.from) return false;
  if (dr.to && d > dr.to) return false;
  // "Other" menu
  if (fo.wMin !== '' && !(o.weight_lb >= parseFloat(fo.wMin))) return false;
  if (fo.wMax !== '' && !(o.weight_lb <= parseFloat(fo.wMax))) return false;
  if (fo.vMin !== '' && !(o.value >= parseFloat(fo.vMin))) return false;
  if (fo.vMax !== '' && !(o.value <= parseFloat(fo.vMax))) return false;
  if (fo.qMin !== '' && !(o.total_qty >= parseInt(fo.qMin, 10))) return false;
  if (fo.qMax !== '' && !(o.total_qty <= parseInt(fo.qMax, 10))) return false;
  if (fo.lineMode === 'single' && o.line_count > 1) return false;
  if (fo.lineMode === 'multi' && o.line_count <= 1) return false;
  // Matches the component SKU or its kit parent — kit orders explode into component lines, so a
  // filter on the KIT sku found nothing (8-12)
  if (fo.sku && !(o.lines || []).some(function(l) { return shipFilterTextMatch(l.sku, fo.sku, fo.skuOp) || shipFilterTextMatch(l.kit_sku, fo.sku, fo.skuOp); })) return false;
  if (fo.location && !(o.locations || []).some(function(b) { return shipFilterTextMatch(b, fo.location, fo.locationOp); })) return false;
  if (fo.tagged === 'yes' && !(o.tags || []).length) return false;
  if (fo.tagged === 'no' && (o.tags || []).length) return false;
  if (fo.noted === 'yes' && !o.note) return false;
  if (fo.noted === 'no' && o.note) return false;
  // HS code coverage across the order's items (kit components included)
  if (fo.hs === 'yes' && !((o.lines || []).length && (o.lines || []).every(function(l) { return l.hs_code; }))) return false;
  if (fo.hs === 'no' && !(o.lines || []).some(function(l) { return !l.hs_code; })) return false;
  if (fo.shipBy) {
    var sb = o.ship_by ? String(o.ship_by).slice(0, 10) : null;
    if (fo.shipBy === 'none' && sb) return false;
    if (fo.shipBy === 'overdue' && !(sb && sb < todayStr)) return false;
    if (fo.shipBy === 'today' && sb !== todayStr) return false;
    if (fo.shipBy === 'tomorrow' && sb !== tomorrowStr) return false;
    if (fo.shipBy === 'later' && !(sb && sb > tomorrowStr)) return false;
  }
  if (fo.svcSource && o.service_source !== fo.svcSource) return false;
  if (fo.carrier && carrierLabel(o.carrier || '') !== fo.carrier) return false;
  if (fo.countries.length && fo.countries.indexOf(countryKey(o.country)) === -1) return false;
  if (fo.states.length && fo.states.indexOf(stateKey(o.state, o.country)) === -1) return false;
  if (fo.recipient && !shipFilterTextMatch(o.customer, fo.recipient, fo.recipientOp)) return false;
  if (fo.itemName && !(o.lines || []).some(function(l) { return shipFilterTextMatch(l.title, fo.itemName, fo.itemNameOp); })) return false;
  // Order # = the Zentail number, both marketplace numbers and Custom Field 2 (9-18)
  if (fo.orderNum && ![o.order_number, o.channel_order_id, o.channel_ref, o.custom2].some(function(v) { return v && shipFilterTextMatch(v, fo.orderNum, fo.orderNumOp); })) return false;
  if (fo.requestedSvc && o.requested_service !== fo.requestedSvc) return false;
  if (fo.ageMin !== '' || fo.ageMax !== '') {
    var ageHrs = (now.getTime() - new Date(o.order_date).getTime()) / 3600000;
    if (fo.ageMin !== '' && !(ageHrs >= parseFloat(fo.ageMin))) return false;
    if (fo.ageMax !== '' && !(ageHrs <= parseFloat(fo.ageMax))) return false;
  }
  if (fo.batchMode === 'in' && !o.batch_id) return false;
  if (fo.batchMode === 'none' && o.batch_id) return false;
  if (fo.rateStatus === 'error' && !o.rate_error) return false;
  if (fo.rateStatus === 'address' && o.rate_error_kind !== 'address') return false;
  if (fo.rateStatus === 'rated' && !(rateTotals[o.id] || o.rate)) return false;
  if (fo.rateStatus === 'unrated' && (rateTotals[o.id] || o.rate || o.rate_error)) return false;
  // Business address (Anthony 2026-10-30): o.business is the queue row's answer (services/address-class.js —
  // the panel's override, else the USPS business flag, else residential), so the page and the sweep agree
  if (fo.business === 'yes' && !o.business) return false;
  if (fo.business === 'no' && o.business) return false;
  return true;
}

// ─── Rule / auto-batch condition validation (sweep 2026-09-27 H2): ONE check for the editors
//     (Save is blocked) and the server (POST/PUT refuse). A blank "contains" matched every order —
//     an auto-batch rule with one could batch and label the whole queue. Every condition needs a
//     known field, a known operator and a value; numeric fields and the comparison operators need
//     numbers ("1,585" is not a number); "between" needs exactly min, max.
var SHIP_COND_OPS = ['eq', 'ne', 'in', 'not_in', 'contains', 'not_contains', 'gt', 'gte', 'lt', 'lte', 'between'];
var SHIP_COND_LIST_OPS = ['in', 'not_in', 'between'];
var SHIP_COND_COMPARE_OPS = ['gt', 'gte', 'lt', 'lte', 'between'];
function shipConditionValues(v, op) {
  if (Array.isArray(v)) return v.map(function(x) { return String(x == null ? '' : x).trim(); }).filter(Boolean);
  var s = String(v == null ? '' : v).trim();
  if (!s) return [];
  if (SHIP_COND_LIST_OPS.indexOf(op) !== -1) return s.split(/[,;\n]+/).map(function(x) { return x.trim(); }).filter(Boolean);
  return [s];
}
function shipIsNumber(s) { return /^-?(\d+\.?\d*|\.\d+)$/.test(String(s).trim()); }
// fieldTypes: { key: 'string' | 'number' } (the rules engine's CONDITION_FIELDS); labels optional.
// Returns null when the condition is usable, else a sentence for the user.
function shipConditionProblem(c, fieldTypes, labels) {
  if (!c || typeof c !== 'object') return 'A condition is empty';
  var type = fieldTypes ? fieldTypes[c.field] : 'string';
  var name = (labels && labels[c.field]) || c.field || '(no field)';
  if (!type) return 'Unknown condition field "' + name + '"';
  if (SHIP_COND_OPS.indexOf(c.op) === -1) return name + ': unknown operator "' + (c.op || '') + '"';
  var vals = shipConditionValues(c.value, c.op);
  if (!vals.length) return name + ': enter a value (a blank condition would match every order)';
  if (c.op === 'between' && vals.length !== 2) return name + ': "between" needs two numbers, min and max';
  var numeric = type === 'number' || SHIP_COND_COMPARE_OPS.indexOf(c.op) !== -1;
  if (numeric && !vals.every(shipIsNumber)) return name + ': "' + vals.join(', ') + '" is not a number';
  return null;
}
// The first problem across a list of conditions, or null.
function shipConditionsProblem(conds, fieldTypes, labels) {
  if (!Array.isArray(conds)) return 'Conditions must be a list';
  for (var i = 0; i < conds.length; i++) { var p = shipConditionProblem(conds[i], fieldTypes, labels); if (p) return p; }
  return null;
}

// ─── One CSV cell for every shipping export (browser and server, sweep 2026-09-27): quoted when it
//     holds a quote, comma or line break, and defused when it would start a spreadsheet formula
//     (= + - @ tab CR) — a buyer name or note like "=HYPERLINK(…)" ran as a formula in Excel.
//     Plain numbers ("-3.5") stay numbers.
function shipCsvCell(v) {
  var s = v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = "'" + s;
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { shipFilterMatches, shipFilterPrepare, shipFilterDateRange, shipFilterTextMatch, shipFilterAddDays, shipOrderDay, SHIP_FILTER_EMPTY_OTHER, SHIP_FILTER_DOMESTIC,
    shipConditionProblem, shipConditionsProblem, shipConditionValues, SHIP_COND_OPS, shipCsvCell };
}
if (typeof window !== 'undefined') {
  window.shipFilterMatches = shipFilterMatches;
  window.shipFilterDateRange = shipFilterDateRange;
  window.shipFilterPrepare = shipFilterPrepare;
  window.shipConditionProblem = shipConditionProblem;
  window.shipConditionsProblem = shipConditionsProblem;
}

;
/* ===== app-shipping-core.js ===== */
// ─── Shipping app: shared helpers, small components, print + CSV utilities ───
// Split from app-shipping.js by page (2026-09-19). All app-shipping-*.js files are concatenated
// in order into one bundle (scripts/build-client.js) and share ONE scope, exactly as before.
// ─── Shipping module (docs/19) — PROTOTYPE ───
// In-app ShipStation replacement, iterating. Zentail remains the FBM master.
// Queue = live Zentail PENDING orders with rule-evaluated tags + suggested
// carrier/service; Rules = the automations editor; Test Shipments = what the
// stubbed "buy" recorded. Real DHL eCommerce / USPS label purchase and Zentail
// tracking writeback arrive in later increments.
// Exports: window.ShippingPage

var { createElement: h } = React;

var _shipPageCache = { tab: null };
var _shipQueueCache = {}; // bucket|batch -> last /shipping/queue response (stale-while-revalidate)
var _shipQueueUi = {};    // bucket|batch -> { scroll, page } of the table when the user left
// Every queue mutation (add to batch, labels, mark shipped, dissolve, manual orders …) dispatches
// ship:nav-refresh; drop every cached page then, so a batch page opened next paints fresh instead of
// its old snapshot (batch #675779, 2026-09-17: orders just labelled were added again from a stale list).
function shipQueueInvalidate() { Object.keys(_shipQueueCache).forEach(function(k) { delete _shipQueueCache[k]; }); }
try { window.addEventListener('ship:nav-refresh', shipQueueInvalidate); } catch (e) {}

// Job-progress polls (label jobs, batch runs) give up instead of retrying every 3 s forever
// (2026-09-26, review fix 4): a 4xx other than 408/429 means the job is gone or not ours and a
// retry cannot help; anything else (network, 5xx, 408/429) is retried up to SHIP_POLL_MAX_FAILS
// consecutive failures. The job itself runs on the server either way.
var SHIP_POLL_MAX_FAILS = 10;
function shipPollGiveUp(err, consecutiveFails) {
  var st = err && Number(err.apiStatus);
  if (st >= 400 && st < 500 && st !== 408 && st !== 429) return true;
  return (consecutiveFails || 0) >= SHIP_POLL_MAX_FAILS;
}
try { window.shipPollGiveUp = shipPollGiveUp; } catch (e) {}

// A void (or other label call) that USPS refused until someone authorizes another payment:
// 409 { needs_usps_authorization: true } (2026-09-26). The caller asks, then re-sends with authorize_usps.
function shipNeedsUspsReauth(err) {
  return !!(err && Number(err.apiStatus) === 409 && err.apiBody && err.apiBody.needs_usps_authorization);
}
try { window.shipNeedsUspsReauth = shipNeedsUspsReauth; } catch (e) {}

// The queue header's subtitle (2026-09-26, review fix 13): view-specific and counted from the rows
// the table shows. It used to print the server's total_pending — every PENDING order of the 30-day
// window, before the warehouse-routing filter and "cancelled here" — so Ready to Ship read 903
// while the table held 885, and On Hold (7 rows) printed the same 903 "awaiting shipment".
var SHIP_QUEUE_HEADER_NOUN = {
  ready: 'awaiting shipment', hold: 'on hold', awaiting_payment: 'awaiting payment',
  pending_fulfillment: 'pending fulfillment', shipped: 'shipped', cancelled: 'cancelled', rejected: 'rejected', all: 'in the last 30 days',
};
function shipQueueHeaderText(bucket, count, truncated) {
  var n = Number(count) || 0;
  var noun = SHIP_QUEUE_HEADER_NOUN[bucket] || SHIP_QUEUE_HEADER_NOUN.ready;
  return n.toLocaleString() + ' order' + (n === 1 ? '' : 's') + ' ' + noun + (truncated ? ' — showing the oldest ' + n.toLocaleString() : '');
}
try { window.shipQueueHeaderText = shipQueueHeaderText; } catch (e) {}

// Money formatter: USD gets the "$"; a non-USD amount is shown with its code ("1,585.13 MXN") so a
// relay order's native total is never mistaken for dollars (currency Phase D, 2026-09-03).
var shipMoney = function(v, currency) {
  var amount = Number(v || 0);
  var n = Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  var minus = amount < 0 ? '-' : ''; // "-$10.11", not "$-10.11" (the discount line, 2026-09-29)
  return (!currency || currency === 'USD') ? minus + '$' + n : minus + n + ' ' + currency;
};

// The lines of the order panel's Cost Summary, as [label, amount] (Anthony 2026-09-29: the order
// total is what the customer paid = product + shipping - discount + tax). The discount shows only
// when there is one, as a minus, so the lines add up to the Total under them.
function shipCostSummaryRows(sales) {
  var s = sales || {};
  var rows = [['Product', s.item_price], ['Shipping', s.shipping_paid]];
  var d = Math.abs(parseFloat(s.discount) || 0);
  if (d > 0) rows.push(['Discount', -d]);
  rows.push(['Tax', s.tax]);
  return rows;
}
try { window.shipCostSummaryRows = shipCostSummaryRows; } catch (e) {}

// ShipStation-style weight display: "9.92 oz" under a pound, "23 lb 5.2 oz" above
var shipWeightFmt = function(lb) {
  var n = parseFloat(lb);
  if (!n || n <= 0) return null;
  var totalOz = n * 16;
  if (totalOz < 16) return (Math.round(totalOz * 100) / 100) + ' oz';
  var wholeLb = Math.floor(n);
  var oz = Math.round((totalOz - wholeLb * 16) * 10) / 10;
  return wholeLb + ' lb' + (oz > 0 ? ' ' + oz + ' oz' : '');
};
// Decimal pounds → the order panel's lb + oz boxes (0.78 → 0 lb 12.48 oz)
var shipLbToLbOz = function(w) {
  var n = Math.max(0, parseFloat(w) || 0), whole = Math.floor(n);
  return { lb: String(whole), oz: String(Math.round((n - whole) * 16 * 100) / 100) };
};

// Custom checkbox matching the app's dropdown-check styling (bordered rounded
// box, accent fill + white check when on) — native checkboxes read as unstyled.
function ShipCheckbox({ checked, onChange, title }) {
  return h('div', {
    className: 'schk',
    title: title,
    onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); }, // no text-selection on shift-click
    onClick: function(e) { e.stopPropagation(); onChange(e); },
    style: {
      width: 16, height: 16, borderRadius: 4, cursor: 'pointer', flexShrink: 0,
      // textDim (not border) for the unchecked lining — the border color is
      // near-invisible against dark-mode row backgrounds (user ask 8-11)
      border: '1px solid ' + (checked ? C.accent : C.textDim),
      background: checked ? C.accent : C.bg,
      // display:flex (block-level), NOT inline-flex: an inline box's baseline
      // moves when it gains/loses content, so checking used to change the
      // cell's line-box height ~1-2px and visibly shift the table (8-12)
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.1s, border-color 0.1s',
    }
  }, checked && h(LIcon, { name: 'check', size: 12, color: '#fff' }));
}

// Themed number stepper — native webkit spinners are tiny and unusable in dark
// mode. Taller input + a full-height ▲▼ column that follows the theme.
// onCommit (optional) gets the typed value once the user leaves the box (blur or Enter): the order
// panel's lb box splits a decimal into lb + oz there, not on every keystroke (2026-11-03)
function ShipStepper({ value, onChange, onCommit, step, width, placeholder, title }) {
  var bump = function(dir) {
    var v = parseFloat(value) || 0;
    var next = Math.max(0, Math.round((v + dir * (step || 1)) * 100) / 100);
    onChange(String(next));
  };
  var btn = { background: C.bg, color: C.textMuted, border: 'none', cursor: 'pointer', fontSize: 7.5, lineHeight: 1, padding: 0, fontFamily: 'inherit', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  var hov = function(e) { e.currentTarget.style.color = C.accent; e.currentTarget.style.background = C.surface; };
  var out = function(e) { e.currentTarget.style.color = C.textMuted; e.currentTarget.style.background = C.bg; };
  return h('div', { style: { display: 'inline-flex', alignItems: 'stretch', height: 34 } },
    h('input', { type: 'number', value: value, min: 0, step: 'any', placeholder: placeholder, title: title,
      onChange: function(e) { onChange(e.target.value); },
      onBlur: onCommit ? function(e) { onCommit(e.target.value); } : undefined,
      onKeyDown: onCommit ? function(e) { if (e.key === 'Enter') onCommit(e.target.value); } : undefined,
      style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRight: 'none', borderRadius: '6px 0 0 6px', padding: '0 8px', fontSize: 12, fontFamily: 'inherit', width: width || 46, boxSizing: 'border-box', outline: 'none' } }),
    h('div', { style: { display: 'flex', flexDirection: 'column', border: '1px solid ' + C.border, borderRadius: '0 6px 6px 0', overflow: 'hidden', width: 20, boxSizing: 'border-box' } },
      h('button', { tabIndex: -1, title: 'Increase', onClick: function() { bump(1); }, onMouseEnter: hov, onMouseLeave: out, style: btn }, '▲'),
      h('button', { tabIndex: -1, title: 'Decrease', onClick: function() { bump(-1); }, onMouseEnter: hov, onMouseLeave: out, style: Object.assign({}, btn, { borderTop: '1px solid ' + C.border }) }, '▼')));
}

// Tag colors: registry colors load into this map; unregistered tags get a
// stable hash-derived hue so every tag is consistently colored everywhere.
var _shipTagColors = {};
function shipTagColor(t) {
  if (_shipTagColors[t]) return _shipTagColors[t];
  var hsh = 0;
  for (var i = 0; i < t.length; i++) hsh = (hsh * 31 + t.charCodeAt(i)) >>> 0;
  return 'hsl(' + (hsh % 360) + ',65%,55%)';
}

function ShipTagChips({ tags, hold }) {
  return h('span', { style: { display: 'inline-flex', gap: 5, flexWrap: 'wrap' } },
    hold && h('span', { style: { fontSize: 11.5, fontWeight: 500, padding: '3px 10px', borderRadius: 5, background: C.dangerSoft, color: C.danger, textTransform: 'uppercase' } }, 'HOLD'),
    (tags || []).map(function(t) {
      var col = shipTagColor(t);
      return h('span', { key: t, style: { fontSize: 11.5, fontWeight: 500, padding: '3px 10px', borderRadius: 5, background: 'color-mix(in srgb, ' + col + ' 18%, transparent)', color: col, border: '1px solid color-mix(in srgb, ' + col + ' 45%, transparent)' } }, t);
    })
  );
}

// ─── Order detail/edit panel (right drawer — ShipStation parity) ──────────
// Trimmed 8-30 (user call): ancillary endorsement became a carrier-level
// Settings default; "print postage" and "notify marketplace" were dead
// concepts post-ShipStation (DHL/eVS labels never print postage; Zentail
// always propagates tracking). no_notify_customer is recorded on the
// shipment — actual suppression rides the pending Zentail-rep answer.
var SHIP_FLAG_DEFS = [
  { key: 'hazmat',            label: 'This shipment contains dangerous goods' },
  { key: 'non_machinable',    label: 'This shipment is non-machinable' },
  { key: 'ddp',               label: 'Bill duties and taxes to payor of shipping charges' },
  { key: 'no_notify_customer',label: 'Do not notify customer' },
];

// MM/DD/YYYY. A date-only value prints as is; a zoned instant (order_date is a TIMESTAMPTZ) prints the
// viewer's local day, not its UTC day (review 2026-09-28, medium #33) — shipOrderDay decides which.
function shipMDY(v) {
  if (!v) return '';
  var day = shipOrderDay(v);
  var m = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return m[2] + '/' + m[3] + '/' + m[1];
  return String(v);
}
// 'YYYY-MM-DD HH:MM' in the viewer's zone for a zoned instant (the queue CSV); other values as they came
function shipLocalStamp(v) {
  if (!v) return '';
  var s = String(v);
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) return s.slice(0, 19).replace('T', ' ');
  var dt = new Date(s);
  if (isNaN(dt.getTime())) return s;
  return shipOrderDay(s) + ' ' + ('0' + dt.getHours()).slice(-2) + ':' + ('0' + dt.getMinutes()).slice(-2);
}

// Region lists for the ship-to editor dropdowns. States cover the carriers we
// print for: US (incl. DC, territories, military), CA provinces, AU states —
// other countries fall back to free text. Country names come from Intl at runtime.
// State / province codes per country — the shared lists in app-utils (StateSelect) are the source (2026-09-19)
var SHIP_US_STATES = stateCodesFor('US');
var SHIP_STATE_LISTS = { US: SHIP_US_STATES, CA: stateCodesFor('CA'), AU: stateCodesFor('AU') };
var SHIP_ISO_COUNTRIES = ('US CA GB AU DE FR IT ES NL BE AT CH SE NO DK FI IE PT PL CZ SK HU RO BG GR HR SI EE LV LT LU MT CY IS MX BR AR CL CO PE EC UY PY BO VE GT CR PA DO PR JM TT BS BB HN NI SV BZ HT CU GY SR JP CN KR TW HK SG MY TH VN PH ID IN PK BD LK NP MM KH LA BN MN KZ UZ AE SA IL TR QA KW BH OM JO LB EG MA TN DZ LY ZA NG KE GH ET TZ UG ZM ZW MZ AO CM CI SN ML BF NE TD SD SS ER DJ SO RW BI MW BW NA SZ LS GA CG CD CF GQ ST CV GM GN GW LR SL TG BJ MR RU UA BY MD GE AM AZ AL BA MK ME RS XK NZ FJ PG SB VU WS TO TV KI NR PW FM MH NC PF GU AS MP VI AI AG AW BM VG KY CW DM GD GP MQ MS KN LC VC SX TC AD GI FO GL LI MC SM VA JE GG IM AX BL MF PM WF YT RE SC MU MG KM YE IQ IR SY AF BT MV TL MO KP GF FK GS PN SH BQ EH TF IO CX CC NF HM UM CK NU TK KG PS TJ TM AQ BV SJ'.split(' ')); // + KG PS TJ TM AQ BV SJ: complete ISO 3166-1 (review 2026-09-28; mirrors server _context.js)
var _shipRegionNames = null;
// True when a stored customs description is really just the product title (or a piece of
// it) — "Blue Ara Parrot" for "Blue Ara Parrot (5/cs)" — so a suggestion may replace it.
function shipDescIsTitle(desc, title) {
  var norm = function(s) { return String(s || '').toLowerCase().replace(/\(\s*\d+\s*\/\s*cs\s*\)|\(\s*\d+\s*-\s*pack\s*\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim(); };
  var d = norm(desc), t = norm(title);
  return !!d && !!t && (t === d || t.indexOf(d) === 0 || t.indexOf(' ' + d) !== -1);
}
// A typed calendar date the order fields accept: complete, real, and in a plausible year — a date
// input fires "0002-09-27" while the year is still being typed (sweep 2026-09-27 M19)
function shipPlausibleDate(v) {
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ''));
  if (!m) return false;
  var y = +m[1], mo = +m[2], da = +m[3];
  if (y < 2020 || y > 2100 || mo < 1 || mo > 12 || da < 1) return false;
  return da <= new Date(Date.UTC(y, mo, 0)).getUTCDate();
}
function shipCountryName(code) {
  try {
    if (!_shipRegionNames) _shipRegionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return _shipRegionNames.of(code) || code;
  } catch (e) { return code; }
}

// Bin codes are slash-multi ("Q-45/Q-190/LOC600/LOC800") — show the primary
// slot + a count, full detail on hover/print
function shipBinShort(code) {
  if (!code) return code;
  var parts = String(code).split('/');
  return parts[0] + (parts.length > 1 ? ' +' + (parts.length - 1) : '');
}

// Carrier display names + tracking URLs live in app-utils.js (shipCarrierLabel /
// shipTrackingUrl) — shared with the Orders detail modal; bundles share top-level scope.

// ─── Print helpers (pick lists + packing slips; templates graduate to
// Settings > Printing later) ───────────────────────────────────────────────
function shipEsc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Modal keyboard nav (Escape / arrows) must not fire while the user is typing in a field
// (review M7 #103): Left in "Assigned To" jumped to the previous order and dropped the edit.
function shipKeyInEditable(e) {
  var t = e && e.target, tn = t && t.tagName;
  return tn === 'INPUT' || tn === 'TEXTAREA' || tn === 'SELECT' || !!(t && t.isContentEditable);
}
function shipCsvEscape(v) {
  return shipCsvCell(v); // app-ship-filter.js: quoting + formula-injection guard, shared with the server export (9-27)
}
function shipDownloadCsv(filename, header, rows) {
  var csv = [header.join(',')].concat(rows.map(function(r) { return r.map(shipCsvEscape).join(','); })).join('\r\n');
  var blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  var a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(function() { URL.revokeObjectURL(a.href); }, 2000);
}

// Physically split a single-.page document into measured letter pages, each
// repeating the .phead header + table <thead>, stamped 'Page i of N'. Chrome
// exposes no CSS page counters to window.print(), so pagination is manual.
// Best-effort: any failure falls back to the browser's natural flow.
var SHIP_PFOOT_ZONE = 40; // px kept free at the bottom of every paginated page for the footer
function shipPaginateDoc(w) {
  try {
    var doc = w.document;
    var page = doc.querySelector('.page');
    if (!page) return;
    var table = page.querySelector('table');
    if (!table || !table.tBodies.length) return;
    var head = page.querySelector('.phead');
    var theadHtml = table.tHead ? table.tHead.outerHTML : '';
    var tailNodes = [];
    var n = table.nextElementSibling;
    while (n) { tailNodes.push(n); n = n.nextElementSibling; }
    var rows = Array.prototype.slice.call(table.tBodies[0].rows);
    if (!rows.length) return;
    // 10in inside the .page padding, minus a reserved footer zone (.pbody clips at the same
    // height, so rows that render taller than measured are cut off ABOVE the footer instead
    // of running over "Page i of N" — Anthony 9-17)
    var PAGE_CONTENT = 10 * 96 - SHIP_PFOOT_ZONE - 16;
    var headH = (head ? head.offsetHeight : 0) + (table.tHead ? table.tHead.offsetHeight : 0) + 8;
    var tailH = tailNodes.reduce(function(s, x) { return s + (x.offsetHeight || 0) + 26; }, 0);
    var groups = [[]]; var used = headH;
    rows.forEach(function(r) {
      var hh = r.offsetHeight || 62;
      if (used + hh > PAGE_CONTENT && groups[groups.length - 1].length) { groups.push([]); used = headH; }
      groups[groups.length - 1].push(r); used += hh;
    });
    if (used + tailH > PAGE_CONTENT && tailNodes.length) groups.push([]); // signature block gets its own page
    var total = groups.length;
    var headHtml = head ? head.outerHTML : '';
    doc.body.innerHTML = groups.map(function(g, i) {
      return '<div class="page pfixed"><div class="pbody">' + headHtml
        + (g.length ? '<table>' + theadHtml + '<tbody>' + g.map(function(r) { return r.outerHTML; }).join('') + '</tbody></table>' : '')
        + (i === total - 1 ? tailNodes.map(function(x) { return x.outerHTML || ''; }).join('') : '')
        + '</div><div class="pfoot">Page ' + (i + 1) + ' of ' + total + '</div></div>';
    }).join('');
  } catch (e) { /* unpaginated print beats none */ }
}

// Packing slips that still run long (a wall of lines, a long gift message) are split into exact
// Letter pages: every page repeats the slip header (logo, order number, barcode, ship-to, order
// details) and the column headings, and carries a "№ … · Page i of n" footer; the count / gift /
// footer block goes on the last page (Anthony 2026-09-29: "add a footer and also keep the header
// portion for each page"). A slip that fits stays exactly as built, with no footer.
// Pure grouping: row heights -> arrays of row indexes per page.
function shipSlipPageGroups(rowHeights, headH, tailH, budget) {
  var groups = [[]]; var used = headH;
  rowHeights.forEach(function(hh, i) {
    if (used + hh > budget && groups[groups.length - 1].length) { groups.push([]); used = headH; }
    groups[groups.length - 1].push(i); used += hh;
  });
  if (used + tailH > budget && groups[groups.length - 1].length) groups.push([]);
  return groups;
}
function shipPaginateSlips(w) {
  try {
    var doc = w.document;
    var BUDGET = 10 * 96 - SHIP_PFOOT_ZONE - 16;
    var pages = Array.prototype.slice.call(doc.querySelectorAll('.page'));
    pages.forEach(function(page) {
      if (page.offsetHeight <= 11 * 96) return; // fits on one sheet: leave it alone
      var table = page.querySelector('table.sA-items');
      if (!table || !table.tBodies.length) return;
      var headNodes = []; var n = page.firstElementChild;
      while (n && n !== table) { headNodes.push(n); n = n.nextElementSibling; }
      var tailNodes = []; n = table.nextElementSibling;
      while (n) { tailNodes.push(n); n = n.nextElementSibling; }
      var rows = Array.prototype.slice.call(table.tBodies[0].rows);
      if (!rows.length) return;
      var headH = headNodes.reduce(function(s, x) { return s + (x.offsetHeight || 0); }, 0) + (table.tHead ? table.tHead.offsetHeight : 0) + 24;
      var tailH = tailNodes.reduce(function(s, x) { return s + (x.offsetHeight || 0); }, 0) + 8;
      var groups = shipSlipPageGroups(rows.map(function(r) { return r.offsetHeight || 77; }), headH, tailH, BUDGET);
      var total = groups.length;
      if (total < 2) return;
      var headHtml = headNodes.map(function(x) { return x.outerHTML; }).join('');
      var theadHtml = table.tHead ? table.tHead.outerHTML : '';
      var ord = ((page.querySelector('.sA-ord') || {}).textContent || '').trim();
      var html = groups.map(function(g, i) {
        return '<div class="page pfixed"><div class="pbody">' + headHtml
          + (i > 0 ? '<div class="sA-cont">Continued from page ' + i + '</div>' : '')
          + (g.length ? '<table class="sA-items">' + theadHtml + '<tbody>' + g.map(function(k) { return rows[k].outerHTML; }).join('') + '</tbody></table>' : '')
          + (i === total - 1 ? tailNodes.map(function(x) { return x.outerHTML || ''; }).join('') : '')
          + '</div><div class="pfoot">' + shipEsc(ord) + ' · Page ' + (i + 1) + ' of ' + total + '</div></div>';
      }).join('');
      var wrap = doc.createElement('div'); wrap.innerHTML = html;
      while (wrap.firstChild) page.parentNode.insertBefore(wrap.firstChild, page);
      page.parentNode.removeChild(page);
    });
  } catch (e) { /* unpaginated print beats none */ }
}

// Fetch an API-served PDF (cookie-authed like every api() call) and print it
// through a hidden iframe — Chrome prints its PDF viewer from script fine.
// Falls back to opening the blob in a tab if the iframe print throws.
function shipPrintLabelFile(shipmentId) { shipPrintApiPdf('/shipping/label/' + shipmentId); }
function shipPrintApiPdf(apiPath) {
  var base = (typeof API_BASE !== 'undefined' && API_BASE) ? API_BASE : '/api';
  fetch(base + apiPath, { credentials: 'include' })
    .then(function(r) {
      // 429 = the general per-IP API budget (the warehouse shares one IP) — say so plainly
      if (r.status === 429) throw new Error('Too many requests in one minute, wait a few seconds and print again');
      if (!r.ok) throw new Error('Label fetch failed (' + r.status + ')');
      // a merged print names the labels it could not include — say so instead of printing short
      // without a word (sweep 2026-09-27 M17)
      var skipped = parseInt(r.headers.get('X-Labels-Skipped'), 10) || 0;
      if (skipped > 0) {
        var asked = parseInt(r.headers.get('X-Labels-Requested'), 10) || 0;
        shipShellToast(skipped + (asked ? ' of ' + asked : '') + ' label' + (skipped === 1 ? '' : 's') + ' not loaded (no label file); the rest are printing', 'error');
      }
      return r.blob();
    })
    .then(function(b) {
      var url = URL.createObjectURL(b);
      var f = document.createElement('iframe');
      f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      f.src = url;
      f.onload = function() {
        setTimeout(function() {
          try { f.contentWindow.focus(); f.contentWindow.print(); }
          catch (e) { window.open(url, '_blank'); }
        }, 150);
      };
      document.body.appendChild(f);
      // Keep the iframe alive while the print dialog is up; clean up later
      setTimeout(function() { URL.revokeObjectURL(url); try { document.body.removeChild(f); } catch (e) {} }, 180000);
    })
    // shipShellToast, not showToast: this helper is shared and never receives a page's toast — the old
    // catch threw "showToast is not defined" and hid a 429 from Paula (batch 1000040, 2026-09-25)
    .catch(function(e) { shipShellToast(String(e.message || e), 'error'); });
}

// Open the print window NOW, inside the click (sweep 2026-09-27 M17): slips and pick lists fetch
// orders first, and a window.open after those awaits has lost the user gesture — Chrome blocked
// big prints as pop-ups. The window shows "Preparing…" until shipPrintWindow fills it (opts.win).
function shipOpenPrintWindow(title) {
  var w = null;
  try { w = window.open('', '_blank'); } catch (e) { w = null; }
  if (w) {
    try { w.document.write('<!doctype html><title>' + shipEsc(title || 'Print') + '</title><body style="font-family:Arial,sans-serif;color:#555;padding:40px">Preparing ' + shipEsc(title || 'the print') + '…</body>'); w.document.close(); } catch (e) {}
  }
  return w;
}
function shipClosePrintWindow(w) { try { if (w && !w.closed) w.close(); } catch (e) {} }
function shipPrintWindow(title, bodyHtml, opts) {
  var w = (opts && opts.win && !opts.win.closed) ? opts.win : window.open('', '_blank');
  if (!w) return appAlert('Pop-up blocked — allow pop-ups to print.');
  try { w.document.open(); } catch (e) {}
  w.document.write('<!doctype html><html><head><title>' + shipEsc(title) + '</title><style>'
    // Documents (slips / pick lists / summaries) are Letter-size — labels are
    // the only 4x6 output, and they come from the carrier phase, not here.
    // Margins live INSIDE each .page (0.5in padding) with @page at 0, so the
    // print dialog's margin setting — sticky "None" from label printing —
    // can't produce edge-flush output OR double margins.
    + '@page{size:letter portrait;margin:0}'
    + 'body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;font-size:12px}'
    // Table headers in <thead> repeat at the top of every printed page
    + 'thead{display:table-header-group}'
    + 'tr{page-break-inside:avoid}'
    // Packing slip (Merchant Classic): serif accents, hairline rules, no cell borders
    // Slip spacing tightened 2026-09-29 (Paula: four items already ran onto a second sheet): the
    // header, address block, rows and footer each gave up a few px; the pictures step down with the
    // line count (see shipPrintPackingSlips). Budget: fixed parts ≈ 250px + 77px per row on a 960px sheet.
    + '.sA-head{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:10px;border-bottom:2px solid #141414;margin-bottom:12px}'
    + '.sA-eyebrow{font-size:10px;font-weight:700;letter-spacing:0.22em;text-transform:uppercase;color:#5a5a58}'
    + '.sA-ord{font-family:Georgia,"Times New Roman",serif;font-size:26px;margin-top:2px;font-variant-numeric:tabular-nums}'
    + '.sA-dates{font-size:11px;color:#5a5a58;margin-top:4px;line-height:1.5}'
    + '.sA-cols{display:flex;gap:40px;padding:0 0 12px}'
    + '.sA-lbl{font-size:9.5px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#5a5a58;margin-bottom:7px}'
    + '.sA-val{font-size:12.5px;line-height:1.65}'
    + '.sA-name{font-size:15px;font-weight:700;line-height:1.4;margin-bottom:2px}'
    + '.sA-addr{font-size:11px;line-height:1.55;color:#333}'
    + 'table.sA-items{width:100%;border-collapse:collapse;font-size:12px}'
    + 'table.sA-items th{text-align:left;font-size:9.5px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#5a5a58;background:none;padding:0 8px 7px 0;border:none;border-bottom:1.5px solid #141414}'
    + 'table.sA-items td{padding:6px 8px 6px 0;border:none;border-bottom:1px solid #d9d7d2;vertical-align:middle}'
    + 'table.sA-items th.sA-sku,table.sA-items th.sA-bin,table.sA-items th.sA-upc,table.sA-items td.sA-sku,table.sA-items td.sA-bin,table.sA-items td.sA-upc{padding-left:18px}'
    + '.sA-qty{width:44px;font-weight:800;font-size:17px;font-variant-numeric:tabular-nums}'
    + '.sA-img{width:76px;padding-right:10px}'
    + '.sA-upc{width:1%;white-space:nowrap;color:#5a5a58;font-size:11px;font-variant-numeric:tabular-nums}'
    // width:1% + nowrap = shrink-to-content on ONE line; the Item column
    // absorbs the wrapping instead (user ask 8-12)
    // SKU and Bin wrap inside a capped width (the bin is a slash-joined location list — one with seven
    // locations ran 334px wide, squeezed the Item column to 52px and made every row 255px tall: that,
    // not the item count, was Paula's second page, 2026-09-29); the Item column keeps at least 200px
    // and a title is clamped to four lines.
    + '.sA-sku{width:1%;color:#5a5a58;font-size:11px}'
    + '.sA-bin{width:1%;font-family:monospace;font-weight:700;color:#111;font-size:11px}'
    + '.sA-sku div{width:118px;white-space:normal;overflow-wrap:break-word}'
    + '.sA-bin div{width:150px;white-space:normal;overflow-wrap:break-word}'
    + '.sA-item{color:#5a5a58;font-size:11px;min-width:200px}'
    + '.sA-ttl{display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}'
    + '.sA-count{font-size:11px;color:#5a5a58;padding-top:8px}'
    + '.sA-gift{margin-top:18px;border-left:2px solid #141414;padding:4px 0 4px 14px;font-family:Georgia,serif;font-style:italic;font-size:12.5px;line-height:1.6;white-space:pre-wrap}'
    + '.sA-glbl{font-family:Arial,Helvetica,sans-serif;font-style:normal;font-size:9.5px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#5a5a58;margin-bottom:4px}'
    + '.sA-foot{margin-top:14px;text-align:center}'
    + '.sA-frule{border-top:1px solid #d9d7d2;width:90px;margin:0 auto 12px}'
    + '.sA-fmsg{font-family:Georgia,serif;font-style:italic;font-size:12.5px;color:#5a5a58;line-height:1.6;white-space:pre-wrap}'
    + 'h1{font-size:18px;margin:0 0 2px} .sub{color:#555;margin-bottom:14px}'
    + 'table{border-collapse:collapse;width:100%} th,td{border:1px solid #999;padding:5px 8px;text-align:left;vertical-align:top}'
    + 'th{background:#eee;font-size:10px;text-transform:uppercase} .qty{font-size:14px;font-weight:700;text-align:center}'
    // Letter width on screen too, so the paginator measures rows at the width they print at
    // (titles wrapped differently in a wide popup and rows came out taller in print — 9-17)
    + '.page{page-break-after:always;padding:0.5in;box-sizing:border-box;width:8.5in} .page:last-child{page-break-after:auto}'
    // Measured pagination (shipPaginateDoc): exact one-sheet pages + footer stamp
    + '.pfixed{position:relative;height:11in;overflow:hidden}'
    + '.pbody{height:calc(10in - 40px);overflow:hidden}'
    + '.pfoot{position:absolute;bottom:0.26in;right:0.5in;font-size:10px;color:#555}'
    + '.sA-cont{font-size:10px;font-style:italic;color:#5a5a58;margin:-4px 0 8px}'
    + '.addr{margin:6px 0 12px;line-height:1.5} .mono{font-family:monospace}'
    // Docs not built from .page blocks (pick list, order summary) get wrapped
    // so they pick up the same in-content margins.
    + '</style></head><body>' + (bodyHtml.indexOf('class="page"') !== -1 ? bodyHtml : '<div class="page">' + bodyHtml + '</div>') + '</body></html>');
  w.document.close();
  w.focus();
  // Print after subresources (the logo) load — 250ms used to race the image.
  var fired = false;
  var go = function() { if (fired) return; fired = true; if (opts && opts.paginate === 'slips') shipPaginateSlips(w); else if (opts && opts.paginate) shipPaginateDoc(w); try { w.print(); } catch (e) {} };
  try { w.addEventListener('load', function() { setTimeout(go, 60); }); } catch (e) {}
  setTimeout(go, 2600); // fallback — pick lists can carry many product thumbnails
}

// Session caches for settings lists (ship-from addresses, package templates,
// tax profiles) and printing config — shared by the order panel, rule editor,
// and print templates. Refreshing the page picks up settings edits.
// Refreshed after a Settings save (shipClearSettingsCaches) and after 5 minutes regardless — an
// edit made in another tab or by another user used to wait for a page reload (sweep 2026-09-27
// LM20); a failed fetch is not cached.
var _shipListCache = {};
var SHIP_SETTINGS_TTL_MS = 5 * 60 * 1000;
function shipFetchList(path, key) {
  var hit = _shipListCache[path];
  if (!hit || Date.now() - hit.at > SHIP_SETTINGS_TTL_MS) {
    var p = api(path).then(function(r) { return r[key] || []; }).catch(function() { if (_shipListCache[path] && _shipListCache[path].p === p) delete _shipListCache[path]; return []; });
    hit = _shipListCache[path] = { at: Date.now(), p: p };
  }
  return hit.p;
}
var _shipPrintCfg = null;
function shipGetPrintCfg() {
  if (!_shipPrintCfg || Date.now() - _shipPrintCfg.at > SHIP_SETTINGS_TTL_MS) {
    var p = api('/shipping/config/printing').catch(function() { if (_shipPrintCfg && _shipPrintCfg.p === p) _shipPrintCfg = null; return {}; });
    _shipPrintCfg = { at: Date.now(), p: p };
  }
  return _shipPrintCfg.p;
}
function shipClearSettingsCaches() { _shipListCache = {}; _shipPrintCfg = null; }

// Aggregated pick list across orders, sorted for a bin walk. Logo, checkbox
// column, and signature line are Settings > Printing options; each bin shows
// the live on-hand remaining in parentheses.
function shipPrintPickList(rows, popts) {
  var win = (popts && popts.win) || shipOpenPrintWindow('Pick List'); // opened inside the click (9-27 M17)
  shipGetPrintCfg().then(function(cfg) {
    var showChk = cfg.pick_checkboxes !== false;
    var showSig = cfg.pick_signature !== false;
    var showLogo = cfg.show_logo !== false;
    var agg = {};
    var units = 0;
    var batches = [];
    rows.forEach(function(o) {
      if (o.batch_number && batches.indexOf(o.batch_number) === -1) batches.push(o.batch_number);
      (o.lines || []).forEach(function(l) {
        var q = parseInt(l.qty, 10) || 0;
        units += q;
        // one pick row per SKU AND bin (sweep 2026-09-27 Lows): the same SKU routed to two
        // warehouses showed one bin for the combined quantity
        var ak = l.sku + '|' + (l.location || '');
        if (!agg[ak]) agg[ak] = { sku: l.sku, title: l.title || l.sku, location: l.location || '', image_url: l.image_url || null, barcode: l.barcode || null, qty: 0 };
        if (!agg[ak].image_url && l.image_url) agg[ak].image_url = l.image_url;
        if (!agg[ak].barcode && l.barcode) agg[ak].barcode = l.barcode;
        agg[ak].qty += q;
      });
    });
    var items = Object.keys(agg).map(function(k) { return agg[k]; })
      .sort(function(a, b) { return String(a.location || '~').localeCompare(String(b.location || '~')) || a.sku.localeCompare(b.sku); });
    api('/shipping/pick-stock', { method: 'POST', body: JSON.stringify({ skus: items.map(function(it) { return it.sku; }) }) })
      .catch(function() { return { stock: {} }; })
      .then(function(sr) {
        var stock = (sr && sr.stock) || {};
        // No logo (user ask 8-12); .phead repeats on every page via shipPaginateDoc
        var html = '<div class="phead">'
          + '<h1>Pick List' + (batches.length ? ' — Batch ' + batches.slice(0, 3).join(', ') + (batches.length > 3 ? ' +' + (batches.length - 3) : '') : '') + '</h1>'
          + '<div class="sub">' + new Date().toLocaleString() + ' · '
          + rows.length + ' orders · ' + units + ' units · ' + Object.keys(items.reduce(function(m, it) { m[it.sku] = 1; return m; }, {})).length + ' SKUs</div></div>'
          + '<table><thead><tr><th style="width:64px"></th>' + (showChk ? '<th style="width:24px"></th>' : '') + '<th style="width:92px">Bin</th><th style="width:44px">Qty</th><th>Item</th><th style="width:118px">SKU</th><th style="width:1%">UPC</th></tr></thead><tbody>'
          + items.map(function(it) {
              var st = stock[it.sku];
              return '<tr>'
                + '<td style="padding:3px 6px">' + (it.image_url ? '<img src="' + shipEsc(it.image_url) + '" style="width:56px;height:56px;object-fit:contain;display:block" onerror="this.style.display=&quot;none&quot;">' : '') + '</td>'
                + (showChk ? '<td style="font-size:14px">&#9744;</td>' : '')
                // Bin wraps inside a fixed width so SKU and UPC never push past the page edge (Anthony 9-17)
                + '<td class="mono" style="font-weight:700;width:92px;max-width:92px;white-space:normal;word-break:break-word">' + shipEsc(it.location || '—')
                +   (st != null ? ' <span style="font-weight:400;color:#555">(' + st + ')</span>' : '') + '</td>'
                + '<td class="qty">' + it.qty + '</td><td>' + shipEsc(it.title) + '</td><td class="mono" style="width:118px;max-width:118px;white-space:normal;word-break:break-all">' + shipEsc(it.sku) + '</td>'
                + '<td class="mono" style="width:1%;white-space:nowrap">' + shipEsc(it.barcode || '') + '</td></tr>';
            }).join('')
          + '</tbody></table>'
          + (showSig ? '<div style="margin-top:26px;display:flex;gap:40px;color:#555">'
            + '<div>Picked by: ______________________</div><div>Checked by: ______________________</div><div>Date: ____________</div></div>' : '');
        shipPrintWindow('Pick List', html, { paginate: true, win: win });
      });
  }).catch(function(e) { shipClosePrintWindow(win); shipShellToast('Pick list failed: ' + String((e && e.message) || e), 'error'); });
}

// Slips need the full order (recipient address, priced lines, totals) — queue
// rows only carry summaries, so anything without .recipient is fetched. Rows
// that already ARE details pass straight through.
// No cap on how many orders print (Anthony 2026-09-28: Paula's bulk batch of 156 printed 150
// packing slips and an alert, and the 6 left over had no way to be printed on their own). A slip is
// a few KB of HTML and the details load three at a time.
// All or nothing (Anthony 2026-10-29: "it should be fetching all or nothing"): a print never goes
// out short. Each fetch gets SHIP_ORDER_FETCH_TIMEOUT_MS — a response that never arrived used to
// hang the print until the page was reloaded, and the reload then counted that order as "not
// loaded" (error_log 891, batch 1000445). If any order fails the promise REJECTS naming the orders
// and the caller prints nothing; the user clicks again (no automatic retry — Anthony's pick).
var SHIP_ORDER_FETCH_TIMEOUT_MS = 15000;
function shipResolveOrderDetails(rows) {
  var list = (rows || []).slice();
  var out = new Array(list.length);
  var failed = [];
  var i = 0;
  function fetchOne(r) {
    var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctl ? setTimeout(function() { ctl.abort(); }, SHIP_ORDER_FETCH_TIMEOUT_MS) : null;
    return api('/shipping/order/' + r.id, ctl ? { signal: ctl.signal } : {})
      .finally(function() { if (timer) clearTimeout(timer); });
  }
  function lane() {
    if (i >= list.length) return Promise.resolve();
    var idx = i++;
    var r = list[idx];
    if (r && r.recipient) { out[idx] = r; return lane(); }
    return fetchOne(r)
      .then(function(d) { out[idx] = d; })
      .catch(function(e) { failed.push({ row: r, error: e }); })
      .then(lane);
  }
  var lanes = [];
  for (var k = 0; k < 3; k++) lanes.push(lane());
  return Promise.all(lanes).then(function() {
    if (failed.length) {
      var names = failed.map(function(f) { return String((f.row && (f.row.order_number || f.row.id)) || '?'); });
      var err = new Error(failed.length + ' of ' + list.length + ' orders could not be loaded (' + names.slice(0, 5).join(', ')
        + (names.length > 5 ? ' +' + (names.length - 5) + ' more' : '') + ') — nothing was printed; try again');
      err.failedOrders = failed;
      throw err;
    }
    return out;
  });
}

// One packing slip per order (page break between orders). "Merchant Classic"
// design (approved 8-2): logo-only header, Qty/SKU/Item table, no pricing
// anywhere. Logo and footer come from Settings > Printing; the header line
// prints only when the logo is turned off (fallback branding).
function shipPrintPackingSlips(rows, popts) {
  var win = (popts && popts.win) || shipOpenPrintWindow('Packing Slips'); // opened inside the click (9-27 M17)
  var stock = {};
  // resolves true once the slips reach the print window, false when nothing printed (the batch
  // dialog stamps "slips printed" only on true — all or nothing, 2026-10-29)
  return Promise.all([shipGetPrintCfg(), shipResolveOrderDetails(rows).then(function(details) {
    var skus = {}; details.forEach(function(d) { ((d && d.lines) || []).forEach(function(l) { if (l.sku) skus[l.sku] = true; }); });
    return api('/shipping/pick-stock', { method: 'POST', body: JSON.stringify({ skus: Object.keys(skus) }) })
      .catch(function() { return { stock: {} }; })
      .then(function(sr) { stock = (sr && sr.stock) || {}; return details; });
  })]).then(function(res) {
    var cfg = res[0], details = res[1];
    if (!details.length) { shipClosePrintWindow(win); appAlert('No orders to print.'); return false; }
    // every order loaded, or shipResolveOrderDetails rejected and the catch below printed nothing
    var showLogo = cfg.show_logo !== false;
    var fmtDate = function(v) {
      try { return new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
      catch (e) { return String(v).slice(0, 10); }
    };
    var html = details.map(function(d) {
      var r = d.recipient || {};
      var addr = [r.name, r.address1, r.address2,
        [r.city, r.state, r.zip].filter(Boolean).join(', ') + ((r.country && r.country !== 'US') ? ' · ' + r.country : '')]
        .filter(function(x) { return x && String(x).trim(); });
      var lines = d.lines || [];
      // The picture steps down with the line count so a slip stays on one sheet (Paula 2026-09-29: four
      // items already spilled to a second page at 90px): 64px up to 6 lines, 48px to 9, 40px beyond.
      var imgPx = lines.length > 9 ? 40 : lines.length > 6 ? 48 : 64;
      var units = 0;
      lines.forEach(function(l) { units += parseInt(l.qty, 10) || 0; });
      return '<div class="page">'
        + '<div class="sA-head">'
        +   '<div>'
        +     (showLogo
              ? '<img src="/api/settings/logo" style="max-height:52px;max-width:220px;display:block" onerror="this.style.display=&quot;none&quot;">'
              : (cfg.slip_header ? '<div style="font-family:Georgia,serif;font-size:19px">' + shipEsc(cfg.slip_header) + '</div>' : ''))
        +   '</div>'
        +   '<div style="text-align:right">'
        +     '<div class="sA-eyebrow">Packing Slip</div>'
        +     '<div style="display:flex;align-items:center;justify-content:flex-end;gap:12px">'
        +       '<div class="sA-ord">№ ' + shipEsc(d.order_number) + '</div>'
        // the Scan Station reads this (cookie auth, so a plain <img> works in the print window; 2026-09-23);
        // beside the number rather than under it — the stacked header cost a row per slip (Paula 2026-09-29)
        +       '<img class="sA-bar" alt="" src="/api/shipping/barcode.png?text=' + encodeURIComponent(d.order_number) + '" style="height:36px;display:block" onerror="this.style.display=&quot;none&quot;">'
        +     '</div>'
        +     '<div class="sA-dates">' + fmtDate(d.order_date) + '</div>'
        +   '</div>'
        + '</div>'
        + '<div class="sA-cols">'
        +   '<div style="flex:1"><div class="sA-lbl">Ship to</div>'
        +     (r.name ? '<div class="sA-name">' + shipEsc(r.name) + '</div>' : '')
        +     '<div class="sA-addr">' + (addr.length ? addr.filter(function(x) { return x !== r.name; }).map(shipEsc).join('<br>') : '') + '</div></div>'
        +   '<div style="flex:1"><div class="sA-lbl">Order Details</div>'
        +     '<div class="sA-val">Order #: ' + shipEsc(d.order_number)
        +       (d.channel_order_id ? '<br>Channel Order #: ' + shipEsc(d.channel_order_id) : '') + '</div></div>'
        + '</div>'
        // thead / tbody so the column headings repeat and shipPaginateSlips can split the rows
        + '<table class="sA-items"><thead><tr><th class="sA-img"></th><th class="sA-qty">Qty</th><th>Item</th><th class="sA-sku">SKU</th><th class="sA-bin">Bin</th><th class="sA-upc">UPC</th></tr></thead><tbody>'
        + lines.map(function(l) {
            // Product picture, same source as the pick list (Anthony 9-17)
            return '<tr><td class="sA-img">' + (l.image_url ? '<img src="' + shipEsc(l.image_url) + '" style="width:' + imgPx + 'px;height:' + imgPx + 'px;object-fit:contain;display:block" onerror="this.style.display=&quot;none&quot;">' : '') + '</td>'
              + '<td class="sA-qty">' + (parseInt(l.qty, 10) || 0) + '</td><td class="sA-item"><div class="sA-ttl">' + shipEsc(l.title || '') + '</div>'
              + (l.kit_qty ? '<div style="font-size:10.5px;color:#5a5a58;margin-top:2px">' + shipEsc('ordered as ' + l.kit_qty + ' × ' + (l.kit_title || 'kit')) + '</div>' : '')
              // bins wrap at the slashes (<wbr>) inside the capped column; the slip goes to the customer: no "—" placeholders (9-27 Lows)
              + '</td><td class="sA-sku"><div>' + shipEsc(l.sku) + '</div></td><td class="sA-bin"><div>' + shipEsc(l.location || '').replace(/\//g, '/<wbr>') + (stock[l.sku] != null ? ' <span style="font-weight:400;color:#777">(' + shipEsc(String(stock[l.sku])) + ')</span>' : '') + '</div></td><td class="sA-upc">' + shipEsc(l.barcode || '') + '</td></tr>';
          }).join('')
        + '</tbody></table>'
        + '<div class="sA-count">' + lines.length + ' line' + (lines.length === 1 ? '' : 's') + ' · ' + units + ' unit' + (units === 1 ? '' : 's') + '</div>'
        + (d.buyer_notes ? '<div class="sA-gift"><div class="sA-glbl">Gift message</div>' + shipEsc(d.buyer_notes) + '</div>' : '')
        + (cfg.slip_footer ? '<div class="sA-foot"><div class="sA-frule"></div><div class="sA-fmsg">' + shipEsc(cfg.slip_footer) + '</div></div>' : '')
        + '</div>';
    }).join('');
    shipPrintWindow('Packing Slips', html, { win: win, paginate: 'slips' }); // long slips split into exact pages with the header repeated
    return true;
  }).catch(function(e) { shipClosePrintWindow(win); shipShellToast('Packing slips failed: ' + String((e && e.message) || e), 'error'); return false; });
}

// Order Date presets resolve RELATIVE at filter time, so a saved "Today"
// filter tab is always today. Custom uses the stored dateFrom/dateTo.
var SHIP_DATE_PRESETS = [
  ['', 'All dates'], ['today', 'Today'], ['yesterday', 'Yesterday'],
  ['7d', 'Last 7 Days'], ['30d', 'Last 30 Days'],
  ['month', 'This Month'], ['lastmonth', 'Last Month'], ['custom', 'Custom Range'],
];

function shipResolveDateRange(filters) {
  var p = filters.datePreset || '';
  var iso = function(dt) { return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
  var now = new Date();
  var today = iso(now);
  if (!p || p === 'custom') return { from: filters.dateFrom || null, to: filters.dateTo || null };
  if (p === 'today') return { from: today, to: today };
  if (p === 'yesterday') { var ys = iso(new Date(now.getTime() - 86400000)); return { from: ys, to: ys }; }
  if (p === '7d') return { from: iso(new Date(now.getTime() - 6 * 86400000)), to: today };
  if (p === '30d') return { from: iso(new Date(now.getTime() - 29 * 86400000)), to: today };
  if (p === 'month') return { from: today.slice(0, 8) + '01', to: today };
  if (p === 'lastmonth') {
    return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  }
  return { from: null, to: null };
}

function shipDateLabel(filters) {
  var p = filters.datePreset || '';
  if (p && p !== 'custom') {
    var hit = SHIP_DATE_PRESETS.find(function(x) { return x[0] === p; });
    return hit ? hit[1] : 'Order Date';
  }
  if (filters.dateFrom || filters.dateTo) {
    return (filters.dateFrom || '…') + ' – ' + (filters.dateTo || '…');
  }
  return 'Order Date';
}

// "Other" filter menu defaults (ShipStation's Other menu) — all client-side,
// serialized into saved filters alongside the primary bar.
// One set of defaults, shared with the server-side matcher (app-ship-filter.js, 2026-09-23).
// Text-field operators (Anthony 9-17): '' = Contains (what every saved filter set means), 'exact', 'starts'
var SHIP_EMPTY_OTHER = SHIP_FILTER_EMPTY_OTHER;

// Search-as-you-type for the app's search boxes (Anthony 9-18): the committed search follows
// the live text after a 500 ms typing pause, at once on paste or when the box is cleared, and
// Enter still commits immediately — every path is the same commit, so an auto-commit behaves
// exactly like pressing Enter (Orders page: including the hand-off to Shipments). `commit`
// gets the trimmed text; a caller that normalises further (Batches lowercases) wraps it.
// Call at the component top (it is a hook) and spread the result onto the input.
var SHIP_SEARCH_PAUSE_MS = 500;
// commit(value, via) — via is 'enter' | 'paste' | 'pause' (2026-09-22): the queue only auto-jumps to
// a shipped order on Enter or an exact match, never on a mid-typing pause.
function useShipAutoSearch(live, setLive, commit) {
  var timerRef = useRef(null);
  var pasteRef = useRef(false);
  var firstRef = useRef(true);
  var fire = function(v) { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } commit(v, 'enter'); };
  useEffect(function() {
    if (firstRef.current) { firstRef.current = false; return; } // mount: the initial (URL) value is already committed
    var v = live.trim();
    var via = pasteRef.current ? 'paste' : 'pause';
    var ms = (pasteRef.current || v === '') ? 0 : SHIP_SEARCH_PAUSE_MS;
    pasteRef.current = false;
    timerRef.current = setTimeout(function() { timerRef.current = null; commit(v, via); }, ms);
    return function() { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  }, [live]);
  return {
    onChange: function(e) { setLive(e.target.value); },
    // The change event that follows a paste commits without the pause (the flag expires in
    // case the paste changed nothing and no change event comes)
    onPaste: function() { pasteRef.current = true; setTimeout(function() { pasteRef.current = false; }, 60); },
    onKeyDown: function(e) { if (e.key === 'Enter') fire(live.trim()); },
  };
}

// One matcher for the Other menu's text fields
function shipTextMatch(hay, needle, op) {
  var h2 = String(hay || '').toLowerCase(), n = String(needle || '').toLowerCase();
  if (op === 'exact') return h2 === n;
  if (op === 'starts') return h2.indexOf(n) === 0;
  return h2.indexOf(n) !== -1;
}

// Red box for a field the last label attempt rejected (reason = tooltip); labels stay one line
var SHIP_ERR_BOX = { border: '1px solid #EF4444', boxShadow: '0 0 0 1px rgba(239,68,68,0.35)' };
var SHIP_OK_BOX = { border: '1px solid #22C55E', boxShadow: '0 0 0 1px rgba(34,197,94,0.35)' };
// warn = '' (no fault) | { fixed: bool, text } → the matching box style
function shipLocalFixed(reason, value) {
  var r = reason || '', v = String(value == null ? '' : value).trim();
  var cap = /at most (\d+) characters/i.exec(r) || /\(max (\d+)\)/i.exec(r);
  if (cap) return v.length <= parseInt(cap[1], 10);
  if (/required|missing/i.test(r)) return v.length > 0;
  return null;
}
function shipErrFixed(warn, value) { return !!warn && (warn.fixed || (value !== undefined && shipLocalFixed(warn.text, value) === true)); }
function shipErrBox(warn, value) { return !warn ? {} : (shipErrFixed(warn, value) ? SHIP_OK_BOX : SHIP_ERR_BOX); }
function shipErrTip(warn, value) { return !warn ? '' : (shipErrFixed(warn, value) ? 'Fixed: ' + warn.text + ' · run Create labels to clear it' : warn.text); }
function ShipCountedField({ label, cur, ph, warn, max, inpS, onCommit }) {
  var [v, setV] = useState(cur == null ? '' : String(cur));
  useEffect(function() { setV(cur == null ? '' : String(cur)); }, [cur]);
  var commit = function() { var t = v.trim(); if (t !== String(cur == null ? '' : cur)) onCommit(t || null); };
  return h('div', { style: { width: '100%', flexShrink: 0 } },
    h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
      label + ' (' + v.length + '/' + max + ')'),
    h('input', {
      value: v, placeholder: ph || '', title: shipErrTip(warn, v) || ph || '', maxLength: max,
      onChange: function(e) { setV(e.target.value); },
      onKeyDown: function(e) { if (e.key === 'Enter') e.target.blur(); },
      onBlur: commit,
      style: Object.assign({}, inpS, { width: '100%' }, shipErrBox(warn, v))
    }));
}

// Click-to-copy icon for one value — the app's copyBadge feedback (toast "Copied …",
// no in-control state flash). `box` = button size in px (matches the row's control height).
function shipCopyIcon(value, showToast, box) {
  var v = value == null ? '' : String(value).trim();
  // Always rendered so every row lines up (Anthony 9-18: "any field without value should
  // still get the copy button"); an empty field's icon is dimmed and inert
  return h('span', {
    onClick: function(e) {
      e.stopPropagation();
      if (!v) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
      } else showToast('Copy failed', 'error');
    },
    onMouseEnter: v ? function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; } : undefined,
    onMouseLeave: v ? function(e) { e.currentTarget.style.filter = 'brightness(1)'; } : undefined,
    title: v ? 'Copy ' + v : 'Nothing to copy',
    style: { cursor: v ? 'pointer' : 'default', opacity: v ? 1 : 0.45, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: box || 28, height: box || 28, borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, flexShrink: 0, transition: 'filter 0.15s' }
  }, h(LIcon, { name: 'clipboard', size: 13, color: C.textMuted }));
}
// Read-only value box (Shipments detail) with the copy icon beside it
function shipRoCopyBox(value, showToast) {
  return h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 } },
    h('div', { style: { flex: 1, minWidth: 0, color: C.text, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', boxSizing: 'border-box', height: 35, lineHeight: '19px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, value || ''),
    shipCopyIcon(value, showToast, 35));
}

// A product-editor Save applied to an order line the way the server reads it back:
// blanks are NULL; a blank HS / COO override keeps the line's Zentail-sourced value
// (the server folds it in again); a typed value replaces it and drops the "· Zentail"
// hint. Used by the panel and the order modal so the amber HS flag on the queue row
// follows the save instead of waiting for the next reload (9-18).
function shipApplyCustomsSave(line, body) {
  var out = Object.assign({}, line);
  ['hs_code', 'country_of_origin', 'customs_value', 'customs_description'].forEach(function(k) {
    if (!body || !(k in body)) return;
    var v = body[k] == null ? '' : String(body[k]).trim();
    var srcKey = k === 'hs_code' ? 'hs_source' : k === 'country_of_origin' ? 'coo_source' : null;
    if (!v && srcKey && line[srcKey] === 'zentail') return; // override cleared → Zentail value stays
    out[k] = v || null;
    if (srcKey) out[srcKey] = null;
  });
  return out;
}

;
/* ===== app-shipping-order-panel.js ===== */
// ─── Shipping app: the order panel (docked + modal) ─── split from app-shipping.js 2026-09-19
// What the panel lets the user do with the order (9-26): a bought label (not a voided reship)
// locks the Shipping form and hides Create Label like shipped/cancelled do — the panel was
// still offering a second purchase while Zentail caught up.
function shipPanelLocks(d) {
  if (!d) return { form: true, createLabel: true, autosave: false };
  var reship = !!(d.overrides && d.overrides.reship);
  var cancelled = d.status === 'CANCELLED' || !!d.cancelled_here;
  var shipped = d.status === 'SHIPPED' && !reship;
  var labelBought = !reship && !!(d.labels && d.labels.length);
  var locked = cancelled || shipped || labelBought;
  return { form: locked, createLabel: locked, autosave: !locked, labelBought: labelBought };
}
// The ship-to editor's seed: the MERGED address (recipient + any saved override), every field
// the editor shows (address3 / email included)
function shipSeedShipTo(recipient, ovr) {
  var r = Object.assign({}, recipient || {}, ovr || {});
  return Object.assign({ name: r.name || '', address1: r.address1 || '', address2: r.address2 || '', address3: r.address3 || '', city: r.city || '', state: r.state || '', zip: r.zip || '', country: r.country || 'US', phone: r.phone || '', email: r.email || '' }, ovr || {});
}
// The ship_to the autosave sends (9-26): opening an order with an address error seeds the
// editor, which is NOT an edit — only typed changes (touched) replace the address; otherwise
// the saved override (or none) is sent back unchanged.
function shipAutosaveShipTo(touched, shipTo, savedOverride) {
  if (!touched) return savedOverride || null;
  var st = shipTo || {};
  var has = Object.keys(st).some(function(k) { return st[k] && String(st[k]).trim(); });
  return has ? st : null;
}
window.shipPanelLocks = shipPanelLocks;
window.shipSeedShipTo = shipSeedShipTo;
window.shipAutosaveShipTo = shipAutosaveShipTo;
// Multi-box editor (UPS, 2026-10-29): the "Boxes" row under Size. One parcel = a link that starts a
// two-box list seeded from the Weight / Size fields; each box = weight (lb) + L×W×H steppers and a
// remove ✕; "+ Add box" / "Single parcel" under the list. Fewer than two boxes = single parcel.
function ShipBoxesEditor({ boxes, setBoxes, docked, rowS, lblS, seed }) {
  var linkS = { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'inherit' };
  var dim = { color: C.textDim, fontSize: 11 };
  var setBox = function(i, k, v) { setBoxes(boxes.map(function(b, j) { if (j !== i) return b; var n = Object.assign({}, b); n[k] = v; return n; })); };
  var remove = function(i) { var n = boxes.filter(function(_, j) { return j !== i; }); setBoxes(n.length >= 2 ? n : null); };
  if (!boxes) {
    return h('div', { style: rowS },
      h('span', { style: lblS }, 'Boxes'),
      h('button', { type: 'button', onClick: function() { setBoxes([seed(), seed()]); },
        title: 'Ship this order as several boxes — one UPS shipment, a label per box', style: linkS }, '+ Several boxes (UPS)'));
  }
  return h(React.Fragment, null,
    h('div', { style: rowS },
      h('span', { style: lblS }, 'Boxes'),
      h('span', { style: { fontSize: 12, color: C.textMuted } }, boxes.length + ' boxes · ships UPS, one label each')),
    boxes.map(function(b, i) {
      return h('div', { key: i, style: rowS },
        h('span', { style: lblS }, 'Box ' + (i + 1)),
        h(ShipStepper, { value: b.weight_lb, placeholder: 'lb', step: 0.5, width: 52, title: 'Weight (lb)', onChange: function(v) { setBox(i, 'weight_lb', v); } }),
        h('span', { style: dim }, 'lb'),
        ['length', 'width', 'height'].map(function(k, j) {
          return h(React.Fragment, { key: k },
            h(ShipStepper, { value: b[k], placeholder: 'LWH'[j], step: 0.5, width: 44, onChange: function(v) { setBox(i, k, v); } }),
            j < 2 && !docked && h('span', { style: dim }, '×'));
        }),
        h('button', { type: 'button', onClick: function() { remove(i); }, title: 'Remove this box', style: Object.assign({}, linkS, { color: C.textMuted }) }, '✕'));
    }),
    h('div', { style: rowS },
      h('span', { style: lblS }, ''),
      h('button', { type: 'button', onClick: function() { setBoxes(boxes.concat([seed()])); }, style: linkS }, '+ Add box'),
      h('span', { style: dim }, ' · '),
      h('button', { type: 'button', onClick: function() { setBoxes(null); }, style: linkS }, 'Single parcel')));
}
// Residential / Commercial chip next to the address's Validated chip (Anthony 2026-10-29: "add a tag
// to the address itself"): what computed.residential says and where it came from (USPS / set by
// hand / default). Residential reads amber — it is the UPS surcharge case.
function shipAddressTypeChip(d, extraStyle) {
  if (!d || !d.computed || d.computed.residential == null) return null;
  var res = d.computed.residential !== false;
  var how = ({ usps: 'USPS', company: 'company name', override: 'set by hand', default: 'default — no USPS flag on this address yet' })[d.computed.residential_source] || d.computed.residential_source || '';
  return h('span', {
    title: (res ? 'Residential' : 'Commercial') + ' (' + how + ')' + (res ? ' — UPS adds its residential surcharge' : ''),
    style: Object.assign({ display: 'inline-block', fontSize: 9, fontWeight: 500, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4,
      background: res ? (C.warningSoft || C.accentSoft) : C.bg, color: res ? C.warning : C.textMuted, border: res ? 'none' : '1px solid ' + C.border }, extraStyle || {}),
  }, res ? 'Residential' : 'Commercial');
}
// What the autosave sends for boxes: the list once EVERY box is complete (numbers); null for a
// single parcel (removes saved boxes); undefined while a box is still being typed (keeps what is saved)
function shipBoxesForSave(boxes) {
  if (boxes === null) return null;
  if (!Array.isArray(boxes) || boxes.length < 2) return undefined;
  var out = boxes.map(function(b) {
    return { weight_lb: parseFloat(b.weight_lb) || 0, length: parseFloat(b.length) || 0, width: parseFloat(b.width) || 0, height: parseFloat(b.height) || 0 };
  });
  return out.every(function(b) { return b.weight_lb > 0 && b.length > 0 && b.width > 0 && b.height > 0; }) ? out : undefined;
}

function ShipOrderPanel({ orderId, onClose, onChanged, showToast, docked, embedded, onDetail, reloadRef, onRates, onEditManual, onRowPatch, onMarkShipped }) {
  // Header Batch ▾ / Print ▾ menus (ShipStation single-order panel parity, 8-12)
  var [hdrBatchOpen, setHdrBatchOpen] = useState(false);
  var [hdrPrintOpen, setHdrPrintOpen] = useState(false);
  var [hdrBatches, setHdrBatches] = useState([]);
  useEffect(function() {
    if (!hdrBatchOpen) return;
    api('/shipping/batches').then(function(r) { setHdrBatches(Array.isArray(r) ? r : (r && r.batches) || []); }).catch(function() {});
  }, [hdrBatchOpen]);
  // Single-order tag editor (registry list loads on open; queue refreshes ONCE
  // on close — not per toggle)
  var [tagEditOpen, setTagEditOpen] = useState(false);
  var [panelTags, setPanelTags] = useState([]);
  var panelTagsDirtyRef = useRef(false);
  useEffect(function() {
    if (tagEditOpen) {
      api('/shipping/tags').then(function(r) { setPanelTags(Array.isArray(r) ? r : (r && r.tags) || []); }).catch(function() {});
    } else if (panelTagsDirtyRef.current) {
      panelTagsDirtyRef.current = false;
      if (onChanged) onChanged();
    }
  }, [tagEditOpen]);
  // Click-away closes the header Batch/Print menus + tag editor. CAPTURE-phase
  // mousedown — bubbling clicks get stopPropagation'd inside panels/modals.
  var hdrBatchRef = useRef(null);
  var hdrPrintRef = useRef(null);
  var tagEditRef = useRef(null);
  var createMenuRef = useRef(null);
  var [createMenuOpen, setCreateMenuOpen] = useState(false);
  var [createMenuPos, setCreateMenuPos] = useState(null);
  var [labelBusy, setLabelBusy] = useState(false);
  var [voidBusy, setVoidBusy] = useState(false);
  var [valBusy, setValBusy] = useState(false);
  // Buy the real label (DHL direct, Phase L1) — the server resolves carrier/
  // service (override > rule > rate winner), posts Zentail, returns the file
  var labelBusyRef = useRef(false);
  function createLabel(print, authorize) {
    if (labelBusy || labelBusyRef.current) return;
    labelBusyRef.current = true;
    setLabelBusy(true);
    var release = function() { labelBusyRef.current = false; setLabelBusy(false); };
    // Flush a pending autosave FIRST (9-26): the label must be bought on the saved form, not
    // on what the server had 900 ms ago
    Promise.resolve(flushAutosave()).then(function(saved) {
      if (saved === false) { release(); showToast('Changes not saved — label not bought', 'error'); return; }
      return api('/shipping/create-label', { method: 'POST', body: JSON.stringify({ order_ids: [orderId], authorize_usps: authorize === true }) })
      .then(async function(r) {
        release();
        // A second USPS payment authorization today = another card hold: the server stops and asks
        if (r.needs_usps_authorization && r.halted) {
          if (await appConfirm(r.halted, { okLabel: 'Authorize' })) createLabel(print, true);
          return;
        }
        var err = r.errors && r.errors[0];
        if (err) { showToast(err.error || 'Label failed', 'error'); return; }
        var out = r.results && r.results[0];
        if (!out) { showToast('No label created', 'error'); return; }
        showToast('Label created · ' + (out.tracking || out.order_number), 'success');
        if (out.zentail_error) showToast(out.zentail_error, 'error');
        if (print && out.shipment_id) shipPrintLabelFile(out.shipment_id);
        loadDetail();
        onChanged && onChanged();
      });
    }).catch(function(e) { release(); showToast(String(e.message || e), 'error'); });
  }
  useEffect(function() {
    if (!hdrBatchOpen && !hdrPrintOpen && !tagEditOpen && !createMenuOpen) return;
    var onDown = function(e) {
      if (hdrBatchRef.current && hdrBatchRef.current.contains(e.target)) return;
      if (hdrPrintRef.current && hdrPrintRef.current.contains(e.target)) return;
      if (tagEditRef.current && tagEditRef.current.contains(e.target)) return;
      if (createMenuRef.current && createMenuRef.current.contains(e.target)) return;
      setHdrBatchOpen(false); setHdrPrintOpen(false); setTagEditOpen(false); setCreateMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return function() { document.removeEventListener('mousedown', onDown, true); };
  }, [hdrBatchOpen, hdrPrintOpen, tagEditOpen, createMenuOpen]);
  var hdrAddToBatch = function(batchId) {
    setHdrBatchOpen(false);
    // move: a person picked the batch for this one order — it may leave another open batch (2026-09-28)
    api('/shipping/batches', { method: 'POST', body: JSON.stringify({ order_ids: [orderId], batch_id: batchId, move: true }) })
      .then(function(r) {
        if (r.skipped && !r.added) showToast('Not added — this order already has a label', 'error');
        else showToast('Added to Batch ' + (r.batch_number || ''), 'success');
        if (onChanged) onChanged();
        try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var [d, setD] = useState(null);
  var [err, setErr] = useState(null);
  var [rates, setRates] = useState(null);
  var [lb, setLb] = useState('');
  var [oz, setOz] = useState('');
  var [svcMode, setSvcMode] = useState('manual'); // 'rate_shop' | 'manual' (set from the order's assignment on load)
  var [ovCarrier, setOvCarrier] = useState('');
  var [ovService, setOvService] = useState('');
  var [dims, setDims] = useState({ length: '', width: '', height: '' });
  // Multi-box shipments (UPS, 2026-10-29): null = one parcel (the Weight / Size fields);
  // [{ weight_lb, length, width, height }, …] = two or more boxes, one UPS label each
  var [boxes, setBoxes] = useState(null);
  var [confirmation, setConfirmation] = useState('none');
  var [addressType, setAddressType] = useState(''); // '' = Auto | 'residential' | 'commercial' (2026-10-29)
  var [flags, setFlags] = useState({});
  var [saving, setSaving] = useState(false);
  var [ovPackage, setOvPackage] = useState('');
  var [customF, setCustomF] = useState({ custom1: '', custom2: '', custom3: '' });
  // The field holds the EFFECTIVE value (typed override, else what Zentail sends) as real,
  // selectable text; it counts as an override only when it differs from the Zentail default
  // (Anthony 9-18: the placeholder version "isn't copyable")
  var customOverride = function(k) {
    var v = (customF[k] || '').trim();
    var dflt = (d && d.custom_defaults && d.custom_defaults[k]) || '';
    return v && v !== dflt ? v : null;
  };
  var [editShipTo, setEditShipTo] = useState(false);
  var [shipTo, setShipTo] = useState({});
  // True only once the user TYPES in the ship-to editor (9-26): seeding the editor (address
  // error on open, the Edit button) is not an edit and must not autosave the address
  var shipToTouchedRef = useRef(false);
  // A carrier-rejected address (rate_error_kind 'address') opens the ship-to editor as
  // soon as the order loads, seeded like the Edit button does (Anthony 9-10).
  // Field-level flags from the last failed label attempt (server: label-error-fields.js)
  var labelErrHit = function(key, line) {
    var fs = (d && d.label_error_fields) || [];
    for (var i = 0; i < fs.length; i++) { var x = fs[i]; if (x.key === key && (x.line == null || line == null || x.line === line)) return x; }
    return null;
  };
  var labelErr = function(key, line) { return !!labelErrHit(key, line); };
  // The text to put on the field's label: the carrier's reason, else a plain marker
  var labelErrText = function(key, line) { var x = labelErrHit(key, line); return x ? { fixed: x.fixed === true, text: x.reason || 'label error' } : ''; };
  var labelErrAddress = !!(d && (d.label_error_fields || []).some(function(x) { return ['name', 'address1', 'address2', 'address3', 'city', 'state', 'zip', 'country', 'phone', 'email'].indexOf(x.key) !== -1; }));
  var labelErrBanner = function(show) {
    if (!show || !d || !d.label_error || d.status === 'SHIPPED' || d.status === 'CANCELLED') return null;
    if (d.label_hold) return null; // the hold banner below carries the same message plus the way out
    return h('div', { style: { margin: '0 0 10px', padding: '7px 10px', borderRadius: 6, background: C.warningSoft || C.bg, border: '1px solid rgba(245,158,11,0.5)', color: C.warning, fontSize: 12, lineHeight: 1.45 } },
      h('span', { style: { fontWeight: 600 } }, 'Last label attempt failed: '), d.label_error);
  };
  // A USPS purchase whose answer was lost: the label may be paid for with no record here, so the
  // order is held until a person has checked the USPS label history (review 2026-09-28, medium #1).
  var clearLabelHold = function() {
    api('/shipping/order/' + orderId + '/label-hold/clear', { method: 'POST' })
      .then(function() { showToast('Hold cleared. The order can be bought again.'); loadDetail(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var labelHoldBanner = function() {
    if (!d || !d.label_hold || d.status === 'SHIPPED' || d.status === 'CANCELLED') return null;
    var when = d.label_hold.since ? new Date(d.label_hold.since).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ET' : '';
    return h('div', { style: { margin: '0 0 10px', padding: '7px 10px', borderRadius: 6, background: C.warningSoft || C.bg, border: '1px solid rgba(245,158,11,0.5)', color: C.warning, fontSize: 12, lineHeight: 1.45 } },
      h('div', null, h('span', { style: { fontWeight: 600 } }, 'On hold: '),
        'a USPS purchase got no answer' + (when ? ' on ' + when : '') + (d.label_hold.error ? ' (' + d.label_hold.error + ')' : '') +
        ', so a paid label may exist with no record here. Check the USPS label history for this order (void it there if it was bought), then clear the hold to buy again.'),
      h('div', { style: { marginTop: 6 } }, h(Btn, { size: 'sm', variant: 'secondary', onClick: clearLabelHold }, 'Clear Hold')));
  };
  useEffect(function() {
    if (!d || (d.rate_error_kind !== 'address' && !labelErrAddress) || d.status === 'SHIPPED' || d.status === 'CANCELLED') return;
    // A seed, not an edit: shipToTouchedRef stays false so the autosave neither fires nor sends it
    if (shipToTouchedRef.current) { setEditShipTo(true); return; }
    setShipTo(shipSeedShipTo(d.recipient, (d.overrides && d.overrides.ship_to) || null));
    setEditShipTo(true);
  }, [d && d.id, d && d.rate_error_kind, labelErrAddress]);
  var [noteEditing, setNoteEditing] = useState(false);
  var [noteDraft, setNoteDraft] = useState('');
  var [prodSku, setProdSku] = useState(null); // item click → product modal
  // Inline customs editing (intl orders, user ask 8-30): saves write the
  // PRODUCT catalog via /products-list/:id; sparkle = AI HS suggest + save
  var [prodHsBusy, setProdHsBusy] = useState(null); // sku being classified
  var patchLine = function(sku, patch) { // patch = object, or function(line) → new line
    var mut = typeof patch === 'function' ? patch : function(x) { return Object.assign({}, x, patch); };
    setD(function(prev) {
      if (!prev) return prev;
      return Object.assign({}, prev, { lines: (prev.lines || []).map(function(x) { return x.sku === sku ? mut(x) : x; }) });
    });
    // Mirror onto the queue rows' own line objects so the amber HS chip and
    // HS filter react live — a full reload here was deselecting the order
    // when the fix dropped it from the active filter (8-30). The save is on the
    // PRODUCT, so every queued order carrying the SKU updates at once (9-17,
    // order 4233583: the second order's flag lagged until the next poll).
    if (onRowPatch) {
      onRowPatch(function(row) { return (row.lines || []).some(function(x) { return x.sku === sku; }); }, function(row) {
        return { lines: (row.lines || []).map(function(x) { return x.sku === sku ? mut(x) : x; }) };
      });
    }
  };
  var aiFillHs = function(l) {
    if (prodHsBusy) return;
    if (!l.product_id) { showToast('No catalog product for ' + l.sku, 'error'); return; }
    setProdHsBusy(l.sku);
    api('/shipping/product/hs-suggest', { method: 'POST', body: JSON.stringify({ sku: l.sku }) })
      .then(function(r) {
        var patch = { hs_code: r.hs_code };
        // A stored description that is just the product title ("Blue Ara Parrot") is not a
        // customs description: the suggestion replaces it (order 4234231, Anthony 9-17).
        if (r.customs_description && (!l.customs_description || shipDescIsTitle(l.customs_description, l.title))) patch.customs_description = r.customs_description;
        return api('/shipping/products-list/' + l.product_id, { method: 'POST', body: JSON.stringify(patch) })
          .then(function() {
            patchLine(l.sku, Object.assign({ hs_source: null }, patch));
            showToast('HS ' + r.hs_code + ' saved — ' + (r.confidence || 'medium') + ' confidence' +
              (r.source === 'catalog' && r.matched_title ? ' (matches "' + String(r.matched_title).slice(0, 40) + '")' : r.source === 'claude' ? ' (AI)' : ''), 'success');
          });
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setProdHsBusy(null); });
  };
  // Plain-language meaning shown under each HS code (server caches per code)
  var [hsMeanings, setHsMeanings] = useState({});
  useEffect(function() {
    if (!d || !d.lines) return;
    var stOv = (d.overrides && d.overrides.ship_to) || {};
    var country = String(stOv.country || (d.recipient && d.recipient.country) || 'US').toUpperCase();
    if (['US', 'PR', 'VI', 'GU', 'AS', 'MP'].indexOf(country) !== -1) return;
    d.lines.forEach(function(l) {
      var c = String(l.hs_code || '').replace(/[^0-9]/g, '');
      if (c.length < 6 || hsMeanings[c] !== undefined) return;
      setHsMeanings(function(p) { var n = Object.assign({}, p); if (n[c] === undefined) n[c] = null; return n; });
      api('/shipping/hs-describe?code=' + c)
        .then(function(r) { setHsMeanings(function(p) { var n = Object.assign({}, p); n[c] = r.description || ''; return n; }); })
        .catch(function() { setHsMeanings(function(p) { var n = Object.assign({}, p); n[c] = ''; return n; }); });
    });
  }, [d]);
  // Enabled services per override-carrier (Zentail vocab → carrier-services keys).
  // Only services checked under Settings > Integrations appear in the menu.
  var [svcCatalog, setSvcCatalog] = useState(null);
  useEffect(function() {
    var KEY_MAP = { stamps_com: 'USPS', dhl_global_mail: 'dhl_global_mail', dhl_ecommerce_us: 'dhl_global_mail', ups: 'ups' };
    api('/shipping/carrier-services').then(function(r) {
      var cat = {}, transit = {};
      (r.carriers || []).forEach(function(c) {
        var k = KEY_MAP[c.carrier]; if (!k) return;
        // our catalog's domestic / international flags: the Service menu only offers the
        // products that serve the order's destination (Anthony 9-19)
        cat[k] = (cat[k] || []).concat(c.services.filter(function(s) { return s.enabled; }).map(function(s) { return { name: s.name, domestic: !!s.domestic, international: !!s.international }; }));
        // published transit window for EVERY service, checked or not (Anthony 9-19)
        c.services.forEach(function(s) { if (s.transit_label) transit[k + '|' + String(s.name).toLowerCase()] = s.transit_label; });
      });
      setSvcCatalog(cat);
      svcTransitRef.current = transit;
    }).catch(function() { setSvcCatalog(null); });
  }, []);
  // "2–5 business days" for a quoted rate (carrier key as the quote spells it: usps / dhl_global_mail)
  var svcTransitRef = useRef({});
  var svcTransitFor = function(rate) {
    if (!rate) return null;
    var k = String(rate.carrier || '').toLowerCase() === 'usps' ? 'USPS' : rate.carrier;
    return svcTransitRef.current[k + '|' + String(rate.service || '').toLowerCase()] || null;
  };
  // What the footer says under the rate: the live estimate the quote carried (USPS service
  // standard for this lane / our own DHL deliveries to this destination) with the carrier's
  // published window as context; a quote without one (older cache) shows the window alone.
  var shipTransitLine = function(rate) {
    var published = svcTransitFor(rate);
    var t = rate && rate.transit;
    if (t && t.source === 'usps_standard') {
      return 'Est. transit ' + t.label + ' · USPS service standard for this lane' + (t.cutoff ? ', ship by ' + t.cutoff : '') + (published ? ' · published ' + published : '');
    }
    if (t && (t.source === 'observed' || t.source === 'dhl_matrix')) {
      return 'Est. transit ' + t.label + (published ? ' · DHL publishes ' + published : '');
    }
    return published ? 'Est. transit ' + published : null;
  };
  // The line itself reads "Estimated Transit 3-5 Days" (Anthony 9-22); the lane detail and the
  // carrier's published window stay on hover.
  var shipTransitShort = function(rate) {
    var full = shipTransitLine(rate);
    if (!full) return null;
    var core = full.replace(/^Est\. transit /, '').split(' · ')[0].replace(/–/g, '-').replace(/\s*business\s*/i, ' ').trim();
    core = core.replace(/\b(day|days)\b/i, function(m) { return m[0].toUpperCase() + m.slice(1).toLowerCase(); });
    return 'Estimated Transit ' + core;
  };
  // The carrier's services for THIS destination: a domestic order (US + PR/VI/GU/AS/MP, the same
  // list the rules use) never sees DHL's international products and vice versa. A product
  // flagged neither way is offered everywhere.
  var svcNamesForDest = function(carrier) {
    if (!d) return [];
    var stOv = (d.overrides && d.overrides.ship_to) || {};
    var country = String(shipTo.country || stOv.country || (d.recipient && d.recipient.country) || 'US').toUpperCase();
    var domestic = ['US', 'PR', 'VI', 'GU', 'AS', 'MP'].indexOf(country) !== -1;
    return ((svcCatalog && svcCatalog[carrier]) || [])
      .filter(function(s) { return domestic ? (s.domestic || !s.international) : (s.international || !s.domestic); })
      .map(function(s) { return s.name; });
  };

  // Settings lists for the Ship From + Package selects (session-cached)
  var [shipFroms, setShipFroms] = useState(null);
  var [pkgTpls, setPkgTpls] = useState(null);
  var [ovShipFrom, setOvShipFrom] = useState('');
  useEffect(function() {
    shipFetchList('/shipping/ship-from', 'addresses').then(setShipFroms);
    shipFetchList('/shipping/package-templates', 'templates').then(setPkgTpls);
  }, []);

  var loadDetail = useCallback(function() {
    // review M7 #102: the shell mounts through the legacy ReactDOM.render root, where setters in a
    // promise callback commit ONE AT A TIME. The autosave effect captured its baseline on the
    // first (setD) render, saw every following setter as an edit, and 900 ms later POSTed the
    // whole form — persisting rule/template dims and the computed ship-from as explicit
    // overrides on every open ("All changes saved" with no edit). Batch the block so the
    // baseline is taken once, with the form fully populated.
    api('/shipping/order/' + orderId).then(function(res) { ReactDOM.unstable_batchedUpdates(function() {
      setD(res);
      if (onDetail) onDetail(res); // ShipOrderModal's left column shares this fetch
      var w = shipLbToLbOz(res.computed.effective_weight_lb || 0);
      setLb(w.lb);
      setOz(w.oz);
      var ov = res.overrides || {};
      // The Service control shows the order's assignment: Rate Shop when the rules sent it to the
      // shopper (or the user chose Rate Shop), otherwise the assigned carrier + service, prefilled.
      // Nothing is saved until the user changes one of them (svcTouchedRef). Anthony 9-18: rules
      // are import-time automation; the user edits the order — no "Automatic (rules)" choice.
      var hasSvcOverride = !!(ov.carrier || ov.service);
      svcTouchedRef.current = hasSvcOverride;
      setSvcMode(ov.carrier === 'rate_shop' ? 'rate_shop' : hasSvcOverride ? 'manual' : ((res.computed && res.computed.rate_shop_eligible) ? 'rate_shop' : 'manual'));
      svcKeyRef.current = [ov.carrier || '', ov.service || ''].join('|');
      // a rule assigns 'UPS' (the rules vocabulary), the panel's option is 'ups' (2026-10-29)
      var carrierOpt = function(v) { return /^ups$/i.test(String(v || '')) ? 'ups' : (v || ''); };
      setOvCarrier(carrierOpt((ov.carrier && ov.carrier !== 'rate_shop') ? ov.carrier : (res.computed && res.computed.carrier)));
      setOvService(ov.service || ((res.computed && res.computed.service) || ''));
      var rd = res.computed.dimensions || {};
      setDims({ length: ov.length || rd.length || '', width: ov.width || rd.width || '', height: ov.height || rd.height || '' });
      setBoxes(Array.isArray(ov.boxes) && ov.boxes.length >= 2
        ? ov.boxes.map(function(b) { return { weight_lb: String(b.weight_lb || ''), length: String(b.length || ''), width: String(b.width || ''), height: String(b.height || '') }; })
        : null);
      setConfirmation(ov.confirmation || 'none');
      setAddressType(ov.address_type === 'residential' || ov.address_type === 'commercial' ? ov.address_type : '');
      setFlags(ov.flags || {});
      setOvPackage(ov.package_template_id ? 'tpl:' + ov.package_template_id : (ov.package || ''));
      setOvShipFrom(ov.ship_from_id ? String(ov.ship_from_id) : (res.computed.ship_from_id ? String(res.computed.ship_from_id) : ''));
      var cdf = res.custom_defaults || {};
      setCustomF({ custom1: ov.custom1 || cdf.custom1 || '', custom2: ov.custom2 || cdf.custom2 || '', custom3: ov.custom3 || cdf.custom3 || '' });
      // A carrier-rejected address opens the editor seeded INSIDE this batch, so the seed is part
      // of the autosave baseline (9-26: the separate effect ran after the baseline and POSTed the
      // seeded address as an override on open). Nothing typed yet → touched stays false.
      shipToTouchedRef.current = false;
      var addrErr = res.status !== 'SHIPPED' && res.status !== 'CANCELLED' && (res.rate_error_kind === 'address' ||
        (res.label_error_fields || []).some(function(x) { return ['name', 'address1', 'address2', 'address3', 'city', 'state', 'zip', 'country', 'phone', 'email'].indexOf(x.key) !== -1; }));
      setShipTo(addrErr ? shipSeedShipTo(res.recipient, ov.ship_to || null) : (ov.ship_to || {}));
      setEditShipTo(addrErr);
      setNoteDraft((res.meta && res.meta.note) || '');
      setNoteEditing(false);
      // Rate-Shopper orders cross-shop; rule-assigned orders price their
      // assigned carrier/service only
      pinRef.current = (res.computed && res.computed.rate_shop_eligible)
        ? null
        : { carrier: res.computed.carrier, service: res.computed.service };
      loadRates();
    }); }).catch(function(e) { setErr(e.message); });
  }, [orderId]);
  useEffect(function() { loadDetail(); }, [loadDetail]);
  // Host (ShipOrderModal) can trigger a refetch after its own edits
  if (reloadRef) reloadRef.current = loadDetail;
  var pinRef = useRef(null);
  // On-screen values (possibly unsaved) so a manual re-quote prices what the
  // user is looking at, not just what's saved
  var previewRef = useRef(null);
  previewRef.current = {
    weight_lb: (parseFloat(lb) || 0) + (parseFloat(oz) || 0) / 16,
    length: dims.length, width: dims.width, height: dims.height,
  };
  var loadRates = useCallback(function(force) {
    setRates(null);
    var body = { order_id: orderId, force: force === true };
    if (force === true && previewRef.current) body.preview = previewRef.current;
    if (pinRef.current) { body.carrier = pinRef.current.carrier; body.service = pinRef.current.service; }
    api('/shipping/rates', { method: 'POST', body: JSON.stringify(body) })
      .then(function(r) {
        setRates(r);
        // Feed the winner back to the queue's Rate column
        if (onRates && r.rates && r.rates.length) onRates(orderId, r.rates[0]);
        // The ADDRESS flag follows the fresh quote, not the detail payload loaded before the
        // edit (order 4235061: the address was fixed and re-rated, the flag stayed — 9-17)
        if (r && Object.prototype.hasOwnProperty.call(r, 'rate_error')) {
          var flag = { rate_error: r.rate_error || null, rate_error_kind: r.rate_error_kind || null };
          setD(function(prev) { return prev ? Object.assign({}, prev, flag) : prev; });
          if (onRowPatch) onRowPatch(orderId, flag);
        }
      })
      .catch(function() { setRates({ rates: [] }); });
  }, [orderId, onRowPatch]);

  // A decimal typed into the lb box is the whole weight (Anthony 2026-11-03, order 4326253: "if the
  // user sets the lbs field to 0.78 it fills in the lbs field and oz field with the decimal pounds"):
  // on leaving the box 0.78 becomes 0 lb 12.48 oz — the oz box is replaced, not added to.
  var commitLb = function(v) {
    var n = parseFloat(v);
    if (!(n > 0) || n === Math.floor(n)) return;
    var split = shipLbToLbOz(n);
    setLb(split.lb); setOz(split.oz);
  };
  var buildOverridesBody = function() {
    var weight = (parseFloat(lb) || 0) + (parseFloat(oz) || 0) / 16;
    return {
      // Blank weight boxes (or 0) mean "no override", never a 0 lb override: the server refuses 0
      // since 9-27, and this used to send 0 whenever both boxes were cleared — every autosave and
      // the leave-flush of the WHOLE form then failed, losing the other edits (review 2026-09-28)
      weight_lb: weight > 0 && Math.abs(weight - d.computed.weight_lb) > 0.001 ? Math.round(weight * 100) / 100 : null,
      // Rate Shop by hand saves the rules' own vocabulary (carrier 'rate_shop', no service): the
      // server cross-shops this order and Create Label buys the winner, rules or no rules (9-18).
      // The prefilled assignment is NOT saved — only a change the user made (svcTouchedRef).
      carrier: !svcTouchedRef.current ? null : svcMode === 'rate_shop' ? 'rate_shop' : (svcMode === 'manual' && ovCarrier.trim() ? ovCarrier.trim() : null),
      service: !svcTouchedRef.current ? null : (svcMode === 'manual' && ovService.trim() ? ovService.trim() : null),
      length: parseFloat(dims.length) > 0 ? parseFloat(dims.length) : null,
      width: parseFloat(dims.width) > 0 ? parseFloat(dims.width) : null,
      height: parseFloat(dims.height) > 0 ? parseFloat(dims.height) : null,
      // Boxes save only once every box is complete (a half-typed box must not fail the whole
      // autosave); "Single parcel" (null) removes them; undefined = leave the saved boxes alone
      boxes: shipBoxesForSave(boxes),
      confirmation: confirmation !== 'none' ? confirmation : null,
      // Address type: '' = Auto (USPS business flag, else the default) saves nothing; a hand pick is the override
      address_type: addressType || null,
      flags: Object.keys(flags).some(function(k) { return flags[k]; }) ? flags : null,
      package: (ovPackage && ovPackage.indexOf('tpl:') !== 0) ? ovPackage : null,
      package_template_id: (ovPackage && ovPackage.indexOf('tpl:') === 0) ? parseInt(ovPackage.slice(4), 10) : null,
      ship_from_id: (ovShipFrom && String(d.computed.ship_from_auto_id || '') !== ovShipFrom) ? parseInt(ovShipFrom, 10) : null,
      // Equal to the Zentail default = no override (null → the row keeps following Zentail)
      custom1: customOverride('custom1'),
      custom2: customOverride('custom2'),
      custom3: customOverride('custom3'),
      ship_to: shipAutosaveShipTo(shipToTouchedRef.current, shipTo, (d.overrides && d.overrides.ship_to) || null),
    };
  };
  // ShipStation-style AUTOSAVE: fields persist ~0.9s after the last change.
  // No detail reload on autosave (it would clobber in-progress typing) — the
  // baseline snapshot advances instead; rates re-quote against the new inputs.
  var baselineRef = useRef(null);
  var svcKeyRef = useRef(''); // carrier|service last saved — a change reloads the detail so the quote pin follows
  var svcTouchedRef = useRef(false); // the user changed the Service controls (or an override already existed)
  var dSnapRef = useRef(null);
  var autoTimer = useRef(null);
  var [autoState, setAutoState] = useState(''); // '' | 'saving' | 'saved'
  // The ship-to is part of the snapshot only once the user typed in it: seeding the editor
  // (address error on open / the Edit button) must not look like an edit (9-26)
  var formSnap = d ? JSON.stringify([lb, oz, svcMode, ovCarrier, ovService, dims, boxes, confirmation, addressType, flags, ovPackage, ovShipFrom, customF, shipToTouchedRef.current ? shipTo : null]) : null;
  // Voided-label orders carry the reship flag: Zentail says SHIPPED but the
  // panel stays fully editable (new label / mark shipped / dismiss)
  var panelReship = !!(d && d.overrides && d.overrides.reship);
  var panelLocks = shipPanelLocks(d);
  // Shipped, or a label bought here (and not flagged for reship): the order is out of the queue, so
  // Batch ▾ and Edit tags are gone (Anthony 2026-10-17); cancelled orders keep them
  var panelShipped = !!d && ((d.status === 'SHIPPED' && !panelReship) || panelLocks.labelBought);
  // Save sequencing (old review #111): each save takes a number; only the LATEST save's
  // response may advance the baseline, so an older response landing late can't mark a
  // newer form as saved (or roll it back). Create Label flushes through flushAutosave.
  var saveSeqRef = useRef(0);
  var pendingSaveRef = useRef(null); // the in-flight save's promise (resolves true/false)
  var queuedSaveRef = useRef(null);  // the debounced save not yet fired
  var flushAutosave = function() {
    if (autoTimer.current) { clearTimeout(autoTimer.current); autoTimer.current = null; }
    if (queuedSaveRef.current) return queuedSaveRef.current();
    return pendingSaveRef.current || Promise.resolve(true);
  };
  useEffect(function() {
    if (!d || !panelLocks.autosave) return;
    // A reloaded detail while a typed edit still waits: save the edit NOW (its closure holds the
    // typed values) instead of re-baselining over it (sweep 2026-09-27 M9)
    if (dSnapRef.current !== d) {
      if (queuedSaveRef.current) queuedSaveRef.current();
      dSnapRef.current = d; baselineRef.current = formSnap; return;
    }
    if (formSnap === baselineRef.current) { queuedSaveRef.current = null; return; }
    if (autoTimer.current) clearTimeout(autoTimer.current);
    var snap = formSnap;
    // leaving = the panel is closing / moving to another order: POST only — no state updates and
    // no detail reload (that would pull the old order back into the next one's panel)
    var runSave = function(leaving) {
      if (autoTimer.current) clearTimeout(autoTimer.current);
      autoTimer.current = null;
      if (queuedSaveRef.current === runSave) queuedSaveRef.current = null;
      var body = buildOverridesBody();
      var seq = ++saveSeqRef.current;
      if (!leaving) setAutoState('saving');
      var p = api('/shipping/order/' + orderId + '/overrides', { method: 'POST', body: JSON.stringify(body) })
        .then(function() {
          if (pendingSaveRef.current === p) pendingSaveRef.current = null;
          if (leaving) { if (onChanged) onChanged(); return true; }
          // A newer save started after this one: its response owns the baseline + follow-ups
          if (seq !== saveSeqRef.current) return true;
          baselineRef.current = snap;
          setAutoState('saved');
          // The queue row's Custom Field columns and search follow the typed value at once (9-18)
          if (onRowPatch) {
            var cd = (d && d.custom_defaults) || {};
            onRowPatch(orderId, { custom1: body.custom1 || cd.custom1 || null, custom2: body.custom2 || cd.custom2 || null, custom3: body.custom3 || null });
          }
          if (onChanged) onChanged();
          // A carrier/service change (Rate Shop ↔ rules ↔ manual) changes what the rater pins, so
          // the detail is reloaded (it recomputes the pin and re-quotes); other edits just re-quote
          var svcKey = [body.carrier || '', body.service || ''].join('|');
          if (svcKey !== svcKeyRef.current) { svcKeyRef.current = svcKey; loadDetail(); }
          else loadRates();
          return true;
        })
        .catch(function(e) {
          if (pendingSaveRef.current === p) pendingSaveRef.current = null;
          if (!leaving && seq === saveSeqRef.current) setAutoState('');
          showToast(e.message, 'error');
          return false;
        });
      pendingSaveRef.current = p;
      return p;
    };
    queuedSaveRef.current = runSave;
    autoTimer.current = setTimeout(function() { runSave(false); }, 900);
    return function() {
      // the timer only — the queued save stays until the next run replaces it, or the panel's
      // leave effect below FLUSHES it (it used to be dropped: edits typed <0.9 s before moving to
      // the next order were lost, sweep 2026-09-27 M9)
      if (autoTimer.current) clearTimeout(autoTimer.current);
      autoTimer.current = null;
    };
  }, [formSnap, d]);
  // Closing the panel or moving to another order flushes a typed-but-unsaved edit to the order it
  // was typed on (runSave's closure carries that order id)
  useEffect(function() {
    return function() { var q = queuedSaveRef.current; queuedSaveRef.current = null; if (q) q(true); };
  }, [orderId]);


  // Each section starts with a divider line under the previous one
  var secTitle = function(t) { return h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: docked ? '14px 0 8px' : '18px 0 10px', borderTop: '1px solid ' + C.border, paddingTop: docked ? 12 : 16 } }, t); };
  var inpS = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', width: docked ? 42 : 54, boxSizing: 'border-box' };
  // Selects fill the row's remaining width beside the label (SS-style rows)
  var selS = Object.assign({}, inpS, { width: 'auto', flex: docked ? 1 : undefined, minWidth: 0, cursor: 'pointer' });
  // Docked = 180px column: labels sit on their own line (width 100% forces the wrap)
  // and the 84px hint indents collapse. Drawer mode keeps the wide two-column rows.
  // SS-style rows: label LEFT, control on the SAME line ("Ship From: [▾]").
  // The full-width stacked labels were a relic of the 260px dock — at 446px
  // the side-by-side layout fits (user ask 8-12).
  var lblS = { fontSize: 12, color: C.textMuted, width: docked ? 88 : 76, flexShrink: 0 };
  var rowS = { display: 'flex', alignItems: 'center', gap: docked ? 6 : 8, flexWrap: 'wrap' };
  var hintInd = docked ? 94 : 84;

  // docked = inline sidebar card (orders page); embedded = static right column
  // inside ShipOrderModal (no drawer chrome; keeps 16px top padding so the ±16
  // sticky header/footer offsets hold); default = fixed right drawer
  var frameStyle = docked
    ? { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, overflowY: 'auto', padding: '16px 16px 22px', flex: 1, minHeight: 0, boxSizing: 'border-box' }
    : embedded
      ? { background: C.surface, borderLeft: '1px solid ' + C.border, overflowY: 'auto', padding: '16px 18px 22px', flex: 1, minHeight: 0, boxSizing: 'border-box' }
      : { position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '92vw', background: C.surface, borderLeft: '1px solid ' + C.border, zIndex: 1201, overflowY: 'auto', padding: '18px 20px', boxShadow: '-8px 0 32px rgba(0,0,0,0.35)' };
  return h('div', { style: (docked || embedded) ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : null },
    !docked && !embedded && h('div', { onClick: onClose, style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1200 } }),
    h('div', { className: 'spanel', style: frameStyle },
      err ? h('div', { style: { color: C.danger, fontSize: 13 } }, err)
      : !d ? h('div', { style: { color: C.textMuted, fontSize: 13, padding: 20 } }, 'Loading order…')
      : h('div', null,
          // Header strip — the only order identity up top; details live in
          // the Order summary section (no duplicated data)
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, margin: '-16px -4px 12px', padding: docked ? '14px 46px 10px 4px' : '14px 4px 10px', borderBottom: '1px solid ' + C.border, position: 'sticky', top: -16, background: C.surface, zIndex: 5 } },
            !embedded && h(LIcon, { name: 'package', size: 14, color: C.accent }),
            !embedded && h('span', { style: { fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: '0.01em' } }, d.order_number),
            d.is_manual && h('span', { style: { fontSize: 9, color: '#F59E0B', fontWeight: 500, border: '1px solid rgba(245,158,11,0.6)', borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase' } }, d.channel || 'Manual'),
            h('span', { style: { flex: 1 } }),
            // Batch ▾ / Print ▾ (ShipStation header parity); Batch ▾ only while the order can still be batched
            !panelShipped && h('div', { ref: hdrBatchRef, style: { position: 'relative' } },
              h('button', { className: 'phov', onClick: function() { setHdrBatchOpen(!hdrBatchOpen); setHdrPrintOpen(false); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit', padding: '2px 6px', borderRadius: 4 } }, 'Batch ▾'),
              hdrBatchOpen && h('div', { style: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 30, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 170, maxHeight: 220, overflowY: 'auto' } },
                h('div', { onClick: function() { hdrAddToBatch(null); }, onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { padding: '7px 12px', fontSize: 12, fontWeight: 500, color: C.accent, cursor: 'pointer', borderBottom: '1px solid ' + C.border } }, '+ New batch'),
                hdrBatches.map(function(bt) {
                  return h('div', { key: bt.id, onClick: function() { hdrAddToBatch(bt.id); }, onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: C.text } }, String(bt.batch_number) + (bt.order_count != null ? '  (' + bt.order_count + ')' : ''));
                }))),
            h('div', { ref: hdrPrintRef, style: { position: 'relative' } },
              h('button', { className: 'phov', onClick: function() { setHdrPrintOpen(!hdrPrintOpen); setHdrBatchOpen(false); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit', padding: '2px 6px', borderRadius: 4 } }, 'Print ▾'),
              hdrPrintOpen && h('div', { style: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 30, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 150 } },
                [{ label: 'Packing slip', fn: function() { shipPrintPackingSlips([d]); } }, { label: 'Pick list', fn: function() { shipPrintPickList([d]); } }].map(function(it) {
                  return h('div', { key: it.label, onClick: function() { setHdrPrintOpen(false); it.fn(); }, onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: C.text } }, it.label);
                }))),
            !embedded && h('button', { onClick: onClose, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),

          // Manual orders: edit/cancel while PENDING — no Zentail behind them
          d.is_manual && d.status === 'PENDING' && h('div', { style: { display: 'flex', gap: 6, marginBottom: 10 } },
            onEditManual && h(Btn, { size: 'sm', style: { fontSize: 11, padding: '4px 10px' }, onClick: function() { onEditManual(d); } }, h(LIcon, { name: 'pencil', size: 12 }), ' Edit order'),
            h(Btn, { size: 'sm', style: { fontSize: 11, padding: '4px 10px', background: 'rgba(248,113,113,0.08)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }, onClick: async function() {
              if (!(await appConfirm('Cancel manual order ' + d.order_number + '? This cannot be undone.', { danger: true, okLabel: 'Cancel order' }))) return;
              api('/shipping/manual-orders/' + (-d.id) + '/cancel', { method: 'POST' })
                .then(function() {
                  showToast('Order cancelled', 'success');
                  if (onChanged) onChanged();
                  if (onClose) onClose();
                  try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
                })
                .catch(function(e) { showToast(e.message, 'error'); });
            } }, h(LIcon, { name: 'x', size: 12 }), ' Cancel order')),

          // Tags — first section (user ask 8-12); editing lives right here
          secTitle('Tags'),
          h('div', { ref: tagEditRef, style: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, position: 'relative' } },
            h(ShipTagChips, { tags: (d.computed.rule_tags || []).concat((d.meta.tags || []).filter(function(t) { return (d.computed.rule_tags || []).indexOf(t) === -1; })) }),
            d.meta.assignee && h('span', { style: { fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 4, background: C.successSoft, color: C.success } }, '@' + d.meta.assignee),
            panelShipped && !((d.computed.rule_tags || []).length || (d.meta.tags || []).length) && h('span', { style: { fontSize: 11.5, color: C.textDim } }, 'None'),
            h('span', { style: { flex: 1 } }),
            !panelShipped && h('button', { className: 'phov', onClick: function() { setTagEditOpen(!tagEditOpen); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', padding: '2px 6px', borderRadius: 4 } }, 'Edit tags'),
            !panelShipped && tagEditOpen && h('div', { style: { position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 40, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 220, maxHeight: 260, overflowY: 'auto', padding: 6 } },
              panelTags.length === 0 && h('div', { style: { fontSize: 11, color: C.textMuted, padding: 6 } }, 'No tags in the registry yet.'),
              panelTags.map(function(t) {
                var name = t.name || t;
                // Manual takes precedence: rule_tags includes the seeded manual
                // tags (engine returns the merged workCtx.tags), so a tag is only
                // "by rule" when it ISN'T in the manual list (bug 8-12).
                var isManual = (d.meta.tags || []).indexOf(name) !== -1;
                var isRule = !isManual && (d.computed.rule_tags || []).indexOf(name) !== -1;
                var col = shipTagColor(name);
                // Optimistic: update the panel locally, POST in the background.
                // Queue refreshes once, when the picker closes. Wired to BOTH the
                // row and the checkbox — ShipCheckbox stopPropagation()s its own
                // clicks, so a row-only handler made the box itself dead (8-12).
                var isSuppressed = !isManual && !isRule && (d.meta.suppressed_tags || []).indexOf(name) !== -1;
                // Every tag is editable by hand (Anthony 9-22): unchecking a rule-applied tag removes it
                // and the server remembers that for this order, so the rule does not put it back;
                // checking it again clears that and adds it as a manual tag.
                var doToggle = function() {
                  var removing = isManual || isRule;
                  var nextTags = removing ? (d.meta.tags || []).filter(function(x) { return x !== name; }) : (d.meta.tags || []).concat([name]);
                  var nextRule = isRule ? (d.computed.rule_tags || []).filter(function(x) { return x !== name; }) : (d.computed.rule_tags || []);
                  var nextSup = removing ? (d.meta.suppressed_tags || []).concat(isRule ? [name] : []) : (d.meta.suppressed_tags || []).filter(function(x) { return x !== name; });
                  setD(Object.assign({}, d, { meta: Object.assign({}, d.meta, { tags: nextTags, suppressed_tags: nextSup }), computed: Object.assign({}, d.computed, { rule_tags: nextRule }) }));
                  // Patch the queue row in place so the table updates instantly
                  if (onRowPatch) onRowPatch(d.id, { tags: nextRule.concat(nextTags.filter(function(x) { return nextRule.indexOf(x) === -1; })) });
                  panelTagsDirtyRef.current = true;
                  var body = { order_ids: [d.id] };
                  body[removing ? 'remove_tags' : 'add_tags'] = [name];
                  api('/shipping/orders/meta', { method: 'POST', body: JSON.stringify(body) })
                    .catch(function(e) { showToast(e.message, 'error'); loadDetail(); });
                };
                return h('div', {
                  key: name,
                  onClick: doToggle,
                  onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; },
                  style: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, cursor: 'pointer' }
                },
                  h(ShipCheckbox, { checked: isRule || isManual, onChange: doToggle }),
                  h('span', { style: { width: 5, height: 15, borderRadius: 2, background: col, flexShrink: 0 } }),
                  h('span', { style: { fontSize: 11.5, color: C.text, flex: 1 } }, name),
                  isRule && h('span', { style: { fontSize: 9, color: C.textDim } }, 'by rule'),
                  isSuppressed && h('span', { style: { fontSize: 9, color: C.textDim } }, 'removed by hand'));
              }))),

          // Shipping — locked once shipped/cancelled (void returns it to the queue)
          secTitle('Shipping'),
          panelReship && h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.45)', borderRadius: 8, marginBottom: 10 } },
            h(LIcon, { name: 'rotate-ccw', size: 14, color: '#FBBF24' }),
            h('div', { style: { flex: 1, fontSize: 11.5, color: C.text, lineHeight: 1.5 } },
              h('div', { style: { fontWeight: 600, color: '#FBBF24' } }, 'Needs reship — label voided'),
              h('div', { style: { color: C.textMuted } },
                (d.overrides.reship.by === 'auto-void'
                  ? 'Auto-voided: ' + (d.overrides.reship.reason || 'no USPS scan')
                  : 'Voided by ' + (d.overrides.reship.by || 'user')) +
                (d.overrides.reship.tracking ? ' · old tracking ' + d.overrides.reship.tracking : '') +
                (d.overrides.reship.voided_at ? ' · ' + new Date(d.overrides.reship.voided_at).toLocaleString() : '')),
              h('div', { style: { color: C.textMuted, marginTop: 2 } }, 'Buy a new label below or mark shipped; dismiss if the order is being cancelled on the marketplace.')),
            h('button', {
              onClick: function() {
                if (!window.confirm('Dismiss the reship flag? The order leaves the queue; Zentail keeps it marked shipped with the old tracking.')) return;
                // a typed edit is saved first — the dismiss's reload would drop it (9-27 M9)
                Promise.resolve(flushAutosave()).then(function() { return api('/shipping/order/' + orderId + '/reship-dismiss', { method: 'POST', body: '{}' }); })
                  .then(function() { showToast('Reship dismissed', 'success'); loadDetail(); onChanged && onChanged(); })
                  .catch(function(e) { showToast(e.message, 'error'); });
              },
              style: { background: 'transparent', border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }
            }, 'Dismiss')),
          // A label bought here shows its block — with Void — as soon as it exists, even while Zentail
          // still says pending (Anthony 2026-09-23: "there's no void label option in the order modal")
          (d.status === 'CANCELLED' || d.cancelled_here || (d.status === 'SHIPPED' && !panelReship) || (!panelReship && d.labels && d.labels.length > 0)) && h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, marginBottom: 8 } },
              h(LIcon, { name: 'lock', size: 13, color: C.textMuted }),
              h('span', { style: { fontSize: 12, color: C.textMuted } },
                d.status === 'SHIPPED' ? 'Shipped — shipping settings are locked.'
                  : (d.status !== 'CANCELLED' && !d.cancelled_here && d.labels && d.labels.length) ? 'Label bought — the marketplace confirmation is on its way; shipping settings are locked.'
                  : d.cancelled_here ? 'Cancelled in the shipping app' + (d.cancelled_by ? ' by ' + d.cancelled_by : '') + (d.cancel_reason ? ' (' + d.cancel_reason + ')' : '') + ' — Zentail still shows it pending; cancel it there too. Restore from Orders › Cancelled.'
                  : 'Cancelled — shipping settings are locked.')),
            (d.packages || []).map(function(p, i) {
              return h('div', { key: i, style: { border: '1px solid ' + C.border, borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12, lineHeight: 1.7 } },
                h('div', { style: { fontWeight: 500, color: C.text } }, shipCarrierLabel(p.carrier) + ' · ' + (p.service || '?')),
                p.tracking && h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 } },
                  h('a', { href: shipTrackingUrl(p.carrier, p.tracking), target: '_blank', rel: 'noopener', style: { color: C.accent, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, p.tracking),
                  // click-to-copy, the panel's copyBadge-style icon (Anthony 2026-10-17: a copy icon on the tracking number)
                  shipCopyIcon(p.tracking, showToast, 22)),
                h('div', { style: { color: C.textMuted } },
                  (p.cost != null ? shipMoney(p.cost) + ' label' : '') +
                  (p.weight != null ? ' · ' + p.weight + ' ' + p.weight_uom : '') +
                  (p.shipped_ts ? ' · ' + new Date(p.shipped_ts).toLocaleString() : '')));
            }),
            (d.labels && d.labels.length) ? h('button', {
              onClick: function() { shipPrintLabelFile(d.labels[0].id); },
              title: 'Reprint the purchased label',
              style: { width: '100%', background: 'transparent', color: C.text, border: '1px solid ' + C.border, borderRadius: 8, padding: '7px 0', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 }
            }, 'Print label') : null,
            (d.labels && d.labels.length) ? h('button', {
              disabled: voidBusy,
              onClick: function() {
                if (!window.confirm('Void this label?\n\nThe label becomes unusable here (reprint blocked, excluded from End of Day). USPS labels are also canceled at USPS for a postage refund. Zentail keeps the order marked shipped with the old tracking — re-ship it in Zentail if it still needs to go out.')) return;
                // USPS may answer "Authorize again?" (409 needs_usps_authorization, 2026-09-26): ask, like
                // Create Label does, and re-send with authorize_usps once someone confirms (a new card hold)
                var runVoid = function(authorize) {
                  setVoidBusy(true);
                  api('/shipping/void-label', { method: 'POST', body: JSON.stringify(authorize ? { order_id: orderId, authorize_usps: true } : { order_id: orderId }) })
                    .then(function(r) { setVoidBusy(false); showToast('Label voided (' + (r.tracking || '') + ')', 'success'); loadDetail(); onChanged && onChanged(); })
                    .catch(function(e) {
                      setVoidBusy(false);
                      if (!authorize && shipNeedsUspsReauth(e)) {
                        if (window.confirm(String(e.message || 'USPS needs a new payment authorization.') + '\n\nAuthorize another USPS payment (a new card hold) and void the label?')) runVoid(true);
                        return;
                      }
                      showToast(String(e.message || e), 'error');
                    });
                };
                runVoid(false);
              },
              style: { width: '100%', background: 'transparent', color: C.danger, opacity: voidBusy ? 0.6 : 1, border: '1px solid ' + C.danger, borderRadius: 8, padding: '7px 0', fontSize: 12, fontWeight: 500, cursor: voidBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }
            }, voidBusy ? 'Voiding…' : 'Void Label')
            : h('button', { disabled: true, title: 'Void applies to labels purchased here', style: { width: '100%', background: 'transparent', color: C.danger, opacity: 0.5, border: '1px solid ' + C.danger, borderRadius: 8, padding: '7px 0', fontSize: 12, fontWeight: 500, cursor: 'not-allowed', fontFamily: 'inherit' } }, 'Void Label'),
            h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 4 } },
              (d.labels && d.labels.length)
                ? 'Voiding blocks reprint and End of Day here; Zentail keeps the order shipped until you re-ship it there.'
                : 'Void applies to labels purchased in this app.')),
          // A bought label locks the form too (9-26) — the lock block above says so
          !panelLocks.form && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Ship From'),
              (shipFroms && shipFroms.length)
                ? h('select', { value: ovShipFrom, onChange: function(e) { setOvShipFrom(e.target.value); }, style: Object.assign({}, selS, { maxWidth: 200 }) },
                    shipFroms.map(function(a) { return h('option', { key: a.id, value: String(a.id) }, a.name + (a.is_default ? ' (default)' : '')); }))
                : h('span', { style: { fontSize: 11.5, color: C.textDim } }, 'none configured — Settings › General')),
            (d.computed.ship_from_source === 'rule' || d.computed.ship_from_source === 'manual') && h('div', { style: { fontSize: 10, color: d.computed.ship_from_source === 'manual' ? C.warning : C.textDim, marginLeft: hintInd, marginTop: -6 } },
              d.computed.ship_from_source === 'manual' ? 'override' : 'assigned by rule'),
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Weight'),
              h(ShipStepper, { value: lb, onChange: setLb, onCommit: commitLb, step: 1, width: 48 }), h('span', { style: { fontSize: 11, color: C.textDim } }, 'lb'),
              h(ShipStepper, { value: oz, onChange: setOz, step: 1, width: 48 }), h('span', { style: { fontSize: 11, color: C.textDim } }, 'oz')
            ),
            (d.overrides && d.overrides.weight_lb != null) && h('div', { style: { fontSize: 10, color: C.warning, marginLeft: hintInd, marginTop: -6 } }, 'override · catalog: ' + (shipWeightFmt(d.computed.weight_lb) || '—')),
            // Carrier (Anthony 9-19): Rate Shopper, or a carrier picked by hand — the Service level
            // row appears for USPS / DHL. Saved exactly as before: carrier 'rate_shop' with no
            // service, or carrier + service.
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Carrier'),
              h('select', {
                value: svcMode === 'rate_shop' ? 'rate_shop' : ovCarrier,
                onChange: function(e) {
                  var v = e.target.value;
                  svcTouchedRef.current = true;
                  if (v === 'rate_shop') { setSvcMode('rate_shop'); return; }
                  setSvcMode('manual');
                  if (v !== ovCarrier) { setOvCarrier(v); setOvService(''); }
                },
                style: selS
              },
                h('option', { value: 'rate_shop' }, 'Rate Shopper'),
                h('option', { value: 'USPS' }, 'USPS'),
                h('option', { value: 'dhl_global_mail' }, 'DHL'),
                h('option', { value: 'ups' }, 'UPS'),
                (svcMode !== 'rate_shop' && ovCarrier && ovCarrier !== 'USPS' && ovCarrier !== 'dhl_global_mail' && ovCarrier !== 'ups') && h('option', { value: ovCarrier }, (shipCarrierLabel(ovCarrier) || ovCarrier) + ' (saved)'),
                (svcMode !== 'rate_shop' && !ovCarrier) && h('option', { value: '' }, 'Carrier…'))
            ),
            svcMode === 'manual' && !!ovCarrier && (function() {
              var svcs = svcNamesForDest(ovCarrier);
              return h('div', { style: rowS },
                h('span', { style: lblS }, 'Service'),
                svcs.length
                  ? h('select', { value: ovService, onChange: function(e) { svcTouchedRef.current = true; setOvService(e.target.value); }, style: Object.assign({}, selS, { maxWidth: 200 }) },
                      h('option', { value: '' }, 'Service…'),
                      (ovService && svcs.indexOf(ovService) === -1) && h('option', { value: ovService }, ovService + ' (saved)'),
                      svcs.map(function(nm) { return h('option', { key: nm, value: nm }, nm); }))
                  : h('input', { value: ovService, onChange: function(e) { svcTouchedRef.current = true; setOvService(e.target.value); }, placeholder: 'service name', style: Object.assign({}, inpS, { width: 160 }) }));
            })(),
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Package'),
              h('select', { value: ovPackage, onChange: function(e) {
                  var v = e.target.value;
                  setOvPackage(v);
                  // Picking a template prefills the Size fields with its dims
                  if (v.indexOf('tpl:') === 0 && pkgTpls) {
                    var t = pkgTpls.find(function(x) { return String(x.id) === v.slice(4); });
                    if (t && (t.length_in || t.width_in || t.height_in)) setDims({ length: t.length_in || '', width: t.width_in || '', height: t.height_in || '' });
                  }
                }, style: selS },
                h('option', { value: '' }, 'Package (Default)'),
                (pkgTpls || []).map(function(t) {
                  return h('option', { key: t.id, value: 'tpl:' + t.id }, t.name + (t.length_in ? ' — ' + t.length_in + '×' + t.width_in + '×' + t.height_in : ''));
                }),
                h('option', { value: 'large_envelope' }, 'Large Envelope / Flat'),
                h('option', { value: 'poly_bag' }, 'Poly Bag'),
                h('option', { value: 'box' }, 'Box'),
                h('option', { value: 'letter' }, 'Letter'))),
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Size (in)'),
              ['length', 'width', 'height'].map(function(k, i) {
                return h(React.Fragment, { key: k },
                  h(ShipStepper, { value: dims[k], placeholder: 'LWH'[i], step: 0.5, width: 48, onChange: function(v) { var n = Object.assign({}, dims); n[k] = v; setDims(n); } }),
                  i < 2 && !docked && h('span', { style: { color: C.textDim, fontSize: 11 } }, '×'));
              })),
            // Multi-box shipments (UPS, 2026-10-29): two or more boxes, each with its own weight and
            // L×W×H — one UPS shipment, one label per box. Weight / Size above stay the one-parcel
            // values (USPS, DHL and a one-box UPS label use them).
            h(ShipBoxesEditor, { boxes: boxes, setBoxes: setBoxes, docked: docked, rowS: rowS, lblS: lblS,
              seed: function() {
                var w = Math.round(((parseFloat(lb) || 0) + (parseFloat(oz) || 0) / 16) * 100) / 100;
                return { weight_lb: w > 0 ? String(w) : '', length: dims.length, width: dims.width, height: dims.height };
              } }),
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Confirm'),
              h('select', { value: confirmation, onChange: function(e) { setConfirmation(e.target.value); }, style: selS },
                h('option', { value: 'none' }, 'No Confirmation'),
                h('option', { value: 'delivery' }, 'Delivery Confirmation'),
                h('option', { value: 'signature' }, 'Signature'),
                h('option', { value: 'adult_signature' }, 'Adult Signature'))),
            // Residential or commercial (2026-10-29): UPS prices its residential surcharge from it. Auto =
            // the USPS validation's business flag, else residential (the B2C default); a hand pick overrides.
            h('div', { style: rowS },
              h('span', { style: lblS }, 'Address'),
              h('select', { value: addressType, onChange: function(e) { setAddressType(e.target.value); }, style: selS,
                title: 'Residential deliveries carry a UPS surcharge; USPS and DHL price the same either way' },
                h('option', { value: '' }, 'Auto — ' + (d.computed && d.computed.residential === false ? 'commercial' : 'residential')
                  + ' (' + ({ usps: 'USPS', company: 'company name', override: 'set by hand', default: 'default' })[(d.computed && d.computed.residential_source) || 'default'] + ')'),
                h('option', { value: 'residential' }, 'Residential'),
                h('option', { value: 'commercial' }, 'Commercial'))),
            h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 } },
              SHIP_FLAG_DEFS.map(function(f) {
                return h('label', { key: f.key, style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: C.text, cursor: 'pointer' } },
                  h(ShipCheckbox, { checked: !!flags[f.key], onChange: function() { var n = Object.assign({}, flags); n[f.key] = !n[f.key]; setFlags(n); } }),
                  f.label);
              })),
            h('div', { style: { display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' } },
              h(Btn, { size: 'sm', style: docked ? { fontSize: 11, padding: '4px 10px' } : undefined, title: 'Clear the weight, size, service, package, confirmation and flag changes on this order', onClick: async function() {
                // asks first, drops a typed-but-unsaved edit and waits for a save in flight so it
                // cannot land after the reset (sweep 2026-09-27 M8 / M9); the server keeps the
                // reship flag, Ship By, a corrected ship-to and Custom Fields, and drops the quote
                if (!(await appConfirm('Reset this order’s package and service settings to the rules’ defaults? The reship flag, Ship By date, ship-to and Custom Fields stay.', { danger: true, okLabel: 'Reset' }))) return;
                if (autoTimer.current) { clearTimeout(autoTimer.current); autoTimer.current = null; }
                queuedSaveRef.current = null;
                Promise.resolve(pendingSaveRef.current).then(function() {
                  return api('/shipping/order/' + orderId + '/overrides', { method: 'POST', body: JSON.stringify({ clear: true }) });
                }).then(function() { showToast('Settings reset', 'success'); loadDetail(); onChanged && onChanged(); })
                  .catch(function(e) { showToast(e.message, 'error'); });
              } }, 'Reset'),
              h('span', { style: { fontSize: 10, color: autoState === 'saved' ? C.success : C.textDim } },
                autoState === 'saving' ? 'Saving…' : autoState === 'saved' ? 'All changes saved' : 'Changes save automatically'))
          ),

          // Anchored rate footer (ShipStation parity): big rate + service line on the
          // left, green Create Label (staged until carrier creds) on the right.
          // Sticky over the frame padding like the header strip.
          // Hidden once a label is bought here (9-26: it offered a second purchase)
          !panelLocks.createLabel && h('div', { style: { position: 'sticky', bottom: -16, background: C.surface, borderTop: '1px solid ' + C.border, margin: '12px -4px 12px', padding: '10px 4px 12px', zIndex: 4 } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
              h('div', { style: { flex: 1, minWidth: 0 } },
                h('div', { style: { display: 'flex', alignItems: 'baseline', gap: 8 } },
                  h('span', { style: { fontSize: 10, fontWeight: 600, color: C.success, textTransform: 'uppercase', letterSpacing: '0.04em' } }, 'Rate'),
                  h('span', { style: { fontSize: 14, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' } },
                    !rates ? '…' : (rates.rates && rates.rates.length ? shipMoney(rates.rates[0].cost) : '—')),
                  h('button', { onClick: function() { loadRates(true); }, title: 'Re-quote', style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', padding: 2, alignSelf: 'center' } },
                    h(LIcon, { name: 'refresh-cw', size: 12 }))),
                rates && rates.rates && rates.rates.length
                  ? h('div', null,
                      h('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                        shipCarrierLabel(rates.rates[0].carrier) + ' · ' + rates.rates[0].service + (rates.rates[0].rate_indicator === 'CP' ? ' (cubic)' : '')
                        + (rates.pinned ? ' — assigned by ' + d.computed.service_source : '')
                        + (rates.pinned && rates.service_matched === false ? ' (assigned service unavailable — cheapest shown)' : '')),
                      // transit estimate for the chosen service (Anthony 9-19)
                      shipTransitShort(rates.rates[0]) && h('div', { title: shipTransitLine(rates.rates[0]), style: { fontSize: 11, color: C.textMuted, marginTop: 1, lineHeight: 1.35 } },
                        shipTransitShort(rates.rates[0])))
                  : h('div', { style: { fontSize: 12, color: C.textMuted } }, !rates ? 'Fetching live rates…' : (rates.note || 'No live rates for this destination.'))),
              // Split button (SS parity): Create Label + ▾ menu (Create & Print
              // staged)
              h('div', { ref: createMenuRef, style: { position: 'relative', display: 'flex', flexShrink: 0 } },
                h('button', {
                  disabled: labelBusy,
                  title: 'Buy the label and mark the order shipped',
                  onClick: function() { createLabel(false); },
                  style: { background: '#16A34A', opacity: labelBusy ? 0.6 : 1, color: '#fff', border: 'none', borderRadius: '6px 0 0 6px', padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: labelBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }
                }, labelBusy ? 'Buying…' : 'Create Label'),
                h('button', { onClick: function() {
                  // Fixed-position drop-DOWN sized to the split button — an absolute
                  // menu would clip inside the panel's scroll container down here
                  if (!createMenuOpen && createMenuRef.current) {
                    var z = window.APP_ZOOM || 1;
                    var rc = createMenuRef.current.getBoundingClientRect();
                    setCreateMenuPos({ top: rc.bottom / z + 4, left: rc.left / z, width: rc.width / z });
                  }
                  setCreateMenuOpen(!createMenuOpen);
                }, title: 'More label actions', style: { background: '#16A34A', opacity: 0.75, color: '#fff', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.35)', borderRadius: '0 6px 6px 0', padding: '6px 7px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' } }, '▾'),
                createMenuOpen && createMenuPos && h('div', { style: { position: 'fixed', top: createMenuPos.top, left: createMenuPos.left, width: createMenuPos.width, boxSizing: 'border-box', zIndex: 5000, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' } },
                  h('div', {
                    onClick: labelBusy ? undefined : function() { setCreateMenuOpen(false); createLabel(true); },
                    onMouseEnter: function(e) { if (!labelBusy) e.currentTarget.style.background = C.bg; },
                    onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; },
                    style: { padding: '8px 12px', fontSize: 12, color: labelBusy ? C.textDim : C.text, cursor: labelBusy ? 'wait' : 'pointer' }
                  }, 'Create & Print'),
                  onMarkShipped && h('div', {
                    onClick: function() { setCreateMenuOpen(false); onMarkShipped(d); },
                    onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
                    onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; },
                    style: { padding: '8px 12px', fontSize: 12, color: C.text, cursor: 'pointer', borderTop: '1px solid ' + C.border }
                  }, 'Mark as shipped…'))))),

          // Items (modal mode renders these full-size in its left column)
          !embedded && h('div', null,
          // Header mirrors the multi-select rollup: Items [count] … Units [total]
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 } },
            h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Items'),
            h('span', { style: { fontSize: 10, fontWeight: 500, background: C.bg, border: '1px solid ' + C.border, color: C.textMuted, padding: '1px 8px', borderRadius: 999 } }, d.lines.length.toLocaleString()),
            h('span', { style: { flex: 1 } }),
            h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Units'),
            h('span', { style: { fontSize: 10, fontWeight: 500, background: C.bg, border: '1px solid ' + C.border, color: C.textMuted, padding: '1px 8px', borderRadius: 999 } },
              d.lines.reduce(function(a, l) { return a + (parseInt(l.qty, 10) || 0); }, 0).toLocaleString())),
          h('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
            d.lines.map(function(l, i) {
              return h('div', {
                key: i,
                className: 'phov',
                onClick: function() { setProdSku(l.sku); },
                title: 'View product information',
                style: { display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', borderRadius: 6, padding: 2 }
              },
                // Sizing matches the multi-select rollup (48px thumbs / 12.5 titles /
                // 11 SKUs / 17 qty) so single vs multi reads identically (8-12)
                l.image_url
                  ? h('img', { src: l.image_url, style: { width: 48, height: 48, objectFit: 'contain', borderRadius: 6, background: '#fff', flexShrink: 0 } })
                  : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.bg, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 13, color: C.accent, lineHeight: 1.4, wordBreak: 'break-word' } }, l.title || l.sku),
                  h('div', { style: { fontSize: 12, color: C.textMuted } }, l.sku)),
                h('div', { style: { fontSize: 17, fontWeight: 700, color: C.text, minWidth: 28, textAlign: 'center', fontVariantNumeric: 'tabular-nums' } }, l.qty),
                h(LIcon, { name: 'chevron-right', size: 13, color: C.textDim }));
            }))),

          // Customs — international orders only (user ask 8-30), directly
          // editable: per-item HS / COO / value / description save to the
          // PRODUCT catalog on blur; ✨ = AI HS suggest that fills and saves.
          // Effective declaration values (user rule): value = product override
          // else the order line's price; description = product override else
          // the item title (both shown as grey placeholders when riding the
          // line). Order (user, 8-30): rate card above, customs BELOW Items.
          // Safe under the sticky rate footer only because Recipient/summary
          // follow — the footer scrolls away before these inputs reach the
          // viewport bottom (the original 8-30 overlap trap bites when
          // customs is the final section).
          (function() {
            var stOv = (d.overrides && d.overrides.ship_to) || {};
            var country = String(stOv.country || d.recipient.country || 'US').toUpperCase();
            var state = String(stOv.state || d.recipient.state || '').toUpperCase();
            // A domestic lane skips customs — except the destinations DHL still declares on a domestic
            // label: the Pacific territories (GU / AS / MP, by country or state) and military mail
            // (APO / FPO / DPO = states AA / AE / AP; order 4242835, Anthony 9-22). Same set as the
            // label route's DHL_CUSTOMS_TERRITORIES.
            var CUSTOMS_TERR = { GU: 1, AS: 1, MP: 1, AA: 1, AE: 1, AP: 1 };
            var domesticLane = ['US', 'PR', 'VI', 'GU', 'AS', 'MP'].indexOf(country) !== -1;
            if (domesticLane && !CUSTOMS_TERR[country] && !CUSTOMS_TERR[state]) return null;
            var miss = function(v) { return v == null || v === ''; };
            return h('div', null,
              secTitle('Customs'),
              d.lines.map(function(l, i) {
                var noHs = miss(l.hs_code);
                var saveLine = function(patch) {
                  if (!l.product_id) { showToast('No catalog product for ' + l.sku, 'error'); return; }
                  api('/shipping/products-list/' + l.product_id, { method: 'POST', body: JSON.stringify(patch) })
                    .then(function() {
                      // A saved value IS the catalog override now — drop the
                      // "· Zentail" source hint on the patched fields
                      var lp = Object.assign({}, patch);
                      if ('hs_code' in lp) lp.hs_source = null;
                      if ('country_of_origin' in lp) lp.coo_source = null;
                      patchLine(l.sku, lp);
                      showToast(l.sku + ' customs saved', 'success');
                    })
                    .catch(function(e) { showToast(e.message, 'error'); });
                };
                var enterBlur = function(e) { if (e.key === 'Enter') e.currentTarget.blur(); };
                // Ship-To-editor styling: 10px label above a full-width input.
                // size: number = FLEX ratio, string = CSS width ('86px'/'100%')
                var fld = function(label, key, cur, ph, size, mono, warn, err) {
                  return h('div', { style: typeof size === 'number' ? { flex: size, minWidth: 0 } : { width: size, flexShrink: 0 } },
                    h('div', { style: { fontSize: 11, color: warn ? '#F59E0B' : C.textMuted, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, label),
                    h('input', {
                      key: key + ':' + (cur == null ? '' : cur), defaultValue: cur == null ? '' : String(cur), placeholder: ph || '',
                      title: shipErrTip(err) || ph || '', onKeyDown: enterBlur,
                      onBlur: function(e) {
                        var v = e.target.value.trim();
                        if (v !== String(cur == null ? '' : cur)) { var p = {}; p[key] = v || null; saveLine(p); }
                      },
                      style: Object.assign({}, inpS, { width: '100%' }, warn ? { border: '1px solid rgba(245,158,11,0.6)' } : {}, shipErrBox(err))
                    }));
                };
                // Read-only cells (Qty from the order line, computed Total) —
                // ShipStation shows both greyed in each declaration
                var roFld = function(label, val, size) {
                  return h('div', { style: typeof size === 'number' ? { flex: size, minWidth: 0 } : { width: size, flexShrink: 0 } },
                    h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, label),
                    h('div', { style: Object.assign({}, inpS, { width: '100%', color: C.textMuted, background: 'transparent', opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }, val == null || val === '' ? '—' : String(val)));
                };
                var q = parseInt(l.qty, 10) || 0;
                // Declared values are USD: fall back to the order line's USD twin (unit_price_usd), never the
                // native price — a 1,585 MXN line used to preview as $1,585 (currency Phase B, 2026-09-03)
                var lineUsd = l.unit_price_usd != null ? parseFloat(l.unit_price_usd) : (l.unit_price != null && (!l.currency || l.currency === 'USD') ? parseFloat(l.unit_price) : NaN);
                var effUnit = l.customs_value != null && l.customs_value !== '' ? parseFloat(l.customs_value) : lineUsd;
                var totalVal = isNaN(effUnit) ? '—' : shipMoney(effUnit * q);
                var hsNorm = String(l.hs_code || '').replace(/[^0-9]/g, '');
                var hsMean = hsNorm.length >= 6 ? hsMeanings[hsNorm] : undefined;
                var cooCur = String(l.country_of_origin || '').toUpperCase();
                if (!window._shipCountriesSorted) window._shipCountriesSorted = SHIP_ISO_COUNTRIES.slice().sort(function(a, b) { return shipCountryName(a).localeCompare(shipCountryName(b)); });
                // ShipStation declaration order (reviewed on SS order 4196468,
                // 8-30): Description → values → HS + Find button → COO dropdown,
                // one numbered block per item. User placements folded in: COO
                // rides directly under the description; AI meaning under HS.
                return h('div', { key: i, style: { paddingTop: i ? 12 : 0, marginTop: i ? 12 : 2, borderTop: i ? '1px solid ' + C.border : 'none' } },
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 } },
                    h('span', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 } }, 'Declaration ' + (i + 1)),
                    noHs && h('span', { style: { fontSize: 9, fontWeight: 600, color: '#F59E0B', textTransform: 'uppercase', flexShrink: 0 } }, 'HS missing'),
                    h('span', { style: { flex: 1 } }),
                    h('span', {
                      className: 'phov',
                      onClick: function() { setProdSku({ sku: l.sku, tab: 'customs', fallback: { value: l.unit_price, title: l.title, zen_hs: l.hs_source === 'zentail' ? l.hs_code : null } }); },
                      title: 'Open the product’s full customs editor',
                      style: { fontSize: 11.5, fontWeight: 600, color: C.accent, cursor: 'pointer', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderRadius: 4, padding: '1px 3px', margin: '-1px -3px' }
                    }, l.sku)),
                  h(ShipCountedField, { key: 'desc' + i, label: 'Description', cur: l.customs_description, max: 50, inpS: inpS,
                    ph: (l.title ? String(l.title).slice(0, 70) : 'Description') + ' (order)',
                    warn: labelErr('customs_description', i) ? labelErrText('customs_description', i) : '',
                    onCommit: function(v) { saveLine({ customs_description: v }); } }),
                  h('div', { style: { marginTop: 6 } },
                    h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 2 } }, 'Country of origin' + (l.coo_source === 'zentail' ? ' · Zentail' : '')),
                    h('select', {
                      key: 'coo:' + cooCur, defaultValue: cooCur,
                      onChange: function(e) { var v = e.target.value.trim().toUpperCase(); if (v !== cooCur) saveLine({ country_of_origin: v || null }); },
                      style: Object.assign({}, inpS, { width: '100%', cursor: 'pointer' })
                    },
                      h('option', { value: '' }, '—'),
                      cooCur && SHIP_ISO_COUNTRIES.indexOf(cooCur) === -1 ? h('option', { value: cooCur }, cooCur + ' (saved)') : null,
                      window._shipCountriesSorted.map(function(c) { return h('option', { key: c, value: c }, shipCountryName(c) + ' (' + c + ')'); }))),
                  h('div', { style: { display: 'flex', gap: 6, marginTop: 6 } },
                    roFld('Qty', q, (docked ? 40 : 46) + 'px'),
                    fld('Item value (ea)', 'customs_value', l.customs_value, !isNaN(lineUsd) ? '$' + lineUsd.toFixed(2) + ' (order' + (l.currency && l.currency !== 'USD' ? ', from ' + l.currency : '') + ')' : '—', 1),
                    roFld('Total value', totalVal, 1)),
                  h('div', { style: { display: 'flex', gap: 6, marginTop: 6, alignItems: 'flex-end' } },
                    fld('HS code' + (l.hs_source === 'zentail' ? ' · Zentail' : ''), 'hs_code', l.hs_code, 'Required', 1, true, noHs, labelErr('hs_code', i) ? labelErrText('hs_code', i) : ''),
                    h('button', {
                      className: 'phov', disabled: !!prodHsBusy,
                      title: 'AI-suggest an HS code (fills and saves)',
                      onClick: function() { aiFillHs(l); },
                      style: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid ' + C.border, color: C.accent, cursor: prodHsBusy ? 'wait' : 'pointer', borderRadius: 6, padding: docked ? '5px 9px' : '6px 10px', fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit', flexShrink: 0, opacity: prodHsBusy === l.sku ? 0.45 : 1 }
                    }, h(LIcon, { name: 'sparkles', size: 12 }), prodHsBusy === l.sku ? 'Classifying…' : 'Find HS Code')),
                  l.hs_code && (hsMean || hsMean === null) ? h('div', {
                    title: hsMean || '',
                    style: { fontSize: 10.5, color: C.textDim, fontStyle: 'italic', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                  }, hsMean === null ? 'Looking up code…' : hsMean) : null);
              }),
              h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 8 } }, 'Saves to the product catalog on blur. Grey value/description = riding the order line.'));
          })(),

          // Recipient (ship-to editable via overrides.ship_to)
          secTitle('Recipient'),
          (function() {
            var ovr = (d.overrides && d.overrides.ship_to) || null;
            // The display follows the edited address as soon as it is typed: the autosave persists
            // it without reloading the detail, so the display read the OLD address after Save and
            // looked unsaved (Anthony 9-23, order 4242835)
            var stEdited = Object.keys(shipTo).some(function(k) { return shipTo[k] && String(shipTo[k]).trim(); });
            var r = Object.assign({}, d.recipient, ovr || {}, stEdited ? shipTo : {});
            var stVal = function(key) { return shipTo[key] == null ? (r[key] || '') : shipTo[key]; };
            var stSet = function(key, v) { var n = {}; n[key] = v; shipToTouchedRef.current = true; setShipTo(Object.assign({}, shipTo, n)); };
            var stWrap = function(label, width, control) {
              return h('div', { style: { flex: width || 1, minWidth: docked ? 70 : 90 } },
                h('div', { style: { fontSize: 10, color: C.textMuted, fontWeight: 500, marginBottom: 2 } }, label), control);
            };
            var stField = function(key, label, width) {
              var err = labelErr(key) ? labelErrText(key) : '';
              return stWrap(label, width, h('input', { value: stVal(key), title: shipErrTip(err, stVal(key)) || undefined,
                onChange: function(e) { stSet(key, e.target.value); },
                style: Object.assign({}, inpS, { width: '100%', boxSizing: 'border-box' }, shipErrBox(err, stVal(key))) }));
            };
            var stateField = function() {
              var country = String(stVal('country') || 'US').toUpperCase();
              var list = SHIP_STATE_LISTS[country];
              if (!list) return stField('state', 'State'); // no fixed list for this country
              var cur = String(stVal('state') || '').toUpperCase();
              var stErr = labelErr('state') ? labelErrText('state') : '';
              return stWrap('State', 1, h('select', { value: cur, onChange: function(e) { stSet('state', e.target.value); }, title: shipErrTip(stErr, cur) || undefined,
                style: Object.assign({}, inpS, { width: '100%', cursor: 'pointer' }, shipErrBox(stErr, cur)) },
                h('option', { value: '' }, '—'),
                (cur && list.indexOf(cur) === -1) && h('option', { value: cur }, cur + ' (saved)'),
                list.map(function(s) { return h('option', { key: s, value: s }, s); })));
            };
            var countryField = function(width) {
              var cur = String(stVal('country') || 'US').toUpperCase();
              if (!window._shipCountriesSorted) window._shipCountriesSorted = SHIP_ISO_COUNTRIES.slice().sort(function(a, b) { return shipCountryName(a).localeCompare(shipCountryName(b)); });
              return stWrap('Country', width, h('select', { value: cur, onChange: function(e) { stSet('country', e.target.value); },
                style: Object.assign({}, inpS, { width: '100%', cursor: 'pointer' }) },
                (cur && SHIP_ISO_COUNTRIES.indexOf(cur) === -1) && h('option', { value: cur }, cur + ' (saved)'),
                window._shipCountriesSorted.map(function(c) { return h('option', { key: c, value: c }, shipCountryName(c)); })));
            };
            return h('div', null,
              labelHoldBanner(),
              labelErrBanner(!((d.label_error_fields || []).length)),
              h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 } },
                h('span', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Ship To'),
                // Prefill the form with the MERGED address (exactly what the
                // display shows) — seeding only override keys let per-field
                // fallbacks drift from the display (blank phone anomaly, 8-12)
                (d.status !== 'SHIPPED' && d.status !== 'CANCELLED') && h('button', { onClick: function() { if (!editShipTo && !shipToTouchedRef.current) setShipTo(shipSeedShipTo(r, ovr)); setEditShipTo(!editShipTo); },
                  style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', padding: 0 } },
                  editShipTo ? 'Save' : 'Edit')),
              d.rate_error_kind === 'address' && d.rate_error && h('div', { style: { margin: '2px 0 8px', padding: '6px 10px', borderRadius: 6, background: C.dangerSoft || C.bg, color: C.danger, fontSize: 12, fontWeight: 500 } },
                d.rate_error + (editShipTo ? '' : ' — click Edit to fix the address')),
              editShipTo
                ? h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
                    stField('name', 'Name'),
                    stField('address1', 'Address 1'),
                    stField('address2', 'Address 2'),
                    stField('address3', 'Address 3'),
                    h('div', { style: { display: 'flex', gap: 6 } }, stField('city', 'City', 2), stateField(), stField('zip', 'ZIP')),
                    h('div', { style: { display: 'flex', gap: 6 } }, countryField(1), stField('phone', 'Phone', 2)),
                    h('div', { style: { fontSize: 10, color: C.textDim } }, 'Save changes (above) stores the corrected address — labels will use it; Zentail keeps the original.'))
                : h('div', { style: { fontSize: 12, color: C.text, lineHeight: 1.6 } },
                    h('div', { style: { fontWeight: 500 } }, r.name || '—'),
                    r.address1 && h('div', null, r.address1),
                    r.address2 && h('div', null, r.address2),
                    r.address3 && h('div', null, r.address3),
                    h('div', null, [r.city, r.state, r.zip].filter(Boolean).join(', ') + ' ' + (r.country !== 'US' ? (r.country || '') : '')),
                    r.phone && h('div', { style: { color: C.textMuted, fontSize: docked ? 10.5 : 11 } }, r.phone),
                    d.recipient.email && h('div', { style: { color: C.textMuted, fontSize: docked ? 10.5 : 11 } }, d.recipient.email)),
              // Validation status lives under the address details (user preference).
              // USPS Addresses 3.0 backs the Validate action (domestic only) —
              // success standardizes the address into the ship-to override.
              (function() {
                var av = d.overrides && d.overrides.address_validation;
                var domesticSt = ['US', 'PR', 'VI', 'GU', 'AS', 'MP'].indexOf(String(r.country || 'US').toUpperCase()) !== -1;
                var chip = function(bg, col, txt, title) {
                  return h('span', { title: title || undefined, style: { fontSize: 9, fontWeight: 500, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, background: bg, color: col } }, txt);
                };
                return h('div', { style: { display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' } },
                  av && av.status === 'validated'
                    // kept_lines: USPS dropped or ignored part of the street line (a forwarder code, a
                    // unit), so only city / state / ZIP+4 were taken and lines 1-3 stay as entered (2026-10-27)
                    ? chip(C.successSoft || C.bg, C.success, av.kept_lines ? '✓ Validated · street kept as entered' : '✓ Validated',
                      (av.lost && av.lost.length ? 'USPS dropped ' + av.lost.join(', ') + ' from the street line, so the lines stay as entered · ' : av.kept_lines ? 'Street lines kept as entered · ' : '')
                      + (av.reason ? av.reason + ' · ' : '') + 'USPS DPV ' + (av.dpv || '') + ' · ' + new Date(av.checked_at).toLocaleString())
                    : av && av.status === 'failed'
                      ? chip(C.dangerSoft || C.bg, C.danger, '⚠ Not verifiable', av.reason || 'USPS could not confirm this address')
                      : chip(C.warningSoft || C.bg, C.warning, 'Not validated'),
                  shipAddressTypeChip(d),
                  ovr && chip(C.accentSoft, C.accent, 'Edited'),
                  domesticSt && (d.status !== 'SHIPPED' && d.status !== 'CANCELLED') && h('button', {
                    disabled: valBusy,
                    onClick: function() {
                      setValBusy(true);
                      // the address just typed is saved first — Validate used to check the OLD one (9-27 M9)
                      Promise.resolve(flushAutosave()).then(function() { return api('/shipping/order/' + orderId + '/validate-address', { method: 'POST', body: '{}' }); })
                        .then(function(res2) {
                          setValBusy(false);
                          var vv = res2.validation || {};
                          showToast(vv.status === 'validated' ? 'Address validated' + (vv.reason ? ' — ' + vv.reason : '') : (vv.reason || 'Validation failed'), vv.status === 'validated' ? 'success' : 'error');
                          loadDetail(); onChanged && onChanged();
                        })
                        .catch(function(e) { setValBusy(false); showToast(String(e.message || e), 'error'); });
                    },
                    style: { background: 'transparent', border: 'none', color: C.accent, cursor: valBusy ? 'wait' : 'pointer', fontSize: 10.5, fontFamily: 'inherit', padding: 0 }
                  }, valBusy ? 'Validating…' : (av ? 'Re-validate' : 'Validate')));
              })());
          })(),

          // Sold to (modal mode: lives in the left-column Order Details card)
          !embedded && h('div', null,
          secTitle('Sold to'),
          h('div', { style: { fontSize: 12, color: C.text, lineHeight: 1.7 } },
            h('div', null, h('span', { style: { color: C.textMuted } }, 'Customer: '), (d.sold_to && d.sold_to.name) || '—'),
            h('div', null, h('span', { style: { color: C.textMuted } }, 'Email: '), (d.sold_to && d.sold_to.email) || '—'),
            // the "Phone: —" line was a placeholder that never read anything (order 4279726,
            // Anthony 2026-10-08); the ship-to phone shows in the Ship To block instead
            h('div', null, h('span', { style: { color: C.textMuted } }, 'Ship-to name: '), (d.sold_to && d.sold_to.ship_to_name) || '—'))),

          // Order summary + sales (modal mode: left-column Order Details card)
          embedded ? null : h('div', null, secTitle('Order summary'), (function() {
            // Left-aligned values (fixed label column) — was space-between (user ask 8-12)
            var row = function(label, val, bold) {
              return h('div', { style: { display: 'flex', gap: 10, fontSize: 12, padding: '2px 0' } },
                h('span', { style: { color: C.textMuted, width: 118, flexShrink: 0 } }, label),
                h('span', { style: { color: C.text, fontWeight: bold ? 700 : 400, minWidth: 0, wordBreak: 'break-word' } }, val));
            };
            var s = d.sales || {};
            var m = function(v) { return v == null ? '—' : shipMoney(v, s.currency); };
            return h('div', null,
              row('Zentail Order ID', d.order_number),
              row('Channel Order ID', marketplaceOrderLink(d.channel, d.channel_order_id)), // links to the marketplace too (Anthony 2026-09-23)
              row('Order date', new Date(d.order_date).toLocaleString()),
              row('Paid date', d.paid_ts ? new Date(d.paid_ts).toLocaleString() : '—'),
              row('Ship by date', d.ship_by ? String(d.ship_by).slice(0, 10) : '—'),
              (d.meta && d.meta.hold_until) ? row('Hold until', String(d.meta.hold_until).slice(0, 10)) : null,
              h('div', { style: { borderTop: '1px dashed ' + C.border, margin: '6px 0' } }),
              row('Product total', m(s.item_price)),
              (s.discount != null && s.discount > 0) ? row('Discount', '−' + shipMoney(s.discount, s.currency)) : null,
              row('Shipping collected', m(s.shipping_paid)),
              row('Tax collected', m(s.tax)),
              // Relay orders: the native total the customer paid, then its USD twin (order-date rate)
              row('Order total', (s.currency && s.currency !== 'USD' && d.total_usd != null) ? m(s.total) + ' = ' + shipMoney(d.total_usd) : m(s.total), true),
              row('Total paid', m(s.paid), true));
          })()),

          // Custom fields, as ShipStation showed them: Zentail fed 1 = channel label and
          // 2 = the marketplace order number (the channel reference number — Faire YUZCS3TMEK
          // on a Shopify relay — else the channel order id); 3 was manual. The defaults are
          // grey placeholders from the server (custom_defaults); typing saves an override
          // on the order and the queue row follows (Anthony 9-18, order 4237510).
          secTitle('Custom fields'),
          h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
            ['custom1', 'custom2', 'custom3'].map(function(k, i) {
              var dflt = (d.custom_defaults && d.custom_defaults[k]) || '';
              var isDefault = !!dflt && customF[k].trim() === dflt;
              var effective = customF[k].trim() || dflt;
              return h('div', { key: k, style: { display: 'flex', alignItems: 'center', gap: 6 } },
                h('span', { style: { fontSize: 11, color: C.textMuted, width: docked ? 76 : 92, flexShrink: 0 } }, 'Custom Field ' + (i + 1)),
                h('input', { value: customF[k], onChange: function(e) { var n = {}; n[k] = e.target.value; setCustomF(Object.assign({}, customF, n)); },
                  // Cleared → back to the Zentail value (the autosave drops the override)
                  onBlur: function(e) { if (!e.target.value.trim() && dflt) { var n = {}; n[k] = dflt; setCustomF(Object.assign({}, customF, n)); } },
                  placeholder: dflt,
                  title: dflt ? (isDefault ? 'From Zentail. Edit to override for this order; clear to restore.' : 'Override for this order (Zentail: ' + dflt + '). Clear to restore.') : '',
                  style: Object.assign({}, inpS, { flex: 1, width: 'auto' }, isDefault ? { color: C.textMuted } : {}) }),
                shipCopyIcon(effective, showToast, 28));
            }),
            h('div', { style: { fontSize: 10, color: C.textDim } }, 'Saves automatically. Grey text is what Zentail sends (channel, marketplace order number): select it or use the copy icon; edit to override for this order, clear to restore.')),

          // Notes — stays last (tags moved to the top section, user ask 8-12)
          secTitle('Notes'),
          d.buyer_notes && h('div', { style: { marginBottom: 8 } },
            h('div', { style: { fontSize: 10.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 } }, 'Notes from buyer'),
            h('div', { style: { fontSize: 12, color: C.text, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', whiteSpace: 'pre-wrap' } }, d.buyer_notes)),
          h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 } },
              h('span', { style: { fontSize: 10.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Internal note'),
              h('button', { onClick: function() { setNoteEditing(!noteEditing); setNoteDraft((d.meta && d.meta.note) || ''); },
                style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', padding: 0 } },
                noteEditing ? 'Close' : (d.meta.note ? 'Edit' : 'Add'))),
            noteEditing
              ? h('div', null,
                  h('textarea', { value: noteDraft, onChange: function(e) { setNoteDraft(e.target.value); }, rows: 2,
                    style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', resize: 'vertical' } }),
                  h(Btn, { size: 'sm', variant: 'primary', onClick: function() {
                    var noteBody = JSON.stringify({ order_ids: [Number(orderId)], note: noteDraft.trim() || null });
                    // a typed panel edit is saved first — the note's reload would drop it (9-27 M9)
                    Promise.resolve(flushAutosave()).then(function() { return api('/shipping/orders/meta', { method: 'POST', body: noteBody }); })
                      .then(function() { showToast('Note saved', 'success'); setNoteEditing(false); loadDetail(); onChanged && onChanged(); })
                      .catch(function(e) { showToast(e.message, 'error'); });
                  } }, 'Save note'))
              : d.meta.note
                ? h('div', { style: { fontSize: 11.5, color: C.textMuted, fontStyle: 'italic' } }, d.meta.note)
                : h('div', { style: { fontSize: 11, color: C.textDim } }, 'None'))
        )
    ),
    prodSku && h(ShipProductModal, {
      sku: typeof prodSku === 'object' ? prodSku.sku : prodSku,
      initialTab: typeof prodSku === 'object' ? prodSku.tab : undefined,
      customsFallback: typeof prodSku === 'object' ? prodSku.fallback : undefined,
      onClose: function() { setProdSku(null); }, showToast: showToast,
      // Save in the editor → the same live patch the inline customs fields apply; the
      // queue row's amber HS flag otherwise stayed until the next reload (Anthony 9-18)
      onSaved: function(body) {
        var sku = typeof prodSku === 'object' ? prodSku.sku : prodSku;
        patchLine(sku, function(l) { return shipApplyCustomsSave(l, body); });
      },
    })
  );
}

// ─── Order detail modal (full-screen deep view for OPEN orders) ─────────────
// Opened by the order-number link in the queue; ←/→ walk the queue's current
// sort. The right column embeds ShipOrderPanel — the same autosave / rates /
// tag / label machinery as the dock, so there is exactly ONE editing
// implementation — and the left column renders the read side at full size
// (items, money, facts), fed by the panel's fetch via onDetail (no second
// request). ─────────────────────────────────────────────────────────────────
function ShipOrderModal({ orderId, onClose, onPrev, onNext, showToast, onChanged, onRowPatch, onEditManual, onMarkShipped }) {
  var [d, setD] = useState(null);
  var [prodSku, setProdSku] = useState(null);
  var [view, setView] = useState('details'); // 'details' | 'activity' (right icon rail)
  useEffect(function() { setD(null); setView('details'); }, [orderId]);
  useEffect(function() {
    var onKey = function(e) {
      if (shipKeyInEditable(e)) return; // typing in a field — review M7 #103
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [onPrev, onNext]);

  var card = function(title, children) {
    return h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: '14px 18px', marginBottom: 14 } },
      h('div', { style: { fontSize: 14, fontWeight: 500, color: C.text, marginBottom: 10 } }, title),
      children);
  };
  var kv = function(label, val) {
    return h('div', { style: { marginBottom: 8 } },
      h('div', { style: { fontSize: 11.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' } }, label),
      h('div', { style: { fontSize: 13.5, color: C.text, marginTop: 1 } }, val == null || val === '' ? '—' : val));
  };
  var money = function(v, cur) { return v == null ? '—' : shipMoney(v, cur); };
  var reloadRef = useRef(null); // panel's loadDetail — refetch after modal-side edits
  var afterEdit = function(label) {
    showToast(label + ' saved', 'success');
    // (ShipActivityTimeline owns its own activity state and refetches on mount — a stray
    // setAct(null) here threw ReferenceError after every Ship-by / Assigned / Hold save and
    // skipped the reload below. Review finding, 2026-09-02.)
    if (reloadRef.current) reloadRef.current();
    if (onChanged) onChanged();
  };
  var saveMeta = function(patch, label) {
    api('/shipping/orders/meta', { method: 'POST', body: JSON.stringify(Object.assign({ order_ids: [Number(orderId)] }, patch)) })
      .then(function() { afterEdit(label); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var saveShipBy = function(v) {
    api('/shipping/order/' + orderId + '/overrides', { method: 'POST', body: JSON.stringify({ ship_by: v || null }) })
      .then(function() { afterEdit('Ship-by date'); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var editInp = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '5px 8px', fontSize: 12, fontFamily: 'inherit', width: 170, boxSizing: 'border-box' };
  var kvEdit = function(label, control) {
    return h('div', { style: { marginBottom: 8 } },
      h('div', { style: { fontSize: 10.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 } }, label),
      control);
  };
  var navBtn = function(dir, fn) {
    return h('button', {
      onClick: fn || undefined, disabled: !fn,
      title: dir === 'l' ? 'Previous order (←)' : 'Next order (→)',
      style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 6, background: C.bg, border: '1px solid ' + C.border, color: fn ? C.text : C.textDim, cursor: fn ? 'pointer' : 'default', opacity: fn ? 1 : 0.5 }
    }, h(LIcon, { name: dir === 'l' ? 'arrow-left' : 'arrow-right', size: 14 }));
  };
  var s = (d && d.sales) || {};

  return h('div', { style: { position: 'fixed', inset: 0, zIndex: 1300, background: C.bg, display: 'flex', flexDirection: 'column' } },
    // Top bar — identity + status + queue navigation; actions (Batch/Print/
    // labels) live in the embedded panel like the dock
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid ' + C.border, background: C.surface } },
      h(LIcon, { name: 'package', size: 16, color: C.accent }),
      h('span', { style: { fontSize: 14, color: C.textMuted } }, d ? (d.channel || '') + ':' : ''),
      h('span', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, d ? 'Order # ' + d.order_number : 'Loading…'),
      d && h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 999, background: C.bg, color: C.textMuted, border: '1px solid ' + C.border } },
        h('span', { style: { width: 7, height: 7, borderRadius: 999, background: d.status === 'PENDING' ? C.warning : C.textDim } }), d.status),
      h('span', { style: { flex: 1 } }),
      navBtn('l', onPrev), navBtn('r', onNext),
      h('button', { onClick: onClose, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 6 } }, h(LIcon, { name: 'x', size: 20 }))),
    // Body: read side + working side, or the activity timeline. The right icon
    // rail switches views; the details pair stays MOUNTED (display toggle) so
    // the embedded panel's autosave/draft state survives the switch.
    h('div', { style: { flex: 1, minHeight: 0, display: 'flex' } },
      h('div', { style: { flex: 1, minWidth: 0, display: view === 'details' ? 'flex' : 'none' } },
      h('div', { style: { flex: 1, minWidth: 0, overflowY: 'auto', padding: '18px 24px 24px' } },
        !d ? h('div', { style: { padding: 60, textAlign: 'center', color: C.textMuted } }, 'Loading order…')
        : h('div', null,
            card('Order Details', h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 } },
              h('div', null,
                h('div', { style: { fontSize: 11.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, 'Cost Summary'),
                // Left-aligned values beside a fixed label column (user ask 8-29 —
                // space-between pushed the amounts too far from their labels)
                [['Product', s.item_price], ['Shipping', s.shipping_paid], ['Tax', s.tax]].map(function(r2) {
                  return h('div', { key: r2[0], style: { display: 'flex', gap: 10, fontSize: 12, padding: '1px 0' } },
                    h('span', { style: { color: C.textMuted, width: 110, flexShrink: 0 } }, r2[0]), h('span', { style: { color: C.text } }, money(r2[1], s.currency)));
                }),
                h('div', { style: { display: 'flex', gap: 10, fontSize: 12.5, padding: '3px 0', borderTop: '1px dashed ' + C.border, marginTop: 3 } },
                  h('span', { style: { color: C.textMuted, width: 110, flexShrink: 0 } }, 'Total'), h('strong', { style: { color: C.text } }, (s.currency && s.currency !== 'USD' && d.total_usd != null) ? money(s.total, s.currency) + ' = ' + shipMoney(d.total_usd) : money(s.total))),
                h('div', { style: { display: 'flex', gap: 10, fontSize: 13.5 } },
                  h('span', { style: { color: C.textMuted, width: 110, flexShrink: 0 } }, 'Total Paid'), h('strong', { style: { color: C.text } }, money(s.paid, s.currency))),
                // Ship To at a glance under the costs (Anthony 2026-10-08): the effective address,
                // overrides included, plus the validation chip. Editing stays in the Recipient block.
                (function() {
                  var st = Object.assign({}, d.recipient || {}, (d.overrides && d.overrides.ship_to) || {});
                  var av = d.overrides && d.overrides.address_validation;
                  var line = function(t, muted) { return t ? h('div', { style: muted ? { color: C.textMuted } : null }, t) : null; };
                  var cityLine = ([st.city, st.state].filter(Boolean).join(', ') + (st.zip ? ' ' + st.zip : '')).trim();
                  var abroad = st.country && String(st.country).toUpperCase() !== 'US';
                  return h('div', { style: { marginTop: 14 } },
                    h('div', { style: { fontSize: 11.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, 'Ship To'),
                    h('div', { style: { fontSize: 12, color: C.text, lineHeight: 1.6 } },
                      line(st.name), line(st.company), line(st.address1), line(st.address2), line(st.address3), line(cityLine),
                      abroad ? line(st.country) : null, line(st.phone, true), line(st.email, true)),
                    av ? h('span', { title: [av.lost && av.lost.length ? 'USPS dropped ' + av.lost.join(', ') + ' from the street line, so the lines stay as entered' : av.kept_lines ? 'Street lines kept as entered' : null, av.reason].filter(Boolean).join(' · ') || undefined,
                      style: { display: 'inline-block', marginTop: 5, fontSize: 9, fontWeight: 500, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4,
                      background: av.status === 'validated' ? (C.successSoft || C.bg) : (C.dangerSoft || C.bg), color: av.status === 'validated' ? C.success : C.danger } },
                      av.status === 'validated' ? (av.kept_lines ? '✓ Validated · street kept as entered' : '✓ Validated') : '⚠ Not verifiable') : null,
                    shipAddressTypeChip(d, { marginTop: 5, marginLeft: av ? 6 : 0 }));
                })()),
              h('div', null,
                kv('Order Date', d.order_date ? new Date(d.order_date).toLocaleString() : null),
                kv('Channel', d.channel),
                kvEdit('Ship By', h('input', {
                  type: 'date', key: 'sb' + (d.ship_by || ''),
                  defaultValue: d.ship_by ? String(d.ship_by).slice(0, 10) : '',
                  title: (d.overrides && d.overrides.ship_by) ? 'Manually set — clear to return to the channel\'s date' : 'Pick a date to override the channel\'s ship-by',
                  // saved on leaving the field / Enter, not per keystroke: typing the year saved
                  // "0002-…" first (sweep 2026-09-27 M19)
                  onBlur: function(e) {
                    var v = e.target.value, was = d.ship_by ? String(d.ship_by).slice(0, 10) : '';
                    if (v === was) return;
                    if (v && !shipPlausibleDate(v)) { showToast('Enter a full date (year 2020 or later)', 'error'); e.target.value = was; return; }
                    saveShipBy(v);
                  },
                  onKeyDown: function(e) { if (e.key === 'Enter') e.currentTarget.blur(); },
                  style: editInp })),
                kvEdit('Assigned To', h('input', {
                  key: 'as' + ((d.meta && d.meta.assignee) || ''),
                  defaultValue: (d.meta && d.meta.assignee) || '', placeholder: 'Unassigned',
                  onBlur: function(e) { var v = e.target.value.trim(); if (v !== ((d.meta && d.meta.assignee) || '')) saveMeta({ assignee: v || null }, 'Assignee'); },
                  onKeyDown: function(e) { if (e.key === 'Enter') e.currentTarget.blur(); },
                  style: editInp })),
                kvEdit('Hold Until', h('input', {
                  type: 'date', key: 'hu' + ((d.meta && d.meta.hold_until) || ''),
                  defaultValue: (d.meta && d.meta.hold_until) ? String(d.meta.hold_until).slice(0, 10) : '',
                  title: 'Holds the order until this date — clear to release',
                  // on leaving the field / Enter (sweep 2026-09-27 M19, see Ship By)
                  onBlur: function(e) {
                    var v = e.target.value, was = (d.meta && d.meta.hold_until) ? String(d.meta.hold_until).slice(0, 10) : '';
                    if (v === was) return;
                    if (v && !shipPlausibleDate(v)) { showToast('Enter a full date (year 2020 or later)', 'error'); e.target.value = was; return; }
                    saveMeta({ hold_until: v || null }, 'Hold');
                  },
                  onKeyDown: function(e) { if (e.key === 'Enter') e.currentTarget.blur(); },
                  style: editInp })),
                // the marketplace's own number opens the order on the marketplace (Anthony 2026-09-23: only here, not the header)
                kv('Channel Order ID', marketplaceOrderLink(d.channel, d.channel_order_id)),
                kv('Sold To', (d.sold_to && (d.sold_to.name || d.sold_to.email)) ? [d.sold_to.name, d.sold_to.email].filter(Boolean).join(' · ') : null)))),
            card('Items (' + (d.lines || []).length + ')', h('div', null,
              h('div', { style: { display: 'grid', gridTemplateColumns: '60px 1fr 90px 60px 90px', gap: 8, fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', padding: '0 0 6px', borderBottom: '1px solid ' + C.border } },
                h('span', null, 'Image'), h('span', null, 'Item'), h('span', { style: { textAlign: 'right' } }, 'Unit Price'), h('span', { style: { textAlign: 'right' } }, 'Qty'), h('span', { style: { textAlign: 'right' } }, 'Total')),
              (d.lines || []).map(function(l, i) {
                var unit = l.unit_price != null ? parseFloat(l.unit_price) : null;
                return h('div', { key: i, style: { display: 'grid', gridTemplateColumns: '60px 1fr 90px 60px 90px', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid ' + C.border } },
                  l.image_url
                    ? h('img', { src: l.image_url, style: { width: 46, height: 46, objectFit: 'contain', borderRadius: 6, background: '#fff' } })
                    : h('div', { style: { width: 46, height: 46, borderRadius: 6, background: C.bg, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, h(LIcon, { name: 'package', size: 18, color: C.textDim })),
                  h('div', null,
                    h('div', { onClick: function() { setProdSku(l.sku); }, style: { fontSize: 13.5, color: C.accent, cursor: 'pointer', lineHeight: 1.4 } }, l.title || l.sku),
                    h('div', { style: { fontSize: 11.5, color: C.textMuted } }, 'SKU: ' + l.sku),
                    (l.barcode || l.location) && h('div', { style: { fontSize: 11.5, color: C.textMuted } },
                      [l.barcode ? 'UPC: ' + l.barcode : null, l.location ? 'Bin: ' + l.location : null].filter(Boolean).join('  ·  '))),
                  h('div', { style: { textAlign: 'right', fontSize: 13.5 } }, unit != null ? shipMoney(unit, d.currency) : '—'),
                  h('div', { style: { textAlign: 'right', fontSize: 16, fontWeight: 500 } }, l.qty),
                  h('div', { style: { textAlign: 'right', fontSize: 13.5 } }, unit != null ? shipMoney(unit * (parseInt(l.qty, 10) || 0), d.currency) : '—'));
              }))),
            d.buyer_notes && card('Note From Buyer',
              h('div', { style: { fontSize: 13.5, color: C.text, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: '8px 12px', whiteSpace: 'pre-wrap' } }, d.buyer_notes)))),
      h('div', { style: { width: 470, flexShrink: 0, display: 'flex', minHeight: 0 } },
        h(ShipOrderPanel, { key: orderId, orderId: orderId, embedded: true, onDetail: setD, reloadRef: reloadRef, onClose: onClose, onChanged: onChanged, showToast: showToast, onRowPatch: onRowPatch, onEditManual: onEditManual, onMarkShipped: onMarkShipped }))),
      // Activity timeline view
      view === 'activity' && h('div', { style: { flex: 1, minWidth: 0, overflowY: 'auto', padding: '18px 24px 24px' } },
        h(ShipActivityTimeline, { orderPk: orderId, showToast: showToast })),
      // Icon rail — view switcher. Rendered last but displayed FIRST (flex
      // order:-1) so it sits on the LEFT edge (user ask 8-29; was right).
      h('div', { style: { order: -1, width: 58, flexShrink: 0, borderRight: '1px solid ' + C.border, background: C.surface, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 8 } },
        [{ k: 'details', icon: 'package', title: 'Order Details' }, { k: 'activity', icon: 'history', title: 'Activity Log' }].map(function(t) {
          var active = view === t.k;
          return h('button', {
            key: t.k, title: t.title, onClick: function() { setView(t.k); },
            className: active ? undefined : 'phov',
            style: { width: 44, height: 44, borderRadius: 10, border: '1px solid ' + (active ? C.accent : 'transparent'), background: active ? 'rgba(79,110,247,0.12)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }
          }, h(LIcon, { name: t.icon, size: 21, color: active ? C.accent : C.textMuted }));
        }))),
    prodSku && h(ShipProductModal, { sku: prodSku, onClose: function() { setProdSku(null); }, showToast: showToast,
      onSaved: function(body) {
        var sku = prodSku;
        if (reloadRef.current) reloadRef.current(); // the embedded panel refetches its lines
        if (onRowPatch) {
          onRowPatch(function(row) { return (row.lines || []).some(function(x) { return x.sku === sku; }); }, function(row) {
            return { lines: (row.lines || []).map(function(x) { return x.sku === sku ? shipApplyCustomsSave(x, body) : x; }) };
          });
        }
      } }));
}

// ─── Queue tab (bucket-aware: ready/hold/all/awaiting_payment/…) ──────────
// ─── Manual orders (off-Zentail: wholesale, replacements, samples) ──────────
// Create/edit form + CSV import. Rows land in the same queue with the chosen
// type as their store/channel; rules, tags, rate shopping and prints all apply.
var SHIP_MANUAL_TYPES = ['Wholesale', 'Replacement', 'Sample', 'Other'];
var SHIP_MANUAL_CSV_COLS = ['OrderRef', 'Type', 'Name', 'Address1', 'Address2', 'City', 'State', 'Zip', 'Country', 'Phone', 'Email', 'SKU', 'Title', 'Qty', 'WeightLb', 'UnitPrice', 'OrderTotal', 'ShipBy', 'Note'];

;
/* ===== app-shipping-modals.js ===== */
// ─── Shipping app: manual-order CSV, batch status, manual order and mark-shipped modals ─── split 2026-09-19
function shipParseCsv(text) {
  var rows = [], row = [], cur = '', inQ = false;
  for (var i = 0; i < text.length; i++) {
    var c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
      else cur += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cur); cur = '';
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
    } else cur += c;
  }
  if (cur !== '' || row.length) { row.push(cur); if (row.length > 1 || row[0] !== '') rows.push(row); }
  return rows;
}

// One row per LINE ITEM; an OrderRef column groups rows into orders (first row
// of a group wins the recipient/order fields) — ShipStation's import model.
function shipCsvToManualOrders(text) {
  var rows = shipParseCsv(text);
  if (!rows.length) return { error: 'Empty file' };
  var hdr = rows[0].map(function(s) { return String(s).trim().toLowerCase(); });
  var col = function(name) { return hdr.indexOf(name.toLowerCase()); };
  if (col('OrderRef') === -1 || col('Name') === -1 || col('SKU') === -1 && col('Title') === -1) {
    return { error: 'Header must include at least OrderRef, Name and SKU (download the template)' };
  }
  var get = function(r, name) { var i = col(name); return i === -1 ? '' : String(r[i] == null ? '' : r[i]).trim(); };
  var byRef = {}; var order = [];
  for (var i = 1; i < rows.length; i++) {
    var r = rows[i];
    if (!r.join('').trim()) continue;
    var ref = get(r, 'OrderRef') || ('row' + i);
    if (!byRef[ref]) {
      byRef[ref] = {
        ref: ref,
        order_type: get(r, 'Type') || 'Other',
        recipient: { name: get(r, 'Name'), address1: get(r, 'Address1'), address2: get(r, 'Address2'), city: get(r, 'City'), state: get(r, 'State'), zip: get(r, 'Zip'), country: get(r, 'Country') || 'US', phone: get(r, 'Phone'), email: get(r, 'Email') },
        lines: [],
        order_total: get(r, 'OrderTotal') || null,
        ship_by: get(r, 'ShipBy') || null,
        note: get(r, 'Note') || null,
      };
      order.push(ref);
    }
    byRef[ref].lines.push({ sku: get(r, 'SKU'), title: get(r, 'Title'), qty: get(r, 'Qty') || 1, weight_lb: get(r, 'WeightLb') || null, unit_price: get(r, 'UnitPrice') || null });
  }
  return { orders: order.map(function(ref) { return byRef[ref]; }) };
}

// ─── Create Labels for several batches at once (Anthony 2026-09-22): every selected Open batch
// runs as its own chain of 25-order chunks — the same call the batch dialog makes — and the chains
// run in parallel, so the system works on all of them at the same time. One dialog, a row per
// batch, filling as chunks land. USPS payment: when runs stop for a fresh authorization, the
// re-run authorizes through ONE chunk first (ten parallel authorizations would each place a card
// hold), then the stopped batches continue in parallel on the shared token. ───
function ShipMultiBatchRunModal({ batches, showToast, onDone, onClose, onOpenBatch }) {
  var [rows, setRows] = useState(function() {
    return (batches || []).map(function(b) { return { id: b.id, number: b.batch_number, status: 'loading', total: 0, done: 0, created: 0, failed: 0, halt: null, needsAuth: false, remaining: [], note: '' }; });
  });
  var [phase, setPhase] = useState('loading'); // loading | running | done
  var [authBusy, setAuthBusy] = useState(false);
  var patchRow = function(id, patch) {
    setRows(function(p) { return p.map(function(r) { return r.id === id ? Object.assign({}, r, typeof patch === 'function' ? patch(r) : patch) : r; }); });
  };
  // One batch = one server job (2026-09-23): POST /label-jobs, then follow the row until it ends.
  // The server buys the first order alone (USPS authorization / account refusals surface there),
  // then four at a time; a halt keeps what was not attempted for a re-run. Closing this window
  // stops nothing — each batch's own dialog reattaches to its run.
  // The polls stop when this window closes and after a 4xx / 10 failures in a row (2026-09-26,
  // review fix 4 — they used to retry every 3 s forever, even after the window was gone).
  var stoppedRef = useRef(false);
  useEffect(function() { stoppedRef.current = false; return function() { stoppedRef.current = true; }; }, []);
  var followJob = function(rowId, jobId) {
    return new Promise(function(resolve) {
      var fails = 0;
      var tick = function() {
        if (stoppedRef.current) { resolve(null); return; }
        api('/shipping/label-jobs/' + jobId).then(function(r) {
          if (stoppedRef.current) { resolve(null); return; }
          fails = 0;
          var j = r && r.job; if (!j) { resolve(null); return; }
          var isLive = j.status === 'checking' || j.status === 'running';
          patchRow(rowId, { done: j.done, created: j.ok, failed: j.fail, halt: j.halted || null, needsAuth: !!j.needs_usps_authorization,
            remaining: isLive ? [] : (j.not_attempted || []), status: isLive ? 'running' : (j.halted ? 'halted' : 'done'), jobId: j.id });
          if (isLive) setTimeout(tick, 1500); else resolve(j); // resolves with the final row
        }).catch(function(e) {
          if (stoppedRef.current) { resolve(null); return; }
          fails++;
          if (shipPollGiveUp(e, fails)) {
            patchRow(rowId, { status: 'halted', note: 'Stopped following the run (' + String((e && e.message) || e) + ') — it continues on the server; open the batch to see its status' });
            resolve(null);
            return;
          }
          setTimeout(tick, 3000);
        });
      };
      tick();
    });
  };
  var runBatch = function(row, ids, authorize) {
    patchRow(row.id, { status: 'running', remaining: ids, halt: null, needsAuth: false });
    return api('/shipping/label-jobs', { method: 'POST', body: JSON.stringify({ order_ids: ids, batch_id: row.id, authorize_usps: authorize === true, skip_check: true }) })
      .then(function(r) { return followJob(row.id, r.job.id); },
            function(e) { patchRow(row.id, { status: 'halted', note: String(e.message || e), remaining: ids }); });
  };
  useEffect(function() {
    // what each batch still has to label (failed or never run), then all chains at once
    var prep = rows.map(function(r) {
      return api('/shipping/batches/' + r.id + '/status').then(function(st) {
        var b = st.batch || {};
        var openish = b.status === 'open' || b.status === 'not_printed';
        var ids = openish ? (st.unlabelled_order_ids || []) : [];
        patchRow(r.id, { total: ids.length, status: ids.length ? 'ready' : 'skipped', note: !openish ? 'not an open batch' : !ids.length ? 'nothing left to label' : '', remaining: ids });
        return { row: r, ids: ids };
      }, function(e) { patchRow(r.id, { status: 'skipped', note: String(e.message || e) }); return { row: r, ids: [] }; });
    });
    Promise.all(prep).then(function(list) {
      setPhase('running');
      return Promise.all(list.filter(function(x) { return x.ids.length; }).map(function(x) { return runBatch(x.row, x.ids, false); }));
    }).then(function() { setPhase('done'); if (onDone) onDone(); });
  }, []);
  var authorizeAndContinue = async function() {
    var halted = rows.filter(function(r) { return r.status === 'halted' && r.remaining.length; });
    if (!halted.length || authBusy) return;
    if (!(await appConfirm('Authorize USPS payment again? This places a new card hold, then the ' + halted.length + ' stopped batch' + (halted.length === 1 ? '' : 'es') + ' continue.', { okLabel: 'Authorize' }))) return;
    setAuthBusy(true); setPhase('running');
    var first = halted[0];
    // one batch carries the authorization (its first order, alone, on the server); the others
    // start once it is through and ride the shared token
    runBatch(first, first.remaining, true).then(function(j) {
      if (!j || j.halted) return; // still stopped: the other batches wait for another answer
      return Promise.all(halted.slice(1).map(function(r) { return runBatch(r, r.remaining, false); }));
    }).then(function() { setPhase('done'); setAuthBusy(false); if (onDone) onDone(); });
  };
  var running = phase !== 'done';
  var tot = rows.reduce(function(a, r) { return { total: a.total + r.total, done: a.done + r.done, created: a.created + r.created, failed: a.failed + r.failed }; }, { total: 0, done: 0, created: 0, failed: 0 });
  var skipped = rows.filter(function(r) { return r.status === 'skipped'; }).length;
  var needsAuth = !running && rows.some(function(r) { return r.status === 'halted' && r.needsAuth && r.remaining.length; });
  var statusText = function(r) {
    if (r.status === 'loading') return 'Loading…';
    if (r.status === 'skipped') return 'Skipped · ' + r.note;
    if (r.status === 'ready') return 'Waiting…';
    if (r.status === 'running') return 'Creating labels…';
    if (r.status === 'halted') return 'Stopped · ' + (r.halt || r.note);
    return r.failed ? 'Done · ' + r.failed + ' failed' : 'Done';
  };
  var cell = function(children, extra) { return h('span', { style: Object.assign({ fontSize: 12, color: C.text }, extra || {}) }, children); };
  var grid = { display: 'grid', gridTemplateColumns: '90px 110px 80px 80px 1fr', gap: 10, alignItems: 'center', padding: '7px 12px' };
  return h('div', { style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }, onMouseDown: function(e) { if (e.target === e.currentTarget) onClose(); } }, // closable mid-run: the runs are server jobs and continue (review fix 5)
    h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: 680, maxWidth: '94vw', maxHeight: '86vh', overflowY: 'auto', boxSizing: 'border-box' } },
      h('div', { style: { display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid ' + C.border } },
        h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, flex: 1 } }, 'Create Labels: ' + rows.length + ' Batch' + (rows.length === 1 ? '' : 'es')),
        h('button', { onClick: onClose, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),
      h('div', { style: { padding: '12px 18px' } },
        h('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
          h('div', { style: Object.assign({}, grid, { fontSize: 10.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, background: C.bg, borderBottom: '1px solid ' + C.border }) },
            h('span', null, 'Batch'), h('span', null, 'Progress'), h('span', null, 'Created'), h('span', null, 'Failed'), h('span', null, 'Status')),
          rows.map(function(r, i) {
            return h('div', { key: r.id, style: Object.assign({}, grid, { borderTop: i === 0 ? 'none' : '1px solid ' + C.border }) },
              h('a', { href: '/batches/' + r.number, onClick: function(e) { e.preventDefault(); if (onOpenBatch) onOpenBatch(r.number); }, title: 'Open this batch', style: { fontSize: 12, fontWeight: 600, color: C.accent, textDecoration: 'none' } }, String(r.number)),
              h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
                cell(r.total ? r.done + ' / ' + r.total : '—'),
                r.total ? h('span', { style: { flex: 1, minWidth: 40, height: 4, background: C.bg, borderRadius: 999, overflow: 'hidden' } },
                  h('span', { style: { display: 'block', height: '100%', width: Math.round((r.done / Math.max(r.total, 1)) * 100) + '%', background: r.status === 'halted' ? C.warning : C.accent, transition: 'width 0.2s' } })) : null),
              cell(r.created ? String(r.created) : '—', { color: r.created ? C.success : C.textDim }),
              cell(r.failed ? String(r.failed) : '—', { color: r.failed ? C.warning : C.textDim }),
              cell(statusText(r), { color: r.status === 'halted' ? C.danger : r.status === 'skipped' ? C.textMuted : C.text }));
          }))),
      h('div', { style: { padding: '12px 18px', borderTop: '1px solid ' + C.border, background: C.bg, fontSize: 12.5, color: C.text, borderRadius: '0 0 12px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
        h('span', { style: { flex: 1 } }, running
          ? 'Creating labels: ' + tot.done + ' of ' + tot.total + ' across ' + (rows.length - skipped) + ' batch' + (rows.length - skipped === 1 ? '' : 'es') + '. The runs continue on the server if you close this window.'
          : tot.created + ' label' + (tot.created === 1 ? '' : 's') + ' created' + (tot.failed ? ', ' + tot.failed + ' failed' : '') + (skipped ? ', ' + skipped + ' batch' + (skipped === 1 ? '' : 'es') + ' skipped' : '') + '.'),
        needsAuth && h(Btn, { size: 'sm', variant: 'primary', disabled: authBusy, onClick: authorizeAndContinue }, 'Authorize USPS payment again (new card hold)'),
        h(Btn, { size: 'sm', onClick: onClose }, 'Close'))));
}

// ─── Batch status dialog (ShipStation "Label Batch Status", Anthony 2026-09-17) ───
// Opens when a Create labels run starts on a batch (live progress) and stays reachable
// from the Batches page afterwards. Everything shown is read from /batches/:id/status,
// so it is the batch record, not the memory of one run.
// Batch statuses (Anthony 9-17): open → not_printed (labels created, not printed; failures can be fixed and re-run) → processed ("Printed", FINAL: unlabelled orders return to the queue) → archived (auto after N days, default 30)
var SHIP_BATCH_STATUS_LABELS = { open: 'Open', not_printed: 'Not Yet Printed', processed: 'Printed', archived: 'Archived', dissolved: 'Dissolved' };
function shipBatchStatusLabel(s) { return SHIP_BATCH_STATUS_LABELS[s] || (s ? String(s) : ''); }
function shipBatchActivityText(a) {
  var d = a.detail || {};
  var n = function(v, one, many) { v = parseInt(v, 10) || 0; return v + ' ' + (v === 1 ? one : many); };
  switch (a.action) {
    case 'created': return 'Batch created with ' + n(d.orders, 'order', 'orders') + (d.note ? ' · note "' + d.note + '"' : '');
    case 'orders_added': return n(d.orders, 'order', 'orders') + ' added';
    case 'orders_removed': return n(d.orders, 'order', 'orders') + ' removed' + (d.reason ? ' · ' + d.reason : '');
    case 'label_run': return 'Labels created: ' + n(d.ok, 'label', 'labels') + (d.failed ? ', ' + n(d.failed, 'order', 'orders') + ' failed' : '');
    case 'labels_printed': return 'Labels printed' + (d.removed ? ' · ' + n(d.removed, 'order', 'orders') + ' without a label returned to the queue' : '');
    case 'slips_printed': return 'Packing slips printed';
    case 'pick_printed': return 'Pick list printed';
    case 'note_changed': return d.note ? 'Note set to "' + d.note + '"' : 'Note cleared';
    case 'assignee_changed': return d.name ? 'Assigned to ' + d.name : 'Unassigned';
    case 'archived': return d.auto ? 'Archived automatically (' + d.days + ' days after printing)' : 'Archived';
    case 'dissolved': return 'Batch dissolved · ' + n(d.orders, 'order', 'orders') + ' returned to the queue' + (d.failed ? ' (' + n(d.failed, 'label error', 'label errors') + ')' : '');
    case 'status_reset': return 'Reset to ' + shipBatchStatusLabel(d.to || 'not_printed');
    default: return a.action;
  }
}
function ShipBatchStatusModal({ batchId, live, jobId, onClose, showToast, onChanged, onOpenOrder, refreshKey, halt, needsAuth }) {
  var [st, setSt] = useState(null);
  var [err, setErr] = useState(null);
  var [noteEdit, setNoteEdit] = useState(null); // null | string being edited
  var [busy, setBusy] = useState('');
  var [run, setRun] = useState(null); // {done,total} while the dialog itself buys labels (retry)
  var [runHalt, setRunHalt] = useState(null); // why the dialog's own run stopped early (USPS daily card limit)
  var [runNeedsAuth, setRunNeedsAuth] = useState(false); // the stop is a question: authorize another USPS payment (new card hold)?
  // The run is a server job (2026-09-23): `job` is the row the dialog follows — its own retry, or a
  // run started on the queue / Shipments page that this dialog reattached to via the batch status
  var [job, setJob] = useState(null);
  var [followId, setFollowId] = useState(jobId || null);
  var [act, setAct] = useState(null); // activity rows, newest first
  var [tab, setTab] = useState('status'); // 'status' | 'activity' (Anthony 9-17: the log is a tab)
  // The dialog keeps the Status tab's height on the Activity tab (Anthony 9-22: it resized): the
  // height measured at the switch locks the box until Status is back.
  var boxRef = useRef(null);
  var [lockH, setLockH] = useState(null);
  var pickTab = function(key) { if (key !== 'status' && boxRef.current) setLockH(boxRef.current.offsetHeight); setTab(key); };
  var tabBtn = function(key, label) {
    var on = tab === key;
    return h('button', { onClick: function() { pickTab(key); },
      style: { background: 'none', border: 'none', borderBottom: '2px solid ' + (on ? C.accent : 'transparent'), color: on ? C.text : C.textMuted, fontWeight: on ? 600 : 500, fontSize: 12.5, padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit', marginBottom: -1 } }, label);
  };
  var reloadActivity = function() {
    return api('/shipping/batches/' + batchId + '/activity').then(function(r) { setAct(Array.isArray(r) ? r : []); }).catch(function() {});
  };
  // Same latest-wins guard as the /progress poll (2026-09-26, review fix 7): reloads fire after
  // every chunk, print, edit and run end; a slow older answer must not overwrite a newer one.
  var reloadSeqRef = useRef(0);
  var reloadLatestRef = useRef(0);
  var reload = function() {
    reloadActivity();
    var mine = ++reloadSeqRef.current;
    return api('/shipping/batches/' + batchId + '/status').then(function(r) {
      if (mine < reloadLatestRef.current) return; // an older request answered late
      reloadLatestRef.current = mine;
      setSt(r); setErr(null);
      // a run still going on this batch (started anywhere): follow it
      if (r && r.job && !(live && live.total) && (r.job.status === 'checking' || r.job.status === 'running')) setFollowId(function(cur) { return cur || r.job.id; });
    }).catch(function(e) { if (mine >= reloadLatestRef.current) setErr(e.message); });
  };
  // Progress comes from the queue's run (live) or the dialog's own retry (run)
  var jobLive = !!(job && (job.status === 'checking' || job.status === 'running'));
  var prog = (live && live.total) ? live : jobLive ? { done: job.done, total: job.total, phase: job.status === 'checking' ? 'checking' : undefined } : run;
  var running = !!prog;
  // Re-read after every chunk of EITHER run and once more when the run ends (prog goes null) —
  // the dialog's own Create labels used to leave the ring at the pre-run share until the end.
  useEffect(function() { reload(); }, [batchId, prog ? prog.done : -1, refreshKey || 0]);
  // While a run is going the ring follows the batch record every second (counts only — a cheap
  // endpoint; 60 requests a minute against the office's shared 200/min budget, Anthony 9-17)
  useEffect(function() {
    if (!running) return;
    var seq = 0, latest = 0;
    var t = setInterval(function() {
      var mine = ++seq;
      api('/shipping/batches/' + batchId + '/progress').then(function(r) {
        if (mine < latest || !r || !r.counts) return; // a slow older answer never moves the ring backwards
        latest = mine;
        setSt(function(s) { return s ? Object.assign({}, s, { counts: r.counts }) : s; });
      }).catch(function() {});
    }, 1000);
    return function() { clearInterval(t); };
  }, [running, batchId]);
  // Follow the job row until the run ends (its own retry, or a reattached run). The queue page
  // follows the job itself while it passes `live`; the dialog only polls when it is on its own.
  useEffect(function() {
    if (!followId || (live && live.total)) return;
    var stop = false;
    var fails = 0;
    var tick = function() {
      api('/shipping/label-jobs/' + followId).then(function(r) {
        if (stop) return;
        fails = 0;
        var j = r && r.job; if (!j) { setFollowId(null); return; }
        setJob(j);
        if (j.status === 'checking' || j.status === 'running') { setTimeout(tick, 1500); return; }
        setRun(null); setFollowId(null);
        if (j.halted) { setRunHalt(j.halted); setRunNeedsAuth(!!j.needs_usps_authorization); }
        reload().then(function() { if (onChanged) onChanged(); });
      }).catch(function(e) {
        if (stop) return;
        fails++;
        if (shipPollGiveUp(e, fails)) { setRun(null); setJob(null); setFollowId(null); setErr('Stopped following the run: ' + String((e && e.message) || e) + ' — it continues on the server'); return; }
        setTimeout(tick, 3000);
      });
    };
    tick();
    return function() { stop = true; };
  }, [followId]);
  // The "printed on" line flips the moment a print starts (optimistic; the status re-read
  // reconciles). The first print date is the one that sticks — a reprint never moves it.
  var stamp = function(kind) {
    if (kind === 'labels' || kind === 'slips') {
      setSt(function(s) {
        var k = kind + '_printed_at';
        if (!s || !s.batch || s.batch[k]) return s;
        var patch = {}; patch[k] = new Date().toISOString();
        return Object.assign({}, s, { batch: Object.assign({}, s.batch, patch) });
      });
    }
    return api('/shipping/batches/' + batchId + '/printed', { method: 'POST', body: JSON.stringify({ kind: kind }) }).catch(function() {}).then(reload);
  };
  var b = st && st.batch, c = st && st.counts;
  // how the batch's last run ended, while orders are still left to label (a halt with its
  // authorization question survives a page change — the job row remembers it)
  var lastJobHalt = (st && st.job && st.job.status === 'halted' && (st.unlabelled_order_ids || []).length) ? st.job : null;
  var haltMsg = runHalt || halt || (lastJobHalt ? lastJobHalt.halted : null) || null;
  // a calendar date ('YYYY-MM-DD', the ship date) formats as itself — as an instant it read as the previous evening (9-22)
  var fmtD = function(v) { if (!v) return '—'; return /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? fmtDateOnly(v) : new Date(v).toLocaleDateString(); };
  var fmtDT = function(v) { return v ? new Date(v).toLocaleString() : '—'; };
  var printLabels = async function() {
    if (!st || !st.shipment_ids.length) return;
    if (b && b.labels_printed_at && !(await appConfirm('Labels for this batch were printed on ' + fmtDT(b.labels_printed_at) + '. Print again?'))) return;
    shipPrintApiPdf('/shipping/label-files?ids=' + st.shipment_ids.join(',') + '&batch=' + batchId); // leads with the batch summary sheet
    stamp('labels');
  };
  // Documents follow the labels: an order that failed at label creation is left out of the
  // packing slips and pick list until a later run labels it (then it is back, in batch order).
  var docIds = function() {
    var bad = {}; (st.failures || []).forEach(function(f) { bad[f.id] = true; });
    return st.order_ids.filter(function(id) { return !bad[id]; });
  };
  // Slips and pick list print all or nothing (Anthony 2026-10-29): shipResolveOrderDetails rejects
  // when any order fails to load, the print window closes, the toast names the orders, and the
  // "printed on" stamp is NOT set — the user clicks again. Nothing prints short any more (error_log 891).
  var printSlips = function() {
    if (!st || !docIds().length) return;
    setBusy('slips');
    // the stamp lands once every order has loaded, still ahead of the browser's print dialog (which would otherwise hold it back)
    shipPrintPackingSlips(docIds().map(function(id) { return { id: id }; }))
      .then(function(ok) { if (ok) stamp('slips'); })
      .finally(function() { setBusy(''); });
  };
  var printPick = function() {
    if (!st || !docIds().length) return;
    setBusy('pick');
    var ids = docIds(); // every order of the batch — no print cap (Anthony 2026-09-28)
    var win = shipOpenPrintWindow('Pick List'); // inside the click, before the fetches (9-27 M17)
    shipResolveOrderDetails(ids.map(function(id) { return { id: id }; })).then(function(rows) {
      shipPrintPickList(rows, { win: win }); stamp('pick');
    }).catch(function(e) { shipClosePrintWindow(win); shipShellToast(String((e && e.message) || e), 'error'); }).finally(function() { setBusy(''); });
  };
  // Remove from Batch (Anthony 9-22): the errored order goes back to the orders queue; its label
  // error stays on the order (the panel still shows it) and the batch activity records the removal
  var removeFromBatch = function(f) {
    if (!st || busy || running) return;
    setBusy('remove:' + f.id);
    api('/shipping/batches/' + batchId + '/remove-orders', { method: 'POST', body: JSON.stringify({ order_ids: [f.id], reason: 'removed from the batch dialog' }) })
      .then(function() { return reload(); }).then(function() { if (onChanged) onChanged(); })
      .catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setBusy(''); });
  };
  var archive = async function() {
    if (!(await appConfirm('Archive Batch ' + b.batch_number + '? Orders keep their batch number; the batch leaves the Open and Processed lists.', { danger: true, okLabel: 'Archive' }))) return;
    setBusy('archive');
    api('/shipping/batches/' + batchId + '/archive', { method: 'POST' }).then(function() { showToast('Batch archived', 'success'); if (onChanged) onChanged(); return reload(); })
      .catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setBusy(''); });
  };
  // Note and assignee edits patch the field in place and refresh only the Activity tab;
  // re-reading the whole status made the dialog flash (Anthony 9-17).
  var patchBatch = function(patch) { setSt(function(s) { return s ? Object.assign({}, s, { batch: Object.assign({}, s.batch, patch) }) : s; }); };
  var saveNote = function() {
    var v = noteEdit; setNoteEdit(null);
    if (String(v || '').trim().slice(0, 200) === String((b && b.note) || '')) return; // nothing changed: no request, no activity row (Anthony 9-22)
    patchBatch({ note: v });
    api('/shipping/batches/' + batchId, { method: 'PUT', body: JSON.stringify({ note: v }) }).then(reloadActivity).then(function() { if (onChanged) onChanged(); }).catch(function(e) { showToast(e.message, 'error'); reload(); });
  };
  var setAssignee = function(uid) {
    patchBatch({ assignee_user_id: uid || null });
    api('/shipping/batches/' + batchId, { method: 'PUT', body: JSON.stringify({ assignee_user_id: uid || null }) }).then(reloadActivity).then(function() { if (onChanged) onChanged(); }).catch(function(e) { showToast(e.message, 'error'); reload(); });
  };
  // Gauge: a half ring, filled by the share of orders with a label
  var total = c ? c.orders : 0, made = c ? c.labels_created : 0, failed = c ? c.labels_failed : 0;
  // During a run the "N of M" text and the ring share one basis — the batch record, re-read every
  // second (attempted = created + failed); until the first read the client's own count stands in.
  var liveDone = running ? (c ? Math.min(total, made + failed) : prog.done) : made;
  var liveTotal = running ? (c && total ? total : prog.total) : total;
  // Retry: buy labels for every order of the batch that still has none (failed or never run).
  // Only while the batch is not printed; a Printed batch is final (unlabelled orders left it).
  var unlabelled = st ? (st.unlabelled_order_ids || []) : [];
  var canRetry = !!(b && (b.status === 'open' || b.status === 'not_printed') && unlabelled.length);
  var retry = async function(authorize) {
    if (running || !canRetry) return;
    var ids = unlabelled.slice(0, 300);
    if (authorize !== true && !(await appConfirm('Buy ' + ids.length + ' shipping label' + (ids.length === 1 ? '' : 's') + ' for the orders in this batch that have none?\n\nPostage is charged immediately and each order is marked shipped. Orders with problems are skipped and reported here.', { okLabel: 'Buy labels' }))) return;
    setRun({ done: 0, total: ids.length });
    setRunHalt(null);
    setRunNeedsAuth(false);
    setJob(null);
    // The run is a server job (2026-09-23): the first order is bought alone (it carries a USPS
    // authorization), then four at a time; leaving the page no longer stops it. skip_check: the
    // queue's live Zentail check already ran when this batch was made.
    api('/shipping/label-jobs', { method: 'POST', body: JSON.stringify({ order_ids: ids, batch_id: batchId, authorize_usps: authorize === true, skip_check: true }) })
      .then(function(r) { setFollowId(r.job.id); })
      .catch(function(e) { setRun(null); showToast(e.message, 'error'); reload(); });
  };
  // Two stacked segments on the grey track (Anthony 9-17): green = labels created, yellow =
  // orders whose label failed; whatever is left grey is still to run. Counts are the batch
  // record, re-read after every chunk of a live run, so the ring fills as labels land.
  var okShare = total ? Math.min(1, made / total) : 0;
  var failShare = total ? Math.min(1 - okShare, failed / total) : 0;
  if (total && made + failed >= total) failShare = 1 - okShare; // every order accounted for: no grey left
  // pathLength normalises the arc to 100 units so the dashes are exact percentages (the
  // browser's measured arc length is not quite PI*R, which left a grey sliver), and every
  // stroke uses square ends so the track never peeks past the last segment.
  var R = 92, SW = 16, CX = 110, CY = 108;
  var ARC = 'M ' + (CX - R) + ' ' + CY + ' A ' + R + ' ' + R + ' 0 0 1 ' + (CX + R) + ' ' + CY;
  var seg = function(start, frac, color) {
    return frac > 0 ? h('path', { d: ARC, pathLength: 100, fill: 'none', stroke: color, strokeWidth: SW, strokeLinecap: 'butt',
      strokeDasharray: (frac * 100) + ' 100', strokeDashoffset: -(start * 100), style: { transition: 'stroke-dasharray 0.4s, stroke-dashoffset 0.4s' } }) : null;
  };
  var gauge = h('svg', { width: 220, height: 120, viewBox: '0 0 220 120' },
    h('path', { d: ARC, pathLength: 100, fill: 'none', stroke: C.border, strokeWidth: SW, strokeLinecap: 'butt' }),
    seg(0, okShare, C.success),
    seg(okShare, failShare, C.warning),
    h('text', { x: CX, y: CY - 22, textAnchor: 'middle', fontSize: 20, fontWeight: 600, fill: C.text }, (running ? '' : (failed ? '' : '✓ ')) + liveDone + ' of ' + liveTotal),
    h('text', { x: CX, y: CY + 2, textAnchor: 'middle', fontSize: 12.5, fill: C.textMuted }, running ? (prog.phase === 'checking' ? 'Checking for Changes' : 'Creating labels…') : 'Labels Created'));
  var row = function(label, val) { return h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 9 } }, label + ' ', h('span', { style: { color: C.text } }, val)); };
  var btn = function(label, opts) { return h(Btn, Object.assign({ size: 'sm', disabled: !!busy || running }, opts), label); };
  return h('div', { style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }, onMouseDown: function(e) { if (e.target === e.currentTarget) onClose(); } }, // closable mid-run: the runs are server jobs and continue (review fix 5)
    h('div', { ref: boxRef, style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: 720, maxWidth: '94vw', maxHeight: '86vh', height: tab !== 'status' && lockH ? lockH : undefined, overflowY: 'auto', boxSizing: 'border-box' } },
      h('div', { style: { display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid ' + C.border } },
        // the batch number is the title (Anthony 9-22: no status in it)
        h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, flex: 1 } }, 'Batch: ' + (b ? b.batch_number : '')),
        b && !onOpenOrder && h('a', { href: '/batches/' + b.batch_number, target: '_blank', rel: 'noopener', title: 'Open the batch queue (the orders still awaiting a label)', style: { fontSize: 12, color: C.accent, textDecoration: 'none', marginRight: 14 } }, 'View Orders'),
        h('button', { onClick: onClose, title: running ? 'Close (the run continues on the server; reopen the batch to follow it)' : 'Close', style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),
      err && h('div', { style: { padding: 18, color: C.danger, fontSize: 12.5 } }, err),
      !st && !err && h('div', { style: { padding: 24, color: C.textMuted, fontSize: 12.5 } }, 'Loading…'),
      st && h('div', { style: { display: 'flex', gap: 2, padding: '4px 18px 0', borderBottom: '1px solid ' + C.border } },
        tabBtn('status', 'Status'), tabBtn('activity', 'Activity' + (act && act.length ? ' (' + act.length + ')' : ''))),
      st && tab === 'status' && h('div', { style: { padding: 18 } },
        h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 } },
          canRetry && btn('Create labels (' + unlabelled.length + ')', { variant: 'primary', onClick: retry, disabled: !!busy || running }),
          btn(st.shipment_ids.length ? 'Print Labels (' + st.shipment_ids.length + ')' : 'Print Labels', { variant: 'primary', onClick: printLabels, disabled: !!busy || running || !st.shipment_ids.length }),
          // greyed out with no orders to print, e.g. a dissolved batch, like Print Labels (Anthony 9-22)
          btn(busy === 'slips' ? 'Preparing…' : 'Print Packing Slips', { variant: 'primary', onClick: printSlips, disabled: !!busy || running || b.status === 'dissolved' || !docIds().length }),
          btn(busy === 'pick' ? 'Preparing…' : 'Print Pick List', { variant: 'primary', onClick: printPick, disabled: !!busy || running || b.status === 'dissolved' || !docIds().length }), // its own button (Anthony 9-22); the old dropdown held nothing else
          // only a batch with its labels done, and not mid-run (the server refuses the rest, 9-27 M12)
          (b.status === 'not_printed' || b.status === 'processed') && !running && btn(busy === 'archive' ? 'Archiving…' : 'Archive Batch', { onClick: archive })),
        h('div', { style: { display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' } },
          h('div', { style: { width: 220, flexShrink: 0 } }, gauge,
            failed > 0 && !running && h('div', { style: { fontSize: 12, color: C.warning, textAlign: 'center', marginTop: 2 } }, failed + ' failed'),
            c && c.pending > 0 && !running && h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', marginTop: 2 } }, c.pending + ' not yet labelled'),
            haltMsg && h('div', { style: { fontSize: 12, color: C.danger, textAlign: 'center', marginTop: 6, lineHeight: 1.4 } }, 'Run stopped: ' + haltMsg,
              ((runNeedsAuth || needsAuth || !!(lastJobHalt && lastJobHalt.needs_usps_authorization)) && canRetry) ? h('div', { style: { marginTop: 8 } },
                h(Btn, { size: 'sm', variant: 'primary', disabled: !!running, onClick: function() { retry(true); } }, 'Authorize USPS payment again (new card hold)')) : null)),
          h('div', { style: { flex: 1, minWidth: 240, paddingTop: 10 } },
            row('Processed By', b.processed_by_name || '—'),
            row('Processed Date', fmtD(b.processed_at)),
            row('Ship Date', fmtD(b.ship_date)),
            row('Ship From', st.ship_from || '—'),
            // the Zentail write-back state stays backend information (Anthony 9-22: no "write-backs pending" row here)
            h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 9, display: 'flex', alignItems: 'center', gap: 8 } }, 'Assigned To ',
              h('select', { value: b.assignee_user_id || '', onChange: function(e) { setAssignee(e.target.value ? parseInt(e.target.value, 10) : null); },
                style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '3px 8px', fontSize: 12, fontFamily: 'inherit' } },
                h('option', { value: '' }, '—'), (st.staff || []).map(function(u) { return h('option', { key: u.id, value: u.id }, u.name); }))),
            h('div', { style: { fontSize: 12.5, color: C.text, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 } },
              h(LIcon, { name: b.labels_printed_at ? 'check-square' : 'square', size: 14, color: b.labels_printed_at ? C.success : C.textDim }),
              b.labels_printed_at ? 'Labels printed on ' + fmtDT(b.labels_printed_at) : 'Labels not printed yet'),
            h('div', { style: { fontSize: 12.5, color: C.text, display: 'flex', alignItems: 'center', gap: 6 } },
              h(LIcon, { name: b.slips_printed_at ? 'check-square' : 'square', size: 14, color: b.slips_printed_at ? C.success : C.textDim }),
              b.slips_printed_at ? 'Packing slips printed on ' + fmtDT(b.slips_printed_at) : 'Packing slips not printed yet'))),
        b.unlabelled_removed > 0 && !st.failures.length && h('div', { style: { marginTop: 14, padding: '8px 10px', border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12, color: C.textMuted } },
          h('span', { style: { fontWeight: 600, color: C.text } }, b.unlabelled_removed + ' order' + (b.unlabelled_removed === 1 ? '' : 's')), ' without a label left this batch when the labels were printed and went back to the orders queue.'),
        st.failures.length > 0 && h('div', { style: { marginTop: 14, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
          // a dissolved batch keeps its label errors (snapshot, Anthony 9-22) — read-only: the orders left the batch
          h('div', { style: { padding: '6px 10px', fontSize: 11, fontWeight: 600, color: C.warning, textTransform: 'uppercase', letterSpacing: 0.4, background: C.warningSoft || C.bg } },
            st.failures.length + ' order' + (st.failures.length === 1 ? '' : 's') + (b.status === 'dissolved' ? ' without a label when the batch was dissolved' : ' without a label · fix the data, then Create labels')),
          st.failures.map(function(f, i) {
            return h('div', { key: f.id, style: { padding: '7px 10px', fontSize: 11.5, lineHeight: 1.5, borderTop: i === 0 ? 'none' : '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
              h('span', { style: { flex: 1, minWidth: 0 } },
                b.status === 'dissolved'
                  ? h('span', { style: { fontWeight: 600, color: C.text } }, f.order_number)
                  : h('a', { href: '/batches/' + b.batch_number + '?bs=1&o=' + f.id, title: f.fixed ? 'Fixed; run Create labels' : 'Open the order to fix it',
                  onClick: function(e) { if (onOpenOrder) { e.preventDefault(); onOpenOrder(f.id, st.failures.map(function(x) { return x.id; })); } },
                  style: { fontWeight: 600, color: f.fixed ? C.success : C.accent, textDecoration: 'none' } }, f.order_number),
                f.fixed ? h('span', { style: { color: C.success } }, ' — Fixed, waiting for Create labels') : h('span', { style: { color: C.warning } }, ' — ' + f.error)),
              b.status !== 'dissolved' && h(Btn, { size: 'sm', disabled: !!busy || running, title: 'Take this order out of the batch; it returns to the orders queue', onClick: function() { removeFromBatch(f); } },
                busy === 'remove:' + f.id ? 'Removing…' : 'Remove from Batch'));
          })),
        h('div', { style: { marginTop: 16, paddingTop: 12, borderTop: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
          h('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Batch Notes:'),
          noteEdit === null
            ? h('span', { onClick: function() { setNoteEdit(b.note || ''); }, title: 'Click to edit', style: { fontSize: 12.5, color: b.note ? C.text : C.textDim, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' } }, b.note || 'Click to add a note')
            : h('span', { style: { display: 'flex', gap: 6, alignItems: 'center', flex: 1 } },
                h('input', { autoFocus: true, value: noteEdit, onChange: function(e) { setNoteEdit(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') saveNote(); if (e.key === 'Escape') setNoteEdit(null); },
                  style: { flex: 1, background: C.bg, color: C.text, border: '1px solid ' + C.accent, borderRadius: 6, padding: '4px 10px', fontSize: 12.5, fontFamily: 'inherit' } }),
                h(Btn, { size: 'sm', variant: 'primary', onClick: saveNote }, 'Save'),
                h(Btn, { size: 'sm', onClick: function() { setNoteEdit(null); } }, 'Cancel')))),
      st && tab === 'activity' && h('div', { style: { padding: 18 } },
        act === null ? h('div', { style: { fontSize: 12, color: C.textDim } }, 'Loading…')
          : !act.length ? h('div', { style: { fontSize: 12, color: C.textDim } }, 'Nothing recorded yet.')
          : h('div', { style: { maxHeight: 420, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 8 } },
              // Fixed columns (date · time · event · user) so every row lines up (Anthony 9-17: "misaligned"); headers on top
              h('div', { style: { display: 'grid', gridTemplateColumns: '78px 82px 1fr 90px', gap: 8, padding: '6px 10px', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, background: C.bg, borderBottom: '1px solid ' + C.border, position: 'sticky', top: 0 } },
                h('span', null, 'Date'), h('span', null, 'Time'), h('span', null, 'Event'), h('span', null, 'User')),
              act.map(function(a, i) {
                var dt = new Date(a.at || a.created_at);
                return h('div', { key: a.id, style: { display: 'grid', gridTemplateColumns: '78px 82px 1fr 90px', gap: 8, alignItems: 'baseline', padding: '6px 10px', fontSize: 11.5, lineHeight: 1.45, borderTop: '1px solid ' + C.border } },
                  // one colour across the row — date, time, event and user (Anthony 9-22)
                  h('span', { style: { color: C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } }, dt.toLocaleDateString()),
                  h('span', { style: { color: C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } }, dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })),
                  h('span', { style: { color: C.text, minWidth: 0 } }, shipBatchActivityText(a)),
                  h('span', { style: { color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, a.user_name || 'system'));
              }))),
      h('div', { style: { padding: '12px 18px', borderTop: '1px solid ' + C.border, background: C.bg, fontSize: 12.5, color: C.text, borderRadius: '0 0 12px 12px' } },
        running ? (prog.phase === 'checking' ? 'Checking Zentail for orders that changed since the last sync, then creating ' + prog.total + ' labels. You can leave this page; the run continues on the server.' : 'Creating labels: ' + prog.done + ' of ' + prog.total + '. You can leave this page; the run continues on the server.')
          : (function() {
              var parts = [];
              if (c && failed) parts.push(failed + ' order' + (failed === 1 ? '' : 's') + ' did not get a label. Fix the reason above, then click Create labels here; the error clears once the label goes through.');
              else if (c && c.pending) parts.push(c.pending + ' order' + (c.pending === 1 ? '' : 's') + ' still without a label.');
              // an Open batch with labels prints the same way (2026-10-15: the warehouse prints a 59-of-60 batch as is; the print finishes it)
              if (b && (b.status === 'not_printed' || (b.status === 'open' && made > 0))) parts.push((b.status === 'not_printed' ? 'Labels are created but not printed yet. ' : '') + 'Print Labels moves this batch to Printed; any order still without a label then leaves the batch and returns to the orders queue.');
              if (b && b.unlabelled_removed > 0) parts.push(b.unlabelled_removed + ' order' + (b.unlabelled_removed === 1 ? '' : 's') + ' without a label returned to the orders queue when the labels were printed. Handle them from the Orders page.');
              return parts.length ? parts.join(' ') : 'This batch has completed successfully. You can close this window at any time.';
            })(),
        h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 3 } }, 'You can always find this and other batches on the Batch History page.'))));
}

// ─── Paste-address parser (New Order modal, Anthony 2026-09-19) ─────────────
// Turns a pasted block (email signature, marketplace order page, "Name / Street / City, ST ZIP")
// into recipient fields. Phone and email lines are recognised anywhere; the city line is a US
// "City, ST 12345[-6789]" (state code or full name), a Canadian "City ON A1A 1A1", or a "ST 12345"
// part following the city when the whole address came on one comma-separated line; a trailing
// country line (name, "USA", or ISO code) sets the country. The first remaining line is the name,
// the rest become address lines in order. Only the fields it could place are returned; never throws.
function shipParsePastedAddress(text) {
  var out = {};
  try {
    var raw = String(text || '').split(/\r?\n|;/).map(function(s) { return s.replace(/\s+/g, ' ').trim(); }).filter(Boolean);
    if (raw.length === 1 && raw[0].indexOf(',') !== -1) raw = raw[0].split(',').map(function(s) { return s.trim(); }).filter(Boolean);
    if (!raw.length) return out;
    var rest = [];
    var LABEL = /^(phone|tel|telephone|mobile|cell|ph|e-?mail|contact)\s*[:.]?\s*/i;
    raw.forEach(function(ln) {
      var em = ln.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
      if (em && !out.email) {
        out.email = em[0].replace(/[.,]+$/, '');
        var left = ln.replace(em[0], '').replace(LABEL, '').replace(/[<>()]/g, '').trim();
        if (left) rest.push(left);
        return;
      }
      var body = ln.replace(LABEL, '');
      var digits = body.replace(/\D/g, '');
      if (!out.phone && digits.length >= 10 && digits.length <= 15 && /^\+?[\d\s().-]{9,}(\s*(x|ext\.?)\s*\d+)?$/i.test(body)) { out.phone = body.trim(); return; }
      rest.push(ln);
    });
    var states = (typeof stateListFor === 'function' && stateListFor('US')) || [];
    var stateCode = function(s) {
      var u = String(s || '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(u)) return u;
      var hit = states.filter(function(x) { return String(x[1] || '').toUpperCase() === u; })[0];
      return hit ? hit[0] : null;
    };
    var countryFor = function(s) {
      var v = String(s || '').trim();
      if (/^(usa|u\.s\.a\.?|u\.s\.|us|united states( of america)?)$/i.test(v)) return 'US';
      var list = (typeof ISO_COUNTRIES !== 'undefined' && ISO_COUNTRIES) || [];
      if (/^[A-Za-z]{2}$/.test(v) && list.indexOf(v.toUpperCase()) !== -1) return v.toUpperCase();
      if (typeof countryName === 'function') {
        for (var i = 0; i < list.length; i++) if (String(countryName(list[i])).toLowerCase() === v.toLowerCase()) return list[i];
      }
      return null;
    };
    // trailing country line
    if (rest.length >= 2) { var cc = countryFor(rest[rest.length - 1]); if (cc) { out.country = cc; rest.pop(); } }
    // city / state / zip — scan from the end
    var US_FULL = /^(.+?)[,\s]+([A-Za-z]{2}|[A-Za-z][A-Za-z .]{2,})[,\s]+(\d{5}(?:-\d{4})?)$/;
    var US_TAIL = /^([A-Za-z]{2}|[A-Za-z][A-Za-z .]{2,})\s+(\d{5}(?:-\d{4})?)$/;
    var CA_FULL = /^(.+?)[,\s]+([A-Za-z]{2})[,\s]+([A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/;
    var CA_TAIL = /^([A-Za-z]{2})\s+([A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/;
    for (var i = rest.length - 1; i >= 1 && out.city == null; i--) {
      var m = rest[i].match(US_FULL), sc;
      if (m && (sc = stateCode(m[2]))) { out.city = m[1].replace(/,$/, '').trim(); out.state = sc; out.zip = m[3]; if (!out.country) out.country = 'US'; rest.splice(i, 1); break; }
      m = rest[i].match(CA_FULL);
      if (m) { out.city = m[1].replace(/,$/, '').trim(); out.state = m[2].toUpperCase(); out.zip = m[3].toUpperCase(); if (!out.country) out.country = 'CA'; rest.splice(i, 1); break; }
      m = rest[i].match(US_TAIL);
      if (m && i >= 2 && (sc = stateCode(m[1]))) { out.city = rest[i - 1]; out.state = sc; out.zip = m[2]; if (!out.country) out.country = 'US'; rest.splice(i - 1, 2); break; }
      m = rest[i].match(CA_TAIL);
      if (m && i >= 2) { out.city = rest[i - 1]; out.state = m[1].toUpperCase(); out.zip = m[2].toUpperCase(); if (!out.country) out.country = 'CA'; rest.splice(i - 1, 2); break; }
    }
    if (rest.length) out.name = rest.shift();
    if (rest.length) out.address1 = rest.shift();
    if (rest.length) out.address2 = rest.shift();
    if (rest.length) out.address3 = rest.join(', ');
  } catch (e) { /* a paste that cannot be parsed just leaves the fields alone */ }
  return out;
}

// ─── New Order modal (manual orders) ─────────────────────────────────────────
// Polished 2026-09-19 (Anthony): app-standard type sizes, three sections (Ship to / Items /
// Order details), shared State + Country dropdowns, an items table with headers and no
// placeholder text, inline required-field errors, Paste address, Create & open, Ctrl+Enter.
function ShipManualOrderModal({ open, onClose, onSaved, showToast, edit }) {
  var blankLine = function() { return { sku: '', title: '', qty: 1, weight_lb: '' }; };
  var [type, setType] = useState('Wholesale');
  var [rec, setRec] = useState({ country: 'US' });
  var [lines, setLines] = useState([blankLine()]);
  var [orderTotal, setOrderTotal] = useState('');
  var [shipBy, setShipBy] = useState('');
  var [note, setNote] = useState('');
  var [saving, setSaving] = useState(false);
  var [csvMode, setCsvMode] = useState(false);
  var [csvResult, setCsvResult] = useState(null); // { orders } | { error }
  var [sugg, setSugg] = useState(null); // { idx, list }
  var [errors, setErrors] = useState({}); // field key → true, line errors as 'line:<i>'
  var [errMsg, setErrMsg] = useState('');
  var [pasteOpen, setPasteOpen] = useState(false);
  var [pasteText, setPasteText] = useState('');
  var [pasteNote, setPasteNote] = useState('');
  var suggTimer = useRef(null);
  var fileRef = useRef(null);
  var fieldRefs = useRef({});
  var skuRefs = useRef([]);
  var latest = useRef({});

  useEffect(function() {
    if (!open) return;
    setCsvMode(false); setCsvResult(null); setSugg(null); setSaving(false);
    setErrors({}); setErrMsg(''); setPasteOpen(false); setPasteText(''); setPasteNote('');
    if (edit) {
      setType(SHIP_MANUAL_TYPES.indexOf(edit.channel) !== -1 ? edit.channel : 'Other');
      var r = edit.recipient || {};
      setRec({ name: r.name || '', address1: r.address1 || '', address2: r.address2 || '', address3: r.address3 || '', city: r.city || '', state: r.state || '', zip: r.zip || '', country: r.country || 'US', phone: r.phone || '', email: r.email || '' });
      // the line's price rides along unseen (the customs value reads it; an edit used to drop it —
      // sweep 2026-09-27 M11) while the SKU stays the one it was priced for (_psku)
      // _rsku: a saved line's SKU counts as resolved — leaving the field must not re-fill its description
      setLines((edit.lines || []).map(function(l) { return { sku: l.sku || '', title: l.title || '', qty: l.qty || 1, weight_lb: l.unit_weight_lb || '', unit_price: l.unit_price != null ? l.unit_price : null, _psku: l.sku || '', _rsku: l.sku || '' }; }));
      setOrderTotal(edit.value != null ? String(edit.value) : '');
      setShipBy(edit.ship_by ? String(edit.ship_by).slice(0, 10) : '');
      setNote(edit.buyer_notes || '');
    } else {
      setType('Wholesale'); setRec({ country: 'US' }); setLines([blankLine()]);
      setOrderTotal(''); setShipBy(''); setNote('');
    }
    setTimeout(function() { var el = fieldRefs.current.name; if (el && el.focus) el.focus(); }, 30);
  }, [open]);

  var clearErr = function(key) { setErrors(function(p) { if (!p[key]) return p; var n = Object.assign({}, p); delete n[key]; return n; }); };
  var setRecField = function(k, v) { setRec(function(p) { var n = Object.assign({}, p); n[k] = v; return n; }); clearErr(k); };
  var setLine = function(i, k, v) { setLines(function(p) { var n = p.slice(); n[i] = Object.assign({}, n[i]); n[i][k] = v; return n; }); clearErr('line:' + i); };
  var addLine = function() {
    var at = lines.length;
    setLines(lines.concat([blankLine()]));
    setTimeout(function() { var el = skuRefs.current[at]; if (el && el.focus) el.focus(); }, 0);
  };
  var removeLine = function(i) { setLines(lines.length > 1 ? lines.filter(function(_, j) { return j !== i; }) : [blankLine()]); setErrors({}); };

  var fetchSugg = function(i, q) {
    if (suggTimer.current) clearTimeout(suggTimer.current);
    if (!q || q.length < 2) { setSugg(null); return; }
    suggTimer.current = setTimeout(function() {
      api('/shipping/products-list?q=' + encodeURIComponent(q)).then(function(r) {
        // the user has left the field meanwhile (Tab right after typing, 10-09): no dropdown under a blurred SKU
        if (document.activeElement !== skuRefs.current[i]) return;
        var list = (r && (r.products || r.rows)) || (Array.isArray(r) ? r : []);
        setSugg({ idx: i, list: list.slice(0, 6) });
      }).catch(function() { setSugg(null); });
    }, 250);
  };
  // Catalog product → line, from a dropdown pick OR a typed / pasted SKU resolved when the field is
  // left. The description takes the catalog title (Anthony 10-09: "adding a SKU should populate the
  // description with the item's title") when it is empty or still the last auto-fill — a description
  // typed by hand stays; same rule for the weight. The list endpoint names the title `title`
  // (10-product-settings); the old `vendor_title` read never matched, so nothing ever filled.
  var linesRef = useRef(lines); linesRef.current = lines;
  var applyProduct = function(i, p) {
    var ttl = p.title || p.vendor_title || '';
    var wt = parseFloat(p.package_weight) > 0 ? parseFloat(p.package_weight) : '';
    setLines(function(prev) {
      var n = prev.slice();
      var cur = n[i] || blankLine();
      var title = ttl && (!cur.title || cur.title === cur._autoTitle) ? ttl : cur.title;
      var weight = wt !== '' && (cur.weight_lb === '' || cur.weight_lb == null || String(cur.weight_lb) === String(cur._autoWeight)) ? wt : cur.weight_lb;
      n[i] = Object.assign({}, cur, { sku: p.sku, title: title, weight_lb: weight, _rsku: p.sku,
        _autoTitle: ttl || cur._autoTitle, _autoWeight: wt !== '' ? wt : cur._autoWeight });
      return n;
    });
    setSugg(null);
  };
  var pickSugg = applyProduct;
  // Blur / Enter / Tab on the SKU without a pick: an exact catalog match fills the line like a pick
  var resolveSku = function(i, raw) {
    var sku = String(raw || '').trim();
    var cur = linesRef.current[i];
    if (!sku || (cur && String(cur._rsku || '').toLowerCase() === sku.toLowerCase())) return;
    api('/shipping/products-list?q=' + encodeURIComponent(sku) + '&limit=50').then(function(r) {
      var list = (r && (r.products || r.rows)) || (Array.isArray(r) ? r : []);
      var hit = list.find(function(p) { return String(p.sku || '').toLowerCase() === sku.toLowerCase(); });
      var now = linesRef.current[i]; // the line may have moved on while the lookup ran
      if (!hit || !now || String(now.sku || '').trim().toLowerCase() !== sku.toLowerCase()) return;
      applyProduct(i, hit);
    }).catch(function() {});
  };
  var exactSugg = function(i, q) {
    var s = String(q || '').trim().toLowerCase();
    return sugg && sugg.idx === i && sugg.list.length
      ? (sugg.list.find(function(p) { return String(p.sku || '').toLowerCase() === s; }) || sugg.list[0]) : null;
  };

  var isUS = (rec.country || 'US') === 'US';
  var realLines = lines.filter(function(l) { return (l.sku || l.title); });
  var totalUnits = realLines.reduce(function(s, l) { return s + (parseInt(l.qty, 10) || 1); }, 0);
  var totalWeight = lines.reduce(function(s, l) { return s + (parseFloat(l.weight_lb) || 0) * (parseInt(l.qty, 10) || 1); }, 0);

  // Same rules as the server (validateManualOrder) so the user sees the gap before the round trip.
  var validate = function() {
    var e = {}, names = [];
    var need = function(key, label, ok) { if (!ok) { e[key] = true; names.push(label); } };
    need('name', 'Recipient name', !!(rec.name || '').trim());
    need('address1', 'Address 1', !!(rec.address1 || '').trim());
    need('city', 'City', !!(rec.city || '').trim());
    if (isUS) { need('state', 'State', !!(rec.state || '').trim()); need('zip', 'ZIP', !!(rec.zip || '').trim()); }
    if (!realLines.length) { e['line:0'] = true; names.push('at least one item (SKU or description)'); }
    lines.forEach(function(l, i) { if ((l.sku || l.title) && !(parseInt(l.qty, 10) >= 1)) { e['line:' + i] = true; names.push('Qty on item ' + (i + 1)); } });
    setErrors(e);
    if (names.length) {
      setErrMsg('Fill in: ' + names.join(', '));
      var first = Object.keys(e)[0];
      var el = first.indexOf('line:') === 0 ? skuRefs.current[parseInt(first.slice(5), 10)] : fieldRefs.current[first];
      if (el && el.focus) el.focus();
      return false;
    }
    setErrMsg('');
    return true;
  };

  var save = function(openAfter) {
    if (saving || !validate()) return;
    var body = {
      order_type: type, recipient: rec,
      lines: realLines.map(function(l) {
        return { sku: l.sku || null, title: l.title || null, qty: parseInt(l.qty, 10) || 1, weight_lb: parseFloat(l.weight_lb) > 0 ? parseFloat(l.weight_lb) : null,
          unit_price: l.unit_price != null && l.unit_price !== '' && (l.sku || '') === (l._psku || '') ? l.unit_price : null };
      }),
      order_total: orderTotal !== '' ? parseFloat(orderTotal) : null,
      ship_by: shipBy || null, note: note || null,
    };
    setSaving(true);
    var req = edit
      ? api('/shipping/manual-orders/' + (-edit.id), { method: 'PUT', body: JSON.stringify(body) })
      : api('/shipping/manual-orders', { method: 'POST', body: JSON.stringify(body) });
    req.then(function(r) {
      showToast(edit ? 'Order updated' : ('Created ' + (r.order_number || 'order')), 'success');
      onClose(); if (onSaved) onSaved(openAfter && !edit && r.order_pk ? r.order_pk : null);
      try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
    }).catch(function(e) { setErrMsg(e.message); showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  var applyPaste = function(txt) {
    var p = shipParsePastedAddress(txt != null ? txt : pasteText);
    var keys = Object.keys(p);
    if (!keys.length) { setPasteNote('Nothing recognisable in that text'); return; }
    setRec(function(prev) { var n = Object.assign({}, prev); keys.forEach(function(k) { n[k] = p[k]; }); if (!n.country) n.country = 'US'; return n; });
    setErrors({});
    var LABELS = { name: 'name', address1: 'address', address2: 'address 2', address3: 'address 3', city: 'city', state: 'state', zip: 'ZIP', country: 'country', phone: 'phone', email: 'email' };
    setPasteNote('Filled ' + keys.map(function(k) { return LABELS[k] || k; }).join(', ') + ' — check the fields');
  };

  // Esc closes (dropdown / paste box first), Ctrl+Enter saves — read through a ref so the
  // single window listener always sees the current state.
  latest.current = { save: save, onClose: onClose, sugg: sugg, pasteOpen: pasteOpen, csvMode: csvMode };
  useEffect(function() {
    if (!open) return;
    var onKey = function(ev) {
      var L = latest.current;
      if (ev.key === 'Escape') {
        if (L.sugg) { setSugg(null); ev.stopPropagation(); return; }
        if (L.pasteOpen) { setPasteOpen(false); ev.stopPropagation(); return; }
        L.onClose();
      } else if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey) && !L.csvMode) { ev.preventDefault(); L.save(false); }
    };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [open]);

  var importCsv = function() {
    if (!csvResult || !csvResult.orders || !csvResult.orders.length) return;
    setSaving(true);
    api('/shipping/manual-orders/bulk', { method: 'POST', body: JSON.stringify({ orders: csvResult.orders }) })
      .then(function(r) {
        var msg = (r.created || []).length + ' order(s) created' + ((r.errors || []).length ? ', ' + r.errors.length + ' failed: ' + r.errors.map(function(e2) { return (e2.ref || '#' + e2.index) + ' — ' + e2.error; }).join('; ') : '');
        showToast(msg, (r.errors || []).length ? 'error' : 'success');
        if ((r.created || []).length) { onClose(); if (onSaved) onSaved(null); try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {} }
      }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  var downloadTemplate = function() {
    var sample = [SHIP_MANUAL_CSV_COLS.join(','),
      'W-1001,Wholesale,Acme Retail,123 Main St,,Austin,TX,78701,US,512-555-0100,buyer@acme.com,ALRE-852,,12,,,240.00,2026-08-20,Net 30 PO 4451',
      'W-1001,Wholesale,Acme Retail,123 Main St,,Austin,TX,78701,US,512-555-0100,buyer@acme.com,VDIM-142,,6,,,,,'].join('\n');
    var a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([sample], { type: 'text/csv' }));
    a.download = 'manual-orders-template.csv';
    document.body.appendChild(a); a.click();
    setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(a.href); }, 200);
  };

  // ── styles (Rate Browser / settings scale) ──
  var lbl = { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, display: 'block', whiteSpace: 'nowrap' };
  var inp = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 9px', fontSize: 12.5, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none', minWidth: 0 };
  var errBox = function(key) { return errors[key] ? { borderColor: C.danger, boxShadow: '0 0 0 2px ' + C.danger + '33' } : {}; };
  var focusRing = {
    onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
    onBlur: function(e) { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; },
  };
  var text = function(key, extra) {
    return h('input', Object.assign({ ref: function(el) { fieldRefs.current[key] = el; }, value: rec[key] || '', onChange: function(e) { setRecField(key, e.target.value); },
      style: Object.assign({}, inp, errBox(key)) }, focusRing, extra || {}));
  };
  var field = function(label, node, required) {
    return h('div', { style: { minWidth: 0 } }, h('span', { style: lbl }, label, required ? h('span', { style: { color: C.danger } }, ' *') : null), node);
  };
  var section = function(title, right, body, first) {
    return h('div', { style: { paddingTop: first ? 0 : 14, marginTop: first ? 0 : 14, borderTop: first ? 'none' : '1px solid ' + C.border } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, minHeight: 26 } },
        h('span', { style: { fontSize: 12.5, fontWeight: 600, color: C.text } }, title),
        h('span', { style: { flex: 1 } }), right),
      body);
  };
  var grid = function(cols, children, mb) { return h('div', { style: { display: 'grid', gridTemplateColumns: cols, gap: 10, marginBottom: mb == null ? 10 : mb } }, children); };

  var typeControl = h('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
    h('span', { style: Object.assign({}, lbl, { marginBottom: 0, marginRight: 4 }) }, 'Type'),
    SHIP_MANUAL_TYPES.map(function(t) {
      var on = type === t;
      return h('button', { key: t, onClick: function() { setType(t); }, style: { cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (on ? C.accent : C.border), background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted } }, t);
    }));

  var titleNode = h('span', { style: { display: 'inline-flex', alignItems: 'baseline', gap: 10 } },
    edit ? 'Edit ' + (edit.order_number || 'manual order') : 'New Order',
    h('span', { style: { fontSize: 12, fontWeight: 400, color: C.textMuted } }, edit ? 'Manual order' : 'Manual order · not in Zentail'));

  var LINE_COLS = '1.3fr 2.2fr 64px 88px 28px';

  return h(Modal, { open: open, onClose: onClose, title: titleNode, width: 780 },
    h('div', null,
      h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' } },
        !edit && h(Btn, { size: 'sm', variant: csvMode ? undefined : 'primary', onClick: function() { setCsvMode(false); } }, 'Order form'),
        !edit && h(Btn, { size: 'sm', variant: csvMode ? 'primary' : undefined, onClick: function() { setCsvMode(true); } }, 'Import CSV'),
        h('span', { style: { flex: 1 } }),
        csvMode ? h(Btn, { size: 'sm', onClick: downloadTemplate }, h(LIcon, { name: 'download', size: 12 }), ' Template') : typeControl),

      csvMode
        ? h('div', null,
            h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
              'One row per line item; the OrderRef column groups rows into orders (the first row of a group provides the recipient). Type is one of: ' + SHIP_MANUAL_TYPES.join(', ') + '.'),
            h('input', { type: 'file', accept: '.csv,text/csv', ref: fileRef, style: { fontSize: 12.5, marginBottom: 10, color: C.text, fontFamily: 'inherit' }, onChange: function(e) {
              var f = e.target.files && e.target.files[0];
              if (!f) return;
              var rd = new FileReader();
              rd.onload = function() { setCsvResult(shipCsvToManualOrders(String(rd.result || ''))); };
              rd.readAsText(f);
            } }),
            csvResult && csvResult.error && h('div', { style: { fontSize: 12.5, color: C.danger, marginBottom: 8 } }, csvResult.error),
            csvResult && csvResult.orders && h('div', { style: { fontSize: 12.5, color: C.text, marginBottom: 8 } },
              csvResult.orders.length + ' order(s), ' + csvResult.orders.reduce(function(s, o) { return s + o.lines.length; }, 0) + ' line(s) parsed: ',
              csvResult.orders.map(function(o) { return o.ref + ' (' + o.lines.length + ')'; }).join(', ')),
            h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
              h(Btn, { onClick: onClose }, 'Cancel'),
              h(Btn, { variant: 'primary', disabled: saving || !(csvResult && csvResult.orders && csvResult.orders.length), onClick: importCsv }, saving ? 'Importing…' : 'Create orders')))
        : h('div', null,
            section('Ship to',
              h(Btn, { size: 'sm', variant: pasteOpen ? 'primary' : undefined, onClick: function() { setPasteOpen(!pasteOpen); setPasteNote(''); }, style: { padding: '4px 10px' } }, h(LIcon, { name: 'clipboard', size: 12 }), ' Paste address'),
              h('div', null,
                pasteOpen && h('div', { style: { marginBottom: 10, padding: 10, background: C.bg, border: '1px solid ' + C.border, borderRadius: 8 } },
                  h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } }, 'Paste a full address block (name, street, city / state / ZIP, phone, email in any order). The fields fill in on paste.'),
                  h('textarea', Object.assign({ value: pasteText, rows: 4, autoFocus: true,
                    onChange: function(e) { setPasteText(e.target.value); },
                    onPaste: function(e) { var t = (e.clipboardData || window.clipboardData).getData('text'); if (t) { e.preventDefault(); setPasteText(t); applyPaste(t); } },
                    style: Object.assign({}, inp, { resize: 'vertical', lineHeight: 1.4 }) }, focusRing)),
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 } },
                    h('span', { style: { fontSize: 12, color: pasteNote.indexOf('Filled') === 0 ? C.success : C.textMuted, flex: 1 } }, pasteNote),
                    h(Btn, { size: 'sm', onClick: function() { setPasteOpen(false); setPasteText(''); setPasteNote(''); } }, 'Close'),
                    h(Btn, { size: 'sm', variant: 'primary', disabled: !pasteText.trim(), onClick: function() { applyPaste(); } }, 'Fill fields'))),
                grid('2fr 1fr 1fr', [
                  h('div', { key: 'n' }, field('Recipient name', text('name'), true)),
                  h('div', { key: 'p' }, field('Phone', text('phone', { type: 'tel' }))),
                  h('div', { key: 'e' }, field('Email', text('email', { type: 'email' })))]),
                grid('1fr', [h('div', { key: 'a1' }, field('Address 1', text('address1'), true))]),
                grid('1fr 1fr', [
                  h('div', { key: 'a2' }, field('Address 2', text('address2'))),
                  h('div', { key: 'a3' }, field('Address 3', text('address3')))]),
                grid('2fr 1.1fr 1fr 1.5fr', [
                  h('div', { key: 'c' }, field('City', text('city'), true)),
                  h('div', { key: 's' }, field('State', h(StateSelect, { value: rec.state, country: rec.country || 'US', onChange: function(e) { setRecField('state', e.target.value); }, style: Object.assign({}, inp, errBox('state')), placeholder: isUS ? '' : 'Province / region' }), isUS)),
                  h('div', { key: 'z' }, field(isUS ? 'ZIP' : 'Postal code', text('zip'), isUS)),
                  h('div', { key: 'k' }, field('Country', h(SearchSelect, { options: countryOptionList(rec.country || 'US'), value: rec.country || 'US', onChange: function(v) { setRecField('country', v); if (v !== (rec.country || 'US')) setRecField('state', ''); }, style: inp })))
                ], 0)), true),

            section('Items',
              h(Btn, { size: 'sm', onClick: addLine, style: { padding: '4px 10px' } }, h(LIcon, { name: 'plus', size: 12 }), ' Add item'),
              h('div', null,
                h('div', { style: { display: 'grid', gridTemplateColumns: LINE_COLS, gap: 8, marginBottom: 4 } },
                  h('span', { style: lbl }, 'SKU'), h('span', { style: lbl }, 'Description'), h('span', { style: lbl }, 'Qty'), h('span', { style: lbl }, 'Wt (lb)'), h('span', null)),
                lines.map(function(l, i) {
                  var last = i === lines.length - 1;
                  var rowErr = errors['line:' + i];
                  // Enter on the SKU takes the exact match when the typed SKU is one, else the top suggestion
                  var onRowKey = function(e) { if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) { var ex = exactSugg(i, l.sku); if (ex) { pickSugg(i, ex); e.preventDefault(); } else if (last && (l.sku || l.title)) { e.preventDefault(); addLine(); } } };
                  return h('div', { key: i, style: { display: 'grid', gridTemplateColumns: LINE_COLS, gap: 8, marginBottom: 6, alignItems: 'center' } },
                    h('div', { style: { position: 'relative' } },
                      h('input', Object.assign({ ref: function(el) { skuRefs.current[i] = el; }, value: l.sku, title: 'SKU — type to search the catalog, or leave blank for a free-text item',
                        onChange: function(e) { setLine(i, 'sku', e.target.value); fetchSugg(i, e.target.value); }, onKeyDown: onRowKey,
                        onBlur: function(e) { focusRing.onBlur(e); if (suggTimer.current) clearTimeout(suggTimer.current); resolveSku(i, e.target.value); setTimeout(function() { setSugg(function(s) { return s && s.idx === i ? null : s; }); }, 150); },
                        style: Object.assign({}, inp, rowErr ? errBox('line:' + i) : {}) }, { onFocus: focusRing.onFocus })),
                      sugg && sugg.idx === i && sugg.list.length > 0 && h('div', { style: { position: 'absolute', top: '100%', left: 0, minWidth: '100%', width: 'max-content', maxWidth: 420, zIndex: 50, background: C.surface, border: '1px solid ' + C.border, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', maxHeight: 200, overflowY: 'auto' } },
                        sugg.list.map(function(p, k) {
                          return h('div', { key: p.sku, onMouseDown: function(e) { e.preventDefault(); pickSugg(i, p); }, style: { padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid ' + C.border + '44', background: k === 0 ? C.bg : 'transparent' } },
                            h('div', { style: { color: C.text, fontWeight: 500 } }, p.sku, k === 0 ? h('span', { style: { fontSize: 10.5, color: C.textDim, marginLeft: 8 } }, 'Enter') : null),
                            h('div', { style: { color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, p.title || p.vendor_title || ''));
                        }))),
                    h('input', Object.assign({ value: l.title, title: 'Description — free text is fine', onChange: function(e) { setLine(i, 'title', e.target.value); }, onKeyDown: onRowKey, style: Object.assign({}, inp, rowErr ? errBox('line:' + i) : {}) }, focusRing)),
                    h('input', Object.assign({ type: 'number', min: 1, value: l.qty, title: 'Quantity', onChange: function(e) { setLine(i, 'qty', e.target.value); }, onKeyDown: onRowKey, style: Object.assign({}, inp, { textAlign: 'right' }, rowErr ? errBox('line:' + i) : {}) }, focusRing)),
                    h('input', Object.assign({ type: 'number', step: '0.01', min: 0, value: l.weight_lb, title: 'Weight per unit in lb — optional; the catalog weight fills in for known SKUs', onChange: function(e) { setLine(i, 'weight_lb', e.target.value); }, onKeyDown: onRowKey, style: Object.assign({}, inp, { textAlign: 'right' }) }, focusRing)),
                    h('button', { onClick: function() { removeLine(i); }, title: 'Remove line', style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4, display: 'inline-flex', borderRadius: 4 } }, h(LIcon, { name: 'x', size: 14 })));
                }),
                h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 2 } },
                  realLines.length + ' line' + (realLines.length === 1 ? '' : 's') + ' · ' + totalUnits + ' unit' + (totalUnits === 1 ? '' : 's') +
                  (totalWeight > 0 ? ' · ~' + totalWeight.toFixed(2) + ' lb from line weights' : '') +
                  ' · catalog weight fills in for known SKUs left blank · Enter on the last line adds another'))),

            section('Order details', null,
              grid('1fr 1fr 2fr', [
                h('div', { key: 't' }, field('Order total ($)', h('input', Object.assign({ type: 'number', step: '0.01', min: 0, value: orderTotal, onChange: function(e) { setOrderTotal(e.target.value); }, style: inp }, focusRing)))),
                h('div', { key: 'd' }, field('Ship by', h('input', Object.assign({ type: 'date', value: shipBy, onChange: function(e) { setShipBy(e.target.value); }, style: inp }, focusRing)))),
                h('div', { key: 'n' }, field('Note', h('input', Object.assign({ value: note, title: 'Shows on the order and the packing slip notes', onChange: function(e) { setNote(e.target.value); }, style: inp }, focusRing))))
              ], 0)),

            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 } },
              h('span', { style: { fontSize: 12.5, color: errMsg ? C.danger : C.textDim, flex: 1, minWidth: 0 } }, errMsg || 'Ctrl+Enter creates the order'),
              h(Btn, { onClick: onClose }, 'Cancel'),
              !edit && h(Btn, { disabled: saving, onClick: function() { save(true); }, title: 'Create the order and open it' }, 'Create & open'),
              h(Btn, { variant: 'primary', disabled: saving, onClick: function() { save(false); } }, saving ? 'Saving…' : (edit ? 'Save changes' : 'Create order'))))));
}

// ─── Mark as Shipped modal (ShipStation parity) ─────────────────────────────
// Per-order carrier + tracking + ship date, with a Bulk Update row that stamps
// all rows; checked rows can be Removed from the list. Submit posts
// /shipping/mark-shipped — Zentail is notified and pushes tracking to the
// channel; manual orders (negative ids) are flipped locally only.
var SHIP_MARK_CARRIERS = [
  { v: 'USPS', l: 'USPS' },
  { v: 'dhl_global_mail', l: 'DHL eCommerce' },
  { v: 'globalpost', l: 'GlobalPost' },
  { v: 'UPS', l: 'UPS' },
  { v: 'FedEx', l: 'FedEx' },
  { v: 'Other', l: 'Other' }, // ShipStation parity (Anthony 2026-10-28) — any carrier not listed above
];

// Zentail's POST /salesOrder/shipments requires service_level. These are the
// exact strings observed round-tripping through real packages (90d of data);
// first entry = the carrier's default. UPS ships as plain 'ground'; FedEx has
// no observed string (Zentail displays blank-service FedEx as "FedEx Ground"),
// so it defaults to 'ground' too. Other pre-fills 'other' so the required cell
// is never empty. Custom… covers anything not listed.
var SHIP_MARK_SERVICES = {
  USPS: ['ground_advantage', 'priority_mail', 'first_class_mail', 'media_mail', 'priority_mail_international', 'first_class_mail_international'],
  dhl_global_mail: ['smartmail_parcels_expedited', 'smartmail_parcels_ground', 'globalmail_parcel_direct_ddp'],
  globalpost: ['globalpost_standard_single_piece', 'globalpost_plus_dutytaxincluded_single_piece'],
  UPS: ['ground'],
  FedEx: ['ground'],
  Other: ['other'],
};
function shipMarkSvcDefault(carrier) { return (SHIP_MARK_SERVICES[carrier] || [])[0] || ''; }
function shipMarkSvcLabel(v) {
  return String(v).split('_').map(function(w) {
    if (w === 'ddp') return 'DDP';
    if (w === 'dutytaxincluded') return 'Duty & Tax Included';
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
}

function ShipMarkShippedModal({ open, orders, onClose, onDone, showToast }) {
  var todayStr = function() {
    var d = new Date();
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  };
  var [rows, setRows] = useState([]);
  var [bulkDate, setBulkDate] = useState('');
  var [bulkCarrier, setBulkCarrier] = useState('');
  var [bulkService, setBulkService] = useState('');
  var [saving, setSaving] = useState(false);
  var [rowErr, setRowErr] = useState({}); // id -> validation/server message

  useEffect(function() {
    if (!open) return;
    setRows((orders || []).map(function(o) {
      return { id: o.id, order_number: o.order_number, customer: o.customer || '', carrier: '', service: '', svc_custom: false, tracking: '', ship_date: todayStr(), checked: true, no_notify_customer: false };
    }));
    setBulkDate(''); setBulkCarrier(''); setBulkService(''); setSaving(false); setRowErr({});
  }, [open]);

  var setRow = function(i, k, v) { setRows(function(p) { var n = p.slice(); n[i] = Object.assign({}, n[i]); n[i][k] = v; return n; }); };
  var allChecked = rows.length > 0 && rows.every(function(r) { return r.checked; });
  var checkedCount = rows.filter(function(r) { return r.checked; }).length;

  var inp = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 6, fontSize: 11.5, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
  var th = { padding: '6px 8px', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid ' + C.border };
  var td = { padding: '5px 8px', fontSize: 11.5, color: C.text, borderBottom: '1px solid ' + C.border + '44', verticalAlign: 'middle' };

  var submit = function() {
    var missing = {};
    rows.forEach(function(r) {
      if (!r.carrier) missing[r.id] = 'Carrier required';
      else if (!r.service.trim()) missing[r.id] = 'Service required';
      else if (!r.tracking.trim()) missing[r.id] = 'Tracking number required';
    });
    if (Object.keys(missing).length) { setRowErr(missing); showToast('Every order needs a carrier, a service, and a tracking number', 'error'); return; }
    setSaving(true);
    api('/shipping/mark-shipped', { method: 'POST', body: JSON.stringify({ rows: rows.map(function(r) { return { id: r.id, carrier: r.carrier, service: r.service.trim(), tracking: r.tracking.trim(), ship_date: r.ship_date, no_notify_customer: !!r.no_notify_customer }; }) }) })
      .then(function(r) {
        var errs = r.errors || [];
        var okCount = (r.shipped || []).length;
        if (!errs.length) {
          showToast('Marked ' + okCount + ' order' + (okCount === 1 ? '' : 's') + ' as shipped', 'success');
          onDone(); onClose();
        } else {
          var em = {}; errs.forEach(function(e) { em[e.id] = e.error; });
          setRowErr(em);
          var okIds = {}; (r.shipped || []).forEach(function(s) { okIds[s.id] = true; });
          setRows(function(p) { return p.filter(function(x) { return !okIds[x.id]; }); });
          showToast((okCount ? okCount + ' shipped, ' : '') + errs.length + ' failed — see the rows below', 'error');
          if (okCount) onDone(); // refresh the queue behind the modal
        }
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  return h(Modal, { open: open, onClose: onClose, title: 'Mark as Shipped', width: 980 },
    h('div', null,
      h('div', { style: { fontSize: 11.5, color: C.textMuted, marginBottom: 12 } },
        'Orders marked as shipped move to ', h('span', { style: { color: C.text, fontWeight: 500 } }, 'Shipped'),
        '. Zentail is notified and pushes the tracking to the channel; a shipment is recorded in ',
        h('span', { style: { color: C.text, fontWeight: 500 } }, 'Shipments › Recent'), '.'),
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
        h('span', { style: { fontSize: 11.5, fontWeight: 600, color: C.text } }, 'Bulk Update:'),
        h('input', { type: 'date', value: bulkDate, title: 'Apply this ship date to all orders', style: Object.assign({}, inp, { width: 132 }), onChange: function(e) {
          var v = e.target.value; setBulkDate(v);
          if (v) setRows(function(p) { return p.map(function(r) { return Object.assign({}, r, { ship_date: v }); }); });
        } }),
        h('select', { value: bulkCarrier, title: 'Apply this carrier (and its default service) to all orders', style: Object.assign({}, inp, { width: 150 }), onChange: function(e) {
          var v = e.target.value; setBulkCarrier(v); setBulkService(v ? shipMarkSvcDefault(v) : '');
          if (v) setRows(function(p) { return p.map(function(r) { return Object.assign({}, r, { carrier: v, service: shipMarkSvcDefault(v), svc_custom: false }); }); });
        } },
          h('option', { value: '' }, 'Carrier'),
          SHIP_MARK_CARRIERS.map(function(c) { return h('option', { key: c.v, value: c.v }, c.l); })),
        h('select', { value: bulkService, disabled: !bulkCarrier, title: 'Apply this service to all orders', style: Object.assign({}, inp, { width: 190, opacity: bulkCarrier ? 1 : 0.5 }), onChange: function(e) {
          var v = e.target.value; setBulkService(v);
          if (v) setRows(function(p) { return p.map(function(r) { return Object.assign({}, r, { service: v, svc_custom: false }); }); });
        } },
          h('option', { value: '' }, 'Service'),
          (SHIP_MARK_SERVICES[bulkCarrier] || []).map(function(s) { return h('option', { key: s, value: s }, shipMarkSvcLabel(s)); })),
        h('label', { title: 'Recorded on the shipment; suppression itself depends on Zentail/channel support', style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textMuted, cursor: 'pointer' } },
          h(ShipCheckbox, { checked: rows.length > 0 && rows.every(function(r) { return r.no_notify_customer; }), onChange: function() {
            var v = !(rows.length > 0 && rows.every(function(r) { return r.no_notify_customer; }));
            setRows(function(p2) { return p2.map(function(r) { return Object.assign({}, r, { no_notify_customer: v }); }); });
          } }),
          'Do not notify customer'),
        h('span', { style: { flex: 1 } }),
        h('button', {
          disabled: checkedCount === 0,
          onClick: function() { setRows(function(p) { return p.filter(function(r) { return !r.checked; }); }); },
          title: 'Remove the checked orders from this list (they stay in the queue)',
          style: { background: 'transparent', border: 'none', color: checkedCount ? C.danger : C.textDim, cursor: checkedCount ? 'pointer' : 'default', fontSize: 11.5, fontFamily: 'inherit', padding: 0 }
        }, 'Remove')),
      h('table', { style: { width: '100%', borderCollapse: 'collapse' } },
        h('thead', null, h('tr', null,
          h('th', { style: Object.assign({}, th, { width: 30 }) },
            h(ShipCheckbox, { checked: allChecked, onChange: function() { var v = !allChecked; setRows(function(p) { return p.map(function(r) { return Object.assign({}, r, { checked: v }); }); }); } })),
          h('th', { style: th }, 'Order #'),
          h('th', { style: th }, 'Recipient'),
          h('th', { style: Object.assign({}, th, { width: 136 }) }, 'Carrier'),
          h('th', { style: Object.assign({}, th, { width: 190 }) }, 'Service'),
          h('th', { style: Object.assign({}, th, { width: 180 }) }, 'Tracking Number'),
          h('th', { style: Object.assign({}, th, { width: 132 }) }, 'Ship Date'),
          h('th', { style: Object.assign({}, th, { width: 64 }), title: 'Do not notify customer' }, 'No Notify'))),
        h('tbody', null,
          rows.map(function(r, i) {
            return h(React.Fragment, { key: r.id },
              h('tr', null,
                h('td', { style: td }, h(ShipCheckbox, { checked: !!r.checked, onChange: function() { setRow(i, 'checked', !r.checked); } })),
                h('td', { style: Object.assign({}, td, {}) }, r.order_number),
                h('td', { style: td }, r.customer || '—'),
                h('td', { style: td },
                  h('select', { value: r.carrier, style: Object.assign({}, inp, rowErr[r.id] && !r.carrier ? { borderColor: C.danger } : null), onChange: function(e) {
                    var v = e.target.value;
                    setRows(function(p) { var n = p.slice(); n[i] = Object.assign({}, n[i], { carrier: v, service: shipMarkSvcDefault(v), svc_custom: false }); return n; });
                  } },
                    h('option', { value: '' }, 'Select One…'),
                    SHIP_MARK_CARRIERS.map(function(c) { return h('option', { key: c.v, value: c.v }, c.l); }))),
                h('td', { style: td },
                  r.svc_custom
                    ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                        h('input', { value: r.service, placeholder: 'service_level', style: Object.assign({}, inp, rowErr[r.id] && !r.service.trim() ? { borderColor: C.danger } : null), onChange: function(e) { setRow(i, 'service', e.target.value); } }),
                        h('button', {
                          title: 'Back to the service list',
                          onClick: function() { setRows(function(p) { var n = p.slice(); n[i] = Object.assign({}, n[i], { svc_custom: false, service: shipMarkSvcDefault(n[i].carrier) }); return n; }); },
                          style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', padding: '0 2px', flexShrink: 0 }
                        }, '↩'))
                    : h('select', { value: r.service, disabled: !r.carrier, style: Object.assign({}, inp, { opacity: r.carrier ? 1 : 0.5 }, rowErr[r.id] && r.carrier && !r.service.trim() ? { borderColor: C.danger } : null), onChange: function(e) {
                        var v = e.target.value;
                        if (v === '__custom') setRows(function(p) { var n = p.slice(); n[i] = Object.assign({}, n[i], { svc_custom: true, service: '' }); return n; });
                        else setRow(i, 'service', v);
                      } },
                        h('option', { value: '' }, 'Select One…'),
                        (SHIP_MARK_SERVICES[r.carrier] || []).map(function(s) { return h('option', { key: s, value: s }, shipMarkSvcLabel(s)); }),
                        h('option', { value: '__custom' }, 'Custom…'))),
                h('td', { style: td },
                  h('input', { value: r.tracking, placeholder: 'Tracking Number', style: Object.assign({}, inp, rowErr[r.id] && !r.tracking.trim() ? { borderColor: C.danger } : null), onChange: function(e) { setRow(i, 'tracking', e.target.value); } })),
                h('td', { style: td },
                  h('input', { type: 'date', value: r.ship_date, style: inp, onChange: function(e) { setRow(i, 'ship_date', e.target.value); } })),
                h('td', { style: Object.assign({}, td, { textAlign: 'center' }) },
                  h(ShipCheckbox, { checked: !!r.no_notify_customer, onChange: function() { setRow(i, 'no_notify_customer', !r.no_notify_customer); } }))),
              rowErr[r.id] ? h('tr', null,
                h('td', { style: { padding: 0, border: 'none' } }),
                h('td', { colSpan: 7, style: { padding: '0 8px 6px', fontSize: 10.5, color: C.danger, border: 'none' } }, rowErr[r.id])) : null);
          }))),
      rows.length === 0 && h('div', { style: { padding: 20, textAlign: 'center', fontSize: 11.5, color: C.textMuted } }, 'No orders left in the list.'),
      h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
        h(Btn, { onClick: onClose }, 'Cancel'),
        h(Btn, { variant: 'primary', disabled: saving || rows.length === 0, onClick: submit }, saving ? 'Marking…' : 'Mark as Shipped'))));
}

;
/* ===== app-shipping-queue.js ===== */
// ─── Shipping app: the orders queue (buckets, batches, bulk panel) ─── split 2026-09-19

// How the details column renders (9-27 phones / tablets): 'wide' = the docked 446px column or its
// 40px strip, exactly as before; narrow widths get a full-screen 'overlay' while open, a floating
// 'button' to reopen it while rows are checked, and nothing ('none') with no selection.
function shipNarrowDockMode(isNarrow, open, selCount) {
  if (!isNarrow) return 'wide';
  if (!selCount) return 'none';
  return open ? 'overlay' : 'button';
}

function ShipQueueTab({ bucket, batchId, batchNumber, showToast }) {
  bucket = bucket || 'ready';
  var canShip = bucket === 'ready' || bucket === 'hold' || !!batchId;
  var canCancelHere = bucket !== 'cancelled' && bucket !== 'shipped'; // Cancel in the shipping app only (Anthony 9-19)
  var SHIP_EMPTY_FILTERS = { channels: [], dest: 'all', assignees: [], tags: [], holdOnly: false, dateFrom: '', dateTo: '', datePreset: '', other: SHIP_EMPTY_OTHER };
  var qKey = bucket + '|' + (batchId || '');
  var [data, setData] = useState(function() { return _shipQueueCache[qKey] || null; });
  var [loading, setLoading] = useState(function() { return !_shipQueueCache[qKey]; });
  // Filters + search live in the URL (?f= / ?q=) so a refresh or a shared
  // link restores the exact view — replaceState only, no history spam.
  var _urlFilterInit = function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      var raw = sp.get('f');
      if (!raw) return { filters: SHIP_EMPTY_FILTERS, q: sp.get('q') || '' };
      var parsed = JSON.parse(raw);
      var merged = Object.assign({}, SHIP_EMPTY_FILTERS, parsed || {});
      merged.other = Object.assign({}, SHIP_EMPTY_OTHER, (parsed && parsed.other) || {});
      return { filters: merged, q: sp.get('q') || '' };
    } catch (e) { return { filters: SHIP_EMPTY_FILTERS, q: '' }; }
  }();
  var [filters, setFilters] = useState(_urlFilterInit.filters);
  // Search is ephemeral (not part of saved filters), same as the Shipments page:
  // qLive is the input text, q is the committed term (Enter).
  var [q, setQ] = useState(_urlFilterInit.q);
  var [qLive, setQLive] = useState(_urlFilterInit.q);
  var qViaRef = useRef('pause'); // how the last term was committed: 'enter' | 'paste' | 'pause' (9-22)
  var qSearchProps = useShipAutoSearch(qLive, setQLive, function(v, via) {
    qViaRef.current = via || 'pause'; setQ(v);
    // A search is a lookup, not a refinement: the active filters reset so the order can show
    // (Anthony 9-22: "it didn't reset my filters")
    if (v && String(v).trim()) setFilters(function(prev) { return JSON.stringify(prev) === JSON.stringify(SHIP_EMPTY_FILTERS) ? prev : SHIP_EMPTY_FILTERS; });
  }); // search-as-you-type (9-18)
  // Mirror active filters/search into the URL (params stripped when pristine)
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      var pristine = JSON.stringify(filters) === JSON.stringify(SHIP_EMPTY_FILTERS);
      if (pristine) sp.delete('f'); else sp.set('f', JSON.stringify(filters));
      if (q) sp.set('q', q); else sp.delete('q');
      var qs = sp.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }, [filters, q]);
  // Changing filters/search clears the selection — orders selected under the
  // old filter may be invisible under the new one (hidden-selection trap)
  useEffect(function() { setSelected({}); }, [filters, q]);
  // Committed search also probes full order history server-side — the queue
  // only holds this bucket's 30-day slice, so "where is order X" needs the
  // global index. Out-of-queue matches render in a section below the table.
  var [histHits, setHistHits] = useState([]);
  useEffect(function() {
    if (!q || q.trim().length < 2) { setHistHits([]); return; }
    var dead = false;
    api('/shipping/order-search?q=' + encodeURIComponent(q.trim()))
      .then(function(r) { if (!dead) setHistHits(r.hits || []); })
      .catch(function() { if (!dead) setHistHits([]); });
    return function() { dead = true; };
  }, [q]);
  // Matches outside this bucket join the same list (Anthony 9-22: "It should show in the same
  // list as the ready to ship orders"): their full queue rows come through the targeted queue
  // endpoint (bucket=all), marked with where the search found them. Orders older than the
  // queue's 30-day window are not queue rows; they keep the compact list below the table.
  var [extraRows, setExtraRows] = useState([]);
  var [extraPending, setExtraPending] = useState(false); // the compact list below waits for the row fetch (no flash)
  // The effect that fills extraRows sits below the filter (it depends on data + filteredQueue, 2026-09-26).
  // A search for a SHIPPED order goes straight to its shipment (Anthony
  // 2026-09-04): when nothing in this queue bucket matches but the history
  // probe found shipped orders, hand off to the Shipments view — a single
  // match opens there. `filtered` is the queue's own match list, assigned
  // further down this render (hoisted var); the effect runs after render.
  useEffect(function() {
    if (!q || q.trim().length < 2 || !histHits.length) return;
    var visible = {};
    (filteredQueue || []).forEach(function(o) { visible[o.id] = true; });
    if ((filteredQueue || []).length) return;
    var shipped = histHits.filter(function(hh) { return !visible[hh.id] && String(hh.status || '').toUpperCase() === 'SHIPPED'; });
    if (!shipped.length) return;
    // Jump only for a term the user finished: an exact order-number match, or Enter. A mid-typing
    // pause used to jump to whichever older shipped order contained the digits typed so far (Anthony 9-22).
    var exact = shipped.filter(function(hh) { return hh.exact; });
    if (!exact.length && qViaRef.current !== 'enter') return;
    // The jump lands on the Shipments list filtered to the term; it never opens the order by itself
    // (Anthony 9-22: searching a shipped order from Ready to Ship popped the order modal).
    try {
      history.pushState({}, '', '/shipments?q=' + encodeURIComponent(q.trim()) + '&days=90');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (e) {}
  }, [histHits]);
  var [savedFilters, setSavedFilters] = useState([]);
  var [sfOpen, setSfOpen] = useState(false);
  var sfRef = useRef(null);
  var [oOpen, setOOpen] = useState(false);
  var oRef = useRef(null);
  // Order Date dropdown: draft state commits on Apply, discards on Cancel/outside
  var [odOpen, setOdOpen] = useState(false);
  var odRef = useRef(null);
  var [odDraft, setOdDraft] = useState({ preset: '', from: '', to: '' });
  var [rating, setRating] = useState(false);
  var [rateProg, setRateProg] = useState(null); // {done, total} while Get Rate runs
  var [rateTotals, setRateTotals] = useState(function() { // orderId -> cheapest rate (seeded from the cached rows)
    var seed = {}; ((_shipQueueCache[qKey] || {}).orders || []).forEach(function(o) { if (o.rate) seed[o.id] = o.rate; }); return seed;
  });
  var [rateBusy, setRateBusy] = useState({}); // orderId -> a spinner in the Rate cell while its quote is in flight
  var rateBusyRef = useRef({});
  var markBusy = function(ids, on) {
    var n = Object.assign({}, rateBusyRef.current);
    ids.forEach(function(id) { if (on) n[id] = true; else delete n[id]; });
    rateBusyRef.current = n; setRateBusy(n);
  };
  // The Rate column overlay follows the server for every row a load / refresh returned: a row
  // without a cached quote loses its number (a failed re-quote shows the warning, not the old
  // price). The old merge kept the on-screen number, so a re-quote never showed — 1,137 orders
  // moved to DHL still read their USPS quotes after two Get Rate runs (Anthony 2026-09-22).
  var syncRates = useCallback(function(orders) {
    setRateTotals(function(p) { var n = Object.assign({}, p); (orders || []).forEach(function(o) { if (o.rate) n[o.id] = o.rate; else delete n[o.id]; }); return n; });
  }, []);
  var getSingleRate = function(o) {
    markBusy([o.id], true);
    api('/shipping/rates', { method: 'POST', body: JSON.stringify({ order_id: o.id, force: true }) })
      .then(function(r) {
        if (r.rates && r.rates.length) setRateTotals(function(p) { var n = Object.assign({}, p); n[o.id] = r.rates[0]; return n; });
        else showToast('No rates for ' + o.order_number, 'error');
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { markBusy([o.id], false); });
  };
  var [selected, setSelected] = useState({});
  // Details column collapse (Anthony 2026-09-06): expanded = the 446px docked column; collapsed =
  // a 40px strip carrying the checked-count badge while the table takes the width. One toggle,
  // no pin, no overlay. Saved per user next to the sidebar pin: localStorage for first paint,
  // /auth/preferences so it follows the user.
  var [panelExpanded, setPanelExpanded] = useState(function() {
    try { return localStorage.getItem('ship_panel_collapsed') !== 'true'; } catch (e) { return true; }
  });
  // The mount-time preference fetch can land AFTER the user already toggled (the orders load
  // hogs the connection pool) — a stale server value must not undo a click made in the meantime
  var panelTouched = useRef(false);
  useEffect(function() {
    api('/auth/preferences').then(function(p) {
      if (panelTouched.current) return;
      if (p && typeof p.ship_panel_collapsed === 'boolean') {
        try { localStorage.setItem('ship_panel_collapsed', String(p.ship_panel_collapsed)); } catch (e) {}
        setPanelExpanded(!p.ship_panel_collapsed);
      }
    }).catch(function() {});
  }, []);
  // Narrow widths (phones / tablets, 9-27): no side-by-side details column — the table takes the
  // width and the details open as a full-screen overlay (Back closes it) when an order is tapped.
  var viewport = useViewport();
  var [narrowDockOpen, setNarrowDockOpen] = useState(false);
  var togglePanel = function() {
    var expanded = !panelExpanded;
    panelTouched.current = true;
    setPanelExpanded(expanded);
    try { localStorage.setItem('ship_panel_collapsed', String(!expanded)); } catch (e) {}
    api('/auth/preferences', { method: 'PUT', body: JSON.stringify({ ship_panel_collapsed: !expanded }) }).catch(function() {});
  };
  // Current DataTable page (for page-scoped select-all, Gmail/ShipStation style)
  var pageRef = useRef([]);
  var [, setPageTick] = useState(0);
  var [openBatches, setOpenBatches] = useState([]);
  var [batchMenuOpen, setBatchMenuOpen] = useState(false);
  var [selPrintOpen, setSelPrintOpen] = useState(false);
  // Click-away for the selection panel's Batch/Print header menus (capture
  // phase — bubbling clicks get stopped inside panel content)
  var selBatchRef = useRef(null);
  var selPrintRef = useRef(null);
  useEffect(function() {
    if (!batchMenuOpen && !selPrintOpen) return;
    var onDown = function(e) {
      if (selBatchRef.current && selBatchRef.current.contains(e.target)) return;
      if (selPrintRef.current && selPrintRef.current.contains(e.target)) return;
      setBatchMenuOpen(false); setSelPrintOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return function() { document.removeEventListener('mousedown', onDown, true); };
  }, [batchMenuOpen, selPrintOpen]);
  var [bulkMode, setBulkMode] = useState(null); // 'tag' | 'untag' | 'assign' | 'hold' | 'note' | 'weight' | 'package' | 'confirmation'
  var [bulkPkgTpls, setBulkPkgTpls] = useState([]);
  useEffect(function() { if (bulkMode === 'package') shipFetchList('/shipping/package-templates', 'templates').then(setBulkPkgTpls); }, [bulkMode]);
  // Enabled services per carrier for the bulk "Set carrier & service…" dialog (Anthony 9-22) — the
  // same Settings > Integrations source and domestic/international flags the order panel uses.
  var [bulkSvcCatalog, setBulkSvcCatalog] = useState(null);
  useEffect(function() {
    if (bulkMode !== 'service' || bulkSvcCatalog) return;
    var KEY_MAP = { stamps_com: 'USPS', dhl_global_mail: 'dhl_global_mail', dhl_ecommerce_us: 'dhl_global_mail', ups: 'ups' };
    api('/shipping/carrier-services').then(function(r) {
      var cat = {};
      (r.carriers || []).forEach(function(c) {
        var k = KEY_MAP[c.carrier]; if (!k) return;
        cat[k] = (cat[k] || []).concat(c.services.filter(function(s) { return s.enabled; }).map(function(s) { return { name: s.name, domestic: !!s.domestic, international: !!s.international }; }));
      });
      setBulkSvcCatalog(cat);
    }).catch(function() { setBulkSvcCatalog({}); });
  }, [bulkMode]);
  // Which of the carrier's services fit the SELECTION: all-domestic or all-international filters
  // like the panel; a mixed selection is offered everything, with a note.
  var bulkSvcDest = function() {
    var DOMESTIC = ['US', 'PR', 'VI', 'GU', 'AS', 'MP'];
    var dom = 0, intl = 0;
    selRows.forEach(function(o) { if (DOMESTIC.indexOf(String(o.country || 'US').toUpperCase()) !== -1) dom++; else intl++; });
    return dom && intl ? 'mixed' : intl ? 'international' : 'domestic';
  };
  var bulkSvcNames = function(carrier) {
    var dest = bulkSvcDest();
    return ((bulkSvcCatalog && bulkSvcCatalog[carrier]) || [])
      .filter(function(s) { return dest === 'mixed' ? true : dest === 'domestic' ? (s.domestic || !s.international) : (s.international || !s.domestic); })
      .map(function(s) { return s.name; });
  };
  var [bulkVal, setBulkVal] = useState('');
  var [bulkVal2, setBulkVal2] = useState('');
  // Tag dialog checklist (Anthony 9-22: "it should already have the current tag checkmarked"):
  // tag -> 'on' (every selected order has it / will get it) | 'mixed' (some have it) | 'off'.
  var [bulkTags, setBulkTags] = useState({});
  // The docked panel's own loadDetail (Anthony 9-22: after a bulk carrier change + re-quote the open
  // panel still showed the old assignment and quote — the panel only refetched on a new order id)
  var dockReloadRef = useRef(null);
  var reloadDock = function() { try { if (dockReloadRef.current) dockReloadRef.current(); } catch (e) {} };

  var setF = function(patch) { setFilters(function(prev) { return Object.assign({}, prev, patch); }); };
  var loadSavedFilters = useCallback(function() {
    api('/shipping/filters').then(setSavedFilters).catch(function() {});
  }, []);
  useEffect(function() { loadSavedFilters(); }, [loadSavedFilters]);
  // Tag registry: tags created via "+ New" exist before any order carries them;
  // registry rows are {name, color} and feed the shared color map
  var [regTags, setRegTags] = useState([]);
  var applyRegTags = function(list) {
    var arr = (list || []).map(function(t) { return typeof t === 'string' ? { name: t } : t; });
    arr.forEach(function(t) { if (t.color) _shipTagColors[t.name] = t.color; });
    setRegTags(arr.map(function(t) { return t.name; }));
  };
  useEffect(function() { api('/shipping/tags').then(applyRegTags).catch(function() {}); }, []);
  var createTag = async function() {
    var name = await appPrompt('New tag name:');
    if (!name || !name.trim()) return;
    api('/shipping/tags', { method: 'POST', body: JSON.stringify({ name: name.trim() }) })
      .then(function(r) {
        if (r.color) _shipTagColors[r.name] = r.color;
        setRegTags(function(prev) { return prev.indexOf(r.name) === -1 ? prev.concat([r.name]) : prev; });
        showToast('Tag "' + r.name + '" created', 'success');
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  useEffect(function() {
    if (!sfOpen) return;
    var onClick = function(e) { if (sfRef.current && !sfRef.current.contains(e.target)) setSfOpen(false); };
    document.addEventListener('mousedown', onClick);
    return function() { document.removeEventListener('mousedown', onClick); };
  }, [sfOpen]);
  useEffect(function() {
    if (!oOpen) return;
    // The Country / State pick lists portal to <body>, outside oRef: a mousedown there closed this
    // menu and unmounted the list before its click landed (Anthony 9-22: "country filter isn't working")
    var onClick = function(e) { if (oRef.current && !oRef.current.contains(e.target) && !(e.target.closest && e.target.closest('.rps-menu'))) setOOpen(false); };
    document.addEventListener('mousedown', onClick);
    return function() { document.removeEventListener('mousedown', onClick); };
  }, [oOpen]);
  useEffect(function() {
    if (!odOpen) return;
    var onClick = function(e) { if (odRef.current && !odRef.current.contains(e.target)) setOdOpen(false); };
    document.addEventListener('mousedown', onClick);
    return function() { document.removeEventListener('mousedown', onClick); };
  }, [odOpen]);

  // Stale-response guard (Anthony 2026-09-25: two orders put on Hold Until "popped back up" for
  // ~10 s): a full load takes ~4 s to build server-side, so a hold / label / tag action that lands
  // meanwhile was undone when the older response arrived, until the next tick removed the rows
  // again. Every full load and every targeted refresh takes a sequence number; a full response
  // older than the latest one is dropped (a user-initiated load re-issues itself; a silent tick
  // just waits for the next one, ≤30 s).
  var loadSeqRef = useRef(0);
  // Bumped by full loads and batch actions (2026-09-26, review fix 7): the batch status record is
  // refetched on this counter, not on every `data` change (each targeted row refresh, rate poll
  // and panel edit used to fire one /batches/:id/status request).
  var [statusTick, setStatusTick] = useState(0);
  var load = useCallback(function(silent) {
    var seq = ++loadSeqRef.current;
    if (silent !== true) setLoading(true); // cached rows stay on screen while the refresh runs; the table shows "Refreshing…" meanwhile (a silent live refresh shows nothing)
    api('/shipping/queue?limit=5000&bucket=' + bucket + (batchId ? '&batch=' + batchId : ''))
      .then(function(r) {
        if (seq !== loadSeqRef.current) { if (silent !== true) load(true); return; } // superseded while in flight
        _shipQueueCache[qKey] = r;
        setData(r);
        setStatusTick(function(t) { return t + 1; }); // the batch record follows full loads only (review fix 7)
        syncRates(r.orders); // server-cached rate-shop results fill the Rate/Carrier columns
      })
      .catch(function(e) { if (silent !== true) showToast(e.message, 'error'); }).finally(function() { if (silent !== true) setLoading(false); });
  }, [bucket, batchId, syncRates]);
  useEffect(function() { load(); }, [load]);
  // Live view (Anthony 9-19): the Zentail mirror now catches a cancellation within ~30 s and the
  // table follows without a reload — a silent refetch every 30 s while the tab is visible and no
  // label run is in flight, plus one the moment the tab comes back into view.
  var liveRef = useRef({ buying: false });
  useEffect(function() {
    var tick = function() { if (document.hidden || liveRef.current.buying) return; load(true); };
    var iv = setInterval(tick, 30000);
    var onVis = function() { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return function() { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);
  // Targeted refresh (Anthony 9-22): after a bulk action the rows you touched are rebuilt server-side
  // and swapped in place — the whole queue (1,150 orders, ~1 s of SQL) no longer reloads per change.
  // Rows the server does not return for this bucket any more (held, cancelled, batched away) drop out.
  var refreshRows = useCallback(function(ids) {
    var list = (ids || []).map(Number).filter(function(x) { return x && !isNaN(x); });
    if (!list.length) return Promise.resolve();
    loadSeqRef.current++; // any full load still in flight is now stale: it predates this change
    return api('/shipping/queue?limit=' + list.length + '&bucket=' + bucket + (batchId ? '&batch=' + batchId : '') + '&ids=' + list.join(','))
      .then(function(r) {
        var byId = {}; (r.orders || []).forEach(function(o) { byId[o.id] = o; });
        var apply = function(prev) {
          if (!prev || !prev.orders) return prev;
          var next = prev.orders.filter(function(o) { return list.indexOf(o.id) === -1 || byId[o.id]; }).map(function(o) { return byId[o.id] || o; });
          return Object.assign({}, prev, { orders: next });
        };
        if (_shipQueueCache[qKey]) _shipQueueCache[qKey] = apply(_shipQueueCache[qKey]);
        setData(apply);
        syncRates(r.orders);
      })
      .catch(function() { load(true); });
  }, [bucket, batchId, load, syncRates]);
  // Rows landed by a rating job arrive in chunks the URL can carry; their spinners clear as they land
  var refreshLanded = useCallback(function(ids) {
    var chunks = []; for (var i = 0; i < ids.length; i += 150) chunks.push(ids.slice(i, i + 150));
    return chunks.reduce(function(p, c) { return p.then(function() { return refreshRows(c); }); }, Promise.resolve())
      .then(function() { markBusy(ids, false); });
  }, [refreshRows]);
  // Live row edits from the order panel / modal (HS code, tags, address flag). id = a row id or a
  // predicate over rows (a product-level customs save patches every queued order carrying the
  // SKU); patch = object or function of the row. The SWR cache gets the same patch — a remount
  // seeds from it, and the stale copy put the amber HS flag back until the refetch landed (9-18).
  var patchRows = useCallback(function(id, patch) {
    var hit = typeof id === 'function' ? id : function(o) { return o.id === id; };
    var apply = function(prev) {
      if (!prev || !prev.orders) return prev;
      return Object.assign({}, prev, { orders: prev.orders.map(function(o) {
        if (!hit(o)) return o;
        return Object.assign({}, o, typeof patch === 'function' ? patch(o) : patch);
      }) });
    };
    if (_shipQueueCache[qKey]) _shipQueueCache[qKey] = apply(_shipQueueCache[qKey]);
    setData(apply);
  }, [qKey]);
  var selCountLive = Object.keys(selected).filter(function(k) { return selected[k]; }).length;
  useEffect(function() {
    if (selCountLive >= 2) api('/shipping/batches').then(setOpenBatches).catch(function() {});
  }, [selCountLive >= 2]);

  // Rate-shop each selected order (3-wide pool; server-side cache makes
  // already-rated orders instant, so any selection size is fine)
  // Server-side bulk rating job: fire POST /rates/bulk then poll — the job
  // survives page navigation and paces itself for the carrier APIs (the old
  // client-side 3-worker loop ground for ~45 min on a full-queue selection).
  var ratePollRef = useRef(null);
  var rateCursorRef = useRef(0); // how many landed ids this page has already swapped in
  var pollBulkRates = function() {
    if (ratePollRef.current) clearInterval(ratePollRef.current);
    var inFlight = false;
    ratePollRef.current = setInterval(function() {
      if (inFlight) return; // a slow poll must not read the cursor twice
      inFlight = true;
      // Each poll asks for the orders finished since the last one and swaps just those rows in:
      // the number replaces the spinner as it comes in, the way ShipStation did it (Anthony 9-22)
      api('/shipping/rates/bulk?after=' + rateCursorRef.current)
        .then(function(r) {
          var j = r.job;
          if (!j) return;
          setRateProg({ done: j.done, total: j.total });
          var landed = j.landed || [];
          rateCursorRef.current += landed.length;
          var p = landed.length ? refreshLanded(landed) : Promise.resolve();
          if (!j.running) {
            clearInterval(ratePollRef.current); ratePollRef.current = null;
            return p.then(function() {
              // whatever still spins was never reported to this page (a failed poll): refresh it
              var left = Object.keys(rateBusyRef.current).map(Number);
              return left.length ? refreshLanded(left) : null;
            }).then(function() {
              setRating(false); setRateProg(null); reloadDock();
              showToast('Rated ' + j.ok + ' of ' + j.total + ' orders' + (j.fail ? ' (' + j.fail + ' failed)' : '') + (j.cancelled ? ' — cancelled' : ''), j.fail > j.ok ? 'error' : 'success');
            });
          }
          return p;
        })
        .catch(function() {})
        .finally(function() { inFlight = false; });
    }, 1500);
  };
  useEffect(function() {
    // Reattach to a job already running (page reload / navigation return)
    api('/shipping/rates/bulk').then(function(r) {
      if (r.job && r.job.running) { rateCursorRef.current = r.job.landed_total || 0; setRating(true); setRateProg({ done: r.job.done, total: r.job.total }); pollBulkRates(); }
    }).catch(function() {});
    return function() { if (ratePollRef.current) clearInterval(ratePollRef.current); };
  }, []);
  // rowsArg: an explicit row set (the batch panel rates the whole batch, 10-09); a click event is not one
  var getRates = function(rowsArg) {
    if (rating) return;
    var rows = Array.isArray(rowsArg) ? rowsArg : selRows;
    if (!rows.length) return;
    var ids = rows.map(function(o) { return o.id; });
    setRating(true);
    setRateProg({ done: 0, total: rows.length });
    // The selected rows' quotes clear to a spinner at once; each number lands as the job reports it
    var idSet = {}; ids.forEach(function(id) { idSet[id] = true; });
    markBusy(ids, true);
    setRateTotals(function(p) { var n = Object.assign({}, p); ids.forEach(function(id) { delete n[id]; }); return n; });
    patchRows(function(o) { return !!idSet[o.id]; }, { rate: null, rate_error: null, rate_error_kind: null });
    rateCursorRef.current = 0;
    api('/shipping/rates/bulk', { method: 'POST', body: JSON.stringify({ order_ids: ids }) })
      .then(function() { pollBulkRates(); })
      .catch(function(e) {
        markBusy(ids, false); load(true); // the refused selection gets its numbers back
        setRating(false); setRateProg(null); reloadDock();
        showToast(e.message, 'error');
      });
  };

  var addBatchBusyRef = useRef(false);
  var addToBatch = function(existingId, note) {
    setBatchMenuOpen(false);
    // A new batch asks for its note first (carrier + packer, ShipStation habit — 9-17)
    if (!existingId && note === undefined) { setNewBatchDlg({ note: '' }); return; }
    // one request at a time (sweep 2026-09-27 Lows): Enter pressed twice, or Enter then the button,
    // made two batches — the second took the orders away from the first
    if (addBatchBusyRef.current) return;
    // selRows is a same-scope var assigned later in this render — live at click time
    var ids = selRows.map(function(o) { return o.id; });
    addBatchBusyRef.current = true;
    api('/shipping/batches', { method: 'POST', body: JSON.stringify({ order_ids: ids, batch_id: existingId || undefined, note: note || undefined }) })
      .finally(function() { addBatchBusyRef.current = false; })
      .then(function(r) {
        setNewBatchDlg(null);
        var other = Array.isArray(r.in_other_batch) ? r.in_other_batch : [];
        var otherNums = other.map(function(x) { return x.batch_number; }).filter(function(v, i, a) { return a.indexOf(v) === i; }).join(', ');
        var labelled = r.skipped - other.length;
        showToast('Added ' + r.added + ' order' + (r.added === 1 ? '' : 's') + ' to Batch ' + r.batch_number
          + (labelled > 0 ? ' · ' + labelled + ' skipped (already labelled)' : '')
          + (other.length ? ' · ' + other.length + ' left in another open batch (' + otherNums + ')' : ''), r.added ? 'success' : 'error');
        setSelected({}); // the rows have moved on; a lingering selection is how orders got batched twice
        refreshRows(ids); // just those rows, not the whole queue (Anthony 9-22: "took a few seconds to show")
        setStatusTick(function(t) { return t + 1; }); // a batch action: the batch record re-reads
        try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var orders = (data && data.orders) || [];
  // A state reads as its code whatever Zentail sent — "Texas" on some channels, "TX" on others, 98
  // distinct values (Anthony 9-22: "state filter has duplicate states"). A country without its own
  // list (PR, VI, GU) resolves against the US list, which carries those codes.
  var shipStateKey = function(state, country) {
    var s = String(state || '').trim().toUpperCase(); if (!s) return '';
    var list = (window.stateListFor && (window.stateListFor(country) || window.stateListFor('US'))) || [];
    for (var i = 0; i < list.length; i++) { if (list[i][0] === s || String(list[i][1]).toUpperCase() === s) return list[i][0]; }
    return s;
  };
  var shipCountryKey = function(c) { return String(c || 'US').trim().toUpperCase(); };
  var allTags = {};
  var allChannels = {};
  var allAssignees = {};
  var allCarriers = {};
  var allCountries = {};
  var allStates = {};
  var allReqSvcs = {};
  orders.forEach(function(o) {
    (o.tags || []).forEach(function(t) { allTags[t] = (allTags[t] || 0) + 1; });
    var ch = o.channel || '?';
    allChannels[ch] = (allChannels[ch] || 0) + 1;
    var a = o.assignee || 'Unassigned';
    allAssignees[a] = (allAssignees[a] || 0) + 1;
    if (o.carrier) allCarriers[shipCarrierLabel(o.carrier)] = 1;
    allCountries[shipCountryKey(o.country)] = 1;
    var sk = shipStateKey(o.state, o.country); if (sk) allStates[sk] = 1;
    if (o.requested_service) allReqSvcs[o.requested_service] = 1;
  });
  regTags.forEach(function(t) { if (!(t in allTags)) allTags[t] = 0; });
  var SHIP_DOMESTIC = ['US', 'PR', 'VI', 'GU', 'AS', 'MP', ''];
  var isDomestic = function(o) { return SHIP_DOMESTIC.indexOf(String(o.country || 'US').toUpperCase()) !== -1; };
  // A customs destination: international, or a customs territory / military mail (the label route's
  // DHL_CUSTOMS_TERRITORIES set) — HS codes are required there since 2026-09-28 (Anthony, 4256409)
  var SHIP_CUSTOMS_TERR = { GU: 1, AS: 1, MP: 1, AA: 1, AE: 1, AP: 1 };
  var needsCustoms = function(o) {
    return !isDomestic(o) || !!SHIP_CUSTOMS_TERR[String(o.country || '').toUpperCase()] || !!SHIP_CUSTOMS_TERR[String(o.state || '').toUpperCase()];
  };
  // "Other" menu state (guarded: saved filters from before the menu lack .other)
  var fo = Object.assign({}, SHIP_EMPTY_OTHER, filters.other || {});
  var otherActive = Object.keys(SHIP_EMPTY_OTHER).filter(function(k) {
    if (/Op$/.test(k)) return false; // an operator only matters with a value
    var v = fo[k];
    return Array.isArray(v) ? v.length > 0 : v !== '';
  }).length;
  var setOther = function(patch) { setF({ other: Object.assign({}, fo, patch) }); };
  // Viewer-local civil dates (review M7 #104): the UTC instant used to make "Today" show tomorrow's
  // orders after 8 PM ET. ship_by is a civil date, so compare civil to civil.
  var todayStr = new Date().toLocaleDateString('en-CA');
  var tomorrowStr = new Date(Date.now() + 86400000).toLocaleDateString('en-CA');
  // The saved-filter matcher is shared with the server (app-ship-filter.js, 2026-09-23): an auto-batch
  // rule can carry a saved filter, and the sweep must read it exactly the way this page does.
  var filterEnv = { now: new Date(), today: todayStr, tomorrow: tomorrowStr, stateKey: shipStateKey, carrierLabel: shipCarrierLabel, rateTotals: rateTotals };
  // Memoized (2026-09-26, queue performance): the ~900-row filter used to re-run on every render —
  // each hover-driven state change, each 1.5 s rating poll. It re-runs only when an input changes.
  var filteredQueue = useMemo(function() { return orders.filter(function(o) {
    // Selected rows stay visible even when an edit stops them matching the
    // active filters (e.g. filling an HS code under the "Missing HS" filter
    // was closing the docked panel mid-review, 8-30). Filter changes clear
    // the selection, so pinning never leaks across filter switches.
    if (selected[o.id]) return true;
    if (q) {
      var ql = q.toLowerCase();
      var qHit = String(o.order_number || '').toLowerCase().indexOf(ql) !== -1 ||
        String(o.channel_order_id || '').toLowerCase().indexOf(ql) !== -1 ||
        // Marketplace reference number + Custom Field 1-3 (channel label, typed values) — what
        // ShipStation's search found for the warehouse (Anthony 9-18, Faire YUZCS3TMEK)
        String(o.channel_ref || '').toLowerCase().indexOf(ql) !== -1 ||
        String(o.custom1 || '').toLowerCase().indexOf(ql) !== -1 ||
        String(o.custom2 || '').toLowerCase().indexOf(ql) !== -1 ||
        String(o.custom3 || '').toLowerCase().indexOf(ql) !== -1 ||
        String(o.customer || '').toLowerCase().indexOf(ql) !== -1 ||
        String(o.batch_number || '').toLowerCase().indexOf(ql) !== -1 ||
        (o.lines || []).some(function(l) {
          return String(l.sku || '').toLowerCase().indexOf(ql) !== -1 ||
            String(l.title || '').toLowerCase().indexOf(ql) !== -1;
        });
      if (!qHit) return false;
    }
    return shipFilterMatches(o, filters, filterEnv);
  }); }, [orders, selected, q, filters, rateTotals, todayStr]);
  // Search matches outside this bucket (see extraRows above). Deps include data + filteredQueue
  // (2026-09-26, review fix 21): the effect used to read them without listing them, so rows loaded
  // (or a match appearing in the list) after the search did not re-run it.
  useEffect(function() {
    var inBucket = {}; ((data && data.orders) || []).forEach(function(o) { inBucket[o.id] = true; });
    var outside = histHits.filter(function(hh) { return !inBucket[hh.id]; });
    // The list already has a match for the term: nothing else is shown. And nothing from outside
    // shows before the list has loaded ("Order 174445 has shipped" appeared first, Anthony 9-22).
    if (!q || loading || !data || !outside.length || (filteredQueue || []).length) { setExtraRows([]); setExtraPending(false); return; }
    var dead = false;
    setExtraPending(true);
    var statusOf = {}; outside.forEach(function(hh) { statusOf[hh.id] = String(hh.status || '').toUpperCase(); });
    var whereLabel = function(o) {
      var st = statusOf[o.id] || '';
      if (st === 'PENDING' || !st) return o.hold ? 'ON HOLD' : o.batch_number ? 'BATCH ' + o.batch_number : batchId ? 'NOT IN BATCH' : 'ELSEWHERE';
      return st.replace(/_/g, ' ');
    };
    // This bucket first: an order newer than the page's data is simply a row the page had not
    // loaded yet, not an outsider (4246636 read "ELSEWHERE" for that reason, 9-22). Whatever this
    // bucket does not return comes from anywhere, badged.
    var ids = outside.map(function(hh) { return hh.id; });
    api('/shipping/queue?limit=' + ids.length + '&bucket=' + bucket + (batchId ? '&batch=' + batchId : '') + '&ids=' + ids.join(','))
      .then(function(r1) {
        var rows = r1.orders || [];
        var got = {}; rows.forEach(function(o) { got[o.id] = true; });
        var rest = ids.filter(function(id) { return !got[id]; });
        if (!rest.length) return rows;
        return api('/shipping/queue?limit=' + rest.length + '&bucket=all&ids=' + rest.join(','))
          .then(function(r2) { return rows.concat((r2.orders || []).map(function(o) { return Object.assign({}, o, { _outside: whereLabel(o) }); })); });
      })
      .then(function(rows) { if (!dead) { setExtraRows(rows); setExtraPending(false); } })
      .catch(function() { if (!dead) { setExtraRows([]); setExtraPending(false); } });
    return function() { dead = true; };
  }, [histHits, q, loading, data, filteredQueue]);
  var holdCount = orders.filter(function(o) { return o.hold; }).length;
  // the queue's own matches decide the shipped-order jump; matches from outside then join the list
  var filtered = filteredQueue;
  if (q && extraRows.length) {
    var seenIds = {}; filtered.forEach(function(o) { seenIds[o.id] = true; });
    filtered = filtered.concat(extraRows.filter(function(o) { return !seenIds[o.id]; }));
  }
  var filtersActive = JSON.stringify(filters) !== JSON.stringify(SHIP_EMPTY_FILTERS);

  // ShipStation-style age color: fresh < 24h, amber < 48h, red beyond
  var ageOf = function(o) {
    var hrs = (Date.now() - new Date(o.order_date).getTime()) / 3600000;
    var label = hrs < 1 ? Math.max(1, Math.round(hrs * 60)) + ' min' : Math.round(hrs) + ' hr'; // hours all the way, never days (Anthony 9-22)
    var color = hrs < 24 ? C.success : hrs < 48 ? C.warning : C.danger;
    return { label: label, color: color, hrs: hrs };
  };


  // Phase L5: bulk label purchase. Chunks of 5 keep each request well under
  // nginx's 60s ceiling (each label = carrier call + Zentail post, ~2-3s);
  // per-order failures ride the summary, never sink the batch.
  var [buyingLabels, setBuyingLabels] = useState(null); // {done, total}
  useEffect(function() { liveRef.current.buying = !!buyingLabels; }, [buyingLabels]);
  var [labelSummary, setLabelSummary] = useState(null); // {bought, cost, sids, errors, printed}
  // Escape closes the label summary (sweep 2026-09-27 Lows: only its X / Done did)
  useEffect(function() {
    if (!labelSummary) return;
    var onKey = function(e) { if (e.key === 'Escape') setLabelSummary(null); };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [!!labelSummary]);
  // Batch status dialog: { id, live: {done,total} | null } — used when the run is on a batch
  // The batch's own record (number, status, label counts): the subtitle shows the real batch
  // number even when the shell's open-batches rail does not know it, and Dissolve is offered
  // only while no label has been bought (Anthony 9-17).
  var [batchInfo, setBatchInfo] = useState(null);
  useEffect(function() {
    if (!batchId) return;
    var dead = false;
    api('/shipping/batches/' + batchId + '/status').then(function(r) { if (!dead) setBatchInfo(r); }).catch(function() {});
    return function() { dead = true; };
  }, [batchId, statusTick]);
  var bnum = (batchInfo && batchInfo.batch && batchInfo.batch.batch_number) || batchNumber || batchId;
  var canDissolve = !!(batchInfo && batchInfo.batch && batchInfo.batch.status === 'open' && !(parseInt(batchInfo.counts && batchInfo.counts.labels_created, 10) > 0));
  var dissolveBatch = async function() {
    if (!(await appConfirm('Dissolve Batch ' + bnum + '? Orders return to the queue (nothing is cancelled).', { danger: true, okLabel: 'Dissolve' }))) return;
    api('/shipping/batches/' + batchId, { method: 'DELETE' })
      .then(function() { try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {} window.location.href = '/'; })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var [batchModal, setBatchModal] = useState(function() {
    try { return (batchId && new URLSearchParams(window.location.search).get('bs') === '1') ? { id: batchId, live: null } : null; } catch (e) { return null; }
  });
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      if (batchModal && batchId) sp.set('bs', '1'); else sp.delete('bs');
      var qs = sp.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }, [!!batchModal]);
  var [newBatchDlg, setNewBatchDlg] = useState(null); // { note } while creating a new batch
  // Set the moment the user confirms (2026-09-26, review fix 6): buyingLabels only turned on after
  // POST /batches answered, so a second click in that window created a second batch for the same orders.
  var labelRunRef = useRef(false);
  var bulkCreateLabels = async function(rows, print, authorize) {
    if (buyingLabels || labelRunRef.current) return;
    var eligible = rows.filter(function(o) { return !o.hold; });
    if (!eligible.length) { showToast('No eligible orders (all held)', 'error'); return; }
    if (eligible.length > 300) { showToast('Max 300 labels per batch', 'error'); return; }
    // Every multi-order run is a batch (ShipStation parity, Anthony 9-17): on a batch page it is
    // that batch; from the orders queue a batch is created first, so the run gets the status
    // dialog with its live count, failure list, retry and the batch-stamped print. A single
    // order keeps the plain summary.
    var runBatchId = batchId || null;
    var makeBatch = !runBatchId && eligible.length > 1;
    if (authorize !== true && !(await appConfirm('Buy ' + eligible.length + ' shipping label' + (eligible.length === 1 ? '' : 's') + '?\n\nPostage is charged immediately and each order is marked shipped. Orders with problems are skipped and reported.', { okLabel: 'Buy labels' }))) return;
    labelRunRef.current = true; // before any request: a second click is ignored from here on
    var ids = eligible.map(function(o) { return o.id; });
    if (ids.length === 1 && !runBatchId) {
      // one order, no batch: a direct purchase and the plain summary
      setBuyingLabels({ done: 0, total: 1 });
      api('/shipping/create-label', { method: 'POST', body: JSON.stringify({ order_ids: ids, authorize_usps: authorize === true }) })
        .then(function(r) { return { results: r.results || [], errors: r.errors || [], halted: r.halted || null, needsAuth: !!r.needs_usps_authorization }; },
              function(e) { return { results: [], errors: [{ id: ids[0], error: String(e.message || e) }], halted: null, needsAuth: false }; })
        .then(async function(r) {
          labelRunRef.current = false;
          setBuyingLabels(null);
          refreshRows(ids);
          var sids = r.results.map(function(x) { return x.shipment_id; }).filter(Boolean);
          var cost = r.results.reduce(function(a, x) { return a + (parseFloat(x.cost) || 0); }, 0);
          if (print && sids.length) shipPrintApiPdf('/shipping/label-files?ids=' + sids.join(','));
          setLabelSummary({ bought: r.results.length, cost: cost, sids: sids, errors: r.errors, printed: !!(print && sids.length) });
          if (r.halted) {
            // ask right here and re-run with the permission
            if (r.needsAuth && (await appConfirm(r.halted, { okLabel: 'Authorize' }))) { setSelected({}); bulkCreateLabels(rows, print, true); return; }
            showToast('Run stopped: ' + r.halted, 'error');
          }
          setSelected({});
          load();
          try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
        });
      return;
    }
    // Two or more orders: the run is a SERVER job (Anthony 2026-09-23, to-do 3 + 4). The batch is
    // created and its dialog opens at once; the job checks Zentail for changes, drops the orders
    // that are no longer pending, then buys the labels (the first alone, then four at a time).
    // Leaving this page no longer stops the run — the batch dialog reattaches to it anywhere.
    // The run buys exactly the orders the batch took: an order another packer batched since this
    // page loaded stays in that batch (the server leaves it there and reports it, 2026-09-28)
    var runIds = ids;
    var start = makeBatch
      ? api('/shipping/batches', { method: 'POST', body: JSON.stringify({ order_ids: ids }) }).then(function(r) {
          runBatchId = r.batch_id;
          if (Array.isArray(r.ids) && r.ids.length) runIds = r.ids;
          var other = Array.isArray(r.in_other_batch) ? r.in_other_batch : [];
          if (other.length) {
            var nums = other.map(function(x) { return x.batch_number; }).filter(function(v, i, a) { return a.indexOf(v) === i; }).join(', ');
            showToast(other.length + ' order' + (other.length === 1 ? '' : 's') + ' left in another open batch (' + nums + ') — not in this run', 'error');
          }
        })
      : Promise.resolve(null);
    start.then(function() {
      setBatchModal({ id: runBatchId, live: { done: 0, total: runIds.length, phase: 'checking' } });
      setBuyingLabels({ done: 0, total: runIds.length, phase: 'checking' });
      return api('/shipping/label-jobs', { method: 'POST', body: JSON.stringify({ order_ids: runIds, batch_id: runBatchId, authorize_usps: authorize === true, print: !!print }) });
    }).then(function(r) {
      labelRunRef.current = false; // buyingLabels (set above) guards the run from here
      followLabelJob(r.job, runBatchId, print);
    }).catch(function(e) {
      labelRunRef.current = false;
      setBuyingLabels(null);
      if (runBatchId) setBatchModal({ id: runBatchId, live: null });
      showToast((runBatchId ? 'Could not start the label run: ' : 'Could not create the batch: ') + String(e.message || e), 'error');
    });
  };
  // Follow a label job until it ends: labelled rows leave the list as each label lands (?after=
  // cursor), the batch dialog's ring and phase follow the job row, and the print + summary happen
  // at the end. A poll that fails is retried; the job itself runs on the server regardless.
  // Stops on unmount and after a 4xx / 10 failures in a row (2026-09-26, review fix 4 — it used to
  // retry every 3 s forever, even after the page was left).
  var followStoppedRef = useRef(false);
  useEffect(function() { followStoppedRef.current = false; return function() { followStoppedRef.current = true; }; }, []);
  var followLabelJob = function(job, runBatchId, print) {
    var cursor = 0;
    var fails = 0;
    var tick = function() {
      if (followStoppedRef.current) return;
      api('/shipping/label-jobs/' + job.id + '?after=' + cursor).then(function(r) {
        if (followStoppedRef.current) return;
        fails = 0;
        var j = r && r.job; if (!j) { setBuyingLabels(null); return; }
        var landed = j.landed || [];
        if (landed.length) { cursor += landed.length; refreshRows(landed); }
        var isLive = j.status === 'checking' || j.status === 'running';
        var live = isLive ? { done: j.done, total: j.total, phase: j.status === 'checking' ? 'checking' : undefined } : null;
        setBuyingLabels(live);
        setBatchModal(function(m) {
          if (!m || m.id !== runBatchId) return m;
          return Object.assign({}, m, isLive ? { live: live, jobId: j.id } : { live: null, jobId: j.id, done: Date.now(), halt: j.halted || null, needsAuth: !!j.needs_usps_authorization });
        });
        if (isLive) { setTimeout(tick, 500); return; } // 500 ms, was 1.5 s (Anthony 2026-09-25: rows leave the list sooner as each label lands)
        var gone = j.removed || [];
        if (gone.length) showToast(gone.length + ' order' + (gone.length === 1 ? '' : 's') + ' changed in Zentail since the last sync (' + gone.map(function(g) { return g.order_number + ': ' + g.status; }).join(', ') + ') — skipped', 'error');
        if (j.status === 'failed') showToast(j.halted || 'The label run failed', 'error');
        var sids = (j.results || []).map(function(x) { return x.shipment_id; }).filter(Boolean);
        if (print && sids.length) shipPrintApiPdf('/shipping/label-files?ids=' + sids.join(',') + (runBatchId ? '&batch=' + runBatchId : ''));
        // every order changed in Zentail: nothing was bought and the job dissolved the empty batch
        if (gone.length && gone.length >= j.total && !sids.length) setBatchModal(null);
        setSelected({});
        load();
        try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
      }).catch(function(e) {
        if (followStoppedRef.current) return;
        fails++;
        if (shipPollGiveUp(e, fails)) {
          setBuyingLabels(null);
          setBatchModal(function(m) { return m && m.id === runBatchId ? Object.assign({}, m, { live: null }) : m; });
          showToast('Stopped following the label run (' + String((e && e.message) || e) + ') — it continues on the server; reopen the batch to see its status', 'error');
          return;
        }
        setTimeout(tick, 3000);
      });
    };
    tick();
  };

  // Mark as Shipped modal: null | { orders: [{id, order_number, customer}] }
  var [markShipModal, setMarkShipModal] = useState(null);
  // Full order modal (order-number click). Deep-linked via ?o= so a refresh
  // or shared link restores the open modal — same replaceState pattern as ?f/?q.
  var [orderNavIds, setOrderNavIds] = useState(null); // ids Previous/Next walk instead of the queue (batch dialog failures)
  var [openOrder, setOpenOrder] = useState(function() {
    try { return parseInt(new URLSearchParams(window.location.search).get('o'), 10) || null; } catch (e) { return null; }
  });
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      if (openOrder != null) sp.set('o', String(openOrder)); else sp.delete('o');
      var qs = sp.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }, [openOrder]);
  // The batch dialog re-reads its record once the order modal on top of it closes (edits autosave)
  useEffect(function() {
    if (openOrder == null) setBatchModal(function(m) { return m && !m.live ? Object.assign({}, m, { tick: (m.tick || 0) + 1 }) : m; });
  }, [openOrder]);
  var openMarkShipped = function(rowsSel) {
    if (!rowsSel || !rowsSel.length) return;
    if (rowsSel.length > 200) { showToast('Max 200 orders per Mark-as-Shipped batch', 'error'); return; }
    setMarkShipModal({ orders: rowsSel.map(function(o) { return { id: o.id, order_number: o.order_number, customer: o.customer || '' }; }) });
  };

  // Manual-order create/edit modal: {} = new, { edit: detailPayload } = edit
  var [manualModal, setManualModal] = useState(null);
  // The count is the checked rows the page still SHOWS (sweep 2026-09-27 M16): an order that left the
  // list (labelled, cancelled, filtered away) stayed in `selected` and the menus said "Get rate (12)"
  // over 10 rows — every action already works on selRows
  var selRows = filtered.filter(function(o) { return selected[o.id]; });
  var selCount = selRows.length;
  // Batch page with nothing checked (Anthony 10-09): the dock shows the batch panel over the batch's
  // OPEN orders (no label yet) — the multi-select panel's actions for the whole batch — instead of
  // the "select an order" placeholder. One checked row = that order's panel, two or more = the
  // multi-select panel over the selection, as before.
  var batchRows = batchId && selCount === 0 ? filtered.filter(function(o) { return !o.has_shipment && !o.cancelled_here; }) : [];
  var dockCount = selCount || batchRows.length;
  var selectRows = function(rows) { var m = {}; rows.forEach(function(o) { m[o.id] = true; }); setSelected(m); };
  // narrow: nothing checked → the details overlay closes (a later checkbox tap must not pop it open)
  useEffect(function() { if (selCount === 0) setNarrowDockOpen(false); }, [selCount]);
  // The details column, expanded (docked) or collapsed to a strip with the checked-count badge
  var shipDock = function(dockContent) {
    var narrowMode = shipNarrowDockMode(viewport.isNarrow, narrowDockOpen, dockCount);
    if (narrowMode === 'overlay') {
      // Full-screen details over the queue; dialogs (z 1200+) still open above it
      return h('div', { role: 'dialog', 'aria-modal': true, 'aria-label': 'Order Details', className: 'ship-narrow-dock',
        style: { position: 'fixed', inset: 0, zIndex: 1000, background: C.bg, display: 'flex', flexDirection: 'column' } },
        h('div', { style: { height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px 0 4px', background: C.surface, borderBottom: '1px solid ' + C.border, boxSizing: 'border-box' } },
          h('button', { type: 'button', 'aria-label': 'Back', onClick: function() { setNarrowDockOpen(false); },
            style: { display: 'inline-flex', alignItems: 'center', gap: 6, height: 40, padding: '0 12px', background: 'transparent', border: 'none', borderRadius: 8, color: C.accent, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' } },
            h(LIcon, { name: 'arrow-left', size: 18 }), 'Back'),
          h('div', { style: { flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } },
            selCount === 1 ? ('Order ' + ((selRows[0] && selRows[0].order_number) || '')) : selCount ? selCount.toLocaleString() + ' Selected' : 'Batch ' + bnum)),
        h('div', { style: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 8, boxSizing: 'border-box' } }, dockContent));
    }
    if (narrowMode === 'button') {
      // Checked rows but the overlay is closed: a floating button brings the details back
      return h('button', { type: 'button', onClick: function() { setNarrowDockOpen(true); },
        style: { position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 900, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 18px', height: 44, borderRadius: 999, border: 'none', background: C.accent, color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.35)' } },
        h(LIcon, { name: 'panel-right-open', size: 16 }), selCount === 1 ? 'Order Details' : selCount ? 'View ' + selCount.toLocaleString() + ' Selected' : 'Batch ' + bnum);
    }
    if (narrowMode === 'none') return null;
    var iconBtn = function(icon, title, size) {
      return h('button', { className: 'phov', title: title, onClick: togglePanel,
        style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: size ? 8 : 6, width: size || 26, height: size || 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.textMuted, padding: 0, flexShrink: 0 } },
        h(LIcon, { name: icon, size: size ? 15 : 13 }));
    };
    if (panelExpanded) {
      // Same box as the table card (no strip above); the toggle sits inside, top-right. The
      // single-order and multi-select headers leave room for it via their right padding.
      return h('div', { style: { width: 446, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' } },
        dockContent,
        // right: 22 keeps it clear of the details panel's own scrollbar (live 9-27: it sat on the ▲ arrow)
        h('div', { style: { position: 'absolute', top: 10, right: 22, zIndex: 8 } },
          iconBtn('panel-right-close', 'Collapse the details panel')));
    }
    return h('div', { style: { width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 } },
      iconBtn('panel-right-open', selCount ? 'Expand the details panel (' + selCount + ' checked)' : 'Expand the details panel', 32),
      selCount > 0 && h('span', { style: { fontSize: 10.5, fontWeight: 700, minWidth: 18, padding: '1px 5px', borderRadius: 999, background: C.accent, color: '#fff', textAlign: 'center', boxSizing: 'border-box' } }, selCount));
  };
  var pageRows = pageRef.current;
  var pageAllSelected = pageRows.length > 0 && pageRows.every(function(o) { return selected[o.id]; });
  // Shift+click range selection anchored on the last clicked row's ID (an
  // index anchor went stale whenever the queue re-rendered between clicks —
  // streaming rate results / count polls shifted the arrays and ranges ended
  // one row short). Both endpoints resolve in the CURRENT sort order at click
  // time (onSortedData keeps sortedRef in sync).
  var sortedRef = useRef([]);
  var lastSelIdRef = useRef(null);
  var toggleRow = function(o, e) {
    var rows = (sortedRef.current && sortedRef.current.length ? sortedRef.current : filtered);
    if (e && e.shiftKey && lastSelIdRef.current != null) {
      var a = rows.findIndex(function(x) { return x.id === lastSelIdRef.current; });
      var b = rows.findIndex(function(x) { return x.id === o.id; });
      if (a !== -1 && b !== -1) {
        var lo = Math.min(a, b), hi = Math.max(a, b);
        setSelected(function(prev) {
          var n = Object.assign({}, prev);
          for (var i = lo; i <= hi; i++) { if (rows[i]) n[rows[i].id] = true; }
          return n;
        });
        lastSelIdRef.current = o.id;
        return;
      }
    }
    setSelected(function(prev) { var n = Object.assign({}, prev); if (n[o.id]) delete n[o.id]; else n[o.id] = true; return n; });
    lastSelIdRef.current = o.id;
  };

  var lastActionIdsRef = useRef([]); // the rows a cancel / restore touched, for the targeted refresh
  var afterCancelChange = function(msg, kind) {
    showToast(msg, kind || 'success');
    setBulkMode(null); setBulkVal(''); setBulkVal2(''); setBulkTags({}); setSelected({});
    if (lastActionIdsRef.current.length) refreshRows(lastActionIdsRef.current); else load();
    try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
  };
  var cancelSelected = function(reason) {
    var ids = selRows.map(function(o) { return o.id; });
    if (!ids.length) { showToast('Select some orders first', 'error'); return; }
    lastActionIdsRef.current = ids;
    api('/shipping/orders/cancel', { method: 'POST', body: JSON.stringify({ order_ids: ids, reason: reason }) })
      .then(function(r) {
        var errs = (r.errors || []);
        afterCancelChange('Cancelled here — ' + r.updated + ' order' + (r.updated === 1 ? '' : 's') + (errs.length ? '; ' + errs.length + ' skipped: ' + errs.slice(0, 3).map(function(e) { return (e.order_number || e.id) + ' (' + e.error + ')'; }).join(', ') + (errs.length > 3 ? '…' : '') : ''), errs.length && !r.updated ? 'error' : 'success');
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var restoreSelected = async function() {
    // a cancelled MANUAL order (ours alone) restores too (9-27 Lows)
    var ids = selRows.filter(function(o) { return o.cancelled_here || (o.is_manual && bucket === 'cancelled'); }).map(function(o) { return o.id; });
    if (!ids.length) { showToast('Select orders that were cancelled here (Zentail cancellations cannot be restored from this app)', 'error'); return; }
    if (!(await appConfirm('Restore ' + ids.length + ' order' + (ids.length === 1 ? '' : 's') + ' to the queue?', { danger: true, okLabel: 'Restore' }))) return;
    lastActionIdsRef.current = ids;
    api('/shipping/orders/restore', { method: 'POST', body: JSON.stringify({ order_ids: ids }) })
      .then(function(r) { afterCancelChange('Restored — ' + r.updated + ' order' + (r.updated === 1 ? '' : 's')); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var applyBulkMeta = function(body, label) {
    var ids = selRows.map(function(o) { return o.id; });
    if (!ids.length) { showToast('Select some orders first', 'error'); return; }
    body.order_ids = ids;
    api('/shipping/orders/meta', { method: 'POST', body: JSON.stringify(body) })
      .then(function(r) {
        showToast(label + ' — ' + r.updated + ' orders', 'success');
        setBulkMode(null); setBulkVal(''); setBulkVal2(''); setBulkTags({}); refreshRows(ids); reloadDock();
        // Hold/release moves orders between sidebar buckets — refresh the nav counts now
        try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var cols = [
    // sticky: pins the checkbox column left and exempts it from drag-reorder
    { key: '_sel', label: '', locked: true, noFilter: true, noSort: true, sticky: true,
      headerRender: function() {
        // Page-scoped: checks this page only; the banner offers the full filtered view
        return h(ShipCheckbox, {
          checked: pageAllSelected,
          title: 'Select this page',
          onChange: function() {
            // Unchecking clears the WHOLE selection, not just this page —
            // after "select all N in this filter", page-only removal stranded
            // invisible off-page selections with no way to clear them (8-12)
            if (pageAllSelected) { setSelected({}); return; }
            var n = Object.assign({}, selected);
            pageRows.forEach(function(o) { n[o.id] = true; });
            setSelected(n);
          },
        });
      },
      // stopClick + a full-cell hit area: a click landing NEXT to the box (the
      // cell padding / between boxes) used to bubble to the row's single-select
      // and wipe a built-up multi-selection (8-12). Anywhere in this column
      // now toggles, and never falls through to the row.
      stopClick: true,
      render: function(o) {
        return h('div', {
          onClick: function(e) { e.stopPropagation(); toggleRow(o, e); },
          onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
          // Mirror the cell padding exactly (3px 8px) so the box doesn't shift —
          // a uniform -8 margin misaligned it against the header checkbox
          style: { display: 'flex', alignItems: 'center', margin: '-3px -8px', padding: '3px 8px', cursor: 'pointer' }
        }, h(ShipCheckbox, {
          checked: !!selected[o.id],
          onChange: function(e) { toggleRow(o, e); },
        }));
      } },
    { key: 'order_number', label: 'Order #', sortKey: 'order_number', render: function(o) {
        // Order number opens the full order modal; the row click keeps
        // single-selecting into the docked panel (user spec 8-29)
        return h('span', {
          className: 'phov',
          onClick: function(e) { e.stopPropagation(); setOpenOrder(o.id); },
          title: 'Open full order view',
          style: { fontWeight: 500, color: C.accent, cursor: 'pointer', padding: '1px 3px', margin: '-1px -3px', borderRadius: 4 }
        }, o.order_number,
          // brought in by the search from outside this list (Anthony 9-22)
          o._outside ? h('span', { title: 'Not in this list — found by the search', style: { marginLeft: 6, fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: C.textMuted + '22', color: C.textMuted, whiteSpace: 'nowrap' } }, o._outside) : null,
          // cancelled in the shipping app only (Anthony 9-19): Zentail still says pending
          o.cancelled_here ? h('span', { title: 'Cancelled here' + (o.cancelled_by ? ' by ' + o.cancelled_by : '') + (o.cancel_reason ? ': ' + o.cancel_reason : '') + ' — Zentail still shows it pending', style: { marginLeft: 6, fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: C.danger + '18', color: C.danger, whiteSpace: 'nowrap' } }, 'CANCELLED HERE') : null);
      } },
    { key: 'order_date', label: 'Order Date', sortKey: 'order_date', render: function(o) {
        return h('span', { style: { whiteSpace: 'nowrap' } }, shipMDY(o.order_date));
      } },
    { key: 'batch_number', label: 'Batch', sortKey: 'batch_number', render: function(o) {
        // The number opens the batch status dialog (Anthony 9-22); the row click no longer swallows it.
        // Ctrl/shift/middle-click still opens the batch's queue view in a new tab, like the Batches page.
        return o.batch_number ? h('a', { href: '/batches/' + o.batch_number, title: 'Batch status',
          onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) { e.stopPropagation(); return; } e.preventDefault(); e.stopPropagation(); setBatchModal({ id: o.batch_number, live: null }); },
          style: { fontWeight: 500, color: C.accent, textDecoration: 'none' } }, String(o.batch_number)) : null;
      } },
    { key: 'age', label: 'Age', sortKey: 'order_date', render: function(o) {
        var a = ageOf(o);
        return h('span', { style: { color: a.color, fontWeight: 500 } }, a.label);
      } },
    { key: 'ship_by', label: 'Ship By', sortKey: 'ship_by', render: function(o) {
        if (!o.ship_by) return h('span', { style: { color: C.textDim } }, '—');
        var d = String(o.ship_by).slice(0, 10);
        var today = new Date().toLocaleDateString('en-CA'); // civil date — review M7 #104
        var days = Math.round((new Date(d) - new Date(today)) / 86400000);
        var color = days <= 0 ? C.danger : days === 1 ? C.warning : C.textMuted;
        return h('span', { style: { color: color, whiteSpace: 'nowrap' }, title: days < 0 ? 'Late' : days === 0 ? 'Due today' : '' }, shipMDY(d));
      } },
    { key: 'channel', label: 'Channel', sortKey: 'channel' },
    { key: 'recipient', label: 'Recipient', sortKey: 'customer', noFilter: true, render: function(o) {
        var name = h('span', { style: { maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom' }, title: o.customer || '' }, o.customer || '—');
        // The carrier rejected the ship-to (DHL field limits, missing ZIP): flag the row; the click
        // opens the order and the panel opens the address editor (Anthony 9-10). A failed USPS
        // check (automatic validation, 2026-10-08) no longer flags the row — Anthony 2026-10-13,
        // "remove the ADDRESS red tag next to the recipient name": the panel's "Not verifiable"
        // chip carries it, and auto-batch still leaves those orders out.
        var bad = o.rate_error_kind === 'address' ? (o.rate_error || 'Carrier rejected this address') : null;
        if (!bad) return name;
        return h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } }, name,
          h('span', { title: bad, onClick: function(e) { e.stopPropagation(); setOpenOrder(o.id); },
            style: { fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, background: C.dangerSoft || C.bg, color: C.danger, cursor: 'pointer', whiteSpace: 'nowrap' } }, 'Address'));
      } },
    { key: 'dest', label: 'State', sortKey: 'state', defaultVisible: false, render: function(o) { return o.state || ''; } },
    { key: 'country', label: 'Country', sortKey: 'country', render: function(o) {
        var c = String(o.country || 'US').toUpperCase();
        return h('span', { style: { color: c === 'US' ? C.textMuted : C.warning, fontWeight: c === 'US' ? 400 : 700 } }, c);
      } },
    { key: 'item_sku', label: 'Item SKU', noFilter: true, sortValue: function(o) { return ((o.lines || [])[0] || {}).sku || ''; }, render: function(o) {
        var L = o.lines || [];
        if (!L.length) return h('span', { style: { color: C.textDim } }, '—');
        if (L.length > 1) return h('span', { style: { color: C.textMuted }, title: L.map(function(l) { return l.qty + '× ' + l.sku; }).join('\n') }, '(' + L.length + ' items)');
        return h('span', { style: {}, title: L[0].qty + '× ' + L[0].sku }, L[0].sku);
      } },
    { key: 'item_name', label: 'Item Name', noFilter: true, sortValue: function(o) { return ((o.lines || [])[0] || {}).title || ''; }, render: function(o) {
        var L = o.lines || [];
        if (!L.length) return h('span', { style: { color: C.textDim } }, '—');
        if (L.length > 1) return h('span', { style: { color: C.textMuted }, title: L.map(function(l) { return l.qty + '× ' + (l.title || l.sku); }).join('\n') }, '(' + L.length + ' items)');
        return h('span', { style: { maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom' }, title: L[0].title || L[0].sku }, L[0].title || L[0].sku);
      } },
    { key: 'line_count', label: 'SKUs', sortKey: 'line_count', render: function(o) {
        return h('span', { title: (o.lines || []).map(function(l) { return l.qty + '× ' + l.sku; }).join('\n') }, o.line_count);
      } },
    { key: 'total_qty', label: 'Units', sortKey: 'total_qty', render: function(o) {
        return h('span', { title: (o.lines || []).map(function(l) { return l.qty + '× ' + l.sku; }).join('\n') }, o.total_qty);
      } },
    { key: 'locations', label: 'Warehouse Location', sortKey: 'loc_sort', render: function(o) {
        var L = o.locations || [];
        if (!L.length) return h('span', { style: { color: C.textDim } }, '—');
        var totalSlots = 0;
        L.forEach(function(c) { totalSlots += String(c).split('/').length; });
        return h('span', { style: { whiteSpace: 'nowrap' }, title: L.join(', ') },
          String(L[0]).split('/')[0] + (totalSlots > 1 ? ' +' + (totalSlots - 1) : ''));
      } },
    { key: 'weight_lb', label: 'Weight', sortKey: 'weight_lb', render: function(o) {
        var w = shipWeightFmt(o.weight_lb);
        return w ? h('span', { style: { whiteSpace: 'nowrap' } }, w) : h('span', { style: { color: C.warning } }, '—');
      } },
    // value is USD (sorts/sums honestly across currencies); relay orders also show their native amount
    { key: 'value', label: 'Order Total', sortKey: 'value', render: function(o) {
        if (o.currency && o.currency !== 'USD') return h('span', { title: 'Order placed in ' + o.currency + '; converted at the rate on the order date' }, shipMoney(o.value), h('span', { style: { color: C.textDim, fontSize: 11, marginLeft: 4 } }, o.currency + ' ' + Number(o.value_native || 0).toFixed(2)));
        return shipMoney(o.value);
      } },
    { key: 'rate', label: 'Rate', noFilter: true, sortValue: function(o) { var r = rateTotals[o.id]; return r ? parseFloat(r.cost) || 0 : (o.rate_error ? -2 : -1); }, render: function(o) {
        var r = rateTotals[o.id];
        if (r) return h('span', { style: { fontWeight: 500, color: C.success }, title: shipCarrierLabel(r.carrier) + ' · ' + r.service }, shipMoney(r.cost));
        if (rateBusy[o.id]) return h(LIcon, { name: 'loader-2', size: 13, color: C.textDim, spin: true, style: { verticalAlign: 'middle' } });
        if (o.rate_error) return h('span', { title: o.rate_error, style: { cursor: 'help' } }, h(LIcon, { name: 'alert-triangle', size: 14, color: C.danger }));
        return null;
      } },
    { key: 'notes', label: 'Notes', noFilter: true, render: function(o) {
        if (!o.note) return null;
        return h('span', { style: { maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom', color: C.warning, fontStyle: 'italic' }, title: o.note }, o.note);
      } },
    { key: 'assignee', label: 'Assigned To', sortKey: 'assignee', defaultVisible: false, render: function(o) {
        return o.assignee ? h('span', { style: { fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 4, background: C.successSoft, color: C.success } }, '@' + o.assignee) : null;
      } },
    { key: 'hold_until', label: 'Hold Until', sortKey: 'hold_until', defaultVisible: false, render: function(o) {
        return o.hold_until ? h('span', { style: { color: C.danger, fontWeight: 500 } }, o.hold_until) : null;
      } },
    // Carrier fills from an actual rate-shop result, not the rules suggestion
    { key: 'carrier_only', label: 'Carrier', defaultVisible: false, noFilter: true,
      sortValue: function(o) { var r = rateTotals[o.id]; return r && r.carrier ? shipCarrierLabel(r.carrier) : ''; },
      render: function(o) { var r = rateTotals[o.id]; return r && r.carrier ? shipCarrierLabel(r.carrier) : null; } },
    // ShipStation's Custom Field 1-3: Zentail fed 1 = channel label, 2 = marketplace order number
    // (the channel reference number when Zentail has one, else the channel order id), 3 = manual;
    // a value typed in the panel wins (server: services/order-custom-fields.js). Off by default (9-18).
    { key: 'custom1', label: 'Custom Field 1', defaultVisible: false, noFilter: true, sortValue: function(o) { return o.custom1 || ''; }, render: function(o) { return o.custom1 ? h(CopyCell, { value: o.custom1 }, o.custom1) : null; } },
    { key: 'custom2', label: 'Custom Field 2', defaultVisible: false, noFilter: true, sortValue: function(o) { return o.custom2 || ''; }, render: function(o) { return o.custom2 ? h(CopyCell, { value: o.custom2 }, o.custom2) : null; } },
    { key: 'custom3', label: 'Custom Field 3', defaultVisible: false, noFilter: true, sortValue: function(o) { return o.custom3 || ''; }, render: function(o) { return o.custom3 ? h(CopyCell, { value: o.custom3 }, o.custom3) : null; } },
    { key: 'tags', label: 'Tag', render: function(o) {
        var squares = [];
        (o.tags || []).forEach(function(t) {
          squares.push(h('span', { key: t, title: t, style: { width: 5, height: 15, borderRadius: 2, background: shipTagColor(t), display: 'inline-block', flexShrink: 0 } }));
        });
        // Customs destination (international, Guam / American Samoa / Northern Marianas, military
        // mail) with item(s) missing an HS code — the label will error until it is filled (user
        // ask 8-30; territories added 2026-09-28)
        if (needsCustoms(o) && (o.lines || []).some(function(l) { return !l.hs_code; })) {
          squares.push(h('span', { key: '_hs', title: 'Customs destination — item(s) missing an HS code; the label will error until it is filled', style: { fontSize: 8.5, fontWeight: 700, color: '#F59E0B', border: '1px solid rgba(245,158,11,0.55)', borderRadius: 3, padding: '0 3px', lineHeight: '13px', flexShrink: 0 } }, 'HS'));
        }
        if (!squares.length) return null;
        return h('span', { style: { display: 'inline-flex', gap: 3, alignItems: 'center' } }, squares);
      } },
  ];

  // Team default layout = Anthony's picks (2026-08-11): baked order + hidden
  // set so NEW users start from the same view (existing users had his layout
  // copied into their prefs the same night; per-user tweaks still win).
  var SHIP_QUEUE_DEFAULT_ORDER = ['_sel', 'batch_number', 'order_number', 'age', 'order_date', 'ship_by', 'tags', 'channel', 'line_count', 'dest', 'value', 'rate', 'weight_lb', 'item_sku', 'total_qty', 'item_name', 'notes', 'assignee', 'hold_until', 'carrier_only', 'locations', 'country', 'recipient'];
  var SHIP_QUEUE_DEFAULT_HIDDEN = { dest: 1, notes: 1, channel: 1, assignee: 1, hold_until: 1, line_count: 1 };
  cols = SHIP_QUEUE_DEFAULT_ORDER
    .map(function(k) { return cols.find(function(c) { return c.key === k; }); })
    .filter(Boolean)
    .concat(cols.filter(function(c) { return SHIP_QUEUE_DEFAULT_ORDER.indexOf(c.key) === -1; }));
  cols.forEach(function(c) { if (SHIP_QUEUE_DEFAULT_HIDDEN[c.key]) c.defaultVisible = false; });

  // Docked sidebar layout (ShipStation): header/filters span full width; the
  // flex row below is table + always-on sidebar, so their tops align. The
  // page fills the viewport — only the table scrolls.
  return h('div', { style: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } },
    h(ShipManualOrderModal, {
      open: !!manualModal, edit: manualModal && manualModal.edit,
      onClose: function() { setManualModal(null); }, showToast: showToast,
      // Create & open (Anthony 9-19): the modal hands back the new order's pk (negative = manual)
      onSaved: function(pk) { load(); if (pk) setOpenOrder(pk); },
    }),
    h(ShipMarkShippedModal, {
      open: !!markShipModal, orders: markShipModal ? markShipModal.orders : [],
      onClose: function() { setMarkShipModal(null); },
      onDone: function() {
        setSelected({}); load();
        try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
      },
      showToast: showToast,
    }),
    // Bulk-buy summary (Phase L5): what bought, what it cost, what was skipped
    batchModal && h(ShipBatchStatusModal, { batchId: batchModal.id, live: batchModal.live, jobId: batchModal.jobId || null, halt: batchModal.halt || null, needsAuth: !!batchModal.needsAuth, showToast: showToast, onChanged: function() { load(); try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {} },
      onOpenOrder: function(id, ids) { setOrderNavIds(ids && ids.length > 1 ? ids : null); setOpenOrder(id); }, refreshKey: batchModal.tick || 0,
      onClose: function() { setBatchModal(null); } }),
    newBatchDlg && h('div', { role: 'dialog', 'aria-modal': true, 'aria-label': 'New Batch', style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }, onMouseDown: function(e) { if (e.target === e.currentTarget) setNewBatchDlg(null); } },
      h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: 20, width: 440, maxWidth: '92vw', boxSizing: 'border-box' } },
        h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 } }, 'New Batch'),
        h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12 } }, selRows.length + ' selected order' + (selRows.length === 1 ? '' : 's') + '. The note shows on the Batches page and the batch dialog, e.g. "dhl international ory".'),
        h('input', { autoFocus: true, value: newBatchDlg.note, placeholder: 'Batch note (carrier, packer…)', maxLength: 200,
          onChange: function(e) { setNewBatchDlg({ note: e.target.value }); },
          onKeyDown: function(e) { if (e.key === 'Enter') addToBatch(null, newBatchDlg.note); if (e.key === 'Escape') setNewBatchDlg(null); },
          style: { width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', marginBottom: 14 } }),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          h(Btn, { size: 'sm', onClick: function() { setNewBatchDlg(null); } }, 'Cancel'),
          h(Btn, { size: 'sm', variant: 'primary', onClick: function() { addToBatch(null, newBatchDlg.note); } }, 'Create Batch')))),
    labelSummary && h('div', { role: 'dialog', 'aria-modal': true, 'aria-label': 'Label Summary', style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
      h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: 20, width: 480, maxWidth: '92vw', maxHeight: '76vh', overflowY: 'auto', boxSizing: 'border-box' } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
          h(LIcon, { name: labelSummary.errors.length ? 'alert-triangle' : 'check-circle', size: 16, color: labelSummary.errors.length ? C.warning : C.success }),
          h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, flex: 1 } }, 'Label purchase finished'),
          h('button', { onClick: function() { setLabelSummary(null); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),
        h('div', { style: { fontSize: 12.5, color: C.text, marginBottom: labelSummary.errors.length ? 10 : 14 } },
          labelSummary.bought + ' label' + (labelSummary.bought === 1 ? '' : 's') + ' bought · ' + shipMoney(labelSummary.cost) +
          (labelSummary.errors.length ? ' · ' + labelSummary.errors.length + ' skipped' : '')),
        labelSummary.errors.length > 0 && h('div', { style: { marginBottom: 14, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
          labelSummary.errors.map(function(er, i) {
            return h('div', { key: i, style: { padding: '7px 10px', fontSize: 11.5, lineHeight: 1.5, borderBottom: i < labelSummary.errors.length - 1 ? '1px solid ' + C.border : 'none' } },
              h('span', { style: { fontWeight: 600, color: C.text } }, er.order_number || ('#' + er.id)),
              h('span', { style: { color: C.warning } }, ' — ' + er.error));
          })),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          labelSummary.sids.length > 0 && h(Btn, { size: 'sm', onClick: function() { shipPrintApiPdf('/shipping/label-files?ids=' + labelSummary.sids.join(',')); } },
            labelSummary.printed ? 'Print again' : 'Print labels (' + labelSummary.sids.length + ')'),
          h(Btn, { size: 'sm', variant: 'primary', onClick: function() { setLabelSummary(null); } }, 'Done')))),
    openOrder != null && (function() {
      // ←/→ walk the queue exactly as displayed (current sort + filters)
      var rows = (sortedRef.current && sortedRef.current.length ? sortedRef.current : filtered);
      var ids = orderNavIds && orderNavIds.indexOf(openOrder) !== -1 ? orderNavIds : rows.map(function(o) { return o.id; });
      var mIdx = ids.indexOf(openOrder);
      return h(ShipOrderModal, {
        orderId: openOrder,
        onClose: function() { setOpenOrder(null); setOrderNavIds(null); },
        onPrev: mIdx > 0 ? function() { setOpenOrder(ids[mIdx - 1]); } : null,
        onNext: (mIdx >= 0 && mIdx < ids.length - 1) ? function() { setOpenOrder(ids[mIdx + 1]); } : null,
        showToast: showToast,
        onChanged: load,
        onEditManual: function(dd) { setOpenOrder(null); setManualModal({ edit: dd }); },
        onMarkShipped: function(dd) { setMarkShipModal({ orders: [{ id: dd.id, order_number: dd.order_number, customer: (dd.recipient && dd.recipient.name) || '' }] }); },
        onRowPatch: patchRows,
      });
    })(),
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' } },
      h('div', { style: { fontSize: 12, color: C.textMuted } },
        batchId
          ? h('span', null, h('span', { style: { fontWeight: 500, color: C.accent } }, 'Batch ' + bnum),
              data ? ' — ' + orders.length.toLocaleString() + ' orders' : '')
          : (data ? (function() {
              // the rows this view holds (the table's own basis), named for the view (review fix 13)
              var base = shipQueueHeaderText(bucket, orders.length, data.truncated);
              if (!filtersActive && !q) return base;
              var name = null;
              savedFilters.forEach(function(sf) {
                if (JSON.stringify(Object.assign({}, SHIP_EMPTY_FILTERS, sf.filters || {})) === JSON.stringify(filters)) name = sf.name;
              });
              return h('span', null, base, ' · ', h('span', { style: { color: C.accent, fontWeight: 500 } },
                filtered.length.toLocaleString() + ' in ' + (name ? '"' + name + '"' : 'current filter')));
            })() : ' ')),
    ),
    // Reserved selection-banner slot ABOVE the filters — fixed height, so the
    // banner appearing/disappearing never reflows the filters or table (8-12)
    h('div', { style: { height: 34, marginBottom: 6 } },
      selCount > 1 && (pageAllSelected || selCount >= filtered.length) && h('div', { style: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: '100%', boxSizing: 'border-box', padding: '0 110px', background: C.accentSoft, border: '1px solid ' + C.accent + '40', borderRadius: 8, fontSize: 12 } },
        h(LIcon, { name: 'check', size: 13, color: C.accent }),
        selCount >= filtered.length
          ? h('span', { style: { color: C.accent, fontWeight: 500 } }, 'All ' + filtered.length.toLocaleString() + ' orders in the filtered view are selected.')
          : h('span', { style: { color: C.text } },
              h('strong', null, selCount.toLocaleString()), ' selected — ',
              h('button', {
                onClick: function() { var n = {}; filtered.forEach(function(o) { n[o.id] = true; }); setSelected(n); },
                style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontWeight: 500, fontSize: 12, fontFamily: 'inherit', padding: 0, textDecoration: 'underline' }
              }, 'select all ' + filtered.length.toLocaleString() + ' orders in this filter')),
        h('button', {
          onClick: function() { setSelected({}); },
          style: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }
        }, 'Clear selection'))),
    // ShipStation-style filter bar: Store · Destination · Assignee · Tag ·
    // Order Date · Hold · Saved Filters ("Other" menu comes with its screenshot)
    h('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
      h('div', { style: { position: 'relative', display: 'inline-block' } },
        h('input', Object.assign({
          value: qLive, placeholder: 'Search order #, recipient, SKU…',
          style: { background: C.bg, color: C.text, border: '1px solid ' + (q ? C.accent : C.border), borderRadius: 6, padding: '7px 26px 7px 10px', fontSize: 12.5, fontFamily: 'inherit', width: 230 }
        }, qSearchProps)),
        (q || qLive) && h('button', {
          onClick: function() { setQ(''); setQLive(''); },
          title: 'Clear search',
          style: { position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 13, padding: 2, lineHeight: 1, fontFamily: 'inherit' }
        }, '✕')),
      h('span', { style: { fontSize: 12.5, color: C.textMuted, fontWeight: 500 } }, 'Filter by:'),
      h(ReportParamSelect, {
        label: 'Store', values: Object.keys(allChannels).sort(),
        selected: filters.channels, onChange: function(v) { setF({ channels: v }); },
      }),
      h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
        (function() {
          var domN = 0, intlN = 0;
          orders.forEach(function(o) { if (isDomestic(o)) domN++; else intlN++; });
          // Click the active pill again to clear back to everything
          return [{ k: 'dom', l: 'Domestic (' + domN.toLocaleString() + ')' }, { k: 'intl', l: 'International (' + intlN.toLocaleString() + ')' }].map(function(d) {
            var on = filters.dest === d.k;
            return h('button', {
              key: d.k, className: 'fpill' + (on ? ' fpill-on' : ''), onClick: function() { setF({ dest: on ? 'all' : d.k }); },
              style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500, fontFamily: 'inherit' }
            }, d.l);
          });
        })()),
      h(ReportParamSelect, {
        label: 'Assignee', values: Object.keys(allAssignees).sort(),
        selected: filters.assignees, onChange: function(v) { setF({ assignees: v }); },
      }),
      h(ReportParamSelect, {
        label: 'Tag', values: Object.keys(allTags).sort(),
        selected: filters.tags, onChange: function(v) { setF({ tags: v }); },
        onCreate: createTag, createLabel: 'Create new tag',
        // Jump to Settings > Tags (rename/recolor/delete live there) via the
        // shell's pushState router — popstate makes it re-parse the path
        onManage: function() {
          try { history.pushState({}, '', '/settings/tags'); window.dispatchEvent(new PopStateEvent('popstate')); } catch (e) { window.location.href = '/settings/tags'; }
        },
        manageLabel: 'Manage tags',
        valueColors: (function() { var m = {}; Object.keys(allTags).forEach(function(t) { m[t] = shipTagColor(t); }); return m; })(),
      }),
      // Order Date preset dropdown (Apply/Cancel; Custom Range opens From/To pickers)
      h('div', { ref: odRef, style: { position: 'relative', display: 'inline-block' } },
        h('button', {
          className: 'fbtn',
          onClick: function() {
            if (!odOpen) setOdDraft({ preset: filters.datePreset || ((filters.dateFrom || filters.dateTo) ? 'custom' : ''), from: filters.dateFrom || '', to: filters.dateTo || '' });
            setOdOpen(!odOpen);
          },
          style: { background: (filters.datePreset || filters.dateFrom || filters.dateTo) ? C.accentSoft : C.bg, border: '1px solid ' + ((filters.datePreset || filters.dateFrom || filters.dateTo) ? C.accent : C.border), color: (filters.datePreset || filters.dateFrom || filters.dateTo) ? C.accent : C.text, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: (filters.datePreset || filters.dateFrom || filters.dateTo) ? 700 : 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }
        }, h(LIcon, { name: 'calendar', size: 12 }), shipDateLabel(filters), h(LIcon, { name: 'chevron-down', size: 12 })),
        odOpen && h('div', { style: { position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 100, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: 8, width: 230 } },
          SHIP_DATE_PRESETS.map(function(p) {
            var on = odDraft.preset === p[0];
            return h('div', {
              key: p[0] || 'all',
              onClick: function() { setOdDraft(Object.assign({}, odDraft, { preset: p[0] })); },
              onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
              onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; },
              style: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: on ? C.accent : C.text, fontWeight: on ? 700 : 400 }
            },
              h('span', { style: { width: 13, height: 13, borderRadius: 999, border: '2px solid ' + (on ? C.accent : C.border), background: on ? C.accent : 'transparent', boxShadow: on ? 'inset 0 0 0 2px ' + C.surface : 'none', flexShrink: 0 } }),
              p[1]);
          }),
          odDraft.preset === 'custom' && h('div', { style: { padding: '6px 8px 2px', display: 'flex', flexDirection: 'column', gap: 6 } },
            h('div', null,
              h('div', { style: { fontSize: 10.5, color: C.textMuted, fontWeight: 500, marginBottom: 2 } }, 'From'),
              h('input', { type: 'date', value: odDraft.from, onChange: function(e) { setOdDraft(Object.assign({}, odDraft, { from: e.target.value })); }, style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, padding: '5px 8px', borderRadius: 6, fontSize: 11.5, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', colorScheme: 'dark' } })),
            h('div', null,
              h('div', { style: { fontSize: 10.5, color: C.textMuted, fontWeight: 500, marginBottom: 2 } }, 'To'),
              h('input', { type: 'date', value: odDraft.to, onChange: function(e) { setOdDraft(Object.assign({}, odDraft, { to: e.target.value })); }, style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, padding: '5px 8px', borderRadius: 6, fontSize: 11.5, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', colorScheme: 'dark' } }))),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '8px 8px 2px', borderTop: '1px solid ' + C.border, marginTop: 6 } },
            h('button', { onClick: function() { setOdOpen(false); }, style: { background: 'transparent', border: '1px solid ' + C.border, color: C.text, borderRadius: 6, padding: '5px 12px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' } }, 'Cancel'),
            h('button', {
              onClick: function() {
                if (odDraft.preset === 'custom') setF({ datePreset: 'custom', dateFrom: odDraft.from, dateTo: odDraft.to });
                else setF({ datePreset: odDraft.preset, dateFrom: '', dateTo: '' });
                setOdOpen(false);
              },
              style: { background: C.accent, border: 'none', color: '#fff', borderRadius: 6, padding: '5px 14px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
            }, 'Apply')))),
      // "Other" filter menu (ShipStation parity)
      h('div', { ref: oRef, style: { position: 'relative', display: 'inline-block' } },
        h('button', {
          className: 'fbtn',
          onClick: function() { setOOpen(!oOpen); },
          style: { background: otherActive ? C.accentSoft : C.bg, border: '1px solid ' + (otherActive ? C.accent : C.border), color: otherActive ? C.accent : C.text, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: otherActive ? 700 : 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }
        }, 'Other', otherActive > 0 && h('span', { style: { fontSize: 10, fontWeight: 500, background: C.accent, color: '#fff', padding: '0 6px', borderRadius: 999 } }, otherActive), h(LIcon, { name: 'chevron-down', size: 12 })),
        oOpen && (function() {
          var inp = { background: C.surface, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '5px 7px', fontSize: 11.5, fontFamily: 'inherit', width: 64 };
          var sel = Object.assign({}, inp, { width: 'auto', cursor: 'pointer' });
          var row = function(label, controls) {
            return h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' } },
              h('span', { style: { fontSize: 11.5, color: C.text, width: 108, flexShrink: 0 } }, label), controls); // labels in the same colour as their controls (Anthony 9-22)
          };
          var range = function(kMin, kMax) {
            return h('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
              h('input', { type: 'number', value: fo[kMin], placeholder: 'min', onChange: function(e) { var p = {}; p[kMin] = e.target.value; setOther(p); }, style: inp }),
              h('span', { style: { color: C.textDim, fontSize: 11 } }, '–'),
              h('input', { type: 'number', value: fo[kMax], placeholder: 'max', onChange: function(e) { var p = {}; p[kMax] = e.target.value; setOther(p); }, style: inp }));
          };
          var textRow = function(label, key, opKey) {
            return row(label, h('span', { style: { display: 'inline-flex', gap: 4, alignItems: 'center' } },
              h('select', { value: fo[opKey] || '', onChange: function(e) { var p = {}; p[opKey] = e.target.value; setOther(p); }, style: Object.assign({}, sel, { width: 96 }) },
                [['', 'Contains'], ['exact', 'Exact'], ['starts', 'Starts with']].map(function(op) { return h('option', { key: op[0], value: op[0] }, op[1]); })),
              h('input', { value: fo[key], onChange: function(e) { var p = {}; p[key] = e.target.value; setOther(p); }, style: Object.assign({}, inp, { width: 118 }) })));
          };
          var choice = function(key, opts) {
            return h('select', { value: fo[key], onChange: function(e) { var p = {}; p[key] = e.target.value; setOther(p); }, style: sel },
              [['', 'Any']].concat(opts).map(function(op) { return h('option', { key: op[0], value: op[0] }, op[1]); }));
          };
          return h('div', { style: { position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 100, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: '10px 14px', width: 360, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 260px)', overflowY: 'auto' } },
            row('Country', h(ReportParamSelect, { label: 'Country', values: Object.keys(allCountries).sort(), selected: fo.countries, onChange: function(v) { setOther({ countries: v }); } })),
            row('State', h(ReportParamSelect, { label: 'State', values: Object.keys(allStates).sort(), selected: fo.states, onChange: function(v) { setOther({ states: v }); } })),
            // the USPS business flag / the panel's Address type (Anthony 2026-10-30: "Business Address, True/False")
            row('Business address', choice('business', [['yes', 'True'], ['no', 'False']])),
            // Text fields carry an operator (Anthony 9-17): Contains (the default every saved filter
            // set already means), Exact, Starts with
            textRow('Recipient', 'recipient', 'recipientOp'),
            textRow('Order #', 'orderNum', 'orderNumOp'),
            textRow('SKU', 'sku', 'skuOp'),
            textRow('Item name', 'itemName', 'itemNameOp'),
            textRow('Bin location', 'location', 'locationOp'),
            row('Weight (lb)', range('wMin', 'wMax')),
            row('Order total ($)', range('vMin', 'vMax')),
            row('Units', range('qMin', 'qMax')),
            row('Age (hours)', range('ageMin', 'ageMax')),
            row('Line items', choice('lineMode', [['single', 'Single-SKU'], ['multi', 'Multi-SKU']])),
            row('Batch', choice('batchMode', [['in', 'In a Batch'], ['none', 'Not Batched']])),
            row('Rate status', choice('rateStatus', [['error', 'Has Rate Error'], ['address', 'Address Error'], ['rated', 'Rated'], ['unrated', 'Not Rated']])),
            row('Tagged', choice('tagged', [['yes', 'Tagged'], ['no', 'Untagged']])),
            row('Note', choice('noted', [['yes', 'Has Note'], ['no', 'No Note']])),
            row('HS code', choice('hs', [['yes', 'All Items Have'], ['no', 'Missing on Item']])),
            row('Ship by', choice('shipBy', [['overdue', 'Overdue'], ['today', 'Today'], ['tomorrow', 'Tomorrow'], ['later', 'Later'], ['none', 'No Date']])),
            row('Requested service', choice('requestedSvc', Object.keys(allReqSvcs).sort().map(function(s) { return [s, s]; }))),
            row('Service source', choice('svcSource', [['rule', 'Rule'], ['default', 'Default'], ['manual', 'Manual']])),
            row('Carrier', choice('carrier', Object.keys(allCarriers).sort().map(function(c) { return [c, c]; }))),
            otherActive > 0 && h('button', {
              onClick: function() { setF({ other: SHIP_EMPTY_OTHER }); },
              style: { marginTop: 8, background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', padding: 0 }
            }, 'Clear other filters')
          );
        })()
      ),
      // Saved Filters (sits at the end of the filter controls, next to Other)
      h('div', { ref: sfRef, style: { position: 'relative', display: 'inline-block' } },
        h('button', {
          className: 'fbtn',
          onClick: function() { setSfOpen(!sfOpen); },
          style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }
        }, h(LIcon, { name: 'bookmark', size: 12 }), 'Saved Filters', h(LIcon, { name: 'chevron-down', size: 12 })),
        sfOpen && h('div', { style: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 100, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', minWidth: 230, maxHeight: 300, overflowY: 'auto' } },
          // Save entry FIRST (user ask 8-12)
          h('div', {
            onClick: async function() {
              var name = await appPrompt('Name this filter set:');
              if (!name || !name.trim()) return;
              api('/shipping/filters', { method: 'POST', body: JSON.stringify({ name: name.trim(), filters: filters }) })
                .then(function() { loadSavedFilters(); setSfOpen(false); showToast('Filter saved', 'success'); })
                .catch(function(e) { showToast(e.message, 'error'); });
            },
            onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
            onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
            style: { padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: C.accent, fontWeight: 500, borderBottom: '1px solid ' + C.border }
          }, '+ Save current filter…'),
          savedFilters.length === 0 && h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, 'No saved filters yet.'),
          savedFilters.map(function(sf) {
            return h('div', { key: sf.id, style: { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13, color: C.text } },
              h('span', {
                onClick: function() { setFilters(Object.assign({}, SHIP_EMPTY_FILTERS, sf.filters || {})); setSfOpen(false); },
                onMouseOver: function(e) { e.currentTarget.style.color = C.accent; },
                onMouseOut: function(e) { e.currentTarget.style.color = C.text; },
                style: { flex: 1, cursor: 'pointer' }
              }, sf.name),
              h('button', {
                onClick: async function() {
                  if (!(await appConfirm('Delete saved filter "' + sf.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
                  api('/shipping/filters/' + sf.id, { method: 'DELETE' }).then(loadSavedFilters);
                },
                style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 }
              }, h(LIcon, { name: 'trash-2', size: 12 }))
            );
          }),
        )
      ),
      filtersActive && h('button', {
        className: 'fpill',
        onClick: function() { setFilters(SHIP_EMPTY_FILTERS); },
        style: { padding: '5px 10px', fontSize: 11, borderRadius: 6, border: 'none', background: 'transparent', color: C.danger, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
      }, 'Clear Filters'),
      // + New Order sits at the far right of the filter row — level with the
      // side menu's top corner without consuming column height (8-12)
      !batchId && h('div', { style: { marginLeft: 'auto' } },
        h(Btn, { variant: 'primary', size: 'sm', onClick: function() { setManualModal({}); } },
          h(LIcon, { name: 'plus', size: 12 }), ' New Order')),
      // Batch pages: the batch actions sit level with the filters (Anthony 9-17)
      batchId && h('div', { style: { marginLeft: 'auto', display: 'flex', gap: 8 } },
        h(Btn, { size: 'sm', onClick: function() { setBatchModal({ id: batchId, live: null }); } }, h(LIcon, { name: 'info', size: 12 }), ' Batch status'),
        canDissolve && h(Btn, { size: 'sm', onClick: dissolveBatch }, h(LIcon, { name: 'trash-2', size: 12 }), ' Dissolve batch'))
    ),
    h('div', { style: { display: 'flex', gap: 14, alignItems: 'stretch', flex: 1, minHeight: 0 } },
      h('div', { style: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 } },
    loading && data && h('div', { style: { fontSize: 11, color: C.textMuted, padding: '0 2px 4px' } }, 'Refreshing…'),
    loading && !data
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, h(LIcon, { name: 'loader-2', size: 28, spin: true, style: { color: C.textMuted } })) // a spinner, not "Loading orders…" (Anthony 9-22)
      : h(DataTable, {
          columns: cols, data: filtered, storageKey: 'ship-queue', compact: true,
          // 100 rows until the user picks a size (2026-09-26: 800 rows ≈ 28k DOM nodes; a saved pick still wins)
          defaultPageSize: 100,
          initialScroll: (_shipQueueUi[qKey] || {}).scroll || 0, onScrollChange: function(y) { (_shipQueueUi[qKey] = _shipQueueUi[qKey] || {}).scroll = y; },
          initialPage: (_shipQueueUi[qKey] || {}).page || 0, onPageChange: function(p) { (_shipQueueUi[qKey] = _shipQueueUi[qKey] || {}).page = p; }, noColumnFilters: true, fontSize: 12, cellPadding: '3px 8px',
          scrollHeight: 'fill',
          // Checked rows get a soft accent wash so the selection reads at a glance
          rowStyle: function(o) { return selected[o.id] ? { background: 'rgba(79,110,247,0.08)' } : null; },
          emptyTitle: 'No Orders to Show', emptySub: '', // one line, centred (Anthony 9-23)
          onRowClick: function(o, e) {
            // Shift = range from the last clicked row, Ctrl/Cmd = toggle into the
            // multi-selection; a plain click stays single-select (docked panel).
            if (e && (e.shiftKey || e.ctrlKey || e.metaKey)) { toggleRow(o, e); return; }
            var n = {}; n[o.id] = true; setSelected(n);
            lastSelIdRef.current = o.id;
            if (viewport.isNarrow) setNarrowDockOpen(true); // narrow: a tapped order opens full screen
          },
          onSortedData: function(d) { sortedRef.current = d; },
          onPageData: function(d) {
            var prev = pageRef.current;
            pageRef.current = d;
            var changed = prev.length !== d.length || d.some(function(o, i) { return !prev[i] || prev[i].id !== o.id; });
            if (changed) setPageTick(function(t) { return t + 1; });
          },
          bulkActions: {
            alwaysShow: true,
            count: selCount,
            items: [
              { section: 'Selected: ' + selCount },
              { label: rating ? 'Rating… ' + (rateProg ? rateProg.done + '/' + rateProg.total : '') : 'Get rate (' + selCount + ')', icon: 'scale', disabled: selCount === 0 || rating, onClick: getRates },
              { label: 'Export CSV (' + (selCount || filtered.length).toLocaleString() + ')', icon: 'download', onClick: function() {
                  var rows = selCount ? selRows : filtered;
                  shipDownloadCsv('orders-' + new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + '.csv',
                    ['Order #', 'Order Date', 'Channel', 'Recipient', 'State', 'Country', 'Items', 'Units', 'Weight (lb)', 'Order Total (USD)', 'Carrier', 'Service', 'Tags', 'Batch', 'Ship By', 'Bins', 'Currency', 'Order Total (native)'],
                    rows.map(function(o) {
                      return [o.order_number, shipLocalStamp(o.order_date), o.channel, o.customer, o.state, o.country,
                        (o.lines || []).map(function(l) { return l.qty + 'x ' + l.sku; }).join('; '), o.total_qty, o.weight_lb, o.value,
                        shipCarrierLabel(o.carrier), o.service, (o.tags || []).join('; '), o.batch_number || '',
                        o.ship_by ? String(o.ship_by).slice(0, 10) : '', (o.locations || []).join('; '),
                        o.currency || 'USD', o.value_native != null ? o.value_native : o.value];
                    }));
                } },
              { label: 'Print Pick List', icon: 'list-checks', disabled: selCount === 0, onClick: function() { shipPrintPickList(selRows); } },
              { label: 'Print Packing Slips', icon: 'file-text', disabled: selCount === 0, onClick: function() { shipPrintPackingSlips(selRows); } },
              { label: 'Add Tag…', icon: 'tag', disabled: selCount === 0, onClick: function() { setBulkMode('tag'); } },
              { label: 'Remove Tag…', icon: 'tag', disabled: selCount === 0, onClick: function() { setBulkMode('untag'); } },
              { label: 'Assign to…', icon: 'user', disabled: selCount === 0, onClick: function() { setBulkMode('assign'); } },
              { label: 'Hold Until…', icon: 'clock', disabled: selCount === 0, onClick: function() { setBulkMode('hold'); } },
              { label: 'Add Note…', icon: 'sticky-note', disabled: selCount === 0, onClick: function() { setBulkMode('note'); } },
              { label: 'Set Weight…', icon: 'scale', disabled: selCount === 0, onClick: function() { setBulkMode('weight'); } },
              { label: 'Set Package…', icon: 'box', disabled: selCount === 0, onClick: function() { setBulkMode('package'); } },
              { label: 'Set Confirmation…', icon: 'check-circle', disabled: selCount === 0, onClick: function() { setBulkMode('confirmation'); } },
              { label: 'Set Carrier & Service…', icon: 'box', disabled: selCount === 0, onClick: function() { setBulkMode('service'); } },
              { label: 'Customer Notification…', icon: 'bell-off', disabled: selCount === 0, onClick: function() { setBulkMode('notify'); } },
              { label: 'Release Hold / Unassign', icon: 'x', disabled: selCount === 0, onClick: function() { applyBulkMeta({ hold_until: null, assignee: null }, 'Released'); } },
            ].concat(canCancelHere ? [
              { label: 'Cancel', icon: 'x-circle', disabled: selCount === 0, onClick: function() { setBulkMode('cancel'); } },
            ] : bucket === 'cancelled' ? [
              { label: 'Restore to Queue (' + selCount + ')', icon: 'rotate-ccw', disabled: selCount === 0, onClick: function() { restoreSelected(); } },
            ] : []).concat(canShip ? [
              // (two dead, always-disabled carrier-credential items removed, sweep 2026-09-27 Lows: labels are live —
              // the Create Labels button buys them)
              { section: 'Labels' },
              { label: 'Mark as Shipped (' + selCount + ')…', icon: 'check-circle', disabled: selCount === 0, onClick: function() { openMarkShipped(selRows); } },
            ] : []).concat([
              { label: 'Clear Selection', icon: 'x', disabled: selCount === 0, onClick: function() { setSelected({}); } },
            ]),
          },
        }),
    // Out-of-queue search hits. Shipped orders belong to the Shipments table
    // (user spec 9-01): the banner hands the search off there with a window
    // wide enough to cover the order. Other statuses (cancelled, other
    // buckets) list compactly and open the order modal.
    (function() {
      // nothing from outside while the list loads, and nothing when the list already matches (Anthony 9-22)
      if (!q || loading || !data || extraPending || !histHits.length || (filteredQueue || []).length) return null;
      var visible = {};
      filtered.forEach(function(o) { visible[o.id] = true; });
      var extra = histHits.filter(function(hh) { return !visible[hh.id]; });
      if (!extra.length) return null;
      var shipped = extra.filter(function(o) { return String(o.status || '').toUpperCase() === 'SHIPPED'; });
      var rest = extra.filter(function(o) { return String(o.status || '').toUpperCase() !== 'SHIPPED'; });
      var goShipments = function() {
        var oldest = shipped.reduce(function(m, o) { return o.order_date && (!m || o.order_date < m) ? o.order_date : m; }, null);
        var d = 30;
        if (oldest) d = Math.min(90, Math.max(1, Math.ceil((Date.now() - new Date(oldest).getTime()) / 86400000) + 7));
        try {
          history.pushState({}, '', '/shipments?q=' + encodeURIComponent(q) + '&days=' + d + (shipped.length === 1 ? '&open=' + shipped[0].id : ''));
          window.dispatchEvent(new PopStateEvent('popstate'));
        } catch (e) {}
      };
      var stColor = { CANCELLED: C.danger, PENDING: C.warning, PENDING_PAYMENT: C.warning, ACCEPTED: C.warning };
      var one = shipped.length === 1 ? shipped[0] : null;
      return h('div', { style: { marginTop: 10, flexShrink: 0 } },
        shipped.length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid ' + C.accent + '44', background: C.accentSoft, borderRadius: 8, fontSize: 12.5, marginBottom: rest.length ? 8 : 0 } },
          h(LIcon, { name: 'truck', size: 15, color: C.accent }),
          h('span', { style: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            one
              ? ['Order ', h('b', { key: 'n' }, one.order_number), (one.customer ? ' (' + one.customer + ')' : ''), ' has shipped',
                 one.tracking ? ' — ' + (one.carrier ? shipCarrierLabel(one.carrier) + ' ' : '') + one.tracking : '']
              : shipped.length + ' orders matching this search have shipped'),
          h(Btn, { size: 'sm', variant: 'primary', onClick: goShipments }, 'View in Shipments')),
        rest.length > 0 && h('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'auto', maxHeight: 150, background: C.surface } },
          rest.map(function(o) {
            var st = String(o.status || '').toUpperCase();
            return h('div', {
              key: o.id,
              onClick: function() { setOpenOrder(o.id); },
              style: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderBottom: '1px solid ' + C.border + '44', cursor: 'pointer', fontSize: 12 }
            },
              h('span', { style: { fontWeight: 600, color: C.accent, whiteSpace: 'nowrap' } }, o.order_number),
              h('span', { style: { color: C.textMuted, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                (o.channel || '') + (o.channel_order_id ? ' · ' + o.channel_order_id : '')),
              h('span', { style: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.customer || ''),
              h('span', { style: { color: C.textMuted, whiteSpace: 'nowrap' } }, shipOrderDay(o.order_date)),
              h('span', { style: { fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8, whiteSpace: 'nowrap', background: (stColor[st] || C.textMuted) + '18', color: stColor[st] || C.textMuted } }, st));
          })));
    })(),
      ), // end table column
      // Details column — expanded (docked) or collapsed to a strip (shipDock above)
      shipDock(
      // The dock unmounts while the order modal is open (review M7 #101): both were the same
      // ShipOrderPanel on the same order, and the hidden dock's stale form autosaved over the
      // modal's edits. It remounts (fresh fetch) when the modal closes.
      selCount === 1 && selRows[0] && openOrder == null
        ? h(ShipOrderPanel, { key: selRows[0].id, docked: true, orderId: selRows[0].id, reloadRef: dockReloadRef, onClose: function() { setSelected({}); }, onChanged: (function(id) { return function() { refreshRows([id]); }; })(selRows[0].id), showToast: showToast,
            onEditManual: function(d) { setManualModal({ edit: d }); },
            onMarkShipped: function(d) { setMarkShipModal({ orders: [{ id: d.id, order_number: d.order_number, customer: (d.recipient && d.recipient.name) || '' }] }); },
            // Shared with the modal: the docked copy matched a plain row id only, so the panel's
            // predicate-style HS save (9-17) never updated the row's amber HS flag (9-18)
            onRowPatch: patchRows,
            onRates: function(id, best) { setRateTotals(function(p) { var n = Object.assign({}, p); n[id] = best; return n; }); } })
        : (selCount >= 2 || batchRows.length) ? (function(panelRows, batchMode) {
      // panelRows: the checked rows, or (batchMode, 10-09) the batch's open orders with nothing checked
      var n = panelRows.length;
      // a bulk dialog works on the selection: in batch mode the batch's open rows get checked first
      var bulk = function(mode) { if (batchMode) selectRows(panelRows); setBulkMode(mode); };
      var units = 0, weight = 0, value = 0;
      var rollup = {};
      panelRows.forEach(function(o) {
        weight += o.weight_lb || 0;
        value += o.value || 0;
        (o.lines || []).forEach(function(l) {
          var q = parseInt(l.qty, 10) || 0;
          units += q;
          if (!rollup[l.sku]) rollup[l.sku] = { sku: l.sku, title: l.title || l.sku, image_url: l.image_url || null, location: l.location || null, qty: 0 };
          rollup[l.sku].qty += q;
          if (!rollup[l.sku].image_url && l.image_url) rollup[l.sku].image_url = l.image_url;
        });
      });
      var items = Object.keys(rollup).map(function(k) { return rollup[k]; }).sort(function(a, b) { return b.qty - a.qty; });
      // 180px column: stats stack as label/value rows (three side-by-side cards can't fit)
      var stat = function(label, val) {
        return h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: '5px 9px' } },
          h('div', { style: { fontSize: 10.5, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, label),
          h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, val));
      };
      var chip = function(label, icon, onClick) {
        return h('button', { className: 'phov', onClick: onClick, style: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.bg, border: '1px solid ' + C.border, color: C.text, borderRadius: 6, padding: '5px 9px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' } },
          h(LIcon, { name: icon, size: 12 }), label);
      };
      return h('div', { className: 'spanel', style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, overflowY: 'auto', padding: '16px 16px 24px', flex: 1, minHeight: 0, boxSizing: 'border-box' } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingRight: 42 } },
          h(LIcon, { name: 'list-checks', size: 16, color: C.accent }),
          h('div', { style: { fontSize: 13.5, fontWeight: 500, color: C.text, flex: 1 } },
            batchMode ? 'Batch ' + bnum + ' · ' + n.toLocaleString() + ' open' : n.toLocaleString() + ' Selected'),
          // Batch ▾ / Print ▾ header menus (parity with the single-order panel); Batch ▾ moves a
          // SELECTION into another batch — not offered for the batch itself
          !batchMode && h('div', { ref: selBatchRef, style: { position: 'relative' } },
            h('button', { className: 'phov', onClick: function() { setBatchMenuOpen(!batchMenuOpen); setSelPrintOpen(false); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit', padding: '2px 6px', borderRadius: 4 } }, 'Batch ▾'),
            batchMenuOpen && h('div', { style: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 950, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 180, maxHeight: 240, overflowY: 'auto' } },
              h('div', { onClick: function() { addToBatch(null); }, onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { padding: '8px 12px', fontSize: 12, fontWeight: 500, color: C.accent, cursor: 'pointer', borderBottom: '1px solid ' + C.border } }, '+ New batch'),
              openBatches.map(function(bt) {
                return h('div', { key: bt.id, onClick: function() { addToBatch(bt.id); }, onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { padding: '7px 12px', fontSize: 12, color: C.text, cursor: 'pointer' } }, String(bt.batch_number) + '  (' + bt.order_count + ')');
              }))),
          h('div', { ref: selPrintRef, style: { position: 'relative' } },
            h('button', { className: 'phov', onClick: function() { setSelPrintOpen(!selPrintOpen); setBatchMenuOpen(false); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11.5, fontWeight: 500, fontFamily: 'inherit', padding: '2px 6px', borderRadius: 4 } }, 'Print ▾'),
            selPrintOpen && h('div', { style: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 950, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 160 } },
              [{ label: 'Pick list', fn: function() { shipPrintPickList(panelRows); } }, { label: 'Packing slips', fn: function() { shipPrintPackingSlips(panelRows); } }].map(function(it) {
                return h('div', { key: it.label, onClick: function() { setSelPrintOpen(false); it.fn(); }, onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: C.text } }, it.label);
              }))),
          !batchMode && h('button', { className: 'phov', onClick: function() { setSelected({}); }, title: 'Clear selection', style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4, borderRadius: 4 } }, h(LIcon, { name: 'x', size: 16 }))),
        h('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 } },
          stat('Units', units.toLocaleString()),
          stat('SKUs', items.length.toLocaleString()),
          stat('Weight', shipWeightFmt(weight) || '—'),
          stat('Value', shipMoney(value))),
        // 2+ orders = one action: the selection is batched and its labels generated. Create & Print
        // stays a single-order action in the docked panel (Anthony 9-19). First button, named
        // "Create Labels" (Anthony 9-22) — still a batch and its dialog.
        canShip && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 } },
          h('button', {
            disabled: !!buyingLabels,
            title: 'Batch the selected orders and buy a label for each (rate-shop winner or assigned service)',
            onClick: function() { bulkCreateLabels(panelRows, false); },
            style: { flex: 1, background: '#16A34A', opacity: buyingLabels ? 0.6 : 1, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 12, fontWeight: 600, cursor: buyingLabels ? 'wait' : 'pointer', fontFamily: 'inherit' }
          }, buyingLabels ? 'Buying ' + buyingLabels.done + ' / ' + buyingLabels.total + '…' : 'Create Labels (' + n.toLocaleString() + ')')),
        (function() {
          var rated = panelRows.filter(function(o) { return rateTotals[o.id]; });
          if (!rated.length && !rating) return null;
          var tot = rated.reduce(function(a, o) { return a + (parseFloat(rateTotals[o.id].cost) || 0); }, 0);
          return h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 10px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8 } },
            h(LIcon, { name: 'scale', size: 14, color: C.accent }),
            rating
              ? h('span', { style: { fontSize: 12, color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1 } },
                  'Rating orders… ' + (rateProg ? rateProg.done + ' / ' + rateProg.total : ''),
                  rateProg && h('span', { style: { flex: 1, height: 4, background: C.bg, borderRadius: 999, overflow: 'hidden' } },
                    h('span', { style: { display: 'block', height: '100%', width: Math.round((rateProg.done / Math.max(rateProg.total, 1)) * 100) + '%', background: C.accent, transition: 'width 0.2s' } })))
              : h('span', { style: { fontSize: 13, color: C.text } },
                  h('strong', { style: { color: C.accent } }, 'Rate: ' + shipMoney(tot)),
                  h('span', { style: { fontSize: 11, color: C.textMuted } }, '  · cheapest services, ' + rated.length + '/' + n + ' rated')));
        })(),
        canShip && h('button', { onClick: function() { getRates(panelRows); }, disabled: rating, style: { width: '100%', background: 'transparent', color: C.text, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 0', fontSize: 12, fontWeight: 500, cursor: rating ? 'default' : 'pointer', fontFamily: 'inherit', marginBottom: 6 } },
          rating ? 'Rating… ' + (rateProg ? rateProg.done + '/' + rateProg.total : '') : 'Get Rate (' + n.toLocaleString() + ')'),
        canShip && h('button', { onClick: function() { openMarkShipped(panelRows); }, style: { width: '100%', background: 'transparent', color: C.success, border: '1px solid ' + C.success, borderRadius: 8, padding: '7px 0', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 } },
          'Mark as Shipped (' + n.toLocaleString() + ')'),
        h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Bulk update'),
        h('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 } },
          chip('Tag', 'tag', function() { bulk('tag'); }),
          chip('Untag', 'tag', function() { bulk('untag'); }),
          chip('Assign', 'user', function() { bulk('assign'); }),
          chip('Hold', 'clock', function() { bulk('hold'); }),
          chip('Note', 'sticky-note', function() { bulk('note'); }),
          chip('Weight', 'scale', function() { bulk('weight'); }),
          chip('Package', 'box', function() { bulk('package'); }),
          chip('Confirmation', 'check-circle', function() { bulk('confirmation'); }),
          chip('Carrier & Service', 'box', function() { bulk('service'); }),
          chip('Notify', 'bell-off', function() { bulk('notify'); }),
          // Cancel here (Anthony 9-19): Zentail has no cancel API — the order leaves the queue and any
          // batch, cannot be labelled, and waits under Orders › Cancelled with a Restore button.
          canCancelHere && chip('Cancel', 'x-circle', function() { bulk('cancel'); }),
          bucket === 'cancelled' && chip('Restore', 'rotate-ccw', function() { restoreSelected(); }),
          !batchMode && chip('Clear selection', 'x', function() { setSelected({}); })),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 } },
          h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Items'),
          h('span', { style: { fontSize: 10, fontWeight: 500, background: C.bg, border: '1px solid ' + C.border, color: C.textMuted, padding: '1px 8px', borderRadius: 999 } }, items.length.toLocaleString()),
          h('span', { style: { flex: 1 } }),
          h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Units'),
          h('span', { style: { fontSize: 10, fontWeight: 500, background: C.bg, border: '1px solid ' + C.border, color: C.textMuted, padding: '1px 8px', borderRadius: 999 } }, units.toLocaleString())),
        items.map(function(it) {
          return h('div', { key: it.sku, style: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid ' + C.border } },
            it.image_url
              ? h('img', { src: it.image_url, style: { width: 48, height: 48, objectFit: 'contain', borderRadius: 6, background: '#fff', flexShrink: 0 } })
              : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.bg, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.4, wordBreak: 'break-word' } }, it.title),
              h('div', { style: { fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, it.sku)),
            h('div', { style: { fontSize: 17, fontWeight: 700, color: C.text, background: C.warningSoft || C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: '4px 12px', flexShrink: 0, fontVariantNumeric: 'tabular-nums' } }, it.qty.toLocaleString()));
        })
      );
    })(selCount >= 2 ? selRows : batchRows, selCount < 2)
        : h('div', { style: { background: C.surface, border: '1px dashed ' + C.border, borderRadius: 12, flex: 1, minHeight: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 20 } },
            h(LIcon, { name: 'package', size: 26, color: C.textDim }),
            h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', lineHeight: 1.5, maxWidth: 220 } },
              batchId && filtered.length ? 'Every order in this batch has a label.' : 'Select an order to see its details.',
              h('br'),
              'Check several for bulk actions and a pick summary.'))
      )
    ),
    bulkMode && h(Modal, {
      // the X / Escape resets the tag toggles too, like Cancel (sweep 2026-09-27 M15: reopening the
      // dialog showed the last attempt's toggles, and Apply sent them)
      open: true, onClose: function() { setBulkMode(null); setBulkVal(''); setBulkVal2(''); setBulkTags({}); },
      // Title Case, and the Cancel dialog has a title too (sweep 2026-09-27 Lows: it had none)
      title: (function() { var n = selCount + ' Order' + (selCount === 1 ? '' : 's'); return { tag: 'Tag ' + n, untag: 'Remove Tag From ' + n, assign: 'Assign ' + n, hold: 'Hold ' + n, note: 'Note on ' + n, weight: 'Set Weight on ' + n, package: 'Set Package on ' + n, confirmation: 'Set Confirmation on ' + n, service: 'Set Carrier & Service on ' + n, notify: 'Customer Notification on ' + n, cancel: 'Cancel ' + n }[bulkMode]; })(),
      width: 420
    },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        bulkMode === 'tag' && (function() {
          // Checklist (Anthony 9-22): tags the whole selection carries come up checked, tags only some
          // of it carries come up mixed; clicking toggles, Apply adds and removes in one go. A tag the
          // rules applied stays checked (removing it would only come back on the next pass).
          var counts = {}, ruleOn = {};
          selRows.forEach(function(o) {
            (o.tags || []).forEach(function(t) { counts[t] = (counts[t] || 0) + 1; });
            ((o.rule_state && o.rule_state.tags) || []).forEach(function(t) { ruleOn[t] = 1; });
          });
          var names = Object.keys(allTags);
          Object.keys(counts).forEach(function(t) { if (names.indexOf(t) === -1) names.push(t); });
          names.sort();
          var stateOf = function(t) {
            if (t in bulkTags) return bulkTags[t];
            var have = counts[t] || 0;
            return have === 0 ? 'off' : have === selCount ? 'on' : 'mixed';
          };
          return h('div', null,
            h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, marginBottom: 6 } }, 'Tags on the ' + selCount + ' selected order' + (selCount === 1 ? '' : 's') + ' — click to add or remove'),
            names.length > 0 && h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 } },
              names.map(function(t) {
                var st = stateOf(t);
                var have = counts[t] || 0;
                var locked = !!ruleOn[t];
                var on = st === 'on', mixed = st === 'mixed';
                return h('button', { key: t,
                  onClick: function() { setBulkTags(function(prev) { var n = Object.assign({}, prev); n[t] = (st === 'on') ? 'off' : 'on'; return n; }); },
                  title: locked ? 'Applied by a rule — unchecking keeps it off for these orders' : have ? have + ' of ' + selCount + ' selected orders have this tag' : undefined,
                  style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', fontSize: 11, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                    border: '1px solid ' + (on ? C.accent : mixed ? C.warning : C.border), background: on ? C.accentSoft : mixed ? C.warningSoft : C.surface, color: C.text } },
                  h('span', { style: { width: 12, height: 12, borderRadius: 3, border: '1px solid ' + (on ? C.accent : mixed ? C.warning : C.border), background: on ? C.accent : mixed ? C.warning : 'transparent', color: '#fff', fontSize: 9, lineHeight: '12px', textAlign: 'center' } }, on ? '✓' : mixed ? '–' : ''),
                  h('span', { style: { width: 8, height: 8, borderRadius: 2, background: shipTagColor(t) } }),
                  t,
                  locked && h('span', { style: { fontSize: 9, color: C.textDim } }, 'rule'));
              })),
            h(Inp, { label: 'New tag', value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, placeholder: 'e.g. Fragile' }));
        })(),
        bulkMode === 'untag' && (function() {
          // Removal targets tags actually on the selected orders (click to toggle)
          var present = {};
          selRows.forEach(function(o) { (o.tags || []).forEach(function(t) { present[t] = 1; }); });
          var names = Object.keys(present).sort();
          if (!names.length) return h('div', { style: { fontSize: 12, color: C.textMuted } }, 'No tags on the selected orders.');
          return h('div', null,
            h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, marginBottom: 6 } }, 'Pick the tag to remove from the selection'),
            h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap' } },
              names.map(function(t) {
                var on = bulkVal === t;
                var col = shipTagColor(t);
                return h('button', { key: t, onClick: function() { setBulkVal(on ? '' : t); },
                  style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', fontSize: 11, borderRadius: 999, border: '1px solid ' + (on ? C.danger : C.border), background: on ? C.dangerSoft : C.surface, color: on ? C.danger : C.textMuted, cursor: 'pointer', fontFamily: 'inherit', textDecoration: on ? 'line-through' : 'none' } },
                  h('span', { style: { width: 8, height: 8, borderRadius: 2, background: col } }), t);
              })),
            h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 8 } }, 'Rule-applied tags come back on the next pass — edit the rule to drop those.'));
        })(),
        bulkMode === 'assign' && h(Inp, { label: 'Assignee (name)', value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, placeholder: 'e.g. Zairy' }),
        bulkMode === 'hold' && h(Inp, { label: 'Hold until (inclusive)', type: 'date', value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); } }),
        bulkMode === 'note' && h('textarea', { value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, rows: 3, placeholder: 'Note visible on hover in the queue', style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' } }),
        bulkMode === 'weight' && h(Inp, { label: 'Weight per order (lb) — leave blank to clear the override', type: 'number', step: '0.01', min: 0, value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); } }),
        bulkMode === 'package' && h('div', null,
          h('label', { style: { fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Package template (sets dimensions' + ' — labels use its size)'),
          h('select', { value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
            h('option', { value: '' }, '— Clear Override —'),
            bulkPkgTpls.map(function(t) { return h('option', { key: t.id, value: t.id }, t.name + (t.length_in ? ' (' + parseFloat(t.length_in) + '×' + parseFloat(t.width_in) + '×' + parseFloat(t.height_in) + ' in)' : '')); }))),
        bulkMode === 'confirmation' && h('div', null,
          h('label', { style: { fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Confirmation'),
          h('select', { value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
            h('option', { value: '' }, '— Clear Override —'),
            h('option', { value: 'none' }, 'No Confirmation'),
            h('option', { value: 'delivery' }, 'Delivery Confirmation'),
            h('option', { value: 'signature' }, 'Signature'),
            h('option', { value: 'adult_signature' }, 'Adult Signature'))),
        bulkMode === 'service' && (function() {
          var selS = { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' };
          var manual = bulkVal === 'USPS' || bulkVal === 'dhl_global_mail' || bulkVal === 'ups';
          var svcs = manual ? bulkSvcNames(bulkVal) : [];
          var dest = bulkSvcDest();
          return h('div', null,
            h('div', { style: { fontSize: 12, color: C.text, marginBottom: 6 } }, 'Sets the carrier and service on the ' + selRows.length + ' selected order' + (selRows.length === 1 ? '' : 's') + ' by hand, exactly as the order panel does. The rules are not changed.'),
            h('label', { style: { fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Carrier'),
            h('select', { value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); setBulkVal2(''); }, style: selS },
              h('option', { value: '' }, '— Clear Override (Rules Decide) —'),
              h('option', { value: 'rate_shop' }, 'Rate Shopper'),
              h('option', { value: 'USPS' }, 'USPS'),
              h('option', { value: 'dhl_global_mail' }, 'DHL eCommerce'),
              h('option', { value: 'ups' }, 'UPS')),
            manual && h('label', { style: { fontSize: 12, color: C.textMuted, display: 'block', margin: '10px 0 4px' } }, 'Service'),
            manual && (bulkSvcCatalog === null
              ? h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Loading services…')
              : svcs.length
                ? h('select', { value: bulkVal2, onChange: function(e) { setBulkVal2(e.target.value); }, style: selS },
                    h('option', { value: '' }, 'Service…'),
                    svcs.map(function(n) { return h('option', { key: n, value: n }, n); }))
                : h('div', { style: { fontSize: 12, color: C.danger } }, 'No enabled ' + (bulkVal === 'USPS' ? 'USPS' : 'DHL') + ' service serves ' + (dest === 'international' ? 'international' : 'domestic') + ' orders — check Settings > Integrations.')),
            manual && dest === 'mixed' && h('div', { style: { fontSize: 12, color: C.warning, marginTop: 8 } }, 'The selection mixes domestic and international orders: every enabled service is listed, and a service that does not serve an order\'s destination will fail at label time for that order.'));
        })(),
        bulkMode === 'cancel' && h('div', null,
          h('div', { style: { fontSize: 12, color: C.text, marginBottom: 6 } }, 'Cancel ' + selRows.length + ' order' + (selRows.length === 1 ? '' : 's') + ' in the shipping app only. They leave the queue and any batch and cannot be labelled; Zentail keeps showing them pending until they are cancelled there too. Restore from Orders › Cancelled.'),
          h('textarea', { value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, rows: 2, placeholder: 'Reason (optional) — shows on the order', style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 10px', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' } })),
        bulkMode === 'notify' && h('div', null,
          h('label', { style: { fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Customer notification (recorded on the shipment; suppression itself depends on Zentail/channel support)'),
          h('select', { value: bulkVal, onChange: function(e) { setBulkVal(e.target.value); }, style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
            h('option', { value: '' }, '— Clear Flag (Notify Normally) —'),
            h('option', { value: 'on' }, 'Do Not Notify Customer'))),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          h(Btn, { onClick: function() { setBulkMode(null); setBulkVal(''); setBulkVal2(''); setBulkTags({}); } }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: function() {
            if (bulkMode === 'tag') {
              var tagCounts = {};
              selRows.forEach(function(o) { (o.tags || []).forEach(function(t) { tagCounts[t] = (tagCounts[t] || 0) + 1; }); });
              var addT = [], removeT = [];
              Object.keys(bulkTags).forEach(function(t) {
                var have = tagCounts[t] || 0;
                if (bulkTags[t] === 'on' && have < selCount) addT.push(t);
                if (bulkTags[t] === 'off' && have > 0) removeT.push(t);
              });
              var typed = bulkVal.trim();
              if (typed && addT.indexOf(typed) === -1) addT.push(typed);
              if (!addT.length && !removeT.length) { showToast('Nothing to change', 'error'); return; }
              var body = {}; if (addT.length) body.add_tags = addT; if (removeT.length) body.remove_tags = removeT;
              applyBulkMeta(body, 'Tags updated');
            }
            else if (bulkMode === 'untag') { if (!bulkVal.trim()) return; applyBulkMeta({ remove_tags: [bulkVal.trim()] }, 'Tag removed'); }
            else if (bulkMode === 'assign') applyBulkMeta({ assignee: bulkVal.trim() || null }, 'Assigned');
            else if (bulkMode === 'hold') { if (!bulkVal) return; applyBulkMeta({ hold_until: bulkVal }, 'Held'); }
            else if (bulkMode === 'note') applyBulkMeta({ note: bulkVal.trim() || null }, 'Noted');
            else if (bulkMode === 'weight') {
              if (bulkVal !== '' && !(parseFloat(bulkVal) > 0)) return;
              applyBulkMeta({ overrides_patch: { weight_lb: bulkVal !== '' ? parseFloat(bulkVal) : null } }, 'Weight set');
            }
            else if (bulkMode === 'package') applyBulkMeta({ overrides_patch: { package_template_id: bulkVal ? parseInt(bulkVal, 10) : null } }, 'Package set');
            else if (bulkMode === 'confirmation') applyBulkMeta({ overrides_patch: { confirmation: bulkVal || null } }, 'Confirmation set');
            else if (bulkMode === 'notify') applyBulkMeta({ overrides_patch: { flags: { no_notify_customer: bulkVal === 'on' ? true : null } } }, 'Customer notification set');
            else if (bulkMode === 'service') {
              var manualSvc = bulkVal === 'USPS' || bulkVal === 'dhl_global_mail' || bulkVal === 'ups';
              if (manualSvc && !bulkVal2) { showToast('Pick a service', 'error'); return; }
              applyBulkMeta({ overrides_patch: { carrier: bulkVal || null, service: manualSvc ? bulkVal2 : null } }, bulkVal ? 'Carrier & service set' : 'Carrier override cleared');
            }
            else if (bulkMode === 'cancel') cancelSelected(bulkVal.trim() || null);
          } }, 'Apply')
        )
      )
    )
  );
}

;
/* ===== app-shipping-rules.js ===== */
// ─── Shipping app: automation rules editor ─── split 2026-09-19
// ─── Rules tab ────────────────────────────────────────────────────────────
function ShipRuleEditor({ rule, fields, actionTypes, onClose, onSaved, showToast }) {
  var isEdit = rule && rule.id;
  var [name, setName] = useState(rule ? rule.name : '');
  var [enabled, setEnabled] = useState(rule ? rule.enabled !== false : true);
  var [conds, setConds] = useState(rule && rule.conditions ? rule.conditions.slice() : []);
  var [acts, setActs] = useState(rule && rule.actions ? rule.actions.slice() : []);
  var [matchMode, setMatchMode] = useState((rule && rule.match_mode) === 'any' ? 'any' : 'all');
  var [saving, setSaving] = useState(false);
  // Live value lists for dropdown criteria (country/state/channel/tag)
  var [fieldValues, setFieldValues] = useState({});
  // Loaded when the editor opens and again whenever the window regains focus: a tag created in
  // Settings › Tags in another tab was missing from Add tag until a page reload (Anthony 2026-10-29)
  useEffect(function() {
    var load = function() { api('/shipping/rule-field-values').then(setFieldValues).catch(function() {}); };
    load();
    window.addEventListener('focus', load);
    return function() { window.removeEventListener('focus', load); };
  }, []);
  // Enabled services per action-carrier (same map the order panel uses)
  var [svcCatalog, setSvcCatalog] = useState(null);
  useEffect(function() {
    var KEY_MAP = { stamps_com: 'USPS', dhl_global_mail: 'dhl_global_mail', dhl_ecommerce_us: 'dhl_global_mail', ups: 'UPS' }; // 'UPS' = the ACTION_CARRIERS value (2026-10-29)
    api('/shipping/carrier-services').then(function(r) {
      var cat = {};
      (r.carriers || []).forEach(function(c) {
        var k = KEY_MAP[c.carrier]; if (!k) return;
        cat[k] = (cat[k] || []).concat(c.services.filter(function(s) { return s.enabled; }).map(function(s) { return s.name; }));
      });
      setSvcCatalog(cat);
    }).catch(function() { setSvcCatalog(null); });
  }, []);
  // Settings lists for the set_package / set_ship_from / set_tax_profile actions
  var [pkgTpls, setPkgTpls] = useState([]);
  var [shipFroms, setShipFroms] = useState([]);
  var [taxProfiles, setTaxProfiles] = useState([]);
  useEffect(function() {
    shipFetchList('/shipping/package-templates', 'templates').then(setPkgTpls);
    shipFetchList('/shipping/ship-from', 'addresses').then(setShipFroms);
    shipFetchList('/shipping/tax-profiles', 'profiles').then(setTaxProfiles);
  }, []);
  var ACTION_CARRIERS = [['rate_shop', 'Rate Shopper'], ['USPS', 'USPS'], ['dhl_global_mail', 'DHL eCommerce'], ['globalpost', 'GlobalPost'], ['UPS', 'UPS'], ['FedEx', 'FedEx']];
  var DROPDOWN_FIELDS = { country: 'Countries', state: 'States', channel: 'Channels', tag: 'Tags', requested_service: 'Requested services', carrier: 'Carriers', is_international: 'Values', is_dropship: 'Values', is_business: 'Values' };
  var asArray = function(v) {
    if (Array.isArray(v)) return v;
    return String(v == null ? '' : v).split(/[,;\n]+/).map(function(s) { return s.trim(); }).filter(Boolean);
  };

  var OPS_STR = ['eq', 'ne', 'in', 'not_in', 'contains', 'not_contains'];
  var OPS_NUM = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between'];
  var OPS_MULTI = ['in', 'not_in'];
  var OP_LBL = { eq: '=', ne: '≠', in: 'is one of', not_in: 'is not one of', contains: 'contains', not_contains: "doesn't contain", gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between' };
  var OP_DESC = {
    eq: 'Equals — matches this value exactly',
    ne: 'Not equal — matches anything except this value',
    in: 'Is one of — matches any of the selected/listed values',
    not_in: 'Is not one of — excludes all of the selected/listed values',
    contains: 'Contains — the value appears anywhere in the text',
    not_contains: 'Does not contain — the value appears nowhere in the text (on a per-line field, no line contains it)',
    gt: 'Greater than',
    gte: 'Greater than or equal to',
    lt: 'Less than',
    lte: 'Less than or equal to',
    between: 'Between — min and max, inclusive (enter: min, max)',
  };

  var fieldType = function(key) { var f = fields.find(function(x) { return x.key === key; }); return f ? f.type : 'string'; };

  // Every condition needs a value, numbers where numbers go, and every action its target (sweep
  // 2026-09-27 H2: a blank "contains" matched every order) — the server refuses the same things
  var ruleProblem = (function() {
    var types = {}, labels = {};
    (fields || []).forEach(function(f) { types[f.key] = f.type; labels[f.key] = f.label; });
    var p = shipConditionsProblem(conds, types, labels);
    if (p) return p;
    for (var ai = 0; ai < acts.length; ai++) {
      var a = acts[ai] || {};
      if (a.type === 'set_carrier' && !a.carrier) return 'Set carrier/service: choose a carrier';
      if (a.type === 'add_tag' && !((Array.isArray(a.tags) && a.tags.length) || a.tag)) return 'Add tag: choose at least one tag';
    }
    return null;
  })();
  var save = function() {
    if (!name.trim()) { showToast('Rule needs a name', 'error'); return; }
    if (ruleProblem) { showToast(ruleProblem, 'error'); return; }
    setSaving(true);
    var body = { name: name.trim(), enabled: enabled, conditions: conds, actions: acts, match_mode: matchMode };
    var path = isEdit ? '/shipping/rules/' + rule.id : '/shipping/rules';
    api(path, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) })
      .then(function() { onSaved(); onClose(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  var selStyle = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' };
  var inpStyle = Object.assign({}, selStyle, { flex: 1, minWidth: 120 });

  return h(Modal, { open: true, onClose: onClose, title: isEdit ? 'Edit Rule' : 'New Rule', width: 680 },
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
      h('div', { style: { display: 'flex', gap: 12, alignItems: 'flex-end' } },
        h('div', { style: { flex: 1 } }, h(Inp, { label: 'Rule name', value: name, onChange: function(e) { setName(e.target.value); }, placeholder: 'e.g. AU orders → DHL + GST tag' })),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, color: C.text, fontSize: 13, cursor: 'pointer', paddingBottom: 8 } },
          h('input', { type: 'checkbox', checked: enabled, onChange: function(e) { setEnabled(e.target.checked); }, style: { accentColor: C.accent, width: 14, height: 14, cursor: 'pointer' } }), 'Enabled')
      ),
      h('div', null,
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
          h('span', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'When'),
          h('select', { value: matchMode, onChange: function(e) { setMatchMode(e.target.value); }, style: { background: C.bg, color: C.accent, border: '1px solid ' + C.accent, borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' } },
            h('option', { value: 'all' }, 'ALL'),
            h('option', { value: 'any' }, 'ANY')),
          h('span', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } },
            matchMode === 'any' ? 'of these match (OR)' : 'of these match (AND)')),
        conds.map(function(c, i) {
          var isDrop = !!DROPDOWN_FIELDS[c.field];
          var ops = isDrop ? OPS_MULTI : (fieldType(c.field) === 'number' ? OPS_NUM : OPS_STR);
          var curOp = isDrop && OPS_MULTI.indexOf(c.op) === -1 ? 'in' : c.op;
          return h('div', { key: i, style: { display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' } },
            h('select', { value: c.field, onChange: function(e) {
                var nf = e.target.value;
                var n = conds.slice();
                // Changing the field starts a fresh criterion — old values must
                // not tag along (a country list is meaningless on a new field)
                n[i] = {
                  field: nf,
                  op: DROPDOWN_FIELDS[nf] ? 'in' : (fieldType(nf) === 'number' ? 'eq' : 'contains'),
                  value: DROPDOWN_FIELDS[nf] ? [] : '',
                };
                setConds(n);
              }, style: selStyle },
              fields.map(function(f) { return h('option', { key: f.key, value: f.key }, f.label); })),
            h('select', { value: curOp, title: OP_DESC[curOp] || '', onChange: function(e) { var n = conds.slice(); n[i] = Object.assign({}, c, { op: e.target.value }); setConds(n); }, style: selStyle },
              ops.map(function(o) { return h('option', { key: o, value: o, title: OP_DESC[o] || '' }, OP_LBL[o]); })),
            isDrop
              ? h(ReportParamSelect, {
                  label: DROPDOWN_FIELDS[c.field],
                  values: fieldValues[c.field] || [],
                  selected: asArray(c.value),
                  onChange: function(v) { var n = conds.slice(); n[i] = Object.assign({}, c, { op: curOp, value: v }); setConds(n); },
                  valueColors: c.field === 'tag' ? (function() { var m = {}; (fieldValues.tag || []).forEach(function(t) { m[t] = shipTagColor(t); }); return m; })() : undefined,
                })
              : h('input', { value: c.value == null ? '' : (Array.isArray(c.value) ? c.value.join(', ') : c.value),
                  onChange: function(e) { var n = conds.slice(); n[i] = Object.assign({}, c, { value: e.target.value }); setConds(n); },
                  placeholder: c.op === 'between' ? 'min, max' : (c.op === 'in' || c.op === 'not_in') ? 'comma-separated' : 'value',
                  style: inpStyle }),
            h('button', { 'aria-label': 'Remove Condition', title: 'Remove Condition', onClick: function() { setConds(conds.filter(function(_, x) { return x !== i; })); }, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer' } }, h(LIcon, { name: 'x', size: 14 }))
          );
        }),
        h(Btn, { size: 'sm', onClick: function() { setConds(conds.concat([{ field: 'channel', op: 'contains', value: '' }])); } }, h(LIcon, { name: 'plus', size: 12 }), ' Condition'),
        conds.length === 0 && h('span', { style: { fontSize: 11, color: C.textDim, marginLeft: 10 } }, 'No conditions = matches every order')
      ),
      h('div', null,
        h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, 'Then do'),
        acts.map(function(a, i) {
          return h('div', { key: i, style: { display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' } },
            h('select', { value: a.type, onChange: function(e) { var n = acts.slice(); n[i] = { type: e.target.value }; setActs(n); }, style: selStyle },
              actionTypes.map(function(t) { return h('option', { key: t.key, value: t.key }, t.label); })),
            a.type === 'set_carrier' && h('select', { value: a.carrier || '', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { carrier: e.target.value, service: '' }); setActs(n); }, style: selStyle },
              [['', 'carrier…']].concat(ACTION_CARRIERS).map(function(c) { return h('option', { key: c[0], value: c[0] }, c[1]); })),
            a.type === 'set_carrier' && a.carrier !== 'rate_shop' && ((svcCatalog && (svcCatalog[a.carrier] || []).length)
              ? h('select', { value: a.service || '', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { service: e.target.value }); setActs(n); }, style: Object.assign({}, selStyle, { maxWidth: 220 }) },
                  h('option', { value: '' }, 'Service…'),
                  (a.service && (svcCatalog[a.carrier] || []).indexOf(a.service) === -1) && h('option', { value: a.service }, a.service + ' (saved)'),
                  (svcCatalog[a.carrier] || []).map(function(nm) { return h('option', { key: nm, value: nm }, nm); }))
              : h('input', { value: a.service || '', placeholder: 'service name', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { service: e.target.value }); setActs(n); }, style: inpStyle })),
            a.type === 'add_tag' && h(ReportParamSelect, {
              label: 'Tags',
              values: fieldValues.tag || [],
              selected: (Array.isArray(a.tags) && a.tags.length) ? a.tags : (a.tag ? [a.tag] : []),
              onChange: function(v) { var n = acts.slice(); n[i] = { type: 'add_tag', tags: v }; setActs(n); },
              valueColors: (function() { var m = {}; (fieldValues.tag || []).forEach(function(t) { m[t] = shipTagColor(t); }); return m; })(),
              onCreate: async function() {
                var name = await appPrompt('New tag name:');
                if (!name || !name.trim()) return;
                api('/shipping/tags', { method: 'POST', body: JSON.stringify({ name: name.trim() }) })
                  .then(function(r) {
                    if (r.color) _shipTagColors[r.name] = r.color;
                    setFieldValues(function(p) { var n2 = Object.assign({}, p); n2.tag = (p.tag || []).indexOf(r.name) === -1 ? (p.tag || []).concat([r.name]) : p.tag; return n2; });
                    showToast('Tag "' + r.name + '" created', 'success');
                  })
                  .catch(function(e) { showToast(e.message, 'error'); });
              },
              createLabel: 'Create new tag',
            }),
            a.type === 'set_dimensions' && ['length', 'width', 'height'].map(function(dk, di) {
              return h(React.Fragment, { key: dk },
                h('input', { type: 'number', value: a[dk] || '', placeholder: 'LWH'[di], onChange: function(e) { var n = acts.slice(); var p = {}; p[dk] = e.target.value; n[i] = Object.assign({}, a, p); setActs(n); }, style: Object.assign({}, selStyle, { width: 62 }) }),
                di < 2 && h('span', { style: { color: C.textDim, fontSize: 11 } }, '×'));
            }),
            a.type === 'set_package' && h('select', { value: a.template_id || '', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { template_id: e.target.value }); setActs(n); }, style: selStyle },
              h('option', { value: '' }, 'Template…'),
              pkgTpls.map(function(t) { return h('option', { key: t.id, value: t.id }, t.name); })),
            a.type === 'set_ship_from' && h('select', { value: a.address_id || '', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { address_id: e.target.value }); setActs(n); }, style: selStyle },
              h('option', { value: '' }, 'Address…'),
              shipFroms.map(function(s) { return h('option', { key: s.id, value: s.id }, s.name + (s.is_default ? ' (default)' : '')); })),
            a.type === 'set_tax_profile' && (taxProfiles.length
              ? h('select', { value: a.profile || '', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { profile: e.target.value }); setActs(n); }, style: selStyle },
                  h('option', { value: '' }, 'Profile…'),
                  (a.profile && !taxProfiles.some(function(p) { return p.name === a.profile; })) && h('option', { value: a.profile }, a.profile + ' (saved)'),
                  taxProfiles.map(function(p) { return h('option', { key: p.id, value: p.name }, p.name + (p.scheme ? ' (' + p.scheme + ')' : '')); }))
              : h('input', { value: a.profile || '', placeholder: 'profile (e.g. amazon-eu-ioss)', onChange: function(e) { var n = acts.slice(); n[i] = Object.assign({}, a, { profile: e.target.value }); setActs(n); }, style: inpStyle })),
            h('button', { 'aria-label': 'Remove Action', title: 'Remove Action', onClick: function() { setActs(acts.filter(function(_, x) { return x !== i; })); }, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer' } }, h(LIcon, { name: 'x', size: 14 }))
          );
        }),
        h(Btn, { size: 'sm', onClick: function() { setActs(acts.concat([{ type: 'add_tag' }])); } }, h(LIcon, { name: 'plus', size: 12 }), ' Action')
      ),
      h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' } },
        ruleProblem && h('span', { role: 'alert', style: { flex: 1, fontSize: 11.5, color: C.danger } }, ruleProblem),
        h(Btn, { onClick: onClose }, 'Cancel'),
        h(Btn, { variant: 'primary', onClick: save, disabled: saving || !!ruleProblem }, saving ? 'Saving…' : (isEdit ? 'Update Rule' : 'Create Rule'))
      )
    )
  );
}

function ShipRulesTab({ showToast }) {
  var [data, setData] = useState(null);
  var [editing, setEditing] = useState(null);
  var [dragId, setDragId] = useState(null);
  var [overId, setOverId] = useState(null);
  var [running, setRunning] = useState(false);
  var [runCounts, setRunCounts] = useState(null); // {per_rule, orders, matched_orders}

  var runAutomations = function() {
    setRunning(true);
    api('/shipping/rules/run', { method: 'POST', body: '{}' })
      .then(function(r) {
        setRunCounts(r);
        showToast('Automations applied to ' + r.orders.toLocaleString() + ' open orders — ' + r.matched_orders.toLocaleString() + ' matched at least one rule', 'success');
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setRunning(false); });
  };

  var [loadErr, setLoadErr] = useState(null);
  var load = useCallback(function() {
    api('/shipping/rules').then(function(r) { setLoadErr(null); setData(r); }).catch(function(e) { setLoadErr(e.message || 'Could not load the rules'); showToast(e.message, 'error'); });
  }, []);
  useEffect(function() { load(); }, [load]);

  if (!data && loadErr) return h('div', { style: { padding: 30, textAlign: 'center', color: C.danger } }, 'Could not load the rules: ' + loadErr + ' ', h(Btn, { size: 'sm', onClick: load }, 'Retry'));
  if (!data) return h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted } }, 'Loading…');
  var rules = data.rules || [];

  // Drag a rule card onto another to reprioritize; sort_order rewrites 1..N
  var dropRule = function(targetId) {
    var dk = dragId;
    setDragId(null); setOverId(null);
    if (!dk || dk === targetId) return;
    var order = rules.map(function(r) { return r.id; });
    var from = order.indexOf(dk), to = order.indexOf(targetId);
    if (from < 0 || to < 0) return;
    order.splice(from, 1);
    order.splice(to, 0, dk);
    var updates = [];
    order.forEach(function(id, idx) {
      var r = rules.find(function(x) { return x.id === id; });
      if (r && r.sort_order !== idx + 1) updates.push(api('/shipping/rules/' + id, { method: 'PUT', body: JSON.stringify({ sort_order: idx + 1 }) }));
    });
    Promise.all(updates).then(load).catch(function(e) { showToast(e.message, 'error'); });
  };

  var move = function(rule, dir) {
    var idx = rules.findIndex(function(r) { return r.id === rule.id; });
    var other = rules[idx + dir];
    if (!other) return;
    Promise.all([
      api('/shipping/rules/' + rule.id, { method: 'PUT', body: JSON.stringify({ sort_order: other.sort_order }) }),
      api('/shipping/rules/' + other.id, { method: 'PUT', body: JSON.stringify({ sort_order: rule.sort_order }) }),
    ]).then(load).catch(function(e) { showToast(e.message, 'error'); });
  };

  var summary = function(r) {
    // Human-readable rule sentences ("Order value < 100", "order is not dropship")
    // — raw field/op keys read terribly on the cards ("value lt 100").
    var FLBL = {};
    (data.condition_fields || []).forEach(function(f) { FLBL[f.key] = f.label; });
    var phrase = function(x) {
      var label = FLBL[x.field] || x.field;
      var vals = Array.isArray(x.value) ? x.value : (x.value == null || x.value === '' ? [] : [x.value]);
      var list = vals.join(', ');
      if (x.field === 'is_international' || x.field === 'is_dropship' || x.field === 'is_business') {
        var yes = String(vals[0]).toUpperCase() === 'TRUE';
        var noun = x.field === 'is_dropship' ? 'dropship' : x.field === 'is_business' ? 'to a business address' : 'international';
        return (x.op === 'not_in' ? !yes : yes) ? 'order is ' + noun : 'order is not ' + noun;
      }
      switch (x.op) {
        case 'in': return vals.length > 1 ? label + ' is one of: ' + list : label + ' is ' + list;
        case 'not_in': return vals.length > 1 ? label + ' is none of: ' + list : label + ' is not ' + list;
        case 'contains': return label + ' contains “' + list + '”';
        case 'not_contains': return label + ' doesn’t contain “' + list + '”';
        case 'eq': return label + ' = ' + list;
        case 'ne': return label + ' ≠ ' + list;
        case 'gt': return label + ' > ' + list;
        case 'gte': return label + ' ≥ ' + list;
        case 'lt': return label + ' < ' + list;
        case 'lte': return label + ' ≤ ' + list;
        case 'between': return label + ' between ' + vals.join(' and ');
        default: return label + ' ' + x.op + ' ' + list;
      }
    };
    var joiner = r.match_mode === 'any' ? ' OR ' : ' AND ';
    var c = (r.conditions || []).map(phrase).join(joiner) || 'every order';
    var a = (r.actions || []).map(function(x) {
      if (x.type === 'set_carrier') return x.carrier === 'rate_shop' ? 'send to Rate Shopper' : 'ship ' + (shipCarrierLabel(x.carrier) || '?') + ' · ' + (x.service || 'any service');
      if (x.type === 'add_tag') return 'tag ' + ((Array.isArray(x.tags) && x.tags.length) ? x.tags.join(', ') : (x.tag || '?'));
      if (x.type === 'set_dimensions') return 'set size ' + (x.length || '?') + '×' + (x.width || '?') + '×' + (x.height || '?') + ' in';
      if (x.type === 'set_package') return 'use package template #' + (x.template_id || '?');
      if (x.type === 'set_ship_from') return 'ship from address #' + (x.address_id || '?');
      if (x.type === 'set_tax_profile') return 'use tax profile ' + (x.profile || '?');
      if (x.type === 'hold') return 'hold the order';
      return x.type;
    }).join(' · ');
    return { c: c, a: a };
  };

  return h('div', { style: { maxWidth: 800 } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 8, flexWrap: 'wrap' } },
      h('div', { style: { fontSize: 12, color: C.textMuted } }, rules.length + ' rule' + (rules.length === 1 ? '' : 's') + ' — run top to bottom; later carrier assignments win, tags accumulate'),
      h('div', { style: { display: 'flex', gap: 8 } },
        h(Btn, { size: 'sm', onClick: runAutomations, disabled: running },
          h(LIcon, { name: running ? 'loader-2' : 'zap', size: 12, spin: running }), running ? ' Running…' : ' Run Automations'),
        h(Btn, { variant: 'primary', size: 'sm', onClick: function() { setEditing({}); } }, h(LIcon, { name: 'plus', size: 12 }), ' New Rule'))
    ),
    rules.length === 0
      ? h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted, background: C.surface, border: '1px dashed ' + C.border, borderRadius: 8, fontSize: 13 } },
          'No rules yet. Recreate your ShipStation automations here — e.g. "country ≠ US → set carrier dhl_global_mail + add tag INTL".')
      : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
          rules.map(function(r, i) {
            var s = summary(r);
            return h(Card, {
              key: r.id,
              draggable: true,
              onDragStart: function(e) { setDragId(r.id); e.dataTransfer.effectAllowed = 'move'; },
              onDragOver: function(e) { if (!dragId || dragId === r.id) return; e.preventDefault(); if (overId !== r.id) setOverId(r.id); },
              onDragLeave: function() { if (overId === r.id) setOverId(null); },
              onDrop: function(e) { e.preventDefault(); dropRule(r.id); },
              onDragEnd: function() { setDragId(null); setOverId(null); },
              style: { padding: 12, opacity: dragId === r.id ? 0.4 : (r.enabled === false ? 0.55 : 1), cursor: 'grab', boxShadow: overId === r.id && dragId !== r.id ? 'inset 0 3px 0 ' + C.accent : undefined }
            },
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
                h(LIcon, { name: 'grip-vertical', size: 15, color: C.textDim }),
                h('span', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, background: C.bg, border: '1px solid ' + C.border, borderRadius: 999, padding: '2px 8px' } }, '#' + (i + 1)),
                h('div', { style: { display: 'flex', flexDirection: 'column', gap: 2 } },
                  h('button', { onClick: function() { move(r, -1); }, disabled: i === 0, style: { background: 'transparent', border: 'none', color: i === 0 ? C.textDim : C.textMuted, cursor: i === 0 ? 'default' : 'pointer', padding: 0 } }, h(LIcon, { name: 'arrow-up', size: 13 })),
                  h('button', { onClick: function() { move(r, 1); }, disabled: i === rules.length - 1, style: { background: 'transparent', border: 'none', color: i === rules.length - 1 ? C.textDim : C.textMuted, cursor: i === rules.length - 1 ? 'default' : 'pointer', padding: 0 } }, h(LIcon, { name: 'arrow-down', size: 13 }))
                ),
                h('div', { style: { flex: 1, minWidth: 200 } },
                  h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, r.name,
                    runCounts && h('span', { style: { marginLeft: 8, fontSize: 10, fontWeight: 500, padding: '1px 8px', borderRadius: 999, background: (runCounts.per_rule[r.id] || 0) > 0 ? C.successSoft : C.bg, color: (runCounts.per_rule[r.id] || 0) > 0 ? C.success : C.textDim, border: '1px solid ' + C.border } },
                      (runCounts.per_rule[r.id] || 0).toLocaleString() + ' orders')),
                  h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'If ' + s.c),
                  h('div', { style: { fontSize: 11, color: C.accent, marginTop: 1 } }, s.a || '(no actions)')
                ),
                h('div', { style: { display: 'flex', gap: 4 } },
                  h(Btn, { size: 'sm', onClick: function() {
                    api('/shipping/rules/' + r.id, { method: 'PUT', body: JSON.stringify({ enabled: r.enabled === false }) }).then(load).catch(function(e) { showToast(e.message, 'error'); });
                  } }, r.enabled === false ? 'Enable' : 'Disable'),
                  h(Btn, { size: 'sm', onClick: function() { setEditing(r); } }, h(LIcon, { name: 'pencil', size: 12 })),
                  h(Btn, { size: 'sm', onClick: async function() {
                    if (!(await appConfirm('Delete rule "' + r.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
                    api('/shipping/rules/' + r.id, { method: 'DELETE' }).then(load).catch(function(e) { showToast(e.message, 'error'); });
                  } }, h(LIcon, { name: 'trash-2', size: 12 }))
                )
              )
            );
          })
        ),
    editing && h(ShipRuleEditor, {
      rule: editing.id ? editing : null,
      fields: data.condition_fields || [],
      actionTypes: data.action_types || [],
      onClose: function() { setEditing(null); },
      onSaved: load,
      showToast: showToast,
    })
  );
}

;
/* ===== app-shipping-shipments.js ===== */
// ─── Shipping app: page shell, shipments list, shipment + product modals, sub-pages (voided / EOD / batch history) ─── split 2026-09-19
// ─── Page ─────────────────────────────────────────────────────────────────
function ShippingPage({ showToast, onNavigate, currentUser }) {
  var [tab, setTab] = useState(_shipPageCache.tab || 'queue');
  useEffect(function() { _shipPageCache.tab = tab; }, [tab]);

  return h('div', null,
    h('div', { style: { marginBottom: 18 } },
      h('h2', { style: { margin: 0, fontSize: 22, fontWeight: 500, color: C.text } }, 'Shipping',
        h('span', { style: { fontSize: 11, fontWeight: 500, color: C.warning, background: C.warningSoft || C.accentSoft, padding: '3px 8px', borderRadius: 4, marginLeft: 10, verticalAlign: 'middle', textTransform: 'uppercase' } }, 'prototype')),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } },
        'FBM ship station — Zentail stays the order master. Label purchase is stubbed until DHL eCommerce / USPS API credentials are connected; nothing here touches live orders yet.')
    ),
    h('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 18 } },
      [
        { key: 'queue', label: 'Queue' },
        { key: 'rules', label: 'Rules & Tags' },
      ].map(function(t) {
        var active = tab === t.key;
        return h('button', {
          key: t.key,
          onClick: function() { setTab(t.key); },
          style: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px 16px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', color: active ? C.accent : C.textMuted, borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', marginBottom: -1 }
        }, t.label);
      })
    ),
    tab === 'queue' && h(ShipQueueTab, { showToast: showToast }),
    tab === 'rules' && h(ShipRulesTab, { showToast: showToast })
  );
}

// ─── Shipments tab: completed (SHIPPED) orders, one row per package ────────
// Shared order-activity timeline — the order modal's rail view and the
// shipment modal's Activity toggle render the same thing (user ask 8-30).
function ShipActivityTimeline({ orderPk, showToast }) {
  var [act, setAct] = useState(null);
  useEffect(function() {
    setAct(null);
    api('/shipping/order/' + orderPk + '/activity')
      .then(function(r) { setAct(r.events || []); })
      .catch(function(e) { setAct([]); showToast && showToast(e.message, 'error'); });
  }, [orderPk]);
  return h('div', { style: { maxWidth: 760, margin: '0 auto' } },
    h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 12 } }, 'Activity'),
    act === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading activity…')
      : act.length === 0
        ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'No recorded activity for this order yet.')
        : h('div', null, act.map(function(ev, i) {
            return h('div', { key: i, style: { display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < act.length - 1 ? '1px solid ' + C.border : 'none', alignItems: 'flex-start' } },
              h('div', { style: { width: 30, height: 30, borderRadius: 999, background: C.surface, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
                h(LIcon, { name: ev.icon || 'circle', size: 14, color: C.accent })),
              h('div', { style: { flex: 1, minWidth: 0 } },
                h('div', { style: { fontSize: 13.5, fontWeight: 500, color: C.text } }, ev.title),
                ev.detail && h('div', { style: { fontSize: 12.5, color: C.textMuted, marginTop: 1, wordBreak: 'break-word', whiteSpace: 'pre-line' } }, ev.detail)),
              h('div', { style: { textAlign: 'right', flexShrink: 0 } },
                h('div', { style: { fontSize: 12, color: C.textMuted, whiteSpace: 'nowrap' } }, ev.ts ? new Date(ev.ts).toLocaleString() : ''),
                ev.user && h('div', { style: { fontSize: 11.5, color: C.textDim } }, ev.user)));
          })));
}

// The Shipments window has no row cap (Anthony 2026-10-17): the list arrives in chunks behind a `next`
// cursor until the server has no more — this ceiling (the CSV export's own) is the only stop, ~5 months
// at ~630 shipments a weekday; beyond it the header says the view is capped and Export has the rest.
var SHIP_LIST_MAX_ROWS = 100000;
function ShipShipmentsTab({ showToast, deliveryFilter }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  // Search + window are deep-linked (?q= / ?days=) — the queue's shipped-order
  // search handoff lands here with the term and a window wide enough to cover
  // the order; a manual refresh keeps the same view (deeplink convention).
  var _shUrlInit = (function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      return { q: sp.get('q') || '', days: parseInt(sp.get('days'), 10) || 0, open: parseInt(sp.get('open'), 10) || 0 };
    } catch (e) { return { q: '', days: 0, open: 0 }; }
  })();
  var [q, setQ] = useState(_shUrlInit.q);
  var [qLive, setQLive] = useState(_shUrlInit.q);
  var qSearchProps = useShipAutoSearch(qLive, setQLive, setQ); // search-as-you-type (9-18)
  // ?open=<order pk> (Orders-page handoff, 2026-09-04): open that order's
  // modal once the first list answer that contains it lands
  var openOnceRef = useRef(_shUrlInit.open);
  // 30d default (Anthony 2026-10-17: the 1d default, his 9-01 call, left Recent empty on a weekend
  // and every early morning) — the 1d pill is still there for today's work; the delivery-status
  // pages were 30d already
  var defaultDays = 30;
  var [days, setDays] = useState(_shUrlInit.days > 0 ? Math.min(_shUrlInit.days, 90) : defaultDays);
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      if (q) sp.set('q', q); else sp.delete('q');
      if (days !== defaultDays) sp.set('days', String(days)); else sp.delete('days');
      var qs = sp.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }, [q, days]);
  // Custom date range (order-date window, same semantics as the day pills);
  // set once both ends are picked, ✕ returns to the preset window
  var [range, setRange] = useState(null); // {from, to}
  var [rFrom, setRFrom] = useState('');
  var [rTo, setRTo] = useState('');
  var [ssel, setSsel] = useState({}); // row _k -> selected
  var [exportModal, setExportModal] = useState(null); // {scope: 'selected'|'filtered'}
  // Deep links (standing convention): ?s=<pk> = shipment modal (row click),
  // ?o=<pk> = full order modal (Order # click) — lazy-init + replaceState
  // mirror, foreign params preserved (queue ?o pattern, 20c)
  var [openOrder, setOpenOrder] = useState(function() {
    try { return parseInt(new URLSearchParams(window.location.search).get('s'), 10) || null; } catch (e) { return null; }
  });
  var [ordModal, setOrdModal] = useState(function() {
    try { return parseInt(new URLSearchParams(window.location.search).get('o'), 10) || null; } catch (e) { return null; }
  });
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      if (openOrder != null) sp.set('s', String(openOrder)); else sp.delete('s');
      if (ordModal != null) sp.set('o', String(ordModal)); else sp.delete('o');
      var qs = sp.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }, [openOrder, ordModal]);
  // Modal prev/next follow the grid EXACTLY as displayed (sort + filters) —
  // queue parity; onSortedData keeps this in step with header-sort clicks
  var sortedShipRef = useRef([]);
  var [busyPrint, setBusyPrint] = useState(false);

  var listGenRef = useRef(0); // review M7 #105: 90 days then 7 days — the slower 90-day answer must not win
  // No view cap (Anthony 2026-10-17: "there should be no limit on the number of orders shown"): the
  // window answer arrives in chunks of 2,500 orders behind a `next` cursor — the first chunk paints the
  // table (~1 s), the rest fill it in the background (a month ≈ 7 s). A pill / search / range change
  // bumps the generation and the old chain stops; SHIP_LIST_MAX_ROWS is the only ceiling.
  var load = useCallback(function() {
    var myGen = ++listGenRef.current;
    setLoading(true);
    var win = range ? ('from=' + range.from + '&to=' + range.to) : ('days=' + days);
    // the status slice is filtered in SQL (sweep 2026-09-27 H4)
    var base = '/shipping/shipments-list?' + win + '&q=' + encodeURIComponent(q) + (deliveryFilter ? '&status=' + encodeURIComponent(deliveryFilter) : '');
    var acc = null;
    var step = function(cursor) {
      return api(base + (cursor ? '&cursor=' + encodeURIComponent(cursor) : '')).then(function(r) {
        if (myGen !== listGenRef.current) return;
        var got = r.shipments || [];
        got.forEach(function(s, i) { s._k = s.order_pk + '-' + (s.shipment_id || i); });
        if (!acc) { acc = Object.assign({}, r, { shipments: got }); setSsel({}); }
        else acc = Object.assign({}, acc, { shipments: acc.shipments.concat(got) });
        var more = !!r.next && acc.shipments.length < SHIP_LIST_MAX_ROWS;
        acc.loading_more = more;
        acc.truncated = !!r.truncated || (!!r.next && !more);
        setData(acc);
        if (openOnceRef.current) {
          var pk = openOnceRef.current;
          if (got.some(function(s) { return s.order_pk === pk; })) { openOnceRef.current = 0; setOpenOrder(pk); }
        }
        if (more) return step(r.next);
      });
    };
    step(null)
      .catch(function(e) { if (myGen === listGenRef.current) showToast(e.message, 'error'); })
      .finally(function() { if (myGen === listGenRef.current) setLoading(false); });
  }, [days, q, range, deliveryFilter]);
  useEffect(function() { load(); }, [load]);

  var rows = (data && data.shipments) || [];
  // Filter bar (parity with the Orders page, on what shipments carry)
  var SHIP_SHIP_EMPTY = { channels: [], dest: 'all', carriers: [], services: [], batches: [] };
  var [sf, setSf] = useState(SHIP_SHIP_EMPTY);
  var setSF = function(patch) { setSf(function(prev) { return Object.assign({}, prev, patch); }); };
  var allCh = {}, allCar = {}, allSvc = {}, allBatch = {};
  var domN = 0, intlN = 0;
  var SHIP_DOM = ['US', 'PR', 'VI', 'GU', 'AS', 'MP', ''];
  var isDom = function(s) { return SHIP_DOM.indexOf(String(s.country || 'US').toUpperCase()) !== -1; };
  rows.forEach(function(s) {
    allCh[s.channel || '?'] = 1;
    if (s.carrier) allCar[shipCarrierLabel(s.carrier)] = 1;
    if (s.service) allSvc[s.service] = 1;
    if (s.batch_number) allBatch[s.batch_number] = 1;
    if (isDom(s)) domN++; else intlN++;
  });
  var searching = !!(q && q.trim().length >= 2); // server answered from the full history (all dates, all statuses)
  var filteredRows = rows.filter(function(s) {
    // A search is "where is this order": it crosses the view's status slice
    // and the date window (Anthony 2026-09-04); the Status column still says
    if (deliveryFilter && !searching && (s.delivery_status || '') !== deliveryFilter) return false;
    if (sf.channels.length && sf.channels.indexOf(s.channel || '?') === -1) return false;
    if (sf.dest === 'dom' && !isDom(s)) return false;
    if (sf.dest === 'intl' && isDom(s)) return false;
    if (sf.carriers.length && sf.carriers.indexOf(shipCarrierLabel(s.carrier) || '') === -1) return false;
    if (sf.services.length && sf.services.indexOf(s.service || '') === -1) return false;
    if (sf.batches.length && sf.batches.indexOf(s.batch_number || '') === -1) return false;
    return true;
  });
  var sfActive = JSON.stringify(sf) !== JSON.stringify(SHIP_SHIP_EMPTY);

  // Selection + actions (completed-shipment actions; carrier ones staged)
  var selShip = filteredRows.filter(function(s) { return ssel[s._k]; });
  var allSel = filteredRows.length > 0 && selShip.length >= filteredRows.length;
  var [pageShipRows, setPageShipRows] = useState([]);
  var pageShipRef = useRef([]); // rows currently on the page, by _k — the guard below compares against it
  var lastShipSelRef = useRef(null);
  var pageAllSelShip = pageShipRows.length > 0 && pageShipRows.every(function(x) { return ssel[x._k]; });
  // Queue-parity selection: shift = range over the grid AS DISPLAYED (sorted
  // ref), plain click/checkbox = toggle; anchor keyed by row _k
  var toggleShipRow = function(row, e) {
    var rows = (sortedShipRef.current && sortedShipRef.current.length) ? sortedShipRef.current : filteredRows;
    if (e && e.shiftKey && lastShipSelRef.current != null) {
      var a = rows.findIndex(function(x) { return x._k === lastShipSelRef.current; });
      var b = rows.findIndex(function(x) { return x._k === row._k; });
      if (a !== -1 && b !== -1) {
        var lo = Math.min(a, b), hi = Math.max(a, b);
        setSsel(function(prev) {
          var n = Object.assign({}, prev);
          for (var i = lo; i <= hi; i++) { if (rows[i]) n[rows[i]._k] = true; }
          return n;
        });
        lastShipSelRef.current = row._k;
        return;
      }
    }
    setSsel(function(prev) { var n = Object.assign({}, prev); if (n[row._k]) delete n[row._k]; else n[row._k] = true; return n; });
    lastShipSelRef.current = row._k;
  };
  // Slips need the FULL order (recipient, lines, notes): the old stripped copy had no
  // .recipient and no id, so shipResolveOrderDetails re-fetched /order/undefined and
  // every slip dropped out — "No orders to print." (Paula, 63 orders, 2026-09-17).
  // The detail passes through unchanged, in table order, with the shipment row's
  // batch number for the pick-list title.
  var fetchOrderDetails = function(sel, cb, title) {
    var seen = {}, items = [];
    sel.forEach(function(s) { if (!seen[s.order_pk]) { seen[s.order_pk] = true; items.push(s); } });
    // every selected order prints — no cap (Anthony 2026-09-28)
    // the print window opens inside the click; after the fetches Chrome calls it a pop-up (9-27 M17)
    var win = shipOpenPrintWindow(title || 'Print');
    setBusyPrint(true);
    // All or nothing (Anthony 2026-10-29): the shared resolver rejects when any order fails to load
    // (15 s timeout each), naming the orders; nothing prints and the user clicks again. The rows go
    // in as bare ids — a shipment row's .recipient is a display string, not a detail to pass through.
    shipResolveOrderDetails(items.map(function(s) { return { id: s.order_pk, order_number: s.order_number }; })).then(function(details) {
      setBusyPrint(false);
      cb(details.map(function(dd, idx) { return Object.assign({}, dd, { batch_number: dd.batch_number || items[idx].batch_number || null }); }), { win: win });
    }).catch(function(e) {
      setBusyPrint(false);
      shipClosePrintWindow(win);
      showToast(String((e && e.message) || e), 'error');
    });
  };
  var printSummary = function(sel) {
    var tot = sel.reduce(function(a, s) { return a + (s.cost || 0); }, 0);
    var html = '<h1>Order Summary</h1><div class="sub">' + new Date().toLocaleString() + ' · ' + sel.length + ' shipments · labels ' + shipMoney(tot) + '</div>'
      + '<table><tr><th>Shipment</th><th>Order #</th><th>Batch</th><th>Tracking</th><th>Carrier · Service</th><th>Recipient</th><th>Cost</th><th>Ship Date</th></tr>'
      + sel.map(function(s) {
          return '<tr><td>' + shipEsc(s.shipment_id || '—') + '</td><td>' + shipEsc(s.order_number) + '</td><td>' + shipEsc(s.batch_number || '') + '</td><td class="mono">' + shipEsc(s.tracking || '—')
            + '</td><td>' + shipEsc(shipCarrierLabel(s.carrier) || '') + ' · ' + shipEsc(s.service || '') + '</td><td>' + shipEsc(s.recipient || '')
            + '</td><td>' + (s.cost != null ? shipMoney(s.cost) : '—') + '</td><td>' + (fmtDateOnly(s.ship_date) || '—') + '</td></tr>';
        }).join('') + '</table>';
    shipPrintWindow('Order Summary', html);
  };
  var exportShipmentsCsv = function(sel) {
    shipDownloadCsv('shipments-' + new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + '.csv',
      ['Shipment #', 'Batch', 'Order #', 'Tracking', 'Status', 'Carrier', 'Service', 'Recipient', 'Country', 'Weight (oz)', 'Label Cost', 'Order Value', 'Ship Date'],
      sel.map(function(s) {
        return [(s.package_count || 1) > 1 ? s.package_count + ' pkgs' : (s.shipment_id || ''), s.batch_number || '', s.order_number, (s.trackings && s.trackings.length ? s.trackings.join('; ') : (s.tracking || '')), s.delivery_status || '', shipCarrierLabel(s.carrier) || '', s.service || '',
          s.recipient || '', s.country || '', s.weight_oz != null ? s.weight_oz : '', s.cost != null ? s.cost : '', s.value, s.ship_date || ''];
      }));
  };
  var wFmt = function(oz) {
    if (oz == null) return '—';
    var lb = Math.floor(oz / 16);
    var rem = Math.round((oz - lb * 16) * 10) / 10;
    return (lb ? lb + ' lb ' : '') + rem + ' oz';
  };
  var cols = [
    { key: '_sel', label: '', locked: true, noFilter: true, noSort: true, sticky: true,
      headerRender: function() {
        // Page-scoped: checks this page only; the banner offers the full
        // filtered view. Unchecking clears the WHOLE selection (queue rule).
        return h(ShipCheckbox, {
          checked: pageAllSelShip,
          title: 'Select this page',
          onChange: function() {
            if (pageAllSelShip) { setSsel({}); return; }
            var n = Object.assign({}, ssel);
            pageShipRows.forEach(function(x) { n[x._k] = true; });
            setSsel(n);
          },
        });
      },
      stopClick: true,
      render: function(s) {
        return h('div', {
          onClick: function(e) { e.stopPropagation(); toggleShipRow(s, e); },
          onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
          // Mirror cellPadding (3px 8px) exactly — misaligned boxes otherwise
          style: { display: 'flex', alignItems: 'center', margin: '-3px -8px', padding: '3px 8px', cursor: 'pointer' }
        }, h(ShipCheckbox, {
          checked: !!ssel[s._k],
          onChange: function(e) { toggleShipRow(s, e); },
        }));
      } },
    { key: 'shipment_id', label: 'Shipment #', sortKey: 'shipment_id', render: function(s) {
        if ((s.package_count || 1) > 1) return h('span', { title: (s.packages || []).map(function(p) { return p.packageId; }).filter(Boolean).join('\n'), style: { color: C.text } }, s.package_count + ' pkgs');
        return s.shipment_id ? h('span', { style: {} }, s.shipment_id) : h('span', { style: { color: C.textDim } }, '—');
      } },
    { key: 'batch_number', label: 'Batch', sortKey: 'batch_number', render: function(s) {
        // no "#" (Anthony 9-22); hover copy icon like Order # / Tracking # (Anthony 2026-10-17)
        return s.batch_number ? h(CopyCell, { value: String(s.batch_number) }, h('span', { style: { color: C.accent, fontWeight: 500 } }, String(s.batch_number))) : null;
      } },
    { key: 'order_number', label: 'Order #', sortKey: 'order_number', render: function(s) {
        // Order # opens the full order modal (activity rail included) — same
        // as the orders area; a plain row click keeps the shipment modal
        return h(CopyCell, { value: s.order_number },
          h('span', {
            onClick: function(e) { e.stopPropagation(); setOrdModal(s.order_pk); },
            style: { fontWeight: 500, color: C.accent, cursor: 'pointer' },
            title: 'Open order'
          }, s.order_number));
      } },
    // Marketplace order number (Amazon 3-7-7, eBay, Walmart PO…) — hidden by
    // default, matched by search either way (2026-09-04)
    { key: 'channel_order_id', label: 'Marketplace #', sortKey: 'channel_order_id', defaultVisible: false, render: function(s) {
        var v = s.channel_order_id || s.channel_ref;
        return v ? h(CopyCell, { value: v }, h('span', { style: { whiteSpace: 'nowrap' } }, v)) : h('span', { style: { color: C.textDim } }, '—');
      } },
    { key: 'tracking', label: 'Tracking #', render: function(s) {
        if (!s.tracking) return h('span', { style: { color: C.warning } }, 'no tracking');
        var multi = (s.package_count || 1) > 1;
        return h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 } },
          h(CopyCell, { value: multi ? (s.trackings || []).join('\n') : s.tracking },
            h('a', {
              href: shipTrackingUrl(s.carrier, s.tracking), target: '_blank', rel: 'noopener',
              onClick: function(e) { e.stopPropagation(); },
              style: { color: C.accent, textDecoration: 'none' },
              title: 'Track on carrier site',
            }, s.tracking)),
          multi && h('span', { title: (s.trackings || []).join('\n'), style: { fontSize: 10.5, color: C.textMuted, flexShrink: 0 } }, '+' + (s.package_count - 1)));
      } },
    { key: 'delivery_status', label: 'Status', sortKey: 'delivery_status', render: function(s) {
        var m = ({ pre_transit: ['Pre-Transit', C.textMuted, C.bg], in_transit: ['In Transit', C.accent, C.accentSoft],
          delivered: ['Delivered', C.success, C.successSoft], exception: ['Exception', C.danger, C.dangerSoft || C.bg] })[s.delivery_status];
        if (!m) return h('span', { style: { color: C.textDim } }, '—');
        return h('span', { title: s.delivery_detail || '',
          style: { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, background: m[2], color: m[1], whiteSpace: 'nowrap' } }, m[0]);
      } },
    { key: 'carrier', label: 'Carrier', sortKey: 'carrier', render: function(s) {
        return s.carrier ? shipCarrierLabel(s.carrier) : h('span', { style: { color: C.textDim } }, '—');
      } },
    { key: 'service', label: 'Service', sortKey: 'service', render: function(s) {
        return h('span', null, s.service ? shipServiceLabel(s.service) : '—');
      } },
    { key: 'recipient', label: 'Recipient', sortKey: 'recipient', render: function(s) {
        return h('span', { style: { maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom' }, title: s.recipient || '' }, s.recipient || '—');
      } },
    { key: 'country', label: 'Country', sortKey: 'country', render: function(s) {
        var c = String(s.country || 'US').toUpperCase();
        return h('span', { style: { color: c === 'US' ? C.textMuted : C.warning, fontWeight: c === 'US' ? 400 : 700 } }, c);
      } },
    { key: 'weight_oz', label: 'Weight', sortKey: 'weight_oz', render: function(s) { return h('span', { style: { whiteSpace: 'nowrap' } }, wFmt(s.weight_oz)); } },
    { key: 'cost', label: 'Label Cost', sortKey: 'cost', render: function(s) { return s.cost != null ? shipMoney(s.cost) : h('span', { style: { color: C.textDim } }, '—'); } },
    { key: 'value', label: 'Order Value', sortKey: 'value', defaultVisible: false, render: function(s) {
        if (s.currency && s.currency !== 'USD') return h('span', { title: 'Order placed in ' + s.currency + '; converted at the rate on the order date' }, shipMoney(s.value), h('span', { style: { color: C.textDim, fontSize: 11, marginLeft: 4 } }, shipMoney(s.value_native, s.currency)));
        return shipMoney(s.value);
      } },
    { key: 'ship_date', label: 'Ship Date', sortKey: 'ship_date', render: function(s) {
        // ship_date is a DATE column: civil date, no clock (review M7 #104 / same class as #75)
        return s.ship_date ? fmtDateOnly(s.ship_date, { month: 'short', day: 'numeric' }) : '—';
      } },
  ];
  var labelCost = rows.reduce(function(a, s) { return a + (s.cost || 0); }, 0);

  return h('div', { style: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } },
    h('div', { style: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' } },
      h('input', Object.assign({
        value: qLive, placeholder: 'Search order #, marketplace #, tracking, recipient, SKU… (all dates)',
        style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', width: 300 }
      }, qSearchProps)),
      h(Btn, { size: 'sm', onClick: function() { setQ(qLive.trim()); } }, 'Search'),
      // While a search is active the server answers from all dates, so the
      // window controls are parked (dimmed, inert) until the search clears
      h('div', { title: searching ? 'Search covers all dates — clear it to browse by window' : undefined,
        style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2, opacity: searching ? 0.4 : 1, pointerEvents: searching ? 'none' : 'auto' } },
        [1, 7, 30, 60, 90].map(function(d) {
          var on = !range && days === d;
          return h('button', {
            key: d, onClick: function() { setRange(null); setRFrom(''); setRTo(''); setDays(d); },
            style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }
          }, d + 'd');
        })),
      h('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 4, background: C.bg, border: '1px solid ' + (range ? C.accent : C.border), borderRadius: 6, padding: '2px 6px', opacity: searching ? 0.4 : 1, pointerEvents: searching ? 'none' : 'auto' }, title: 'Custom date range (ship dates, inclusive)' },
        h('input', { type: 'date', value: rFrom, max: rTo || undefined,
          onChange: function(e) { var v = e.target.value; setRFrom(v); if (v && rTo) setRange({ from: v, to: rTo }); },
          style: { background: 'transparent', color: C.text, border: 'none', fontSize: 11.5, fontFamily: 'inherit', padding: '3px 2px', outline: 'none' } }),
        h('span', { style: { color: C.textDim, fontSize: 11 } }, '–'),
        h('input', { type: 'date', value: rTo, min: rFrom || undefined,
          onChange: function(e) { var v = e.target.value; setRTo(v); if (rFrom && v) setRange({ from: rFrom, to: v }); },
          style: { background: 'transparent', color: C.text, border: 'none', fontSize: 11.5, fontFamily: 'inherit', padding: '3px 2px', outline: 'none' } }),
        range && h('button', {
          onClick: function() { setRange(null); setRFrom(''); setRTo(''); },
          title: 'Clear the custom range',
          style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: '0 2px', display: 'inline-flex', alignItems: 'center' }
        }, h(LIcon, { name: 'x', size: 12 }))),
      h('div', { style: { marginLeft: 'auto', fontSize: 12, color: C.textMuted } },
        (searching
          ? filteredRows.length.toLocaleString() + (filteredRows.length === 1 ? ' match' : ' matches') + ' across all dates'
          : filteredRows.length.toLocaleString() + ' shipments') +
        (data && data.loading_more ? ' · loading more…' : '') +
        (data && data.truncated ? (searching ? ' (first 5,000 — refine the search)' : ' (view capped at ' + SHIP_LIST_MAX_ROWS.toLocaleString() + ' — Export gets the full window)') : '') +
        (labelCost > 0 ? ' · labels ' + shipMoney(labelCost) : ''))),
    // Reserved selection-banner slot — fixed height so it never reflows (queue parity)
    h('div', { style: { height: 34, marginBottom: 6 } },
      selShip.length > 1 && (pageAllSelShip || selShip.length >= filteredRows.length) && h('div', { style: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: '100%', boxSizing: 'border-box', padding: '0 110px', background: C.accentSoft, border: '1px solid ' + C.accent + '40', borderRadius: 8, fontSize: 12 } },
        h(LIcon, { name: 'check', size: 13, color: C.accent }),
        selShip.length >= filteredRows.length
          ? h('span', { style: { color: C.accent, fontWeight: 500 } }, 'All ' + filteredRows.length.toLocaleString() + ' shipments in the filtered view are selected.')
          : h('span', { style: { color: C.text } },
              h('strong', null, selShip.length.toLocaleString()), ' selected — ',
              h('button', {
                onClick: function() { var n = {}; filteredRows.forEach(function(x) { n[x._k] = true; }); setSsel(n); },
                style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontWeight: 500, fontSize: 12, fontFamily: 'inherit', padding: 0, textDecoration: 'underline' }
              }, 'select all ' + filteredRows.length.toLocaleString() + ' shipments in this filter')),
        h('button', {
          onClick: function() { setSsel({}); },
          style: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }
        }, 'Clear selection'))),
    h('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
      h('span', { style: { fontSize: 12.5, color: C.textMuted, fontWeight: 500 } }, 'Filter by:'),
      h(ReportParamSelect, { label: 'Store', values: Object.keys(allCh).sort(), selected: sf.channels, onChange: function(v) { setSF({ channels: v }); } }),
      h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
        [{ k: 'dom', l: 'Domestic (' + domN.toLocaleString() + ')' }, { k: 'intl', l: 'International (' + intlN.toLocaleString() + ')' }].map(function(d) {
          var on = sf.dest === d.k;
          return h('button', {
            key: d.k, className: 'fpill' + (on ? ' fpill-on' : ''), onClick: function() { setSF({ dest: on ? 'all' : d.k }); },
            style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500, fontFamily: 'inherit' }
          }, d.l);
        })),
      h(ReportParamSelect, { label: 'Carrier', values: Object.keys(allCar).sort(), selected: sf.carriers, onChange: function(v) { setSF({ carriers: v }); } }),
      h(ReportParamSelect, { label: 'Service', values: Object.keys(allSvc).sort(), selected: sf.services, onChange: function(v) { setSF({ services: v }); } }),
      h(ReportParamSelect, { label: 'Batch', values: Object.keys(allBatch).sort(), selected: sf.batches, onChange: function(v) { setSF({ batches: v }); } }),
      sfActive && h('button', {
        onClick: function() { setSf(SHIP_SHIP_EMPTY); },
        style: { padding: '5px 10px', fontSize: 11, borderRadius: 6, border: 'none', background: 'transparent', color: C.accent, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
      }, 'Clear')),
    loading && !data
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading shipments…')
      : h(DataTable, {
          columns: cols, data: filteredRows, storageKey: 'ship-shipments',
        onSortedData: function(d2) { sortedShipRef.current = d2; },
        onPageData: function(d2) {
          // DataTable calls this on EVERY render with a fresh array; setting state unconditionally
          // re-rendered the tab forever (a core pegged while any Shipments page was open — review
          // 2026-09-28 HIGH #1, measured live). Same guard as the queue: only a change in the rows
          // on the page (by _k) is worth a state update.
          var prev = pageShipRef.current;
          var changed = prev.length !== d2.length || d2.some(function(x, i) { return !prev[i] || prev[i]._k !== x._k; });
          if (!changed) return;
          pageShipRef.current = d2;
          setPageShipRows(d2);
        },
        compact: true, noColumnFilters: true, fontSize: 12, cellPadding: '3px 8px',
          scrollHeight: 'fill', defaultSort: 'ship_date', defaultSortDir: 'desc',
          emptyTitle: 'No shipments', emptySub: 'No SHIPPED orders in this window',
          onRowClick: function(s) { setOpenOrder(s.order_pk); },
          bulkActions: {
            alwaysShow: true,
            count: selShip.length,
            items: [
              { section: 'Selected: ' + selShip.length },
              { label: busyPrint ? 'Preparing…' : 'Print packing slips', icon: 'file-text', disabled: selShip.length === 0 || busyPrint, onClick: function() { fetchOrderDetails(selShip, shipPrintPackingSlips, 'Packing Slips'); } },
              { label: busyPrint ? 'Preparing…' : 'Print pick list', icon: 'list-checks', disabled: selShip.length === 0 || busyPrint, onClick: function() { fetchOrderDetails(selShip, shipPrintPickList, 'Pick List'); } },
              { label: 'Print Order Summary', icon: 'clipboard-list', disabled: selShip.length === 0, onClick: function() { printSummary(selShip); } },
              (function() {
                var printable = [];
                selShip.forEach(function(s) { if (s.has_label && s.own_shipment_id && printable.indexOf(s.own_shipment_id) === -1) printable.push(s.own_shipment_id); });
                return { label: 'Print labels (' + printable.length + ')', icon: 'ticket', disabled: !printable.length,
                  onClick: function() { shipPrintApiPdf('/shipping/label-files?ids=' + printable.join(',')); } };
              })(),
              { label: 'Print Customs Form — Intl Phase', icon: 'globe', disabled: true, onClick: function() {} },
              { label: 'Print Shipment Manifest — Carrier Phase', icon: 'file-check', disabled: true, onClick: function() {} },
              { divider: true },
              { label: 'Void Label — Needs Carrier Creds', icon: 'x-circle', disabled: true, onClick: function() {} },
              { label: 'Refresh Tracking — Needs Carrier Creds', icon: 'radar', disabled: true, onClick: function() {} },
              { divider: true },
              { label: 'Export…', icon: 'download', onClick: function() { setExportModal({ scope: selShip.length ? 'selected' : 'filtered' }); } },
              { label: 'Clear Selection', icon: 'x', disabled: selShip.length === 0, onClick: function() { setSsel({}); } },
            ],
          },
        }),
    // Export confirm modal (user ask 8-31/9-01, SS-style): choose scope, see
    // exactly what's about to leave, then download
    exportModal && (function() {
      var scope = exportModal.scope === 'selected' && selShip.length ? 'selected' : 'filtered';
      var rows = scope === 'selected' ? selShip : filteredRows;
      var filterLines = [];
      filterLines.push(range ? 'Window: ' + range.from + ' to ' + range.to : 'Window: last ' + days + ' days');
      if (q) filterLines.push('Search: "' + q + '"');
      if (sf.channels && sf.channels.length) filterLines.push('Store: ' + sf.channels.join(', '));
      if (sf.dest && sf.dest !== 'all') filterLines.push('Destination: ' + (sf.dest === 'dom' ? 'Domestic' : 'International'));
      if (sf.carriers && sf.carriers.length) filterLines.push('Carrier: ' + sf.carriers.join(', '));
      if (sf.services && sf.services.length) filterLines.push('Service: ' + sf.services.join(', '));
      if (sf.batches && sf.batches.length) filterLines.push('Batch: ' + sf.batches.join(', '));
      var scopeOpt = function(key, label, count, disabled) {
        var on = scope === key;
        return h('div', {
          onClick: disabled ? undefined : function() { setExportModal({ scope: key }); },
          style: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
            border: '1px solid ' + (on ? C.accent : C.border), background: on ? C.accentSoft : 'transparent', marginBottom: 6 }
        },
          h('span', { style: { width: 14, height: 14, borderRadius: 999, border: '2px solid ' + (on ? C.accent : C.textDim), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
            on && h('span', { style: { width: 6, height: 6, borderRadius: 999, background: C.accent } })),
          h('span', { style: { flex: 1, fontSize: 12.5, color: C.text } }, label),
          h('span', { style: { fontSize: 11, color: C.textMuted } }, count.toLocaleString() + ' row' + (count === 1 ? '' : 's')));
      };
      return h('div', { style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
        h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: 20, width: 440, maxWidth: '92vw', boxSizing: 'border-box' } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
            h(LIcon, { name: 'download', size: 16, color: C.accent }),
            h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, flex: 1 } }, 'Export shipments'),
            h('button', { onClick: function() { setExportModal(null); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),
          scopeOpt('selected', 'Selected shipments', selShip.length, selShip.length === 0),
          scopeOpt('filtered', 'Everything in the current view', filteredRows.length, false),
          data && data.truncated && h('div', { style: { fontSize: 10.5, color: C.textDim, margin: '2px 0 6px' } },
            'The view is capped at ' + SHIP_LIST_MAX_ROWS.toLocaleString() + ' rows — this export includes the FULL window, so it may contain more than shown.'),
          h('div', { style: { fontSize: 11, color: C.textMuted, margin: '10px 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 } }, 'Active filters'),
          h('div', { style: { fontSize: 11.5, color: C.textMuted, lineHeight: 1.6, marginBottom: 10 } }, filterLines.join(' · ')),
          h('div', { style: { fontSize: 10.5, color: C.textDim, marginBottom: 14 } },
            'Columns: Shipment #, Batch, Order #, Tracking, Status, Carrier, Service, Recipient, Country, Weight, Label Cost, Order Value, Ship Date'),
          h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
            h(Btn, { size: 'sm', onClick: function() { setExportModal(null); } }, 'Cancel'),
            (function() {
              var fullWindow = scope === 'filtered' && data && data.truncated;
              var exportCount = rows.length;
              return h(Btn, { size: 'sm', variant: 'primary', disabled: !fullWindow && exportCount === 0, onClick: function() {
                if (scope === 'selected') {
                  exportShipmentsCsv(rows);
                  showToast('Exported ' + rows.length.toLocaleString() + ' shipment' + (rows.length === 1 ? '' : 's'), 'success');
                } else {
                  // Server-built CSV: no 5,000-row view cap; carries the window,
                  // search, and every active filter chip
                  var ps = new URLSearchParams();
                  if (range) { ps.set('from', range.from); ps.set('to', range.to); } else ps.set('days', String(days));
                  if (q) ps.set('q', q);
                  // the page's status slice too (In Transit / Delivered / Exceptions) — the export
                  // used to hand back every shipment of the window (sweep 2026-09-27 LM20)
                  if (deliveryFilter && !searching) ps.set('status', deliveryFilter);
                  if (sf.channels.length) ps.set('channels', sf.channels.join(','));
                  if (sf.dest && sf.dest !== 'all') ps.set('dest', sf.dest);
                  if (sf.carriers.length) ps.set('carriers', sf.carriers.join(','));
                  if (sf.services.length) ps.set('services', sf.services.join(','));
                  if (sf.batches.length) ps.set('batches', sf.batches.join(','));
                  var base = (typeof API_BASE !== 'undefined' && API_BASE) ? API_BASE : '/api';
                  fetch(base + '/shipping/shipments-export.csv?' + ps.toString(), { credentials: 'include' })
                    .then(function(r2) { if (!r2.ok) throw new Error('Export failed (' + r2.status + ')'); return r2.blob(); })
                    .then(function(b) {
                      var url = URL.createObjectURL(b);
                      var a = document.createElement('a');
                      a.href = url; a.download = 'shipments-' + new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + '.csv';
                      document.body.appendChild(a); a.click();
                      setTimeout(function() { URL.revokeObjectURL(url); try { document.body.removeChild(a); } catch (e2) {} }, 10000);
                    })
                    .catch(function(e2) { showToast(String(e2.message || e2), 'error'); });
                  showToast('Export started', 'success');
                }
                setExportModal(null);
              } }, fullWindow ? 'Export CSV (full window)' : 'Export CSV (' + exportCount.toLocaleString() + ')');
            })())));
    })(),
    openOrder && (function() {
      var src = (sortedShipRef.current && sortedShipRef.current.length) ? sortedShipRef.current : filteredRows;
      var pks = [];
      src.forEach(function(s) { if (pks.indexOf(s.order_pk) === -1) pks.push(s.order_pk); });
      var idx = pks.indexOf(openOrder);
      return h(ShipShipmentModal, {
        orderPk: openOrder,
        onClose: function() { setOpenOrder(null); },
        onPrev: idx > 0 ? function() { setOpenOrder(pks[idx - 1]); } : null,
        onNext: (idx >= 0 && idx < pks.length - 1) ? function() { setOpenOrder(pks[idx + 1]); } : null,
        showToast: showToast,
      });
    })(),
    ordModal != null && (function() {
      var src = (sortedShipRef.current && sortedShipRef.current.length) ? sortedShipRef.current : filteredRows;
      var pks = [];
      src.forEach(function(s) { if (pks.indexOf(s.order_pk) === -1) pks.push(s.order_pk); });
      var idx = pks.indexOf(ordModal);
      return h(ShipOrderModal, {
        orderId: ordModal,
        onClose: function() { setOrdModal(null); },
        onPrev: idx > 0 ? function() { setOrdModal(pks[idx - 1]); } : null,
        onNext: (idx >= 0 && idx < pks.length - 1) ? function() { setOrdModal(pks[idx + 1]); } : null,
        onChanged: load,
        showToast: showToast,
      });
    })()
  );
}

// ─── Product detail modal (ShipStation "Standalone Product Detail" parity):
// left rail = image + name + SKU + vertical tabs; right = tab content.
// Edits write the SHARED products table — changes apply everywhere, and open
// orders pick them up automatically (queue reads the catalog live). ─────────
function ShipProductModal({ sku, onClose, showToast, initialTab, customsFallback, onSaved }) {
  var [info, setInfo] = useState(null);
  var [err, setErr] = useState(null);
  var [tab, setTab] = useState(initialTab || 'general');
  var [form, setForm] = useState({});
  var [active, setActive] = useState(true);
  var [saving, setSaving] = useState(false);
  // "Find HS Code" (AI classification via the Anthropic integration, 8-30):
  // suggestion fills the fields for REVIEW — Save is still the write
  var [hsBusy, setHsBusy] = useState(false);
  var [hsInfo, setHsInfo] = useState(null);
  var findHs = function() {
    if (hsBusy) return;
    setHsBusy(true); setHsInfo(null);
    api('/shipping/product/hs-suggest', { method: 'POST', body: JSON.stringify({ sku: sku }) })
      .then(function(r) {
        var n = {};
        if (r.hs_code) n.hs_code = r.hs_code;
        if (r.customs_description && (!form.customs_description || shipDescIsTitle(form.customs_description, form.vendor_title || form.title))) n.customs_description = r.customs_description;
        setForm(Object.assign({}, form, n));
        setHsInfo(r);
        showToast('Suggested HS ' + r.hs_code + ' — review and Save', 'success');
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setHsBusy(false); });
  };

  var SHIP_FIELDS = [
    { key: 'package_weight', label: 'Package weight (lb)' },
    { key: 'package_length', label: 'Length (in)' },
    { key: 'package_width', label: 'Width (in)' },
    { key: 'package_height', label: 'Height (in)' },
  ];
  // Placeholders surface the effective declaration values when the product
  // fields are blank (user rule 8-30: value falls back to the order line's
  // price, description to the item title) — visible without persisting a
  // snapshot of this order into the product.
  // "Override" labels (user ask 8-30): make it plain these values beat the
  // defaults (Zentail attrs / order line) on every current AND future order
  var CUSTOMS_FIELDS = [
    { key: 'hs_code', label: 'HS Code Override', placeholder: customsFallback && customsFallback.zen_hs ? 'From Zentail: ' + customsFallback.zen_hs : undefined },
    { key: 'country_of_origin', label: 'Country of Origin Override', select: 'country' },
    { key: 'customs_value', label: 'Customs Value Override ($)', placeholder: customsFallback && customsFallback.value != null ? 'From order line: $' + (parseFloat(customsFallback.value) || 0).toFixed(2) : undefined },
    { key: 'customs_description', label: 'Customs Description Override (max 50, DHL)', wide: true, maxLength: 50, placeholder: customsFallback && customsFallback.title ? 'From item title: ' + customsFallback.title : undefined },
  ];
  var GENERAL_FIELDS = ['title', 'barcode'];

  useEffect(function() {
    api('/shipping/product-info?sku=' + encodeURIComponent(sku))
      .then(function(r) {
        setInfo(r);
        var f = {};
        SHIP_FIELDS.concat(CUSTOMS_FIELDS).forEach(function(x) { f[x.key] = r.product[x.key] == null ? '' : String(r.product[x.key]); });
        GENERAL_FIELDS.forEach(function(k) { f[k] = r.product[k] == null ? '' : String(r.product[k]); });
        setForm(f);
        setActive(r.product.is_active !== false);
      })
      .catch(function(e) { setErr(e.message); });
  }, [sku]);

  var save = function() {
    // Customs is the ONLY editable section (user rule 8-12) — name/UPC/
    // shipping sync from the catalog and must not be edited here.
    // Only the fields the user CHANGED are sent (review 2026-09-28): the server validates every
    // field it receives, so re-sending an untouched stored value (a customs value of 0.00) blocked
    // an HS-code edit until that value was changed too.
    var body = {};
    var stored = (info && info.product) || {};
    CUSTOMS_FIELDS.forEach(function(x) {
      var was = stored[x.key] == null ? '' : String(stored[x.key]);
      if (String(form[x.key] == null ? '' : form[x.key]) !== was) body[x.key] = form[x.key];
    });
    if (!Object.keys(body).length) { showToast('No changes to save', 'info'); onClose(); return; }
    setSaving(true);
    api('/shipping/products-list/' + info.product.id, { method: 'POST', body: JSON.stringify(body) })
      .then(function() { showToast(sku + ' updated', 'success'); if (onSaved) onSaved(body); onClose(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  var inpS = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 9px', fontSize: 12, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  var lbl = { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 };
  var secH = function(t) { return h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, margin: '0 0 10px' } }, t); };
  var setF = function(k, v) { var n = {}; n[k] = v; setForm(Object.assign({}, form, n)); };
  var roS = Object.assign({}, inpS, { opacity: 0.6, cursor: 'not-allowed' });
  var fieldGrid = function(fields, readOnly) {
    return h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
      fields.map(function(x) {
        var ctl;
        if (x.select === 'country') {
          // Type-to-search picker (same control as the vendor/PO country fields since 9-13). The
          // native <select> here lost its scroll position every time the modal re-rendered behind
          // it, so scrolling to the bottom of 250 countries snapped back to the top (Anthony 9-17).
          var cooCur = (form[x.key] || '').trim().toUpperCase();
          if (!window._shipCountryOptions) {
            var sortedC = SHIP_ISO_COUNTRIES.slice().sort(function(a, b) { return shipCountryName(a).localeCompare(shipCountryName(b)); });
            window._shipCountryOptions = [{ value: '', label: '—' }].concat(sortedC.map(function(c) { return { value: c, label: shipCountryName(c) + ' (' + c + ')' }; }));
          }
          var cOpts = cooCur && SHIP_ISO_COUNTRIES.indexOf(cooCur) === -1
            ? [{ value: cooCur, label: cooCur + ' (saved)' }].concat(window._shipCountryOptions) : window._shipCountryOptions;
          ctl = h(SearchSelect, { options: cOpts, value: cooCur, disabled: !!readOnly, placeholder: '—',
            onChange: function(v) { setF(x.key, v || ''); }, style: readOnly ? roS : inpS });
        } else {
          ctl = h('input', { value: form[x.key] || '', placeholder: x.placeholder, maxLength: x.maxLength || undefined, disabled: !!readOnly, onChange: readOnly ? undefined : function(e) { setF(x.key, e.target.value); }, style: readOnly ? roS : inpS });
        }
        return h('div', { key: x.key, style: x.wide ? { gridColumn: '1 / -1' } : null },
          h('div', { style: lbl }, x.label + (x.maxLength ? ' · ' + String(form[x.key] || '').length + '/' + x.maxLength : '')),
          ctl);
      }));
  };

  var TABS = [['general', 'General'], ['shipping', 'Shipping'], ['customs', 'Customs'], ['inventory', 'Inventory'], ['activity', 'Activity']];

  return h(Modal, { open: true, onClose: onClose, title: 'Product Detail', width: 860 },
    err ? h('div', { style: { color: C.danger, fontSize: 13 } }, err)
    : !info ? h('div', { style: { color: C.textMuted, fontSize: 13, padding: 16 } }, 'Loading product…')
    : h('div', null,
        h('div', { style: { display: 'flex', gap: 18, minHeight: 380 } },
          // Left rail: image + identity + vertical tabs (SS layout)
          h('div', { style: { width: 200, flexShrink: 0, borderRight: '1px solid ' + C.border, paddingRight: 16 } },
            h('div', { style: { display: 'flex', justifyContent: 'center', marginBottom: 10 } },
              info.product.image_url
                ? h('img', { src: info.product.image_url, style: { width: 110, height: 110, objectFit: 'contain', borderRadius: 10, background: '#fff' } })
                : h('div', { style: { width: 110, height: 110, borderRadius: 10, background: C.bg, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, h(LIcon, { name: 'package', size: 36, color: C.textDim }))),
            h('div', { style: { fontSize: 12.5, fontWeight: 500, color: C.text, lineHeight: 1.4, textAlign: 'center', marginBottom: 3, wordBreak: 'break-word' } }, info.product.title || sku),
            h('div', { style: { fontSize: 10.5, color: C.textMuted, textAlign: 'center', marginBottom: 14 } }, 'SKU: ' + sku),
            h('div', { style: { display: 'flex', flexDirection: 'column', gap: 2 } },
              TABS.map(function(t) {
                var on = tab === t[0];
                return h('button', {
                  key: t[0], className: on ? undefined : 'phov', onClick: function() { setTab(t[0]); },
                  style: { textAlign: 'left', background: on ? C.accent : 'transparent', color: on ? '#fff' : C.text, border: 'none', borderRadius: 6, cursor: 'pointer', padding: '8px 12px', fontSize: 12, fontWeight: on ? 600 : 400, fontFamily: 'inherit' }
                }, t[1]);
              }))),
          // Right content
          h('div', { style: { flex: 1, minWidth: 0 } },
            tab === 'general' && h('div', null,
              secH('Overview'),
              h('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 6 } },
                h('div', null, h('div', { style: lbl }, 'Name'),
                  h('input', { value: form.title || '', disabled: true, style: roS })),
                h('div', null, h('div', { style: lbl }, 'SKU'),
                  h('input', { value: sku, disabled: true, style: roS }))),
              secH('Attributes'),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
                h('div', null, h('div', { style: lbl }, 'UPC / Barcode'),
                  h('input', { value: form.barcode || '', disabled: true, style: roS }))),
              h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 12 } }, 'Read-only — synced from the catalog.')),
            tab === 'shipping' && h('div', null, secH('Shipping'), fieldGrid(SHIP_FIELDS, true),
              h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 10 } }, 'Read-only — synced from the catalog. Used for rate quotes and labels when the order has no size override.')),
            tab === 'customs' && h('div', null, secH('Customs'), fieldGrid(CUSTOMS_FIELDS),
              h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 10, flexWrap: 'wrap' } },
                h('button', {
                  className: 'phov', disabled: hsBusy, onClick: findHs,
                  title: 'Classify this product with AI and suggest an HS/HTS code',
                  style: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(79,110,247,0.08)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)', borderRadius: 6, padding: '5px 12px', fontSize: 11.5, fontWeight: 500, cursor: hsBusy ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: hsBusy ? 0.6 : 1 }
                }, h(LIcon, { name: 'sparkles', size: 13 }), hsBusy ? 'Classifying…' : 'Find HS Code'),
                hsInfo && h('div', { style: { fontSize: 10.5, lineHeight: 1.5, flex: 1, minWidth: 200 } },
                  h('span', { style: { fontWeight: 600, color: hsInfo.confidence === 'high' ? C.success : hsInfo.confidence === 'low' ? C.danger : C.warning, textTransform: 'uppercase' } }, hsInfo.confidence + ' confidence'),
                  h('span', { style: { color: C.textMuted } }, ' — ' + (hsInfo.rationale || 'review before saving')),
                  h('div', { style: { color: C.textMuted, marginTop: 2 } },
                    hsInfo.source === 'catalog' ? 'From the catalog' + (hsInfo.matched_title ? ': matches "' + String(hsInfo.matched_title).slice(0, 50) + '"' : '')
                      : hsInfo.source === 'claude' ? 'AI classification' + (hsInfo.matched_title ? ', picked the code used for "' + String(hsInfo.matched_title).slice(0, 40) + '"' : '') : '',
                    hsInfo.cached ? ' (cached)' : (hsInfo.ms != null ? ' (' + (hsInfo.ms / 1000).toFixed(1) + ' s)' : '')))),
              h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 10 } },
                'Overrides beat the defaults (Zentail attributes, order line value/title) on every current and future order for this SKU. Blank fields keep riding the defaults, shown greyed on declarations.')),
            tab === 'inventory' && h('div', null,
              secH('Inventory'),
              h('div', { style: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 } },
                h('span', { style: { fontSize: 22, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' } }, (info.product.on_hand != null ? info.product.on_hand : 0).toLocaleString()),
                h('span', { style: { fontSize: 11.5, color: C.textMuted } }, 'on hand in FBM bins')),
              h('div', { style: lbl }, 'Bin locations (from Zentail)'),
              (function() {
                var bins = Object.assign({}, info.product.bins || {});
                // Catalog fallback — same chain the queue's bin lookup uses
                if (!Object.keys(bins).length && info.product.mia_bin_location) bins['MIA Bin Location'] = info.product.mia_bin_location;
                var keys = Object.keys(bins);
                if (!keys.length) return h('div', { style: { fontSize: 12, color: C.textDim } }, 'No bin locations on file.');
                return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
                  keys.map(function(k) {
                    return h('div', { key: k, style: { display: 'flex', gap: 10, fontSize: 12 } },
                      h('span', { style: { color: C.textMuted, width: 110, flexShrink: 0 } }, k.replace(' Bin Location', '')),
                      h('span', { style: { color: C.text } }, bins[k]));
                  }));
              })(),
              h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 12 } }, 'Read-only — bins sync from Zentail product attributes.')),
            tab === 'activity' && (
              (info.activity || []).length === 0
                ? h('div', { style: { fontSize: 12, color: C.textDim, padding: '8px 0' } }, 'No recorded activity for this SKU yet.')
                : h('div', { style: { maxHeight: 330, overflowY: 'auto' } },
                    info.activity.map(function(a, i) {
                      return h('div', { key: i, style: { padding: '6px 0', borderBottom: '1px solid ' + C.border, fontSize: 11.5 } },
                        h('div', { style: { color: C.text } }, a.action.replace(/_/g, ' ')),
                        h('div', { style: { color: C.textMuted, fontSize: 10.5 } }, (a.username || '?') + ' · ' + new Date(a.created_at).toLocaleString()));
                    }))))),
        // Footer — Save covers every editable tab at once
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, borderTop: '1px solid ' + C.border, paddingTop: 12 } },
          h('span', { style: { fontSize: 10.5, color: C.textDim, flex: 1 } }, 'Shared catalog — changes apply everywhere, including open orders.'),
          h(Btn, { onClick: onClose }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: save, disabled: saving }, saving ? 'Saving…' : 'Save'))
      ));
}

// ─── Full-window shipped-order modal (ShipStation Shipments view parity) ───
function ShipShipmentModal({ orderPk, onClose, onPrev, onNext, showToast }) {
  var [d, setD] = useState(null);
  var [err, setErr] = useState(null);
  var [prodSku, setProdSku] = useState(null);
  var [printOpen, setPrintOpen] = useState(false);
  var [noteDraft, setNoteDraft] = useState('');
  var [noteEditing, setNoteEditing] = useState(false);
  var [view, setView] = useState('details'); // 'details' | 'activity'
  // Return labels (prepaid, addresses swapped; USPS per the returns default)
  var [returns, setReturns] = useState([]);
  var [retDialog, setRetDialog] = useState(null); // {service, services, weight, length, width, height}
  var [retBusy, setRetBusy] = useState(false);
  var loadReturns = function() {
    api('/shipping/order/' + orderPk + '/return-labels')
      .then(function(r) { setReturns(r.return_labels || []); }).catch(function() {});
  };

  useEffect(function() {
    setD(null); setErr(null); setNoteEditing(false); setView('details'); setReturns([]); setRetDialog(null);
    api('/shipping/order/' + orderPk)
      .then(function(res) { setD(res); setNoteDraft((res.meta && res.meta.note) || ''); })
      .catch(function(e) { setErr(e.message); });
    loadReturns();
  }, [orderPk]);

  // Returns are domestic-only (user rule 9-01; the server refuses intl too)
  var retDomestic = (function() {
    if (!d) return false;
    var ov = (d.overrides && d.overrides.ship_to) || {};
    var country = String(ov.country || (d.recipient && d.recipient.country) || 'US').toUpperCase();
    return ['US', 'PR', 'VI', 'GU', 'AS', 'MP', ''].indexOf(country) !== -1;
  })();
  var openReturnDialog = function() {
    if (!retDomestic) return;
    Promise.all([api('/shipping/label-carriers'), api('/shipping/config/returns')])
      .then(function(rs) {
        var capable = (rs[0].carriers || []).filter(function(c) { return c.returns_capable; });
        var services = [];
        capable.forEach(function(c) { (c.services || []).forEach(function(s) { services.push(s.code); }); });
        var cd = (d && d.computed && d.computed.dimensions) || {};
        setRetDialog({
          services: services,
          service: services.indexOf(rs[1].service) !== -1 ? rs[1].service : services[0],
          weight: (d && d.computed && d.computed.effective_weight_lb) || '',
          length: cd.length || '', width: cd.width || '', height: cd.height || '',
        });
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var buyReturnLabel = function() {
    if (retBusy) return;
    setRetBusy(true);
    api('/shipping/order/' + orderPk + '/return-label', { method: 'POST', body: JSON.stringify({
      service: retDialog.service, weight_lb: retDialog.weight,
      length: retDialog.length, width: retDialog.width, height: retDialog.height,
    }) })
      .then(function(r) {
        showToast('Return label bought — ' + r.tracking + ' (' + shipMoney(r.cost) + ')', 'success');
        setRetDialog(null); loadReturns();
        shipPrintApiPdf('/shipping/return-label-file/' + r.id);
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setRetBusy(false); });
  };
  useEffect(function() {
    var onKey = function(e) {
      if (shipKeyInEditable(e)) return; // typing in a field — review M7 #103
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [onPrev, onNext]);

  var card = function(title, children, extra) {
    return h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: '14px 18px', marginBottom: 14 } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
        h('div', { style: { fontSize: 14.5, fontWeight: 500, color: C.text, flex: 1 } }, title), extra || null),
      children);
  };
  var kv = function(label, val) {
    return h('div', { style: { marginBottom: 9 } },
      h('div', { style: { fontSize: 11.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' } }, label),
      h('div', { style: { fontSize: 14.5, color: C.text, marginTop: 1 } }, val == null || val === '' ? '—' : val));
  };
  var money = function(v, cur) { return v == null ? '—' : shipMoney(v, cur); };

  var arrowBtn = function(dir, fn) {
    return fn && h('button', {
      onClick: fn, title: dir === 'l' ? 'Previous shipment (←)' : 'Next shipment (→)',
      style: { position: 'fixed', top: '50%', transform: 'translateY(-50%)', left: dir === 'l' ? 72 : undefined, right: dir === 'r' ? 14 : undefined, zIndex: 1302, width: 38, height: 38, borderRadius: 999, background: C.surface, border: '1px solid ' + C.border, color: C.text, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }
    }, h(LIcon, { name: dir === 'l' ? 'arrow-left' : 'arrow-right', size: 16 }));
  };

  return h('div', { style: { position: 'fixed', inset: 0, zIndex: 1300, background: C.bg, display: 'flex', flexDirection: 'column' } },
    // Top bar
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid ' + C.border, background: C.surface } },
      h(LIcon, { name: 'package-check', size: 16, color: C.accent }),
      h('span', { style: { fontSize: 14, color: C.textMuted } }, d ? (d.channel || '') + ':' : ''),
      h('span', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, d ? 'Order # ' + d.order_number : 'Loading…'),
      h('span', { style: { flex: 1 } }),
      h('div', { style: { position: 'relative' } },
        h('button', { onClick: function() { setPrintOpen(!printOpen); }, style: { display: 'inline-flex', alignItems: 'center', gap: 6, background: C.bg, border: '1px solid ' + C.border, color: C.text, borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' } },
          h(LIcon, { name: 'printer', size: 13 }), 'Print', h(LIcon, { name: 'chevron-down', size: 12 })),
        printOpen && d && h('div', { style: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 1301, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', minWidth: 170 } },
          [['Packing slip', function() { shipPrintPackingSlips([d]); }],
           ['Pick list', function() { shipPrintPickList([{ lines: d.lines }]); }]].map(function(it) {
            return h('div', { key: it[0], onClick: function() { setPrintOpen(false); it[1](); },
              onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; },
              style: { padding: '8px 12px', fontSize: 12, color: C.text, cursor: 'pointer' } }, it[0]);
          }))),
      h('button', { onClick: onClose, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 6 } }, h(LIcon, { name: 'x', size: 20 }))),
    arrowBtn('l', onPrev), arrowBtn('r', onNext),
    // Body row — content + the same left icon rail as ShipOrderModal (rendered
    // last, flex order:-1) so the two modals switch views the same way.
    h('div', { style: { flex: 1, minHeight: 0, display: 'flex' } },
      h('div', { style: { order: -1, width: 58, flexShrink: 0, borderRight: '1px solid ' + C.border, background: C.surface, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 12, gap: 8 } },
        [{ k: 'details', icon: 'package', title: 'Order Details' }, { k: 'activity', icon: 'history', title: 'Activity Log' }].map(function(t) {
          var active = view === t.k;
          return h('button', {
            key: t.k, title: t.title, onClick: function() { setView(t.k); },
            className: active ? undefined : 'phov',
            style: { width: 44, height: 44, borderRadius: 10, border: '1px solid ' + (active ? C.accent : 'transparent'), background: active ? 'rgba(79,110,247,0.12)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }
          }, h(LIcon, { name: t.icon, size: 21, color: active ? C.accent : C.textMuted }));
        })),
    err ? h('div', { style: { flex: 1, padding: 40, color: C.danger, fontSize: 13 } }, err)
    : !d ? h('div', { style: { flex: 1, padding: 60, textAlign: 'center', color: C.textMuted } }, 'Loading order…')
    : view === 'activity' ? h('div', { style: { flex: 1, minWidth: 0, overflowY: 'auto', padding: '18px 24px' } },
        h(ShipActivityTimeline, { orderPk: orderPk, showToast: showToast }))
    : h('div', { style: { flex: 1, minWidth: 0, overflowY: 'auto', padding: '18px 60px' } },
        // Status strip + staged actions
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' } },
          h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 999, background: d.status === 'SHIPPED' ? C.successSoft : C.bg, color: d.status === 'SHIPPED' ? C.success : C.textMuted, border: '1px solid ' + (d.status === 'SHIPPED' ? C.success : C.border) } },
            h('span', { style: { width: 7, height: 7, borderRadius: 999, background: d.status === 'SHIPPED' ? C.success : C.textDim } }), d.status),
          h(ShipTagChips, { tags: (d.computed.rule_tags || []).concat((d.meta.tags || []).filter(function(t) { return (d.computed.rule_tags || []).indexOf(t) === -1; })) }),
          h('span', { style: { flex: 1 } }),
          h('button', { disabled: true, title: 'Void the purchased label (Return Labels card / order panel) to send an order back to the queue', style: { background: C.accent, opacity: 0.45, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: 'not-allowed', fontFamily: 'inherit' } }, 'Reship'),
          h('button', {
            disabled: !retDomestic,
            title: retDomestic ? 'Buy a prepaid return label (customer → warehouse)' : 'Returns are eligible only for domestic orders',
            onClick: retDomestic ? openReturnDialog : undefined,
            style: { background: 'transparent', border: '1px solid ' + (retDomestic ? C.accent : C.border), color: retDomestic ? C.accent : C.textMuted, opacity: retDomestic ? 1 : 0.5, borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: retDomestic ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }
          }, 'Create Return')),
        h('div', { style: { display: 'flex', gap: 16, alignItems: 'flex-start' } },
          // Left column
          h('div', { style: { flex: 1, minWidth: 0 } },
            card('Shipment Details', h('div', { style: { display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 16 } },
              h('div', null,
                h('div', { style: { fontSize: 11.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, 'Ship To Address'),
                h('div', { style: { fontSize: 14, color: C.text, lineHeight: 1.65 } },
                  h('div', { style: { fontWeight: 500 } }, d.recipient.name || '—'),
                  d.recipient.address1 && h('div', null, d.recipient.address1),
                  d.recipient.address2 && h('div', null, d.recipient.address2),
                  d.recipient.address3 && h('div', null, d.recipient.address3),
                  h('div', null, [d.recipient.city, d.recipient.state, d.recipient.zip].filter(Boolean).join(', ')),
                  h('div', null, d.recipient.country),
                  d.recipient.email && h('div', { style: { color: C.accent, fontSize: 12.5 } }, d.recipient.email)),
                h('div', { style: { marginTop: 6, fontSize: 10, fontWeight: 500, color: C.warning } }, '⚠ Not validated')),
              h('div', null,
                h('div', { style: { fontSize: 11.5, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, 'Cost Summary'),
                // The total is what the customer paid: product + shipping - discount + tax (Anthony 2026-09-29).
                // The discount line shows only when there is one, as a minus.
                shipCostSummaryRows(d.sales).map(function(r) {
                  return h('div', { key: r[0], style: { display: 'flex', justifyContent: 'space-between', fontSize: 13.5, padding: '2px 0' } },
                    h('span', { style: { color: C.textMuted } }, r[0]), h('span', { style: { color: C.text } }, money(r[1], d.sales.currency)));
                }),
                h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0', borderTop: '1px dashed ' + C.border, marginTop: 3 } },
                  h('span', { style: { color: C.textMuted } }, 'Total'), h('strong', { style: { color: C.text } }, (d.sales.currency && d.sales.currency !== 'USD' && d.total_usd != null) ? money(d.sales.total, d.sales.currency) + ' = ' + shipMoney(d.total_usd) : money(d.sales.total))),
                h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 14 } },
                  h('span', { style: { color: C.textMuted } }, 'Total Paid'), h('strong', { style: { color: C.text } }, money(d.sales.paid, d.sales.currency)))),
              h('div', null,
                kv('Order Date', new Date(d.order_date).toLocaleString()),
                kv('Date Paid', d.paid_ts ? new Date(d.paid_ts).toLocaleDateString() : null),
                kv('Ship By', d.ship_by ? String(d.ship_by).slice(0, 10) : null),
                kv('Assigned To', d.meta.assignee),
                kv('Channel Order ID', d.channel_order_id)))),
            card('Shipment Items', h('div', null,
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 84px 90px 64px 90px', gap: 10, fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', padding: '0 0 6px', borderBottom: '1px solid ' + C.border } },
                h('span', null, 'Item'), h('span', null, 'Image'), h('span', { style: { textAlign: 'right' } }, 'Unit Cost'), h('span', { style: { textAlign: 'right' } }, 'Qty'), h('span', { style: { textAlign: 'right' } }, 'Total')),
              d.lines.map(function(l, i) {
                var unit = l.unit_price != null ? parseFloat(l.unit_price) : null;
                return h('div', { key: i, style: { display: 'grid', gridTemplateColumns: '1fr 84px 90px 64px 90px', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid ' + C.border } },
                  h('div', null,
                    h('div', { onClick: function() { setProdSku(l.sku); }, style: { fontSize: 15, color: C.accent, cursor: 'pointer', lineHeight: 1.4 } }, l.title || l.sku),
                    h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'SKU: ' + l.sku)),
                  l.image_url ? h('img', { src: l.image_url, style: { width: 68, height: 68, objectFit: 'contain', borderRadius: 8, background: '#fff' } }) : h('span', null),
                  h('div', { style: { textAlign: 'right', fontSize: 14.5 } }, unit != null ? shipMoney(unit, d.currency) : '—'),
                  h('div', { style: { textAlign: 'right', fontSize: 18, fontWeight: 500 } }, l.qty),
                  h('div', { style: { textAlign: 'right', fontSize: 14.5 } }, unit != null ? shipMoney(unit * (parseInt(l.qty, 10) || 0), d.currency) : '—'));
              }))),
            card('Notes', h('div', { style: { display: 'grid', gridTemplateColumns: '150px 1fr', gap: 8, alignItems: 'center', fontSize: 14 } },
              h('span', { style: { color: C.textMuted } }, 'Note From Buyer'),
              h('div', { style: { color: C.text, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', boxSizing: 'border-box', minHeight: 35, lineHeight: '19px', whiteSpace: 'pre-wrap' } }, d.buyer_notes || ''),
              h('span', { style: { color: C.textMuted } }, 'Internal Note'),
              // Edit and view states share an identical 31px footprint (the
              // swap used to resize the field, user 9-01) — Save floats
              // INSIDE the input so the row never reflows
              noteEditing
                ? (function() {
                    var saveNote = function() {
                      api('/shipping/orders/meta', { method: 'POST', body: JSON.stringify({ order_ids: [Number(orderPk)], note: noteDraft.trim() || null }) })
                        .then(function() { showToast('Note saved', 'success'); setNoteEditing(false); })
                        .catch(function(e) { showToast(e.message, 'error'); });
                    };
                    return h('div', { style: { position: 'relative', display: 'flex' } },
                      h('input', {
                        value: noteDraft, autoFocus: true,
                        onChange: function(e) { setNoteDraft(e.target.value); },
                        // Click-away with nothing changed just closes; the
                        // timeout lets a Save click land before the blur check
                        onBlur: function() {
                          setTimeout(function() {
                            setNoteEditing(function(editing) {
                              if (editing && noteDraft === ((d.meta && d.meta.note) || '')) return false;
                              return editing;
                            });
                          }, 150);
                        },
                        onKeyDown: function(e) {
                          if (e.key === 'Escape') { setNoteDraft((d.meta && d.meta.note) || ''); setNoteEditing(false); }
                          if (e.key === 'Enter') saveNote();
                        },
                        style: { flex: 1, boxSizing: 'border-box', height: 35, background: C.bg, border: '1px solid ' + C.accent, color: C.text, borderRadius: 6, padding: '7px 58px 7px 10px', fontSize: 14, lineHeight: '19px', fontFamily: 'inherit', outline: 'none' } }),
                      h('button', {
                        onClick: saveNote,
                        style: { position: 'absolute', right: 3, top: 3, bottom: 3, background: C.accent, color: '#fff', border: 'none', borderRadius: 4, padding: '0 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }
                      }, 'Save'));
                  })()
                : h('div', { onClick: function() { setNoteEditing(true); }, title: 'Click to edit', style: { color: C.text, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', boxSizing: 'border-box', height: 35, lineHeight: '19px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' } }, (d.meta && d.meta.note) || ''),
              h('span', { style: { color: C.textMuted } }, 'Custom Field 1'),
              shipRoCopyBox(d.custom1, showToast),
              h('span', { style: { color: C.textMuted } }, 'Custom Field 2'),
              shipRoCopyBox(d.custom2, showToast),
              h('span', { style: { color: C.textMuted } }, 'Custom Field 3'),
              shipRoCopyBox(d.custom3, showToast)))),
          // Right column
          h('div', { style: { width: 330, flexShrink: 0 } },
            (d.packages || []).map(function(p, i) {
              // Print Label (user ask 9-03): our own label file for THIS package — matched by
              // tracking, else the order's only label when it has a single package. SS-era
              // packages have no file, so they get no button rather than a broken one.
              var labels = d.labels || [];
              var ownLabel = labels.find(function(l) { return p.tracking && l.tracking === p.tracking; })
                || ((d.packages || []).length === 1 && labels.length === 1 ? labels[0] : null);
              return h('div', { key: i, style: { background: C.accent, borderRadius: 12, padding: '14px 16px', marginBottom: 12, color: '#fff' } },
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                  h('div', { style: { fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0 } }, shipCarrierLabel(p.carrier) + (p.service ? ' — ' + shipServiceLabel(p.service) : '')),
                  ownLabel && h('button', {
                    onClick: function() { shipPrintLabelFile(ownLabel.id); },
                    title: 'Print the purchased label (4x6)',
                    style: { display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.55)', borderRadius: 6, padding: '4px 10px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
                  }, h(LIcon, { name: 'printer', size: 13 }), 'Print Label')),
                p.tracking && h('a', { href: shipTrackingUrl(p.carrier, p.tracking), target: '_blank', rel: 'noopener', style: { fontSize: 13, color: '#fff', textDecoration: 'underline' } }, p.tracking),
                h('div', { style: { display: 'flex', gap: 18, marginTop: 10 } },
                  h('div', null, h('div', { style: { fontSize: 10, fontWeight: 500, opacity: 0.75, textTransform: 'uppercase' } }, 'Ship Date'), h('div', { style: { fontSize: 13.5, fontWeight: 500 } }, p.shipped_ts ? new Date(p.shipped_ts).toLocaleDateString() : '—')),
                  h('div', null, h('div', { style: { fontSize: 10, fontWeight: 500, opacity: 0.75, textTransform: 'uppercase' } }, 'Cost'), h('div', { style: { fontSize: 13.5, fontWeight: 500 } }, p.cost != null ? shipMoney(p.cost) : '—')),
                  h('div', null, h('div', { style: { fontSize: 10, fontWeight: 500, opacity: 0.75, textTransform: 'uppercase' } }, 'Weight'), h('div', { style: { fontSize: 13.5, fontWeight: 500 } }, p.weight != null ? p.weight + ' ' + p.weight_uom : '—'))));
            }),
            card('Shipment Record', h('div', null,
              kv('Status', d.status),
              kv('Batch', d.batch_number || null),
              kv('Confirmation', (d.overrides && d.overrides.confirmation) || 'No Confirmation'),
              kv('Insurance', 'None'))),
            card('Return Labels', h('div', null,
              returns.length === 0 && h('div', { style: { fontSize: 13.5, color: C.textMuted, marginBottom: 8 } }, 'No return labels for this order.'),
              returns.map(function(rl) {
                var voided = rl.status === 'voided';
                return h('div', { key: rl.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid ' + C.border + '44', fontSize: 13.5, opacity: voided ? 0.55 : 1 } },
                  h('a', { href: shipTrackingUrl(rl.carrier, rl.tracking), target: '_blank', rel: 'noopener', style: { color: C.accent, textDecoration: voided ? 'line-through' : 'none' } }, rl.tracking),
                  h('span', { style: { color: C.textMuted } }, (shipCarrierLabel(rl.carrier) || rl.carrier) + ' · ' + (rl.service ? shipServiceLabel(rl.service) : '') + ' · ' + (rl.cost != null ? shipMoney(rl.cost) : '')),
                  h('span', { style: { color: C.textDim, fontSize: 11 } }, new Date(rl.created_at).toLocaleDateString()),
                  h('span', { style: { flex: 1 } }),
                  voided
                    ? h('span', { style: { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: C.danger } }, 'Voided')
                    : h('span', { style: { display: 'inline-flex', gap: 6 } },
                        h('button', { onClick: function() { shipPrintApiPdf('/shipping/return-label-file/' + rl.id); },
                          style: { background: 'transparent', border: '1px solid ' + C.border, color: C.text, borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' } }, 'Print'),
                        h('button', {
                          onClick: function() {
                            if (!window.confirm('Void this return label? USPS refunds the postage; a label the customer already used cannot be voided.')) return;
                            api('/shipping/return-label/' + rl.id + '/void', { method: 'POST', body: '{}' })
                              .then(function() { showToast('Return label voided and refunded', 'success'); loadReturns(); })
                              .catch(function(e) { showToast(e.message, 'error'); });
                          },
                          style: { background: 'transparent', border: '1px solid ' + C.danger, color: C.danger, borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' } }, 'Void')));
              }),
              h('button', {
                disabled: !retDomestic,
                onClick: retDomestic ? openReturnDialog : undefined,
                title: retDomestic ? undefined : 'Returns are eligible only for domestic orders',
                style: { marginTop: 8, background: 'transparent', border: '1px solid ' + (retDomestic ? C.accent : C.border), color: retDomestic ? C.accent : C.textMuted, opacity: retDomestic ? 1 : 0.5, borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: retDomestic ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }
              }, 'Create Return Label')))),
        retDialog && h('div', { style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
          h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: 20, width: 380, maxWidth: '92vw', boxSizing: 'border-box' } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
              h(LIcon, { name: 'undo-2', size: 16, color: C.accent }),
              h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, flex: 1 } }, 'Create return label'),
              h('button', { onClick: function() { setRetDialog(null); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),
            h('div', { style: { fontSize: 11.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
              'Prepaid label from the customer back to the warehouse. Postage is charged now and refunds if voided unused.'),
            h('div', { style: { fontSize: 10.5, color: C.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 } }, 'Service'),
            h('select', { value: retDialog.service, onChange: function(e) { setRetDialog(Object.assign({}, retDialog, { service: e.target.value })); },
              style: { width: '100%', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 9px', fontSize: 12.5, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box' } },
              retDialog.services.map(function(s) { return h('option', { key: s, value: s }, shipServiceLabel(s)); })),
            h('div', { style: { display: 'flex', gap: 8, marginBottom: 14 } },
              [['weight', 'Weight (lb)'], ['length', 'L (In)'], ['width', 'W (In)'], ['height', 'H (In)']].map(function(f) {
                return h('div', { key: f[0], style: { flex: 1 } },
                  h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 3 } }, f[1]),
                  h('input', { type: 'number', min: 0, step: '0.01', value: retDialog[f[0]],
                    onChange: function(e) { var p = {}; p[f[0]] = e.target.value; setRetDialog(Object.assign({}, retDialog, p)); },
                    style: { width: '100%', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12.5, fontFamily: 'inherit', boxSizing: 'border-box' } }));
              })),
            h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
              h(Btn, { size: 'sm', onClick: function() { setRetDialog(null); } }, 'Cancel'),
              h(Btn, { size: 'sm', variant: 'primary', disabled: retBusy, onClick: buyReturnLabel },
                retBusy ? 'Buying…' : 'Buy & Print'))))),
        prodSku && h(ShipProductModal, { sku: prodSku, onClose: function() { setProdSku(null); }, showToast: showToast }))));
}

// ─── Shipments sub-pages (Voided / Pending Customs / End of Day / Batch
// History; tracking-based buckets unlock with the carrier APIs) ────────────
function ShipShipmentsSubTab({ mode, showToast }) {
  var [data, setData] = useState(null);
  var [mfBusy, setMfBusy] = useState(false);
  var [sfBusy, setSfBusy] = useState(false); // USPS SCAN form close-out (synchronous)

  // History toolbar (Voided / Batch History): search + window pills + custom
  // range + export confirm modal — Shipments-page parity (user ask 9-01)
  var [hq, setHq] = useState('');
  var [hqLive, setHqLive] = useState('');
  var hqSearchProps = useShipAutoSearch(hqLive, setHqLive, function(v) { setHq(v.toLowerCase()); }); // search-as-you-type (9-18)
  var [hDays, setHDays] = useState(30);
  var [hRange, setHRange] = useState(null); // {from, to}
  var [hFrom, setHFrom] = useState('');
  var [hTo, setHTo] = useState('');
  var [hExport, setHExport] = useState(false);
  // Batches page (9-17): Open / Processed / Archived tabs + the batch status dialog
  // Deep-linked (?tab=printed&batch=3) so a refresh lands on the same tab with the same dialog open (Anthony 9-17)
  var [bStatus, setBStatus] = useState(function() {
    // Lands on Not Yet Printed (Anthony 9-17): the batches whose labels still have to be printed
    try { var t = new URLSearchParams(window.location.search).get('tab'); return ['open', 'not_printed', 'processed', 'archived', 'all'].indexOf(t) !== -1 ? t : 'not_printed'; } catch (e) { return 'not_printed'; }
  });
  var [bRun, setBRun] = useState(null); // batches whose labels are being created together (Anthony 9-22)
  var [bModal, setBModal] = useState(function() { // batch id
    try { return parseInt(new URLSearchParams(window.location.search).get('batch'), 10) || null; } catch (e) { return null; }
  });
  var [bTick, setBTick] = useState(0);
  var [bSel, setBSel] = useState({}); // batch history multi-select (Anthony 9-22): id -> true
  useEffect(function() {
    if (mode !== 'batches') return;
    try {
      var sp = new URLSearchParams(window.location.search);
      if (bStatus && bStatus !== 'not_printed') sp.set('tab', bStatus); else sp.delete('tab');
      if (bModal) sp.set('batch', String(bModal)); else sp.delete('batch');
      var qs = sp.toString();
      history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (e) {}
  }, [mode, bStatus, bModal]);
  useEffect(function() { setHq(''); setHqLive(''); setHDays(30); setHRange(null); setHFrom(''); setHTo(''); setHExport(false); }, [mode]);

  // the page's date window as civil dates (the SQL of the batch list reads it too, 9-27 LM20)
  // ONE formatter for both helpers: the 9-27 change declared it inside histWindow only, so
  // histWindowFilter threw ReferenceError on every row, the catch below swallowed it, and the
  // Printed / Archived / All batch tabs and the Voided tab showed nothing (Anthony 2026-09-28,
  // "the printed batches are all missing").
  var histFmt = new Intl.DateTimeFormat('en-CA');
  var histWindow = function() {
    if (hRange) return { from: hRange.from, to: hRange.to };
    var now = new Date();
    return { from: histFmt.format(new Date(now.getTime() - (hDays - 1) * 86400000)), to: histFmt.format(now) };
  };
  var histWindowFilter = function(rows, dateOf) {
    var hw = histWindow();
    var fromD = hw.from, toD = hw.to;
    return rows.filter(function(r) {
      var ts = dateOf(r);
      if (!ts) return false;
      var d;
      try { d = histFmt.format(new Date(ts)); } catch (e) { return false; }
      return d >= fromD && d <= toD;
    });
  };

  var histToolbar = function(placeholder, count) {
    var dateInp = { background: 'transparent', color: C.text, border: 'none', fontSize: 11.5, fontFamily: 'inherit', padding: '3px 2px', outline: 'none' };
    return h('div', { style: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' } },
      h('input', Object.assign({
        value: hqLive, placeholder: placeholder,
        style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', width: 280 }
      }, hqSearchProps)),
      h(Btn, { size: 'sm', onClick: function() { setHq(hqLive.trim().toLowerCase()); } }, 'Search'),
      h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
        [1, 7, 30, 60, 90].map(function(d) {
          var on = !hRange && hDays === d;
          return h('button', {
            key: d, onClick: function() { setHRange(null); setHFrom(''); setHTo(''); setHDays(d); },
            style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }
          }, d + 'd');
        })),
      h('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 4, background: C.bg, border: '1px solid ' + (hRange ? C.accent : C.border), borderRadius: 6, padding: '2px 6px' }, title: 'Custom date range (inclusive)' },
        h('input', { type: 'date', value: hFrom, max: hTo || undefined,
          onChange: function(e) { var v = e.target.value; setHFrom(v); if (v && hTo) setHRange({ from: v, to: hTo }); }, style: dateInp }),
        h('span', { style: { color: C.textDim, fontSize: 11 } }, '–'),
        h('input', { type: 'date', value: hTo, min: hFrom || undefined,
          onChange: function(e) { var v = e.target.value; setHTo(v); if (hFrom && v) setHRange({ from: hFrom, to: v }); }, style: dateInp }),
        hRange && h('button', {
          onClick: function() { setHRange(null); setHFrom(''); setHTo(''); },
          style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: '0 2px', display: 'inline-flex', alignItems: 'center' }
        }, h(LIcon, { name: 'x', size: 12 }))),
      h('span', { style: { marginLeft: 'auto', fontSize: 12, color: C.textMuted } }, count.toLocaleString() + ' rows'),
      h(Btn, { size: 'sm', onClick: function() { setHExport(true); } }, h(LIcon, { name: 'download', size: 13 }), ' Export…'));
  };

  var histExportModal = function(cfg) { // {title, count, columns, doExport}
    var filterLine = (hRange ? hRange.from + ' to ' + hRange.to : 'last ' + hDays + ' days') + (hq ? ' · search "' + hq + '"' : '');
    return h('div', { style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
      h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: 20, width: 420, maxWidth: '92vw', boxSizing: 'border-box' } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
          h(LIcon, { name: 'download', size: 16, color: C.accent }),
          h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, flex: 1 } }, cfg.title),
          h('button', { onClick: function() { setHExport(false); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'x', size: 16 }))),
        h('div', { style: { fontSize: 12.5, color: C.text, marginBottom: 6 } }, cfg.count.toLocaleString() + ' row' + (cfg.count === 1 ? '' : 's') + ' — ' + filterLine),
        h('div', { style: { fontSize: 10.5, color: C.textDim, marginBottom: 14 } }, 'Columns: ' + cfg.columns.join(', ')),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          h(Btn, { size: 'sm', onClick: function() { setHExport(false); } }, 'Cancel'),
          h(Btn, { size: 'sm', variant: 'primary', disabled: cfg.count === 0, onClick: function() {
            cfg.doExport();
            setHExport(false);
            showToast('Exported ' + cfg.count.toLocaleString() + ' row' + (cfg.count === 1 ? '' : 's'), 'success');
          } }, 'Export CSV (' + cfg.count.toLocaleString() + ')'))));
  };

  function loadEod() {
    return Promise.all([
      api('/shipping/end-of-day').catch(function() { return { carriers: [] }; }),
      api('/shipping/eod/manifests?month=' + encodeURIComponent(calMonth)).catch(function() { return { unmanifested_dhl: 0, manifests: [] }; }),
    ]).then(function(rs) { setData(Object.assign({}, rs[0], { mf: rs[1] })); });
  }

  // Manifest calendar state — month/day live in the URL (?m / ?d) per the
  // deeplink convention; buckets use the viewer's LOCAL date.
  var _calInit = (function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      var t = new Date();
      var curM = t.getFullYear() + '-' + ('0' + (t.getMonth() + 1)).slice(-2);
      var m = sp.get('m'), d = sp.get('d');
      return {
        m: (m && /^\d{4}-\d{2}$/.test(m)) ? m : curM,
        d: (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) ? d : t.toLocaleDateString('en-CA'),
      };
    } catch (e) { var t2 = new Date(); return { m: t2.getFullYear() + '-' + ('0' + (t2.getMonth() + 1)).slice(-2), d: t2.toLocaleDateString('en-CA') }; }
  })();
  var [calMonth, setCalMonth] = useState(_calInit.m);
  var [calDay, setCalDay] = useState(_calInit.d);
  var setCal = function(m2, d2) {
    setCalMonth(m2); setCalDay(d2);
    try {
      var sp = new URLSearchParams(window.location.search);
      sp.set('m', m2);
      if (d2) sp.set('d', d2); else sp.delete('d');
      history.replaceState({}, '', window.location.pathname + '?' + sp.toString());
    } catch (e) {}
  };

  // Client-side manifest polling — the server only holds the request ~15s
  var pollManifest = function(mid, tries) {
    if (tries <= 0) { setMfBusy(false); showToast('Manifest still rendering — use Refresh on the row', 'info'); loadEod(); return; }
    setTimeout(function() {
      api('/shipping/eod/manifest/' + mid + '/refresh', { method: 'POST', body: '{}' })
        .then(function(r) {
          if (r.error) { setMfBusy(false); showToast('Close-out failed: ' + r.error, 'error'); loadEod(); return; }
          if (r.completed) { setMfBusy(false); showToast('Manifest #' + mid + ' ready', 'success'); loadEod(); shipPrintApiPdf('/shipping/manifest-file/' + mid); return; }
          pollManifest(mid, tries - 1);
        })
        .catch(function() { pollManifest(mid, tries - 1); });
    }, 3000);
  };

  // Month navigation swaps the manifests IN PLACE — blanking the whole page
  // (setData(null)) on every ‹ › click read as a full reload (user, 8-30).
  var eodLoadedRef = useRef(false);
  // Batch-list refreshes (bTick: the dialog's archive / note / assignee / label retry, a dissolve)
  // swap the rows in place; only a tab or search change blanks the page. Closing the batch dialog
  // used to reload the list and blank it, which read as a full-screen reload (Anthony 9-22).
  var batchKeyRef = useRef(null);
  useEffect(function() {
    var monthOnly = mode === 'eod' && eodLoadedRef.current;
    eodLoadedRef.current = mode === 'eod';
    // Processed / Archived / All read the date window in SQL — the 500-row LIMIT used to apply
    // BEFORE the page's date filter, so an older window came back empty (sweep 2026-09-27 LM20)
    var bWin = (mode === 'batches' && !hq && bStatus !== 'open' && bStatus !== 'not_printed') ? histWindow() : null;
    var batchKey = mode === 'batches' ? bStatus + '|' + hq + '|' + (bWin ? bWin.from + '|' + bWin.to : '') : null;
    var batchInPlace = mode === 'batches' && batchKeyRef.current === batchKey && data !== null;
    batchKeyRef.current = batchKey;
    if (!monthOnly && !batchInPlace) setData(null);
    if (mode === 'voided') api('/shipping/shipments?status=voided&limit=5000').then(function(r) { setData(Array.isArray(r) ? r : (r.shipments || [])); }).catch(function() { setData([]); });
    else if (mode === 'customs') api('/shipping/pending-customs').then(function(r) { setData(r.orders || []); }).catch(function() { setData([]); });
    else if (mode === 'eod') {
      if (monthOnly) {
        api('/shipping/eod/manifests?month=' + encodeURIComponent(calMonth))
          .catch(function() { return { unmanifested_dhl: 0, manifests: [] }; })
          .then(function(mf) { setData(function(prev) { return prev ? Object.assign({}, prev, { mf: mf }) : prev; }); });
      } else loadEod();
    }
    else if (mode === 'batches') api('/shipping/batches/list?' + (hq ? 'q=' + encodeURIComponent(hq) : 'status=' + bStatus) + (bWin ? '&from=' + bWin.from + '&to=' + bWin.to : '')).then(function(r) { setData(Array.isArray(r) ? r : []); }).catch(function(e) { showToast('Could not load the batches: ' + ((e && e.message) || e), 'error'); setData([]); });
    else setData(false); // tracking-based placeholder buckets
  }, [mode, calMonth, bStatus, bTick, mode === 'batches' ? hq : '', mode === 'batches' ? hDays : 0, mode === 'batches' && hRange ? hRange.from + '|' + hRange.to : '']);

  // In Transit / Delivered / Exceptions render the full ShipShipmentsTab with
  // a deliveryFilter (shell routes them there directly since 9-01)
  if (data === null) return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading…');

  if (mode === 'voided') {
    var vRows = histWindowFilter(data || [], function(s) { return s.voided_at || s.created_at; });
    if (hq) {
      vRows = vRows.filter(function(s) {
        return ['order_number', 'tracking_number', 'carrier', 'service', 'created_by_name'].some(function(k) {
          return String(s[k] || '').toLowerCase().indexOf(hq) !== -1;
        });
      });
    }
    var voidedCsv = function() {
      shipDownloadCsv('voided-' + new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + '.csv',
        ['Shipment #', 'Order #', 'Tracking', 'Carrier', 'Service', 'Label Cost', 'Created By', 'Created', 'Voided'],
        vRows.map(function(s) {
          return [s.id, s.order_number || '', s.tracking_number || '', shipCarrierLabel(s.carrier) || s.carrier || '', s.service || '',
            s.label_cost != null ? s.label_cost : '', s.created_by_name || '', s.created_at || '', s.voided_at || ''];
        }));
    };
    return h('div', null,
      histToolbar('Search order #, tracking, carrier…', vRows.length),
      h(DataTable, {
        columns: [
          { key: 'id', label: 'Shipment', sortKey: 'id', render: function(s) { return '#' + s.id; } },
          { key: 'order_number', label: 'Order #', sortKey: 'order_number', render: function(s) { return h('span', { style: { fontWeight: 500, color: C.accent } }, s.order_number || s.zentail_order_id); } },
          { key: 'tracking_number', label: 'Tracking', sortKey: 'tracking_number', render: function(s) { return s.tracking_number ? h('a', { href: shipTrackingUrl(s.carrier, s.tracking_number), target: '_blank', rel: 'noopener', style: { color: C.accent, textDecoration: 'none' } }, s.tracking_number) : '—'; } },
          { key: 'carrier', label: 'Carrier · Service', render: function(s) { return (shipCarrierLabel(s.carrier) || s.carrier || '?') + ' · ' + (s.service ? shipServiceLabel(s.service) : '?'); } },
          { key: 'label_cost', label: 'Cost', sortKey: 'label_cost', render: function(s) { return s.label_cost != null ? shipMoney(s.label_cost) : '—'; } },
          { key: 'created_at', label: 'Created', sortKey: 'created_at', render: function(s) { return s.created_at ? new Date(s.created_at).toLocaleString() : '—'; } },
          { key: 'voided_at', label: 'Voided', sortKey: 'voided_at', render: function(s) { return s.voided_at ? new Date(s.voided_at).toLocaleString() : '—'; } },
        ],
        data: vRows, storageKey: 'ship-voided', defaultSort: 'voided_at', defaultSortDir: 'desc',
        emptyTitle: 'No voided shipments', emptySub: 'Voids appear here once labels are purchased and cancelled',
      }),
      hExport && histExportModal({
        title: 'Export voided shipments', count: vRows.length,
        columns: ['Shipment #', 'Order #', 'Tracking', 'Carrier', 'Service', 'Label Cost', 'Created By', 'Created', 'Voided'],
        doExport: voidedCsv,
      }));
  }
  if (mode === 'customs') {
    return h('div', null,
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } },
        data.length.toLocaleString() + ' open international orders — rows highlight SKUs still missing an HS code (fill them on the Products page).'),
      h(DataTable, {
        columns: [
          { key: 'order_number', label: 'Order #', sortKey: 'order_number', render: function(o) { return h('span', { style: { fontWeight: 500, color: C.accent } }, o.order_number); } },
          { key: 'channel', label: 'Channel', sortKey: 'channel' },
          { key: 'country', label: 'Country', sortKey: 'country', render: function(o) { return h('span', { style: { color: C.warning, fontWeight: 500 } }, o.country); } },
          { key: 'status', label: 'Status', sortKey: 'status' },
          { key: 'order_date', label: 'Order Date', sortKey: 'order_date', render: function(o) { return new Date(o.order_date).toLocaleDateString(); } },
          { key: 'missing', label: 'Missing HS codes', render: function(o) {
              if (!o.missing_skus.length) return h('span', { style: { color: C.success, fontSize: 11, fontWeight: 500 } }, '✓ complete');
              return h('span', { style: { fontSize: 11, color: C.danger }, title: o.missing_skus.join('\n') },
                o.missing_skus.slice(0, 2).join(', ') + (o.missing_skus.length > 2 ? ' +' + (o.missing_skus.length - 2) : ''));
            } },
        ],
        data: data, storageKey: 'ship-customs', defaultSort: 'order_date', emptyTitle: 'No international orders waiting', emptySub: 'Open intl orders appear here with their customs readiness',
      }));
  }
  if (mode === 'eod') {
    var totalPk = (data.carriers || []).reduce(function(a, c) { return a + (c.packages || 0); }, 0);
    // Content is width-capped — full-viewport cards left the right 60% of the
    // page as dead space and everything read as unanchored (user, 8-30)
    return h('div', { style: { maxWidth: 900 } },
      h(Card, { style: { padding: 16, marginBottom: 16 } },
        h('div', { style: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: (data.carriers || []).length ? 12 : 0 } },
          h('div', { style: { fontSize: 14, fontWeight: 500, color: C.text, flex: 1 } }, 'Orders Shipped Today'),
          h('div', { style: { fontSize: 12, color: C.textMuted } },
            new Date(data.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) +
            ' · ' + totalPk.toLocaleString() + ' shipped')),
        (data.carriers || []).length === 0
          ? h('div', { style: { fontSize: 12.5, color: C.textMuted } }, 'Nothing shipped yet today.')
          : h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap' } },
              data.carriers.map(function(c) {
                return h('div', { key: c.carrier || '?', style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 16px', minWidth: 150 } },
                  h('div', { style: { fontSize: 12, fontWeight: 500, color: C.text } }, shipCarrierLabel(String(c.carrier || '').toLowerCase()) || c.carrier || 'unknown'),
                  h('div', { style: { fontSize: 20, fontWeight: 600, color: C.accent, margin: '3px 0 2px' } }, c.packages.toLocaleString()),
                  // just the count (Anthony 9-19); the label spend stays as the only sub-line
                  c.label_cost != null && h('div', { style: { fontSize: 11, color: C.textMuted } }, shipMoney(c.label_cost) + ' labels'));
              }))),
      h(Card, { style: { padding: 16, marginBottom: 16 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 } },
          h('div', { style: { flex: 1 } },
            h('div', { style: { fontSize: 14, fontWeight: 500, color: C.text } }, 'DHL eCommerce close-out'),
            h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 2 } },
              ((data.mf && data.mf.unmanifested_dhl) || 0) + ' purchased label(s) not yet on a manifest')),
          h(Btn, {
            size: 'sm', disabled: mfBusy || !(data.mf && data.mf.unmanifested_dhl),
            onClick: function() {
              setMfBusy(true);
              api('/shipping/eod/manifest', { method: 'POST', body: JSON.stringify({ carrier: 'dhl_global_mail' }) })
                .then(function(r) {
                  if (r.error) { setMfBusy(false); showToast('Close-out failed: ' + r.error, 'error'); loadEod(); return; }
                  if (r.completed) { setMfBusy(false); showToast('Manifest #' + r.manifest_id + ' ready (' + r.packages + ' pkg)', 'success'); loadEod(); shipPrintApiPdf('/shipping/manifest-file/' + r.manifest_id); return; }
                  showToast('Manifest #' + r.manifest_id + ' created — waiting for the document…', 'info');
                  loadEod();
                  pollManifest(r.manifest_id, 40);
                })
                .catch(function(e) { setMfBusy(false); showToast(String(e.message || e), 'error'); });
            }
          }, mfBusy ? 'Closing out…' : 'Create DHL manifest')),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid ' + C.border } },
          h('div', { style: { flex: 1 } },
            h('div', { style: { fontSize: 14, fontWeight: 500, color: C.text } }, 'USPS SCAN form'),
            h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 2 } },
              ((data.mf && data.mf.unmanifested_usps) || 0) + ' purchased label(s) not yet on a SCAN form'),
            (data.mf && data.mf.rejected_usps) ? h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } },
              data.mf.rejected_usps + ' label(s) USPS would not accept on a SCAN form (mailed on an earlier date or already manifested) — left out of future forms') : null),
          h(Btn, {
            size: 'sm', disabled: sfBusy || !(data.mf && data.mf.unmanifested_usps),
            onClick: function() {
              setSfBusy(true);
              api('/shipping/eod/manifest', { method: 'POST', body: JSON.stringify({ carrier: 'usps' }) })
                .then(function(r) {
                  setSfBusy(false);
                  if (r.error) { showToast('SCAN form failed: ' + r.error, 'error'); loadEod(); return; }
                  showToast('SCAN form #' + r.manifest_id + ' ready (' + r.packages + ' pkg' + (r.excluded ? ', ' + r.excluded + ' rejected by USPS and left off' : '') + ')', 'success');
                  loadEod();
                  shipPrintApiPdf('/shipping/manifest-file/' + r.manifest_id);
                })
                .catch(function(e) { setSfBusy(false); showToast(String(e.message || e), 'error'); loadEod(); });
            }
          }, sfBusy ? 'Creating…' : 'Create SCAN form')),
        h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 8 } }, 'Counts above come from today\'s Zentail shipments; the close-outs cover labels purchased in this app.')),
      // ── Manifest calendar: month grid, day click → that day's manifests ──
      (function() {
        var parts = calMonth.split('-');
        var yy = parseInt(parts[0], 10), mm = parseInt(parts[1], 10);
        var first = new Date(yy, mm - 1, 1);
        var dim = new Date(yy, mm, 0).getDate();
        var offset = first.getDay();
        var todayStr = new Date().toLocaleDateString('en-CA');
        var byDay = {};
        // One row per DOCUMENT: DHL renders international and domestic packages
        // as separate manifests under one close-out (ShipStation shows them as
        // two downloads — Anthony 9-17). Error/processing close-outs have none.
        ((data.mf && data.mf.manifests) || []).forEach(function(m) {
          var k = new Date(m.created_at).toLocaleDateString('en-CA');
          var docs = (m.documents && m.documents.length) ? m.documents : [null];
          docs.forEach(function(doc) {
            (byDay[k] = byDay[k] || []).push(Object.assign({}, m, { doc: doc, rowKey: m.id + ':' + (doc ? doc.index : 'x') }));
          });
        });
        var nav = function(delta) {
          var d2 = new Date(yy, mm - 1 + delta, 1);
          setCal(d2.getFullYear() + '-' + ('0' + (d2.getMonth() + 1)).slice(-2), null);
        };
        var monthLabel = first.toLocaleString(undefined, { month: 'long', year: 'numeric' });
        var navBtn = function(txt, fn, title) {
          return h('button', { onClick: fn, title: title, className: 'phov', style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, borderRadius: 6, padding: '1px 8px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.6 } }, txt);
        };
        // Manifest table (Anthony 9-17): Carrier | Service | Packages | Time |
        // Printed By | Status — one row per document, DHL international and
        // domestic separately, the way ShipStation lists them.
        var thS = { padding: '6px 8px', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' };
        var tdS = { padding: '6px 8px', fontSize: 12.5, color: C.text, borderBottom: '1px solid ' + C.border + '44', verticalAlign: 'middle', whiteSpace: 'nowrap' };
        // Same button as the batch status modal's Print Labels (Btn sm primary)
        var linkBtn = function(label, fn, variant) {
          return h(Btn, { size: 'sm', variant: variant || 'primary', onClick: fn }, label);
        };
        var mfRow = function(m) {
          var doc = m.doc;
          var carrier = m.carrier === 'dhl_global_mail' ? 'DHL' : m.carrier === 'usps' ? 'USPS' : String(m.carrier || '').toUpperCase();
          var service = doc && doc.kind === 'international' ? 'International'
            : doc && (doc.kind === 'domestic' || doc.kind === 'scan_form') ? 'Domestic' : '—';
          // one Print button per document — no multi-select (Anthony 9-19)
          return h('tr', { key: m.rowKey },
            h('td', { style: Object.assign({}, tdS, { fontWeight: 500 }) }, carrier),
            h('td', { style: tdS }, service),
            h('td', { style: Object.assign({}, tdS, { textAlign: 'right' }) }, (doc && doc.total != null) ? doc.total : m.package_count),
            h('td', { style: tdS }, new Date(m.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })),
            h('td', { style: tdS }, m.created_by || '—'),
            h('td', { style: tdS },
              h('span', {
                style: { fontSize: 10.5, fontWeight: 500, textTransform: 'uppercase', padding: '1px 7px', borderRadius: 4,
                  background: m.status === 'completed' ? C.successSoft : C.bg,
                  color: m.status === 'completed' ? C.success : (m.status === 'error' ? C.danger : C.textMuted) }
              }, m.status)),
            h('td', { style: Object.assign({}, tdS, { textAlign: 'right' }) },
              doc ? linkBtn('Print', function() { shipPrintApiPdf('/shipping/manifest-file/' + m.id + '?doc=' + doc.index); })
                : (m.status === 'processing') ? linkBtn('Refresh', function() {
                    api('/shipping/eod/manifest/' + m.id + '/refresh', { method: 'POST', body: '{}' })
                      .then(function(r) {
                        showToast(r.error ? 'Close-out failed: ' + r.error : r.completed ? 'Manifest ready' : 'Still rendering (' + (r.status || '…') + ')', r.error ? 'error' : r.completed ? 'success' : 'info');
                        loadEod();
                      })
                      .catch(function(e) { showToast(String(e.message || e), 'error'); });
                  }, 'default') : null));
        };
        var cells = [];
        for (var i = 0; i < offset; i++) cells.push(h('div', { key: 'e' + i }));
        for (var day = 1; day <= dim; day++) {
          var ds = calMonth + '-' + ('0' + day).slice(-2);
          var list = byDay[ds] || [];
          var isSel = calDay === ds;
          var isToday = todayStr === ds;
          var worst = list.some(function(m) { return m.status === 'error'; }) ? 'error'
            : list.some(function(m) { return m.status === 'processing'; }) ? 'processing'
            : list.length ? 'completed' : null;
          // Just the date, perfectly square — manifest days signal through the
          // number's color/weight instead of an extra element (user call 8-30)
          cells.push(h('div', {
            key: ds,
            onClick: (function(dsx) { return function() { setCal(calMonth, dsx); }; })(ds),
            className: isSel ? undefined : 'phov',
            title: list.length ? list.length + ' manifest(s)' : undefined,
            style: { aspectRatio: '1 / 1', border: '1px solid ' + (isSel ? C.accent : 'transparent'), background: isSel ? 'rgba(79,110,247,0.10)' : 'transparent',
              borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }
          },
            h('span', { style: { fontSize: 11, fontWeight: (isToday || worst) ? 700 : 500,
              color: isToday ? C.accent : worst === 'completed' ? C.success : worst === 'error' ? C.danger : worst ? C.text : C.textMuted } }, day)));
        }
        var dayList = calDay ? (byDay[calDay] || []) : [];
        return h(Card, { style: { padding: 14 } },
          h('div', { style: { display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'flex-start' } },
            h('div', { style: { width: 300, flexShrink: 0 } },
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 } },
                h('div', { style: { fontSize: 14, fontWeight: 500, color: C.text, flex: 1 } }, 'Manifests'),
                navBtn('‹', function() { nav(-1); }, 'Previous month'),
                navBtn('›', function() { nav(1); }, 'Next month'),
                navBtn('Today', function() { var t = new Date(); setCal(t.getFullYear() + '-' + ('0' + (t.getMonth() + 1)).slice(-2), t.toLocaleDateString('en-CA')); }, 'Jump to today')),
              h('div', { style: { fontSize: 13.5, fontWeight: 600, color: C.text, margin: '0 0 10px' } }, monthLabel),
              h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 } },
                ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(function(w, wi) {
                  return h('div', { key: wi, style: { fontSize: 9, fontWeight: 500, color: C.textDim, textAlign: 'center', padding: '1px 0' } }, w);
                })),
              h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 } }, cells)),
            h('div', { style: { flex: 1, minWidth: 280 } },
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0 4px' } },
                h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, flex: 1 } },
                  calDay ? new Date(calDay + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'Select a day'),
                null),
              calDay
                ? (dayList.length
                    ? h('table', { style: { width: '100%', borderCollapse: 'collapse' } },
                        h('thead', null, h('tr', null,
                          h('th', { style: thS }, 'Carrier'),
                          h('th', { style: thS }, 'Service'),
                          h('th', { style: Object.assign({}, thS, { textAlign: 'right' }) }, 'Packages'),
                          h('th', { style: thS }, 'Time'),
                          h('th', { style: thS }, 'Printed By'),
                          h('th', { style: thS }, 'Status'),
                          h('th', { style: thS }, ''))),
                        h('tbody', null, dayList.map(mfRow)))
                    : h('div', { style: { fontSize: 12, color: C.textDim, padding: '6px 0' } }, 'No manifests on this day.'))
                : null)));
      })());
  }
  if (mode === 'batches') {
    // Open batches are always listed in full; the date window applies to processed/archived history
    // A search covers all history (every status, no date window); otherwise Open / Not Yet Printed list in full and the rest follow the date window
    var bRows = (hq || bStatus === 'open' || bStatus === 'not_printed') ? (data || []) : histWindowFilter(data || [], function(b) { return b.processed_at || b.created_at; });
    if (hq) {
      bRows = bRows.filter(function(b) {
        return [String(b.batch_number || ''), shipBatchStatusLabel(b.status), b.created_by_name || '', b.assignee_name || '', b.note || ''].some(function(v) {
          return String(v).toLowerCase().indexOf(hq) !== -1;
        });
      });
    }
    var fmtDate = function(v) { return v ? new Date(v).toLocaleDateString() : ''; };
    var bCsvCols = ['Batch', 'Status', 'Orders', 'Labels', 'Shipping Cost', 'Assigned To', 'Note', 'Created By', 'Created', 'Processed', 'Printed'];
    var batchesCsv = function() {
      shipDownloadCsv('batches-' + new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + '.csv', bCsvCols,
        bRows.map(function(b) { return [String(b.batch_number), shipBatchStatusLabel(b.status), b.order_count, b.labels_created, (parseFloat(b.labels_cost) || 0).toFixed(2), b.assignee_name || '', b.note || '', b.created_by_name || '', b.created_at || '', b.processed_at || '', b.labels_printed_at || '']; }));
    };
    var bTabs = [['open', 'Open'], ['not_printed', 'Not Yet Printed'], ['processed', 'Printed'], ['archived', 'Archived'], ['dissolved', 'Dissolved'], ['all', 'All']];
    var bReload = function() { setBTick(function(t) { return t + 1; }); try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {} };
    // Multi-select + Actions (Anthony 9-22): dissolve batches that never bought a label — errored runs
    // and never-run batches. Postage that was charged keeps its batch; the server says so per batch.
    var bSelRows = bRows.filter(function(b) { return bSel[b.id]; });
    var bSelCount = bSelRows.length;
    var bAllSelected = bRows.length > 0 && bRows.every(function(b) { return bSel[b.id]; });
    var toggleBatch = function(b) { setBSel(function(prev) { var n = Object.assign({}, prev); if (n[b.id]) delete n[b.id]; else n[b.id] = true; return n; }); };
    var dissolveSelected = async function() {
      var ids = bSelRows.map(function(b) { return b.id; });
      if (!ids.length) { showToast('Select some batches first', 'error'); return; }
      if (!(await appConfirm('Dissolve ' + ids.length + ' batch' + (ids.length === 1 ? '' : 'es') + '?\n\nOnly batches with no labels are dissolved (never run, or every order errored); their orders return to the queue and the batch stays in history as Dissolved. Batches with labels are skipped.', { danger: true, okLabel: 'Dissolve' }))) return;
      api('/shipping/batches/dissolve', { method: 'POST', body: JSON.stringify({ batch_ids: ids }) })
        .then(function(r) {
          var sk = r.skipped || [];
          showToast('Dissolved ' + (r.dissolved || []).length + ' batch' + ((r.dissolved || []).length === 1 ? '' : 'es')
            + (sk.length ? ' · skipped ' + sk.length + ': ' + sk.slice(0, 3).map(function(s) { return (s.batch_number || s.id) + ' ' + s.reason; }).join('; ') + (sk.length > 3 ? '…' : '') : ''),
            sk.length && !(r.dissolved || []).length ? 'error' : 'success');
          setBSel({}); bReload();
        })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    // Create Labels for every selected batch at once (Anthony 9-22): open batches with orders still
    // to label; each runs its own chain, all chains in parallel (ShipMultiBatchRunModal)
    var createLabelsSelected = async function() {
      var left = function(b) { return (parseInt(b.order_count, 10) || 0) - (parseInt(b.labels_created, 10) || 0); };
      var eligible = bSelRows.filter(function(b) { return b.status === 'open' && left(b) > 0; });
      var skipped = bSelRows.length - eligible.length;
      if (!eligible.length) { showToast(bSelRows.length ? 'The selected batches have nothing left to label' : 'Select some batches first', 'error'); return; }
      var orders = eligible.reduce(function(a, b) { return a + left(b); }, 0);
      if (!(await appConfirm('Buy labels for ' + eligible.length + ' batch' + (eligible.length === 1 ? '' : 'es') + ' (' + orders + ' order' + (orders === 1 ? '' : 's') + ')?'
        + (skipped ? '\n\n' + skipped + ' selected batch' + (skipped === 1 ? ' has' : 'es have') + ' nothing left to label and will be skipped.' : '')
        + '\n\nAll batches start at once. Postage is charged immediately and each order is marked shipped. Orders with problems are skipped and reported.', { okLabel: 'Buy labels' }))) return;
      setBRun(eligible);
    };
    return h('div', null,
      h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2, marginBottom: 12 } },
        bTabs.map(function(t) {
          var on = bStatus === t[0];
          return h('button', { key: t[0], onClick: function() { setBStatus(t[0]); setBSel({}); },
            style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500, fontFamily: 'inherit' } }, t[1]);
        })),
      histToolbar('Search batch #, note, assignee (all history)…', bRows.length),
      hq && h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: -6, marginBottom: 10 } }, 'Showing every batch matching "' + hq + '" across all statuses.'),
      h(DataTable, {
        columns: [
          { key: '_sel', label: '', locked: true, noFilter: true, noSort: true, sticky: true, stopClick: true,
            headerRender: function() { return h(ShipCheckbox, { checked: bAllSelected, title: 'Select all shown', onChange: function() { if (bAllSelected) { setBSel({}); return; } var n = {}; bRows.forEach(function(b) { n[b.id] = true; }); setBSel(n); } }); },
            render: function(b) { return h('div', { onClick: function(e) { e.stopPropagation(); toggleBatch(b); }, style: { display: 'flex', alignItems: 'center', margin: '-3px -8px', padding: '3px 8px', cursor: 'pointer' } }, h(ShipCheckbox, { checked: !!bSel[b.id], onChange: function() { toggleBatch(b); } })); } },
          // Batch # opens the status dialog (Anthony 9-17); ctrl/shift/middle-click still opens the queue in a new tab;
          // hover copy icon like the Shipments table's Order # / Tracking # / Batch cells (Anthony 2026-10-17)
          { key: 'batch_number', label: 'Batch', sortKey: 'batch_number', render: function(b) { return h(CopyCell, { value: String(b.batch_number) }, h('a', { href: '/batches/' + b.batch_number, onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); e.stopPropagation(); setBModal(b.batch_number); }, title: 'Batch status', style: { fontWeight: 500, color: C.accent, textDecoration: 'none' } }, String(b.batch_number))); } },
          { key: 'created_at', label: 'Created', sortKey: 'created_at', render: function(b) { return new Date(b.created_at).toLocaleString(); } },
          { key: 'processed_at', label: 'Processed Date', sortKey: 'processed_at', render: function(b) { return fmtDate(b.processed_at) || '—'; } },
          { key: 'labels_printed_at', label: 'Printed Date', sortKey: 'labels_printed_at', render: function(b) { return fmtDate(b.labels_printed_at) || '—'; } },
          { key: 'note', label: 'Note', sortKey: 'note', render: function(b) { return b.note ? h('span', { title: b.note, style: { display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' } }, b.note) : h('span', { style: { color: C.textDim } }, '—'); } },
          { key: 'assignee_name', label: 'Assigned To', sortKey: 'assignee_name', render: function(b) { return b.assignee_name || '—'; } },
          { key: 'order_count', label: 'Orders', sortKey: 'order_count' },
          { key: 'labels_created', label: 'Labels', sortKey: 'labels_created', render: function(b) {
              var n = parseInt(b.labels_created, 10) || 0, t = parseInt(b.order_count, 10) || 0, f = parseInt(b.labels_failed, 10) || 0;
              return h('span', { style: { color: t && n === t ? C.success : (f ? C.warning : C.text) } }, n + ' of ' + t + (f ? ' · ' + f + ' failed' : ''));
            } },
          { key: 'labels_cost', label: 'Shipping Cost', sortKey: 'labels_cost', render: function(b) { var c = parseFloat(b.labels_cost); return isFinite(c) && c > 0 ? '$' + c.toFixed(2) : '—'; } },
          { key: 'status', label: 'Status', sortKey: 'status', render: function(b) {
              var col = b.status === 'open' ? C.success : b.status === 'not_printed' ? C.warning : b.status === 'processed' ? C.accent : C.textMuted;
              return h('span', { style: { fontSize: 10, fontWeight: 500, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap', background: b.status === 'open' ? C.successSoft : b.status === 'not_printed' ? C.warningSoft : C.bg, color: col } }, shipBatchStatusLabel(b.status));
            } },
        ],
        data: bRows, storageKey: 'ship-batch-history-v5', defaultSort: 'created_at', defaultSortDir: 'desc',
        rowStyle: function(b) { return bSel[b.id] ? { background: 'rgba(79,110,247,0.08)' } : null; },
        bulkActions: {
          alwaysShow: true, count: bSelCount,
          items: [
            { section: 'Selected: ' + bSelCount },
          ]
            // Create Labels only on the Open tab (Anthony 9-22): a Not Yet Printed batch already bought
            // its labels; the few that failed are retried from that batch's own dialog
            .concat(bStatus === 'open' ? [{ label: 'Create Labels (' + bSelCount + ')', icon: 'tag', disabled: bSelCount === 0, onClick: createLabelsSelected }] : [])
            .concat([{ label: 'Dissolve (' + bSelCount + ')', icon: 'trash-2', disabled: bSelCount === 0, onClick: dissolveSelected }]),
          onClear: function() { setBSel({}); },
        },
        rowHref: function(b) { return '/batches/' + b.batch_number; },
        onRowClick: function(b) { setBModal(b.batch_number); },
        emptyTitle: bStatus === 'not_printed' ? 'No batches waiting to print' : bStatus === 'open' ? 'No open batches' : 'No batches here',
        emptySub: bStatus === 'not_printed' ? 'Batches land here once their labels are created' : bStatus === 'open' ? 'Create batches from the order selection panel' : 'Nothing in this window. Widen the date range or pick another tab.',
      }),
      // Close just closes: the dialog already calls onChanged after anything that changes the list (9-22)
      bModal && h(ShipBatchStatusModal, { batchId: bModal, live: null, showToast: showToast, onChanged: bReload, onClose: function() { setBModal(null); } }),
      bRun && h(ShipMultiBatchRunModal, { batches: bRun, showToast: showToast, onDone: bReload, onClose: function() { setBRun(null); setBSel({}); }, onOpenBatch: function(n) { setBModal(n); } }),
      hExport && histExportModal({
        title: 'Export batch history', count: bRows.length,
        columns: bCsvCols,
        doExport: batchesCsv,
      }));
  }
  return null;
}

;
/* ===== app-shipping-insights.js ===== */
// ─── Shipping app: Insights pages and their chart helpers ─── split 2026-09-19
// ─── Insights (ShipStation Insights parity) ────────────────────────────────
// First metric (user spec 9-01): average days to delivery from the date the
// order is BATCHED. Batching starts in this app at the SS cutover, so the
// shipped/ordered bases keep the page alive until then. Single-series line
// chart in the app accent (palette validated on both theme surfaces).
// By Carrier splits every carrier into Domestic and International (Anthony 2026-09-24: the one DHL
// row averaged 5-6 day domestic with 11-15 day international, so the international speed was
// invisible). Domestic = the USPS definition: the states, DC and the US territories; military
// addresses (AA/AE/AP) already arrive with co = US. The composite key is what a carrier-row click
// focuses on, so the tiles, chart, state and country tables follow the same split.
var SHIP_INSIGHT_DOMESTIC = { US: 1, PR: 1, VI: 1, GU: 1, AS: 1, MP: 1, UM: 1 };
// Layout (2026-09-26, review fix 15): the pages use the full content width like the queue (they
// were capped at 980 px, about half a wide screen), and breakdown tables scroll sideways inside
// their card instead of clipping (By Carrier's Orders column was cut off by overflow: hidden).
var SHIP_INSIGHT_PAGE_STYLE = { width: '100%', minWidth: 0, boxSizing: 'border-box' };
var SHIP_INSIGHT_TABLE_CARD = { padding: 0, overflowX: 'auto', overflowY: 'hidden', maxWidth: '100%' };
function shipInsightCarrierKey(row) { return row.c + '|' + (SHIP_INSIGHT_DOMESTIC[row.co] ? 'dom' : 'intl'); }
function shipInsightCarrierLabel(key) {
  var i = String(key).lastIndexOf('|');
  var c = i < 0 ? key : key.slice(0, i), scope = i < 0 ? 'dom' : key.slice(i + 1);
  return (shipCarrierLabel(c) || c) + (scope === 'intl' ? ' · International' : ' · Domestic');
}

function ShipInsightsPage({ showToast }) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _w = useState(30); var days = _w[0], setDays = _w[1];
  var _b = useState(null); var basisSel = _b[0], setBasisSel = _b[1]; // null = auto
  var _h = useState(null); var hover = _h[0], setHover = _h[1]; // hovered point index
  // Dimension filters: click a breakdown row to focus tiles + trend on that
  // segment (stackable; each table stays the picker for its own dimension).
  // The server ships compact per-order rows ONCE per window — filtering and
  // aggregation happen HERE, so dimension clicks are instant (the old
  // per-click server refetch made clearing a filter take ~15s).
  var _f = useState({ carrier: null, state: null, country: null }); var dim = _f[0], setDim = _f[1];
  var toggleDim = function(k, v) {
    var next = Object.assign({}, dim);
    next[k] = dim[k] === v ? null : v;
    setDim(next); setHover(null);
  };
  useEffect(function() {
    var live = true; // review M7 #105: a slower earlier window must not overwrite the selected one
    setData(null);
    api('/shipping/insights/delivery-time?days=' + days).then(function(r) { if (live) setData(r); })
      .catch(function(e) { if (!live) return; showToast(e.message, 'error'); setData({ rows: [] }); });
    return function() { live = false; };
  }, [days]);

  // Client-side aggregation over the raw rows → same `bases` shape the render
  // code consumes. Tiles/trend respect ALL filters; each breakdown table is
  // filtered by the OTHER dimensions only (it stays its own picker).
  var bases = useMemo(function() {
    var rows = (data && data.rows) || [];
    var mk = function() { return { vals: [], byDay: {}, byCarrier: {}, byState: {}, byCountry: {} }; };
    var acc = { batched: mk(), handling: mk(), shipped: mk(), ordered: mk() };
    rows.forEach(function(row) {
      var carKey = shipInsightCarrierKey(row);
      var mCar = !dim.carrier || carKey === dim.carrier;
      var mState = !dim.state || row.st === dim.state;
      var mCountry = !dim.country || row.co === dim.country;
      [['batched', row.b], ['handling', row.h], ['shipped', row.s], ['ordered', row.o]].forEach(function(kv) {
        var v = kv[1];
        if (v == null) return;
        var b = acc[kv[0]];
        if (mCar && mState && mCountry) {
          b.vals.push(v);
          (b.byDay[row.d] = b.byDay[row.d] || []).push(v);
        }
        if (mState && mCountry) (b.byCarrier[carKey] = b.byCarrier[carKey] || []).push(v);
        if (row.st && mCar && mCountry) (b.byState[row.st] = b.byState[row.st] || []).push(v);
        if (mCar && mState) (b.byCountry[row.co] = b.byCountry[row.co] || []).push(v);
      });
    });
    var round1 = function(x) { return Math.round(x * 10) / 10; };
    var stats = function(vals) {
      if (!vals.length) return { avg: null, median: null, n: 0 };
      var s = vals.slice().sort(function(a, b2) { return a - b2; });
      var mid = s.length >> 1;
      return { avg: round1(vals.reduce(function(a, b2) { return a + b2; }, 0) / vals.length),
        median: round1(s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2), n: vals.length };
    };
    var out = {};
    Object.keys(acc).forEach(function(k) {
      var b = acc[k];
      out[k] = Object.assign(stats(b.vals), {
        by_day: Object.keys(b.byDay).sort().map(function(dd) { return Object.assign({ day: dd }, stats(b.byDay[dd])); }),
        by_carrier: Object.keys(b.byCarrier).sort().map(function(c2) { return Object.assign({ carrier: c2 }, stats(b.byCarrier[c2])); }),
        by_state: Object.keys(b.byState).sort().map(function(s2) { return Object.assign({ state: s2 }, stats(b.byState[s2])); }),
        by_country: Object.keys(b.byCountry).sort().map(function(c2) { return Object.assign({ country: c2 }, stats(b.byCountry[c2])); }),
      });
    });
    return out;
  }, [data, dim]);

  var BASES = [
    { key: 'batched', label: 'Batched → Delivered', hint: 'From the moment the order joined a batch' },
    { key: 'handling', label: 'Handling Time', hint: 'Ordered → first carrier scan (warehouse speed)' },
    { key: 'shipped', label: 'Shipped → Delivered', hint: 'Carrier transit time (from the ship scan date)' },
    { key: 'ordered', label: 'Ordered → Delivered', hint: 'The customer’s full wait, order to doorstep' },
  ];
  // Chart defaults to the headline basis once it has data; falls to shipped
  var basis = basisSel || ((bases.batched && bases.batched.n > 0) ? 'batched' : 'shipped');
  var cur = bases[basis] || { by_day: [], n: 0 };

  var tile = function(b) {
    var s = bases[b.key] || { avg: null, median: null, n: 0 };
    var on = basis === b.key;
    return h('div', {
      key: b.key,
      onClick: function() { setBasisSel(b.key); setHover(null); },
      title: b.hint,
      style: { flex: 1, minWidth: 200, background: C.surface, border: '1px solid ' + (on ? C.accent : C.border), borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }
    },
      h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, b.label),
      h('div', { style: { fontSize: 26, fontWeight: 700, color: C.text, margin: '6px 0 2px', fontVariantNumeric: 'tabular-nums' } },
        s.n ? s.avg + ' days' : '—'),
      h('div', { style: { fontSize: 11, color: C.textMuted } },
        s.n ? 'median ' + s.median + 'd · ' + s.n.toLocaleString() + ' orders'
          : (b.key === 'batched' ? 'Fills in once orders are batched here'
            : b.key === 'handling' ? 'Fills in as first-scan data accrues'
            : 'No delivered orders in this window')));
  };

  // ── Trend: avg days by delivery day (single series, accent) ──
  var chart = (function() {
    var pts = cur.by_day || [];
    var W = 860, H = 240, padL = 44, padR = 16, padT = 14, padB = 30;
    if (!pts.length) {
      return h('div', { style: { padding: '48px 0', textAlign: 'center', color: C.textMuted, fontSize: 12.5 } },
        basis === 'batched'
          ? 'No batched-order deliveries yet — this chart starts filling in once orders are batched in this app.'
          : 'No delivered orders in this window yet.');
    }
    var maxY = Math.max.apply(null, pts.map(function(p) { return p.avg; }).concat([1]));
    var yTop = Math.ceil(maxY * 1.15);
    var x = function(i) { return padL + (pts.length === 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (pts.length - 1)); };
    var y = function(v) { return padT + (H - padT - padB) * (1 - v / yTop); };
    var path = pts.map(function(p, i) { return (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(p.avg).toFixed(1); }).join(' ');
    var area = path + ' L' + x(pts.length - 1).toFixed(1) + ' ' + y(0) + ' L' + x(0).toFixed(1) + ' ' + y(0) + ' Z';
    var ticks = [0, 0.25, 0.5, 0.75, 1].map(function(f) { return Math.round(yTop * f * 10) / 10; });
    var lbl = function(dd) { var d2 = new Date(dd + 'T12:00:00'); return (d2.getMonth() + 1) + '/' + d2.getDate(); };
    var xEvery = Math.max(1, Math.ceil(pts.length / 10));
    var hv = hover != null && pts[hover] ? pts[hover] : null;
    return h('div', { style: { position: 'relative' } },
      h('svg', {
        viewBox: '0 0 ' + W + ' ' + H, style: { width: '100%', height: 'auto', display: 'block' },
        onMouseLeave: function() { setHover(null); },
        onMouseMove: function(e) {
          var r = e.currentTarget.getBoundingClientRect();
          var mx = ((e.clientX - r.left) / r.width) * W;
          var best = 0, bd = Infinity;
          pts.forEach(function(p, i) { var d2 = Math.abs(x(i) - mx); if (d2 < bd) { bd = d2; best = i; } });
          setHover(best);
        }
      },
        // recessive grid
        ticks.map(function(t, i) {
          return h('g', { key: i },
            h('line', { x1: padL, x2: W - padR, y1: y(t), y2: y(t), stroke: C.border, strokeWidth: 1, opacity: 0.6 }),
            h('text', { x: padL - 8, y: y(t) + 3.5, textAnchor: 'end', fontSize: 10, fill: C.textMuted }, t));
        }),
        pts.map(function(p, i) {
          if (i % xEvery !== 0 && i !== pts.length - 1) return null;
          return h('text', { key: 'x' + i, x: x(i), y: H - 10, textAnchor: 'middle', fontSize: 10, fill: C.textMuted }, lbl(p.day));
        }),
        h('path', { d: area, fill: C.accent, opacity: 0.08 }),
        h('path', { d: path, fill: 'none', stroke: C.accent, strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }),
        // crosshair + hovered marker
        hv && h('line', { x1: x(hover), x2: x(hover), y1: padT, y2: H - padB, stroke: C.textDim, strokeWidth: 1, strokeDasharray: '3 3' }),
        hv && h('circle', { cx: x(hover), cy: y(hv.avg), r: 4.5, fill: C.accent, stroke: C.surface, strokeWidth: 2 }),
        // selective direct label: last point
        h('text', { x: x(pts.length - 1) - 4, y: y(pts[pts.length - 1].avg) - 8, textAnchor: 'end', fontSize: 11, fontWeight: 600, fill: C.text },
          pts[pts.length - 1].avg + 'd')),
      hv && h('div', {
        style: { position: 'absolute', left: Math.min(92, Math.max(8, (x(hover) / W) * 100)) + '%', top: 4, transform: 'translateX(-50%)',
          background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 10px', fontSize: 11.5, color: C.text,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)', pointerEvents: 'none', whiteSpace: 'nowrap' }
      },
        h('div', { style: { fontWeight: 600 } }, new Date(hv.day + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })),
        h('div', { style: { color: C.textMuted } }, 'avg ' + hv.avg + 'd · median ' + hv.median + 'd · ' + hv.n + ' orders')));
  })();

  var curBase = BASES.find(function(b) { return b.key === basis; });
  var dimChips = [
    dim.carrier && { k: 'carrier', label: shipCarrierLabel(dim.carrier) || dim.carrier },
    dim.state && { k: 'state', label: dim.state },
    dim.country && { k: 'country', label: dim.country },
  ].filter(Boolean);
  return h('div', { style: SHIP_INSIGHT_PAGE_STYLE },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' } },
      h('div', { style: { fontSize: 12.5, color: C.textMuted, flex: 1, minWidth: 220 } },
        'Delivery speed across orders delivered in the window. Click a tile to chart that basis; click a breakdown row to focus on it.'),
      dimChips.map(function(c2) {
        return h('button', {
          key: c2.k,
          onClick: function() { toggleDim(c2.k, dim[c2.k]); },
          title: 'Clear this filter',
          style: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.accentSoft, color: C.accent, border: '1px solid ' + C.accent + '55', borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }
        }, c2.label, h(LIcon, { name: 'x', size: 11 }));
      }),
      dimChips.length > 0 && h('button', {
        onClick: function() { setDim({ carrier: null, state: null, country: null }); setHover(null); },
        style: { background: 'transparent', border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 999, padding: '3px 12px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
      }, 'Clear filters'),
      shipInsightWindowPills(days, function(d) { setDays(d); setHover(null); })),
    data === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Crunching delivery data…')
      : h('div', null,
          h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 } }, BASES.map(tile)),
          h(Card, { style: { padding: '16px 18px', marginBottom: 16 } },
            h('div', { style: { fontSize: 13.5, fontWeight: 500, color: C.text, marginBottom: 2 } },
              'Average days — ' + (curBase ? curBase.label : '')),
            h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 10 } }, 'By delivery day · hover for detail'),
            chart),
          (function() {
            // Breakdown tables: carrier / state / country, all for the selected basis
            var tbl = function(title, rows, keyField, dimKey, labelFn, scroll) {
              if (!rows || !rows.length) return null;
              return h(Card, { style: Object.assign({}, SHIP_INSIGHT_TABLE_CARD, { flex: 1, minWidth: 300 }) },
                h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, padding: '10px 14px 0' } }, title),
                h('div', { style: scroll ? { maxHeight: 340, overflowY: 'auto' } : null },
                  h('table', { style: { width: '100%', fontSize: 12.5, borderCollapse: 'collapse' } },
                    h('thead', null, h('tr', { style: { borderBottom: '1px solid ' + C.border } },
                      [title.replace('By ', ''), 'Avg', 'Median', 'Orders'].map(function(hd, i) {
                        return h('th', { key: hd, style: { padding: '8px 14px', textAlign: i ? 'right' : 'left', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', top: 0, background: C.surface, whiteSpace: 'nowrap' } }, hd);
                      }))),
                    h('tbody', null, rows.map(function(c2, i) {
                      var on = dim[dimKey] === c2[keyField];
                      return h('tr', {
                        key: c2[keyField],
                        className: 'phov',
                        onClick: function() { toggleDim(dimKey, c2[keyField]); },
                        title: on ? 'Click to clear this focus' : 'Focus the tiles and chart on ' + (labelFn ? labelFn(c2[keyField]) : c2[keyField]),
                        style: { borderBottom: i < rows.length - 1 ? '1px solid ' + C.border : 'none', cursor: 'pointer',
                          background: on ? C.accentSoft : 'transparent', boxShadow: on ? 'inset 3px 0 0 ' + C.accent : 'none' }
                      },
                        h('td', { style: { padding: '8px 14px', fontWeight: 500, color: on ? C.accent : C.text, whiteSpace: 'nowrap' } }, labelFn ? labelFn(c2[keyField]) : c2[keyField]),
                        h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, c2.avg + 'd'),
                        h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } }, c2.median + 'd'),
                        h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } }, c2.n.toLocaleString()));
                    })))));
            };
            return h('div', { style: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' } },
              tbl('By Carrier', cur.by_carrier, 'carrier', 'carrier', shipInsightCarrierLabel, false),
              tbl('By State', cur.by_state, 'state', 'state', null, true),
              tbl('By Country', cur.by_country, 'country', 'country', null, true));
          })()));
}

// ─── Insights: Orders + Shipping volume pages ──────────────────────────────
// Both ride GET /insights/volume (grouped rows, warmed server-side): `ord` =
// orders received by NY order-day × channel, `shp` = packages shipped by NY
// ship-day × carrier × destination. All filtering/aggregation is client-side
// (same instant-filter pattern as the Overview page).

// NY-calendar day list for the window — charts zero-fill so a no-order day
// shows as 0, not a hole (dedup guards the DST double-day). 'y' = yesterday.
function shipInsightDayRange(days) {
  var fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' });
  var now = Date.now();
  if (days === 'y') return [fmt.format(new Date(now - 86400000))];
  var out = [];
  for (var i = days - 1; i >= 0; i--) {
    var d = fmt.format(new Date(now - i * 86400000));
    if (out[out.length - 1] !== d) out.push(d);
  }
  return out;
}

// Window pills shared by every insights page ('y' = yesterday, the last
// complete NY day — a trailing "1d" would always be a partial today)
var SHIP_INSIGHT_WINDOWS = [['y', 'Yesterday'], [7, '7d'], [30, '30d'], [60, '60d'], [90, '90d']];
function shipInsightWindowPills(days, setDays) {
  return h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
    SHIP_INSIGHT_WINDOWS.map(function(w) {
      var on = days === w[0];
      return h('button', {
        key: String(w[0]), onClick: function() { setDays(w[0]); },
        style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }
      }, w[1]);
    }));
}
var shipInsightWinLabel = function(w) { return w === 'y' ? 'yesterday' : 'in the last ' + w + ' days'; };
var shipInsightDayCount = function(w) { return w === 'y' ? 1 : w; };

var shipMoneyCompact = function(v) {
  return v >= 10000 ? '$' + Math.round(v / 1000) + 'k'
    : v >= 1000 ? '$' + (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
    : '$' + Math.round(v);
};

// Generic single-series trend (accent line + soft area, crosshair tooltip,
// last-point direct label) — the Overview chart's anatomy, reusable.
// pts = [{day, val, ...}]; fmtTick for y-axis, fmtVal for labels/tooltip.
function ShipInsightTrend({ pts, fmtTick, fmtVal, tipText, empty }) {
  var _h = useState(null); var hover = _h[0], setHover = _h[1];
  if (!pts || !pts.length) {
    return h('div', { style: { padding: '42px 0', textAlign: 'center', color: C.textMuted, fontSize: 12.5 } }, empty || 'No data in this window yet.');
  }
  var W = 860, H = 220, padL = 50, padR = 16, padT = 14, padB = 30;
  var maxY = Math.max.apply(null, pts.map(function(p) { return p.val; }).concat([1]));
  var yTop = maxY * 1.15;
  var x = function(i) { return padL + (pts.length === 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (pts.length - 1)); };
  var y = function(v) { return padT + (H - padT - padB) * (1 - v / yTop); };
  var path = pts.map(function(p, i) { return (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(p.val).toFixed(1); }).join(' ');
  var area = path + ' L' + x(pts.length - 1).toFixed(1) + ' ' + y(0) + ' L' + x(0).toFixed(1) + ' ' + y(0) + ' Z';
  var tickVals = [], seen = {};
  [0, 0.25, 0.5, 0.75, 1].forEach(function(f) {
    var t = yTop * f, lbl2 = fmtTick(t);
    if (!seen[lbl2]) { seen[lbl2] = true; tickVals.push({ v: t, label: lbl2 }); }
  });
  var lbl = function(dd) { var d2 = new Date(dd + 'T12:00:00'); return (d2.getMonth() + 1) + '/' + d2.getDate(); };
  var xEvery = Math.max(1, Math.ceil(pts.length / 10));
  var hv = hover != null && pts[hover] ? pts[hover] : null;
  return h('div', { style: { position: 'relative' } },
    h('svg', {
      viewBox: '0 0 ' + W + ' ' + H, style: { width: '100%', height: 'auto', display: 'block' },
      onMouseLeave: function() { setHover(null); },
      onMouseMove: function(e) {
        var r = e.currentTarget.getBoundingClientRect();
        var mx = ((e.clientX - r.left) / r.width) * W;
        var best = 0, bd = Infinity;
        pts.forEach(function(p, i) { var d2 = Math.abs(x(i) - mx); if (d2 < bd) { bd = d2; best = i; } });
        setHover(best);
      }
    },
      tickVals.map(function(t, i) {
        return h('g', { key: i },
          h('line', { x1: padL, x2: W - padR, y1: y(t.v), y2: y(t.v), stroke: C.border, strokeWidth: 1, opacity: 0.6 }),
          h('text', { x: padL - 8, y: y(t.v) + 3.5, textAnchor: 'end', fontSize: 10, fill: C.textMuted }, t.label));
      }),
      pts.map(function(p, i) {
        if (i % xEvery !== 0 && i !== pts.length - 1) return null;
        return h('text', { key: 'x' + i, x: x(i), y: H - 10, textAnchor: 'middle', fontSize: 10, fill: C.textMuted }, lbl(p.day));
      }),
      h('path', { d: area, fill: C.accent, opacity: 0.08 }),
      h('path', { d: path, fill: 'none', stroke: C.accent, strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }),
      hv && h('line', { x1: x(hover), x2: x(hover), y1: padT, y2: H - padB, stroke: C.textDim, strokeWidth: 1, strokeDasharray: '3 3' }),
      hv && h('circle', { cx: x(hover), cy: y(hv.val), r: 4.5, fill: C.accent, stroke: C.surface, strokeWidth: 2 }),
      h('text', { x: x(pts.length - 1) - 4, y: y(pts[pts.length - 1].val) - 8, textAnchor: 'end', fontSize: 11, fontWeight: 600, fill: C.text },
        fmtVal(pts[pts.length - 1].val))),
    hv && h('div', {
      style: { position: 'absolute', left: Math.min(92, Math.max(8, (x(hover) / W) * 100)) + '%', top: 4, transform: 'translateX(-50%)',
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 10px', fontSize: 11.5, color: C.text,
        boxShadow: '0 4px 14px rgba(0,0,0,0.25)', pointerEvents: 'none', whiteSpace: 'nowrap' }
    },
      h('div', { style: { fontWeight: 600 } }, new Date(hv.day + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })),
      h('div', { style: { color: C.textMuted } }, tipText(hv))));
}

// Shared page chrome for the volume pages: blurb + filter chip + optional
// extra controls (e.g. a channel dropdown) + window pills
function shipInsightToolbar(blurb, chip, onClearChip, days, setDays, extra) {
  return h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' } },
    h('div', { style: { fontSize: 12.5, color: C.textMuted, flex: 1, minWidth: 220 } }, blurb),
    chip && h('button', {
      onClick: onClearChip, title: 'Clear this filter',
      style: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.accentSoft, color: C.accent, border: '1px solid ' + C.accent + '55', borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }
    }, chip, h(LIcon, { name: 'x', size: 11 })),
    chip && h('button', {
      onClick: onClearChip,
      style: { background: 'transparent', border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 999, padding: '3px 12px', fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
    }, 'Clear filters'),
    extra || null,
    shipInsightWindowPills(days, setDays));
}

var shipInsightTile = function(label, value, sub) {
  return h('div', { key: label, style: { flex: 1, minWidth: 200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: '14px 16px' } },
    h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, label),
    h('div', { style: { fontSize: 26, fontWeight: 700, color: C.text, margin: '6px 0 2px', fontVariantNumeric: 'tabular-nums' } }, value),
    h('div', { style: { fontSize: 11, color: C.textMuted } }, sub || ' '));
};

var shipInsightChartCard = function(title, sub, chart) {
  return h(Card, { style: { padding: '16px 18px', marginBottom: 16 } },
    h('div', { style: { fontSize: 13.5, fontWeight: 500, color: C.text, marginBottom: 2 } }, title),
    h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 10 } }, sub),
    chart);
};

function ShipInsightsOrdersPage({ showToast }) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _w = useState(30); var days = _w[0], setDays = _w[1];
  var _c = useState([]); var chans = _c[0], setChans = _c[1]; // empty = all
  useEffect(function() {
    var live = true; // review M7 #105: a slower earlier window must not overwrite the selected one
    setData(null);
    api('/shipping/insights/volume?days=' + days).then(function(r) { if (live) setData(r); })
      .catch(function(e) { if (!live) return; showToast(e.message, 'error'); setData({ ord: [], shp: [] }); });
    return function() { live = false; };
  }, [days]);

  var agg = useMemo(function() {
    var rows = (data && data.ord) || [];
    var filtered = chans.length ? rows.filter(function(r2) { return chans.indexOf(r2.ch) !== -1; }) : rows;
    var n = 0, v = 0, byDay = {}, byCh = {};
    filtered.forEach(function(r2) {
      n += r2.n; v += r2.v;
      var d2 = byDay[r2.d] || (byDay[r2.d] = { n: 0, v: 0 });
      d2.n += r2.n; d2.v += r2.v;
    });
    rows.forEach(function(r2) {
      var c2 = byCh[r2.ch] || (byCh[r2.ch] = { n: 0, v: 0 });
      c2.n += r2.n; c2.v += r2.v;
    });
    var winDays = data ? shipInsightDayRange(data.days || days) : [];
    var mkPts = function(field) {
      return winDays.map(function(dd) { return { day: dd, val: byDay[dd] ? Math.round(byDay[dd][field] * 100) / 100 : 0, n: byDay[dd] ? byDay[dd].n : 0 }; });
    };
    return {
      n: n, v: Math.round(v * 100) / 100,
      ptsN: mkPts('n'), ptsV: mkPts('v'),
      channels: Object.keys(byCh).sort().map(function(ch) { return { ch: ch, n: byCh[ch].n, v: Math.round(byCh[ch].v * 100) / 100 }; }),
      allN: rows.reduce(function(a, r2) { return a + r2.n; }, 0),
    };
  }, [data, chans, days]);

  var winN = data ? (data.days || days) : days;
  var chKey = chans.join('|');
  return h('div', { style: SHIP_INSIGHT_PAGE_STYLE },
    shipInsightToolbar(
      'Orders received and GMV by order date. FBM scope (the app’s allowed warehouses); cancelled orders excluded. Click a channel row to focus.',
      null, null, days, setDays,
      h(ReportParamSelect, { label: 'Channel', values: agg.channels.map(function(c2) { return c2.ch; }), selected: chans, onChange: setChans })),
    data === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Crunching order data…')
      : h('div', null,
          h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 } },
            shipInsightTile('Orders Received', agg.n.toLocaleString(),
              chans.length === 1 ? chans[0] : chans.length ? chans.length + ' channels' : shipInsightWinLabel(winN)),
            shipInsightTile('GMV', shipMoney(agg.v), 'order totals, before refunds'),
            shipInsightTile('Avg Order Value', agg.n ? shipMoney(agg.v / agg.n) : '—', 'GMV ÷ orders'),
            shipInsightTile('Orders / Day', agg.n ? (Math.round((agg.n / shipInsightDayCount(winN)) * 10) / 10).toLocaleString() : '—', 'average across the window')),
          shipInsightChartCard('Orders per day', 'By order date · hover for detail',
            h(ShipInsightTrend, { key: 'n' + winN + chKey, pts: agg.ptsN,
              fmtTick: function(t) { return String(Math.round(t)); },
              fmtVal: function(v2) { return v2.toLocaleString(); },
              tipText: function(p) { return p.val.toLocaleString() + ' order' + (p.val === 1 ? '' : 's'); } })),
          shipInsightChartCard('GMV per day', 'Order totals by order date',
            h(ShipInsightTrend, { key: 'v' + winN + chKey, pts: agg.ptsV,
              fmtTick: shipMoneyCompact,
              fmtVal: shipMoneyCompact,
              tipText: function(p) { return shipMoney(p.val) + ' · ' + p.n.toLocaleString() + ' order' + (p.n === 1 ? '' : 's'); } })),
          agg.channels.length > 0 && h(Card, { style: SHIP_INSIGHT_TABLE_CARD },
            h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, padding: '10px 14px 0' } }, 'By Channel'),
            h('table', { style: { width: '100%', fontSize: 12.5, borderCollapse: 'collapse' } },
              h('thead', null, h('tr', { style: { borderBottom: '1px solid ' + C.border } },
                ['Channel', 'Orders', 'GMV', 'AOV', 'Share'].map(function(hd, i) {
                  return h('th', { key: hd, style: { padding: '8px 14px', textAlign: i ? 'right' : 'left', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, hd);
                }))),
              h('tbody', null, agg.channels.map(function(c2, i) {
                var on = chans.indexOf(c2.ch) !== -1;
                return h('tr', {
                  key: c2.ch, className: 'phov',
                  onClick: function() { setChans(on ? chans.filter(function(x) { return x !== c2.ch; }) : chans.concat([c2.ch])); },
                  title: on ? 'Click to remove this channel from the focus' : 'Focus the tiles and charts on ' + c2.ch,
                  style: { borderBottom: i < agg.channels.length - 1 ? '1px solid ' + C.border : 'none', cursor: 'pointer',
                    background: on ? C.accentSoft : 'transparent', boxShadow: on ? 'inset 3px 0 0 ' + C.accent : 'none' }
                },
                  h('td', { style: { padding: '8px 14px', fontWeight: 500, color: on ? C.accent : C.text } }, c2.ch),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, c2.n.toLocaleString()),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, shipMoney(c2.v)),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } }, c2.n ? shipMoney(c2.v / c2.n) : '—'),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } },
                    agg.allN ? Math.round((c2.n / agg.allN) * 100) + '%' : '—'));
              }))))));
}

function ShipInsightsShippingPage({ showToast }) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _w = useState(30); var days = _w[0], setDays = _w[1];
  var _c = useState(null); var carSel = _c[0], setCarSel = _c[1];
  useEffect(function() {
    var live = true; // review M7 #105: a slower earlier window must not overwrite the selected one
    setData(null);
    api('/shipping/insights/volume?days=' + days).then(function(r) { if (live) setData(r); })
      .catch(function(e) { if (!live) return; showToast(e.message, 'error'); setData({ ord: [], shp: [] }); });
    return function() { live = false; };
  }, [days]);

  var agg = useMemo(function() {
    var rows = (data && data.shp) || [];
    var filtered = carSel ? rows.filter(function(r2) { return r2.c === carSel; }) : rows;
    var n = 0, lc = 0, nc = 0, byDay = {}, byCar = {};
    filtered.forEach(function(r2) {
      n += r2.n; lc += r2.lc || 0; nc += r2.nc || 0;
      var d2 = byDay[r2.d] || (byDay[r2.d] = { n: 0, lc: 0 });
      d2.n += r2.n; d2.lc += r2.lc || 0;
    });
    rows.forEach(function(r2) {
      var c2 = byCar[r2.c] || (byCar[r2.c] = { n: 0, lc: 0, nc: 0 });
      c2.n += r2.n; c2.lc += r2.lc || 0; c2.nc += r2.nc || 0;
    });
    var winDays = data ? shipInsightDayRange(data.days || days) : [];
    var mkPts = function(field) {
      return winDays.map(function(dd) { return { day: dd, val: byDay[dd] ? Math.round(byDay[dd][field] * 100) / 100 : 0, n: byDay[dd] ? byDay[dd].n : 0 }; });
    };
    return {
      n: n, lc: Math.round(lc * 100) / 100, nc: nc,
      ptsN: mkPts('n'), ptsC: mkPts('lc'),
      carriers: Object.keys(byCar).sort().map(function(c2) { return { c: c2, n: byCar[c2].n, lc: Math.round(byCar[c2].lc * 100) / 100, nc: byCar[c2].nc }; }),
    };
  }, [data, carSel, days]);

  var winN = data ? (data.days || days) : days;
  return h('div', { style: SHIP_INSIGHT_PAGE_STYLE },
    shipInsightToolbar(
      'Labels shipped and postage spend by ship date. Spend covers labels with a recorded cost (ShipStation history can be patchy). Click a carrier row to focus.',
      carSel ? (shipCarrierLabel(carSel) || carSel) : null, function() { setCarSel(null); }, days, setDays),
    data === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Crunching shipment data…')
      : h('div', null,
          h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 } },
            shipInsightTile('Shipments', agg.n.toLocaleString(), 'labels ' + shipInsightWinLabel(winN)),
            shipInsightTile('Label Spend', shipMoney(agg.lc), agg.nc < agg.n ? agg.nc.toLocaleString() + ' of ' + agg.n.toLocaleString() + ' labels have a cost' : 'all labels costed'),
            shipInsightTile('Avg Cost / Label', agg.nc ? shipMoney(agg.lc / agg.nc) : '—', 'across costed labels'),
            shipInsightTile('Shipments / Day', agg.n ? (Math.round((agg.n / shipInsightDayCount(winN)) * 10) / 10).toLocaleString() : '—', 'average across the window')),
          shipInsightChartCard('Shipments per day', 'By ship date · hover for detail',
            h(ShipInsightTrend, { key: 'n' + winN + (carSel || ''), pts: agg.ptsN,
              fmtTick: function(t) { return String(Math.round(t)); },
              fmtVal: function(v2) { return v2.toLocaleString(); },
              tipText: function(p) { return p.val.toLocaleString() + ' label' + (p.val === 1 ? '' : 's'); } })),
          shipInsightChartCard('Label spend per day', 'Postage for labels shipped that day',
            h(ShipInsightTrend, { key: 'c' + winN + (carSel || ''), pts: agg.ptsC,
              fmtTick: shipMoneyCompact,
              fmtVal: shipMoneyCompact,
              tipText: function(p) { return shipMoney(p.val) + ' · ' + p.n.toLocaleString() + ' label' + (p.n === 1 ? '' : 's'); } })),
          agg.carriers.length > 0 && h(Card, { style: SHIP_INSIGHT_TABLE_CARD },
            h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, padding: '10px 14px 0' } }, 'By Carrier'),
            h('table', { style: { width: '100%', fontSize: 12.5, borderCollapse: 'collapse' } },
              h('thead', null, h('tr', { style: { borderBottom: '1px solid ' + C.border } },
                ['Carrier', 'Shipments', 'Spend', 'Avg Cost'].map(function(hd, i) {
                  return h('th', { key: hd, style: { padding: '8px 14px', textAlign: i ? 'right' : 'left', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, hd);
                }))),
              h('tbody', null, agg.carriers.map(function(c2, i) {
                var on = carSel === c2.c;
                var label = shipCarrierLabel(c2.c) || c2.c;
                return h('tr', {
                  key: c2.c, className: 'phov',
                  onClick: function() { setCarSel(on ? null : c2.c); },
                  title: on ? 'Click to clear this focus' : 'Focus the tiles and charts on ' + label,
                  style: { borderBottom: i < agg.carriers.length - 1 ? '1px solid ' + C.border : 'none', cursor: 'pointer',
                    background: on ? C.accentSoft : 'transparent', boxShadow: on ? 'inset 3px 0 0 ' + C.accent : 'none' }
                },
                  h('td', { style: { padding: '8px 14px', fontWeight: 500, color: on ? C.accent : C.text } }, label),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, c2.n.toLocaleString()),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, shipMoney(c2.lc)),
                  h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } }, c2.nc ? shipMoney(c2.lc / c2.nc) : '—'));
              }))))));
}

// ─── Insights: Regions page — four hover choropleths (US/CA/EU/AU) over the
// same /insights/volume shp rows. Map shapes ship in the bundle
// (SHIP_MAP_DATA from app-ship-maps.js, generated offline). ────────────────
function ShipRegionMap({ mapKey, title, byRegion, total, spendByRegion }) {
  var _h = useState(null); var hover = _h[0], setHover = _h[1]; // {code, x, y}
  var map = (window.SHIP_MAP_DATA || {})[mapKey];
  if (!map) return null;
  var codes = Object.keys(map.regions);
  var max = 0;
  codes.forEach(function(c2) { if ((byRegion[c2] || 0) > max) max = byRegion[c2] || 0; });
  // Sequential fill: single accent hue, sqrt scale (counts are heavy-tailed)
  var fillFor = function(n) {
    if (!n) return C.bg;
    var t = Math.sqrt(n / max);
    return 'rgba(79,110,247,' + (0.14 + 0.72 * t).toFixed(3) + ')';
  };
  var strokeW = map.w > 700 ? 1 : 0.75;
  var mapped = 0;
  codes.forEach(function(c2) { mapped += byRegion[c2] || 0; });
  var unmapped = total - mapped;
  var hv = hover && map.regions[hover.code] ? hover : null;
  var pathFor = function(c2, isTop) {
    var r2 = map.regions[c2];
    var n = byRegion[c2] || 0;
    return h('path', {
      key: c2 + (isTop ? '_top' : ''),
      d: r2.d,
      fill: fillFor(n),
      stroke: isTop ? C.accent : C.border,
      strokeWidth: isTop ? strokeW * 2 : strokeW,
      strokeLinejoin: 'round',
      // The highlight copy must be pointer-transparent — as a hit target it
      // would cover the base path and instantly mouseleave-null the hover.
      style: { cursor: n ? 'pointer' : 'default', transition: 'fill 0.15s', pointerEvents: isTop ? 'none' : undefined },
      onMouseMove: isTop ? undefined : function(e) {
        var box = e.currentTarget.ownerSVGElement.parentNode.getBoundingClientRect();
        setHover({ code: c2, x: e.clientX - box.left, y: e.clientY - box.top });
      },
      onMouseLeave: isTop ? undefined : function() { setHover(null); },
    });
  };
  return h(Card, { style: { padding: '14px 16px', flex: 1, minWidth: 400, maxWidth: 486, boxSizing: 'border-box' } },
    h('div', { style: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 } },
      h('div', { style: { fontSize: 13.5, fontWeight: 500, color: C.text, flex: 1 } }, title),
      h('div', { style: { fontSize: 11.5, color: C.textMuted } },
        total ? total.toLocaleString() + ' label' + (total === 1 ? '' : 's') : 'no shipments')),
    h('div', { style: { position: 'relative' } },
      h('svg', { viewBox: '0 0 ' + map.w + ' ' + map.h, style: { width: '100%', height: 'auto', display: 'block' } },
        codes.map(function(c2) { return pathFor(c2, false); }),
        hv && pathFor(hv.code, true)),
      hv && (function() {
        var r2 = map.regions[hv.code];
        var n = byRegion[hv.code] || 0;
        var sp = spendByRegion[hv.code] || 0;
        return h('div', {
          style: { position: 'absolute', left: Math.min(hv.x + 14, 300), top: Math.max(hv.y - 14, 0),
            background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 10px',
            fontSize: 11.5, color: C.text, boxShadow: '0 4px 14px rgba(0,0,0,0.25)', pointerEvents: 'none',
            whiteSpace: 'nowrap', zIndex: 5 }
        },
          h('div', { style: { fontWeight: 600 } }, r2.n),
          h('div', { style: { color: C.textMuted } },
            n ? n.toLocaleString() + ' label' + (n === 1 ? '' : 's') + (total ? ' · ' + Math.round((n / total) * 100) + '% of ' + mapKey.toUpperCase() + ' shipments' : '')
              : 'No shipments in this window'),
          n && sp ? h('div', { style: { color: C.textMuted } }, shipMoney(sp) + ' postage') : null);
      })()),
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 } },
      h('div', { style: { fontSize: 10, color: C.textMuted } }, '0'),
      h('div', { style: { flex: '0 0 90px', height: 8, borderRadius: 4, border: '1px solid ' + C.border,
        background: 'linear-gradient(to right, ' + C.bg + ', rgba(79,110,247,0.86))' } }),
      h('div', { style: { fontSize: 10, color: C.textMuted } }, max ? max.toLocaleString() : '—'),
      h('span', { style: { flex: 1 } }),
      unmapped > 0 && h('div', { style: { fontSize: 10, color: C.textDim } },
        '+' + unmapped.toLocaleString() + ' in unmapped regions')));
}

function ShipInsightsRegionsPage({ showToast }) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _w = useState(30); var days = _w[0], setDays = _w[1];
  var _c = useState(null); var carSel = _c[0], setCarSel = _c[1];
  useEffect(function() {
    var live = true; // review M7 #105: a slower earlier window must not overwrite the selected one
    setData(null);
    api('/shipping/insights/volume?days=' + days).then(function(r) { if (live) setData(r); })
      .catch(function(e) { if (!live) return; showToast(e.message, 'error'); setData({ ord: [], shp: [] }); });
    return function() { live = false; };
  }, [days]);

  var agg = useMemo(function() {
    var rows = (data && data.shp) || [];
    var carriers = [];
    rows.forEach(function(r2) { if (carriers.indexOf(r2.c) === -1) carriers.push(r2.c); });
    carriers.sort();
    if (carSel) rows = rows.filter(function(r2) { return r2.c === carSel; });
    var euCodes = (window.SHIP_MAP_DATA && window.SHIP_MAP_DATA.eu) ? window.SHIP_MAP_DATA.eu.regions : {};
    var mk = function() { return { by: {}, spend: {}, total: 0 }; };
    var maps = { us: mk(), ca: mk(), eu: mk(), au: mk() };
    var other = {}; var otherTotal = 0;
    rows.forEach(function(r2) {
      var co = r2.co === 'UK' ? 'GB' : r2.co;
      var m = null, key = null;
      if (co === 'US') { m = maps.us; key = r2.st; }
      else if (co === 'CA') { m = maps.ca; key = r2.st; }
      else if (co === 'AU') { m = maps.au; key = r2.st; }
      else if (euCodes[co]) { m = maps.eu; key = co; }
      if (m) {
        m.total += r2.n;
        if (key) {
          m.by[key] = (m.by[key] || 0) + r2.n;
          m.spend[key] = Math.round(((m.spend[key] || 0) + (r2.lc || 0)) * 100) / 100;
        }
      } else {
        other[co] = (other[co] || 0) + r2.n;
        otherTotal += r2.n;
      }
    });
    return { maps: maps, carriers: carriers,
      other: Object.keys(other).sort().map(function(co) { return { co: co, n: other[co] }; }),
      otherTotal: otherTotal };
  }, [data, carSel]);

  return h('div', { style: SHIP_INSIGHT_PAGE_STYLE },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' } },
      h('div', { style: { fontSize: 12.5, color: C.textMuted, flex: 1, minWidth: 220 } },
        'Where shipments go, by ship date. Hover a region for detail.'),
      agg.carriers.length > 1 && h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
        [null].concat(agg.carriers).map(function(c2) {
          var on = carSel === c2;
          return h('button', {
            key: c2 || 'all', onClick: function() { setCarSel(c2); },
            style: { background: on ? C.accent : 'transparent', color: on ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }
          }, c2 ? (shipCarrierLabel(c2) || c2) : 'All carriers');
        })),
      shipInsightWindowPills(days, setDays)),
    data === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Crunching shipment data…')
      : h('div', null,
          h('div', { style: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 } },
            h(ShipRegionMap, { mapKey: 'us', title: 'United States', byRegion: agg.maps.us.by, spendByRegion: agg.maps.us.spend, total: agg.maps.us.total }),
            h(ShipRegionMap, { mapKey: 'ca', title: 'Canada', byRegion: agg.maps.ca.by, spendByRegion: agg.maps.ca.spend, total: agg.maps.ca.total }),
            h(ShipRegionMap, { mapKey: 'eu', title: 'Europe', byRegion: agg.maps.eu.by, spendByRegion: agg.maps.eu.spend, total: agg.maps.eu.total }),
            h(ShipRegionMap, { mapKey: 'au', title: 'Australia', byRegion: agg.maps.au.by, spendByRegion: agg.maps.au.spend, total: agg.maps.au.total })),
          agg.other.length > 0 && h(Card, { style: { padding: '12px 16px', maxWidth: 480 } },
            h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, marginBottom: 6 } },
              'Other destinations · ' + agg.otherTotal.toLocaleString() + ' label' + (agg.otherTotal === 1 ? '' : 's')),
            h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6 } },
              agg.other.map(function(o2) {
                return h('span', { key: o2.co, style: { fontSize: 11.5, color: C.textMuted, background: C.bg, border: '1px solid ' + C.border, borderRadius: 999, padding: '2px 10px' } },
                  o2.co + ' · ' + o2.n.toLocaleString());
              })))));
}

// ─── Insights: Products page — SKU sales for the window (orders received,
// cancelled excluded; grouped rows from GET /insights/products) ────────────
function ShipInsightsProductsPage({ showToast }) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _w = useState(30); var days = _w[0], setDays = _w[1];
  var _c = useState([]); var chans = _c[0], setChans = _c[1]; // empty = all
  var _q = useState(''); var q = _q[0], setQ = _q[1];
  var _s = useState({ k: 'v', dir: -1 }); var sort = _s[0], setSort = _s[1];
  useEffect(function() {
    var live = true; // review M7 #105: a slower earlier window must not overwrite the selected one
    setData(null);
    api('/shipping/insights/products?days=' + days).then(function(r) { if (live) setData(r); })
      .catch(function(e) { if (!live) return; showToast(e.message, 'error'); setData({ rows: [], skus: {} }); });
    return function() { live = false; };
  }, [days]);

  var agg = useMemo(function() {
    var rows = (data && data.rows) || [];
    var skuMeta = (data && data.skus) || {};
    var channels = [];
    rows.forEach(function(r2) { if (channels.indexOf(r2.ch) === -1) channels.push(r2.ch); });
    channels.sort();
    if (chans.length) rows = rows.filter(function(r2) { return chans.indexOf(r2.ch) !== -1; });
    var bySku = {};
    var units = 0, rev = 0;
    rows.forEach(function(r2) {
      var b = bySku[r2.s] || (bySku[r2.s] = { s: r2.s, n: 0, v: 0, o: 0 });
      b.n += r2.n; b.v = Math.round((b.v + r2.v) * 100) / 100; b.o += r2.o;
      units += r2.n; rev += r2.v;
    });
    var list = Object.keys(bySku).map(function(s2) {
      var b = bySku[s2];
      var meta = skuMeta[s2] || {};
      return { s: s2, t: meta.t || s2, img: meta.i || null, n: b.n, v: b.v, o: b.o, avg: b.n ? b.v / b.n : 0 };
    });
    var needle = q.trim().toLowerCase();
    if (needle) list = list.filter(function(r2) {
      return r2.s.toLowerCase().indexOf(needle) !== -1 || r2.t.toLowerCase().indexOf(needle) !== -1;
    });
    list.sort(function(a, b) {
      var av = a[sort.k], bv = b[sort.k];
      if (av === bv) return a.s < b.s ? -1 : 1;
      return (av < bv ? -1 : 1) * sort.dir;
    });
    return { list: list, channels: channels, units: units, rev: Math.round(rev * 100) / 100, skuCount: Object.keys(bySku).length };
  }, [data, chans, q, sort]);

  var CAP = 100;
  var shown = agg.list.slice(0, CAP);
  var sortable = [['n', 'Units'], ['v', 'Revenue'], ['avg', 'Avg Price'], ['o', 'Orders']];
  var headTh = function(label, key, alignRight) {
    var on = sort.k === key;
    return h('th', {
      key: label,
      onClick: key ? function() { setSort(on ? { k: key, dir: -sort.dir } : { k: key, dir: -1 }); } : undefined,
      title: key ? 'Sort by ' + label : undefined,
      style: { padding: '8px 14px', textAlign: alignRight ? 'right' : 'left', fontSize: 10.5, fontWeight: 600,
        color: on ? C.accent : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em',
        cursor: key ? 'pointer' : 'default', position: 'sticky', top: 0, background: C.surface, whiteSpace: 'nowrap' }
    }, label + (on ? (sort.dir === -1 ? ' ↓' : ' ↑') : ''));
  };

  return h('div', { style: SHIP_INSIGHT_PAGE_STYLE },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' } },
      h('div', { style: { fontSize: 12.5, color: C.textMuted, flex: 1, minWidth: 200 } },
        'SKU sales for orders received in the window (item revenue, cancelled excluded).'),
      agg.channels.length > 1 && h(ReportParamSelect, { label: 'Channel', values: agg.channels, selected: chans, onChange: setChans }),
      shipInsightWindowPills(days, setDays)),
    data === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Crunching product sales…')
      : h('div', null,
          h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 } },
            shipInsightTile('Units Sold', agg.units.toLocaleString(),
              chans.length === 1 ? chans[0] : chans.length ? chans.length + ' channels' : shipInsightWinLabel(data.days || days)),
            shipInsightTile('Item Revenue', shipMoney(agg.rev), 'line prices × quantities'),
            shipInsightTile('Distinct SKUs', agg.skuCount.toLocaleString(), 'with at least one sale'),
            shipInsightTile('Avg Unit Price', agg.units ? shipMoney(agg.rev / agg.units) : '—', 'revenue ÷ units')),
          h(Card, { style: SHIP_INSIGHT_TABLE_CARD },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' } },
              h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, flex: 1 } }, 'By SKU'),
              h('div', { style: { position: 'relative' } },
                h('input', {
                  value: q, placeholder: 'Search SKU or title…',
                  onChange: function(e) { setQ(e.target.value); },
                  onKeyDown: function(e) { if (e.key === 'Escape') setQ(''); },
                  style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 26px 6px 10px', fontSize: 12, fontFamily: 'inherit', width: 240, boxSizing: 'border-box' }
                }),
                q && h('button', {
                  onClick: function() { setQ(''); }, title: 'Clear search',
                  style: { position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center' }
                }, h(LIcon, { name: 'x', size: 13 }))),
              h('div', { style: { fontSize: 11, color: C.textMuted } },
                agg.list.length > CAP
                  ? 'top ' + CAP + ' of ' + agg.list.length.toLocaleString() + ' — search to narrow'
                  : agg.list.length.toLocaleString() + ' SKU' + (agg.list.length === 1 ? '' : 's'))),
            h('div', { style: { maxHeight: 640, overflowY: 'auto' } },
              h('table', { style: { width: '100%', fontSize: 12.5, borderCollapse: 'collapse' } },
                h('thead', null, h('tr', { style: { borderBottom: '1px solid ' + C.border } },
                  headTh('Product', null, false),
                  sortable.map(function(col) { return headTh(col[1], col[0], true); }))),
                h('tbody', null, shown.map(function(r2, i) {
                  return h('tr', { key: r2.s, style: { borderBottom: i < shown.length - 1 ? '1px solid ' + C.border : 'none' } },
                    h('td', { style: { padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 } },
                      r2.img
                        ? h('img', { src: r2.img, loading: 'lazy', style: { width: 38, height: 38, objectFit: 'contain', borderRadius: 6, background: '#fff', flexShrink: 0 } })
                        : h('div', { style: { width: 38, height: 38, borderRadius: 6, background: C.bg, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
                            h(LIcon, { name: 'package', size: 15, color: C.textDim })),
                      h('div', { style: { minWidth: 0 } },
                        h('div', { style: { fontSize: 12.5, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 420 } }, r2.t),
                        h('div', { style: { fontSize: 11, color: C.textMuted } }, r2.s))),
                    h('td', { style: { padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 } }, r2.n.toLocaleString()),
                    h('td', { style: { padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, shipMoney(r2.v)),
                    h('td', { style: { padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } }, shipMoney(r2.avg)),
                    h('td', { style: { padding: '7px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.textMuted } }, r2.o.toLocaleString()));
                }))))),
          agg.list.length === 0 && h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted, fontSize: 12.5 } },
            q ? 'No SKUs match your search in this window.' : 'No sales in this window.')));
}

;
/* ===== app-shipping-products.js ===== */
// ─── Shipping app: product shipping settings ─── split 2026-09-19
// Only the fields the user changed go to the server (review 2026-09-28, medium #34): the form used
// to post every field, so a SKU whose stored customs value is 0 failed the customs check on every
// edit, even a weight change. Same rule the Shipments modal got on 9-28.
function shipProductChangedFields(form, original, keys) {
  var out = {};
  keys.forEach(function(k) {
    var was = original == null || original[k] == null ? '' : String(original[k]);
    var now = form[k] == null ? '' : String(form[k]);
    if (now !== was) out[k] = form[k];
  });
  return out;
}
// ─── Products tab: per-SKU shipping settings (writes the shared products
// table — the main app sees the same values) ───────────────────────────────
function ShipProductsTab({ showToast }) {
  var [rows, setRows] = useState(null);
  var [q, setQ] = useState('');
  var [qLive, setQLive] = useState('');
  var qSearchProps = useShipAutoSearch(qLive, setQLive, setQ); // search-as-you-type (9-18)
  var [edit, setEdit] = useState(null); // product row being edited
  var [form, setForm] = useState({});
  var [saving, setSaving] = useState(false);

  var FIELDS = [
    { key: 'package_weight', label: 'Package weight (lb)' },
    { key: 'package_length', label: 'Length (in)' },
    { key: 'package_width', label: 'Width (in)' },
    { key: 'package_height', label: 'Height (in)' },
    { key: 'hs_code', label: 'HS code' },
    { key: 'customs_description', label: 'Customs description (max 50)', maxLength: 50 },
    { key: 'country_of_origin', label: 'Country of origin' },
    { key: 'customs_value', label: 'Customs value ($)' },
  ];

  var [loadErr, setLoadErr] = useState(null);
  var load = useCallback(function() {
    api('/shipping/products-list?limit=200&q=' + encodeURIComponent(q))
      .then(function(r) { setLoadErr(null); setRows(r.products || []); })
      .catch(function(e) { setLoadErr(e.message || 'Could not load products'); showToast(e.message, 'error'); });
  }, [q]);
  useEffect(function() { load(); }, [load]);

  var openEdit = function(p) {
    var f = {};
    FIELDS.forEach(function(x) { f[x.key] = p[x.key] == null ? '' : String(p[x.key]); });
    setForm(f);
    setEdit(p);
  };
  var save = function() {
    var body = shipProductChangedFields(form, edit, FIELDS.map(function(x) { return x.key; }));
    if (!Object.keys(body).length) { setEdit(null); return; } // nothing changed
    setSaving(true);
    api('/shipping/products-list/' + edit.id, { method: 'POST', body: JSON.stringify(body) })
      .then(function() { showToast(edit.sku + ' updated', 'success'); setEdit(null); load(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  var dim = function(p) {
    var L = [p.package_length, p.package_width, p.package_height].map(function(v) { return v == null ? '' : String(v); });
    return L.some(Boolean) ? L.map(function(v) { return v || '?'; }).join('×') : '—';
  };
  var cols = [
    { key: 'sku', label: 'SKU', sortKey: 'sku', render: function(p) { return h('span', { style: { fontSize: 11, fontWeight: 500, color: C.accent } }, p.sku); } },
    { key: 'title', label: 'Title', render: function(p) {
        return h('span', { style: { maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom' }, title: p.title || '' }, p.title || '—');
      } },
    { key: 'package_weight', label: 'Pkg Weight', sortKey: 'package_weight', render: function(p) { return p.package_weight ? p.package_weight + ' lb' : h('span', { style: { color: C.warning } }, '—'); } },
    { key: 'dims', label: 'Pkg Size (in)', render: function(p) { return h('span', { style: { fontSize: 11 } }, dim(p)); } },
    { key: 'hs_code', label: 'HS Code', render: function(p) { return p.hs_code || h('span', { style: { color: C.textDim } }, '—'); } },
    { key: 'country_of_origin', label: 'Origin', render: function(p) { return p.country_of_origin || h('span', { style: { color: C.textDim } }, '—'); } },
    { key: 'customs_value', label: 'Customs $', render: function(p) { return p.customs_value != null && p.customs_value !== '' ? shipMoney(p.customs_value) : h('span', { style: { color: C.textDim } }, '—'); } },
  ];

  var inpS = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  return h('div', null,
    h('div', { style: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 } },
      h('input', Object.assign({
        value: qLive, placeholder: 'Search SKU or title…',
        style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', width: 280 }
      }, qSearchProps)),
      h(Btn, { size: 'sm', onClick: function() { setQ(qLive.trim()); } }, 'Search'),
      h('div', { style: { marginLeft: 'auto', fontSize: 12, color: C.textMuted } },
        'Shipping fields save to the shared catalog — the main app sees the same values.')),
    rows === null && loadErr
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.danger } }, 'Could not load products: ' + loadErr + ' ', h(Btn, { size: 'sm', onClick: load }, 'Retry'))
      : rows === null
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading products…')
      : h(DataTable, { columns: cols, data: rows, storageKey: 'ship-products', defaultSort: 'sku', onRowClick: openEdit, emptyTitle: 'No products', emptySub: 'Try a different search' }),
    edit && h(Modal, { open: true, onClose: function() { setEdit(null); }, title: edit.sku, width: 460 },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 2 } }, edit.title || ''),
        h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
          FIELDS.map(function(x) {
            return h('div', { key: x.key, style: x.key === 'customs_description' ? { gridColumn: '1 / -1' } : null },
              h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 } }, x.label + (x.maxLength ? ' · ' + String(form[x.key] || '').length + '/' + x.maxLength : '')),
              h('input', { value: form[x.key], maxLength: x.maxLength || undefined, onChange: function(e) { var n = {}; n[x.key] = e.target.value; setForm(Object.assign({}, form, n)); }, style: inpS }));
          })),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 } },
          h(Btn, { onClick: function() { setEdit(null); } }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: save, disabled: saving }, saving ? 'Saving…' : 'Save'))))
  );
}

window.ShippingPage = ShippingPage;

;
/* ===== app-shipping-rate-browser.js ===== */
// ─── Rate Browser (Anthony 2026-09-19): quotes without an order ───
// ShipStation parity: pick a ship-from, type a destination, a weight and a box, get every
// enabled service's price side by side with its transit estimate. Runs the same quote path as an
// order (POST /shipping/rate-browser — quoteShipment), nothing is cached or saved.
// First page to live in its own file; the shipping client is being split by page (9-19).
// 2026-10-29 (Anthony: "update the rate browser to show from and to … polish it … submit a custom
// from … a tick box for residential"): Ship from and Ship to are two panels, the origin can be a
// saved address or a typed one (used for this quote only), the destination carries a Residential
// box (UPS prices its residential surcharge from it; USPS and DHL ignore it), and UPS rates say
// whether the account's negotiated price applied or the published one came back.
var SHIP_RB_EMPTY = {
  ship_from_id: '', from_country: 'US', from_state: '', from_city: '', from_zip: '', from_address1: '',
  country: 'US', state: '', city: '', zip: '', address1: '', residential: false,
  lb: '1', oz: '0', length: '', width: '', height: '', template: '', carrier: '',
};
function ShipRateBrowserPage({ showToast }) {
  var _sf = useState(null); var shipFroms = _sf[0], setShipFroms = _sf[1];
  var _pt = useState(null); var pkgTpls = _pt[0], setPkgTpls = _pt[1];
  var _f = useState(Object.assign({}, SHIP_RB_EMPTY));
  var form = _f[0], setForm = _f[1];
  var _r = useState(null); var result = _r[0], setResult = _r[1]; // null | { loading } | response
  var set = function(k, v) { setForm(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); };
  useEffect(function() {
    shipFetchList('/shipping/ship-from', 'addresses').then(function(a) {
      setShipFroms(a || []);
      var dflt = (a || []).find(function(x) { return x.is_default; }) || (a || [])[0];
      if (dflt) setForm(function(prev) { return prev.ship_from_id ? prev : Object.assign({}, prev, { ship_from_id: String(dflt.id) }); });
    });
    shipFetchList('/shipping/package-templates', 'templates').then(function(t) { setPkgTpls(t || []); });
  }, []);
  var pickTemplate = function(id) {
    var t = (pkgTpls || []).find(function(x) { return String(x.id) === String(id); });
    setForm(function(prev) {
      return Object.assign({}, prev, { template: id }, t ? { length: t.length_in || '', width: t.width_in || '', height: t.height_in || '' } : {});
    });
  };
  var DOMESTIC = ['US', 'PR', 'VI', 'GU', 'AS', 'MP'];
  var domestic = DOMESTIC.indexOf(String(form.country || 'US').toUpperCase()) !== -1;
  var customFrom = form.ship_from_id === 'custom';
  var fromDomestic = DOMESTIC.indexOf(String(form.from_country || 'US').toUpperCase()) !== -1;
  var fromOk = !customFrom || (String(form.from_country || '').trim() && (!fromDomestic || /^\d{5}/.test(String(form.from_zip || '').trim())));
  var weightLb = (parseFloat(form.lb) || 0) + (parseFloat(form.oz) || 0) / 16;
  var canQuote = weightLb > 0 && String(form.country || '').trim() && (!domestic || /^\d{5}/.test(String(form.zip || '').trim())) && fromOk;
  var getRates = function() {
    if (!canQuote) return;
    setResult({ loading: true });
    api('/shipping/rate-browser', { method: 'POST', body: JSON.stringify({
      ship_from_id: customFrom ? 'custom' : (form.ship_from_id || null),
      from: customFrom ? { country: form.from_country, state: form.from_state, city: form.from_city, zip: form.from_zip, address1: form.from_address1 } : null,
      dest: { country: form.country, state: form.state, city: form.city, zip: form.zip, address1: form.address1, residential: !!form.residential },
      weight_lb: weightLb,
      length: form.length || null, width: form.width || null, height: form.height || null,
      carrier: form.carrier || null,
    }) })
      .then(function(r) { setResult(r || { rates: [] }); })
      .catch(function(e) { showToast(e.message, 'error'); setResult({ rates: [], rate_error: e.message }); });
  };
  var onKey = function(e) { if (e.key === 'Enter') getRates(); };
  var lbl = { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };
  var inp = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 9px', fontSize: 12.5, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  var field = function(label, node, width) { return h('div', { style: { flex: width ? '0 0 ' + width + 'px' : '1 1 120px', minWidth: 0 } }, h('div', { style: lbl }, label), node); };
  var row = function(children, extra) { return h('div', { style: Object.assign({ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }, extra || {}) }, children); };
  var text = function(k, ph, extra) { return h('input', Object.assign({ value: form[k], placeholder: ph || '', onChange: function(e) { set(k, e.target.value); }, onKeyDown: onKey, style: inp }, extra || {})); };
  var panel = function(title, sub, children) {
    return h('div', { style: { flex: '1 1 340px', minWidth: 0, border: '1px solid ' + C.border, borderRadius: 8, padding: '12px 14px 14px', background: C.bg } },
      h('div', { style: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 } },
        h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text } }, title),
        sub && h('div', { style: { fontSize: 11, color: C.textDim } }, sub)),
      children);
  };
  // ── Ship from: a saved address, or a typed origin used for this quote only ──
  var fromPanel = panel('Ship from', customFrom ? 'typed origin — used for this quote only' : 'saved address (Settings › General)',
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
      h('select', { value: form.ship_from_id, onChange: function(e) { set('ship_from_id', e.target.value); }, style: inp },
        (shipFroms || []).map(function(a) { return h('option', { key: a.id, value: String(a.id) }, a.name + (a.zip ? ' · ' + a.zip : '') + (a.is_default ? ' (default)' : '')); }),
        h('option', { value: 'custom' }, 'Custom address…')),
      customFrom && row([
        field('Country', text('from_country', 'US', { maxLength: 2, style: Object.assign({}, inp, { textTransform: 'uppercase' }) }), 70),
        field('State', h(StateSelect, { value: form.from_state, country: form.from_country, onChange: function(e) { set('from_state', e.target.value); }, style: inp, placeholder: fromDomestic ? '— State —' : 'province' })),
      ]),
      customFrom && row([
        field('City', text('from_city', '')),
        field(fromDomestic ? 'ZIP' : 'Postal code', text('from_zip', ''), 110),
      ]),
      customFrom && row([field('Street (optional)', text('from_address1', ''))]),
      !customFrom && (function() {
        var a = (shipFroms || []).find(function(x) { return String(x.id) === String(form.ship_from_id); });
        return a ? h('div', { style: { fontSize: 11.5, color: C.textMuted, lineHeight: 1.5 } },
          [a.company || a.name, a.street1, [a.city, a.state, a.zip].filter(Boolean).join(', ')].filter(Boolean).join(' · ')) : null;
      })()));
  // ── Ship to: the destination, with the residential box UPS prices from ──
  var toPanel = panel('Ship to', 'destination',
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
      row([
        field('Country', text('country', 'US', { maxLength: 2, style: Object.assign({}, inp, { textTransform: 'uppercase' }) }), 70),
        field('State', h(StateSelect, { value: form.state, country: form.country, onChange: function(e) { set('state', e.target.value); }, style: inp, placeholder: domestic ? '— State —' : 'province' })),
      ]),
      row([
        field('City', text('city', '')),
        field(domestic ? 'ZIP' : 'Postal code', text('zip', ''), 110),
      ]),
      row([field('Street (optional, helps DHL)', text('address1', ''))]),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, color: C.text, marginTop: 2 } },
        h(ShipCheckbox, { checked: !!form.residential, onChange: function() { set('residential', !form.residential); }, title: 'Residential delivery' }),
        h('span', null, 'Residential address'),
        h('span', { style: { fontSize: 11, color: C.textDim } }, 'UPS adds its residential surcharge; USPS and DHL price the same either way'))));
  var transitOf = function(r) {
    var t = r && r.transit;
    if (t && t.source === 'usps_standard') return t.label + (t.cutoff ? ' · ship by ' + t.cutoff : '');
    if (t && t.label) return t.label;
    return '—';
  };
  // UPS: did the account's negotiated price apply, or did UPS answer with the published one?
  var priceTag = function(r) {
    if (r.carrier !== 'ups') return null;
    var neg = r.negotiated === true && r.published_cost != null && parseFloat(r.published_cost) > parseFloat(r.cost);
    return h('span', { title: neg ? 'Negotiated rate (published ' + shipMoney(r.published_cost) + ')' : 'Published rate — the account\'s negotiated price did not apply',
      style: { marginLeft: 8, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4,
        background: neg ? C.successSoft : (C.warningSoft || C.accentSoft), color: neg ? C.success : C.warning } }, neg ? 'negotiated' : 'published');
  };
  var rates = (result && result.rates) || [];
  var toLabel = result && result.to ? [result.to.city, result.to.state].filter(Boolean).join(', ') + (result.to.zip ? ' ' + result.to.zip : '') + (result.to.country && result.to.country !== 'US' ? ' · ' + result.to.country : '') : '';
  return h('div', { style: { maxWidth: 980, margin: '0 auto' } },
    h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 14 } },
      'Quote a parcel without an order: every enabled USPS, DHL eCommerce and UPS service for the lane, cheapest first, with its transit estimate. Same rates the order panel gets; nothing is saved.'),
    h(Card, { style: { padding: 16, marginBottom: 16 } },
      h('div', { style: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' } }, fromPanel, toPanel),
      h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, margin: '16px 0 8px' } }, 'Parcel'),
      row([
        field('Weight', h('div', { style: { display: 'flex', gap: 6, alignItems: 'center' } },
          h('input', { value: form.lb, onChange: function(e) { set('lb', e.target.value); }, onKeyDown: onKey, style: Object.assign({}, inp, { width: 64 }) }), h('span', { style: { fontSize: 11, color: C.textDim } }, 'lb'),
          h('input', { value: form.oz, onChange: function(e) { set('oz', e.target.value); }, onKeyDown: onKey, style: Object.assign({}, inp, { width: 64 }) }), h('span', { style: { fontSize: 11, color: C.textDim } }, 'oz')), 200),
        field('Package', h('select', { value: form.template, onChange: function(e) { pickTemplate(e.target.value); }, style: inp },
          h('option', { value: '' }, 'Custom Size…'),
          (pkgTpls || []).map(function(t) { return h('option', { key: t.id, value: String(t.id) }, t.name + (t.length_in ? ' · ' + t.length_in + '×' + t.width_in + '×' + t.height_in : '')); })), 220),
        field('L × W × H (in)', h('div', { style: { display: 'flex', gap: 6, alignItems: 'center' } },
          ['length', 'width', 'height'].map(function(k, i) {
            return h(React.Fragment, { key: k }, i ? h('span', { style: { color: C.textDim } }, '×') : null,
              h('input', { value: form[k], onChange: function(e) { set(k, e.target.value); set('template', ''); }, onKeyDown: onKey, placeholder: k[0].toUpperCase(), style: Object.assign({}, inp, { width: 58 }) }));
          })), 230),
        field('Carrier', h('select', { value: form.carrier, onChange: function(e) { set('carrier', e.target.value); }, style: inp },
          h('option', { value: '' }, 'All Carriers'),
          h('option', { value: 'usps' }, 'USPS'),
          h('option', { value: 'dhl_global_mail' }, 'DHL eCommerce'),
          h('option', { value: 'ups' }, 'UPS')), 150),
        h('div', { style: { flex: '0 0 auto' } },
          h(Btn, { variant: 'primary', onClick: getRates, disabled: !canQuote || !!(result && result.loading) }, result && result.loading ? 'Quoting…' : 'Get Rates')),
      ], { marginTop: 0 }),
      !canQuote && h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 8 } },
        !fromOk ? 'The custom ship-from needs a country and, for the US, a 5-digit ZIP.' : domestic ? 'A weight and a 5-digit destination ZIP are needed.' : 'A weight and a destination country are needed.'),
      domestic && !(form.length && form.width && form.height) && h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4 } }, 'USPS and UPS prices need L × W × H; DHL quotes without them.')),
    result && !result.loading && h(Card, { style: { padding: 0, overflow: 'hidden' } },
      h('div', { style: { display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 14px 0' } },
        h('div', { style: { fontSize: 12.5, fontWeight: 600, color: C.text, flex: 1 } },
          (rates.length ? rates.length + ' rate' + (rates.length === 1 ? '' : 's') : 'No rates')
          + ' · ' + (result.from_zip || '?') + (result.from && result.from.custom ? ' (custom)' : '') + ' → ' + (toLabel || '?')
          + (result.to && result.to.residential ? ' · residential' : '')),
        result.rate_error && h('div', { style: { fontSize: 11.5, color: C.danger } }, result.rate_error)),
      rates.length
        ? h('table', { style: { width: '100%', fontSize: 12.5, borderCollapse: 'collapse', marginTop: 6 } },
            h('thead', null, h('tr', { style: { borderBottom: '1px solid ' + C.border } },
              ['Carrier', 'Service', 'Rate', 'Est. transit'].map(function(hd, i) {
                return h('th', { key: hd, style: { padding: '8px 14px', textAlign: i === 2 ? 'right' : 'left', fontSize: 10.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, hd);
              }))),
            h('tbody', null, rates.map(function(r, i) {
              return h('tr', { key: r.carrier + '|' + r.service_code + '|' + i, style: { borderBottom: i < rates.length - 1 ? '1px solid ' + C.border : 'none', background: i === 0 ? C.accentSoft : 'transparent' } },
                h('td', { style: { padding: '8px 14px', fontWeight: 500, color: C.text } }, shipCarrierLabel(r.carrier) || r.carrier),
                h('td', { style: { padding: '8px 14px', color: C.text } }, r.service + (r.rate_indicator === 'CP' ? ' (cubic)' : ''), priceTag(r)),
                h('td', { style: { padding: '8px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: i === 0 ? 700 : 500, color: i === 0 ? C.accent : C.text } }, shipMoney(r.cost)),
                h('td', { style: { padding: '8px 14px', color: C.textMuted, fontSize: 12 } }, transitOf(r)));
            })))
        : h('div', { style: { padding: '10px 14px 14px', fontSize: 12, color: C.textMuted } }, result.note || 'Nothing quoted for this lane.')));
}
window.ShipRateBrowserPage = ShipRateBrowserPage;

;
/* ===== app-shipping-scan.js ===== */
// ─── Scan Station (Anthony 2026-09-23, to-do 9) ───
// Warehouse staff scan the order-number barcode on a packing slip and report that the order did
// NOT go out, with a reason. A report is a record only: the order is not cancelled, held or
// tagged (see routes/shipping/30-scan-station.js). Scanning works from anywhere on the page —
// a printable keystroke with the focus elsewhere lands in the scan box (the FBA transparency
// station's pattern). The daily digest to subscribed users comes from the same summary.
var SHIP_SCAN_REASONS = [
  { key: 'out_of_stock', label: 'Out of Stock', icon: 'package-x', color: '#F59E0B' },
  { key: 'oversold', label: 'Oversold', icon: 'trending-down', color: '#EF4444' },
  { key: 'damaged', label: 'Damaged', icon: 'alert-triangle', color: '#A855F7' },
  { key: 'other', label: 'Other', icon: 'help-circle', color: '#64748B' },
];
function shipScanReasonLabel(key) {
  var r = SHIP_SCAN_REASONS.find(function(x) { return x.key === key; });
  return r ? r.label : key;
}
// A short tone per outcome (sweep 2026-09-27 M18): staff scan without looking at the screen — a
// high beep = found / recorded, a middle one = needs a look, a long low one = not found / failed.
function shipScanBeep(kind) {
  try {
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    var ac = shipScanBeep._ac || (shipScanBeep._ac = new AC());
    if (ac.state === 'suspended' && ac.resume) ac.resume();
    var o = ac.createOscillator(), g = ac.createGain();
    o.frequency.value = kind === 'ok' ? 880 : kind === 'warn' ? 520 : 220;
    g.gain.value = 0.08;
    o.connect(g); g.connect(ac.destination);
    o.start();
    o.stop(ac.currentTime + (kind === 'error' ? 0.35 : 0.12));
  } catch (e) { /* no audio: the banner still says it */ }
}

function ShipScanStationPage({ showToast }) {
  var inputRef = useRef(null);
  var _v = useState(''); var val = _v[0], setVal = _v[1];
  var _b = useState(false); var busy = _b[0], setBusy = _b[1];
  var _o = useState(null); var order = _o[0], setOrder = _o[1];      // the scanned order (lookup result)
  var _m = useState(null); var msg = _m[0], setMsg = _m[1];          // { type: 'ok' | 'error' | 'warn', text }
  var _n = useState(''); var note = _n[0], setNote = _n[1];
  var _r = useState(null); var recent = _r[0], setRecent = _r[1];    // last reports, newest first
  var _s = useState(null); var summary = _s[0], setSummary = _s[1];  // the 30/60/90/180/365-day rollup
  var focus = function() { setTimeout(function() { if (inputRef.current) inputRef.current.focus(); }, 60); };
  // a failed load says so instead of "Loading…" forever (9-27 Lows)
  var loadRecent = function() { api('/shipping/scan/recent?limit=10').then(function(r) { setRecent(r.reports || []); }).catch(function(e) { setRecent({ error: (e && e.message) || 'failed' }); }); };
  var loadSummary = function() { api('/shipping/scan/summary').then(function(r) { setSummary(r.windows || []); }).catch(function() {}); };
  useEffect(function() { loadRecent(); loadSummary(); focus(); }, []);
  // scan-anywhere: a scanner types fast and ends with Enter; if the focus wandered, pull it back
  useEffect(function() {
    var onKey = function(e) {
      var t = e.target, tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && inputRef.current) inputRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, []);

  // term: an explicit order number (undo re-reads the open order, review fix 8 — the input is
  // cleared after every lookup, so reading `val` there looked up nothing); else the typed text
  // Scans never drop (sweep 2026-09-27 M18): the box stays enabled; a scan that arrives while a
  // lookup runs, or while an order waits for its reason, is queued and opens next — the old box
  // was disabled while busy and the scanner's keystrokes went nowhere.
  var queueRef = useRef([]);
  var busyRef = useRef(false);
  var orderRef = useRef(null);
  orderRef.current = order;
  var _q = useState(0); var queued = _q[0], setQueued = _q[1];
  var say = function(m) { setMsg(m); if (m) shipScanBeep(m.type); };
  // the next queued scan, once nothing is open
  var drain = function() {
    if (busyRef.current || orderRef.current) return;
    var next = queueRef.current.shift();
    setQueued(queueRef.current.length);
    if (next) lookup(next);
  };
  var lookup = function(term) {
    var fromBox = typeof term !== 'string';
    var q = String(fromBox ? val : term).trim();
    if (fromBox) setVal('');
    if (!q) return;
    if (busyRef.current || (fromBox && orderRef.current)) { queueRef.current.push(q); setQueued(queueRef.current.length); shipScanBeep('warn'); return; }
    busyRef.current = true; setBusy(true); setMsg(null);
    api('/shipping/scan/lookup?q=' + encodeURIComponent(q))
      .then(function(r) {
        if (!r.found) { setOrder(null); orderRef.current = null; say({ type: 'error', text: 'No order matches "' + q + '"' }); return; }
        setOrder(r.order); orderRef.current = r.order; setNote('');
        if (r.order.already_reported) {
          var last = r.order.reports[0];
          say({ type: 'warn', text: 'Order ' + r.order.order_number + ' was already reported' + (last.reported_by_name ? ' by ' + last.reported_by_name : '') + ': ' + shipScanReasonLabel(last.reason) + '. Undo it below to report it again.' });
        } else if (r.order.label) {
          say({ type: 'warn', text: 'Order ' + r.order.order_number + ' already has a ' + String(r.order.label.carrier || '').toUpperCase() + ' label (' + (r.order.label.tracking_number || '') + ') — reporting it will not void the label.' });
        } else shipScanBeep('ok');
      })
      .catch(function(e) { setOrder(null); orderRef.current = null; say({ type: 'error', text: e.message }); })
      .then(function() { busyRef.current = false; setBusy(false); focus(); drain(); });
  };
  var closeOrder = function() { setOrder(null); orderRef.current = null; setMsg(null); focus(); setTimeout(drain, 0); };
  var report = function(reason) {
    if (!order || busyRef.current) return;
    busyRef.current = true; setBusy(true);
    api('/shipping/scan/report', { method: 'POST', body: JSON.stringify({ order_id: order.id, reason: reason, note: note.trim() || null }) })
      .then(function(r) {
        say({ type: 'ok', text: 'Order ' + order.order_number + ' reported: ' + (r.reason_label || shipScanReasonLabel(reason)) + (order.paid_usd != null ? ' · ' + shipMoney(order.paid_usd, 'USD') + ' paid' : '') });
        setOrder(null); orderRef.current = null; setNote('');
        loadRecent(); loadSummary();
      })
      .catch(function(e) { say({ type: 'error', text: e.message }); })
      .then(function() { busyRef.current = false; setBusy(false); focus(); drain(); });
  };
  var undo = async function(rep) {
    if (!(await appConfirm('Undo the report on order ' + rep.order_number + ' (' + shipScanReasonLabel(rep.reason) + ')?', { danger: true, okLabel: 'Undo' }))) { focus(); return; }
    api('/shipping/scan/report/' + rep.id, { method: 'DELETE' })
      .then(function() { showToast('Report on ' + rep.order_number + ' undone'); loadRecent(); loadSummary(); if (order && order.id === rep.zentail_order_pk) lookup(order.order_number); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .then(focus);
  };

  // read out by screen readers as it changes (errors interrupt)
  var banner = msg && h('div', {
    role: msg.type === 'error' ? 'alert' : 'status', 'aria-live': msg.type === 'error' ? 'assertive' : 'polite',
    style: { marginTop: 12, padding: '12px 16px', borderRadius: 8, fontSize: 15, fontWeight: 600,
      background: (msg.type === 'ok' ? C.success : msg.type === 'warn' ? C.warning : C.danger) + '22',
      color: msg.type === 'ok' ? C.success : msg.type === 'warn' ? C.warning : C.danger, display: 'flex', alignItems: 'center', gap: 8 }
  }, h(LIcon, { name: msg.type === 'ok' ? 'check-circle' : msg.type === 'warn' ? 'alert-triangle' : 'x-circle', size: 18 }), msg.text);

  var orderCard = order && h(Card, { style: { padding: '16px 18px', marginTop: 14 } },
    h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' } },
      h('div', { style: { flex: 1, minWidth: 240 } },
        h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Order'),
        h('div', { style: { fontSize: 22, fontWeight: 600, color: C.text } }, order.order_number),
        h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 2 } },
          [order.channel, order.channel_order_id, order.customer].filter(Boolean).join(' · '),
          order.order_date ? ' · ' + new Date(order.order_date).toLocaleDateString() : ''),
        (order.lines || []).length > 0 && h('div', { style: { marginTop: 8, fontSize: 12.5, color: C.text } },
          order.lines.slice(0, 6).map(function(l, i) { return h('div', { key: i }, (l.qty || 1) + ' × ' + (l.sku || '') + (l.title ? ' — ' + String(l.title).slice(0, 60) : '')); }),
          order.lines.length > 6 && h('div', { style: { color: C.textDim } }, '+ ' + (order.lines.length - 6) + ' more'))),
      h('div', { style: { textAlign: 'right' } },
        h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Paid'),
        h('div', { style: { fontSize: 22, fontWeight: 600, color: C.text } }, order.paid_usd != null ? shipMoney(order.paid_usd, 'USD') : '—'),
        h('div', { style: { fontSize: 12, color: C.textMuted } }, order.status || ''))),
    h('div', { style: { fontSize: 12.5, color: C.textMuted, margin: '14px 0 8px' } }, 'Why did this order not go out?'),
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 } },
      SHIP_SCAN_REASONS.map(function(r) {
        return h('button', {
          key: r.key, onClick: function() { report(r.key); }, disabled: busy || order.already_reported,
          style: { padding: '18px 12px', borderRadius: 10, border: '1px solid ' + r.color, background: r.color + '18', color: C.text, fontSize: 15, fontWeight: 600, cursor: (busy || order.already_reported) ? 'default' : 'pointer', opacity: (busy || order.already_reported) ? 0.55 : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontFamily: 'inherit' }
        }, h(LIcon, { name: r.icon, size: 22, color: r.color }), r.label);
      })),
    h('div', { style: { marginTop: 10 } },
      h('input', { value: note, placeholder: 'Note (optional) — e.g. which SKU was short', onChange: function(e) { setNote(e.target.value); },
        onKeyDown: function(e) { if (e.key === 'Escape') closeOrder(); },
        style: { width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit' } })),
    h('div', { style: { marginTop: 8, display: 'flex', justifyContent: 'flex-end' } },
      h(Btn, { size: 'sm', variant: 'ghost', onClick: closeOrder }, 'Cancel')));

  var recentList = h(Card, { style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
    h('div', { style: { padding: '10px 16px', borderBottom: '1px solid ' + C.border, fontSize: 12, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Last Reports'),
    recent === null ? h('div', { style: { padding: 16, color: C.textMuted, fontSize: 13 } }, 'Loading…')
      : recent && recent.error ? h('div', { style: { padding: 16, color: C.danger, fontSize: 13 } }, 'Could not load the last reports: ' + recent.error)
      : !recent.length ? h('div', { style: { padding: 16, color: C.textDim, fontSize: 13 } }, 'Nothing reported yet')
      : recent.map(function(r, i) {
          return h('div', { key: r.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderTop: i ? '1px solid ' + C.border : 'none', fontSize: 13 } },
            h('span', { style: { fontWeight: 600, color: C.text, minWidth: 90 } }, r.order_number),
            h('span', { style: { color: C.text, minWidth: 100 } }, shipScanReasonLabel(r.reason)),
            h('span', { style: { color: C.textMuted, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
              [r.customer, r.note].filter(Boolean).join(' · ')),
            h('span', { style: { color: C.textMuted, whiteSpace: 'nowrap' } }, r.paid_usd != null ? shipMoney(r.paid_usd, 'USD') : ''),
            h('span', { style: { color: C.textDim, whiteSpace: 'nowrap' } }, (r.reported_by_name || '') + ' · ' + new Date(r.reported_at).toLocaleString()),
            h('button', { onClick: function() { undo(r); }, title: 'Undo this report', style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 } }, h(LIcon, { name: 'rotate-ccw', size: 12 }), 'Undo'));
        }));

  var summaryCard = h(Card, { style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
    h('div', { style: { padding: '10px 16px', borderBottom: '1px solid ' + C.border, fontSize: 12, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Not Shipped'),
    h('div', { style: { display: 'grid', gridTemplateColumns: '90px 80px 110px 1fr', gap: 8, padding: '6px 16px', fontSize: 11, color: C.textMuted, borderBottom: '1px solid ' + C.border } },
      h('span', null, 'Window'), h('span', { style: { textAlign: 'right' } }, 'Orders'), h('span', { style: { textAlign: 'right' } }, 'Paid Value'), h('span', null, 'Reasons')),
    (summary || []).map(function(w) {
      return h('div', { key: w.days, style: { display: 'grid', gridTemplateColumns: '90px 80px 110px 1fr', gap: 8, padding: '7px 16px', fontSize: 13, borderBottom: '1px solid ' + C.border } },
        h('span', { style: { color: C.text } }, w.days + ' days'),
        h('span', { style: { textAlign: 'right', color: C.text, fontWeight: 600 } }, String(w.orders)),
        h('span', { style: { textAlign: 'right', color: C.text } }, shipMoney(w.paid_usd, 'USD')),
        h('span', { style: { color: C.textMuted } }, w.by_reason.length ? w.by_reason.map(function(x) { return x.label + ' ' + x.pct + '%'; }).join(' · ') : '—'));
    }),
    h('div', { style: { padding: '8px 16px', fontSize: 11.5, color: C.textDim } }, 'The same numbers go out daily at 7:00 AM ET to everyone subscribed to "Shipping: Orders Not Shipped" under Settings › Users.'));

  return h('div', { style: { maxWidth: 860 } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 } },
      h(LIcon, { name: 'scan-line', size: 20, color: C.accent }),
      h('div', { style: { fontSize: 18, fontWeight: 600, color: C.text } }, 'Scan Station')),
    h('div', { style: { fontSize: 13, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
      'Scan the barcode on a packing slip (or type the order number) for an order that did not go out, then pick the reason. Nothing changes on the order itself; it is recorded and reported.'),
    h('input', {
      // never disabled: a scan during a lookup is queued, not lost (9-27 M18)
      ref: inputRef, value: val, placeholder: 'Scan or type an order number, then Enter', 'aria-label': 'Order number',
      onChange: function(e) { setVal(e.target.value); },
      onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); lookup(); } },
      style: { width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: '2px solid ' + C.accent, borderRadius: 10, padding: '15px 16px', fontSize: 18, fontFamily: 'inherit', outline: 'none' }
    }),
    (busy || queued > 0) && h('div', { role: 'status', 'aria-live': 'polite', style: { marginTop: 6, fontSize: 12, color: C.textMuted } },
      (busy ? 'Looking up…' : '') + (queued > 0 ? (busy ? ' · ' : '') + queued + ' scan' + (queued === 1 ? '' : 's') + ' waiting — they open one at a time' : '')),
    banner,
    orderCard,
    recentList,
    summaryCard);
}
window.ShipScanStationPage = ShipScanStationPage;

;
/* ===== app-shipping-auto-batch.js ===== */
// ─── Auto-batching (Anthony 2026-09-23, to-do 10) ───
// Settings › Auto-batching: definitions that put unbatched ready orders into batches on a schedule
// — conditions in the automation rules' vocabulary, times of day or every N minutes, a size, an
// action (create / create and buy labels), a note template and an assignee. A master switch and
// every definition start OFF. The sweep runs on the server every five minutes
// (routes/shipping/31-auto-batch.js); Preview shows what a definition would do without doing it.
var SHIP_AB_TZ = { 'America/New_York': 'Eastern', 'America/Chicago': 'Central', 'America/Denver': 'Mountain', 'America/Los_Angeles': 'Pacific' };
// the times a definition can run at: the day in half-hour steps (Anthony 2026-09-23: "can we make the times drop down?")
// The editor's "Then" list before the server's own list arrives (or when it sends none)
var SHIP_AB_DEFAULT_ACTIONS = { create: 'Create the batch' };
var SHIP_AB_TIME_SLOTS =(function() { var out = []; for (var hh = 0; hh < 24; hh++) { out.push((hh < 10 ? '0' : '') + hh + ':00'); out.push((hh < 10 ? '0' : '') + hh + ':30'); } return out; })();

function ShipAutoBatchEditor({ def, fields, users, actions, timezones, maxLabelOrders, savedFilters, onClose, onSaved, showToast }) {
  var isEdit = def && def.id;
  var _n = useState(def ? def.name : ''); var name = _n[0], setName = _n[1];
  var _e = useState(def ? def.enabled === true : false); var enabled = _e[0], setEnabled = _e[1];
  var _c = useState(def && def.conditions ? def.conditions.slice() : []); var conds = _c[0], setConds = _c[1];
  var _mm = useState((def && def.match_mode) === 'any' ? 'any' : 'all'); var matchMode = _mm[0], setMatchMode = _mm[1];
  var _sm = useState(def && def.every_minutes ? 'every' : 'times'); var schedMode = _sm[0], setSchedMode = _sm[1];
  var _t = useState(def && def.times ? def.times.slice() : []); var times = _t[0], setTimes = _t[1]; // ['10:00', '15:00']
  var _ev = useState(def && def.every_minutes ? String(def.every_minutes) : '30'); var every = _ev[0], setEvery = _ev[1];
  var _tz = useState((def && def.timezone) || 'America/New_York'); var tz = _tz[0], setTz = _tz[1];
  // days of the week (Anthony 2026-10-08: "batch only on weekdays"): 0 = Sun … 6 = Sat; all seven = every day
  var _dy = useState(def && Array.isArray(def.days) && def.days.length ? def.days.map(Number) : [0, 1, 2, 3, 4, 5, 6]); var days = _dy[0], setDays = _dy[1];
  var dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var _mx = useState(def ? String(def.max_orders) : '100'); var maxOrders = _mx[0], setMaxOrders = _mx[1];
  var _mn = useState(def ? String(def.min_orders) : '1'); var minOrders = _mn[0], setMinOrders = _mn[1];
  var _a = useState((def && def.action) || 'create'); var action = _a[0], setAction = _a[1];
  var _nt = useState((def && def.note_template) || ''); var noteTpl = _nt[0], setNoteTpl = _nt[1];
  var _as = useState(def && def.assignee_user_id ? String(def.assignee_user_id) : ''); var assignee = _as[0], setAssignee = _as[1];
  var _fi = useState(def && def.filter_id ? String(def.filter_id) : ''); var filterId = _fi[0], setFilterId = _fi[1]; // a saved Ready to Ship filter (Anthony 9-23)
  // group by item (Anthony 2026-10-01): one batch per item from single-item orders, no rule per SKU
  var _gi = useState(!!(def && def.group_by_item)); var groupByItem = _gi[0], setGroupByItem = _gi[1];
  var _ms = useState((def && def.min_scope) === 'item' ? 'item' : 'batch'); var minScope = _ms[0], setMinScope = _ms[1];
  var _sv = useState(false); var saving = _sv[0], setSaving = _sv[1];
  var _fv = useState({}); var fieldValues = _fv[0], setFieldValues = _fv[1];
  useEffect(function() { api('/shipping/rule-field-values').then(setFieldValues).catch(function() {}); }, []);

  // the automation rules' condition row, verbatim (same fields, operators and value lists)
  var DROPDOWN_FIELDS = { country: 'Countries', state: 'States', channel: 'Channels', tag: 'Tags', requested_service: 'Requested services', carrier: 'Carriers', is_international: 'Values' };
  var asArray = function(v) { if (Array.isArray(v)) return v; return String(v == null ? '' : v).split(/[,;\n]+/).map(function(s) { return s.trim(); }).filter(Boolean); };
  var OPS_STR = ['eq', 'ne', 'in', 'not_in', 'contains', 'not_contains'];
  var OPS_NUM = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between'];
  var OPS_MULTI = ['in', 'not_in'];
  var OP_LBL = { eq: '=', ne: '≠', in: 'is one of', not_in: 'is not one of', contains: 'contains', not_contains: "doesn't contain", gt: '>', gte: '≥', lt: '<', lte: '≤', between: 'between' };
  var fieldType = function(key) { var f = (fields || []).find(function(x) { return x.key === key; }); return f ? f.type : 'string'; };
  var selStyle = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', colorScheme: getThemeName() === 'light' ? 'light' : 'dark' };
  var inpStyle = Object.assign({}, selStyle, { flex: 1, minWidth: 120 });
  var section = function(t) { return h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, t); };

  // Every condition needs a value (sweep 2026-09-27 H2: a blank "contains" matched every order and
  // could batch + label the whole queue) — the same check the server runs (app-ship-filter.js)
  var condProblem = (function() {
    var types = {}, labels = {};
    (fields || []).forEach(function(f) { types[f.key] = f.type; labels[f.key] = f.label; });
    return shipConditionsProblem(conds, types, labels);
  })();
  var save = function() {
    if (!name.trim()) { showToast('The auto-batch rule needs a name', 'error'); return; }
    if (condProblem) { showToast(condProblem, 'error'); return; }
    var body = { name: name.trim(), enabled: enabled, conditions: conds, match_mode: matchMode,
      times: schedMode === 'times' ? times.slice().sort() : [], every_minutes: schedMode === 'every' ? parseInt(every, 10) || null : null, timezone: tz,
      days: days.length === 7 || days.length === 0 ? null : days.slice().sort(function(a, b) { return a - b; }),
      max_orders: parseInt(maxOrders, 10) || 100, min_orders: parseInt(minOrders, 10) || 1, action: action, note_template: noteTpl.trim() || null, assignee_user_id: assignee ? parseInt(assignee, 10) : null, filter_id: filterId ? parseInt(filterId, 10) : null,
      group_by_item: groupByItem, min_scope: groupByItem ? minScope : 'batch' };
    setSaving(true);
    api(isEdit ? '/shipping/auto-batches/' + def.id : '/shipping/auto-batches', { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) })
      .then(function() { onSaved(); onClose(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .then(function() { setSaving(false); });
  };

  return h(Modal, { open: true, onClose: onClose, title: isEdit ? 'Edit Auto-batch' : 'New Auto-batch', width: 720 },
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
      h('div', { style: { display: 'flex', gap: 12, alignItems: 'flex-end' } },
        h('div', { style: { flex: 1 } }, h(Inp, { label: 'Name', value: name, onChange: function(e) { setName(e.target.value); }, placeholder: 'e.g. Morning USPS domestic' })),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, color: C.text, fontSize: 13, cursor: 'pointer', paddingBottom: 8 } },
          h('input', { type: 'checkbox', checked: enabled, onChange: function(e) { setEnabled(e.target.checked); }, style: { accentColor: C.accent, width: 14, height: 14, cursor: 'pointer' } }), 'Enabled')),
      h('div', null,
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
          h('span', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Orders where'),
          h('select', { value: matchMode, onChange: function(e) { setMatchMode(e.target.value); }, style: Object.assign({}, selStyle, { color: C.accent, border: '1px solid ' + C.accent, padding: '3px 8px' }) },
            h('option', { value: 'all' }, 'ALL'), h('option', { value: 'any' }, 'ANY')),
          h('span', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, matchMode === 'any' ? 'of these match (OR)' : 'of these match (AND)')),
        conds.map(function(c, i) {
          var isDrop = !!DROPDOWN_FIELDS[c.field];
          var ops = isDrop ? OPS_MULTI : (fieldType(c.field) === 'number' ? OPS_NUM : OPS_STR);
          var curOp = isDrop && OPS_MULTI.indexOf(c.op) === -1 ? 'in' : c.op;
          return h('div', { key: i, style: { display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' } },
            h('select', { value: c.field, onChange: function(e) {
                var nf = e.target.value; var n = conds.slice();
                n[i] = { field: nf, op: DROPDOWN_FIELDS[nf] ? 'in' : (fieldType(nf) === 'number' ? 'eq' : 'contains'), value: DROPDOWN_FIELDS[nf] ? [] : '' };
                setConds(n);
              }, style: selStyle },
              (fields || []).map(function(f) { return h('option', { key: f.key, value: f.key }, f.label); })),
            h('select', { value: curOp, onChange: function(e) { var n = conds.slice(); n[i] = Object.assign({}, c, { op: e.target.value }); setConds(n); }, style: selStyle },
              ops.map(function(o) { return h('option', { key: o, value: o }, OP_LBL[o]); })),
            isDrop
              ? h(ReportParamSelect, { label: DROPDOWN_FIELDS[c.field], values: fieldValues[c.field] || [], selected: asArray(c.value),
                  onChange: function(v) { var n = conds.slice(); n[i] = Object.assign({}, c, { op: curOp, value: v }); setConds(n); },
                  valueColors: c.field === 'tag' ? (function() { var m = {}; (fieldValues.tag || []).forEach(function(t) { m[t] = shipTagColor(t); }); return m; })() : undefined })
              : h('input', { value: c.value == null ? '' : (Array.isArray(c.value) ? c.value.join(', ') : c.value),
                  onChange: function(e) { var n = conds.slice(); n[i] = Object.assign({}, c, { value: e.target.value }); setConds(n); },
                  placeholder: c.op === 'between' ? 'min, max' : (c.op === 'in' || c.op === 'not_in') ? 'comma-separated' : 'value', style: inpStyle }),
            h('button', { onClick: function() { setConds(conds.filter(function(_, x) { return x !== i; })); }, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer' } }, h(LIcon, { name: 'x', size: 14 })));
        }),
        h(Btn, { size: 'sm', onClick: function() { setConds(conds.concat([{ field: 'channel', op: 'contains', value: '' }])); } }, h(LIcon, { name: 'plus', size: 12 }), ' Condition'),
        conds.length === 0 && h('span', { style: { fontSize: 11, color: C.textDim, marginLeft: 10 } }, 'No conditions = every unbatched ready order · "Carrier" is the carrier a label would be bought from (the rate shopper\'s winner on a Rate Shop order)'),
        // a saved Ready to Ship filter narrows the pick as well (Anthony 2026-09-23: "add filters to the auto batch conditions")
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' } },
          h('span', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'And the saved filter'),
          h('select', { value: filterId, onChange: function(e) { setFilterId(e.target.value); }, style: selStyle },
            h('option', { value: '' }, 'None'),
            (savedFilters || []).map(function(sf) { return h('option', { key: sf.id, value: String(sf.id) }, sf.name); })),
          h('span', { style: { fontSize: 11, color: C.textDim } }, 'From Ready to Ship › Filters. The order must pass it as well as the conditions above.'))),
      h('div', null, section('When'),
        h('div', { style: { display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' } },
          h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' } },
            h('input', { type: 'radio', checked: schedMode === 'times', onChange: function() { setSchedMode('times'); } }), 'At these times'),
          schedMode === 'times' && h(ReportParamSelect, { label: 'Time', values: SHIP_AB_TIME_SLOTS, selected: times, onChange: function(v) { setTimes((v || []).slice().sort()); } }),
          schedMode === 'times' && times.length > 0 && h('span', { style: { fontSize: 12, color: C.textMuted } }, times.join(', ')),
          h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' } },
            h('input', { type: 'radio', checked: schedMode === 'every', onChange: function() { setSchedMode('every'); } }), 'Every'),
          schedMode === 'every' && h('input', { value: every, onChange: function(e) { setEvery(e.target.value); }, inputMode: 'numeric', style: Object.assign({}, inpStyle, { width: 60, minWidth: 0, flex: 'none' }) }),
          schedMode === 'every' && h('span', { style: { fontSize: 12, color: C.textMuted } }, 'minutes'),
          h('select', { value: tz, onChange: function(e) { setTz(e.target.value); }, style: selStyle },
            (timezones || Object.keys(SHIP_AB_TZ)).map(function(z) { return h('option', { key: z, value: z }, SHIP_AB_TZ[z] || z); }))),
        h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 } },
          h('span', { style: { fontSize: 12, color: C.textMuted, marginRight: 4 } }, 'On'),
          [1, 2, 3, 4, 5, 6, 0].map(function(d) {
            var on = days.indexOf(d) !== -1;
            return h('button', { key: d, type: 'button', onClick: function() { setDays(on ? days.filter(function(x) { return x !== d; }) : days.concat([d])); },
              style: { fontSize: 11.5, fontWeight: 500, padding: '3px 9px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                border: '1px solid ' + (on ? C.accent : C.border), background: on ? (C.accentSoft || 'transparent') : 'transparent', color: on ? C.accent : C.textMuted } },
              dayNames[d]);
          }),
          h('button', { type: 'button', onClick: function() { setDays([1, 2, 3, 4, 5]); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11.5, fontFamily: 'inherit', padding: '0 4px' } }, 'Weekdays'),
          h('button', { type: 'button', onClick: function() { setDays([0, 1, 2, 3, 4, 5, 6]); }, style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11.5, fontFamily: 'inherit', padding: '0 4px' } }, 'Every day'),
          days.length === 0 && h('span', { style: { fontSize: 11, color: C.warning } }, 'No day picked means every day')),
        h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4 } }, 'The sweep runs every five minutes, so a time lands within five minutes of the clock. Enabling or editing a rule never replays a time that already passed today; the first run is the next listed time. Days are read in the timezone above; Run Now ignores them. A run first tops up an open batch this rule made earlier, while nobody has started on it and it is under the max; the rest makes a new batch.')),
      h('div', null, section('Grouping'),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' } },
          h('input', { type: 'checkbox', checked: groupByItem, onChange: function(e) { setGroupByItem(e.target.checked); }, style: { accentColor: C.accent, width: 14, height: 14, cursor: 'pointer' } }), 'Group By Item'),
        h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4, lineHeight: 1.5 } },
          groupByItem
            ? 'One item per batch. Only orders that hold a single item take part (a multipack listing is its own item); orders of 1 unit and orders of 2 or more batch apart. An order with any other item on it is left for the rules below.'
            : 'On: one item per batch, from orders that hold only that item, so bulk items need no rule per SKU.'),
        groupByItem && h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' } },
          h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Minimum Applies To'),
          h('select', { value: minScope, onChange: function(e) { setMinScope(e.target.value); }, style: selStyle },
            h('option', { value: 'batch' }, 'Each Batch'),
            h('option', { value: 'item' }, 'The Item\'s Total')),
          h('span', { style: { fontSize: 11, color: C.textDim } }, minScope === 'item'
            ? 'An item with at least the minimum waiting is batched in full, the last short batch included.'
            : 'Every batch needs the minimum; what is left after the full batches waits.'))),
      h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 } },
        h('div', null, section('Max orders per batch'), h('input', { value: maxOrders, onChange: function(e) { setMaxOrders(e.target.value); }, inputMode: 'numeric', style: Object.assign({}, selStyle, { width: '100%', boxSizing: 'border-box' }) }),
          h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4 } }, 'More matches make more batches' + (action === 'create_and_label' ? ' (' + (maxLabelOrders || 300) + ' max with labels)' : ''))),
        h('div', null, section(groupByItem ? (minScope === 'item' ? 'Minimum orders of an item' : 'Minimum per item batch') : 'Minimum per batch'), h('input', { value: minOrders, onChange: function(e) { setMinOrders(e.target.value); }, inputMode: 'numeric', style: Object.assign({}, selStyle, { width: '100%', boxSizing: 'border-box' }) }),
          h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4 } }, groupByItem && minScope === 'item'
            ? 'An item with fewer orders than this is not batched; its orders go to the rules below'
            : 'A batch smaller than this is not made; its orders wait for the next run')),
        h('div', null, section('Then'),
          h('select', { value: action, onChange: function(e) { setAction(e.target.value); }, style: Object.assign({}, selStyle, { width: '100%' }) },
            // actions not loaded yet: the default list, and no crash reading it (review fix 11)
            (function(acts) { return Object.keys(acts).map(function(k) { return h('option', { key: k, value: k }, acts[k]); }); })(actions || SHIP_AB_DEFAULT_ACTIONS)),
          action === 'create_and_label' && h('div', { style: { fontSize: 11, color: C.warning, marginTop: 4 } }, 'Labels are bought unattended. A USPS "authorize again" question stops that run; the batch dialog shows it.'))),
      h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
        h('div', null, section('Batch note'), h('input', { value: noteTpl, onChange: function(e) { setNoteTpl(e.target.value); }, placeholder: groupByItem ? '{name} {sku} {units} {date} {time}' : '{name} {date} {time}', style: Object.assign({}, selStyle, { width: '100%', boxSizing: 'border-box' }) }),
          h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4 } }, 'Placeholders: {name} {date} {time} {count}' + (groupByItem ? ' {sku} {item} {units}' : ''))),
        h('div', null, section('Assign to'),
          h('select', { value: assignee, onChange: function(e) { setAssignee(e.target.value); }, style: Object.assign({}, selStyle, { width: '100%' }) },
            h('option', { value: '' }, 'Unassigned'),
            (users || []).map(function(u) { return h('option', { key: u.id, value: String(u.id) }, u.name); })))),
      h('div', { style: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 } },
        condProblem && h('span', { role: 'alert', style: { flex: 1, fontSize: 11.5, color: C.danger } }, condProblem),
        h(Btn, { variant: 'ghost', onClick: onClose }, 'Cancel'),
        h(Btn, { variant: 'primary', disabled: saving || !!condProblem, onClick: save }, saving ? 'Saving…' : 'Save'))));
}

function ShipAutoBatchPage({ showToast, embedded }) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _ed = useState(null); var editing = _ed[0], setEditing = _ed[1]; // null | {} (new) | def
  var _pv = useState({}); var previews = _pv[0], setPreviews = _pv[1];   // id → result
  var _busy = useState({}); var busy = _busy[0], setBusy = _busy[1];
  var load = function() { return api('/shipping/auto-batches').then(function(r) { setData(r); }).catch(function(e) { showToast(e.message, 'error'); }); };
  useEffect(function() { load(); }, []);
  if (!data) return h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted } }, 'Loading…');
  var defs = data.definitions || [];
  var cfg = data.config || {};
  var avCfg = data.address_validation || {}; // automatic USPS address validation (2026-10-08)
  var toggleAutoValidate = function() {
    var next = !avCfg.enabled;
    api('/shipping/config/address_validation', { method: 'PUT', body: JSON.stringify({ enabled: next }) })
      .then(function() { showToast(next ? 'Automatic address validation is ON' : 'Automatic address validation is OFF'); load(); }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var mark = function(id, v) { setBusy(function(p) { var n = Object.assign({}, p); n[id] = v; return n; }); };
  var toggleMaster = function() {
    var next = !cfg.enabled;
    api('/shipping/config/auto_batch', { method: 'PUT', body: JSON.stringify({ enabled: next }) })
      .then(function() { showToast(next ? 'Auto-batching is ON' : 'Auto-batching is OFF'); load(); }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var toggleDef = function(d) {
    api('/shipping/auto-batches/' + d.id, { method: 'PUT', body: JSON.stringify({ enabled: !d.enabled }) }).then(load).catch(function(e) { showToast(e.message, 'error'); });
  };
  var preview = function(d) {
    mark(d.id, 'preview');
    api('/shipping/auto-batches/' + d.id + '/preview').then(function(r) { setPreviews(function(p) { var n = Object.assign({}, p); n[d.id] = r; return n; }); })
      .catch(function(e) { showToast(e.message, 'error'); }).then(function() { mark(d.id, null); });
  };
  var runNow = async function(d) {
    if (!(await appConfirm('Run "' + d.name + '" now? Matching unbatched ready orders go into batch' + (d.action === 'create_and_label' ? 'es and their labels are bought.' : 'es.')))) return;
    mark(d.id, 'run');
    api('/shipping/auto-batches/' + d.id + '/run-now', { method: 'POST', body: '{}' })
      .then(function(r) {
        var n = (r.batches || []).length;
        showToast(n ? n + ' batch' + (n === 1 ? '' : 'es') + ': ' + r.batches.map(function(b) { return b.batch_number + (b.topped_up ? ' (+' : ' (') + b.orders + ')'; }).join(', ') : (r.skipped && r.skipped.length ? r.skipped.join('; ') : 'Nothing matched'), n ? 'success' : 'info');
        load(); try { window.dispatchEvent(new Event('ship:nav-refresh')); } catch (e) {}
      })
      .catch(function(e) { showToast(e.message, 'error'); }).then(function() { mark(d.id, null); });
  };
  // priority = list order: rules due in the same tick run top to bottom, the first takes its orders first
  var move = function(d, dir) {
    var ids = defs.map(function(x) { return x.id; });
    var i = ids.indexOf(d.id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    ids.splice(i, 1); ids.splice(j, 0, d.id);
    api('/shipping/auto-batches/reorder', { method: 'POST', body: JSON.stringify({ ids: ids }) }).then(load).catch(function(e) { showToast(e.message, 'error'); });
  };
  var remove = async function(d) {
    if (!(await appConfirm('Delete "' + d.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
    api('/shipping/auto-batches/' + d.id, { method: 'DELETE' }).then(load).catch(function(e) { showToast(e.message, 'error'); });
  };
  var lastText = function(d) {
    var r = d.last_result; if (!r || !r.at) return 'Never run';
    var n = (r.batches || []).length;
    return new Date(r.at).toLocaleString() + ': ' + r.matched + ' matched, ' + (n ? n + ' batch' + (n === 1 ? '' : 'es') + ' (' + r.batches.map(function(b) { return b.batch_number + (b.topped_up ? ' +' + b.orders : ''); }).join(', ') + ')' : 'no batch') + (r.skipped && r.skipped.length ? ' · ' + r.skipped.join('; ') : '');
  };
  var chip = function(t, color) { return h('span', { style: { fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: (color || C.textMuted) + '22', color: color || C.textMuted, whiteSpace: 'nowrap' } }, t); };

  return h('div', { style: { maxWidth: embedded ? 800 : 860 } },
    editing && h(ShipAutoBatchEditor, { def: editing.id ? editing : null, fields: data.fields, users: data.users, actions: data.actions, timezones: data.timezones, maxLabelOrders: data.max_label_orders, savedFilters: data.saved_filters || [],
      onClose: function() { setEditing(null); }, onSaved: load, showToast: showToast }),
    !embedded && h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' } }, 'Auto-batching'),
    h(Card, { style: { padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 } },
      h('div', { style: { flex: 1 } },
        h('div', { style: { fontSize: 12.5, color: C.text } }, 'Put ready orders into batches on a schedule'),
        h('div', { style: { fontSize: 10.5, color: C.textDim } }, 'Each auto-batch rule below picks its orders with conditions, runs at its times, and either creates the batch or creates it and buys the labels. Off means nothing runs, whatever the rules say. Batches respect the Settings › Batches guards.')),
      chip(cfg.enabled ? 'ON' : 'OFF', cfg.enabled ? C.success : C.textMuted),
      h(ShipCheckbox, { checked: !!cfg.enabled, onChange: toggleMaster })),
    h(Card, { style: { padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 } },
      h('div', { style: { flex: 1 } },
        h('div', { style: { fontSize: 12.5, color: C.text } }, 'Validate new domestic addresses with USPS, once per order'),
        h('div', { style: { fontSize: 10.5, color: C.textDim } }, 'Every minute, open domestic orders the system has not checked yet go through USPS Addresses (' + (avCfg.per_tick || 30) + ' per minute, up to ' + (avCfg.daily_cap || 'unlimited') + ' a day). The standardized address replaces the ship-to and the order\'s activity shows before and after. An address USPS cannot confirm gets the Address badge in Ready to Ship, is left out of auto-batches and waits for a person. A later hand edit is re-validated only with the Validate button.')),
      chip(avCfg.enabled ? 'ON' : 'OFF', avCfg.enabled ? C.success : C.textMuted),
      h(ShipCheckbox, { checked: !!avCfg.enabled, onChange: toggleAutoValidate })),
    h('div', { style: { display: 'flex', alignItems: 'center', marginBottom: 8 } },
      h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 } }, 'Auto-batch Rules'),
      h(Btn, { size: 'sm', variant: 'primary', onClick: function() { setEditing({}); } }, h(LIcon, { name: 'plus', size: 12 }), ' New')),
    defs.length > 1 && h('div', { style: { fontSize: 11.5, color: C.textDim, marginBottom: 8, lineHeight: 1.5 } },
      'Rules due at the same time run in this order, top to bottom: the first takes its orders, the next ones see what is left. Rules meant to run together should share a time.'),
    !defs.length && h(Card, { style: { padding: 20, color: C.textDim, fontSize: 13 } }, 'No auto-batch rules yet. Add one, preview what it would do, then enable it and the switch above.'),
    defs.map(function(d, i) {
      var pv = previews[d.id];
      var arrow = function(dir, name, title, off) { return h('button', { onClick: function() { move(d, dir); }, disabled: off, title: title, style: { background: 'none', border: 'none', color: off ? C.textDim : C.textMuted, cursor: off ? 'default' : 'pointer', padding: 0, lineHeight: 1, display: 'flex' } }, h(LIcon, { name: name, size: 15 })); };
      var conds = Array.isArray(d.conditions) ? d.conditions : [];
      return h(Card, { key: d.id, style: { padding: '12px 16px', marginBottom: 10 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
          h('div', { style: { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' } },
            arrow(-1, 'chevron-up', 'Run earlier', i === 0),
            h('span', { style: { fontSize: 10.5, color: C.textDim } }, String(i + 1)),
            arrow(1, 'chevron-down', 'Run later', i === defs.length - 1)),
          h('div', { style: { flex: 1, minWidth: 220 } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              h('span', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, d.name),
              chip(d.enabled ? 'Enabled' : 'Disabled', d.enabled ? C.success : C.textMuted),
              chip(d.action === 'create_and_label' ? 'Create + labels' : 'Create only', d.action === 'create_and_label' ? C.warning : C.accent)),
            h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 3 } },
              d.schedule_text + ' · ' + (conds.length ? conds.length + ' condition' + (conds.length === 1 ? '' : 's') + ' (' + (d.match_mode === 'any' ? 'any' : 'all') + ')' : 'every ready order') + (d.filter_id ? ' + filter "' + ((data.saved_filters || []).find(function(f) { return f.id === d.filter_id; }) || { name: '#' + d.filter_id }).name + '"' : '') + ' · up to ' + d.max_orders + ' per batch' + (d.min_orders > 1 ? ', at least ' + d.min_orders + (d.group_by_item && d.min_scope === 'item' ? ' of an item' : ' per batch') : '') + (d.group_by_item ? ' · grouped by item' : '')),
            h('div', { style: { fontSize: 11.5, color: C.textDim, marginTop: 3 } }, lastText(d)),
            pv && h('div', { style: { fontSize: 12, color: C.text, marginTop: 6, padding: '6px 10px', background: C.bg, borderRadius: 6 } },
              'Right now: ' + pv.matched + ' order' + (pv.matched === 1 ? '' : 's') + ' match → ' + (pv.batches.length ? pv.batches.map(function(b) { return b.orders + (b.top_up ? ' into Batch ' + b.top_up : '') + (b.group ? ' ' + b.group : ''); }).join(' + ') + ' (' + pv.batches.length + ' batch' + (pv.batches.length === 1 ? '' : 'es') + ')' : 'no batch') + (pv.skipped && pv.skipped.length ? ' · ' + pv.skipped.join('; ') : ''))),
          h(Btn, { size: 'sm', disabled: busy[d.id] === 'preview', onClick: function() { preview(d); } }, busy[d.id] === 'preview' ? 'Checking…' : 'Preview'),
          h(Btn, { size: 'sm', disabled: busy[d.id] === 'run', onClick: function() { runNow(d); } }, busy[d.id] === 'run' ? 'Running…' : 'Run Now'),
          h(Btn, { size: 'sm', onClick: function() { setEditing(d); } }, 'Edit'),
          h(Btn, { size: 'sm', variant: 'ghost', onClick: function() { remove(d); } }, h(LIcon, { name: 'trash-2', size: 13 })),
          h(ShipCheckbox, { checked: !!d.enabled, onChange: function() { toggleDef(d); }, title: d.enabled ? 'Disable' : 'Enable' })));
    }));
}
window.ShipAutoBatchPage = ShipAutoBatchPage;

// Settings › Automation (Anthony 2026-09-23: "auto-batching is part of automation. The Settings->Automation
// area needs to have a 'Rules' section and then the 'Auto-batching' section"): the rules tab first, the
// auto-batching definitions under it, on the one page the rail already had.
function ShipAutomationPage({ showToast }) {
  var heading = function(t) { return h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' } }, t); };
  return h('div', null,
    heading('Rules'),
    h(ShipRulesTab, { showToast: showToast }),
    h('div', { style: { borderTop: '1px solid ' + C.border, margin: '28px 0 18px', maxWidth: 800 } }),
    heading('Auto-batching'),
    h(ShipAutoBatchPage, { showToast: showToast, embedded: true }));
}
window.ShipAutomationPage = ShipAutomationPage;

;
/* ===== app-shipping-shell.js ===== */
// ─── Shipping subdomain shell (ship.atharvatoolz.com) ───
// Standalone SPA shell for the Shipping app, modeled on the Advertising shell
// (docs/14 pattern): shared login cookie, one canonical sign-in on the main app,
// area-gated ('shipping'), hostname-based self-mount. The shipping bundle =
// app-utils.js + app-shipping.js (page components) + this shell.
// Sidebar owns app-specific navigation: Queue, Rules & Tags,
// Settings — exactly why this became its own app.

var { createElement: h } = React;

var SHIP_MAIN_APP_URL = 'https://app.atharvatoolz.com';

function shipRedirectToLogin() {
  var target = SHIP_MAIN_APP_URL + '/login?returnTo=' + encodeURIComponent(window.location.href);
  try { window.location.replace(target); } catch (e) { window.location.href = target; }
}

// Orders group — status buckets mirroring ShipStation's left rail
// showCount: only actionable buckets carry a sidebar badge (user request 8-1)
var SHIP_ORDER_BUCKETS = [
  { key: 'orders_ready',               bucket: 'ready',               label: 'Ready to Ship',        path: '/', showCount: true },
  { key: 'orders_hold',                bucket: 'hold',                label: 'On Hold',              path: '/orders/hold', showCount: true },
  { key: 'orders_awaiting_payment',    bucket: 'awaiting_payment',    label: 'Awaiting Payment',     path: '/orders/awaiting-payment' },
  { key: 'orders_rejected',            bucket: 'rejected',            label: 'Rejected Fulfillment', path: '/orders/rejected' },
  { key: 'orders_pending_fulfillment', bucket: 'pending_fulfillment', label: 'Pending Fulfillment',  path: '/orders/pending-fulfillment' },
  // 'Shipped' removed 8-30 (user call) — Shipments → Recent is the shipped view
  { key: 'orders_cancelled',           bucket: 'cancelled',           label: 'Cancelled',            path: '/orders/cancelled' },
];
var SHIP_PAGES = [
  { key: 'rate_browser', label: 'Rate Browser', icon: 'scale',      path: '/rate-browser' }, // quotes without an order (Anthony 9-19)
  { key: 'products',  label: 'Products',       icon: 'package',       path: '/products' },
  { key: 'scan_station', label: 'Scan Station', icon: 'scan-line', path: '/scan-station' }, // orders that did not go out, with a reason (Anthony 9-23)
];
// Insights — first group in the rail (ShipStation Insights parity)
var SHIP_INSIGHTS_PAGES = [
  { key: 'insights',          label: 'Delivery Speed', title: 'Insights — Delivery Speed', icon: 'bar-chart-3', path: '/insights' },
  { key: 'insights_orders',   label: 'Orders',         title: 'Insights — Orders',         icon: 'trending-up', path: '/insights/orders' },
  { key: 'insights_shipping', label: 'Shipping',       title: 'Insights — Shipping',       icon: 'truck',       path: '/insights/shipping' },
  { key: 'insights_regions',  label: 'Regions',        title: 'Insights — Regions',        icon: 'globe',       path: '/insights/regions' },
  { key: 'insights_products', label: 'Products',       title: 'Insights — Products',       icon: 'tag',         path: '/insights/products' },
];
// Shipments group (ShipStation's Shipped rail): tracking buckets unlock with
// the carrier APIs; Recent/Voided/Customs/EOD/Batch History are live now
var SHIP_SHIPMENT_PAGES = [
  { key: 'ship_recent',     label: 'Recent',                 path: '/shipments' },
  { key: 'ship_transit',    label: 'In Transit',             path: '/shipments/in-transit' },
  { key: 'ship_delivered',  label: 'Delivered',              path: '/shipments/delivered' },
  { key: 'ship_exceptions', label: 'Delivery Exceptions',    path: '/shipments/exceptions' },
  { key: 'ship_voided',     label: 'Voided',                 path: '/shipments/voided' },
  { key: 'ship_customs',    label: 'Pending Customs Docs',   path: '/shipments/pending-customs', hidden: true }, // off the menu (Anthony 9-19); the URL still routes
  { key: 'ship_eod',        label: 'End of Day',             path: '/shipments/end-of-day' },
  { key: 'ship_batches',    label: 'Batch History',          path: '/shipments/batch-history' },
];
// Settings group — app-specific configuration lives under here
var SHIP_SETTINGS_PAGES = [
  { key: 'settings',            label: 'General',      icon: 'settings', path: '/settings' },
  { key: 'rules',               label: 'Automation',   icon: 'zap',      path: '/settings/automation' },
  { key: 'setting_ship',        label: 'Shipping',     icon: 'truck',    path: '/settings/shipping' },
  { key: 'setting_tags',        label: 'Tags',         icon: 'tag',      path: '/settings/tags' },
  { key: 'setting_integrations',label: 'Integrations', icon: 'plug',     path: '/settings/integrations' },
  { key: 'setting_print',       label: 'Printing',     icon: 'printer',  path: '/settings/printing' },
];
var SHIP_ALL_PAGES = SHIP_INSIGHTS_PAGES.concat(SHIP_ORDER_BUCKETS).concat(SHIP_SHIPMENT_PAGES).concat(SHIP_PAGES).concat(SHIP_SETTINGS_PAGES);

function shipPathToPage(pathname) {
  var p = (pathname || '/').replace(/\/+$/, '') || '/';
  if (p === '/rules' || p === '/settings/rules') return 'rules'; // legacy paths
  var bm = p.match(/^\/batches\/(\d+)$/);
  if (bm) return 'batch:' + bm[1];
  var hit = SHIP_ALL_PAGES.find(function(x) { return x.path === p; });
  return hit ? hit.key : 'orders_ready';
}
function shipPageToPath(key) {
  if (String(key).indexOf('batch:') === 0) return '/batches/' + key.slice(6);
  var hit = SHIP_ALL_PAGES.find(function(x) { return x.key === key; });
  return hit ? hit.path : '/';
}

// ─── Settings pages (stubs unlock as the carrier layer lands) ─────────────
function shipSettingsRow(icon, title, desc, status) {
  return h('div', { style: { display: 'flex', gap: 12, padding: '14px 16px', borderBottom: '1px solid ' + C.border, alignItems: 'flex-start' } },
    h(LIcon, { name: icon, size: 18, color: C.textMuted, style: { marginTop: 2 } }),
    h('div', { style: { flex: 1 } },
      h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, title),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2, lineHeight: 1.5 } }, desc)
    ),
    h('span', { style: { fontSize: 10, fontWeight: 500, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, background: C.warningSoft || C.accentSoft, color: C.warning, whiteSpace: 'nowrap' } }, status)
  );
}

function shipBtnStyle(primary) {
  return { background: primary ? C.accent : 'transparent', color: primary ? '#fff' : C.text, border: '1px solid ' + (primary ? C.accent : C.border), borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
}
var shipSetInp = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 9px', fontSize: 12.5, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };

function ShipShipFromForm({ initial, onSaved, onCancel }) {
  var _f = useState(initial); var f = _f[0], setF = _f[1];
  var _b = useState(false); var busy = _b[0], setBusy = _b[1];
  var set = function(k, v) { var n = Object.assign({}, f); n[k] = v; setF(n); };
  var inp = function(k, ph) {
    return h('input', { value: f[k] || '', placeholder: ph, onChange: function(e) { set(k, e.target.value); }, style: shipSetInp });
  };
  var save = function() {
    setBusy(true);
    api('/shipping/ship-from', { method: 'POST', body: JSON.stringify(f) })
      .then(function() { onSaved(); shipShellToast('Ship-from address saved'); })
      .catch(function(e) { appAlert(e.message); })
      .finally(function() { setBusy(false); });
  };
  return h('div', { style: { padding: '12px 16px 16px', borderBottom: '1px solid ' + C.border, background: C.bg, display: 'flex', flexDirection: 'column', gap: 8 } },
    h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } }, inp('name', 'Name (e.g. Miami warehouse) *'), inp('company', 'Company')),
    inp('street1', 'Street address'),
    inp('street2', 'Street 2 (suite, unit)'),
    h('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8 } }, inp('city', 'City'),
      h(StateSelect, { value: f.state, country: f.country, onChange: function(e) { set('state', e.target.value); }, style: shipSetInp, placeholder: 'State' }),
      inp('zip', 'ZIP *'), inp('country', 'Country (US)')),
    h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } }, inp('phone', 'Phone')),
    h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, cursor: 'pointer' } },
      h(ShipCheckbox, { checked: !!f.is_default, onChange: function() { set('is_default', !f.is_default); } }), 'Default ship-from for all orders'),
    h('div', { style: { display: 'flex', gap: 8 } },
      h('button', { onClick: save, disabled: busy, style: shipBtnStyle(true) }, busy ? 'Saving…' : 'Save address'),
      h('button', { onClick: onCancel, style: shipBtnStyle(false) }, 'Cancel'))
  );
}

// Browser tab title follows the page (Anthony 9-17). Its own component: the shell has early
// returns (loading / login) before its page logic, so a hook there would break hook order.
function ShipTabTitle({ text }) {
  useEffect(function() { try { document.title = (text ? text + ' · ' : '') + 'AtharvaToolz Shipping'; } catch (e) {} }, [text]);
  return null;
}

function ShipSettingsPage() {
  var _a = useState(null); var addrs = _a[0], setAddrs = _a[1];
  var _e = useState(null); var editing = _e[0], setEditing = _e[1]; // null | {country:'US'} | row
  // every save reloads through here: the panel / rule editor lists follow at once (9-27 LM20)
  var load = function() { shipClearSettingsCaches(); api('/shipping/ship-from').then(function(r) { setAddrs(r.addresses || []); }).catch(function() { setAddrs([]); }); };
  useEffect(function() { load(); }, []);
  var del = async function(a) {
    if (!(await appConfirm('Delete ship-from address "' + a.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
    api('/shipping/ship-from/' + a.id, { method: 'DELETE' }).then(function() { load(); shipShellToast('Address deleted'); }).catch(function(e) { appAlert(e.message); });
  };
  var mkDefault = function(a) { api('/shipping/ship-from/' + a.id + '/default', { method: 'POST' }).then(function() { load(); shipShellToast('Default ship-from updated'); }).catch(function(e) { appAlert(e.message); }); };
  var line2 = function(a) {
    return [a.street1, a.city, a.state, a.zip, (a.country && a.country !== 'US') ? a.country : null].filter(Boolean).join(', ');
  };
  return h('div', { style: { maxWidth: 680 } },
    h('div', { style: { display: 'flex', alignItems: 'center', margin: '0 0 8px' } },
      h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 } }, 'Ship-from addresses'),
      h('button', { onClick: function() { setEditing({ country: 'US' }); }, style: shipBtnStyle(true) }, '+ Add address')),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 8px', lineHeight: 1.5 } },
      'Every order ships from the default address unless an automation rule ("Set ship-from address") or a per-order override picks another. Rate quotes use the effective address’s ZIP; labels and customs forms will use the full address.'),
    h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 20 } },
      editing && h(ShipShipFromForm, { initial: editing, onSaved: function() { setEditing(null); load(); }, onCancel: function() { setEditing(null); } }),
      addrs === null ? h('div', { style: { padding: 14, fontSize: 12, color: C.textMuted } }, 'Loading…')
      : addrs.length === 0 && !editing ? h('div', { style: { padding: 16, fontSize: 12.5, color: C.textMuted } }, 'No ship-from addresses yet — add your warehouse address. Rate quotes fall back to ZIP 33172 until one exists.')
      : addrs.map(function(a) {
          return h('div', { key: a.id, style: { display: 'flex', gap: 12, padding: '12px 16px', borderBottom: '1px solid ' + C.border, alignItems: 'center' } },
            h(LIcon, { name: 'map-pin', size: 16, color: a.is_default ? C.accent : C.textMuted }),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, a.name,
                a.is_default && h('span', { style: { fontSize: 9, fontWeight: 500, color: C.accent, border: '1px solid ' + C.accent, borderRadius: 4, padding: '1px 6px', marginLeft: 8, textTransform: 'uppercase' } }, 'Default')),
              h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 2 } }, (a.company ? a.company + ' · ' : '') + (line2(a) || 'no street details'))),
            !a.is_default && h('button', { onClick: function() { mkDefault(a); }, style: { background: 'transparent', border: 'none', color: C.accent, fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' } }, 'Make default'),
            h('button', { onClick: function() { setEditing(a); }, title: 'Edit', style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'pencil', size: 14 })),
            h('button', { onClick: function() { del(a); }, title: 'Delete', style: { background: 'transparent', border: 'none', color: C.danger, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'trash-2', size: 14 })));
        })),
    h(Card, { style: { padding: 0, overflow: 'hidden' } },
      shipSettingsRow('users', 'Floor & assignees', 'Packer accounts, stations, and default assignments.', 'planned'))
  );
}

// Direct carrier credential entry + connection test (DHL eCommerce / USPS).
var SHIP_CRED_FIELDS = {
  dhl_ecommerce: {
    icon: 'truck', title: 'DHL eCommerce',
    desc: 'Direct API for label purchase, international products, and end-of-day manifests.',
    idLabel: 'Client ID', secretLabel: 'Client secret',
    extras: [
      { key: 'pickup_account', label: 'Pickup account #', hint: '5-digit pickup ID — required for label purchase' },
      { key: 'distribution_center', label: 'Distribution center', hint: 'e.g. USMIA1 (optional)' },
    ],
  },
  usps: {
    icon: 'mail', title: 'USPS',
    desc: 'USPS Ship API for your negotiated rates, labels, and SCAN forms.',
    idLabel: 'Consumer key', secretLabel: 'Consumer secret',
    extras: [
      { key: 'crid', label: 'CRID', hint: 'Customer Registration ID' },
      { key: 'mid', label: 'MID', hint: 'Mailer ID' },
      { key: 'manifest_mid', label: 'Manifest MID', hint: 'optional' },
      { key: 'eps_account', label: 'EPS account #', hint: 'payment account — required for label purchase' },
    ],
  },
  // UPS direct (2026-10-29): the status row — credentials live in the main app's card like the others
  ups: {
    icon: 'package', title: 'UPS',
    desc: 'UPS Shipping, Rating and Tracking APIs with your negotiated rates — labels, multi-box shipments, voids.',
    idLabel: 'Client ID', secretLabel: 'Client secret',
    extras: [{ key: 'account_number', label: 'Account number', hint: '6-character shipper number' }],
  },
};

// Read-only carrier status rows — credentials are managed in the main app
// (Settings > Integrations, shared integrations rows); each row deep-links
// there. The services checklists below stay in this app: which services
// compete in rate shopping is a shipping concern.
function shipCarrierStatusRow(channel, data) {
  var def = SHIP_CRED_FIELDS[channel];
  var status = !data || !data.configured ? { label: 'awaiting creds', bg: C.warningSoft || C.accentSoft, fg: C.warning }
    : data.last_test && data.last_test.ok ? { label: 'connected', bg: C.successSoft, fg: C.success }
    : data.last_test ? { label: 'auth failed', bg: C.dangerSoft || C.bg, fg: C.danger }
    : { label: 'saved — untested', bg: C.warningSoft || C.accentSoft, fg: C.warning };
  return h('a', {
    key: channel,
    href: SHIP_MAIN_APP_URL + '/settings/integrations', target: '_blank', rel: 'noopener',
    title: 'Manage credentials in AtharvaToolz → Settings → Integrations',
    style: { display: 'flex', gap: 12, padding: '13px 16px', borderBottom: '1px solid ' + C.border, alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }
  },
    h(LIcon, { name: def.icon, size: 17, color: C.accent }),
    h('div', { style: { flex: 1, minWidth: 0 } },
      h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, def.title),
      h('div', { style: { fontSize: 11, color: C.textMuted, lineHeight: 1.4 } },
        data && data.last_test
          ? (data.last_test.ok ? '✓ ' : '✕ ') + data.last_test.detail + ' · ' + new Date(data.last_test.at).toLocaleString()
          : def.desc)
    ),
    h('span', { style: { fontSize: 10, fontWeight: 500, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, background: status.bg, color: status.fg, whiteSpace: 'nowrap' } }, status.label),
    h(LIcon, { name: 'arrow-up-right', size: 14, color: C.textMuted })
  );
}

// Per-carrier service checklists (ShipStation "Shipping Options" parity).
// Checked services compete in rate shopping; unchecked can never be used.
var SHIP_CARRIER_LABELS = {
  stamps_com: 'USPS',
  dhl_global_mail: 'DHL eCommerce',
  dhl_ecommerce_us: 'DHL eCommerce US',
  ups: 'UPS', // UPS direct (2026-10-29)
};

// Settings pages surface saves through the shell's toast (user ask 8-30 —
// the tiny per-card 'saving…' flicker wasn't noticeable feedback)
var _shellToast = null;
function shipShellToast(msg, kind) { if (_shellToast) _shellToast(msg, kind || 'success'); }

function ShipCarrierServicesSection() {
  var _d = useState(null); var data = _d[0], setData = _d[1]; // null=loading, 'nocreds', or [{carrier, services}]
  var _x = useState({}); var open = _x[0], setOpen = _x[1];
  var _sv = useState(null); var saving = _sv[0], setSaving = _sv[1];

  useEffect(function() {
    api('/shipping/carrier-services')
      .then(function(r) { setData(r.carriers || []); })
      .catch(function() { setData('nocreds'); });
  }, []);

  var save = function(carrier, services) {
    var map = {};
    services.forEach(function(s) { map[s.code] = s.enabled; });
    setSaving(carrier);
    api('/shipping/carrier-services', { method: 'POST', body: JSON.stringify({ carrier: carrier, services: map }) })
      .then(function() { setSaving(null); shipShellToast('Carrier services saved'); })
      .catch(function(e) { setSaving(null); appAlert(e.message); });
  };

  var saveOptions = function(carrier, options) {
    setSaving(carrier);
    setData(function(prev) { return prev.map(function(c) { return c.carrier === carrier ? Object.assign({}, c, { options: Object.assign({}, c.options, options) }) : c; }); });
    api('/shipping/carrier-options', { method: 'POST', body: JSON.stringify({ carrier: carrier, options: options }) })
      .then(function() { setSaving(null); shipShellToast('Carrier option saved'); })
      .catch(function(e) { setSaving(null); appAlert(e.message); });
  };

  var toggle = function(carrier, code) {
    setData(function(prev) {
      var next = prev.map(function(c) {
        if (c.carrier !== carrier) return c;
        var services = c.services.map(function(s) { return s.code === code ? Object.assign({}, s, { enabled: !s.enabled }) : s; });
        save(carrier, services);
        return Object.assign({}, c, { services: services });
      });
      return next;
    });
  };

  var setAll = function(carrier, enabled) {
    setData(function(prev) {
      return prev.map(function(c) {
        if (c.carrier !== carrier) return c;
        var services = c.services.map(function(s) { return Object.assign({}, s, { enabled: enabled }); });
        save(carrier, services);
        return Object.assign({}, c, { services: services });
      });
    });
  };

  var svcRow = function(carrier, s) {
    return h('div', {
      key: s.code,
      onClick: function() { toggle(carrier, s.code); },
      style: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 16px 7px 40px', cursor: 'pointer', borderBottom: '1px solid ' + C.border }
    },
      h(ShipCheckbox, { checked: s.enabled, onChange: function() { toggle(carrier, s.code); } }),
      h('div', { style: { flex: 1, minWidth: 0 } },
        h('span', { style: { fontSize: 12.5, color: s.enabled ? C.text : C.textMuted } }, s.name),
        s.transit_label && h('span', { style: { fontSize: 10.5, color: C.textDim, marginLeft: 8 } }, s.transit_label)),
      h('span', { style: { fontSize: 10, color: C.textDim } }, s.code)
    );
  };

  var group = function(carrier, list, label) {
    if (!list.length) return null;
    return h(React.Fragment, { key: label },
      h('div', { style: { padding: '5px 16px 5px 40px', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textMuted, background: C.bg, borderBottom: '1px solid ' + C.border } }, label),
      list.map(function(s) { return svcRow(carrier, s); })
    );
  };

  return h('div', null,
    h(ShipWarehousesCard),
    h(ShipReturnsDefaultCard),
    h(ShipBatchesCard),
    h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' } }, 'Carrier services'),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 8px', lineHeight: 1.5 } },
      'The products we buy through our own USPS and DHL eCommerce accounts. Checked services compete in rate shopping and appear as service choices on orders. Unchecked services are never used.'),
    data === null ? h(Card, { style: { padding: 14, fontSize: 12, color: C.textMuted, marginBottom: 20 } }, 'Loading carrier services…')
    : data === 'nocreds' ? h(Card, { style: { padding: 14, fontSize: 12, color: C.textMuted, marginBottom: 20 } }, 'Carrier services could not be loaded.')
    : h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 20 } },
        data.map(function(c) {
          var enabledCount = c.services.filter(function(s) { return s.enabled; }).length;
          var isOpen = !!open[c.carrier];
          var dom = c.services.filter(function(s) { return s.domestic; });
          var intl = c.services.filter(function(s) { return !s.domestic && s.international; });
          var other = c.services.filter(function(s) { return !s.domestic && !s.international; });
          return h('div', { key: c.carrier },
            h('div', {
              onClick: function() { setOpen(function(p) { var n = Object.assign({}, p); n[c.carrier] = !isOpen; return n; }); },
              style: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', cursor: 'pointer', borderBottom: '1px solid ' + C.border }
            },
              h(LIcon, { name: isOpen ? 'chevron-down' : 'chevron-right', size: 15, color: C.textMuted }),
              h(LIcon, { name: c.carrier === 'stamps_com' ? 'mail' : 'truck', size: 16, color: C.accent }),
              h('div', { style: { flex: 1 } },
                h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, SHIP_CARRIER_LABELS[c.carrier] || c.carrier),
                h('div', { style: { fontSize: 11, color: C.textMuted } }, enabledCount + ' of ' + c.services.length + ' services enabled')),
              saving === c.carrier && h('span', { style: { fontSize: 10, color: C.textDim } }, 'saving…'),
              isOpen && h('span', {
                onClick: function(e) { e.stopPropagation(); setAll(c.carrier, enabledCount < c.services.length); },
                style: { fontSize: 11, fontWeight: 500, color: C.accent, cursor: 'pointer' }
              }, enabledCount < c.services.length ? 'Select all' : 'Deselect all')
            ),
            isOpen && h('div', null,
              group(c.carrier, dom, 'Domestic'),
              group(c.carrier, intl, 'International'),
              group(c.carrier, other, 'Other'),
              (c.carrier === 'dhl_global_mail' || c.carrier === 'stamps_com') && h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px 10px 40px', borderBottom: '1px solid ' + C.border } },
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 12.5, color: C.text } }, 'Default ancillary endorsement'),
                  h('div', { style: { fontSize: 10.5, color: C.textDim } }, c.carrier === 'dhl_global_mail' ? 'Stamped on every DHL label bought here. The printed endorsement text on the next label confirms the wording.' : 'Stamped on every USPS label bought here.')),
                h('select', {
                  value: (c.options && c.options.endorsement) || '',
                  onChange: function(e) { saveOptions(c.carrier, { endorsement: e.target.value }); },
                  style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }
                },
                  h('option', { value: '' }, 'None'),
                  h('option', { value: '1' }, 'Address Service Requested (1)'),
                  h('option', { value: '2' }, 'Forwarding Service Requested (2)'),
                  h('option', { value: '3' }, 'Change Service Requested (3)'),
                  h('option', { value: '4' }, 'Return Service Requested (4)'))),
              c.carrier === 'stamps_com' && h(UspsAutoVoidRow),
              c.carrier === 'dhl_global_mail' && h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px 10px 40px', borderBottom: '1px solid ' + C.border } },
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 12.5, color: C.text } }, 'Hazmat content category'),
                  h('div', { style: { fontSize: 10.5, color: C.textDim } }, 'Sent when an order is flagged hazmat. 08 = limited-quantity/ORM-D (perfumes). Requires DHL account approval, and LQ likely rides SM Parcel Ground only — Expedited supports just the lithium codes.')),
                h('select', {
                  value: (c.options && c.options.hazmat_category) || '08',
                  onChange: function(e) { saveOptions(c.carrier, Object.assign({}, c.options || {}, { hazmat_category: e.target.value })); },
                  style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }
                },
                  h('option', { value: '08' }, '08 — Limited Quantity / ORM-D (Default)'),
                  h('option', { value: '09' }, '09 — Small Quantity Provision'),
                  h('option', { value: '01' }, '01 — Lithium Metal in Equipment'),
                  h('option', { value: '04' }, '04 — Lithium-Ion in Equipment')))
            )
          );
        })
      )
  );
}

// Order-warehouse allowlist (config key 'warehouses'): the live Zentail
// warehouse catalog with checkboxes — orders routed to checked warehouses
// appear across the shipping app; FBA/WFS/3PL/vendor lanes stay out.
function ShipWarehousesCard() {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  useEffect(function() {
    api('/shipping/warehouses').then(setData).catch(function() { setData({ warehouses: [], included: [] }); });
  }, []);
  var toggle = function(id) {
    var inc = data.included.slice();
    var i = inc.indexOf(id);
    if (i === -1) inc.push(id);
    else {
      if (inc.length === 1) { appAlert('At least one warehouse must stay included.'); return; }
      inc.splice(i, 1);
    }
    setData(Object.assign({}, data, { included: inc }));
    api('/shipping/config/warehouses', { method: 'PUT', body: JSON.stringify({ included: inc, known: data.warehouses }) })
      .then(function() { shipShellToast('Warehouse selection saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  return h('div', null,
    h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' } }, 'Order warehouses'),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 8px', lineHeight: 1.5 } },
      'Orders routed to checked warehouses appear across the shipping app (queue, shipments, end of day). Amazon FBA, WFS, and vendor/3PL lanes are fulfilled by their channels — leave them unchecked.'),
    data === null
      ? h(Card, { style: { padding: 14, fontSize: 12, color: C.textMuted, marginBottom: 20 } }, 'Loading warehouses from Zentail…')
      : h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 20 } },
          data.warehouses.length === 0
            ? h('div', { style: { padding: 14, fontSize: 12, color: C.textMuted } }, 'Warehouse list unavailable — check the Zentail integration.')
            : data.warehouses.map(function(w, i) {
                var on = data.included.indexOf(w.id) !== -1;
                return h('div', {
                  key: w.id,
                  onClick: function() { toggle(w.id); },
                  style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', borderBottom: i < data.warehouses.length - 1 ? '1px solid ' + C.border : 'none' }
                },
                  h(ShipCheckbox, { checked: on, onChange: function() { toggle(w.id); } }),
                  h('span', { style: { flex: 1, fontSize: 12.5, color: on ? C.text : C.textMuted } }, w.name),
                  h('span', { style: { fontSize: 10, color: C.textDim } }, 'wh ' + w.id));
              })));
}

// Default return label (config key 'returns'): carrier + service used when a
// return label is created from an order. The carrier list comes from the
// live label-carrier registry, so new carriers appear automatically.
function ShipReturnsDefaultCard() {
  var _c = useState(null); var cars = _c[0], setCars = _c[1];
  var _g = useState(null); var cfg = _g[0], setCfg = _g[1];
  useEffect(function() {
    api('/shipping/label-carriers').then(function(r) { setCars(r.carriers || []); }).catch(function() { setCars([]); });
    api('/shipping/config/returns').then(setCfg).catch(function() { setCfg({ carrier: 'usps', service: 'usps_ground_advantage' }); });
  }, []);
  if (cars === null || cfg === null) return null;
  var save = function(patch) {
    var next = Object.assign({}, cfg, patch);
    setCfg(next);
    api('/shipping/config/returns', { method: 'PUT', body: JSON.stringify(next) })
      .then(function() { shipShellToast('Return default saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  var cur = cars.find(function(c) { return c.carrier === cfg.carrier; }) || cars[0] || { services: [] };
  var sel = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' };
  return h('div', null,
    h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' } }, 'Return labels'),
    h(Card, { style: { padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
      h('div', { style: { flex: 1, minWidth: 220 } },
        h('div', { style: { fontSize: 12.5, color: C.text } }, 'Default return carrier and service'),
        h('div', { style: { fontSize: 10.5, color: C.textDim } }, 'Used when a return label is created from an order; adjustable per label.')),
      h('select', {
        value: cfg.carrier,
        onChange: function(e) {
          var c = cars.find(function(x) { return x.carrier === e.target.value; });
          save({ carrier: e.target.value, service: (c && c.services[0] && c.services[0].code) || cfg.service });
        },
        style: sel
      }, cars.map(function(c) {
        return h('option', { key: c.carrier, value: c.carrier, disabled: !c.returns_capable },
          c.label + (c.returns_capable ? '' : ' — ' + (c.returns_note || 'no returns support')));
      })),
      h('select', {
        value: cfg.service,
        onChange: function(e) { save({ service: e.target.value }); },
        style: sel
      }, (cur.services || []).map(function(s) {
        return h('option', { key: s.code, value: s.code }, shipServiceLabel(s.code));
      }))));
}

// Batches (config key 'batches'): a Printed batch is archived automatically N days
// after its labels were printed (Anthony 9-17); 0 = never. Hourly sweep in server/index.js.
// Two guards (Anthony 9-23): one carrier per batch, and no domestic + international together —
// the server refuses a Create Labels / Add to Batch selection that would break them.
function ShipBatchesCard() {
  var _g = useState(null); var cfg = _g[0], setCfg = _g[1];
  useEffect(function() { api('/shipping/config/batches').then(setCfg).catch(function() { setCfg({ auto_archive_days: 30 }); }); }, []);
  // The days box is a draft saved on blur / Enter (review fix 10): it used to PUT the setting and
  // toast on every keystroke — typing "45" saved 4 days first.
  var _dd = useState(null); var daysDraft = _dd[0], setDaysDraft = _dd[1];
  if (cfg === null) return null;
  var save = function(patch) {
    var next = Object.assign({}, cfg, patch);
    setCfg(next);
    api('/shipping/config/batches', { method: 'PUT', body: JSON.stringify(next) })
      .then(function() { shipShellToast('Batch setting saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  var days = parseInt(cfg.auto_archive_days, 10); if (!(days >= 0)) days = 30;
  var commitDays = function() {
    if (daysDraft == null) return;
    var n = Math.max(0, Math.min(365, parseInt(daysDraft, 10) || 0));
    setDaysDraft(null);
    if (n !== days) save({ auto_archive_days: n }); // unchanged: no request, no toast
  };
  var row = function(title, sub, control, last) {
    return h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: last ? 'none' : '1px solid ' + C.border } },
      h('div', { style: { flex: 1, minWidth: 220 } },
        h('div', { style: { fontSize: 12.5, color: C.text } }, title),
        h('div', { style: { fontSize: 10.5, color: C.textDim } }, sub)),
      control);
  };
  return h('div', null,
    h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' } }, 'Batches'),
    h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 20 } },
      row('Archive printed batches automatically',
        'Open → Not Yet Printed (labels created) → Printed (labels printed) → Archived. A Printed batch is archived this many days after its labels were printed; 0 leaves it to the Archive button.',
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          h('input', {
            type: 'number', min: 0, max: 365, value: daysDraft != null ? daysDraft : days,
            onChange: function(e) { setDaysDraft(e.target.value); },
            onBlur: function() { commitDays(); },
            onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); commitDays(); } else if (e.key === 'Escape') setDaysDraft(null); },
            style: { width: 64, background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }
          }),
          h('span', { style: { fontSize: 11, color: C.textMuted } }, days === 0 ? 'days (off)' : 'days after printing'))),
      row('One carrier per batch',
        'Create Labels and Add to Batch refuse a selection that mixes carriers (USPS, DHL, …). A Rate Shop order counts as its quoted winner; one without a quote counts as Rate Shop.',
        h(ShipCheckbox, { checked: !!cfg.one_carrier, onChange: function() { save({ one_carrier: !cfg.one_carrier }); } })),
      row('Keep domestic and international apart',
        'A batch holds either domestic orders (US, PR, VI, GU, AS, MP) or international ones, never both.',
        h(ShipCheckbox, { checked: !!cfg.no_mixed_intl, onChange: function() { save({ no_mixed_intl: !cfg.no_mixed_intl }); } }), true)));
}

// Stale-label auto-void (config key 'auto_void'): USPS labels unscanned past
// the window get canceled daily for a full postage refund; money recovery
// only — the orders stay out of the queue (activity log + Voided view).
function UspsAutoVoidRow() {
  var _s = useState(null); var cfg = _s[0], setCfg = _s[1];
  // the days box is a draft saved on blur / Enter (sweep 2026-09-27 M19): every keystroke used to
  // save — clearing the box to type "14" saved 21, then 3 (the clamp), then 14
  var _vd = useState(null); var daysDraft = _vd[0], setDaysDraft = _vd[1];
  useEffect(function() { api('/shipping/config/auto_void').then(setCfg).catch(function() { setCfg({ enabled: true, days: 21 }); }); }, []);
  var save = function(patch) {
    var next = Object.assign({}, cfg, patch);
    setCfg(next);
    api('/shipping/config/auto_void', { method: 'PUT', body: JSON.stringify(next) })
      .then(function() { shipShellToast('Auto-void setting saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  if (!cfg) return null;
  var curDays = parseInt(cfg.days, 10) || 21;
  var commitVoidDays = function() {
    if (daysDraft == null) return;
    var n = parseInt(daysDraft, 10);
    setDaysDraft(null);
    if (!(n >= 3 && n <= 28)) { shipShellToast('Auto-void days must be 3 to 28', 'error'); return; }
    if (n !== curDays) save({ days: n });
  };
  return h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px 10px 40px', borderBottom: '1px solid ' + C.border } },
    h('div', { style: { flex: 1, minWidth: 0 } },
      h('div', { style: { fontSize: 12.5, color: C.text } }, 'Auto-void stale labels'),
      h('div', { style: { fontSize: 10.5, color: C.textDim } },
        'Purchased USPS labels with no scan after the window are canceled daily for a full postage refund. Refunds are recorded on the order activity log and the Voided view — the orders do not re-enter the queue.')),
    h('input', {
      type: 'number', min: 3, max: 28, value: daysDraft != null ? daysDraft : curDays, 'aria-label': 'Auto-void after days',
      onChange: function(e) { setDaysDraft(e.target.value); },
      onBlur: function() { commitVoidDays(); },
      onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); commitVoidDays(); } else if (e.key === 'Escape') setDaysDraft(null); },
      style: { width: 56, background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }
    }),
    h('span', { style: { fontSize: 11, color: C.textMuted } }, 'days'),
    h(ShipCheckbox, { checked: cfg.enabled !== false, onChange: function() { save({ enabled: cfg.enabled === false }); } }));
}

function ShipSettingsIntegrationsPage() {
  var _cc = useState(null); var creds = _cc[0], setCreds = _cc[1]; // {channel: rowData}
  var loadCreds = function() {
    api('/shipping/carrier-creds').then(function(r) {
      var m = {};
      (r.carriers || []).forEach(function(c) { m[c.channel] = c; });
      setCreds(m);
    }).catch(function() { setCreds({}); });
  };
  useEffect(function() { loadCreds(); }, []);

  return h('div', { style: { maxWidth: 680 } },
    h('div', { style: { display: 'flex', alignItems: 'center', margin: '0 0 8px' } },
      h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 } }, 'Shipping integrations'),
      h('a', {
        href: SHIP_MAIN_APP_URL + '/settings/integrations', target: '_blank', rel: 'noopener',
        style: { display: 'flex', alignItems: 'center', gap: 5, color: C.accent, fontSize: 12, fontWeight: 500, textDecoration: 'none' }
      }, 'Manage credentials', h(LIcon, { name: 'arrow-up-right', size: 13, color: C.accent }))
    ),
    h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 20 } },
      creds === null && h('div', { style: { padding: '13px 16px', fontSize: 12, color: C.textMuted, borderBottom: '1px solid ' + C.border } }, 'Loading…'),
      creds !== null && Object.keys(SHIP_CRED_FIELDS).map(function(ch) { return shipCarrierStatusRow(ch, creds[ch]); }),
      shipSettingsRow('arrow-up-right', 'Zentail writeback', 'Posts carrier + tracking to Zentail on label purchase — Zentail propagates to every channel.', 'pending test'),
      shipSettingsRow('scale', 'ShipStation rates bridge', 'Live rate shopping via your ShipStation account during the transition — retires when direct carriers go live.', 'active')
    ),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '-10px 0 18px', lineHeight: 1.5 } },
      'Carrier credentials (DHL eCommerce, USPS) are managed in the main app under Settings > Integrations — click a carrier row to open it. Which services compete in rate shopping is set under Settings › Shipping › Rate shopper.')
  );
}

var SHIP_TAX_SCHEMES = ['IOSS', 'EU VAT', 'UK VAT', 'AU GST', 'NZ GST', 'CA GST', 'Other'];

function ShipSettingsShippingPage() {
  var _pt = useState(null); var tpls = _pt[0], setTpls = _pt[1];
  var _pf = useState(null); var tplForm = _pf[0], setTplForm = _pf[1]; // null | {} | row
  var _tp = useState(null); var profiles = _tp[0], setProfiles = _tp[1];
  var _tf = useState(null); var profForm = _tf[0], setProfForm = _tf[1];
  var _ic = useState(null); var intlCfg = _ic[0], setIntlCfg = _ic[1];
  var _df = useState(null); var defAddr = _df[0], setDefAddr = _df[1];

  var loadAll = function() {
    shipClearSettingsCaches(); // saves reload through here: the panel / rule editor lists follow at once (9-27 LM20)
    api('/shipping/package-templates').then(function(r) { setTpls(r.templates || []); }).catch(function() { setTpls([]); });
    api('/shipping/tax-profiles').then(function(r) { setProfiles(r.profiles || []); }).catch(function() { setProfiles([]); });
    api('/shipping/config/international').then(setIntlCfg).catch(function() { setIntlCfg({}); });
    api('/shipping/ship-from').then(function(r) {
      var d = (r.addresses || []).find(function(a) { return a.is_default; }) || (r.addresses || [])[0];
      setDefAddr(d || null);
    }).catch(function() {});
  };
  useEffect(function() { loadAll(); }, []);

  var saveIntl = function(patch) {
    setIntlCfg(Object.assign({}, intlCfg, patch));
    api('/shipping/config/international', { method: 'PUT', body: JSON.stringify(patch) })
      .then(function() { shipShellToast('International settings saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  var saveTpl = function() {
    api('/shipping/package-templates', { method: 'POST', body: JSON.stringify(tplForm) })
      .then(function() { setTplForm(null); loadAll(); shipShellToast('Package template saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  var delTpl = async function(t) {
    if (!(await appConfirm('Delete package template "' + t.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
    api('/shipping/package-templates/' + t.id, { method: 'DELETE' }).then(function() { loadAll(); shipShellToast('Package template deleted'); }).catch(function(e) { appAlert(e.message); });
  };
  var saveProf = function() {
    api('/shipping/tax-profiles', { method: 'POST', body: JSON.stringify(profForm) })
      .then(function() { setProfForm(null); loadAll(); shipShellToast('Tax profile saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  var delProf = async function(p) {
    if (!(await appConfirm('Delete tax ID profile "' + p.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
    api('/shipping/tax-profiles/' + p.id, { method: 'DELETE' }).then(function() { loadAll(); shipShellToast('Tax profile deleted'); }).catch(function(e) { appAlert(e.message); });
  };

  var secHdr = function(title, addLabel, onAdd) {
    return h('div', { style: { display: 'flex', alignItems: 'center', margin: '18px 0 8px' } },
      h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 } }, title),
      onAdd && h('button', { onClick: onAdd, style: shipBtnStyle(true) }, addLabel));
  };
  var setTF = function(k, v) { var n = Object.assign({}, tplForm); n[k] = v; setTplForm(n); };
  var setPF = function(k, v) { var n = Object.assign({}, profForm); n[k] = v; setProfForm(n); };
  var numIn = function(k, ph) {
    return h('input', { type: 'number', value: tplForm[k] == null ? '' : tplForm[k], placeholder: ph, onChange: function(e) { setTF(k, e.target.value); }, style: shipSetInp });
  };

  return h('div', { style: { maxWidth: 680 } },
    // ── Rate shopper — checked services (from the live carrier catalogs)
    //    compete; unchecked never do. Single mechanism, no exclude lists.
    secHdr('Rate shopper'),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 10px', lineHeight: 1.5 } },
      'Quote origin: ', defAddr ? (defAddr.name + ' (ZIP ' + defAddr.zip + ')') : 'no ship-from address configured — fallback ZIP 33172', ' — set under Settings › General.'),
    h(ShipCarrierServicesSection),

    // ── Package templates ──
    secHdr('Package templates', '+ Add template', function() { setTplForm({}); }),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 8px', lineHeight: 1.5 } },
      'Saved box sizes selectable on orders (Package field) and in automation rules ("Set package template"). Dimensions feed rate quotes; tare weight is reserved for the label phase.'),
    h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 4 } },
      tplForm && h('div', { style: { padding: '12px 16px', borderBottom: '1px solid ' + C.border, background: C.bg, display: 'flex', flexDirection: 'column', gap: 8 } },
        h('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8 } },
          h('input', { value: tplForm.name || '', placeholder: 'Name (e.g. 7×6×4 box) *', onChange: function(e) { setTF('name', e.target.value); }, style: shipSetInp }),
          numIn('length_in', 'L in'), numIn('width_in', 'W in'), numIn('height_in', 'H in'), numIn('tare_oz', 'tare oz')),
        h('div', { style: { display: 'flex', gap: 8 } },
          h('button', { onClick: saveTpl, style: shipBtnStyle(true) }, 'Save template'),
          h('button', { onClick: function() { setTplForm(null); }, style: shipBtnStyle(false) }, 'Cancel'))),
      tpls === null ? h('div', { style: { padding: 14, fontSize: 12, color: C.textMuted } }, 'Loading…')
      : tpls.length === 0 && !tplForm ? h('div', { style: { padding: 16, fontSize: 12.5, color: C.textMuted } }, 'No templates yet — add your standard box sizes.')
      : tpls.map(function(t) {
          return h('div', { key: t.id, style: { display: 'flex', gap: 12, padding: '10px 16px', borderBottom: '1px solid ' + C.border, alignItems: 'center' } },
            h(LIcon, { name: 'package', size: 15, color: C.textMuted }),
            h('span', { style: { flex: 1, fontSize: 13, fontWeight: 500, color: C.text } }, t.name),
            h('span', { style: { fontSize: 12, color: C.textMuted } },
              (t.length_in || '?') + '×' + (t.width_in || '?') + '×' + (t.height_in || '?') + ' in' + (parseFloat(t.tare_oz) ? ' · ' + t.tare_oz + ' oz tare' : '')),
            h('button', { onClick: function() { setTplForm(t); }, title: 'Edit', style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'pencil', size: 14 })),
            h('button', { onClick: function() { delTpl(t); }, title: 'Delete', style: { background: 'transparent', border: 'none', color: C.danger, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'trash-2', size: 14 })));
        })),

    // ── International ──
    secHdr('International', '+ Add tax profile', function() { setProfForm({ scheme: 'IOSS' }); }),
    h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 8px', lineHeight: 1.5 } },
      'Tax ID profiles (IOSS / VAT / GST). Marketplace-collected IDs are assigned per channel with automation rules — the "Set tax ID profile" action now offers these profiles. Customs forms use the defaults below.'),
    h(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 4 } },
      profForm && h('div', { style: { padding: '12px 16px', borderBottom: '1px solid ' + C.border, background: C.bg, display: 'flex', flexDirection: 'column', gap: 8 } },
        h('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8 } },
          h('input', { value: profForm.name || '', placeholder: 'Name (e.g. amazon-eu-ioss) *', onChange: function(e) { setPF('name', e.target.value); }, style: shipSetInp }),
          h('select', { value: profForm.scheme || 'IOSS', onChange: function(e) { setPF('scheme', e.target.value); }, style: Object.assign({}, shipSetInp, { cursor: 'pointer' }) },
            SHIP_TAX_SCHEMES.map(function(s) { return h('option', { key: s, value: s }, s); })),
          h('input', { value: profForm.number || '', placeholder: 'ID number (e.g. IM0400000000)', onChange: function(e) { setPF('number', e.target.value); }, style: shipSetInp })),
        h('input', { value: profForm.notes || '', placeholder: 'Notes (which channel collects this, etc.)', onChange: function(e) { setPF('notes', e.target.value); }, style: shipSetInp }),
        h('div', { style: { display: 'flex', gap: 8 } },
          h('button', { onClick: saveProf, style: shipBtnStyle(true) }, 'Save profile'),
          h('button', { onClick: function() { setProfForm(null); }, style: shipBtnStyle(false) }, 'Cancel'))),
      profiles === null ? h('div', { style: { padding: 14, fontSize: 12, color: C.textMuted } }, 'Loading…')
      : profiles.length === 0 && !profForm ? h('div', { style: { padding: 16, fontSize: 12.5, color: C.textMuted } }, 'No tax ID profiles yet — add the IOSS/VAT/GST numbers your channels collect under.')
      : profiles.map(function(p) {
          return h('div', { key: p.id, style: { display: 'flex', gap: 12, padding: '10px 16px', borderBottom: '1px solid ' + C.border, alignItems: 'center' } },
            h(LIcon, { name: 'globe', size: 15, color: C.textMuted }),
            h('span', { style: { fontSize: 13, fontWeight: 500, color: C.text } }, p.name),
            p.scheme && h('span', { style: { fontSize: 9, fontWeight: 500, color: C.accent, border: '1px solid ' + C.accent, borderRadius: 4, padding: '1px 6px', textTransform: 'uppercase' } }, p.scheme),
            h('span', { style: { flex: 1, fontSize: 12, color: C.textMuted } }, p.number || ''),
            h('button', { onClick: function() { setProfForm(p); }, title: 'Edit', style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'pencil', size: 14 })),
            h('button', { onClick: function() { delProf(p); }, title: 'Delete', style: { background: 'transparent', border: 'none', color: C.danger, cursor: 'pointer', padding: 2 } }, h(LIcon, { name: 'trash-2', size: 14 })));
        })),
    h(Card, { style: { padding: '14px 16px', marginBottom: 4 } },
      h('div', { style: { fontSize: 12, fontWeight: 500, color: C.text, marginBottom: 8 } }, 'Customs defaults'),
      intlCfg === null ? h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Loading…')
      : h('div', { style: { display: 'flex', gap: 16, flexWrap: 'wrap' } },
          h('div', null,
            h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 } }, 'Contents type'),
            h('select', { value: intlCfg.customs_contents || 'merchandise', onChange: function(e) { saveIntl({ customs_contents: e.target.value }); }, style: Object.assign({}, shipSetInp, { width: 180, cursor: 'pointer' }) },
              ['merchandise', 'gift', 'documents', 'sample', 'returned goods'].map(function(v) { return h('option', { key: v, value: v }, v); }))),
          h('div', null,
            h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 } }, 'If undeliverable'),
            h('select', { value: intlCfg.customs_non_delivery || 'return', onChange: function(e) { saveIntl({ customs_non_delivery: e.target.value }); }, style: Object.assign({}, shipSetInp, { width: 180, cursor: 'pointer' }) },
              h('option', { value: 'return' }, 'Return to Sender'),
              h('option', { value: 'abandon' }, 'Treat as Abandoned'))))),

    // ── Parked ──
    secHdr('Parked'),
    h(Card, { style: { padding: 0, overflow: 'hidden' } },
      shipSettingsRow('shield', 'Insurance', 'Coverage rules per order value — currently not used per ops.', 'not used'),
      shipSettingsRow('building-2', 'Fulfillment providers', 'External fulfillment routing (beyond FBA/WFS/MELI modules).', 'planned'))
  );
}

function ShipSettingsTagsPage() {
  var _t = useState(null); var tags = _t[0], setTags = _t[1];
  var _n = useState(''); var newName = _n[0], setNewName = _n[1];
  var load = function() { api('/shipping/tags').then(setTags).catch(function() { setTags([]); }); };
  useEffect(function() { load(); }, []);
  var create = function() {
    var name = newName.trim();
    if (!name) return;
    api('/shipping/tags', { method: 'POST', body: JSON.stringify({ name: name }) })
      .then(function() { setNewName(''); load(); shipShellToast('Tag created'); })
      .catch(function(e) { appAlert(e.message); });
  };
  return h('div', { style: { maxWidth: 560 } },
    h('div', { style: { fontSize: 12, color: C.textDim, marginBottom: 12, lineHeight: 1.6 } },
      'Tags apply to orders manually (bulk actions), via automation rules, or from the Tag filter. Deleting a tag removes it from the pickers — orders already tagged keep it.'),
    h('div', { style: { display: 'flex', gap: 8, marginBottom: 14 } },
      h('input', {
        value: newName, placeholder: 'New tag name…',
        onChange: function(e) { setNewName(e.target.value); },
        onKeyDown: function(e) { if (e.key === 'Enter') create(); },
        style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 12.5, fontFamily: 'inherit', flex: 1 }
      }),
      h('button', { onClick: create, style: { background: C.accent, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' } }, '+ Create')),
    tags === null ? h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Loading…')
    : tags.length === 0 ? h(Card, { style: { padding: 20, fontSize: 12.5, color: C.textMuted } }, 'No tags yet — create one above or from the Tag filter on the Orders page.')
    : h(Card, { style: { padding: 0, overflow: 'hidden' } },
        tags.map(function(t) {
          var name = typeof t === 'string' ? t : t.name;
          var color = (t && t.color) || C.accent;
          return h('div', { key: name, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid ' + C.border } },
            h('span', { style: { width: 12, height: 12, borderRadius: 4, background: color, flexShrink: 0 } }),
            h('span', { style: { flex: 1, fontSize: 13, color: C.text } }, name),
            h('button', {
              title: 'Rename tag',
              onClick: async function() {
                var next = await appPrompt('Rename tag "' + name + '" to:', name);
                if (!next || !next.trim() || next.trim() === name) return;
                api('/shipping/tags/' + encodeURIComponent(name), { method: 'PUT', body: JSON.stringify({ name: next.trim() }) })
                  .then(function(r) { load(); shipShellToast('Renamed — updated ' + r.orders_updated + ' orders and ' + r.rules_updated + ' rules'); })
                  .catch(function(e) { appAlert(e.message); });
              },
              style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 }
            }, h(LIcon, { name: 'pencil', size: 13 })),
            h('button', {
              onClick: async function() {
                if (!(await appConfirm('Delete tag "' + name + '" from the registry? Orders already tagged keep it.', { danger: true, okLabel: 'Delete' }))) return;
                api('/shipping/tags/' + encodeURIComponent(name), { method: 'DELETE' }).then(function() { load(); shipShellToast('Tag deleted'); }).catch(function(e) { appAlert(e.message); });
              },
              style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 }
            }, h(LIcon, { name: 'trash-2', size: 13 })));
        }))
  );
}

function ShipSettingsPrintingPage() {
  var _c = useState(null); var cfg = _c[0], setCfg = _c[1];
  var _b = useState(false); var busy = _b[0], setBusy = _b[1];
  var _sv = useState(null); var savedFlash = _sv[0], setSavedFlash = _sv[1]; // section key
  useEffect(function() { api('/shipping/config/printing').then(setCfg).catch(function() { setCfg({}); }); }, []);
  function mkObj(k, v) { var o = {}; o[k] = v; return o; }
  var set = function(k, v) { setCfg(Object.assign({}, cfg, mkObj(k, v))); };
  var flash = function(sec) { setSavedFlash(sec); setTimeout(function() { setSavedFlash(null); }, 2000); };
  // PUT merges into the stored config server-side, so partial patches are safe;
  // toggles and selects save immediately, text fields save via their button.
  var savePatch = function(patch, sec) {
    api('/shipping/config/printing', { method: 'PUT', body: JSON.stringify(patch) })
      .then(function(merged) { shipClearSettingsCaches(); setCfg(merged); flash(sec); shipShellToast('Printing settings saved'); })
      .catch(function(e) { appAlert(e.message); });
  };
  var toggle = function(k, cur, sec) { set(k, !cur); savePatch(mkObj(k, !cur), sec); };
  var chk = function(label, key, defaultOn, sec) {
    var cur = defaultOn ? cfg[key] !== false : cfg[key] === true;
    return h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: C.text, cursor: 'pointer' } },
      h(ShipCheckbox, { checked: cur, onChange: function() { toggle(key, cur, sec); } }), label);
  };
  var secHdr = function(title, sec) {
    return h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 8px' } },
      h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, title),
      savedFlash === sec && h('span', { style: { fontSize: 11, color: C.success, fontWeight: 500 } }, 'Saved ✓'));
  };
  var note = function(t) { return h('div', { style: { fontSize: 11.5, color: C.textDim, margin: '0 0 8px', lineHeight: 1.5 } }, t); };
  var saveSlipText = function() {
    setBusy(true);
    api('/shipping/config/printing', { method: 'PUT', body: JSON.stringify({ slip_header: cfg.slip_header || '', slip_footer: cfg.slip_footer || '' }) })
      .then(function(merged) { shipClearSettingsCaches(); setCfg(merged); flash('slips'); shipShellToast('Slip header & footer saved'); })
      .catch(function(e) { appAlert(e.message); })
      .finally(function() { setBusy(false); });
  };
  if (cfg === null) {
    return h('div', { style: { maxWidth: 680 } }, h(Card, { style: { padding: 14, fontSize: 12, color: C.textMuted } }, 'Loading…'));
  }
  return h('div', { style: { maxWidth: 680 } },
    // ── Packing slips ──
    secHdr('Packing slips', 'slips'),
    note('One slip per order, standard Letter (8.5×11) paper.'),
    h(Card, { style: { padding: '14px 16px', marginBottom: 4, display: 'flex', flexDirection: 'column', gap: 12 } },
      h('div', null,
        h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 } }, 'Header line'),
        h('input', { value: cfg.slip_header || '', placeholder: 'e.g. Atharva Imports — atharvaimports.com', onChange: function(e) { set('slip_header', e.target.value); }, style: shipSetInp }),
        h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 3 } }, 'Fallback branding — printed only when the logo is turned off or unavailable.')),
      h('div', null,
        h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 } }, 'Footer'),
        h('textarea', { value: cfg.slip_footer || '', rows: 3, placeholder: 'e.g. Thank you for your order! Questions? support@atharvaimports.com',
          onChange: function(e) { set('slip_footer', e.target.value); }, style: Object.assign({}, shipSetInp, { resize: 'vertical' }) }),
        h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 3 } }, 'Printed under the item table on every slip. Blank = no footer.')),
      h('div', { style: { display: 'flex', gap: 8 } },
        h('button', { onClick: saveSlipText, disabled: busy, style: shipBtnStyle(true) }, busy ? 'Saving…' : 'Save header & footer')),
      chk('Show the company logo (slips & pick lists)', 'show_logo', true, 'slips')),

    // ── Pick lists ──
    secHdr('Pick lists', 'picks'),
    note('Aggregated per SKU across the selected orders and sorted for a bin walk. Letter paper.'),
    h(Card, { style: { padding: '14px 16px', marginBottom: 4, display: 'flex', flexDirection: 'column', gap: 10 } },
      chk('Show the checkbox column', 'pick_checkboxes', true, 'picks'),
      chk('Show the Picked / Checked / Date signature line', 'pick_signature', true, 'picks'),
      h('div', { style: { fontSize: 11, color: C.textDim } }, 'Custom warehouse-layout sorting is planned — bin-walk order is on by default.')),

    // ── Labels ──
    secHdr('Labels', 'labels'),
    note('Thermal 4×6 — activates with the direct carrier APIs (DHL / USPS).'),
    h(Card, { style: { padding: '14px 16px', marginBottom: 4, display: 'flex', flexDirection: 'column', gap: 10 } },
      h('div', null,
        h('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 3 } }, 'Label format'),
        h('select', { value: cfg.label_format || 'PDF_4x6', onChange: function(e) { set('label_format', e.target.value); savePatch({ label_format: e.target.value }, 'labels'); }, style: Object.assign({}, shipSetInp, { width: 220, cursor: 'pointer' }) },
          h('option', { value: 'PDF_4x6' }, 'PDF — 4×6 Thermal'),
          h('option', { value: 'ZPL' }, 'ZPL — Direct to Zebra Printer'))),
      h('div', { style: { fontSize: 11, color: C.textDim } }, 'Per-station printer routing lands with the carrier phase.'))
  );
}

// ─── App shell ────────────────────────────────────────────────────────────
function ShipApp() {
  var _a = useState('loading'); var auth = _a[0], setAuth = _a[1]; // loading | ok | anon | denied
  var _u = useState(null); var user = _u[0], setUser = _u[1];
  var _p = useState(shipPathToPage(window.location.pathname)); var page = _p[0], setPage = _p[1];
  var _lg = useState(null); var logoUrl = _lg[0], setLogoUrl = _lg[1];
  var _th = useState('dark'); var theme = _th[0], setTheme = _th[1];
  var _to = useState(null); var toast = _to[0], setToast = _to[1];
  var toastTimer = useRef(null);
  // Orders and Shipments start expanded and stay expanded across navigation (Anthony 9-19, like
  // the CS menu); a header click collapses one for the session. Insights and Settings still
  // follow the active section and close each other.
  var _sg = useState(false);
  var settingsOpen = _sg[0], setSettingsOpen = _sg[1];
  var toggleSettingsGroup = function() { setSettingsOpen(function(prev) { return !prev; }); };
  var _og = useState(true);
  var ordersOpen = _og[0], setOrdersOpen = _og[1];
  var toggleOrdersGroup = function() { setOrdersOpen(function(prev) { return !prev; }); };
  var _shg = useState(true);
  var shipmentsOpen = _shg[0], setShipmentsOpen = _shg[1];
  var _ig = useState(false);
  var insightsOpen = _ig[0], setInsightsOpen = _ig[1];
  var toggleOrdersGroupX = toggleOrdersGroup;
  var toggleShipmentsGroup = function() { setShipmentsOpen(function(prev) { return !prev; }); };
  var toggleSettingsGroupX = function() { setSettingsOpen(function(prev) { var n = !prev; if (n) setInsightsOpen(false); return n; }); };
  var toggleInsightsGroup = function() { setInsightsOpen(function(prev) { var n = !prev; if (n) setSettingsOpen(false); return n; }); };
  useEffect(function() {
    setInsightsOpen(page.indexOf('insights') === 0);
    setSettingsOpen(SHIP_SETTINGS_PAGES.some(function(p) { return p.key === page; }));
  }, [page]);
  // Sidebar badge counts per bucket + open batches list
  var _ct = useState({}); var counts = _ct[0], setCounts = _ct[1];
  var _bt = useState([]); var batches = _bt[0], setBatches = _bt[1];
  var refreshCounts = function() {
    fetch('/api/shipping/queue-counts', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : {}; })
      .then(setCounts).catch(function() {});
    fetch('/api/shipping/batches', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : []; })
      .then(function(b) { setBatches(Array.isArray(b) ? b : []); }).catch(function() {});
  };
  useEffect(function() {
    var onRefresh = function() { refreshCounts(); };
    window.addEventListener('ship:nav-refresh', onRefresh);
    // Live sidebar: poll every 5s while the tab is visible; hidden tabs pause and
    // catch up immediately when the user comes back.
    var poll = setInterval(function() { if (!document.hidden) refreshCounts(); }, 5000);
    var onVis = function() { if (!document.hidden) refreshCounts(); };
    document.addEventListener('visibilitychange', onVis);
    return function() {
      window.removeEventListener('ship:nav-refresh', onRefresh);
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
  // Pin state persists per-origin under the same key name the main app uses
  var _sp = useState(function() {
    try { var sc = localStorage.getItem('sidebar_collapsed'); return sc === null ? true : sc !== 'true'; } catch (e) { return true; }
  });
  var sidePinned = _sp[0], setSidePinned = _sp[1];
  var _sh = useState(false); var sideHover = _sh[0], setSideHover = _sh[1];
  var sideHoverTimer = useRef(null);
  // Phones / tablets (< 900 layout px): top bar + hamburger drawer instead of the sidebar (9-27)
  var viewport = useViewport();
  var toggleSidebar = function() {
    var pinned = !sidePinned;
    setSidePinned(pinned); setSideHover(false);
    if (window.saveSidebarPref) window.saveSidebarPref(!pinned);
  };

  var showToast = function(msg, kind) {
    setToast({ msg: msg, kind: kind || 'success' });
    // Error toasts reach error_log and the system-error email (Anthony 2026-09-18); the reporter
    // throttles to one post per 5 s per tab and the server dedupes per message for 10 min
    if (kind === 'error' && window.__reportError) {
      try { window.__reportError({ area: 'shipping', source: 'client', message: String(msg || '') }); } catch (e) { /* never break the toast */ }
    }
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(function() { setToast(null); }, 4000);
  };

  _shellToast = showToast;

  var navigate = function(pg) {
    setPage(pg);
    try { history.pushState({}, '', shipPageToPath(pg)); } catch (e) {}
    refreshCounts();
  };

  useEffect(function() {
    var onPop = function() { setPage(shipPathToPage(window.location.pathname)); };
    window.addEventListener('popstate', onPop);
    return function() { window.removeEventListener('popstate', onPop); };
  }, []);

  // Raw fetch (NOT api()) — api() reloads on 401 which would loop for anonymous
  // visitors; anonymous goes to the main app's login with returnTo back here.
  // ONLY a real 401 bounces to login. Transient failures (refresh racing a pm2
  // reload, network blip, 5xx) retry twice before giving up — a bounce on a
  // hiccup dumped a logged-in user onto the main app (8-11).
  useEffect(function() {
    var attempt = 0;
    var probe = function() {
      fetch('/api/auth/me', { credentials: 'include' })
        .then(function(r) {
          if (r.status === 401) { setAuth('anon'); shipRedirectToLogin(); return null; }
          if (!r.ok) throw new Error('auth probe ' + r.status);
          return r.json();
        })
        .then(function(u) {
          if (u === null) return; // 401 handled above
          if (!u || !u.id) { setAuth('anon'); shipRedirectToLogin(); return; }
          setUser(u);
          if (!hasPermission(u, 'shipping', 'read')) { setAuth('denied'); return; }
          setAuth('ok');
          refreshCounts();
          fetch('/api/auth/preferences', { credentials: 'include' })
            .then(function(r) { return r.ok ? r.json() : {}; })
            .then(function(prefs) {
              if (prefs && prefs.theme && prefs.theme !== 'dark') { setThemeColors(prefs.theme); setTheme(prefs.theme); }
              if (window.I18N) window.I18N.setLanguage(prefs && prefs.language);
              if (prefs && typeof prefs.sidebar_collapsed === 'boolean') {
                try { localStorage.setItem('sidebar_collapsed', String(prefs.sidebar_collapsed)); } catch (e) {}
                setSidePinned(!prefs.sidebar_collapsed);
              }
            }).catch(function() {});
        })
        .catch(function() {
          attempt++;
          if (attempt <= 2) setTimeout(probe, 1200 * attempt);
          else { setAuth('anon'); shipRedirectToLogin(); }
        });
    };
    probe();
    fetch('/api/settings/logo').then(function(r) { if (r.ok) setLogoUrl('/api/settings/logo'); }).catch(function() {});
  }, []);

  var toggleTheme = function() {
    var next = theme === 'dark' ? 'light' : 'dark';
    setThemeColors(next);
    setTheme(next);
    fetch('/api/auth/preferences', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ theme: next }) }).catch(function() {});
  };

  var handleLogout = async function() {
    try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch (e) {}
    if (window.setAuthToken) window.setAuthToken(null);
    setUser(null); setAuth('anon');
    shipRedirectToLogin();
  };

  if (auth === 'loading' || auth === 'anon') {
    return h('div', { style: { minHeight: 'calc(100vh * var(--invzoom, 0.8))', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 } },
      h(LIcon, { name: 'loader-2', size: 28, spin: true, style: { color: C.textMuted } }),
      auth === 'anon' && h('div', { style: { fontSize: 13, color: C.textMuted } }, 'Redirecting to sign-in…'));
  }
  if (auth === 'denied') {
    return h('div', { style: { minHeight: 'calc(100vh * var(--invzoom, 0.8))', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 } },
      h(Card, { style: { maxWidth: 440, width: '100%', padding: 32, textAlign: 'center' } },
        h(LIcon, { name: 'lock', size: 32, style: { color: C.warning, marginBottom: 12 } }),
        h('div', { style: { fontSize: 18, fontWeight: 500, color: C.text, marginBottom: 8 } }, 'No Shipping access'),
        h('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 16 } },
          'Your account (' + ((user && user.display_name) || '') + ') does not have the Shipping area enabled. Ask an admin to grant it under Settings > Roles.'),
        h(Btn, { size: 'sm', onClick: handleLogout }, 'Sign in as someone else')));
  }

  var sideBtn = function(active) { return SIDENAV.item(active); };
  var sideBottomBtn = function(p2) {
    return h(p2.href ? 'a' : 'button', {
      key: p2.label,
      className: 'side-bottom-btn snav',
      href: p2.href, target: p2.href ? '_blank' : undefined, rel: p2.href ? 'noopener' : undefined,
      onClick: p2.onClick, title: p2.iconOnly ? p2.label : undefined,
      style: p2.iconOnly
        ? SIDENAV.railBottomBtn({ topGap: p2.topGap })
        : SIDENAV.bottomBtn({ topGap: p2.topGap })
    }, p2.iconOnly ? h(LIcon, { name: p2.icon, size: 16 }) // icon only on the rail (Anthony 2026-09-23)
      : p2.href ? h(React.Fragment, null, p2.label, h(LIcon, { name: 'arrow-up-right', size: 13, style: SIDENAV.newTab })) // a link opens a new tab: say so (Anthony 2026-10-13)
      : p2.label);
  };
  var current = SHIP_ALL_PAGES.find(function(x) { return x.key === page; })
    || (String(page).indexOf('batch:') === 0 ? { key: page, label: 'Batch', title: 'Batch' } : SHIP_PAGES[0]);
  var settingsChildActive = SHIP_SETTINGS_PAGES.some(function(x) { return x.key === page; });

  // Full sidebar content — shared by the pinned nav and the hover drawer
  // narrow = the phone/tablet drawer: same content minus the Pin button (no sidebar to pin)
  var sidebarInner = function(inDrawer, narrow) {
    var navTo = function(key) { navigate(key); if (inDrawer) setSideHover(false); };
    return [
      h('div', { key: 'logo', style: SIDENAV.logoBox },
        logoUrl
          ? h('img', { src: logoUrl, alt: 'Logo', style: SIDENAV.logoImg })
          : h('div', { style: { fontWeight: 500, fontSize: 14, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 } }, h(LIcon, { name: 'ship', size: 15 }), ' Shipping')),
      h('div', { key: 'label', style: SIDENAV.sectionLabel }, 'Shipping'),
      h('div', { key: 'items', style: SIDENAV.scroller },
        // Insights — first group (ShipStation Insights parity)
        h('button', {
          key: 'insights_h',
          className: 'snav' + (page.indexOf('insights') === 0 ? ' snav-active' : ''),
          onClick: toggleInsightsGroup,
          style: Object.assign({}, sideBtn(page.indexOf('insights') === 0), { justifyContent: 'space-between' }),
        },
          h('span', null, 'Insights'), // no row icons on the side menu (Anthony 2026-09-23); the rail keeps them
          h('span', { style: { fontSize: 10, color: '#9CA3AF', transform: insightsOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex', alignItems: 'center', transition: 'transform 0.2s' } },
            h(LIcon, { name: 'chevron-down', size: 14 }))),
        insightsOpen && h('div', { key: 'insights_c', style: SIDENAV.childWrap(true) },
          SHIP_INSIGHTS_PAGES.map(function(pdef) {
            var childActive = page === pdef.key;
            return h('a', {
              key: pdef.key,
              className: 'snav' + (childActive ? ' snav-active' : ''),
              href: pdef.path,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navTo(pdef.key); },
              style: SIDENAV.child(childActive)
            }, pdef.label);
          })),
        // Orders — collapsible bucket group (ShipStation left-rail parity)
        h('button', {
          key: 'orders_h',
          className: 'snav' + (page.indexOf('orders_') === 0 ? ' snav-active' : ''),
          onClick: toggleOrdersGroupX,
          style: Object.assign({}, sideBtn(page.indexOf('orders_') === 0), { justifyContent: 'space-between' }),
        },
          h('span', null, 'Orders'),
          h('span', { style: { fontSize: 10, color: '#9CA3AF', transform: ordersOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex', alignItems: 'center', transition: 'transform 0.2s' } },
            h(LIcon, { name: 'chevron-down', size: 14 }))),
        ordersOpen && h('div', { key: 'orders_c', style: SIDENAV.childWrap(true) },
          SHIP_ORDER_BUCKETS.map(function(bdef) {
            var childActive = page === bdef.key;
            var n = counts[bdef.bucket];
            return h('a', {
              key: bdef.key,
              className: 'snav' + (childActive ? ' snav-active' : ''),
              href: bdef.path,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navTo(bdef.key); },
              style: SIDENAV.child(childActive)
            },
              h('span', { style: { flex: 1 } }, bdef.label),
              (bdef.showCount && n > 0) && h('span', { style: { fontSize: 9.5, fontWeight: 500, background: childActive ? '#4F6EF7' : '#374151', color: '#fff', padding: '1px 7px', borderRadius: 999 } }, n.toLocaleString()));
          })),
        // Shipments — collapsible group (ShipStation Shipped rail parity)
        h('button', {
          key: 'shipments_h',
          className: 'snav' + (page.indexOf('ship_') === 0 ? ' snav-active' : ''),
          onClick: toggleShipmentsGroup,
          style: Object.assign({}, sideBtn(page.indexOf('ship_') === 0), { justifyContent: 'space-between' }),
        },
          h('span', null, 'Shipments'),
          h('span', { style: { fontSize: 10, color: '#9CA3AF', transform: shipmentsOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex', alignItems: 'center', transition: 'transform 0.2s' } },
            h(LIcon, { name: 'chevron-down', size: 14 }))),
        shipmentsOpen && h('div', { key: 'shipments_c', style: SIDENAV.childWrap(true) },
          SHIP_SHIPMENT_PAGES.filter(function(sdef) { return !sdef.hidden; }).map(function(sdef) {
            var childActive = page === sdef.key;
            return h('a', {
              key: sdef.key,
              className: 'snav' + (childActive ? ' snav-active' : ''),
              href: sdef.path,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navTo(sdef.key); },
              style: SIDENAV.child(childActive)
            }, sdef.label);
          })),
        SHIP_PAGES.map(function(pdef) {
          return h('a', {
            key: pdef.key,
            className: 'snav' + (page === pdef.key ? ' snav-active' : ''),
            href: pdef.path,
            onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navTo(pdef.key); },
            style: sideBtn(page === pdef.key),
          }, pdef.label);
        }),
        // Open batches (ShipStation's Batches rail) — appear as they're created
        batches.length > 0 && h('div', { key: 'batches_h', style: { padding: '8px 10px 3px', fontSize: 10, fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 } },
          'Batches'),
        batches.length > 0 && h('div', { key: 'batches_c', style: SIDENAV.childWrap(true) },
          batches.map(function(bt) {
            var bkey = 'batch:' + bt.batch_number;
            var childActive = page === bkey;
            return h('a', {
              key: bt.id,
              className: 'snav' + (childActive ? ' snav-active' : ''),
              href: '/batches/' + bt.batch_number,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navTo(bkey); },
              style: SIDENAV.child(childActive)
            },
              h('span', { style: { flex: 1 } }, String(bt.batch_number)),
              bt.order_count > 0 && h('span', { style: { fontSize: 9.5, fontWeight: 500, background: childActive ? '#4F6EF7' : '#374151', color: '#fff', padding: '1px 7px', borderRadius: 999, fontFamily: 'inherit' } }, bt.order_count.toLocaleString()));
          })),
        // Settings — collapsible group, closed until clicked
        h('button', {
          key: 'settings_h',
          className: 'snav' + (settingsChildActive ? ' snav-active' : ''),
          onClick: toggleSettingsGroupX,
          style: Object.assign({}, sideBtn(settingsChildActive), { justifyContent: 'space-between' }),
        },
          h('span', null, 'Settings'),
          h('span', { style: { fontSize: 10, color: '#9CA3AF', transform: settingsOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex', alignItems: 'center', transition: 'transform 0.2s' } },
            h(LIcon, { name: 'chevron-down', size: 14 }))),
        settingsOpen && h('div', { key: 'settings_c', style: SIDENAV.childWrap(true) },
          SHIP_SETTINGS_PAGES.map(function(pdef) {
            var childActive = page === pdef.key;
            return h('a', {
              key: pdef.key,
              className: 'snav' + (childActive ? ' snav-active' : ''),
              href: pdef.path,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navTo(pdef.key); },
              style: SIDENAV.child(childActive)
            }, pdef.label);
          })),
        h('div', { key: 'div1', style: SIDENAV.divider }),
        sideBottomBtn({ href: SHIP_MAIN_APP_URL, icon: 'arrow-up-right', label: 'AtharvaToolz' })),
      h('div', { key: 'bottom', style: SIDENAV.bottomWrap },
        sideBottomBtn({ onClick: toggleTheme, icon: theme === 'dark' ? 'sun' : 'moon', label: theme === 'dark' ? 'Light Mode' : 'Dark Mode' }),
        sideBottomBtn({ onClick: handleLogout, icon: 'log-out', label: 'Sign Out' }),
        !narrow && sideBottomBtn({ onClick: toggleSidebar, icon: 'pin', label: sidePinned ? 'Unpin' : 'Pin Open', topGap: true })),
    ];
  };

  var sidebar;
  var narrowNav = null;
  if (viewport.isNarrow) {
    // Narrow: no sidebar and no rail — the top bar's hamburger opens the full menu as a drawer
    sidebar = null;
    narrowNav = h(window.NarrowNav, {
      navKey: page,
      title: String(page).indexOf('batch:') === 0 ? 'Batch ' + String(page).slice(6) : (current.title || current.label || 'Shipping'),
      drawer: sidebarInner(true, true),
    });
  } else if (sidePinned) {
    sidebar = h('nav', { style: SIDENAV.nav }, sidebarInner(false));
  } else {
    // Unpinned: 56px icon strip + hover drawer overlay (same mechanics as ads/main)
    var hoverIn = function() { if (sideHoverTimer.current) clearTimeout(sideHoverTimer.current); setSideHover(true); };
    var hoverOut = function() { sideHoverTimer.current = setTimeout(function() { setSideHover(false); }, 300); };
    sidebar = h(React.Fragment, null,
      h('nav', {
        onMouseEnter: hoverIn, onMouseLeave: hoverOut,
        style: SIDENAV.rail
      },
        h('div', { style: SIDENAV.railLogoBox },
          h('img', { src: '/api/settings/logo-icon', alt: '', style: SIDENAV.railLogoImg, onError: function(e) { e.target.style.display = 'none'; } })),
        h('div', { style: SIDENAV.railList },
          // One icon per top-level entry, same order as the expanded menu (groups = their first page)
          [{ key: 'insights', label: 'Insights', icon: 'bar-chart-3', path: '/insights' }, { key: 'orders_ready', label: 'Orders', icon: 'inbox', path: '/' }, { key: 'ship_recent', label: 'Shipments', icon: 'package-check', path: '/shipments' }].concat(SHIP_PAGES).concat([{ key: 'settings', label: 'Settings', icon: 'settings', path: '/settings' }]).map(function(pdef) {
            var active = page === pdef.key || (pdef.key === 'insights' && page.indexOf('insights') === 0) || (pdef.key === 'settings' && settingsChildActive) || (pdef.key === 'orders_ready' && page.indexOf('orders_') === 0) || (pdef.key === 'ship_recent' && page.indexOf('ship_') === 0);
            return h('a', {
              key: pdef.key,
              className: 'snav' + (active ? ' snav-active' : ''),
              href: pdef.path,
              title: pdef.label,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); navigate(pdef.key); },
              style: SIDENAV.railItem(active)
            }, h(LIcon, { name: pdef.icon, size: 16 }));
          }),
          h('div', { style: { borderTop: '1px solid #E5E7EB', margin: '8px 0' } }),
          sideBottomBtn({ href: SHIP_MAIN_APP_URL, icon: 'arrow-up-right', label: 'AtharvaToolz', iconOnly: true })),
        h('div', { style: SIDENAV.railBottomWrap },
          sideBottomBtn({ onClick: toggleTheme, icon: theme === 'dark' ? 'sun' : 'moon', label: theme === 'dark' ? 'Light Mode' : 'Dark Mode', iconOnly: true }),
          sideBottomBtn({ onClick: handleLogout, icon: 'log-out', label: 'Sign Out', iconOnly: true }),
          sideBottomBtn({ onClick: toggleSidebar, icon: 'pin', label: 'Pin Open', iconOnly: true }))),
      h('div', {
        onMouseEnter: hoverIn, onMouseLeave: hoverOut,
        style: SIDENAV.drawer(sideHover)
      }, sidebarInner(true)));
  }

  // Main-app layout mechanics: the root row is locked to exactly the viewport
  // height, so the sidebar never stretches with tall pages (its bottom buttons
  // stay visible) and content scrolls INSIDE <main> instead of the document.
  var fillPage = page.indexOf('orders_') === 0 || page.indexOf('batch:') === 0 || page === 'ship_recent';
  var narrow = viewport.isNarrow;
  var mainStyle = narrow
    ? narrowMainStyle(fillPage ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' } : null)
    : Object.assign({ flex: 1, minWidth: 0, padding: '22px 26px', boxSizing: 'border-box' },
      fillPage ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' } : { overflowY: 'auto' });
  return h('div', { style: Object.assign({ display: 'flex', height: 'calc(100vh * var(--invzoom, 0.8))', background: C.bg, color: C.text }, narrow ? { flexDirection: 'column' } : null) },
    h('style', null,
      '.snav:not(.snav-active):hover { background: rgba(79,110,247,0.07) !important; }'
      // Panel hover affordance (order panel + selection panel interactive bits)
      + ' .phov { transition: background 0.12s, color 0.12s; }'
      + ' .phov:hover { background: rgba(79,110,247,0.12) !important; color: #4F6EF7 !important; }'
      // Blanket hovers inside the order panel (.spanel scope): accent border on
      // every field, brightness lift on enabled buttons, checkbox lining accent
      + ' .spanel select:not(:disabled):hover, .spanel input:not(:disabled):hover, .spanel textarea:not(:disabled):hover { border-color: #4F6EF7 !important; }'
      + ' .spanel button:not(:disabled):hover { filter: brightness(1.25); }'
      + ' .schk { transition: border-color 0.12s; }'
      + ' .schk:hover { border-color: #4F6EF7 !important; }'
      // Native date-picker calendar icon follows the theme (dark default —
      // matches the main app's colorScheme handling; light when toggled)
      + ' input[type=date] { color-scheme: dark; }'
      + " html[data-theme='light'] input[type=date] { color-scheme: light; }"
      + ' .fbtn:hover { border-color: rgba(79,110,247,0.65) !important; }'
      + ' .fpill:not(.fpill-on):hover { background: rgba(79,110,247,0.10) !important; }'
      // Filter-bar type rides one notch ABOVE the old dense look (user ask
      // 8-30, pairs with the 12px queue tables) — these !important rules beat
      // the inline sizes on every .fbtn/.fpill, ship app only
      + ' .fbtn { font-size: 12.5px !important; padding: 7px 12px !important; }'
      + ' .fpill { font-size: 12px !important; }'),
    sidebar,
    narrowNav,
    // Content
    h('main', { style: mainStyle },
      h('div', { style: { marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 } },
        h(ShipTabTitle, { text: String(page).indexOf('batch:') === 0 ? 'Batch ' + String(page).slice(6) : (current.title || current.label || '') }),
        // narrow: the title sits in the top bar
        !narrow && h('h2', { style: { margin: 0, fontSize: 16, fontWeight: 500, color: C.text } }, current.title || current.label)
      ),
      // Crash containment: a page-level render error shows the boundary's retry
      // card + logs the React componentStack (error_log 333 white-screened the
      // whole shell with an anonymous minified stack — never again).
      h(window.ErrorBoundary, { key: 'eb:' + page, area: 'ship:' + page },
      page.indexOf('orders_') === 0 && h(ShipQueueTab, { key: page, bucket: page.slice(7), showToast: showToast }),
      page.indexOf('batch:') === 0 && (function() {
        var bid = page.slice(6);
        var bt = batches.find(function(x) { return String(x.batch_number) === bid || String(x.id) === bid; });
        return h(ShipQueueTab, { key: page, bucket: 'all', batchId: bid, batchNumber: bt && bt.batch_number, showToast: showToast });
      })(),
      page === 'insights' && h(ShipInsightsPage, { showToast: showToast }),
      page === 'insights_orders' && h(ShipInsightsOrdersPage, { showToast: showToast }),
      page === 'insights_shipping' && h(ShipInsightsShippingPage, { showToast: showToast }),
      page === 'insights_regions' && h(ShipInsightsRegionsPage, { showToast: showToast }),
      page === 'insights_products' && h(ShipInsightsProductsPage, { showToast: showToast }),
      page === 'ship_recent' && h(ShipShipmentsTab, { showToast: showToast }),
      page === 'ship_transit' && h(ShipShipmentsTab, { key: 'transit', showToast: showToast, deliveryFilter: 'in_transit' }),
      page === 'ship_delivered' && h(ShipShipmentsTab, { key: 'delivered', showToast: showToast, deliveryFilter: 'delivered' }),
      page === 'ship_exceptions' && h(ShipShipmentsTab, { key: 'exceptions', showToast: showToast, deliveryFilter: 'exception' }),
      page === 'ship_voided' && h(ShipShipmentsSubTab, { mode: 'voided', showToast: showToast }),
      page === 'ship_customs' && h(ShipShipmentsSubTab, { mode: 'customs', showToast: showToast }),
      page === 'ship_eod' && h(ShipShipmentsSubTab, { mode: 'eod', showToast: showToast }),
      page === 'ship_batches' && h(ShipShipmentsSubTab, { mode: 'batches', showToast: showToast }),
      page === 'products' && h(ShipProductsTab, { showToast: showToast }),
      page === 'rate_browser' && h(window.ShipRateBrowserPage, { showToast: showToast }),
      page === 'scan_station' && h(window.ShipScanStationPage, { showToast: showToast }),
      page === 'rules' && h(window.ShipAutomationPage, { showToast: showToast }), // Rules, then Auto-batching (Anthony 2026-09-23)
      page === 'settings' && h(ShipSettingsPage),
      page === 'setting_ship' && h(ShipSettingsShippingPage),
      page === 'setting_tags' && h(ShipSettingsTagsPage),
      page === 'setting_integrations' && h(ShipSettingsIntegrationsPage),
      page === 'setting_print' && h(ShipSettingsPrintingPage)
      )
    ),
    // Ask-AI floating widget — same 'chat' permission gate as the main app;
    // full-chat opens the main app in a new tab (no chat page in this shell)
    user && hasPermission(user, 'chat', 'read') && window.ChatWidget
      ? h(window.ChatWidget, { onOpenFull: function() { window.open(SHIP_MAIN_APP_URL + '/chat', '_blank'); } }) : null,
    // Toast
    // above every dialog (they sit at 1200+): an error toast used to hide behind the dialog that
    // caused it (sweep 2026-09-27 Lows); read out by screen readers
    toast && h('div', {
      role: toast.kind === 'error' ? 'alert' : 'status', 'aria-live': toast.kind === 'error' ? 'assertive' : 'polite',
      style: {
        position: 'fixed', bottom: 20, right: 20, zIndex: 11000, maxWidth: 380,
        background: toast.kind === 'error' ? C.danger : C.success, color: '#fff',
        padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)', animation: 'slideIn 0.2s ease',
      }
    }, toast.msg)
  );
}

window.ShipApp = ShipApp;

// Self-mount when loaded on the shipping subdomain (hostname-based — the CSP has
// no 'unsafe-inline', so ship.html cannot set a flag). The main bundle never
// includes this file; the guard keeps a future inclusion from double-mounting.
if (/^ship(ping)?\./i.test(window.location.hostname) && document.getElementById('root')) {
  ReactDOM.render(React.createElement(React.Fragment, null, React.createElement(ShipApp), React.createElement(AppDialogHost)), document.getElementById('root')); // AppDialogHost: in-app confirm / prompt / alert (2026-09-29)
}
